/* eslint-disable react-hooks/rules-of-hooks */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FileText, 
  FileCheck, 
  FileSearch, 
  DollarSign, 
  ShoppingCart, 
  Truck, 
  Send, 
  Paperclip,
  Plus,
  ArrowRight,
  ArrowLeft,
  Save,
  Eye,
  User,
  Users,
  Building,
  Calendar,
  Hash,
  Trash2,
  PlusCircle,
  Download,
  CheckCircle,
  Settings,
  FilePlus,
  Folder,
  Package,
  RefreshCcw,
  AlertTriangle,
  Undo2,
  CreditCard,
  Receipt,
  RefreshCw,
  Lock,
  Calculator,
  ClipboardList,
  FileInput,
  FileOutput,
  PackageCheck,
  RotateCcw,
  Banknote,
  FileSignature,
  FileBarChart,
  ClipboardCheck,
  Warehouse,
  ArrowRightLeft,
  Clock,
  PackagePlus,
  FileX,
  CheckSquare,
  Square,
  ChevronDown,
  PenTool,
  Printer,
  X,
  XCircle,
  ThumbsUp,
  ThumbsDown,
  HourglassIcon,
  CheckCircle2,
  Shield,
  TrendingUp
} from 'lucide-react';
import { CREATE_DOCUMENT_PICKER_META } from '../config/createDocumentPickerMeta';
import { getPickerCardField, buildPickerDocuments } from '../config/facturationDocumentPickerI18n';
import {
  getCanonicalStatusOrder,
  resolveFormStatusFromArchive,
} from '../config/facturationArchiveStatusOrder';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import { useGlobalTheme } from '../../../shared/hooks/useGlobalTheme';
import { formatBrandMoney, getBrandDefaultCurrency, getBrandCurrencySuffix, getBrandCurrencyOptions, getBrandVatRate, resolveLiveVatRate, isSaudiBrand, getBrandLocale } from '../../../shared/utils/brandCurrency';
import {
  getDefaultTaxOrder,
  sanitizeTaxOrderForBrand,
  getTaxElementDisplayLabel,
} from './documentTaxElements';
import {
  resolvePaymentDocumentDisplayType,
  encodePaymentVoucherAccountsPayerContact,
  decodePaymentVoucherAccountsPayerContact,
  resolveExplicitOriginalInvoiceDate,
  CREDIT_NOTE_LABEL_SUPPLIER,
  CREDIT_NOTE_LABEL_CLIENT,
  getPaymentVoucherReasonOptions,
  formatPaymentVoucherReason,
  resolvePaymentVoucherReasonValue,
  summarizePaymentDocumentTitleCore,
  findArchiveDocumentByNumber,
  buildPaymentVoucherPatchFromPurchaseOrder,
  buildPaymentVoucherPatchFromCreditNote,
  buildSupplierCreditNotePickerList,
  matchPaymentVoucherSupplierId,
  emptyPaymentVoucherLine,
  buildPaymentVoucherLineFromPurchaseOrder,
  buildPaymentVoucherLineFromCreditNote,
  syncPaymentVoucherHeaderFromLines,
  normalizePaymentVoucherLines,
  getUsedPaymentVoucherLineRefs,
  buildPaymentReceiptPatchFromInvoice,
  buildPaymentReceiptPatchFromClientCreditNote,
  buildClientCreditNotePickerList,
  buildClientInvoicePickerList,
  matchPaymentReceiptClientId,
  emptyPaymentReceiptLine,
  buildPaymentReceiptLineFromInvoice,
  buildPaymentReceiptLineFromClientCreditNote,
  syncPaymentReceiptHeaderFromLines,
  normalizePaymentReceiptLines,
  getUsedPaymentReceiptLineRefs,
  resolvePartnerReferenceCode,
} from '../../../shared/utils/documentTypeUtils';
import {
  applyArchiveModuleToDocumentPayload,
  getArchivesRouteForModule,
  getArchiveModuleForDocType,
  ARCHIVE_MODULE_INVENTAIRE,
  ARCHIVE_MODULE_LOGISTIQUE,
} from '../utils/archiveModuleScope';
import { resolveSectionRouteFromFromParam } from '../config/facturationSectionArchivesConfig';
import {
  MEASURE_UNIT_OPTIONS,
  measureUnitSelectValue,
  canonicalizeMeasureUnit,
  formatMeasureUnitForDisplay,
} from '../../../shared/utils/measureUnits';
import { mapPreviewEnumValue, PREVIEW_ENUM_VALUES } from '../../../shared/i18n/documentPreviewLabels';
import { downloadDocumentPDF, downloadDocumentPDFSilent, previewDocumentPDF } from '../../../shared/utils/pdfGenerator';
import { getTemplateComponent } from './templates/TemplateRegistry';
import TemplateWrapper, {
  normalizeClientDataForInvoicePreview,
  coerceCreditNoteFormData,
  formatReturnReason,
  formatReturnReasons,
} from './templates/TemplateWrapper';
// import ModernDocumentPreview from './ModernDocumentPreview'; // FILE DELETED
import { useSettings } from '../../../core/contexts/SettingsContext';
import { useFacturationConfig } from '../useFacturationConfig';
import {
  getFacturationDocumentDelay,
  getFacturationDocumentPrefix,
  getFacturationVatMode,
  getClientTypePreferentialRate,
  resolveFacturationDefaultPaymentTerms,
  computeVatBreakdown,
} from '../facturationConfigDefaults';
import { useClients } from '../../../core/contexts/ClientsContext';
import { useDocuments } from '../../../core/contexts/DocumentsContext';
import { useEmployees } from '../../../core/contexts/EmployeesContext';
import { useProducts } from '../../../core/contexts/ProductsContext';
import { useReturns } from '../../../core/contexts/ReturnsContext';
import { usePayments } from '../../../core/contexts/PaymentsContext';
import { useAppData } from '../../../core/contexts/AppDataContext';
import PageHeader from '../../../shared/components/common/PageHeader';
import Card from '../../../shared/components/common/Card';
import Button from '../../../shared/components/common/Button';
import { CustomSelect, CustomDatePicker, CustomNumberInput } from '../../../shared/components/custom';
import localApiAdapter from '../../../core/services/localApiAdapter';
import { isActiveRecord, isActiveRecordStatus } from '../../../shared/utils/recordStatusUtils';
import RequiredLabelText from '../../../shared/components/custom/RequiredLabelText';
// import './DocumentCreation.css'; // DELETED - Using Tailwind only
// import DocumentButtonsOverride from './DocumentButtons-override'; // FILE DELETED

function getDocCreationAccentGradient() {
  return isSaudiBrand()
    ? 'linear-gradient(135deg, #3a624e 0%, #2e452e 100%)'
    : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
}

function getDocCreationAccentSolid() {
  return isSaudiBrand() ? '#3a624e' : '#3b82f6';
}

function getDocCreationAccentBorderRgba() {
  return isSaudiBrand() ? 'rgba(110, 155, 110, 0.35)' : 'rgba(59, 130, 246, 0.3)';
}

function getDocCreationAccentShadow(alpha = 0.3) {
  return isSaudiBrand()
    ? `0 4px 12px rgba(46, 69, 46, ${alpha})`
    : `0 4px 12px rgba(59, 130, 246, ${alpha})`;
}

function getDocCreationHoverBgRgba() {
  return isSaudiBrand() ? 'rgba(110, 155, 110, 0.2)' : 'rgba(59, 130, 246, 0.2)';
}

/**
 * Résout client / fournisseur pour sauvegarde, aperçu et PDF.
 * Utilise la liste complète du contexte (allClients / allSuppliers), pas seulement le filtre « actif »,
 * pour ne pas perdre RC, téléphone et identifiants si le statut ne matche pas exactement.
 */
function resolveIsSupplierDocument(selectedTypeId, formData = {}) {
  const supplierTypes = [
    'purchase-order',
    'quote-request',
    'reception-note',
    'payment-voucher',
    'stock-entry',
  ];
  if (supplierTypes.includes(selectedTypeId)) return true;
  if (selectedTypeId === 'exit-note') {
    return !!formData.supplier && !formData.client;
  }
  if (selectedTypeId === 'credit-note' || selectedTypeId === 'credit_note') {
    return formData.creditNoteParty === 'supplier';
  }
  return false;
}

function isCreditNoteDocType(typeId) {
  return typeId === 'credit-note' || typeId === 'credit_note';
}

/** Nom / fonction destinataire pour signatures « Reçue par » (fiche client ou fournisseur). */
function resolvePartnerDestinataire(partner) {
  if (!partner) return { name: '', role: '' };
  const name = String(
    partner.destinataire_name ||
      partner.destinataireName ||
      partner.contact_person ||
      partner.contact ||
      '',
  ).trim();
  const role = String(
    partner.destinataire_fonction ||
      partner.destinataireFonction ||
      partner.contact_fonction ||
      partner.contactFonction ||
      '',
  ).trim();
  return { name, role };
}

/** Libellé partenaire stocké en archive (`client` = nom affiché, y compris docs fournisseur). */
function archivePartnerLabelFromDocument(documentToEdit, isSupplierSide) {
  if (!documentToEdit) return '';
  const raw =
    (isSupplierSide
      ? documentToEdit.supplierName || documentToEdit.supplier_name
      : documentToEdit.clientName || documentToEdit.client_name) ||
    (typeof documentToEdit.client === 'string' ? documentToEdit.client : '');
  const label = String(raw || '').trim();
  return label && label !== '---' ? label : '';
}

/** Valeur attendue par CustomSelect (option.value) à partir de l'id ou du nom. */
function resolvePartnerDropdownLabel(partnerId, partnerName, partnerList, allPartners) {
  const trimmedName = String(partnerName || '').trim();
  if (trimmedName && trimmedName !== '---') {
    const byName = (partnerList || []).find(
      (p) => p.name === trimmedName || p.value === trimmedName,
    );
    if (byName) return byName.value || byName.name || trimmedName;
    return trimmedName;
  }
  if (partnerId != null && String(partnerId).trim() !== '') {
    const fromAll = (allPartners || []).find((p) => String(p.id) === String(partnerId));
    const fromList = (partnerList || []).find((p) => String(p.id) === String(partnerId));
    const p = fromAll || fromList;
    if (p) return p.value || p.name || p.company_name || '';
  }
  return '';
}

/** Remplit clientName / supplierName à l'édition (dropdown + sauvegarde). */
function hydratePartnerFieldsOnEditLoad(loaded, documentToEdit, allClients, allSuppliers) {
  if (!loaded || !documentToEdit) return;
  const docIsSupplier = Boolean(documentToEdit.isSupplier || documentToEdit.is_supplier);
  const archiveLabel = archivePartnerLabelFromDocument(documentToEdit, docIsSupplier);

  if (docIsSupplier) {
    if (loaded.supplier != null && String(loaded.supplier).trim() !== '') {
      const fromDb = (allSuppliers || []).find((s) => String(s.id) === String(loaded.supplier));
      let name = String(
        loaded.supplierName ||
          fromDb?.company_name ||
          fromDb?.name ||
          documentToEdit.supplierName ||
          documentToEdit.supplier_name ||
          archiveLabel ||
          '',
      ).trim();
      if (name === '---') name = fromDb?.company_name || fromDb?.name || '';
      loaded.supplierName = name;
    } else if (!String(loaded.supplierName || '').trim() && archiveLabel) {
      loaded.supplierName = archiveLabel;
    }
    return;
  }

  if (loaded.client != null && String(loaded.client).trim() !== '') {
    const fromDb = (allClients || []).find((c) => String(c.id) === String(loaded.client));
    let name = String(
      loaded.clientName ||
        fromDb?.company_name ||
        fromDb?.name ||
        documentToEdit.clientName ||
        documentToEdit.client_name ||
        archiveLabel ||
        '',
    ).trim();
    if (name === '---') name = fromDb?.company_name || fromDb?.name || '';
    loaded.clientName = name;
  } else if (!String(loaded.clientName || '').trim() && archiveLabel) {
    loaded.clientName = archiveLabel;
  }
}

function resolveDocumentPartner(formData, isSupplierDocument, allClients, allSuppliers) {
  if (isSupplierDocument) {
    const id = formData.supplier;
    const hasId = id != null && String(id).trim() !== '';
    const fromDb = hasId
      ? (allSuppliers || []).find((s) => String(s.id) === String(id))
      : null;
    if (fromDb) return fromDb;

    // Saisie libre (ex. bon d'entrée) : nom hors liste
    const supplierNameField = (formData.stockEntrySupplier || formData.supplierName || '').trim();
    if (supplierNameField) {
      return {
        id,
        name: supplierNameField,
        company_name: supplierNameField,
        address: formData.supplierAddress || formData.deliveryAddress || '',
        city: formData.supplierCity || '',
        fiscal_id: formData.fiscalId || formData.supplierFiscalId || formData.nif || '',
        nif: formData.nif || formData.fiscalId || formData.supplierFiscalId || '',
        article: formData.article || '',
        rc: formData.rc || formData.supplierRc || '',
        rc_number: formData.rc || formData.supplierRc || '',
        phone: formData.supplierPhone || formData.contactPhone || '',
        phone1: formData.supplierPhone || formData.contactPhone || '',
        nis: formData.nis || '',
        nis_number: formData.nis || '',
        email: formData.supplierEmail || formData.email || '',
      };
    }

    // Aucun fournisseur choisi : pas de données fantômes (évite l'aperçu rempli alors que le champ est vide)
    return {
      id: '',
      name: '---',
      company_name: '---',
      address: '',
      city: '',
      fiscal_id: '',
      nif: '',
      article: '',
      rc: '',
      rc_number: '',
      phone: '',
      phone1: '',
      nis: '',
      nis_number: '',
      email: ''
    };
  }
  const id = formData.client;
  const fromDb = (allClients || []).find((c) => String(c.id) === String(id));
  if (fromDb) return fromDb;
  return {
    id,
    name: formData.clientName || '---',
    address: formData.clientAddress || formData.deliveryAddress || '',
    city: formData.clientCity || '',
    fiscal_id: formData.fiscalId || formData.nif || '',
    nif: formData.nif || formData.fiscalId || '',
    article: formData.article || '',
    rc: formData.rc || '',
    rc_number: formData.rc || '',
    phone: formData.clientPhone || formData.contactPhone || '',
    phone1: formData.clientPhone || formData.contactPhone || '',
    nis: formData.nis || '',
    nis_number: formData.nis || '',
    email: formData.clientEmail || formData.email || '',
  };
}

/** Persistance archives : coches infos complémentaires + ordre des sections (state React séparé) — colonne validity_notes : devis, proforma, facture, livraison, sortie matériel, reçu de paiement, retour client, note de crédit. */
const QAWAEM_DOC_UI_NOTE_PREFIX = 'QAWAEM_UI_JSON:';

function encodeDocumentUiOverrides(fd, supplementarySectionOrder, taxOrderState) {
  const order =
    Array.isArray(supplementarySectionOrder) && supplementarySectionOrder.length > 0
      ? supplementarySectionOrder
      : Array.isArray(fd.sectionOrder)
        ? fd.sectionOrder
        : null;
  const taxOrder =
    Array.isArray(taxOrderState) && taxOrderState.length > 0
      ? taxOrderState
      : Array.isArray(fd.taxOrder)
        ? fd.taxOrder
        : null;
  const payload = {
    showNotes: fd.showNotes,
    showConditions: fd.showConditions,
    showObjectif: fd.showObjectif,
    showObservations: fd.showObservations,
    showGaranties: fd.showGaranties,
    showValidite: fd.showValidite,
    showMotif: fd.showMotif,
    showInstructions: fd.showInstructions,
    showEcheancier: fd.showEcheancier,
    showSupplementarySections: fd.showSupplementarySections !== false,
    showBonPour: fd.showBonPour,
    showEtabliePar: fd.showEtabliePar,
    showRecuePar: fd.showRecuePar,
    sectionOrder: order,
    taxOrder,
    showTva: fd.showTva,
    showTap: fd.showTap,
    showTimbre: fd.showTimbre,
    showRemise: fd.showRemise,
    showRabais: fd.showRabais,
    showEscompte: fd.showEscompte,
    showTransport: fd.showTransport,
    showRetour: fd.showRetour,
    showAcompte: fd.showAcompte,
    showAvance: fd.showAvance,
    showGarantie: fd.showGarantie,
    showIrg: fd.showIrg,
    showIbs: fd.showIbs,
    tvaRate: fd.tvaRate,
    tapRate: fd.tapRate,
    remise: fd.remise,
    rabais: fd.rabais,
    escompte: fd.escompte,
    transport: fd.transport,
    retour: fd.retour,
    acompte: fd.acompte,
    avance: fd.avance,
    garantie: fd.garantie,
    irg: fd.irg,
    ibs: fd.ibs,
    etablieParName: fd.etablieParName || '',
    etablieParRole: fd.etablieParRole || '',
    etablieParDate: fd.etablieParDate || '',
    etablieParPlace: fd.etablieParPlace || '',
    recueParName: fd.recueParName || '',
    recueParRole: fd.recueParRole || '',
    recueParDate: fd.recueParDate || '',
    recueParPlace: fd.recueParPlace || '',
    invoiceType: fd.invoiceType || '',
    shipmentCarrierTrackingNumber: fd.shipmentCarrierTrackingNumber || '',
    carrierTrackingNumber: fd.carrierTrackingNumber || '',
  };
  return QAWAEM_DOC_UI_NOTE_PREFIX + JSON.stringify(payload);
}

function decodeDocumentUiOverrides(note) {
  const s = String(note ?? '');
  if (!s.startsWith(QAWAEM_DOC_UI_NOTE_PREFIX)) return null;
  try {
    return JSON.parse(s.slice(QAWAEM_DOC_UI_NOTE_PREFIX.length));
  } catch {
    return null;
  }
}

const INVOICE_PAYMENT_CONDITION_SLUGS = new Set([
  'comptant',
  '30_jours_fin_mois',
  '30_jours_nets',
  '45_jours_fin_mois',
  '60_jours_fin_mois',
  '90_jours',
  'sur_facturation',
  'echelonne',
  '30_jours',
  '60_jours',
  '50_50',
  'echeance',
  'immediat',
]);

/** Slug liste déroulante facture — exclut le texte complémentaire devis collé par erreur. */
export function isInvoicePaymentConditionSlug(v) {
  if (v == null || v === '') return false;
  const s = String(v).trim();
  if (!s || s.length > 64 || s.includes('\n') || s.startsWith('- ')) return false;
  if (
    s.includes('Tarifs préférentiels') ||
    s.includes('Garantie 12 mois') ||
    s.includes('Observations particulières')
  ) {
    return false;
  }
  const key = s.toLowerCase().replace(/\s+/g, '_');
  if (INVOICE_PAYMENT_CONDITION_SLUGS.has(key)) return true;
  return /^[a-z0-9_]+$/.test(key) && key.length <= 40;
}

const QUOTE_TO_INVOICE_PAYMENT_CONDITIONS_MAP = {
  comptant: 'comptant',
  '15': '30_jours_nets',
  '30': '30_jours_nets',
  '45': '45_jours_fin_mois',
  '60': '60_jours_fin_mois',
  '90': '90_jours',
  acompte_30: 'echelonne',
  acompte_50: 'echelonne',
};

/** Devis / proforma → facture : mappe la liste déroulante devis vers slug facture. */
export function mapQuotePaymentTermsToInvoicePaymentConditions(value) {
  if (value == null || String(value).trim() === '') return '';
  const key = String(value).trim().toLowerCase().replace(/\s+/g, '_');
  if (QUOTE_TO_INVOICE_PAYMENT_CONDITIONS_MAP[key]) {
    return QUOTE_TO_INVOICE_PAYMENT_CONDITIONS_MAP[key];
  }
  if (INVOICE_PAYMENT_CONDITION_SLUGS.has(key)) return key;
  return '';
}

/** Facture archives : `conditions` SQLite = conditions de paiement (pas le bloc complémentaire devis). */
export function resolveInvoicePaymentConditionsFromArchive(doc) {
  if (!doc || typeof doc !== 'object') return '';
  const raw =
    doc.paymentConditions ??
    doc.payment_conditions ??
    doc.conditions ??
    '';
  return isInvoicePaymentConditionSlug(raw) ? String(raw).trim() : '';
}

const UI_OVERRIDE_BOOL_KEYS = [
  'showNotes',
  'showConditions',
  'showObjectif',
  'showObservations',
  'showGaranties',
  'showValidite',
  'showMotif',
  'showInstructions',
  'showEcheancier',
  'showSupplementarySections',
  'showBonPour',
  'showEtabliePar',
  'showRecuePar',
];

const UI_OVERRIDE_STR_KEYS = [
  'etablieParName',
  'etablieParRole',
  'etablieParDate',
  'etablieParPlace',
  'recueParName',
  'recueParRole',
  'recueParDate',
  'recueParPlace',
  'shipmentCarrierTrackingNumber',
  'carrierTrackingNumber',
];

const TAX_OVERRIDE_BOOL_KEYS = [
  'showTva',
  'showTap',
  'showTimbre',
  'showRemise',
  'showRabais',
  'showEscompte',
  'showTransport',
  'showRetour',
  'showAcompte',
  'showAvance',
  'showGarantie',
  'showIrg',
  'showIbs',
];

const TAX_OVERRIDE_NUM_KEYS = [
  'tvaRate',
  'tapRate',
  'remise',
  'rabais',
  'escompte',
  'transport',
  'retour',
  'acompte',
  'avance',
  'garantie',
  'irg',
  'ibs',
];

function resolveShowBonPourFromArchive(doc) {
  if (doc?.showBonPour === false) return false;
  if (doc?.show_bon_pour === 0) return false;
  if (doc?.show_bon_pour != null) return Boolean(doc.show_bon_pour);
  if (doc?.showBonPour != null) return Boolean(doc.showBonPour);
  return true;
}

function applyDecodedUiOverridesToFormData(loadedFormData, dec) {
  if (!dec || typeof dec !== 'object') return;
  UI_OVERRIDE_BOOL_KEYS.forEach((k) => {
    if (typeof dec[k] === 'boolean') loadedFormData[k] = dec[k];
  });
  TAX_OVERRIDE_BOOL_KEYS.forEach((k) => {
    if (typeof dec[k] === 'boolean') loadedFormData[k] = dec[k];
  });
  UI_OVERRIDE_STR_KEYS.forEach((k) => {
    if (dec[k] != null && String(dec[k]).trim() !== '') loadedFormData[k] = dec[k];
  });
  TAX_OVERRIDE_NUM_KEYS.forEach((k) => {
    if (dec[k] != null && dec[k] !== '' && !Number.isNaN(Number(dec[k]))) {
      loadedFormData[k] = Number(dec[k]);
    }
  });
  if (dec.invoiceType != null && String(dec.invoiceType).trim() !== '') {
    loadedFormData.invoiceType = String(dec.invoiceType).trim();
  }
}

/** Clé canonique type document (taxes / conversion). */
export function canonicalDocTypeKey(type) {
  return String(type ?? '')
    .trim()
    .replace(/-/g, '_')
    .toLowerCase();
}

const TAX_SHOW_KEYS = [
  'showTva',
  'showTap',
  'showTimbre',
  'showRemise',
  'showRabais',
  'showEscompte',
  'showTransport',
  'showRetour',
  'showAcompte',
  'showAvance',
  'showGarantie',
  'showIrg',
  'showIbs',
];

const TAX_ELEMENT_UI_TO_SHOW = {
  TVA: 'showTva',
  TAP: 'showTap',
  Timbre: 'showTimbre',
  Remise: 'showRemise',
  Rabais: 'showRabais',
  Escompte: 'showEscompte',
  Transport: 'showTransport',
  Retour: 'showRetour',
  Acompte: 'showAcompte',
  Avance: 'showAvance',
  Garantie: 'showGarantie',
  IRG: 'showIrg',
  IBS: 'showIbs',
};

const TAX_ELEMENT_VALUE_KEYS = {
  showTva: ['tvaRate', 'tva_rate'],
  showTap: ['tapRate', 'tap_rate'],
  showTimbre: ['timbre', 'stamp', 'stampDuty', 'stamp_duty'],
  showRemise: ['remise', 'discount', 'discountAmount', 'discount_amount'],
  showRabais: ['rabais'],
  showEscompte: ['escompte', 'earlyPaymentDiscount', 'early_payment_discount'],
  showTransport: ['transport', 'transportCost', 'transport_cost'],
  showRetour: ['retour', 'returnCost', 'return_cost'],
  showAcompte: ['acompte', 'downPayment', 'down_payment'],
  showAvance: ['avance'],
  showGarantie: ['garantie', 'guarantee'],
  showIrg: ['irg'],
  showIbs: ['ibs'],
};

const COMMERCIAL_DOC_KEYS = new Set([
  'quote',
  'proforma',
  'invoice',
  'delivery',
  'delivery_note',
  'material_exit',
  'purchase_order',
  'return_slip',
  'client_return_slip',
  'credit_note',
]);

const LOGISTICS_DOC_KEYS = new Set([
  'reception_note',
  'receipt_note',
  'stock_entry',
  'exit_slip',
  'exit_note',
  'transfer_slip',
  'transfer_note',
]);

const PAYMENT_DOC_KEYS = new Set([
  'payment_voucher',
  'payment_slip',
  'payment_receipt',
]);

const SHIPPING_DOC_KEYS = new Set(['shipping_slip', 'shipping']);

const COMMERCIAL_REQUEST_DOC_KEYS = new Set([
  'quote_request',
  'proforma_request',
]);

export function getDocumentTaxFamily(typeId) {
  const key = canonicalDocTypeKey(typeId);
  if (SHIPPING_DOC_KEYS.has(key)) return 'shipping';
  if (LOGISTICS_DOC_KEYS.has(key)) return 'logistics';
  if (PAYMENT_DOC_KEYS.has(key)) return 'payment';
  if (COMMERCIAL_REQUEST_DOC_KEYS.has(key)) return 'commercialRequest';
  if (COMMERCIAL_DOC_KEYS.has(key)) return 'commercial';
  return 'other';
}

/** Types sans bloc Calculs & Remises sur le formulaire. */
export function hasCalculsRemisesSection(typeId) {
  const key = canonicalDocTypeKey(typeId);
  const hidden = new Set([
    'reception_note',
    'receipt_note',
    'payment_voucher',
    'payment_slip',
    'shipping_slip',
    'shipping',
    'payment_receipt',
    'exit_slip',
    'exit_note',
    'transfer_slip',
    'transfer_note',
    'stock_entry',
    'quote_request',
    'proforma_request',
  ]);
  return !hidden.has(key);
}

export function getTaxAvailability(typeId) {
  const key = canonicalDocTypeKey(typeId);
  const isCn = key === 'credit_note';
  const inList = (keys) => keys.includes(key) || keys.some((k) => canonicalDocTypeKey(k) === key);

  return (taxElement) => {
    if (isSaudiBrand() && ['TAP', 'Timbre', 'IRG', 'IBS', 'Rabais', 'Escompte'].includes(taxElement)) {
      return false;
    }
    switch (taxElement) {
      case 'TVA':
        return (
          isCn ||
          inList([
            'quote_request',
            'proforma_request',
            'purchase_order',
            'return_slip',
            'quote',
            'proforma',
            'invoice',
            'delivery',
            'delivery_note',
            'material_exit',
            'client_return_slip',
          ])
        );
      case 'TAP':
        return (
          isCn ||
          inList([
            'quote_request',
            'proforma_request',
            'purchase_order',
            'quote',
            'proforma',
            'invoice',
            'delivery',
            'delivery_note',
            'material_exit',
          ])
        );
      case 'Timbre':
        return (
          isCn ||
          inList(['proforma', 'invoice', 'delivery', 'delivery_note', 'material_exit'])
        );
      case 'Remise':
        return (
          isCn ||
          inList([
            'quote_request',
            'proforma_request',
            'purchase_order',
            'return_slip',
            'quote',
            'proforma',
            'invoice',
            'delivery',
            'delivery_note',
            'material_exit',
            'client_return_slip',
          ])
        );
      case 'Rabais':
        return (
          isCn ||
          inList([
            'quote_request',
            'proforma_request',
            'purchase_order',
            'return_slip',
            'quote',
            'proforma',
            'invoice',
            'delivery',
            'delivery_note',
            'material_exit',
            'client_return_slip',
          ])
        );
      case 'Escompte':
        return inList(['purchase_order', 'quote', 'proforma', 'invoice']);
      case 'Transport':
        return inList([
          'quote_request',
          'proforma_request',
          'purchase_order',
          'quote',
          'proforma',
          'invoice',
          'delivery',
          'delivery_note',
          'material_exit',
          'shipping_slip',
          'shipping',
        ]);
      case 'Retour':
        return inList(['return_slip', 'client_return_slip']);
      case 'Acompte':
        return inList(['purchase_order', 'proforma', 'invoice']);
      case 'Avance':
        return inList(['purchase_order', 'proforma', 'invoice']);
      case 'Garantie':
        return inList([
          'purchase_order',
          'quote',
          'proforma',
          'invoice',
          'delivery',
          'delivery_note',
        ]);
      case 'IRG':
        return inList(['invoice', 'proforma', 'payment_voucher', 'receipt']);
      case 'IBS':
        return inList(['invoice', 'proforma', 'purchase_order', 'payment_voucher']);
      default:
        return false;
    }
  };
}

/** Defaults Calculs & Remises par type (coches ON à la création). */
export function getTaxDefaults(typeId) {
  const key = canonicalDocTypeKey(typeId);
  const off = Object.fromEntries(TAX_SHOW_KEYS.map((k) => [k, false]));
  const tvaOn = { ...off, showTva: true, tvaRate: getBrandVatRate() };

  switch (key) {
    case 'invoice':
    case 'quote':
    case 'proforma':
    case 'purchase_order':
    case 'credit_note':
    case 'return_slip':
    case 'client_return_slip':
    case 'quote_request':
    case 'proforma_request':
      return tvaOn;
    case 'delivery':
    case 'delivery_note':
    case 'material_exit':
      return { ...tvaOn, showTap: false, showTimbre: false, showRemise: false, showRabais: false, showTransport: false };
    case 'shipping_slip':
    case 'shipping':
      return { ...tvaOn, showTransport: true, showTap: false, showRemise: false, showRabais: false, showEscompte: false, showTimbre: false };
    case 'reception_note':
    case 'receipt_note':
    case 'stock_entry':
    case 'exit_slip':
    case 'exit_note':
    case 'transfer_slip':
    case 'transfer_note':
    case 'payment_voucher':
    case 'payment_slip':
    case 'payment_receipt':
      return off;
    default:
      return off;
  }
}

function readTaxShowFlag(doc, showKey) {
  if (!doc) return false;
  if (doc[showKey] === false) return false;
  const snake = showKey.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`);
  if (doc[snake] === 0 || doc[snake] === false) return false;
  if (doc[showKey] === true) return true;
  if (doc[snake] != null) return Boolean(doc[snake]);
  if (showKey === 'showTva') {
    const tvaAmt = parseFloat(doc?.totalTva ?? doc?.total_tva ?? doc?.tax_amount);
    if (Number.isFinite(tvaAmt) && tvaAmt > 0) return true;
  }
  if (showKey === 'showTap') {
    const tapAmt = parseFloat(doc?.totalTap ?? doc?.total_tap);
    if (Number.isFinite(tapAmt) && tapAmt > 0) return true;
  }
  return false;
}

function copyTaxValueFields(newDoc, sourceDoc, showKey) {
  const keys = TAX_ELEMENT_VALUE_KEYS[showKey] || [];
  keys.forEach((k) => {
    if (sourceDoc[k] != null && sourceDoc[k] !== '') {
      newDoc[k] = sourceDoc[k];
    }
  });
}

function sumItemsSubtotalForTax(items) {
  return (items || []).reduce((sum, item) => {
    const lineFromTotal = parseFloat(item?.total);
    if (!Number.isNaN(lineFromTotal) && lineFromTotal > 0) {
      return sum + lineFromTotal;
    }
    const price = parseFloat(item?.price ?? item?.unitPrice) || 0;
    const quantity = parseFloat(item?.quantity) || 1;
    return sum + price * quantity;
  }, 0);
}

function resolveConversionTaxMode(sourceKey, targetKey) {
  const targetFamily = getDocumentTaxFamily(targetKey);
  const sourceFamily = getDocumentTaxFamily(sourceKey);
  if (targetFamily === 'logistics' || targetFamily === 'payment') return 'forceOff';
  if (targetFamily === 'shipping') return 'shipping';
  if (targetFamily === 'commercial') {
    if (sourceFamily === 'commercial') return 'inheritCommercial';
    return 'targetDefaults';
  }
  return 'targetDefaults';
}

function applyInheritCommercialTaxProfile(newDoc, sourceDoc, targetTypeId) {
  const isAllowed = getTaxAvailability(targetTypeId);
  const defaults = getTaxDefaults(targetTypeId);

  TAX_SHOW_KEYS.forEach((showKey) => {
    const uiKey = Object.entries(TAX_ELEMENT_UI_TO_SHOW).find(([, v]) => v === showKey)?.[0];
    const allowed = uiKey ? isAllowed(uiKey) : false;
    if (!allowed) {
      newDoc[showKey] = false;
      return;
    }
    if (readTaxShowFlag(sourceDoc, showKey)) {
      newDoc[showKey] = true;
      copyTaxValueFields(newDoc, sourceDoc, showKey);
    } else if (defaults[showKey]) {
      newDoc[showKey] = true;
      copyTaxValueFields(newDoc, defaults, showKey);
    } else {
      newDoc[showKey] = false;
    }
  });

  if (newDoc.showTva && newDoc.tvaRate == null) {
    newDoc.tvaRate = pickArchiveNum(sourceDoc?.tvaRate, sourceDoc?.tva_rate) ?? defaults.tvaRate ?? getBrandVatRate();
  }
}

/** Applique le profil taxes cible après conversion archives (flags + totaux HT/TTC). */
export function applyConversionTaxProfile(newDoc, sourceDoc, targetTypeId) {
  if (!newDoc) return;
  const sourceKey = canonicalDocTypeKey(sourceDoc?.type);
  const targetKey = canonicalDocTypeKey(targetTypeId);
  const mode = resolveConversionTaxMode(sourceKey, targetKey);
  const defaults = getTaxDefaults(targetTypeId);
  const targetFamily = getDocumentTaxFamily(targetKey);

  if (mode === 'forceOff') {
    TAX_SHOW_KEYS.forEach((flag) => {
      newDoc[flag] = false;
    });
  } else if (mode === 'targetDefaults') {
    TAX_SHOW_KEYS.forEach((flag) => {
      newDoc[flag] = Boolean(defaults[flag]);
    });
    Object.keys(defaults).forEach((k) => {
      if (!k.startsWith('show') && defaults[k] != null) newDoc[k] = defaults[k];
    });
  } else if (mode === 'inheritCommercial') {
    applyInheritCommercialTaxProfile(newDoc, sourceDoc, targetTypeId);
  } else if (mode === 'shipping') {
    applyInheritCommercialTaxProfile(newDoc, sourceDoc, targetTypeId);
    if (defaults.showTransport && !readTaxShowFlag(newDoc, 'showTransport')) {
      newDoc.showTransport = true;
      if (newDoc.transport == null && defaults.transport != null) {
        newDoc.transport = defaults.transport;
      }
    }
  }

  if (targetFamily === 'payment') {
    return;
  }

  if (targetFamily === 'logistics' && !hasCalculsRemisesSection(targetTypeId)) {
    return;
  }

  const subtotal = sumItemsSubtotalForTax(newDoc.items);
  if (subtotal > 0 || hasCalculsRemisesSection(targetTypeId)) {
    persistArchiveTaxTotals(newDoc, newDoc, subtotal);
    if (targetFamily === 'commercial' || targetFamily === 'shipping') {
      newDoc.amount = newDoc.netToPay ?? newDoc.net_to_pay ?? subtotal;
    }
  }
}

export function resolveShowTvaFromArchive(doc) {
  if (doc?.showTva === false) return false;
  if (doc?.show_tva === 0) return false;
  if (doc?.show_tva != null) return Boolean(doc.show_tva);
  if (doc?.showTva != null) return Boolean(doc.showTva);
  const tvaAmt = parseFloat(doc?.totalTva ?? doc?.total_tva ?? doc?.tax_amount);
  if (Number.isFinite(tvaAmt) && tvaAmt > 0) return true;
  return true;
}

export function pickArchiveNum(...values) {
  for (const v of values) {
    if (v == null || v === '') continue;
    const n = parseFloat(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/** Restaure coches Calculs & Remises depuis colonnes SQLite quand validity_notes est absent. */
function applyTaxFieldsFromArchiveToFormData(loadedFormData, documentToEdit, docTypeUnderscore) {
  const d = documentToEdit || {};
  const typeNorm = String(docTypeUnderscore || '').replace(/_/g, '-');

  if (loadedFormData.showTva === undefined) {
    loadedFormData.showTva = resolveShowTvaFromArchive(d);
  }
  if (loadedFormData.tvaRate === undefined) {
    const rate = pickArchiveNum(d.tvaRate, d.tva_rate);
    loadedFormData.tvaRate = rate != null ? rate : getBrandVatRate();
  }

  if (loadedFormData.showTap === undefined) {
    const tapAmt = pickArchiveNum(d.totalTap, d.total_tap);
    loadedFormData.showTap = tapAmt != null && tapAmt > 0;
  }
  if (loadedFormData.tapRate === undefined && loadedFormData.showTap) {
    const rate = pickArchiveNum(d.tapRate, d.tap_rate);
    if (rate != null && rate > 0) {
      loadedFormData.tapRate = rate;
    } else {
      const ht = pickArchiveNum(d.totalHt, d.total_ht, d.subtotal);
      const tapAmt = pickArchiveNum(d.totalTap, d.total_tap);
      if (ht != null && ht > 0 && tapAmt != null && tapAmt > 0) {
        loadedFormData.tapRate = Math.round((tapAmt / ht) * 10000) / 100;
      }
    }
  }

  const inferToggleFromAmount = (showKey, valueKey, ...amountSources) => {
    if (loadedFormData[showKey] !== undefined) return;
    const amt = pickArchiveNum(...amountSources);
    if (amt != null && amt > 0) {
      loadedFormData[showKey] = true;
      if (loadedFormData[valueKey] === undefined) loadedFormData[valueKey] = amt;
    }
  };

  inferToggleFromAmount('showTransport', 'transport', d.transportCost, d.transport_cost);
  inferToggleFromAmount('showRemise', 'remise', d.discountAmount, d.discount_amount, d.discount);
  inferToggleFromAmount('showEscompte', 'escompte', d.earlyPaymentDiscount, d.early_payment_discount);
  inferToggleFromAmount('showAcompte', 'acompte', d.downPayment, d.down_payment);
  inferToggleFromAmount('showRetour', 'retour', d.returnCost, d.return_cost);
  inferToggleFromAmount('showGarantie', 'garantie', d.guarantee, d.garantie);

  const stampAmt = pickArchiveNum(d.stampDuty, d.stamp_duty, d.stamp);
  if (loadedFormData.showTimbre === undefined && stampAmt != null && stampAmt > 0) {
    loadedFormData.showTimbre = true;
  }

  if (typeNorm === 'purchase-order') {
    if (loadedFormData.showTva === undefined) loadedFormData.showTva = true;
    if (loadedFormData.tvaRate === undefined) loadedFormData.tvaRate = getBrandVatRate();
  }
}

/** Écrit total_ht / total_tva / total_tap / total_ttc / net_to_pay — colonnes archives existantes. */
export function persistArchiveTaxTotals(documentData, formData, subtotal, vatMode) {
  const f = formData || {};
  const mode = vatMode || f.vatMode || 'exclusive';
  let currentTotal = subtotal;
  const remiseRate = f.showRemise ? parseFloat(f.remise) || 0 : 0;
  currentTotal -= currentTotal * (remiseRate / 100);
  const transport = f.showTransport ? parseFloat(f.transport) || 0 : 0;
  currentTotal += transport;
  const tvaRate = f.showTva !== false ? parseFloat(f.tvaRate) || getBrandVatRate() : 0;
  const { totalHT, tvaAmount, totalTTC: ttcBeforeTap } = f.showTva !== false
    ? computeVatBreakdown(currentTotal, tvaRate, mode)
    : { totalHT: currentTotal, tvaAmount: 0, totalTTC: currentTotal };
  const tapParsed = parseFloat(f.tapRate);
  const tapRate = f.showTap && Number.isFinite(tapParsed) ? tapParsed : 0;
  const tapAmount = f.showTap ? totalHT * (tapRate / 100) : 0;
  currentTotal = ttcBeforeTap + tapAmount;
  const totalTTC = currentTotal;
  const calcTimbre = (base) => {
    if (base <= 300) return 0;
    const tranches = Math.ceil(base / 100);
    let t;
    if (base <= 30000) t = tranches * 1;
    else if (base <= 100000) t = tranches * 1.5;
    else t = tranches * 2;
    return Math.max(5, t);
  };
  const timbre = f.showTimbre ? calcTimbre(totalTTC) : 0;
  currentTotal += timbre;
  currentTotal -= f.showRabais ? parseFloat(f.rabais) || 0 : 0;
  const escompteRate = f.showEscompte ? parseFloat(f.escompte) || 0 : 0;
  currentTotal -= totalTTC * (escompteRate / 100);
  currentTotal -= f.showAcompte ? parseFloat(f.acompte) || 0 : 0;
  currentTotal -= f.showAvance ? parseFloat(f.avance) || 0 : 0;
  currentTotal -= f.showGarantie ? parseFloat(f.garantie) || 0 : 0;
  const irgRate = f.showIrg ? parseFloat(f.irg) || 0 : 0;
  currentTotal -= totalTTC * (irgRate / 100);
  const ibsRate = f.showIbs ? parseFloat(f.ibs) || 0 : 0;
  currentTotal -= totalTTC * (ibsRate / 100);
  currentTotal += f.showRetour ? parseFloat(f.retour) || 0 : 0;
  const netAPayer = currentTotal;

  documentData.subtotal = Math.round(subtotal * 100) / 100;
  documentData.totalHt = Math.round(totalHT * 100) / 100;
  documentData.total_ht = documentData.totalHt;
  documentData.totalTva = Math.round(tvaAmount * 100) / 100;
  documentData.total_tva = documentData.totalTva;
  documentData.totalTap = Math.round(tapAmount * 100) / 100;
  documentData.total_tap = documentData.totalTap;
  documentData.totalTtc = Math.round(totalTTC * 100) / 100;
  documentData.total_ttc = documentData.totalTtc;
  documentData.netToPay = Math.round(netAPayer * 100) / 100;
  documentData.net_to_pay = documentData.netToPay;
  documentData.tax_amount = documentData.totalTva;
}

const PURCHASE_ORDER_SUPPLEMENTARY_MARKERS = [
  { field: 'showEcheancier', patterns: [/échéancier/i, /echeancier/i] },
  { field: 'showNotes', patterns: [/devis fournisseur/i, /référence du devis/i, /reference du devis/i] },
  { field: 'showConditions', patterns: [/livraison sous/i, /paiement net/i] },
  { field: 'showObjectif', patterns: [/réapprovisionner le stock/i, /reapprovisionner le stock/i] },
  { field: 'showObservations', patterns: [/observations particulières/i, /observations particulieres/i] },
  { field: 'showGaranties', patterns: [/garantie fabricant/i] },
  { field: 'showValidite', patterns: [/validité/i, /validite/i, /valable/i] },
  { field: 'showMotif', patterns: [/réapprovisionnement stock/i, /reapprovisionnement stock/i, /commande prévisionnelle/i] },
];

function inferSupplementaryFlagsFromTermsText(text) {
  const s = String(text || '');
  const out = {};
  if (!s.trim()) return out;
  PURCHASE_ORDER_SUPPLEMENTARY_MARKERS.forEach(({ field, patterns }) => {
    if (patterns.some((p) => p.test(s))) out[field] = true;
  });
  return out;
}

function getPurchaseOrderSupplementaryDefaults() {
  return {
    showNotes: true,
    showConditions: true,
    showObservations: true,
    showEcheancier: true,
    showObjectif: false,
    showGaranties: false,
    showValidite: false,
    showMotif: false,
    showInstructions: false,
  };
}

/** Slugs stables pour « État des marchandises » (formulaire + PDF). */
function normalizeReturnGoodsConditionValue(v) {
  if (v === undefined || v === null || String(v).trim() === '') return '';
  const key = String(v)
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[''`]/g, '')
    .replace(/[\s–—-]+/g, '_')
    .replace(/_+/g, '_');
  const aliases = {
    bon_etat: 'bon_etat',
    etat_moyen: 'etat_moyen',
    endommage: 'endommage',
    endommagee: 'endommage',
    emballage_original: 'emballage_original',
    emballage_d_origine: 'emballage_original',
    a_recycler: 'a_recycler',
    inutilisable: 'inutilisable',
    neuf: 'neuf',
    defectueux: 'defectueux',
    incomplet: 'incomplet',
    utilise: 'utilise',
  };
  return aliases[key] || key;
}

function getReturnGoodsConditionOptions(t) {
  const fl = (key) => t(`document_creation.form.goods_conditions.${key}`);
  return [
    { value: 'emballage_original', label: fl('original_packaging') },
    { value: 'bon_etat', label: fl('good') },
    { value: 'etat_moyen', label: fl('average') },
    { value: 'endommage', label: fl('damaged') },
    { value: 'inutilisable', label: fl('unusable') },
    { value: 'a_recycler', label: fl('recyclable') },
  ];
}

/** Restaure coches infos complémentaires + signatures quand validity_notes manque ou est partiel. */
function applyFallbackUiOverridesOnEditLoad(loadedFormData, documentToEdit, docTypeUnderscore) {
  const typeNorm = String(docTypeUnderscore || '').replace(/_/g, '-');

  if (typeNorm === 'purchase-order') {
    const termsText = loadedFormData.terms || documentToEdit.terms || '';
    const inferred = inferSupplementaryFlagsFromTermsText(termsText);
    const defaults = getPurchaseOrderSupplementaryDefaults();
    const hasTerms = String(termsText).trim().length > 0;

    SUPPLEMENTARY_SECTION_TOGGLE_FIELDS.forEach((k) => {
      if (loadedFormData[k] !== undefined) return;
      if (inferred[k] !== undefined) {
        loadedFormData[k] = inferred[k];
      } else if (hasTerms && defaults[k] !== undefined) {
        loadedFormData[k] = defaults[k];
      }
    });
  }

  if (loadedFormData.showBonPour === undefined) {
    loadedFormData.showBonPour = resolveShowBonPourFromArchive(documentToEdit);
  }

  if (loadedFormData.showEtabliePar === undefined) {
    loadedFormData.showEtabliePar = Boolean(
      loadedFormData.etablieParName ||
        documentToEdit.etablieParName ||
        documentToEdit.etablie_par_name ||
        loadedFormData.issuedBy ||
        documentToEdit.issuedBy ||
        documentToEdit.issued_by,
    );
  }

  if (typeNorm === 'reception-note') {
    loadedFormData.showRecuePar = true;
    const hasPreparer = Boolean(
      loadedFormData.issuedBy ||
        documentToEdit.issuedBy ||
        documentToEdit.issued_by ||
        loadedFormData.etablieParName ||
        documentToEdit.etablieParName ||
        documentToEdit.etablie_par_name,
    );
    if (hasPreparer) {
      loadedFormData.showEtabliePar = true;
      if (!loadedFormData.etablieParName) {
        loadedFormData.etablieParName =
          loadedFormData.issuedBy ||
          documentToEdit.issuedBy ||
          documentToEdit.issued_by ||
          '';
      }
    }
  }

  if (typeNorm === 'return-slip') {
    const hasPreparer = Boolean(
      loadedFormData.issuedBy ||
        documentToEdit.issuedBy ||
        documentToEdit.issued_by ||
        loadedFormData.etablieParName ||
        documentToEdit.etablieParName ||
        documentToEdit.etablie_par_name,
    );
    if (hasPreparer) {
      loadedFormData.showEtabliePar = true;
      if (!loadedFormData.etablieParName) {
        loadedFormData.etablieParName =
          loadedFormData.issuedBy ||
          documentToEdit.issuedBy ||
          documentToEdit.issued_by ||
          '';
      }
    }
  }

  if (loadedFormData.showRecuePar === undefined) {
    const recueParFromName = Boolean(
      loadedFormData.recueParName ||
        documentToEdit.recueParName ||
        documentToEdit.recue_par_name,
    );
    if (typeNorm === 'payment-voucher') {
      loadedFormData.showRecuePar = recueParFromName;
    } else {
      loadedFormData.showRecuePar = recueParFromName || Boolean(
        documentToEdit.receivedBy ||
          documentToEdit.received_by,
      );
    }
  }
}

/** Normalise les alias archives / conversion (receipt_note → reception-note, etc.). */
function normalizeSupplementaryDocType(docTypeId) {
  return normDocTypeForArchiveTitle(docTypeId);
}

function getSupplementaryFormFieldName(docTypeId) {
  const docType = normalizeSupplementaryDocType(docTypeId);
  if (docType === 'quote' || docType === 'proforma') return 'conditions';
  if (docType === 'quote-request' || docType === 'proforma-request') return 'requirements';
  if (docType === 'purchase-order') return 'terms';
  if (docType === 'delivery-note' || docType === 'delivery' || docType === 'material-exit') return 'deliveryNotes';
  if (docType === 'shipping-slip' || docType === 'shipping') return 'instructions';
  return 'notes';
}

const SUPPLEMENTARY_SECTION_TOGGLE_FIELDS = [
  'showNotes',
  'showConditions',
  'showObjectif',
  'showObservations',
  'showGaranties',
  'showValidite',
  'showMotif',
  'showInstructions',
  'showEcheancier',
  'showSupplementarySections',
];

/** Motif(s) de retour — tableau ou chaîne « a|b » → slug pipe-delimited pour la BDD. */
function serializeReturnReasonValue(value) {
  if (value === undefined || value === null) return '';
  if (Array.isArray(value)) {
    return value.map((v) => String(v).trim()).filter(Boolean).join('|');
  }
  const s = String(value).trim();
  if (!s) return '';
  if (s.includes('|')) {
    return s
      .split('|')
      .map((v) => v.trim())
      .filter(Boolean)
      .join('|');
  }
  return s;
}

/** Champs archives bordereau retour fournisseur — réappliqués après blocs stock/transfert/expédition. */
function buildReturnSlipArchivePayload(fd) {
  const tr = (v) =>
    v != null && String(v).trim() !== '' ? String(v).trim() : '';
  const por = tr(fd.returnOriginalPurchaseOrderRef);
  const dor = tr(fd.returnOriginalDeliveryRef);
  const ivr = tr(fd.returnOriginalInvoiceRef);
  const ivd = tr(fd.returnOriginalInvoiceDate);
  const rma = tr(fd.returnRMANumber);
  const condRaw = fd.returnCondition;
  const cond =
    condRaw != null && String(condRaw).trim() !== ''
      ? normalizeReturnGoodsConditionValue(condRaw)
      : '';

  const insp = tr(fd.returnInspector);

  return {
    processingDeadline: tr(fd.processingDeadline),
    priority:
      fd.priority !== undefined && fd.priority !== null ? String(fd.priority) : '',
    issuedBy: tr(fd.issuedBy),
    returnWarehouse: tr(fd.returnWarehouse),
    returnOriginalPurchaseOrder: por,
    returnOriginalDelivery: dor,
    returnOriginalInvoice: ivr,
    returnOriginalInvoiceDate: ivd,
    return_original_invoice_date: ivd,
    returnRMANumber: rma,
    returnCarrier: tr(fd.returnCarrier),
    returnTrackingNumber: tr(fd.returnTrackingNumber),
    returnInspector: insp,
    controlledByName: insp,
    returnReason: serializeReturnReasonValue(fd.returnReason),
    returnType: tr(fd.returnType),
    returnDate: tr(fd.returnDate) || tr(fd.date),
    returnCondition: cond,
    goodsCondition: cond,
    carrier: '',
    trackingNumber: '',
    purchaseOrderReference: '',
    invoiceReference: '',
    deliveryNoteReference: '',
    originWarehouseName: '',
    destinationWarehouseName: '',
  };
}

/** Champs archives note de crédit — littéral + réparation après blocs stock/transfert/expédition. */
function buildCreditNoteArchivePayload(fd) {
  const cn = fd.creditNote || {};
  const inv = fd.originalInvoice || {};
  const nestedAmtDefined =
    inv.amount != null &&
    inv.amount !== '' &&
    !(typeof inv.amount === 'number' && Number.isNaN(inv.amount));
  return {
    priority: fd.priority ?? '',
    issuerName: fd.issuerName || '',
    creditIssuerName: fd.issuerName || '',
    issuerNif: fd.issuerNif || '',
    creditIssuerNif: fd.issuerNif || '',
    issuerAddress: fd.issuerAddress || '',
    creditIssuerAddress: fd.issuerAddress || '',
    originalInvoiceNumber: fd.originalInvoiceNumber || inv.number || '',
    originalInvoiceDate: resolveExplicitOriginalInvoiceDate(fd),
    originalInvoiceAmount: nestedAmtDefined ? inv.amount : fd.originalInvoiceAmount || 0,
    creditNoteApplicationDate: fd.creditNoteApplicationDate || cn.applicationDate || '',
    creditNoteType: fd.creditNoteType || cn.type || '',
    creditType: fd.creditNoteType || cn.type || '',
    credit_type: fd.creditNoteType || cn.type || '',
    creditNoteAmount: fd.creditNoteAmount || 0,
    creditNoteVatRate: fd.creditNoteVatRate ?? 19,
    creditNoteVatAmount: fd.creditNoteVatAmount || 0,
    creditNoteTotalAmount: fd.creditNoteTotalAmount || 0,
    creditNoteRefundMethod: fd.creditNoteRefundMethod || cn.refundMethod || '',
    refundMethod: fd.creditNoteRefundMethod || cn.refundMethod || '',
    refund_method: fd.creditNoteRefundMethod || cn.refundMethod || '',
    creditNoteRefundDelay: fd.creditNoteRefundDelay || '15_jours',
    creditNoteStatus: fd.status || fd.creditNoteStatus || 'pending',
    creditNoteApprovedBy: '',
    approvedByName: '',
    creditNoteReason:
      fd.creditNoteReason ||
      (fd.notes != null && String(fd.notes).trim() !== '' ? String(fd.notes).trim() : ''),
    creditNoteRefundConditions: fd.creditNoteRefundConditions || '',
    creditNoteParty: fd.creditNoteParty || 'client',
  };
}

/* -------------------------------------------------------------------------- */
/* Archive title (Titre) — auto from lignes / motif, pas de fichier util séparé */
/* -------------------------------------------------------------------------- */

const ARCHIVE_TITLE_MAX_LEN = 60;
/** Aperçu Archives : titre complet (pas de « … » alors qu'il reste de la place). */
export const ARCHIVE_TITLE_PREVIEW_MAX_LEN = 240;
/** Moins de 4 lignes → désignations listées ; 4 ou plus → « N articles divers ». */
const ARCHIVE_TITLE_DIVERSE_MIN_LINES = 4;

const ARCHIVE_TITLE_LOCALE_STRINGS = {
  ar: {
    quoteRequest: 'طلب عرض أسعار',
    proformaRequest: 'طلب فاتورة أولية',
    order: 'أمر شراء',
    supplierReturn: 'إرجاع للمورد',
    paymentVoucher: 'سند صرف',
    quote: 'عرض سعر',
    proforma: 'فاتورة أولية',
    invoice: 'فاتورة',
    delivery: 'بون تسليم',
    paymentReceipt: 'سند قبض',
    clientReturn: 'إرجاع العميل',
    creditNote: 'إشعار دائن',
    creditNoteSupplier: 'إشعار دائن (مورد)',
    creditNoteClient: 'إشعار دائن (عميل)',
    reception: 'إشعار استلام',
    stockEntry: 'إدخال مخزون',
    stockExit: 'صرف مخزون',
    transfer: 'نقل',
    shipping: 'شحن',
    sale: 'بيع',
    purchase: 'شراء',
    neutral: 'بنود',
    payment: 'دفع',
    paymentLines: (n) => `${n} بند${n > 1 ? '' : ''}`,
    draft: '(مسودة)',
    diverse: (n) => `${n} أصناف متنوعة`,
    itemsHeader: (n) => `${n} أصناف`,
    returnOf: 'إرجاع',
    returnItemsReturned: (n) => `${n} صنف مُرجَع`,
    separator: ' — ',
  },
};

/** Préfixe de titre auto « Type — résumé » par type de document (Facturation + Inventaire). */
const ARCHIVE_TITLE_PREFIX_KEY_BY_TYPE = {
  'quote-request': 'quoteRequest',
  'proforma-request': 'proformaRequest',
  'purchase-order': 'order',
  'return-slip': 'supplierReturn',
  'payment-voucher': 'paymentVoucher',
  quote: 'quote',
  proforma: 'proforma',
  invoice: 'invoice',
  'delivery-note': 'delivery',
  delivery: 'delivery',
  'material-exit': 'stockExit',
  'payment-receipt': 'paymentReceipt',
  'client-return-slip': 'clientReturn',
  'credit-note': 'creditNote',
  'reception-note': 'reception',
  'stock-entry': 'stockEntry',
  'exit-note': 'stockExit',
  'transfer-note': 'transfer',
  'shipping-slip': 'shipping',
  shipping: 'shipping',
};

function getArchiveTitlePrefixKey(type) {
  const id = normDocTypeForArchiveTitle(type);
  return ARCHIVE_TITLE_PREFIX_KEY_BY_TYPE[id] || null;
}

function usesAutoArchiveTitle(type) {
  return Boolean(getArchiveTitlePrefixKey(type));
}

function formatArchiveTitleWithPrefix(type, core, locale, maxLen = ARCHIVE_TITLE_MAX_LEN) {
  const trimmed = core != null ? String(core).trim().replace(/^[—–\-:\s]+/, '') : '';
  const prefixKey = getArchiveTitlePrefixKey(type);
  if (!prefixKey) return truncateArchiveTitle(trimmed, maxLen);
  const prefix = archiveTitleStr(locale, prefixKey);
  const draft = archiveTitleStr(locale, 'draft');
  if (!trimmed || trimmed === draft || isDraftOnlyArchiveTitle(trimmed, locale)) {
    return truncateArchiveTitle(prefix, maxLen);
  }
  const sep = archiveTitleStr(locale, 'separator');
  return truncateArchiveTitle(`${prefix}${sep}${trimmed}`, maxLen);
}

function isSupplierCreditNoteArchiveForm(formData) {
  if (!formData || typeof formData !== 'object') return false;
  if (
    formData.creditNoteParty === 'supplier' ||
    formData.credit_note_party === 'supplier' ||
    formData.isSupplier ||
    formData.is_supplier
  ) {
    return true;
  }
  const num = String(formData.number ?? '').trim().toUpperCase();
  return /^AVO[-_]/.test(num);
}

function creditNoteArchiveTitlePrefixKey(formData) {
  return isSupplierCreditNoteArchiveForm(formData) ? 'creditNoteSupplier' : 'creditNoteClient';
}

function formatCreditNoteArchiveTitleWithPrefix(formData, core, locale, maxLen = ARCHIVE_TITLE_MAX_LEN) {
  const trimmed = core != null ? String(core).trim().replace(/^[—–\-:\s]+/, '') : '';
  const prefixKey = creditNoteArchiveTitlePrefixKey(formData);
  const prefix = archiveTitleStr(locale, prefixKey);
  if (!trimmed) return truncateArchiveTitle(prefix, maxLen);
  const sep = archiveTitleStr(locale, 'separator');
  return truncateArchiveTitle(`${prefix}${sep}${trimmed}`, maxLen);
}

function archiveTitleStr(_locale, key) {
  const L = ARCHIVE_TITLE_LOCALE_STRINGS.ar;
  return L[key];
}

function normDocTypeForArchiveTitle(type) {
  if (!type && type !== 0) return '';
  const raw = String(type).trim();
  const s = raw.includes('_') ? raw.replace(/_/g, '-') : raw;
  const map = {
    'quote-request': 'quote-request',
    'proforma-request': 'proforma-request',
    'purchase-order': 'purchase-order',
    quote_request: 'quote-request',
    proforma_request: 'proforma-request',
    purchase_order: 'purchase-order',
    delivery_note: 'delivery-note',
    'delivery-note': 'delivery-note',
    delivery: 'delivery-note',
    material_exit: 'material-exit',
    'material-exit': 'material-exit',
    return_slip: 'return-slip',
    'return-slip': 'return-slip',
    payment_voucher: 'payment-voucher',
    'payment-voucher': 'payment-voucher',
    payment_slip: 'payment-voucher',
    'payment-slip': 'payment-voucher',
    credit_note: 'credit-note',
    'credit-note': 'credit-note',
    client_return_slip: 'client-return-slip',
    'client-return-slip': 'client-return-slip',
    payment_receipt: 'payment-receipt',
    'payment-receipt': 'payment-receipt',
    /** Aligné Archives / TemplateWrapper (`receipt_note` stocké à réception PDF). */
    'receipt-note': 'reception-note',
    receipt_note: 'reception-note',
    reception_note: 'reception-note',
    'reception-note': 'reception-note',
    'exit-slip': 'exit-note',
    exit_slip: 'exit-note',
    'exit-note': 'exit-note',
    'transfer-slip': 'transfer-note',
    transfer_slip: 'transfer-note',
    'transfer-note': 'transfer-note',
    stock_entry: 'stock-entry',
    'stock-entry': 'stock-entry',
    shipping_slip: 'shipping-slip',
    'shipping-slip': 'shipping-slip',
    shipping: 'shipping-slip',
  };
  return map[s] || s;
}

function truncateArchiveTitle(text, max = ARCHIVE_TITLE_MAX_LEN) {
  if (text == null || text === '') return '';
  const t = String(text).trim().replace(/\s+/g, ' ');
  if (t.length <= max) return t;
  return `${t.slice(0, Math.max(0, max - 1))}…`;
}

function firstMeaningfulLineArchiveTitle(text, max = ARCHIVE_TITLE_MAX_LEN) {
  if (!text || typeof text !== 'string') return '';
  const line = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .find((l) => l.length > 0);
  if (!line) return '';
  return truncateArchiveTitle(line, max);
}

function looksLikeCatalogId(value) {
  const s = value != null ? String(value).trim() : '';
  if (!s) return false;
  return /^(PRD|SRV|PRO|CLI|FRN|CAT|DOC|EMP|WH)-[\w-]+$/i.test(s);
}

function getArchiveTitleItemLabel(item) {
  if (!item || typeof item !== 'object') return '';
  const candidates = [
    item.name,
    item.designation,
    item.description,
    item.product_name,
    item.manualProductName,
    item.title,
    item.label,
    item.reference,
  ];
  for (const c of candidates) {
    const t = c != null ? String(c).trim() : '';
    if (t && !looksLikeCatalogId(t)) return t;
  }
  const product = item.product != null ? String(item.product).trim() : '';
  if (product && !looksLikeCatalogId(product)) return product;
  return '';
}

function isNonEmptyArchiveLineItem(item) {
  const label = getArchiveTitleItemLabel(item);
  if (label) return true;
  const q = parseFloat(item?.quantity);
  if (!Number.isNaN(q) && q > 0) return true;
  return false;
}

function normalizeArchiveTitleItems(items) {
  if (!Array.isArray(items)) return [];
  return items.filter(isNonEmptyArchiveLineItem);
}

function archiveTitleFormatItemPart(item) {
  const label = getArchiveTitleItemLabel(item);
  if (!label) return '';
  const qty = parseFloat(item.quantity);
  const qtyStr = !Number.isNaN(qty) && qty !== 1 && qty !== 0 ? `${Math.round(qty)}× ` : '';
  return `${qtyStr}${label}`;
}

function archiveTitleFormatSpecPart(row) {
  if (!row || typeof row !== 'object') return '';
  const desc =
    row.description || row.title || row.label
      ? String(row.description || row.title || row.label).trim()
      : '';
  if (!desc) return '';
  const qty = row.quantity != null ? parseFloat(row.quantity) : NaN;
  const qtyStr = !Number.isNaN(qty) && qty > 0 ? `${Math.round(qty)}× ` : '';
  return `${qtyStr}${desc}`;
}

function normalizeArchiveTitleSpecs(specs) {
  if (!Array.isArray(specs)) return [];
  return specs.filter((row) => archiveTitleFormatSpecPart(row));
}

/** Résumé lignes titre : < 4 → toutes les lignes ; ≥ 4 → « N articles divers ». */
function summarizeArchiveTitleLineLabels(entries, locale, maxLen, formatPart) {
  const L = archiveTitleStr.bind(null, locale);
  const parts = [];
  for (const entry of entries) {
    const p = formatPart(entry);
    if (p) parts.push(p);
  }
  if (parts.length === 0) return '';
  if (parts.length >= ARCHIVE_TITLE_DIVERSE_MIN_LINES) {
    return L('diverse')(parts.length);
  }
  return truncateArchiveTitle(parts.join(', '), maxLen);
}

function summarizeTypedArchiveTitle(
  items,
  documentType,
  locale,
  fallbackTypeLabel = '',
  maxLen = ARCHIVE_TITLE_MAX_LEN,
) {
  const id = normDocTypeForArchiveTitle(documentType);
  const prefixKey = getArchiveTitlePrefixKey(id);
  if (!prefixKey) return '';
  const L = archiveTitleStr.bind(null, locale);
  const prefix = L(prefixKey);
  const sep = L('separator');
  const arr = normalizeArchiveTitleItems(items);

  if (arr.length === 0) {
    const draft = fallbackTypeLabel
      ? `${fallbackTypeLabel} ${L('draft')}`.trim()
      : L('draft');
    return truncateArchiveTitle(`${prefix}${sep}${draft}`, maxLen);
  }

  const maxCoreLen = maxLen - prefix.length - sep.length;
  const core = summarizeArchiveTitleLineLabels(
    arr,
    locale,
    maxCoreLen,
    archiveTitleFormatItemPart,
  );

  return truncateArchiveTitle(`${prefix}${sep}${truncateArchiveTitle(core, maxCoreLen)}`, maxLen);
}

function isPaymentStyleArchiveTitleDoc(t) {
  const id = normDocTypeForArchiveTitle(t);
  return id === 'payment-voucher' || id === 'payment-receipt' || id === 'payment_slip' || id === 'payment_receipt';
}

function getSupplementaryMotifArchiveTitle(formData, docType) {
  const id = normDocTypeForArchiveTitle(docType);
  if (id === 'credit-note' || id === 'credit_note') return '';
  if (id === 'quote' || id === 'proforma') return formData.conditions || '';
  if (id === 'purchase-order') return formData.terms || '';
  if (id === 'delivery-note' || id === 'delivery' || id === 'shipping-slip' || id === 'shipping')
    return formData.deliveryNotes || '';
  if (id === 'quote-request' || id === 'proforma-request') return formData.requirements || '';
  if (id === 'attachment') return formData.notes || '';
  return formData.notes || '';
}

function summarizeProductLinesArchiveTitle(items, mode, locale, fallbackTypeLabel = '') {
  const arr = normalizeArchiveTitleItems(items);
  const L = archiveTitleStr.bind(null, locale);

  if (arr.length === 0) {
    if (fallbackTypeLabel) return truncateArchiveTitle(`${fallbackTypeLabel} ${L('draft')}`.trim(), ARCHIVE_TITLE_MAX_LEN);
    return L('draft');
  }

  const prefix =
    mode === 'purchase' ? `${L('purchase')} ` : mode === 'sale' ? `${L('sale')} ` : '';

  const core = summarizeArchiveTitleLineLabels(
    arr,
    locale,
    ARCHIVE_TITLE_MAX_LEN - prefix.length,
    archiveTitleFormatItemPart,
  );

  const result = `${prefix}${core}`.trim();
  return truncateArchiveTitle(result, ARCHIVE_TITLE_MAX_LEN);
}

function summarizeSpecificationsArchiveTitle(specs, locale, maxLen = ARCHIVE_TITLE_MAX_LEN) {
  const normalized = normalizeArchiveTitleSpecs(specs);
  return summarizeArchiveTitleLineLabels(
    normalized,
    locale,
    maxLen,
    archiveTitleFormatSpecPart,
  );
}

function mergeArchiveTitleParts(a, b, locale, maxLen = ARCHIVE_TITLE_MAX_LEN) {
  if (a && b) {
    const S = archiveTitleStr(locale, 'separator');
    return truncateArchiveTitle(`${a}${S}${b}`, maxLen);
  }
  return a || b || '';
}

function generateTitleFromPaymentLinesArchive(paymentLines, documentType, locale) {
  const lines = Array.isArray(paymentLines)
    ? paymentLines.filter(
        (r) => r && (r.description || r.reference || r.amount != null),
      )
    : [];
  const L = archiveTitleStr.bind(null, locale);
  const sep = L('separator');
  const prefixKey =
    normDocTypeForArchiveTitle(documentType) === 'payment-receipt'
      ? 'paymentReceipt'
      : 'paymentVoucher';
  const prefix = L(prefixKey);

  const core = summarizePaymentDocumentTitleCore(lines, ARCHIVE_TITLE_MAX_LEN - prefix.length - sep.length);
  if (core) return formatArchiveTitleWithPrefix(documentType, core, locale);

  return truncateArchiveTitle(`${prefix}${sep}${L('draft')}`, ARCHIVE_TITLE_MAX_LEN);
}

/** Titre BRF : motif de retour (détails doc) + nb lignes actives ; sinon « Retour de … ». */
function generateReturnSlipArchiveTitleCore(formData, locale, maxLen = ARCHIVE_TITLE_MAX_LEN) {
  const L = archiveTitleStr.bind(null, locale);
  const items = normalizeArchiveTitleItems(formData.items || []);
  const count = items.length;
  const countLabel = count > 0 ? L('returnItemsReturned')(count) : '';
  const motifLabel = formatReturnReasons(formData.returnReason || formData.return_reason || '');

  if (motifLabel) {
    const core = countLabel ? `${motifLabel} — ${countLabel}` : motifLabel;
    return truncateArchiveTitle(core, maxLen);
  }

  const returnOf = L('returnOf');
  if (count === 0) return L('draft');

  const lineCore = summarizeArchiveTitleLineLabels(
    items,
    locale,
    maxLen,
    archiveTitleFormatItemPart,
  );
  if (!lineCore) return truncateArchiveTitle(countLabel, maxLen);
  return truncateArchiveTitle(`${returnOf} ${lineCore}`, maxLen);
}

function isTemplatePlaceholderArchiveTitleLine(line) {
  const s = String(line || '')
    .trim()
    .replace(/^[-–—•]\s*/, '')
    .toLowerCase();
  if (!s) return true;
  return (
    s.includes('notes générales du document') ||
    s.includes('notes generales du document') ||
    s.includes('conditions générales applicables') ||
    s.includes('motif: [à préciser]') ||
    s === 'motif: [à preciser]' ||
    s.includes('[يُحدَّد لاحقاً]') ||
    s.includes('[يُحدد لاحقاً]') ||
    s.includes('ملاحظات على') ||
    s.includes('ملاحظات خاصة')
  );
}

function formatCreditNoteTypeArchiveTitle(typeValue) {
  return mapPreviewEnumValue(typeValue, PREVIEW_ENUM_VALUES.creditNoteType);
}

function formatCreditNoteRefundArchiveTitle(method, isSupplier) {
  const key = String(method || '').trim();
  if (!key) return '';
  if (key === 'account-credit') {
    return isSupplier ? 'رصيد على حساب المورد' : PREVIEW_ENUM_VALUES.refundMethod.account_credit;
  }
  return mapPreviewEnumValue(key, PREVIEW_ENUM_VALUES.refundMethod);
}

function creditNoteArchiveTitleReason(formData, maxLen = ARCHIVE_TITLE_MAX_LEN) {
  let reason = firstMeaningfulLineArchiveTitle(formData.creditNoteReason || '', maxLen);
  if (isTemplatePlaceholderArchiveTitleLine(reason)) reason = '';
  return reason;
}

/** Titre Avoir : type / motif / remboursement + lignes (< 4 désignations, ≥ 4 « N articles divers »). */
function generateCreditNoteArchiveTitleCore(formData, locale, maxLen = ARCHIVE_TITLE_MAX_LEN) {
  const L = archiveTitleStr.bind(null, locale);
  const sep = L('separator');
  const items = normalizeArchiveTitleItems(formData.items || []);
  const cnType =
    formData.creditNoteType ||
    formData.creditNote?.type ||
    formData.credit_type ||
    '';
  const isSupplier = isSupplierCreditNoteArchiveForm(formData);

  const reason = creditNoteArchiveTitleReason(formData, maxLen);

  const typeLabel = formatCreditNoteTypeArchiveTitle(cnType);
  const refundLabel = formatCreditNoteRefundArchiveTitle(
    formData.creditNoteRefundMethod ||
      formData.creditNote?.refundMethod ||
      formData.refundMethod,
    isSupplier,
  );

  const actionParts = [];
  if (typeLabel) actionParts.push(typeLabel);
  else if (reason) actionParts.push(reason);
  if (refundLabel && !actionParts.includes(refundLabel)) actionParts.push(refundLabel);

  const action = actionParts.join(' · ');
  const lineCore = summarizeArchiveTitleLineLabels(
    items,
    locale,
    maxLen,
    archiveTitleFormatItemPart,
  );

  if (lineCore) {
    if (action) return truncateArchiveTitle(`${action}${sep}${lineCore}`, maxLen);
    return truncateArchiveTitle(lineCore, maxLen);
  }
  if (action) return truncateArchiveTitle(action, maxLen);
  return '';
}

function generateTitleFromMotifFieldArchive(
  formData,
  documentType,
  selectedTypeTitle,
  maxLen = ARCHIVE_TITLE_MAX_LEN,
) {
  const id = normDocTypeForArchiveTitle(documentType);
  if (id !== 'return-slip' && id !== 'credit-note' && id !== 'credit_note') {
    const supplementary = getSupplementaryMotifArchiveTitle(formData, id);
    const first = firstMeaningfulLineArchiveTitle(supplementary, maxLen);
    if (first && !isTemplatePlaceholderArchiveTitleLine(first)) return first;
  }

  if (id === 'client-return-slip') {
    const m = firstMeaningfulLineArchiveTitle(
      formData.clientReturnMotif || formData.clientReturnReason || '',
      maxLen,
    );
    if (m && !isTemplatePlaceholderArchiveTitleLine(m)) return m;
  }

  return selectedTypeTitle || '';
}

function isDraftOnlyArchiveTitle(text, locale) {
  const d = archiveTitleStr(locale, 'draft');
  return text === d || text.endsWith(d);
}

function generateTitleFromLineItemsArchive(
  items,
  documentType,
  locale,
  fallbackTypeLabel = '',
  maxLen = ARCHIVE_TITLE_MAX_LEN,
) {
  const id = normDocTypeForArchiveTitle(documentType);
  if (getArchiveTitlePrefixKey(id)) {
    return summarizeTypedArchiveTitle(items, id, locale, fallbackTypeLabel, maxLen);
  }

  return summarizeProductLinesArchiveTitle(items, 'sale', locale, fallbackTypeLabel);
}

const SUPPLEMENTARY_TEXT_FIELD_KEYS = [
  'notes',
  'conditions',
  'terms',
  'requirements',
  'deliveryNotes',
  'deliveryConditions',
  'instructions',
];

const DEFAULT_SUPPLEMENTARY_SECTION_ORDER = [
  'Notes',
  'Conditions',
  'Objectif',
  'Observations',
  'Garanties',
  'Validité',
  'Motif',
  'Instructions',
];

const BASE_SUPPLEMENTARY_SECTION_DEFAULTS = {
  showNotes: true,
  showConditions: false,
  showObjectif: false,
  showObservations: true,
  showGaranties: false,
  showValidite: false,
  showMotif: true,
  showInstructions: false,
  showEcheancier: false,
  showSupplementarySections: true,
};

const SUPPLEMENTARY_SECTION_OVERRIDES = {
  invoice: {
    showConditions: true,
    showObservations: false,
    showMotif: false,
    showEcheancier: true,
  },
  proforma: {
    showConditions: true,
    showObservations: false,
    showValidite: true,
    showMotif: false,
    showEcheancier: true,
  },
  'quote-request': {
    showConditions: true,
    showObjectif: true,
    showObservations: false,
    showMotif: false,
  },
  'proforma-request': {
    showConditions: true,
    showObjectif: true,
    showObservations: false,
    showMotif: false,
  },
  quote: {
    showConditions: true,
    showObjectif: true,
    showObservations: false,
    showValidite: true,
    showMotif: false,
  },
  'purchase-order': {
    showConditions: true,
    showObservations: true,
    showMotif: false,
    showEcheancier: true,
  },
  'delivery-note': {
    showObservations: true,
    showMotif: false,
    showInstructions: true,
  },
  delivery: {
    showObservations: true,
    showMotif: false,
    showInstructions: true,
  },
  'material-exit': {
    showObservations: true,
    showMotif: false,
    showInstructions: true,
  },
  'shipping-slip': {
    showObservations: true,
    showMotif: false,
    showInstructions: true,
  },
  shipping: {
    showObservations: true,
    showMotif: false,
    showInstructions: true,
  },
  'reception-note': {
    showObservations: true,
    showMotif: false,
  },
  'credit-note': {
    showObservations: false,
    showMotif: true,
  },
  credit_note: {
    showObservations: false,
    showMotif: true,
  },
  'return-slip': {
    showObservations: true,
    showMotif: true,
  },
  return_slip: {
    showObservations: true,
    showMotif: true,
  },
  'client-return-slip': {
    showObservations: true,
    showMotif: true,
  },
  'payment-voucher': {
    showObservations: false,
    showMotif: true,
  },
  payment_voucher: {
    showObservations: false,
    showMotif: true,
  },
  'payment-receipt': {
    showObservations: false,
    showMotif: true,
  },
  payment_receipt: {
    showObservations: false,
    showMotif: true,
  },
  'transfer-note': {
    showObservations: false,
    showMotif: true,
  },
  transfer_slip: {
    showObservations: false,
    showMotif: true,
  },
  'exit-note': {
    showObservations: false,
    showMotif: true,
  },
  exit_slip: {
    showObservations: false,
    showMotif: true,
  },
  'stock-entry': {
    showObservations: true,
    showMotif: false,
  },
  stock_entry: {
    showObservations: true,
    showMotif: false,
  },
};

function getSupplementarySectionDefaultsForDocType(docTypeId) {
  const dt = normalizeSupplementaryDocType(docTypeId);
  const overrides = SUPPLEMENTARY_SECTION_OVERRIDES[dt] || {};
  return { ...BASE_SUPPLEMENTARY_SECTION_DEFAULTS, ...overrides };
}

/** Saudi market — Arabic supplementary text for the 5 billing document types. */
function buildSaudiSupplementaryContent(docTypeId, flags = {}, installments = [], sectionOrder = DEFAULT_SUPPLEMENTARY_SECTION_ORDER) {
  const docType = normalizeSupplementaryDocType(docTypeId);
  const f = flags || {};
  const order =
    Array.isArray(sectionOrder) && sectionOrder.length > 0
      ? sectionOrder
      : DEFAULT_SUPPLEMENTARY_SECTION_ORDER;
  const parts = [];
  const vat = getBrandVatRate();

  if (f.showEcheancier && ['invoice', 'proforma', 'purchase-order'].includes(docType)) {
    if (Array.isArray(installments) && installments.length > 0) {
      const lines = installments.map((inst) => {
        const pct = inst.percentage ? `${inst.percentage}%` : '';
        const amt = inst.amount ? formatBrandMoney(parseFloat(inst.amount)) : '';
        return `• ${[pct, amt].filter(Boolean).join(' — ')}`;
      });
      parts.push('- جدول الدفعات:\n' + lines.join('\n'));
    } else {
      parts.push('- جدول الدفعات:\n• 30% عند الطلب\n• 40% عند التسليم\n• 30% عند الاستلام');
    }
  }

  const saudiSectionText = {
    quote: {
      Notes: '- أسعار صالحة 30 يوماً — وفق توفر المخزون',
      Conditions: '- 30% عند الطلب، 70% عند التسليم · تحويل بنكي / مدى / بطاقة',
      Objectif: '- تلبية احتياجات العميل وفق السوق السعودي',
      Observations: '- ملاحظات خاصة بعرض السعر',
      Garanties: '- ضمان 12 شهراً على المنتجات (حسب سياسة الشركة)',
      Validité: '- العرض ساري 30 يوماً من تاريخ الإصدار',
      Motif: '- [يُحدَّد لاحقاً]',
    },
    proforma: {
      Notes: `- فاتورة أولية قبل الفاتورة الضريبية · ض.ق.م ${vat}% · ZATCA`,
      Conditions: '- الدفع عند الطلب · التسليم خلال المدة المتفق عليها',
      Observations: '- ملاحظات على الفاتورة الأولية',
      Validité: '- الأسعار صالحة حتى [التاريخ] — وفق توفر المخزون',
      Motif: '- [يُحدَّد لاحقاً]',
    },
    invoice: {
      Notes: `- فاتورة ضريبية B2B · ترقيم · PDF عربي · ZATCA · ض.ق.م ${vat}%`,
      Conditions: '- الدفع خلال 30 يوماً · متوافقة مع هيئة الزكاة والضريبة والجمارك',
      Observations: '- ملاحظات على الفاتورة',
      Motif: '- [يُحدَّد لاحقاً]',
    },
    purchase_order: {
      Notes: '- أمر شراء للمورد · مطابقة عرض السعر',
      Conditions: '- التسليم خلال 15 يوماً · الدفع خلال 30 يوماً',
      Observations: '- ملاحظات على أمر الشراء',
      Motif: '- [يُحدَّد لاحقاً]',
    },
    delivery: {
      Notes: '- تسليم جزئي/كامل · مطابقة الطلب',
      Observations: '- حالة البضائع عند التسليم',
      Instructions: '- التوقيع إلزامي عند الاستلام · مطابقة الكميات',
      Motif: '- [يُحدَّد لاحقاً]',
    },
  };

  const sectionFieldMap = {
    Notes: 'showNotes',
    Conditions: 'showConditions',
    Objectif: 'showObjectif',
    Observations: 'showObservations',
    Garanties: 'showGaranties',
    Validité: 'showValidite',
    Motif: 'showMotif',
    Instructions: 'showInstructions',
  };

  const texts = saudiSectionText[docType] || saudiSectionText.quote;
  order.forEach((section) => {
    const fieldKey = sectionFieldMap[section] || `show${section}`;
    if (!f[fieldKey]) return;
    const line = texts[section];
    if (line) parts.push(line);
  });

  return parts.join('\n');
}

/** Même gabarits que l’auto-génération du formulaire DocumentCreation. */
function buildAutoSupplementaryContent(docTypeId, flags = {}, installments = [], sectionOrder = DEFAULT_SUPPLEMENTARY_SECTION_ORDER) {
  if (isSaudiBrand()) {
    return buildSaudiSupplementaryContent(docTypeId, flags, installments, sectionOrder);
  }
  const st = (key, opts) => i18n.t(`document_creation.form.supplementary_templates.${key}`, opts);
  const docType = normalizeSupplementaryDocType(docTypeId);
  const templateParts = [];
  const f = flags || {};
  const order =
    Array.isArray(sectionOrder) && sectionOrder.length > 0
      ? sectionOrder
      : DEFAULT_SUPPLEMENTARY_SECTION_ORDER;

  if (f.showEcheancier && ['invoice', 'proforma', 'purchase-order'].includes(docType)) {
    const conditionLabels = {
      a_la_commande: docType === 'purchase-order'
        ? st('installment_conditions.on_order')
        : st('installment_conditions.on_order_deposit'),
      a_la_livraison: st('installment_conditions.on_delivery'),
      a_la_reception: docType === 'purchase-order'
        ? st('installment_conditions.on_reception')
        : st('installment_conditions.on_final_reception'),
      apres_reception: st('installment_conditions.after_reception_30d'),
      fin_de_mois: st('installment_conditions.end_of_month'),
      date_precise: st('installment_conditions.fixed_date'),
    };
    const statusLabels = {
      pending: st('installment_status.pending'),
      paid: docType === 'purchase-order'
        ? st('installment_status.paid_supplier')
        : st('installment_status.collected'),
    };

    if (Array.isArray(installments) && installments.length > 0) {
      const lines = installments.map((inst) => {
        const pct = inst.percentage ? `${inst.percentage}%` : '';
        const cond = inst.condition ? (conditionLabels[inst.condition] || inst.condition) : '';
        const amt = inst.amount
          ? formatBrandMoney(parseFloat(inst.amount))
          : '';
        const status = inst.status ? `[${statusLabels[inst.status] || inst.status}]` : '';
        return `• ${[pct, cond, amt, status].filter(Boolean).join(' - ')}`;
      });
      templateParts.push(`${st('payment_schedule_header')}\n${lines.join('\n')}`);
    } else {
      templateParts.push(st('payment_schedule_default'));
    }
  }

  const sectionFieldMap = {
    Notes: 'showNotes',
    Conditions: 'showConditions',
    Objectif: 'showObjectif',
    Observations: 'showObservations',
    Garanties: 'showGaranties',
    Validité: 'showValidite',
    Motif: 'showMotif',
    Instructions: 'showInstructions',
  };

  order.forEach((section) => {
    const fieldKey = sectionFieldMap[section] || `show${section}`;
    if (!f[fieldKey]) return;

    switch (section) {
      case 'Notes':
        if (docType === 'quote-request') templateParts.push(st('notes.quote_request'));
        else if (docType === 'proforma-request') templateParts.push(st('notes.proforma_request'));
        else if (docType === 'purchase-order') templateParts.push(st('notes.purchase_order'));
        else if (docType === 'invoice') templateParts.push(st('notes.invoice', { vat: getBrandVatRate() }));
        else if (docType === 'proforma') templateParts.push(st('notes.proforma'));
        else if (docType === 'quote') templateParts.push(st('notes.quote'));
        else if (docType === 'delivery-note' || docType === 'delivery') templateParts.push(st('notes.delivery'));
        else if (docType === 'reception-note') templateParts.push(st('notes.reception_note'));
        else if (docType === 'return-slip') templateParts.push(st('notes.return_slip'));
        else if (docType === 'client-return-slip') templateParts.push(st('notes.client_return_slip'));
        else if (docType === 'credit-note' || docType === 'credit_note') {
          templateParts.push(st('notes.credit_note'));
        } else if (docType === 'payment-voucher') templateParts.push(st('notes.payment_voucher'));
        else if (docType === 'payment-receipt') templateParts.push(st('notes.payment_receipt'));
        else if (docType === 'exit-note' || docType === 'exit_slip') templateParts.push(st('notes.exit_note'));
        else if (docType === 'material-exit') templateParts.push(st('notes.material_exit'));
        else templateParts.push(st('notes.default'));
        break;
      case 'Conditions':
        if (docType === 'quote-request') templateParts.push(st('conditions.quote_request'));
        else if (docType === 'proforma-request') templateParts.push(st('conditions.proforma_request'));
        else if (docType === 'purchase-order') templateParts.push(st('conditions.purchase_order'));
        else if (docType === 'invoice') templateParts.push(st('conditions.invoice'));
        else if (docType === 'proforma') templateParts.push(st('conditions.proforma'));
        else if (docType === 'quote') templateParts.push(st('conditions.quote'));
        else templateParts.push(st('conditions.default'));
        break;
      case 'Objectif':
        if (docType === 'quote-request') templateParts.push(st('objectif.quote_request'));
        else if (docType === 'proforma-request') templateParts.push(st('objectif.proforma_request'));
        else if (docType === 'purchase-order') templateParts.push(st('objectif.purchase_order'));
        else if (docType === 'quote') templateParts.push(st('objectif.quote'));
        else templateParts.push(st('objectif.default'));
        break;
      case 'Observations':
        if (docType === 'quote-request') templateParts.push(st('observations.quote_request'));
        else if (docType === 'reception-note') templateParts.push(st('observations.reception_note'));
        else if (docType === 'delivery-note' || docType === 'delivery') templateParts.push(st('observations.delivery'));
        else if (docType === 'exit-note' || docType === 'exit_slip') templateParts.push(st('observations.exit_note'));
        else if (docType === 'stock-entry') templateParts.push(st('observations.stock_entry'));
        else if (docType === 'transfer-note' || docType === 'transfer_slip') templateParts.push(st('observations.transfer_note'));
        else if (docType === 'payment-receipt') templateParts.push(st('observations.payment_receipt'));
        else templateParts.push(st('observations.default'));
        break;
      case 'Garanties':
        if (docType === 'quote') templateParts.push(st('garanties.quote'));
        else if (docType === 'purchase-order') templateParts.push(st('garanties.purchase_order'));
        else templateParts.push(st('garanties.default'));
        break;
      case 'Validité':
        if (docType === 'quote') templateParts.push(st('validite.quote'));
        else if (docType === 'proforma') templateParts.push(st('validite.proforma'));
        else templateParts.push(st('validite.default'));
        break;
      case 'Motif':
        if (docType === 'payment-voucher') templateParts.push(st('motif.payment_voucher'));
        else if (docType === 'payment-receipt') templateParts.push(st('motif.payment_receipt'));
        else if (docType === 'return-slip') templateParts.push(st('motif.return_slip'));
        else if (docType === 'client-return-slip') templateParts.push(st('motif.client_return_slip'));
        else if (docType === 'purchase-order') templateParts.push(st('motif.purchase_order'));
        else if (docType === 'exit-note' || docType === 'exit_slip') templateParts.push(st('motif.exit_note'));
        else if (docType === 'material-exit') templateParts.push(st('motif.material_exit'));
        else if (docType === 'credit-note' || docType === 'credit_note') {
          templateParts.push(st('motif.credit_note'));
        } else templateParts.push(st('motif.default'));
        break;
      case 'Instructions':
        if (docType === 'delivery-note' || docType === 'delivery') {
          templateParts.push(st('instructions.delivery'));
        } else if (docType === 'material-exit') {
          templateParts.push(st('instructions.material_exit'));
        } else if (docType === 'shipping-slip') {
          templateParts.push(st('instructions.shipping_slip'));
        } else templateParts.push(st('instructions.default'));
        break;
      default:
        break;
    }
  });

  return templateParts.join('\n');
}

function archiveConversionTodayIso() {
  return new Date().toISOString().split('T')[0];
}

function addDaysToIsoDate(isoDate, days) {
  const base = isoDate ? new Date(isoDate) : new Date();
  const d = new Date(base.getTime() + Number(days || 0) * 86400000);
  if (Number.isNaN(d.getTime())) return archiveConversionTodayIso();
  return d.toISOString().split('T')[0];
}

function conversionDelayDate(documentDelays, delayKey, defaultDays, baseIso) {
  const delay =
    documentDelays && documentDelays[delayKey] != null
      ? Number(documentDelays[delayKey])
      : defaultDays;
  return addDaysToIsoDate(baseIso || archiveConversionTodayIso(), Number.isFinite(delay) ? delay : defaultDays);
}

function pickFirstIsoDate(source, keys) {
  if (!source || typeof source !== 'object') return '';
  for (const k of keys) {
    const v = source[k];
    if (v != null && String(v).trim()) return String(v).trim().slice(0, 10);
  }
  return '';
}

function assignIsoDateField(doc, camelKey, snakeKey, value) {
  if (!doc || value == null || value === '') {
    if (camelKey) doc[camelKey] = '';
    if (snakeKey && snakeKey !== camelKey) doc[snakeKey] = '';
    return;
  }
  const iso = String(value).trim().slice(0, 10);
  if (camelKey) doc[camelKey] = iso;
  if (snakeKey && snakeKey !== camelKey) doc[snakeKey] = iso;
}

function clearIsoDateFields(doc, pairs) {
  pairs.forEach(([camel, snake]) => assignIsoDateField(doc, camel, snake || camel, ''));
}

/** Blocs signature PDF — coches par type cible (aligné création document). */
const CONVERSION_SIGNATURE_FLAGS_BY_TYPE = {
  'reception-note': { showBonPour: true, showEtabliePar: false, showRecuePar: true },
  'stock-entry': { showBonPour: true, showEtabliePar: false, showRecuePar: true },
  'delivery-note': { showBonPour: true, showEtabliePar: false, showRecuePar: true },
  'payment-receipt': { showBonPour: true, showEtabliePar: false, showRecuePar: true },
  'client-return-slip': { showBonPour: true, showEtabliePar: false, showRecuePar: false },
  'shipping-slip': { showBonPour: true, showEtabliePar: false, showRecuePar: false },
  'transfer-note': { showBonPour: true, showEtabliePar: false, showRecuePar: false },
  'exit-note': { showBonPour: true, showEtabliePar: false, showRecuePar: false },
  'payment-voucher': { showBonPour: true, showEtabliePar: false, showRecuePar: false },
  'credit-note': { showBonPour: true, showEtabliePar: false, showRecuePar: false },
};

const CONVERSION_SECONDARY_DATE_FIELDS = [
  ['dueDate', 'due_date'],
  ['validUntil', 'valid_until'],
  ['paymentDueDate', 'payment_due_date'],
  ['responseDate', 'response_date'],
  ['desiredStartDate', 'desired_start_date'],
  ['expectedCashingDate', 'expected_cashing_date'],
  ['paymentDate', 'payment_date'],
  ['shipmentDate', 'shipment_date'],
  ['deliveryDate', 'delivery_date'],
  ['expectedDeliveryDate', 'expected_delivery_date'],
  ['expectedReceptionDate', 'expected_reception_date'],
  ['receptionDate', 'reception_date'],
  ['expectedEntryDate', 'expected_entry_date'],
  ['transferNoteExpectedDate', 'transfer_note_expected_date'],
  ['exitNoteExpectedDate', 'exit_note_expected_date'],
  ['returnDate', 'return_date'],
  ['processingDeadline', 'processing_deadline'],
  ['clientReturnReturnDate', 'client_return_return_date'],
  ['creditNoteApplicationDate', 'credit_note_application_date'],
  ['orderDeliveryDate', 'order_delivery_date'],
  ['validityDate', 'validity_date'],
];

function applyConversionTargetSecondaryDates(
  doc,
  typeId,
  sourceDoc,
  todayIso,
  documentDelays,
  context = {},
) {
  clearIsoDateFields(doc, CONVERSION_SECONDARY_DATE_FIELDS);

  const src = sourceDoc || {};
  const srcKey = normDocTypeForArchiveTitle(src.type || '');
  const fromDeliveryBl =
    context.deliveryToShipping === true ||
    srcKey === 'delivery' ||
    srcKey === 'delivery-note' ||
    srcKey === 'material-exit' ||
    srcKey === 'material_exit';

  switch (typeId) {
    case 'purchase-order': {
      const fromSource = pickFirstIsoDate(src, [
        'deliveryDate',
        'delivery_date',
        'desiredDeliveryTime',
        'expectedDeliveryDate',
        'expected_delivery_date',
      ]);
      const deliv = fromSource || conversionDelayDate(documentDelays, 'purchaseOrder', 7, todayIso);
      assignIsoDateField(doc, 'deliveryDate', 'delivery_date', deliv);
      assignIsoDateField(doc, 'orderDeliveryDate', 'order_delivery_date', deliv);
      break;
    }
    case 'reception-note': {
      assignIsoDateField(doc, 'receptionDate', 'reception_date', todayIso);
      const planned =
        pickFirstIsoDate(src, [
          'deliveryDate',
          'delivery_date',
          'orderDeliveryDate',
          'order_delivery_date',
          'expectedDeliveryDate',
          'expected_delivery_date',
          'expectedReceptionDate',
          'expected_reception_date',
        ]) || todayIso;
      assignIsoDateField(doc, 'expectedReceptionDate', 'expected_reception_date', planned);
      // Colonne archives persistée (pas de expected_reception_date en base)
      assignIsoDateField(doc, 'expectedDeliveryDate', 'expected_delivery_date', planned);
      break;
    }
    case 'return-slip': {
      assignIsoDateField(doc, 'returnDate', 'return_date', todayIso);
      assignIsoDateField(
        doc,
        'processingDeadline',
        'processing_deadline',
        conversionDelayDate(documentDelays, 'supplierReturn', 0, todayIso),
      );
      break;
    }
    case 'credit-note': {
      assignIsoDateField(doc, 'creditNoteApplicationDate', 'credit_note_application_date', todayIso);
      assignIsoDateField(doc, 'orderDate', 'order_date', todayIso);
      if (doc.creditNote && typeof doc.creditNote === 'object') {
        doc.creditNote.applicationDate = todayIso;
      } else {
        doc.creditNote = { applicationDate: todayIso };
      }
      const origInvDate = pickFirstIsoDate(src, ['date', 'invoiceDate', 'invoice_date']);
      if (srcKey === 'invoice' && origInvDate) {
        assignIsoDateField(doc, 'originalInvoiceDate', 'original_invoice_date', origInvDate);
        if (doc.originalInvoice && typeof doc.originalInvoice === 'object') {
          doc.originalInvoice.date = origInvDate;
        }
      }
      break;
    }
    case 'quote': {
      assignIsoDateField(
        doc,
        'validUntil',
        'valid_until',
        conversionDelayDate(documentDelays, 'quote', 15, todayIso),
      );
      break;
    }
    case 'proforma': {
      assignIsoDateField(
        doc,
        'validUntil',
        'valid_until',
        conversionDelayDate(documentDelays, 'proforma', 30, todayIso),
      );
      assignIsoDateField(
        doc,
        'dueDate',
        'due_date',
        conversionDelayDate(documentDelays, 'proforma', 30, todayIso),
      );
      break;
    }
    case 'invoice': {
      assignIsoDateField(
        doc,
        'dueDate',
        'due_date',
        conversionDelayDate(documentDelays, 'invoice', 30, todayIso),
      );
      break;
    }
    case 'delivery-note': {
      assignIsoDateField(
        doc,
        'deliveryDate',
        'delivery_date',
        conversionDelayDate(documentDelays, 'delivery', 0, todayIso),
      );
      const expectDeliv =
        pickFirstIsoDate(doc, ['expectedDeliveryDate', 'expected_delivery_date']) ||
        pickFirstIsoDate(src, [
          'expectedDeliveryDate',
          'expected_delivery_date',
          'deliveryDate',
          'delivery_date',
        ]) ||
        todayIso;
      assignIsoDateField(doc, 'expectedDeliveryDate', 'expected_delivery_date', expectDeliv);
      break;
    }
    case 'shipping-slip': {
      const shipDate =
        (fromDeliveryBl
          ? pickFirstIsoDate(src, [
              'deliveryDate',
              'delivery_date',
              'expectedDeliveryDate',
              'expected_delivery_date',
            ])
          : '') ||
        pickFirstIsoDate(doc, ['shipmentDate', 'shipment_date']) ||
        pickFirstIsoDate(src, ['shipmentDate', 'shipment_date', 'date']) ||
        todayIso;
      assignIsoDateField(doc, 'shipmentDate', 'shipment_date', shipDate);
      break;
    }
    case 'payment-voucher': {
      const encash = conversionDelayDate(documentDelays, 'paymentVoucher', 0, todayIso);
      assignIsoDateField(doc, 'expectedCashingDate', 'expected_cashing_date', encash);
      assignIsoDateField(doc, 'dueDate', 'due_date', encash);
      break;
    }
    case 'payment-receipt': {
      const pay = conversionDelayDate(documentDelays, 'paymentReceipt', 0, todayIso);
      assignIsoDateField(doc, 'paymentDate', 'payment_date', pay);
      assignIsoDateField(doc, 'dueDate', 'due_date', pay);
      break;
    }
    case 'transfer-note': {
      assignIsoDateField(doc, 'transferNoteExpectedDate', 'transfer_note_expected_date', todayIso);
      break;
    }
    case 'exit-note': {
      assignIsoDateField(doc, 'exitNoteExpectedDate', 'exit_note_expected_date', todayIso);
      break;
    }
    case 'stock-entry': {
      assignIsoDateField(doc, 'expectedEntryDate', 'expected_entry_date', todayIso);
      break;
    }
    case 'client-return-slip': {
      assignIsoDateField(
        doc,
        'clientReturnReturnDate',
        'client_return_return_date',
        conversionDelayDate(documentDelays, 'customerReturn', 0, todayIso),
      );
      break;
    }
    default:
      break;
  }
}

function pickDocStringField(doc, ...keys) {
  if (!doc || typeof doc !== 'object') return '';
  for (const k of keys) {
    const v = doc[k];
    if (v != null && String(v).trim()) return String(v).trim();
  }
  return '';
}

function assignSignaturePairFields(doc, prefix, { name = '', role = '', place = '', date = '' } = {}) {
  const snake = prefix.replace(/([A-Z])/g, '_$1').toLowerCase();
  doc[`${prefix}Name`] = name;
  doc[`${snake}_name`] = name;
  doc[`${prefix}Role`] = role;
  doc[`${snake}_role`] = role;
  doc[`${prefix}Place`] = place;
  doc[`${snake}_place`] = place;
  doc[`${prefix}Date`] = date;
  doc[`${snake}_date`] = date;
}

function resolveSourceSignatureUi(sourceDoc) {
  return decodeDocumentUiOverrides(sourceDoc?.validityNotes ?? sourceDoc?.validity_notes);
}

/** Bon pour + Établi par : reprise depuis le document source ; Reçue par : toujours réinitialisé. */
function applyConversionSignatureFields(doc, sourceDoc, todayIso, sigFlags) {
  const src = sourceDoc || {};
  const srcUi = resolveSourceSignatureUi(src);

  const srcShowBonPour =
    srcUi && typeof srcUi.showBonPour === 'boolean'
      ? srcUi.showBonPour
      : resolveShowBonPourFromArchive(src);
  const keepBonPour = sigFlags.showBonPour && srcShowBonPour;
  doc.showBonPour = keepBonPour;
  if (keepBonPour) {
    assignSignaturePairFields(doc, 'bonPour', {
      name: pickDocStringField(src, 'bonPourName', 'bon_pour_name'),
      role: pickDocStringField(src, 'bonPourRole', 'bon_pour_role'),
      place: pickDocStringField(src, 'bonPourPlace', 'bon_pour_place'),
      date: todayIso,
    });
  } else {
    assignSignaturePairFields(doc, 'bonPour', { date: todayIso });
  }

  const etablieParName =
    pickDocStringField(src, 'etablieParName', 'etablie_par_name') ||
    (srcUi?.etablieParName && String(srcUi.etablieParName).trim()) ||
    pickDocStringField(src, 'issuedBy', 'issued_by', 'createdBy', 'created_by') ||
    pickDocStringField(doc, 'issuedBy', 'issued_by', 'createdBy', 'created_by');
  const etablieParRole =
    pickDocStringField(src, 'etablieParRole', 'etablie_par_role') ||
    (srcUi?.etablieParRole && String(srcUi.etablieParRole).trim()) ||
    '';
  const etablieParPlace =
    pickDocStringField(src, 'etablieParPlace', 'etablie_par_place') ||
    (srcUi?.etablieParPlace && String(srcUi.etablieParPlace).trim()) ||
    '';
  const srcShowEtabliePar =
    srcUi && typeof srcUi.showEtabliePar === 'boolean'
      ? srcUi.showEtabliePar
      : undefined;
  const keepEtabliePar =
    Boolean(etablieParName) &&
    (sigFlags.showEtabliePar || srcShowEtabliePar === true);
  doc.showEtabliePar = keepEtabliePar;
  if (keepEtabliePar) {
    assignSignaturePairFields(doc, 'etabliePar', {
      name: etablieParName,
      role: etablieParRole,
      place: etablieParPlace,
      date: todayIso,
    });
  } else {
    assignSignaturePairFields(doc, 'etabliePar', { date: todayIso });
  }

  doc.showRecuePar = sigFlags.showRecuePar;
  assignSignaturePairFields(doc, 'recuePar', {
    date: sigFlags.showRecuePar ? todayIso : '',
  });

  const issuedByValue =
    pickDocStringField(src, 'issuedBy', 'issued_by') ||
    pickDocStringField(doc, 'issuedBy', 'issued_by') ||
    etablieParName;
  if (issuedByValue) {
    doc.issuedBy = issuedByValue;
    doc.issued_by = issuedByValue;
  }
}

/**
 * Conversion Archives : signatures + dates secondaires pour le nouveau document.
 * @param {object} doc — document converti (muté)
 * @param {string} targetTypeId
 * @param {object} [sourceDoc]
 * @param {{ documentDelays?: object, todayIso?: string }} [options]
 */
export function applyArchiveConversionDatesAndSignatures(doc, targetTypeId, sourceDoc, options = {}) {
  if (!doc || typeof doc !== 'object') return;

  const todayIso = options.todayIso || archiveConversionTodayIso();
  const typeId = normDocTypeForArchiveTitle(targetTypeId || doc.type || '');
  const srcKey = normDocTypeForArchiveTitle(sourceDoc?.type || '');
  const deliveryToShipping =
    options.preserveDeliveryToShippingDates === true ||
    ((typeId === 'shipping-slip' || typeId === 'shipping') &&
      (srcKey === 'delivery-note' || srcKey === 'delivery' || srcKey === 'material-exit'));

  if (deliveryToShipping) {
    doc.date =
      pickFirstIsoDate(doc, ['date']) ||
      pickFirstIsoDate(sourceDoc, ['date', 'deliveryDate', 'delivery_date']) ||
      todayIso;
  } else {
    doc.date = todayIso;
  }

  const sigFlags = CONVERSION_SIGNATURE_FLAGS_BY_TYPE[typeId] || {
    showBonPour: true,
    showEtabliePar: false,
    showRecuePar: false,
  };

  applyConversionSignatureFields(doc, sourceDoc, todayIso, sigFlags);

  applyConversionTargetSecondaryDates(
    doc,
    typeId,
    sourceDoc,
    todayIso,
    options.documentDelays || {},
    { deliveryToShipping },
  );

  const sectionOrder =
    Array.isArray(doc.sectionOrder) && doc.sectionOrder.length > 0
      ? doc.sectionOrder
      : DEFAULT_SUPPLEMENTARY_SECTION_ORDER;
  const encoded = encodeDocumentUiOverrides(doc, sectionOrder, doc.taxOrder || null);
  doc.validityNotes = encoded;
  doc.validity_notes = encoded;
}

/**
 * Conversion Archives : ne pas reprendre les textes du document source ;
 * conserver les coches internes du type cible, mais masquer le bloc (case principale décochée).
 */
export function applySupplementaryDefaultsForArchiveConversion(doc, targetTypeId) {
  if (!doc || typeof doc !== 'object') return;

  SUPPLEMENTARY_TEXT_FIELD_KEYS.forEach((k) => {
    doc[k] = '';
  });
  doc.validityNotes = '';
  doc.validity_notes = '';

  const sectionDefaults = getSupplementarySectionDefaultsForDocType(targetTypeId);
  Object.assign(doc, sectionDefaults);

  const fieldName = getSupplementaryFormFieldName(targetTypeId);
  doc[fieldName] = buildAutoSupplementaryContent(
    targetTypeId,
    { ...doc, ...sectionDefaults },
    doc.installments || [],
  );

  doc.showSupplementarySections = false;

  const encoded = encodeDocumentUiOverrides(doc, DEFAULT_SUPPLEMENTARY_SECTION_ORDER, null);
  doc.validityNotes = encoded;
  doc.validity_notes = encoded;
}

/**
 * Titre archive : motif ou résumé des lignes (utilisé à la sauvegarde et depuis Archives conversion).
 * @param {object} formData
 * @param {{ id: string, title?: string }} selectedType
 * @param {string} [locale]
 */
export function generateDocumentTitle(formData, selectedType, locale = 'ar', maxLen = ARCHIVE_TITLE_MAX_LEN) {
  if (!formData || typeof formData !== 'object') formData = {};
  const titleLocale = isSaudiBrand() ? 'ar' : locale;
  const typeId = normDocTypeForArchiveTitle(selectedType?.id || formData.type || '');
  const selectedTitle = (selectedType && selectedType.title) || '';
  const T = (text) => truncateArchiveTitle(text, maxLen);

  if (isPaymentStyleArchiveTitleDoc(typeId)) {
    const fromPayments = generateTitleFromPaymentLinesArchive(
      formData.paymentLines,
      typeId,
      titleLocale,
    );
    const draft = archiveTitleStr(titleLocale, 'draft');
    const paymentCore = summarizePaymentDocumentTitleCore(formData.paymentLines || []);
    if (paymentCore || (fromPayments && !isDraftOnlyArchiveTitle(fromPayments, titleLocale))) {
      return T(fromPayments);
    }
    const motifReason = formatPaymentVoucherReason(formData.paymentReason || formData.motif);
    if (motifReason) {
      return formatArchiveTitleWithPrefix(typeId, motifReason, titleLocale, maxLen);
    }
    const motif = generateTitleFromMotifFieldArchive(formData, typeId, '', maxLen);
    if (motif) return formatArchiveTitleWithPrefix(typeId, motif, titleLocale, maxLen);
    return T(fromPayments || selectedTitle);
  }

  if (typeId === 'return-slip') {
    const core = generateReturnSlipArchiveTitleCore(formData, titleLocale, maxLen);
    if (core) return formatArchiveTitleWithPrefix(typeId, core, titleLocale, maxLen);
    return T(selectedTitle);
  }

  if (typeId === 'client-return-slip') {
    const motif = generateTitleFromMotifFieldArchive(formData, typeId, '', maxLen);
    if (motif) return formatArchiveTitleWithPrefix(typeId, motif, titleLocale, maxLen);
    return T(
      generateTitleFromLineItemsArchive(formData.items, typeId, titleLocale, selectedTitle, maxLen) ||
        selectedTitle,
    );
  }

  if (typeId === 'quote-request' || typeId === 'proforma-request') {
    const itemLines = normalizeArchiveTitleItems(formData.items || []);
    const specLines = normalizeArchiveTitleSpecs(formData.specifications);
    const primaryLines = itemLines.length > 0 ? itemLines : specLines;

    let core = '';
    if (primaryLines.length > 0) {
      core = summarizeArchiveTitleLineLabels(
        primaryLines,
        titleLocale,
        maxLen,
        itemLines.length > 0 ? archiveTitleFormatItemPart : archiveTitleFormatSpecPart,
      );
    }
    if (!core) {
      core = firstMeaningfulLineArchiveTitle(formData.requirements || '', maxLen);
    }
    if (core) return formatArchiveTitleWithPrefix(typeId, core, titleLocale, maxLen);
    return (
      formatArchiveTitleWithPrefix(typeId, archiveTitleStr(titleLocale, 'draft'), titleLocale, maxLen) ||
      T(selectedTitle)
    );
  }

  if (typeId === 'credit-note' || typeId === 'credit_note') {
    const core = generateCreditNoteArchiveTitleCore(formData, titleLocale, maxLen);
    return formatCreditNoteArchiveTitleWithPrefix(formData, core, titleLocale, maxLen);
  }

  const supplementary = getSupplementaryMotifArchiveTitle(formData, typeId);
  const lines = normalizeArchiveTitleItems(formData.items || []);
  if (
    lines.length === 0 &&
    supplementary &&
    typeId !== 'credit-note' &&
    typeId !== 'credit_note'
  ) {
    const sLine = firstMeaningfulLineArchiveTitle(supplementary, maxLen);
    if (sLine && !isTemplatePlaceholderArchiveTitleLine(sLine)) {
      return formatArchiveTitleWithPrefix(typeId, sLine, titleLocale, maxLen) || T(sLine);
    }
  }

  const fromItems = generateTitleFromLineItemsArchive(
    formData.items,
    typeId,
    titleLocale,
    selectedTitle,
    maxLen,
  );
  if (fromItems) return T(fromItems);

  return T(selectedTitle || typeId || 'Document');
}

function parseArchiveTitleJsonField(value, fallback = []) {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : fallback;
    } catch {
      return fallback;
    }
  }
  return fallback;
}

function buildArchiveTitleFormData(document) {
  const typeId = normDocTypeForArchiveTitle(document.type || document.documentType || '');
  const cn = document.creditNote && typeof document.creditNote === 'object' ? document.creditNote : {};
  const docNumber = document.number || document.documentNumber || '';
  const isSupplier =
    document.creditNoteParty === 'supplier' ||
    document.credit_note_party === 'supplier' ||
    document.isSupplier ||
    document.is_supplier ||
    /^AVO-/i.test(String(docNumber));
  const parsedItems = parseArchiveTitleJsonField(document.items);
  const nestedItems = parseArchiveTitleJsonField(cn.items);
  return {
    number: docNumber,
    items: parsedItems.length > 0 ? parsedItems : nestedItems,
    specifications: parseArchiveTitleJsonField(document.specifications),
    requirements: document.requirements || '',
    paymentLines: parseArchiveTitleJsonField(
      document.paymentLines ?? document.payments,
    ),
    paymentReason:
      document.paymentReason ||
      document.payment_reason ||
      document.motif ||
      '',
    returnReason: document.returnReason || document.return_reason || '',
    clientReturnMotif: document.clientReturnMotif || document.client_return_motif || '',
    clientReturnReason: document.clientReturnReason || document.client_return_reason || '',
    creditNoteReason:
      document.creditNoteReason ||
      document.credit_note_reason ||
      cn.reason ||
      '',
    creditNoteType:
      document.creditNoteType ||
      document.credit_type ||
      cn.type ||
      '',
    creditNoteRefundMethod:
      document.creditNoteRefundMethod ||
      document.refundMethod ||
      cn.refundMethod ||
      '',
    creditNoteParty:
      document.creditNoteParty ||
      document.credit_note_party ||
      (isSupplier ? 'supplier' : 'client'),
    isSupplier,
    is_supplier: isSupplier,
    creditNote: cn,
    notes: document.notes || '',
    conditions: document.conditions || '',
    terms: document.terms || '',
    deliveryNotes: document.deliveryNotes || document.delivery_notes || '',
    type: typeId,
  };
}

/** Type archives → id picker DocumentCreation pour l’édition. */
function resolveDocumentCreationTypeConfigId(archiveType, editTypeParam) {
  const raw = String(editTypeParam || archiveType || '').trim();
  if (!raw) return '';
  const key = raw.replace(/-/g, '_').toLowerCase();
  const map = {
    delivery: 'delivery-note',
    delivery_note: 'delivery-note',
    material_exit: 'delivery-note',
    payment_receipt: 'payment-receipt',
    payment_voucher: 'payment-voucher',
    payment_slip: 'payment-voucher',
    credit_note: 'credit-note',
    quote_request: 'quote-request',
    proforma_request: 'proforma-request',
    purchase_order: 'purchase-order',
    return_slip: 'return-slip',
    client_return_slip: 'client-return-slip',
    reception_note: 'reception-note',
    receipt_note: 'reception-note',
    stock_entry: 'stock-entry',
    exit_slip: 'exit-note',
    exit_note: 'exit-note',
    transfer_slip: 'transfer-note',
    transfer_note: 'transfer-note',
    shipping: 'shipping-slip',
    shipping_slip: 'shipping-slip',
  };
  if (map[key]) return map[key];
  return raw.includes('_') ? raw.replace(/_/g, '-') : raw;
}

/** Titre affiché en Archives — régénère les titres auto pour corriger les anciens libellés. */
export function resolveArchiveDocumentTitle(
  document,
  locale = 'ar',
  maxLen = ARCHIVE_TITLE_MAX_LEN,
) {
  if (!document || typeof document !== 'object') return '';
  const typeId = normDocTypeForArchiveTitle(document.type || document.documentType || '');
  if (!usesAutoArchiveTitle(typeId)) {
    return document.title || document.number || '';
  }
  const titleLocale = isSaudiBrand() ? 'ar' : locale;
  return generateDocumentTitle(
    buildArchiveTitleFormData(document),
    { id: typeId, title: document.title || '' },
    titleLocale,
    maxLen,
  );
}

const CREATE_DOCUMENT_CARD_ICONS = {
  quote: DollarSign,
  proforma: FileText,
  'purchase-order': ShoppingCart,
  invoice: Receipt,
  'delivery-note': Truck,
  'credit-note': FileX,
};

const AGENT_EMBED_STYLES = `
  .agent-document-embed-root,
  .agent-document-embed-root > div {
    width: 100% !important;
    max-width: 100% !important;
    min-width: 0 !important;
    box-sizing: border-box;
  }
`;

/** Même recette que le modal Facturier (Nouveau Facturier) : cartes bg-white/5 border-white/20 rounded-xl, labels text-white/70, champs bg-white/10, placeholders white/30, focus ring bleu. */
const DOCUMENT_CREATION_INJECTED_STYLES = `
  .form-group {
    display: flex;
    flex-direction: column;
    gap: 0;
  }
  .form-group label {
    display: block;
    color: rgba(255, 255, 255, 0.7) !important; /* Même teinte que le modal Facturier (gagne sur text-white/80) */
    font-size: 0.9375rem; /* 15px - plus lisible */
    line-height: 1.4;
    font-weight: 500;
    margin-bottom: 0.5rem;
  }
  .form-group input,
  .form-group textarea,
  .form-group select {
    width: 100%;
    padding: 0.6875rem 1rem;
    background-color: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 0.75rem;
    color: #ffffff;
    font-size: 0.9375rem; /* 15px - plus lisible */
    line-height: 1.5;
    transition: all 0.2s ease;
    outline: none;
  }
  .form-group textarea {
    min-height: 4.5rem;
    resize: vertical;
  }
  .form-group input:focus,
  .form-group textarea:focus,
  .form-group select:focus {
    background-color: rgba(255, 255, 255, 0.12);
    border-color: rgba(96, 165, 250, 0.5);
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.45);
  }
  .form-group input::placeholder,
  .form-group textarea::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
  .readonly-input {
    width: 100%;
    padding: 0.6875rem 1rem;
    background-color: rgba(255, 255, 255, 0.06) !important;
    border: 1px solid rgba(255, 255, 255, 0.15) !important;
    border-radius: 0.75rem;
    color: rgba(255, 255, 255, 0.65) !important;
    font-size: 0.9375rem; /* 15px - plus lisible */
    cursor: not-allowed;
    outline: none;
  }
  .form-section {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 0.75rem;
    /* Aligné sur p-5 des cartes (1.25rem) pour le même ressenti vertical */
    padding: 1.25rem;
    margin-bottom: 0; /* l'espace entre blocs = gap du parent (space-y-4) uniquement, sans doubler */
    box-shadow: none;
  }
  /* Même recette que h3 du modal : text-lg font-semibold */
  .section-title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: #ffffff;
    font-size: 1.125rem;
    line-height: 1.3;
    font-weight: 600;
    letter-spacing: 0.01em;
    text-transform: none;
    margin: 0 0 1rem 0;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  .section-title svg { flex-shrink: 0; opacity: 0.95; }
  .section-subtitle {
    font-size: 0.8125rem; /* 13px - plus lisible */
    font-weight: 600;
    color: rgba(255, 255, 255, 0.55);
    text-transform: none;
    letter-spacing: 0.02em;
    margin: 1.25rem 0 0.75rem 0;
  }
  .form-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
  }
  .form-group.full-width { grid-column: 1 / -1; }
  @media (min-width: 768px) {
    .form-group.col-span-2 { grid-column: span 2; }
  }
  .client-info {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 0.75rem;
    padding: 1rem 1.25rem;
  }
  .client-details { color: rgba(255, 255, 255, 0.9); font-size: 0.9375rem; line-height: 1.5; }
  .fiscal-info { color: rgba(255, 255, 255, 0.65); font-size: 0.875rem; display: flex; flex-direction: column; gap: 0.2rem; }
  .items-container { display: flex; flex-direction: column; gap: 0.75rem; }
  .item-row {
    display: flex;
    gap: 0.75rem;
    align-items: flex-start;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 0.75rem;
    padding: 1rem;
  }
  .item-fields { display: flex; flex-wrap: wrap; gap: 0.75rem; flex: 1; }
  .item-fields .form-group { flex: 1; min-width: 140px; }
  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.6rem 1.25rem;
    border-radius: 0.75rem;
    font-size: 0.9375rem; /* 15px - plus lisible */
    font-weight: 500;
    border: 1px solid rgba(255, 255, 255, 0.12);
    background: rgba(59, 130, 246, 0.25);
    color: #fff;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  .btn:hover { background: rgba(59, 130, 246, 0.4); }
  .btn.btn-secondary {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.15);
    color: rgba(255, 255, 255, 0.95);
  }
  .btn.btn-secondary:hover { background: rgba(255, 255, 255, 0.12); }
  .add-item { width: 100%; }
  .btn-remove {
    flex-shrink: 0;
    margin-top: 1.5rem;
    padding: 0.5rem 0.65rem;
    background: rgba(239, 68, 68, 0.15);
    border: 1px solid rgba(239, 68, 68, 0.35) !important;
    border-radius: 0.5rem;
    color: #fecaca;
    cursor: pointer;
  }
  .btn-remove:hover { background: rgba(239, 68, 68, 0.28); }
  .proforma-products-section { margin-top: 0; } /* l'espace = gap du conteneur flex uniquement */

  .totals-container { align-items: start; }
  @media (max-width: 767px) {
    .totals-container { grid-template-columns: 1fr !important; gap: 1.25rem !important; }
  }
  .taxes-config {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    min-width: 0;
  }
  .totals-summary {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 0.75rem;
    padding: 0.75rem 1rem 0.85rem;
    display: flex;
    flex-direction: column;
    min-width: 0;
    overflow: hidden;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
  .totals-summary-header {
    font-size: 0.8125rem;
    font-weight: 800;
    letter-spacing: 0.02em;
    text-transform: none;
    color: rgba(255, 255, 255, 0.92);
    margin: 0 0 0.5rem 0;
    padding: 0.5rem 0.5rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
    background: rgba(255, 255, 255, 0.06);
    margin-left: -1rem;
    margin-right: -1rem;
    margin-top: -0.75rem;
    padding-left: 1rem;
    padding-right: 1rem;
    padding-top: 0.55rem;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
  }
  .totals-summary-body { display: flex; flex-direction: column; }
  .total-line {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 0.75rem 1.25rem;
    padding: 0.5rem 0.25rem;
    font-size: 0.875rem;
    line-height: 1.35;
    border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  }
  .total-line span:first-child {
    color: rgba(255, 255, 255, 0.82);
    font-weight: 600;
  }
  .total-line span:last-child {
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    text-align: right;
    color: #ffffff;
    text-shadow: 0 1px 1px rgba(0, 0, 0, 0.2);
  }
  .total-line.total {
    margin-top: 0.15rem;
    padding: 0.65rem 0.35rem 0.55rem;
    border-bottom: none;
    border-top: 1px solid rgba(255, 255, 255, 0.14);
    background: rgba(255, 255, 255, 0.06);
    margin-left: -1rem;
    margin-right: -1rem;
    margin-bottom: -0.85rem;
    padding-left: 1rem;
    padding-right: 1rem;
    padding-bottom: 0.7rem;
    border-radius: 0 0 0.65rem 0.65rem;
  }
  .total-line.total span:first-child {
    color: rgba(255, 255, 255, 0.95);
    font-weight: 800;
    text-transform: none;
    letter-spacing: 0.02em;
    font-size: 0.78rem;
  }
  .total-line.total span:last-child {
    color: #ffffff;
    font-size: 1.125rem;
    font-weight: 800;
    letter-spacing: 0.02em;
  }
  /* Ghost delete on payment line rows — same look as product lines; wins over generic button rules */
  .payment-line-delete-btn {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    color: rgba(239, 68, 68, 0.8) !important;
    cursor: pointer !important;
    padding: 0.75rem 0.35rem !important;
    border-radius: 0.5rem !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    height: 48px !important;
    min-width: 0 !important;
  }
  .payment-line-delete-btn:hover {
    background: rgba(239, 68, 68, 0.1) !important;
    color: rgba(239, 68, 68, 1) !important;
  }

  /* Sticky document header — hover pop must never trigger scrollbars */
  .document-creation-sticky-bar,
  .document-creation-sticky-bar__row,
  .document-creation-sticky-bar__actions {
    overflow: visible !important;
  }
  .document-creation-sticky-bar__actions {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    flex-shrink: 0;
    gap: 0.5rem;
    padding-block: 0.4rem;
  }
  @media (min-width: 768px) {
    .document-creation-sticky-bar__actions {
      gap: 0.75rem;
    }
  }
  .document-creation-action-btn {
    flex-shrink: 0;
    transition: border-color 0.2s ease, background-color 0.2s ease, opacity 0.2s ease;
  }
`;

const isPaymentReceiptDoc = (t) => {
  const id = t?.id;
  return id === 'payment-receipt' || id === 'payment_receipt';
};

const isPaymentVoucherDoc = (t) => {
  const id = t?.id;
  return id === 'payment-voucher' || id === 'payment_voucher' || id === 'payment_slip';
};

/** Per-type i18n keys when label differs from the status code (mirrors ArchivesFacturation). */
const STATUS_LABEL_KEY_OVERRIDES = {
  return_slip: {
    processing: 'processing_short',
    received: 'received_supplier',
    rejected: 'refused',
  },
  receipt_note: {
    partial: 'partial_short',
  },
};

const normalizeDocumentStatus = (typeId, status, extraCandidates = []) =>
  resolveFormStatusFromArchive(typeId, status, extraCandidates);

const resolveStatusDocumentTypeKey = (typeId) => {
  if (typeId == null || typeId === '') return 'default';
  return String(typeId).replace(/-/g, '_');
};

const getStatusLabelKeyForType = (typeId, statusKey) => {
  const typeKey = resolveStatusDocumentTypeKey(typeId);
  const canonical =
    typeKey === 'reception_note' ? 'receipt_note' : typeKey === 'delivery_note' ? 'delivery' : typeKey;
  const overrides = STATUS_LABEL_KEY_OVERRIDES[canonical];
  return overrides?.[statusKey] || statusKey;
};

function resolveBonPourTitleKey(docId) {
  const kn = String(docId || '').replace(/-/g, '_');
  if (kn === 'quote') return 'quote';
  if (kn === 'proforma') return 'proforma';
  if (kn === 'invoice') return 'invoice';
  if (kn === 'purchase_order') return 'purchase_order';
  if (kn === 'delivery' || kn === 'delivery_note') return 'delivery_note';
  return 'default';
}

function resolveReceivedByTitleKey(docId, creditNoteParty) {
  const kn = String(docId || '').replace(/-/g, '_');
  if (kn === 'credit_note' && creditNoteParty === 'supplier') return 'received_by_supplier';
  if (['quote', 'proforma', 'invoice', 'delivery', 'delivery_note', 'payment_receipt', 'credit_note'].includes(kn)) {
    return 'received_by_client';
  }
  if (['quote_request', 'proforma_request', 'purchase_order', 'return_slip', 'payment_voucher'].includes(kn)) {
    return 'received_by_supplier';
  }
  if (['reception_note', 'receipt_note', 'stock_entry', 'client_return_slip'].includes(kn)) {
    return 'received_by_company';
  }
  return 'received_by_recipient';
}

const DocumentCreation = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language && i18n.language.startsWith('ar');
  const pickerLabel = (docId, field, fallback) => getPickerCardField(t, docId, field, fallback);
  const docDisplayTitle = (doc) => {
    if (!doc) return '';
    if (isCreditNoteDocType(doc.id)) {
      return formData.creditNoteParty === 'supplier'
        ? CREDIT_NOTE_LABEL_SUPPLIER
        : CREDIT_NOTE_LABEL_CLIENT;
    }
    const normalizedId = String(doc.id || '').replace(/_/g, '-');
    return normalizedId ? pickerLabel(normalizedId, 'title', doc.title) : doc?.title || '';
  };
  const statusLbl = (key) => t(`document_creation.form.statuses.${key}`);
  const documentStatusValue = (typeId = selectedType?.id) =>
    normalizeDocumentStatus(typeId, formData.status);
  const handleFormStatusChange = (value) => {
    const typeId = selectedType?.id;
    const normalized = normalizeDocumentStatus(typeId, value);
    setFormData((prev) => {
      const next = { ...prev, status: normalized };
      const u = resolveStatusDocumentTypeKey(typeId);
      if (u === 'credit_note') next.creditNoteStatus = normalized;
      if (u === 'client_return_slip') next.clientReturnStatus = normalized;
      if (u === 'return_slip') next.returnStatus = normalized;
      if (u === 'receipt_note' || u === 'reception_note') next.receptionStatus = normalized;
      if (u === 'exit_slip' || u === 'exit_note') next.exitNoteStatus = normalized;
      if (u === 'transfer_slip' || u === 'transfer_note') next.transferNoteStatus = normalized;
      return next;
    });
  };
  const prioritySelectOptions = [
    { value: 'basse', label: t('document_creation.form.priority_levels.low') },
    { value: 'normale', label: t('document_creation.form.priority_levels.normal') },
    { value: 'haute', label: t('document_creation.form.priority_levels.high') },
    { value: 'urgente', label: t('document_creation.form.priority_levels.urgent') },
  ];
  const prioritySelectOptionsEn = [
    { value: 'low', label: t('document_creation.form.priority_levels.low') },
    { value: 'normal', label: t('document_creation.form.priority_levels.normal') },
    { value: 'high', label: t('document_creation.form.priority_levels.high') },
    { value: 'urgent', label: t('document_creation.form.priority_levels.urgent') },
  ];
  const delaySelectOptions = useMemo(
    () => [
      { value: 'Immédiat', label: t('document_creation.form.delay_options.immediate') },
      { value: '24 heures', label: t('document_creation.form.delay_options.24h') },
      { value: '2-3 jours ouvrables', label: t('document_creation.form.delay_options.2_3_days') },
      { value: '1 semaine', label: t('document_creation.form.delay_options.1_week') },
      { value: '10 jours ouvrables', label: t('document_creation.form.delay_options.10_days') },
      { value: '15 jours ouvrables', label: t('document_creation.form.delay_options.15_days') },
      { value: '3 semaines', label: t('document_creation.form.delay_options.3_weeks') },
      { value: '1 mois', label: t('document_creation.form.delay_options.1_month') },
      { value: '6 semaines', label: t('document_creation.form.delay_options.6_weeks') },
      { value: '2 mois', label: t('document_creation.form.delay_options.2_months') },
      { value: '3 mois', label: t('document_creation.form.delay_options.3_months') },
    ],
    [t],
  );
  const projectTypeOptions = useMemo(
    () => [
      { value: 'sur_mesure', label: t('document_creation.form.project_types.custom') },
      { value: 'standard', label: t('document_creation.form.project_types.standard') },
      { value: 'maintenance', label: t('document_creation.form.project_types.maintenance') },
      { value: 'installation', label: t('document_creation.form.project_types.installation') },
      { value: 'formation', label: t('document_creation.form.project_types.training') },
      { value: 'consulting', label: t('document_creation.form.project_types.consulting') },
    ],
    [t],
  );
  const productsSectionTitle = (typeId) => {
    const keyMap = {
      quote: 'quote',
      proforma: 'proforma',
      invoice: 'invoice',
      'purchase-order': 'purchase_order',
      'delivery-note': 'delivery_note',
      delivery: 'delivery_note',
    };
    const key = keyMap[typeId] || 'default';
    return t(`document_creation.form.products_section.${key}`);
  };
  const paymentMethodOptions = useMemo(
    () => [
      { value: 'virement', label: t('document_creation.form.payment_methods.bank_transfer') },
      { value: 'cheque', label: t('document_creation.form.payment_methods.cheque') },
      { value: 'especes', label: t('document_creation.form.payment_methods.cash') },
      { value: 'carte', label: t('document_creation.form.payment_methods.card') },
      { value: 'effet', label: t('document_creation.form.payment_methods.trade_bill') },
      { value: 'traite', label: t('document_creation.form.payment_methods.promissory') },
    ],
    [t],
  );
  const paymentTermsOptions = useMemo(
    () => [
      { value: 'comptant', label: t('document_creation.form.payment_terms_options.cash') },
      { value: '15', label: t('document_creation.form.payment_terms_options.15') },
      { value: '30', label: t('document_creation.form.payment_terms_options.30') },
      { value: '45', label: t('document_creation.form.payment_terms_options.45') },
      { value: '60', label: t('document_creation.form.payment_terms_options.60') },
      { value: '90', label: t('document_creation.form.payment_terms_options.90') },
      { value: 'acompte_30', label: t('document_creation.form.payment_terms_options.deposit_30') },
      { value: 'acompte_50', label: t('document_creation.form.payment_terms_options.deposit_50') },
    ],
    [t],
  );
  const paymentTermsExtendedOptions = useMemo(
    () => [
      { value: 'comptant', label: t('document_creation.form.payment_terms_options.cash') },
      { value: '30_jours_fin_mois', label: t('document_creation.form.payment_terms_options.30_jours_fin_mois') },
      { value: '30_jours_nets', label: t('document_creation.form.payment_terms_options.30_jours_nets') },
      { value: '45_jours_fin_mois', label: t('document_creation.form.payment_terms_options.45_jours_fin_mois') },
      { value: '60_jours_fin_mois', label: t('document_creation.form.payment_terms_options.60_jours_fin_mois') },
      { value: '90_jours', label: t('document_creation.form.payment_terms_options.90_jours') },
      { value: 'sur_facturation', label: t('document_creation.form.payment_terms_options.sur_facturation') },
      { value: 'echelonne', label: t('document_creation.form.payment_terms_options.installment') },
    ],
    [t],
  );
  const paymentTermsDaysValueOptions = useMemo(
    () => [
      { value: 'Comptant', label: t('document_creation.form.payment_terms_options.cash') },
      { value: '15 jours', label: t('document_creation.form.payment_terms_options.15') },
      { value: '30 jours', label: t('document_creation.form.payment_terms_options.30') },
      { value: '45 jours', label: t('document_creation.form.payment_terms_options.45') },
      { value: '60 jours', label: t('document_creation.form.payment_terms_options.60') },
      { value: '90 jours', label: t('document_creation.form.payment_terms_options.90') },
      { value: 'Acompte 30%', label: t('document_creation.form.payment_terms_options.deposit_30_short') },
      { value: '50% à la commande', label: t('document_creation.form.payment_terms_options.deposit_50') },
    ],
    [t],
  );
  const paymentMethodWithInstallmentOptions = useMemo(
    () => [
      ...paymentMethodOptions,
      { value: 'echeances', label: t('document_creation.form.payment_installment') },
    ],
    [paymentMethodOptions, t],
  );
  const deliveryModeSimpleOptions = useMemo(
    () => [
      { value: 'standard', label: t('document_creation.form.delivery_modes.standard_short') },
      { value: 'express', label: t('document_creation.form.delivery_modes.express_short') },
      { value: 'retrait', label: t('document_creation.form.delivery_modes.pickup') },
      { value: 'coursier', label: t('document_creation.form.delivery_modes.courier') },
    ],
    [t],
  );
  const purchaseOrderDeliveryModeOptions = useMemo(
    () => [
      { value: 'Franco de port', label: t('document_creation.form.delivery_modes.franco_port') },
      { value: 'FOB', label: t('document_creation.form.delivery_modes.fob') },
      { value: 'EXW', label: t('document_creation.form.delivery_modes.exw') },
      { value: 'CIF', label: t('document_creation.form.delivery_modes.cif') },
      { value: 'DDP', label: t('document_creation.form.delivery_modes.ddp') },
      { value: 'DAP', label: t('document_creation.form.delivery_modes.dap') },
      { value: 'Livraison standard', label: t('document_creation.form.delivery_modes.standard') },
      { value: 'Livraison express', label: t('document_creation.form.delivery_modes.express') },
    ],
    [t],
  );
  const carrierSelectOptions = useMemo(
    () => [
      { value: 'SMSA', label: t('document_creation.form.carriers.smsa') },
      { value: 'Aramex', label: t('document_creation.form.carriers.aramex') },
      { value: 'DHL', label: t('document_creation.form.carriers.dhl') },
      { value: 'Saudi Post', label: t('document_creation.form.carriers.saudi_post') },
      { value: 'FedEx', label: t('document_creation.form.carriers.fedex') },
      { value: 'UPS', label: t('document_creation.form.carriers.ups') },
    ],
    [t],
  );
  const creditNoteTypeOptions = [
    { value: 'retour', label: t('document_creation.form.credit_note_types.return') },
    { value: 'remise', label: t('document_creation.form.credit_note_types.discount') },
    { value: 'correction', label: t('document_creation.form.credit_note_types.correction') },
    { value: 'annulation', label: t('document_creation.form.credit_note_types.partial_cancel') },
    { value: 'défaut', label: t('document_creation.form.credit_note_types.non_conformity') },
    { value: 'ristourne', label: t('document_creation.form.credit_note_types.year_end_rebate') },
  ];
  const entrySourceOptions = [
    { value: 'supplier', label: t('document_creation.form.entry_sources.supplier') },
    { value: 'production', label: t('document_creation.form.entry_sources.production') },
    { value: 'transfer', label: t('document_creation.form.entry_sources.transfer') },
    { value: 'adjustment', label: t('document_creation.form.entry_sources.adjustment') },
    { value: 'return', label: t('document_creation.form.entry_sources.return') },
    { value: 'purchase', label: t('document_creation.form.entry_sources.purchase') },
  ];
  const formLabel = (key) => t(`document_creation.form.${key}`);
  const legalDefault = (key) => formLabel(`legal_defaults.${key}`);
  const { isDashboardLightMode } = useGlobalTheme();
  const documentCreationLightClass = isDashboardLightMode ? 'document-creation-light' : '';
  const documentCreationStickyBarStyle = isDashboardLightMode
    ? {
        background: 'rgba(255, 255, 255, 0.96)',
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.05), 0 4px 14px rgba(15, 23, 42, 0.07)',
      }
    : {
        background: 'rgba(255, 255, 255, 0.08)',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      };
  const { settings, incrementDocumentCounter } = useSettings();
  const facturationConfig = useFacturationConfig();
  const requirePartner = facturationConfig.requireClient !== false;
  const facturationTableType = facturationConfig.tableType || settings?.invoicing?.tableType || 'standard';
  const { clients: allClients, suppliers: allSuppliers, getActiveClients, getActiveSuppliers } = useClients();
  const { documents, addDocument, updateDocument } = useDocuments();
  const { employees, getActiveEmployees } = useEmployees();
  const { products, getActiveProducts } = useProducts();
  const { createReturnFromDocument } = useReturns();
  const { createPayment } = usePayments();
  const { getOrders, convertOrderToSale, createInvoiceFromPurchase, getPurchases, createTransactionFromDocument, updateTransactionStatus } = useAppData();
  
  // URL parameters for editing
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const editId = searchParams.get('editId');
  const editType = searchParams.get('type');
  const isAgentEmbed = searchParams.get('embed') === 'agent';
  
  // Facturiers state
  const [facturiers, setFacturiers] = useState([]);
  
  // Warehouses state
  const [warehouses, setWarehouses] = useState([]);
  
  // State
  const [currentStep, setCurrentStep] = useState('select'); // 'select', 'create', or 'preview'
  const [selectedType, setSelectedType] = useState(null);
  const [formData, setFormData] = useState({});
  const [itemType, setItemType] = useState('product'); // 'product' or 'service' toggle
  const [paymentLineAddType, setPaymentLineAddType] = useState('purchase_order'); // BC or Avoir sur achat
  const [paymentReceiptLineAddType, setPaymentReceiptLineAddType] = useState('invoice'); // Facture or Avoir sur vente
  const [partnerCardType, setPartnerCardType] = useState('client'); // 'client' | 'supplier' — used for docs that accept both
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [isFromConversion, setIsFromConversion] = useState(false);
  
  // ?? Section Order State for Drag & Drop
  const [sectionOrder, setSectionOrder] = useState(['Notes', 'Conditions', 'Objectif', 'Observations', 'Garanties', 'Validité', 'Motif', 'Instructions']);
  const [draggedSection, setDraggedSection] = useState(null);
  
  // ?? Tax Order State for Drag & Drop
  const [taxOrder, setTaxOrder] = useState(() => getDefaultTaxOrder());
  const [draggedTax, setDraggedTax] = useState(null);
  
  // ?? State for Product/Service type selection per item
  const [itemTypes, setItemTypes] = useState({});
  
  // ?? State for searchable product dropdown
  const [productSearch, setProductSearch] = useState({});
  const [showProductDropdown, setShowProductDropdown] = useState({});
  
  // ?? Track if content is auto-generated or manually edited
  const [isAutoGenerated, setIsAutoGenerated] = useState(true);
  
  // Import from Order states
  const [showImportFromOrder, setShowImportFromOrder] = useState(false);
  const [availableOrders, setAvailableOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // Import from Purchase states
  const [showImportFromPurchase, setShowImportFromPurchase] = useState(false);
  
  // Paper format selection state
  const [selectedPaperFormat, setSelectedPaperFormat] = useState('A4'); // 'A4' or 'A5'
  const [availablePurchases, setAvailablePurchases] = useState([]);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  
  // ?? Searchable/Creatable dropdown states
  const [availableClientsForDropdown, setAvailableClientsForDropdown] = useState([]);
  const [availableSuppliersForDropdown, setAvailableSuppliersForDropdown] = useState([]);
  const [availableFacturiersForDropdown, setAvailableFacturiersForDropdown] = useState([]);
  const [availableEmployeesForDropdown, setAvailableEmployeesForDropdown] = useState([]);
  const [availableWarehousesForDropdown, setAvailableWarehousesForDropdown] = useState([]);
  const [availableProductsForDropdown, setAvailableProductsForDropdown] = useState([]);
  const [availableServicesForDropdown, setAvailableServicesForDropdown] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  
  // ?? Helper function to get icon color based on document category
  const getIconColor = () => {
    if (isSaudiBrand()) {
      return '#a67c3d'; // Qawaem bronze
    }

    if (!selectedType) return '#60a5fa'; // default blue
    
    if (selectedType.id === 'credit-note' || selectedType.id === 'credit_note') {
      return formData.creditNoteParty === 'supplier' ? '#60a5fa' : '#4ade80';
    }

    // ACHATS - BLUE
    const achatsDocuments = ['quote-request', 'purchase-order', 'reception-note', 'payment-voucher'];
    if (achatsDocuments.includes(selectedType.id)) {
      return '#60a5fa'; // blue-400
    }
    
    // VENTES - GREEN
    const ventesDocuments = ['quote', 'invoice', 'simplified-invoice', 'credit-note', 'debit-note'];
    if (ventesDocuments.includes(selectedType.id)) {
      return '#4ade80'; // green-400
    }
    
    // LOGISTIQUE documents moved to INVENTAIRE module
    // (reception-note, stock-entry, exit-note, transfer-note, shipping-slip)
    
    return '#60a5fa'; // default blue
  };

  // ?? Helper function to map status to icon - UNIQUE ICON FOR EACH STATUS
  const getStatusIcon = (statusKey, color) => {
    const size = 14;
    const style = { color };
    
    // Financial statuses
    if (statusKey === 'paid') return <CheckCircle size={size} style={style} />;
    if (statusKey === 'partially_paid') return <DollarSign size={size} style={style} />;
    if (statusKey === 'pending') return <Clock size={size} style={style} />;
    if (statusKey === 'overdue') return <AlertTriangle size={size} style={style} />;
    if (statusKey === 'draft') return <FileText size={size} style={style} />;
    if (statusKey === 'partial') return <PackagePlus size={size} style={style} />;
    
    // Preparatory statuses
    if (statusKey === 'accepted') return <PlusCircle size={size} style={style} />;
    if (statusKey === 'rejected') return <FileX size={size} style={style} />;
    if (statusKey === 'approved') return <FileCheck size={size} style={style} />;
    if (statusKey === 'negotiating') return <ArrowRightLeft size={size} style={style} />;
    if (statusKey === 'expired') return <Calendar size={size} style={style} />;
    
    // Logistic statuses
    if (statusKey === 'delivered') return <Truck size={size} style={style} />;
    if (statusKey === 'in_transit') return <Package size={size} style={style} />;
    if (statusKey === 'shipped') return <Send size={size} style={style} />;
    if (statusKey === 'received') return <Download size={size} style={style} />;
    if (statusKey === 'canceled') return <X size={size} style={style} />;
    if (statusKey === 'cancelled') return <X size={size} style={style} />;
    if (statusKey === 'processed') return <Settings size={size} style={style} />;
    
    // Communication statuses
    if (statusKey === 'sent') return <ArrowRight size={size} style={style} />;
    if (statusKey === 'answered') return <Receipt size={size} style={style} />;
    if (statusKey === 'processing') return <RefreshCw size={size} style={style} />;
    
    // Completion statuses
    if (statusKey === 'completed') return <CheckSquare size={size} style={style} />;
    if (statusKey === 'verified') return <Eye size={size} style={style} />;
    
    // Credit note / return statuses
    if (statusKey === 'issued') return <FileOutput size={size} style={style} />;
    if (statusKey === 'applied') return <Save size={size} style={style} />;
    if (statusKey === 'refunded') return <Banknote size={size} style={style} />;
    if (statusKey === 'transferred') return <RefreshCcw size={size} style={style} />;
    if (statusKey === 'consolidated') return <ClipboardList size={size} style={style} />;
    
    // Default icon for any new statuses
    return <FileText size={size} style={style} />;
  };

  // Status colors matching Archives statusConfig - UNIQUE COLOR FOR EACH STATUS
  const statusColors = {
    // Financial statuses
    paid: '#10b981',           // Green
    partially_paid: '#84cc16',  // Lime
    pending: '#f59e0b',        // Amber
    overdue: '#ef4444',        // Red
    draft: '#64748b',          // Gray
    partial: '#eab308',        // Yellow
    
    // Preparatory statuses
    accepted: '#34d399',       // Emerald (lighter green)
    rejected: '#dc2626',       // Dark red
    approved: '#22c55e',       // Green (different shade)
    negotiating: '#3b82f6',    // Blue
    expired: '#f97316',        // Orange
    
    // Logistic statuses
    delivered: '#059669',      // Dark green
    in_transit: '#06b6d4',     // Cyan
    shipped: '#0284c7',        // Sky blue
    received: '#8b5cf6',       // Purple
    canceled: '#f43f5e',       // Rose
    cancelled: '#e11d48',      // Pink-red
    processed: '#14b8a6',      // Teal
    
    // Communication statuses
    sent: '#0891b2',           // Dark cyan
    answered: '#a855f7',       // Violet
    processing: '#6366f1',     // Indigo
    
    // Completion statuses
    completed: '#16a34a',      // Forest green
    verified: '#15803d',       // Dark emerald
    
    // Credit note / return statuses
    issued: '#0d9488',         // Teal green
    applied: '#9333ea',        // Purple (different)
    refunded: '#10d5a0',       // Bright mint green
    transferred: '#22d3ee',    // Bright cyan
    consolidated: '#fb923c'    // Light orange
  };

  // ?? Helper function to get document-specific status options
  // Built from facturationArchiveStatusOrder.js — keep in sync with Archives allowedStatuses.
  const getStatusOptions = (documentTypeId) => {
    const statuses = getCanonicalStatusOrder(documentTypeId);
    const baseOptions = statuses.map((value) => ({
      value,
      label: statusLbl(getStatusLabelKeyForType(documentTypeId, value)),
    }));

    return baseOptions.map((opt) => ({
      ...opt,
      icon: getStatusIcon(opt.value, statusColors[opt.value] || '#64748b'),
    }));
  };
  
  // Bons de commande / livraison — même logique que quoteRequestDocuments / proformaRequestDocuments
  const purchaseOrders = useMemo(() => {
    return documents
      .filter(
        (doc) =>
          String(doc?.type || doc?.documentType || '')
            .trim()
            .replace(/-/g, '_')
            .toLowerCase() === 'purchase_order',
      )
      .filter((doc) => doc?.number && String(doc.number).trim())
      .map((doc) => {
        const reference = String(doc.number).trim();
        const title =
          resolveArchiveDocumentTitle(doc, i18n.language) ||
          String(doc.title || '').trim();
        const dateStr = doc.date
          ? new Date(doc.date).toLocaleDateString(getBrandLocale(i18n.language))
          : '';
        const label =
          title && title !== reference
            ? `${reference} — ${title}${dateStr ? ` (${dateStr})` : ''}`
            : dateStr
              ? `${reference} (${dateStr})`
              : reference;
        return { reference, label, date: doc.date };
      })
      .sort((a, b) => {
        const ta = a.date ? new Date(a.date).getTime() : 0;
        const tb = b.date ? new Date(b.date).getTime() : 0;
        return tb - ta;
      });
  }, [documents, i18n.language]);

  const supplierCreditNotes = useMemo(
    () =>
      buildSupplierCreditNotePickerList(documents, (doc) =>
        resolveArchiveDocumentTitle(doc, i18n.language),
      ),
    [documents, i18n.language],
  );

  const clientCreditNotes = useMemo(
    () =>
      buildClientCreditNotePickerList(documents, (doc) =>
        resolveArchiveDocumentTitle(doc, i18n.language),
      ),
    [documents, i18n.language],
  );

  const clientInvoicesForReceipt = useMemo(
    () =>
      buildClientInvoicePickerList(documents, (doc) =>
        resolveArchiveDocumentTitle(doc, i18n.language),
      ),
    [documents, i18n.language],
  );

  const applyPaymentVoucherReferencePatch = useCallback(
    (patch) => {
      if (!patch || typeof patch !== 'object') return;
      const merged = matchPaymentVoucherSupplierId(patch, availableSuppliersForDropdown);
      setFormData((prev) => {
        const next = { ...prev };
        Object.entries(merged).forEach(([key, val]) => {
          if (val !== undefined) next[key] = val;
        });
        return next;
      });
    },
    [availableSuppliersForDropdown],
  );

  const handlePaymentVoucherLineRefChange = useCallback(
    (index, value) => {
      setFormData((prev) => {
        const lines = [...(prev.paymentLines || [])];
        const line = { ...(lines[index] || emptyPaymentVoucherLine('purchase_order')) };
        const lineType = line.lineType || 'purchase_order';
        const ref = value != null ? String(value).trim() : '';
        const usedRefs = getUsedPaymentVoucherLineRefs(lines, index, lineType);
        if (ref && usedRefs.has(ref)) return prev;

        const doc = ref ? findArchiveDocumentByNumber(documents, ref) : null;

        if (lineType === 'credit_note') {
          if (doc) Object.assign(line, buildPaymentVoucherLineFromCreditNote(doc));
          else {
            Object.assign(line, {
              lineType: 'credit_note',
              reference: ref,
              description: ref ? `Règlement avoir sur achat ${ref}` : '',
              amount: 0,
              relatedInvoice: '',
            });
          }
        } else if (doc) {
          Object.assign(line, buildPaymentVoucherLineFromPurchaseOrder(doc));
        } else {
          Object.assign(line, {
            lineType: 'purchase_order',
            reference: ref,
            description: ref ? `Règlement bon de commande ${ref}` : '',
            amount: 0,
            relatedInvoice: '',
          });
        }
        lines[index] = line;

        const headerPatch = syncPaymentVoucherHeaderFromLines(lines);
        const next = { ...prev, paymentLines: lines, ...headerPatch };

        if (doc) {
          const docPatch =
            lineType === 'credit_note'
              ? buildPaymentVoucherPatchFromCreditNote(doc)
              : buildPaymentVoucherPatchFromPurchaseOrder(doc);
          const merged = matchPaymentVoucherSupplierId(docPatch, availableSuppliersForDropdown);
          if (merged.supplierName) next.supplierName = merged.supplierName;
          if (merged.supplier) next.supplier = merged.supplier;
          if (merged.client) next.client = merged.client;
          if (lineType === 'credit_note' && docPatch.purchaseOrderRef) {
            const bcSet = new Set(
              String(headerPatch.purchaseOrderRef || '')
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean),
            );
            bcSet.add(docPatch.purchaseOrderRef);
            next.purchaseOrderRef = [...bcSet].join(', ');
          }
        }
        return next;
      });
    },
    [documents, availableSuppliersForDropdown],
  );

  const handlePaymentVoucherLineAmountChange = useCallback((index, val) => {
    setFormData((prev) => {
      const lines = [...(prev.paymentLines || [])];
      lines[index] = { ...(lines[index] || emptyPaymentVoucherLine('purchase_order')), amount: parseFloat(val) || 0 };
      return { ...prev, paymentLines: lines };
    });
  }, []);

  const handlePaymentLineAddTypeClick = useCallback(
    (lineType) => {
      setFormData((prev) => {
        if (paymentLineAddType === lineType) {
          return {
            ...prev,
            paymentLines: [...(prev.paymentLines || []), emptyPaymentVoucherLine(lineType)],
          };
        }
        const lines = (prev.paymentLines || []).map((line) => {
          if (!String(line?.reference || '').trim()) {
            return emptyPaymentVoucherLine(lineType);
          }
          return line;
        });
        if (!lines.length) lines.push(emptyPaymentVoucherLine(lineType));
        const headerPatch = syncPaymentVoucherHeaderFromLines(lines);
        return { ...prev, paymentLines: lines, ...headerPatch };
      });
      if (paymentLineAddType !== lineType) {
        setPaymentLineAddType(lineType);
      }
    },
    [paymentLineAddType],
  );

  const addPaymentVoucherLine = useCallback((lineType) => {
    setFormData((prev) => ({
      ...prev,
      paymentLines: [...(prev.paymentLines || []), emptyPaymentVoucherLine(lineType)],
    }));
  }, []);

  const removePaymentVoucherLine = useCallback((index) => {
    setFormData((prev) => {
      const lines = (prev.paymentLines || []).filter((_, i) => i !== index);
      const nextLines = lines.length
        ? lines
        : [emptyPaymentVoucherLine(paymentLineAddType || 'purchase_order')];
      const headerPatch = syncPaymentVoucherHeaderFromLines(nextLines);
      return { ...prev, paymentLines: nextLines, ...headerPatch };
    });
  }, [paymentLineAddType]);

  const handlePaymentReceiptLineRefChange = useCallback(
    (index, value) => {
      setFormData((prev) => {
        const lines = [...(prev.paymentLines || [])];
        const line = { ...(lines[index] || emptyPaymentReceiptLine('invoice')) };
        const lineType = line.lineType || 'invoice';
        const ref = value != null ? String(value).trim() : '';
        const usedRefs = getUsedPaymentReceiptLineRefs(lines, index, lineType);
        if (ref && usedRefs.has(ref)) return prev;

        const doc = ref ? findArchiveDocumentByNumber(documents, ref) : null;

        if (lineType === 'credit_note') {
          if (doc) Object.assign(line, buildPaymentReceiptLineFromClientCreditNote(doc));
          else {
            Object.assign(line, {
              lineType: 'credit_note',
              reference: ref,
              description: ref ? `Règlement avoir sur vente ${ref}` : '',
              amount: 0,
              relatedInvoice: '',
            });
          }
        } else if (doc) {
          Object.assign(line, buildPaymentReceiptLineFromInvoice(doc));
        } else {
          Object.assign(line, {
            lineType: 'invoice',
            reference: ref,
            description: ref ? `Encaissement facture ${ref}` : '',
            amount: 0,
            relatedInvoice: ref,
          });
        }
        lines[index] = line;

        const headerPatch = syncPaymentReceiptHeaderFromLines(lines);
        const next = { ...prev, paymentLines: lines, ...headerPatch };

        if (doc) {
          const docPatch =
            lineType === 'credit_note'
              ? buildPaymentReceiptPatchFromClientCreditNote(doc)
              : buildPaymentReceiptPatchFromInvoice(doc);
          const merged = matchPaymentReceiptClientId(docPatch, availableClientsForDropdown);
          if (merged.clientName) next.clientName = merged.clientName;
          if (merged.client) next.client = merged.client;
        }
        return next;
      });
    },
    [documents, availableClientsForDropdown],
  );

  const handlePaymentReceiptLineAmountChange = useCallback((index, val) => {
    setFormData((prev) => {
      const lines = [...(prev.paymentLines || [])];
      lines[index] = {
        ...(lines[index] || emptyPaymentReceiptLine('invoice')),
        amount: parseFloat(val) || 0,
      };
      return { ...prev, paymentLines: lines };
    });
  }, []);

  const handlePaymentReceiptLineAddTypeClick = useCallback(
    (lineType) => {
      setFormData((prev) => {
        if (paymentReceiptLineAddType === lineType) {
          return {
            ...prev,
            paymentLines: [...(prev.paymentLines || []), emptyPaymentReceiptLine(lineType)],
          };
        }
        const lines = (prev.paymentLines || []).map((line) => {
          if (!String(line?.reference || '').trim()) {
            return emptyPaymentReceiptLine(lineType);
          }
          return line;
        });
        if (!lines.length) lines.push(emptyPaymentReceiptLine(lineType));
        const headerPatch = syncPaymentReceiptHeaderFromLines(lines);
        return { ...prev, paymentLines: lines, ...headerPatch };
      });
      if (paymentReceiptLineAddType !== lineType) {
        setPaymentReceiptLineAddType(lineType);
      }
    },
    [paymentReceiptLineAddType],
  );

  const handleClientCreditNoteInvoiceRefChange = useCallback(
    (value) => {
      const ref = value != null ? String(value).trim() : '';
      setFormData((prev) => {
        const prevInv =
          prev.originalInvoice && typeof prev.originalInvoice === 'object'
            ? prev.originalInvoice
            : { date: '', amount: 0 };
        if (!ref) {
          return {
            ...prev,
            originalInvoice: { ...prevInv, number: '' },
            originalInvoiceNumber: '',
          };
        }

        const doc = findArchiveDocumentByNumber(documents, ref);
        if (!doc) {
          return {
            ...prev,
            originalInvoice: { ...prevInv, number: ref },
            originalInvoiceNumber: ref,
          };
        }

        let total = Number(doc.amount ?? doc.total) || 0;
        if (!total && Array.isArray(doc.items) && doc.items.length > 0) {
          total = doc.items.reduce(
            (sum, item) =>
              sum +
              (Number(item.total) ||
                Number(item.quantity) * Number(item.price || item.unitPrice) ||
                0),
            0,
          );
        }
        const dateStr = doc.date ? String(doc.date).slice(0, 10) : '';
        const next = {
          ...prev,
          originalInvoice: {
            number: ref,
            date: dateStr,
            amount: total || 0,
          },
          originalInvoiceNumber: ref,
          originalInvoiceDate: dateStr,
          originalInvoiceAmount: total || 0,
        };

        const clientName = String(
          doc.clientName || doc.client_name || doc.client || '',
        ).trim();
        if (clientName) next.clientName = clientName;
        const clientId = doc.clientId ?? doc.client_id;
        if (clientId != null && String(clientId).trim() !== '') {
          next.client = String(clientId);
        }

        return next;
      });
    },
    [documents],
  );

  const removePaymentReceiptLine = useCallback((index) => {
    setFormData((prev) => {
      const lines = (prev.paymentLines || []).filter((_, i) => i !== index);
      const nextLines = lines.length
        ? lines
        : [emptyPaymentReceiptLine(paymentReceiptLineAddType || 'invoice')];
      const headerPatch = syncPaymentReceiptHeaderFromLines(nextLines);
      return { ...prev, paymentLines: nextLines, ...headerPatch };
    });
  }, [paymentReceiptLineAddType]);

  const deliveryNotes = useMemo(() => {
    return documents
      .filter((doc) => {
        const t = String(doc?.type || doc?.documentType || '')
          .trim()
          .replace(/-/g, '_')
          .toLowerCase();
        return t === 'delivery' || t === 'delivery_note';
      })
      .filter((doc) => doc?.number && String(doc.number).trim())
      .map((doc) => {
        const reference = String(doc.number).trim();
        const title =
          resolveArchiveDocumentTitle(doc, i18n.language) ||
          String(doc.title || '').trim();
        const dateStr = doc.date
          ? new Date(doc.date).toLocaleDateString(getBrandLocale(i18n.language))
          : '';
        const label =
          title && title !== reference
            ? `${reference} — ${title}${dateStr ? ` (${dateStr})` : ''}`
            : dateStr
              ? `${reference} (${dateStr})`
              : reference;
        return { reference, label, date: doc.date };
      })
      .sort((a, b) => {
        const ta = a.date ? new Date(a.date).getTime() : 0;
        const tb = b.date ? new Date(b.date).getTime() : 0;
        return tb - ta;
      });
  }, [documents, i18n.language]);
  
  // Filter invoices for dropdown in bordereau de retour
  const invoices = useMemo(() => {
    const filtered = documents.filter(doc => 
      doc.type === 'invoice' || 
      doc.documentType === 'invoice' ||
      (doc.title && doc.title.toLowerCase().includes('facture')) ||
      (doc.number && (doc.number.includes('FAC-') || doc.number.includes('DOC-'))) ||
      (doc.label && doc.label === 'Facture')
    ).map((doc, index) => {
      // Generate proper FAC reference instead of using DOC-xxx
      let reference;
      if (doc.number && doc.number.startsWith('FAC-')) {
        // Use existing FAC reference if valid
        reference = doc.number;
      } else {
        // Generate proper FAC reference based on index position
        const number = String(index + 1).padStart(2, '0');
        reference = `FAC-${number}`;
      }
      
      return {
        id: doc.id,
        reference: reference,
        date: doc.date
      };
    });
    
    console.log('Filtered invoices:', filtered);
    return filtered;
  }, [documents]);

  const quoteRequestDocuments = useMemo(() => {
    return documents
      .filter(
        (doc) =>
          String(doc?.type || doc?.documentType || '')
            .trim()
            .replace(/-/g, '_')
            .toLowerCase() === 'quote_request',
      )
      .filter((doc) => doc?.number && String(doc.number).trim())
      .map((doc) => {
        const reference = String(doc.number).trim();
        const title =
          resolveArchiveDocumentTitle(doc, i18n.language) ||
          String(doc.title || '').trim();
        const dateStr = doc.date
          ? new Date(doc.date).toLocaleDateString(getBrandLocale(i18n.language))
          : '';
        const label =
          title && title !== reference
            ? `${reference} — ${title}${dateStr ? ` (${dateStr})` : ''}`
            : dateStr
              ? `${reference} (${dateStr})`
              : reference;
        return { reference, label, date: doc.date };
      })
      .sort((a, b) => {
        const ta = a.date ? new Date(a.date).getTime() : 0;
        const tb = b.date ? new Date(b.date).getTime() : 0;
        return tb - ta;
      });
  }, [documents, i18n.language]);

  const proformaRequestDocuments = useMemo(() => {
    return documents
      .filter(
        (doc) =>
          String(doc?.type || doc?.documentType || '')
            .trim()
            .replace(/-/g, '_')
            .toLowerCase() === 'proforma_request',
      )
      .filter((doc) => doc?.number && String(doc.number).trim())
      .map((doc) => {
        const reference = String(doc.number).trim();
        const title =
          resolveArchiveDocumentTitle(doc, i18n.language) ||
          String(doc.title || '').trim();
        const dateStr = doc.date
          ? new Date(doc.date).toLocaleDateString(getBrandLocale(i18n.language))
          : '';
        const label =
          title && title !== reference
            ? `${reference} — ${title}${dateStr ? ` (${dateStr})` : ''}`
            : dateStr
              ? `${reference} (${dateStr})`
              : reference;
        return { reference, label, date: doc.date };
      })
      .sort((a, b) => {
        const ta = a.date ? new Date(a.date).getTime() : 0;
        const tb = b.date ? new Date(b.date).getTime() : 0;
        return tb - ta;
      });
  }, [documents, i18n.language]);

  const matchesSelectedSalesClient = useCallback(
    (doc) => {
      if (Boolean(doc?.isSupplier ?? doc?.is_supplier)) return false;
      const selectedId = formData.client;
      if (!selectedId) return true;
      const docClientId = doc?.clientId ?? doc?.client_id;
      if (docClientId != null && String(docClientId) === String(selectedId)) return true;
      const selectedClient =
        availableClientsForDropdown.find((c) => String(c.id) === String(selectedId)) ||
        (allClients || []).find((c) => String(c.id) === String(selectedId));
      const selectedName = String(
        selectedClient?.name ||
          selectedClient?.companyName ||
          selectedClient?.company ||
          formData.clientName ||
          '',
      )
        .trim()
        .toLowerCase();
      if (!selectedName) return false;
      const docName = String(doc.clientName || doc.client || '').trim().toLowerCase();
      return docName === selectedName;
    },
    [formData.client, formData.clientName, availableClientsForDropdown, allClients],
  );

  const mapDocumentsToReferenceOptions = useCallback(
    (sourceDocs) =>
      sourceDocs
        .filter((doc) => doc?.number && String(doc.number).trim())
        .map((doc) => {
          const reference = String(doc.number).trim();
          const title =
            resolveArchiveDocumentTitle(doc, i18n.language) ||
            String(doc.title || '').trim();
          const dateStr = doc.date
            ? new Date(doc.date).toLocaleDateString(getBrandLocale(i18n.language))
            : '';
          const label =
            title && title !== reference
              ? `${reference} — ${title}${dateStr ? ` (${dateStr})` : ''}`
              : dateStr
                ? `${reference} (${dateStr})`
                : reference;
          return { value: reference, label, reference, date: doc.date };
        })
        .sort((a, b) => {
          const ta = a.date ? new Date(a.date).getTime() : 0;
          const tb = b.date ? new Date(b.date).getTime() : 0;
          return tb - ta;
        }),
    [i18n.language],
  );

  const quoteDocumentsForDelivery = useMemo(() => {
    const filtered = documents.filter((doc) => {
      const t = String(doc?.type || doc?.documentType || '')
        .trim()
        .replace(/-/g, '_')
        .toLowerCase();
      return t === 'quote';
    }).filter(matchesSelectedSalesClient);
    return mapDocumentsToReferenceOptions(filtered);
  }, [documents, matchesSelectedSalesClient, mapDocumentsToReferenceOptions]);

  const invoiceDocumentsForDelivery = useMemo(() => {
    const filtered = documents.filter((doc) => {
      const t = String(doc?.type || doc?.documentType || '')
        .trim()
        .replace(/-/g, '_')
        .toLowerCase();
      return t === 'invoice';
    }).filter(matchesSelectedSalesClient);
    return mapDocumentsToReferenceOptions(filtered);
  }, [documents, matchesSelectedSalesClient, mapDocumentsToReferenceOptions]);

  /** BC archives — même liste sur Facture et Bon de livraison (filtré client si possible). */
  const purchaseOrdersForSalesClient = useMemo(() => {
    const allPo = documents.filter((doc) => {
      const t = String(doc?.type || doc?.documentType || '')
        .trim()
        .replace(/-/g, '_')
        .toLowerCase();
      return t === 'purchase_order';
    });
    const clientPo = allPo.filter(matchesSelectedSalesClient);
    const source = clientPo.length > 0 ? clientPo : allPo;
    return mapDocumentsToReferenceOptions(source);
  }, [documents, matchesSelectedSalesClient, mapDocumentsToReferenceOptions]);

  /** BL archives — bordereau retour client, facture, etc. (filtré client si possible). */
  const deliveryDocumentsForSalesClient = useMemo(() => {
    const allBl = documents.filter((doc) => {
      const t = String(doc?.type || doc?.documentType || '')
        .trim()
        .replace(/-/g, '_')
        .toLowerCase();
      return t === 'delivery' || t === 'delivery_note' || t === 'material_exit';
    });
    const clientBl = allBl.filter(matchesSelectedSalesClient);
    const source = clientBl.length > 0 ? clientBl : allBl;
    return mapDocumentsToReferenceOptions(source);
  }, [documents, matchesSelectedSalesClient, mapDocumentsToReferenceOptions]);

  /** Factures client — bordereau retour client (filtré client si possible). */
  const invoiceDocumentsForSalesClient = useMemo(() => {
    const allInv = documents.filter((doc) => {
      const t = String(doc?.type || doc?.documentType || '')
        .trim()
        .replace(/-/g, '_')
        .toLowerCase();
      return t === 'invoice';
    });
    const clientInv = allInv.filter(matchesSelectedSalesClient);
    const source = clientInv.length > 0 ? clientInv : allInv;
    return mapDocumentsToReferenceOptions(source);
  }, [documents, matchesSelectedSalesClient, mapDocumentsToReferenceOptions]);

  /** BS archives — bordereau d'expédition (filtré client si possible). */
  const exitDocumentsForSalesClient = useMemo(() => {
    const allExit = documents.filter((doc) => {
      const t = String(doc?.type || doc?.documentType || '')
        .trim()
        .replace(/-/g, '_')
        .toLowerCase();
      return t === 'exit_note' || t === 'exit_slip';
    });
    const clientExit = allExit.filter(matchesSelectedSalesClient);
    const source = clientExit.length > 0 ? clientExit : allExit;
    return mapDocumentsToReferenceOptions(source);
  }, [documents, matchesSelectedSalesClient, mapDocumentsToReferenceOptions]);

  // Générer le numéro automatiquement quand le type est sélectionné (mais pas pendant la conversion)
  useEffect(() => {
    if (selectedType && !formData.number && !isFromConversion) {
      const documentNumber = generateDocumentNumber(selectedType.id);
      setFormData(prev => ({
        ...prev,
        number: documentNumber
      }));
    }
  }, [selectedType, formData.number, isFromConversion]);

  // ?? Reset auto-generation flag when document type changes
  useEffect(() => {
    if (selectedType && !isFromConversion && !isEditMode) {
      setIsAutoGenerated(true);
    }
  }, [selectedType, isFromConversion, isEditMode]);

  // Live facturation config → open document form (VAT mode, payment terms, tax rate)
  useEffect(() => {
    const liveTerms = resolveFacturationDefaultPaymentTerms(facturationConfig);
    const liveVatMode = getFacturationVatMode(settings);
    const liveTvaRate = resolveLiveVatRate(settings, facturationConfig);
    setFormData((prev) => {
      if (!prev || Object.keys(prev).length === 0) return prev;
      const next = { ...prev, vatMode: liveVatMode };
      if (liveTvaRate != null && prev.tvaRate == null) next.tvaRate = liveTvaRate;
      if (!prev.paymentTerms) next.paymentTerms = liveTerms;
      if (!prev.orderPaymentTerms) next.orderPaymentTerms = resolveFacturationDefaultPaymentTerms(facturationConfig, { format: 'extended' });
      return next;
    });
  }, [
    facturationConfig.defaultPaymentTermsDays,
    facturationConfig.defaultTaxRate,
    facturationConfig.vatMode,
    settings?.facturation,
    settings?.invoicing?.vatMode,
    settings?.taxes?.tva,
  ]);

  // ?? AUTO-GENERATE smart template content based on active sections
  useEffect(() => {
    if (!selectedType || isFromConversion || !isAutoGenerated) return;

    const docType = normalizeSupplementaryDocType(selectedType.id);
    const fieldName = getSupplementaryFormFieldName(selectedType.id);
    const generatedContent = buildAutoSupplementaryContent(
      docType,
      formData,
      formData.installments || [],
      sectionOrder,
    );

    if (isAutoGenerated) {
      setFormData(prev => ({
        ...prev,
        [fieldName]: generatedContent
      }));
    }
  }, [selectedType, isFromConversion, isAutoGenerated]);

  // Utiliser les clients et fournisseurs actifs du contexte partagé
  const clients = getActiveClients();
  const suppliers = getActiveSuppliers();
  // Utiliser les employés actifs du contexte partagé
  const activeEmployees = getActiveEmployees();
  // Utiliser les produits actifs du contexte partagé
  const activeProducts = getActiveProducts();

  // Load available orders for import functionality
  useEffect(() => {
    const loadOrders = () => {
      try {
        const orders = getOrders ? getOrders() : [];
        const completedOrders = orders.filter(order => 
          order.status === 'completed' && 
          !order.convertedToSaleId // Don't show already converted orders
        );
        setAvailableOrders(completedOrders);
      } catch (error) {
        console.error('Error loading orders:', error);
        setAvailableOrders([]);
      }
    };
    
    if (showImportFromOrder) {
      loadOrders();
    }
  }, [showImportFromOrder, getOrders]);

  // Load available purchases for invoice import functionality
  useEffect(() => {
    const loadPurchases = () => {
      try {
        const purchases = getPurchases ? getPurchases() : [];
        const completedPurchases = purchases.filter(purchase => 
          purchase.status === 'completed' && 
          !purchase.invoiceGenerated // Don't show already invoiced purchases
        );
        setAvailablePurchases(completedPurchases);
      } catch (error) {
        console.error('Error loading purchases:', error);
        setAvailablePurchases([]);
      }
    };
    
    if (showImportFromPurchase) {
      loadPurchases();
    }
  }, [showImportFromPurchase, getPurchases]);

  // ?? Load clients for searchable dropdown
  const loadClientsForDropdown = async () => {
    try {
      setLoadingClients(true);
      const response = await localApiAdapter.get('/facturation/clients');
      const clientsData = response?.data || response || [];
      
      console.log('?? Raw clients data:', clientsData);
      
      // Transform to proper dropdown format with company_name from database
      const clientsOptions = clientsData
        .filter(isActiveRecord) // Only active clients (Actif / active)
        .map(client => ({
          id: client.id,
          name: client.company_name || client.name || '',
          code: client.code || `CLI-${String(client.id).padStart(2, '0')}`,
          email: client.email || '',
          phone: client.phone1 || client.phone || '',
          contact: client.contact_person || '',
          contact_fonction: client.contact_fonction || '',
          destinataire_name: client.destinataire_name || '',
          destinataire_fonction: client.destinataire_fonction || '',
          type: client.client_type || '',
          value: client.company_name || client.name || '',
          label: `${client.company_name || client.name || formLabel('no_name')}${client.code ? ` - ${client.code}` : ''}`
        }));
      
      setAvailableClientsForDropdown(clientsOptions);
      console.log('? Loaded clients for dropdown:', clientsOptions.length, clientsOptions);
    } catch (error) {
      console.error('? Error loading clients:', error);
      setAvailableClientsForDropdown([]);
    } finally {
      setLoadingClients(false);
    }
  };

  // ?? Load suppliers for searchable dropdown
  const loadSuppliersForDropdown = async () => {
    try {
      setLoadingSuppliers(true);
      const response = await localApiAdapter.get('/facturation/fournisseurs');
      const suppliersData = response?.data || response || [];
      
      console.log('?? Raw suppliers data:', suppliersData);
      
      // Transform to proper dropdown format with company_name from database
      const suppliersOptions = suppliersData
        .filter(isActiveRecord) // Only active suppliers (Actif / active)
        .map(supplier => ({
          id: supplier.id,
          name: supplier.company_name || supplier.name || '',
          code: supplier.code || `FRN-${String(supplier.id).padStart(2, '0')}`,
          email: supplier.email || '',
          phone: supplier.phone1 || supplier.phone || '',
          contact: supplier.contact_person || '',
          contact_fonction: supplier.contact_fonction || '',
          destinataire_name: supplier.destinataire_name || '',
          destinataire_fonction: supplier.destinataire_fonction || '',
          type: supplier.supplier_type || '',
          value: supplier.company_name || supplier.name || '',
          label: `${supplier.company_name || supplier.name || formLabel('no_name')}${supplier.code ? ` - ${supplier.code}` : ''}`
        }));
      
      setAvailableSuppliersForDropdown(suppliersOptions);
      console.log('? Loaded suppliers for dropdown:', suppliersOptions.length, suppliersOptions);
    } catch (error) {
      console.error('? Error loading suppliers:', error);
      setAvailableSuppliersForDropdown([]);
    } finally {
      setLoadingSuppliers(false);
    }
  };

  // ?? Load employees for searchable dropdown
  const loadEmployeesForDropdown = async () => {
    try {
      setLoadingEmployees(true);
      // Must match RH module + EmployeesPage: GET /api/hr/employees (not /ressources-humaines/...)
      const response = await localApiAdapter.get('/hr/employees');
      const raw = Array.isArray(response) ? response : response?.data;
      const employeesData = Array.isArray(raw) ? raw : [];

      const isRhActive = (emp) => {
        if (!emp) return false;
        const s = String(emp.status ?? '').toLowerCase();
        return (
          emp.active === true ||
          emp.active === 1 ||
          s === 'active' ||
          s === 'actif'
        );
      };

      const employeesOptions = employeesData
        .filter(isRhActive)
        .filter((emp) => emp.canApprove === true || emp.canApprove === 1 || emp.can_approve === true || emp.can_approve === 1) // ?? ONLY employees who can approve
        .map((emp) => {
          const prenom = emp.prenom || emp.firstName || emp.first_name || '';
          const nom = emp.nom || emp.lastName || emp.last_name || '';
          const full = `${prenom} ${nom}`.trim() || emp.email || '';
          const fonction = emp.fonction || emp.position || emp.posteOccupe || '';
          return {
            id: emp.id,
            firstName: prenom,
            lastName: nom,
            position: fonction,
            value: full,
            label: full // Show ONLY the full name (no fonction, no matricule)
          };
        })
        .filter((o) => Boolean(o.value));

      setAvailableEmployeesForDropdown(employeesOptions);
      console.log('? Loaded APPROVER employees for dropdown (RH /hr/employees):', employeesOptions.length, 'approvers');
    } catch (error) {
      console.error('? Error loading employees:', error);
      setAvailableEmployeesForDropdown([]);
    } finally {
      setLoadingEmployees(false);
    }
  };

  // ?? Load facturiers for "Établi par" dropdown
  const loadFacturiersForDropdown = async () => {
    try {
      const response = await localApiAdapter.get('/facturation/facturiers');
      const facturiersData = response?.data || response || [];
      // Transform to dropdown format with first_name and last_name
      const facturiersOptions = facturiersData
        .filter(isActiveRecord) // Only active facturiers
        .map(f => ({
          value: `${f.first_name} ${f.last_name}`,
          label: `${f.first_name} ${f.last_name}`, // Show ONLY the full name
          position: f.position || f.role || '' // Store position for auto-fill
        }));
      setAvailableFacturiersForDropdown(facturiersOptions);
      setFacturiers(facturiersData);
    } catch (error) {
      console.error('Error loading facturiers:', error);
      setAvailableFacturiersForDropdown([]);
      setFacturiers([]);
    }
  };

  // ?? Load warehouses for "Entrepôt" / "Lieu de livraison" dropdowns
  const loadWarehousesForDropdown = async () => {
    try {
      const response = await localApiAdapter.get('/inventory/warehouses');
      const warehousesData = response?.data || response || [];
      
      console.log('?? Raw warehouses data:', warehousesData);
      
      // Transform to dropdown format with name and reference
      const warehousesOptions = warehousesData
        .filter(isActiveRecord) // Only active warehouses
        .map(warehouse => ({
          id: warehouse.id,
          name: warehouse.name || '',
          reference: warehouse.code || `ENT-${String(warehouse.id).padStart(3, '0')}`,
          city: warehouse.city || '',
          address: warehouse.address || warehouse.location || warehouse.city || '',
          manager: warehouse.contact_person || warehouse.contact || warehouse.manager || warehouse.manager_name || '',
          value: warehouse.name || '',
          label: `${warehouse.name || formLabel('no_name')}${warehouse.code ? ` - ${warehouse.code}` : ''}`
        }));
      
      setAvailableWarehousesForDropdown(warehousesOptions);
      console.log('? Loaded warehouses for dropdown:', warehousesOptions.length, warehousesOptions);
    } catch (error) {
      console.error('? Error loading warehouses:', error);
      setAvailableWarehousesForDropdown([]);
    }
  };

  // ?? Load products and services for item selection dropdowns
  const loadProductsAndServicesForDropdown = () => {
    try {
      // Get all active products from context (includes both products and services)
      const allActiveProducts = activeProducts || [];
      
      console.log('?? All active products/services:', allActiveProducts);
      
      // Separate products and services
      const products = allActiveProducts
        .filter(item => item.type !== 'service')
        .map(product => ({
          id: product.id,
          name: product.name || '',
          category: product.category || '',
          price: product.price || product.sellingPrice || 0,
          type: product.type || 'product',
          value: product.name || '',
          label: `${product.name || formLabel('no_name')}${product.category ? ` - ${product.category}` : ''}`
        }));
      
      const services = allActiveProducts
        .filter(item => item.type === 'service')
        .map(service => ({
          id: service.id,
          name: service.name || '',
          category: service.category || '',
          price: service.price || service.sellingPrice || 0,
          type: 'service',
          value: service.name || '',
          label: `${service.name || formLabel('no_name')}${service.category ? ` - ${service.category}` : ''}`
        }));
      
      setAvailableProductsForDropdown(products);
      setAvailableServicesForDropdown(services);
      console.log('? Loaded products:', products.length, 'services:', services.length);
    } catch (error) {
      console.error('? Error loading products/services:', error);
      setAvailableProductsForDropdown([]);
      setAvailableServicesForDropdown([]);
    }
  };

  // ?? Load data when creating a new document
  useEffect(() => {
    if (currentStep === 'create' && selectedType) {
      loadClientsForDropdown();
      loadSuppliersForDropdown();
      loadEmployeesForDropdown();
      loadFacturiersForDropdown(); // ?? Load facturiers for "Établi par" field
      loadWarehousesForDropdown(); // ?? Load warehouses for "Entrepôt" fields
      loadProductsAndServicesForDropdown(); // ?? Load products and services for item dropdowns
    }
  }, [currentStep, selectedType]); // Removed activeProducts - it was causing infinite loop by creating new ref on every render

  // Import from Order functionality
  const handleImportFromOrder = async (order) => {
    if (!order) return;

    try {
      // Auto-fill form data from order
      const importedFormData = {
        client: order.customerId,
        supplier: order.supplierId,
        date: new Date().toISOString().split('T')[0],
        dueDate: order.dueDate || '',
        reference: t('document_creation.form.order_import_reference', { orderNumber: order.orderNumber || order.id }),
        notes: `${t('document_creation.form.order_import_notes', { orderNumber: order.orderNumber || order.id, notes: order.notes || '' })}`,
        items: order.items || [],
        discount: order.discount || 0,
        tax: order.tax || 19
      };

      setFormData(importedFormData);
      setSelectedOrder(order);
      setShowImportFromOrder(false);
      setCurrentStep('create');

      // Auto-select appropriate document type based on order type
      if (order.customerId) {
        setSelectedType({ id: 'invoice', title: formLabel('doc_types.invoice'), icon: Receipt, color: '#10b981' });
      } else if (order.supplierId) {
        setSelectedType({ id: 'reception-note', title: formLabel('doc_types.reception_note'), icon: Package, color: '#f97316' });
      }

      console.log('? Successfully imported order:', order.id);
    } catch (error) {
      console.error('? Error importing order:', error);
      alert(t('document_creation.alerts.import_order_error'));
    }
  };

  // Import from Purchase functionality
  const handleImportFromPurchase = async (purchase) => {
    if (!purchase) return;

    try {
      // Generate invoice data from purchase
      const result = await createInvoiceFromPurchase(purchase);
      
      if (result.success) {
        const invoiceData = result.invoiceData;
        
        // Auto-fill form data from invoice data
        const importedFormData = {
          supplier: invoiceData.supplierId,
          date: invoiceData.date,
          dueDate: invoiceData.dueDate || '',
          reference: invoiceData.notes,
          notes: invoiceData.notes,
          items: invoiceData.items || [],
          discount: 0,
          tax: invoiceData.taxRate || 19,
          showTva: true,
          tvaRate: invoiceData.taxRate || getBrandVatRate()
        };

        setFormData(importedFormData);
        setSelectedPurchase(purchase);
        setShowImportFromPurchase(false);
        setCurrentStep('create');

        // Auto-select invoice document type
        setSelectedType({ id: 'invoice', title: formLabel('doc_types.invoice'), icon: Receipt, color: '#10b981' });

        console.log('? Successfully imported purchase for invoice:', purchase.id);
      } else {
        alert(t(result.messageKey) || result.message || t('common.error_generic'));
      }
    } catch (error) {
      console.error('? Error importing purchase:', error);
      alert(t('document_creation.alerts.import_purchase_order_error'));
    }
  };

  // Document types definition for editing
  // Function to get available paper formats for each document type
  const getAvailableFormats = (documentId) => {
    // All documents support A4 and A5
    return ['A4', 'A5'];
  };

  const documentTypes = [
    { id: 'quote', title: 'عرض سعر', icon: DollarSign, color: '#10b981' },
    { id: 'proforma', title: 'فاتورة أولية', icon: FileText, color: '#10b981' },
    { id: 'invoice', title: 'فاتورة', icon: Receipt, color: '#10b981' },
    { id: 'simplified-invoice', title: 'فاتورة ضريبية مبسطة', icon: FileCheck, color: '#10b981' },
    { id: 'simplified_invoice', title: 'فاتورة ضريبية مبسطة', icon: FileCheck, color: '#10b981' },
    { id: 'delivery-note', title: 'بون تسليم', icon: Truck, color: '#10b981' },
    { id: 'delivery_note', title: 'بون تسليم', icon: Truck, color: '#10b981' },
    { id: 'delivery', title: 'بون تسليم', icon: Truck, color: '#10b981' },
    { id: 'purchase-order', title: 'أمر', icon: ShoppingCart, color: '#60a5fa' },
    { id: 'purchase_order', title: 'أمر', icon: ShoppingCart, color: '#60a5fa' },
    { id: 'credit-note', title: 'إشعار دائن', icon: FileX, color: '#10b981' },
    { id: 'credit_note', title: 'إشعار دائن', icon: FileX, color: '#10b981' },
    { id: 'debit-note', title: 'إشعار مدين', icon: TrendingUp, color: '#10b981' },
    { id: 'debit_note', title: 'إشعار مدين', icon: TrendingUp, color: '#10b981' },
    { id: 'quote-request', title: 'طلب عرض أسعار', icon: ClipboardList, color: '#60a5fa' },
    { id: 'quote_request', title: 'طلب عرض أسعار', icon: ClipboardList, color: '#60a5fa' },
    { id: 'reception-note', title: 'إشعار استلام', icon: PackageCheck, color: '#60a5fa' },
    { id: 'reception_note', title: 'إشعار استلام', icon: PackageCheck, color: '#60a5fa' },
    { id: 'receipt_note', title: 'إشعار استلام', icon: PackageCheck, color: '#60a5fa' },
    { id: 'payment-voucher', title: 'سند صرف', icon: Banknote, color: '#60a5fa' },
    { id: 'payment_voucher', title: 'سند صرف', icon: Banknote, color: '#60a5fa' },
    { id: 'payment_slip', title: 'سند صرف', icon: Banknote, color: '#60a5fa' },
    // INVENTAIRE (stock module — deep links only)
    { id: 'stock-entry', title: formLabel('doc_types.stock_entry'), icon: PackagePlus, color: '#f97316' },
    { id: 'exit-note', title: formLabel('doc_types.exit_note'), icon: FileOutput, color: '#f97316' },
    { id: 'exit_slip', title: formLabel('doc_types.exit_note'), icon: FileOutput, color: '#f97316' },
    { id: 'transfer-note', title: formLabel('doc_types.transfer_note'), icon: ArrowRightLeft, color: '#f97316' },
    { id: 'transfer_slip', title: formLabel('doc_types.transfer_note'), icon: ArrowRightLeft, color: '#f97316' },
    { id: 'shipping-slip', title: formLabel('doc_types.shipping_slip'), icon: Send, color: '#f97316' },
    { id: 'shipping', title: formLabel('doc_types.shipping_slip'), icon: Send, color: '#f97316' },
  ];

  // Reset paper format when document type changes
  useEffect(() => {
    if (selectedType) {
      const availableFormats = getAvailableFormats(selectedType.id);
      // If current format is not available for this document, reset to A4
      if (!availableFormats.includes(selectedPaperFormat)) {
        setSelectedPaperFormat('A4');
      }
    }
  }, [selectedType]);

  // Handle edit mode - load document for editing
  useEffect(() => {
    if (!editId) return;

    let cancelled = false;

    (async () => {
      let documentToEdit = documents.find((doc) => doc.id === editId);

      try {
        const fresh = await localApiAdapter.documents.getDocument(editId);
        if (fresh && !cancelled) documentToEdit = fresh;
      } catch (err) {
        console.warn('Edit load: fresh archive fetch failed, using cache', err);
      }

      if (cancelled) return;

      if (documentToEdit) {
        console.log('?? Loading document for editing:', documentToEdit);
        
        setIsEditMode(true);
        setEditingDocument(documentToEdit);
        
        // Find the document type configuration
        const resolvedPaymentDisplay = resolvePaymentDocumentDisplayType(documentToEdit);
        const creationTypeId = resolveDocumentCreationTypeConfigId(
          documentToEdit.type,
          editType,
        );
        const typeConfig = documentTypes.find(type => 
          type.id === creationTypeId ||
          type.id === documentToEdit.type || 
          type.id === editType ||
          type.id === resolvedPaymentDisplay ||
          type.id === documentToEdit.type.replace('_', '-') ||
          type.id === documentToEdit.type.replace('-', '_') ||
          type.id === resolvedPaymentDisplay.replace(/_/g, '-') ||
          type.id === resolvedPaymentDisplay.replace(/-/g, '_')
        );
        
        if (typeConfig) {
          setSelectedType(typeConfig);
          setCurrentStep('create');
          
          // Load document data into form
          const loadedFormData = {
            number: documentToEdit.number,
            title: documentToEdit.title,
            date: documentToEdit.date,
            dueDate: documentToEdit.dueDate || documentToEdit.due_date || '',
            client:
              documentToEdit.clientId?.toString() ||
              documentToEdit.client_id?.toString() ||
              '',
            supplier:
              documentToEdit.supplierId?.toString() ||
              documentToEdit.supplier_id?.toString() ||
              (documentToEdit.isSupplier || documentToEdit.is_supplier
                ? documentToEdit.clientId != null
                  ? String(documentToEdit.clientId)
                  : documentToEdit.client_id != null
                    ? String(documentToEdit.client_id)
                    : ''
                : ''),
            items: Array.isArray(documentToEdit.items) ? documentToEdit.items : [],
            total: documentToEdit.amount || 0,
            // Load additional fields based on document type
            paymentTerms:
              documentToEdit.paymentTerms ||
              documentToEdit.payment_terms ||
              documentToEdit.desiredPaymentTerms ||
              documentToEdit.desired_payment_terms ||
              '',
            deliveryDate:
              documentToEdit.deliveryDate ||
              documentToEdit.delivery_date ||
              new Date().toISOString().split('T')[0],
            deliveryLocation:
              documentToEdit.deliveryLocation ||
              documentToEdit.delivery_location ||
              documentToEdit.deliveryPlace ||
              documentToEdit.delivery_place ||
              documentToEdit.deliveryAddress ||
              documentToEdit.delivery_address ||
              '',
            deliveryPlace:
              documentToEdit.deliveryPlace ||
              documentToEdit.delivery_place ||
              documentToEdit.deliveryLocation ||
              documentToEdit.delivery_location ||
              documentToEdit.deliveryAddress ||
              documentToEdit.delivery_address ||
              '',
            validUntil: documentToEdit.validUntil || documentToEdit.valid_until || '',
            conditions: documentToEdit.conditions || '',
            notes: documentToEdit.notes || '',
            requirements: documentToEdit.requirements || '',
            observations: documentToEdit.observations || '',
            instructions: documentToEdit.instructions || '',
            deliveryNotes: documentToEdit.deliveryNotes || documentToEdit.delivery_notes || '',
            deliveryConditions: documentToEdit.deliveryConditions || documentToEdit.delivery_conditions || '',
            paymentConditions: resolveInvoicePaymentConditionsFromArchive(documentToEdit),
            status: documentToEdit.status || 'pending',
            responseDate:
              documentToEdit.responseDate ||
              documentToEdit.response_date ||
              documentToEdit.responseDeadline ||
              documentToEdit.response_deadline ||
              '',
            preferredPaymentTerms:
              documentToEdit.desiredPaymentTerms ||
              documentToEdit.desired_payment_terms ||
              '',
            estimatedDeliveryTime:
              documentToEdit.estimatedDeliveryTime ||
              documentToEdit.estimated_delivery_time ||
              '',
            requiredDocuments:
              documentToEdit.requiredDocuments || documentToEdit.required_documents || '',
            desiredDeliveryTime:
              documentToEdit.desiredDeliveryTime || documentToEdit.desired_delivery_time || '',
            projectType: documentToEdit.projectType || documentToEdit.project_type || '',
            projectReference:
              documentToEdit.projectReference || documentToEdit.project_reference || '',
            commercial: documentToEdit.commercial || documentToEdit.salesPerson || '',
            salesPerson: documentToEdit.salesPerson || documentToEdit.commercial || '',
            paymentMethod:
              documentToEdit.paymentMethod || documentToEdit.payment_method || '',
            deliveryTime:
              documentToEdit.deliveryTime ||
              documentToEdit.estimatedDeliveryTime ||
              documentToEdit.estimated_delivery_time ||
              documentToEdit.estimatedDelivery ||
              documentToEdit.estimated_delivery ||
              '',
            estimatedDelivery:
              documentToEdit.estimatedDelivery ||
              documentToEdit.estimated_delivery ||
              documentToEdit.deliveryTime ||
              documentToEdit.estimatedDeliveryTime ||
              documentToEdit.estimated_delivery_time ||
              '',
            deliveryDelay:
              documentToEdit.deliveryDelay ||
              documentToEdit.desiredDeliveryTime ||
              documentToEdit.desired_delivery_time ||
              '',
            priority: documentToEdit.priority ?? '',
            issuedBy: documentToEdit.issuedBy || documentToEdit.issued_by || '',
            approximateBudget:
              documentToEdit.approximateBudget ?? documentToEdit.approximate_budget,
            showBonPour: resolveShowBonPourFromArchive(documentToEdit),
            bonPourName: documentToEdit.bonPourName || documentToEdit.bon_pour_name || '',
            bonPourRole: documentToEdit.bonPourRole || documentToEdit.bon_pour_role || '',
            bonPourDate: documentToEdit.bonPourDate || documentToEdit.bon_pour_date || '',
            bonPourPlace: documentToEdit.bonPourPlace || documentToEdit.bon_pour_place || '',
            quoteRequestRef: '',
            proformaRequestRef: '',
            deliveryTimeframe: '',
            buyerContact: '',
            terms: documentToEdit.terms || '',
            installments: Array.isArray(documentToEdit.installments)
              ? documentToEdit.installments
              : [],
            deliveryAddress:
              documentToEdit.deliveryAddress ||
              documentToEdit.delivery_address ||
              '',
            deliveryMode: documentToEdit.deliveryMode || documentToEdit.delivery_mode || '',
            // Add more fields as needed
          };

          const docTypeUnderscore = String(documentToEdit.type || '').replace(/-/g, '_');
          if (docTypeUnderscore === 'purchase_order') {
            loadedFormData.quoteRequestRef =
              documentToEdit.documentReference || documentToEdit.document_reference || '';
            loadedFormData.proformaRequestRef =
              documentToEdit.originalDocumentReference ||
              documentToEdit.original_document_reference ||
              '';
            loadedFormData.deliveryTimeframe =
              documentToEdit.desiredDeliveryTime ||
              documentToEdit.desired_delivery_time ||
              '';
            loadedFormData.buyerContact =
              documentToEdit.buyerContact || documentToEdit.buyer_contact || '';
            loadedFormData.terms = documentToEdit.terms || loadedFormData.terms || '';
            loadedFormData.installments = Array.isArray(documentToEdit.installments)
              ? documentToEdit.installments
              : [];
            loadedFormData.deliveryAddress =
              documentToEdit.delivery_address ||
              documentToEdit.deliveryAddress ||
              loadedFormData.deliveryAddress ||
              '';
            loadedFormData.deliveryMode =
              documentToEdit.delivery_mode || documentToEdit.deliveryMode || loadedFormData.deliveryMode || '';
            loadedFormData.paymentTerms =
              loadedFormData.paymentTerms ||
              documentToEdit.desiredPaymentTerms ||
              documentToEdit.desired_payment_terms ||
              '';
          } else if (docTypeUnderscore === 'reception_note') {
            loadedFormData.receptionOrderRef =
              documentToEdit.purchaseOrderReference ||
              documentToEdit.purchase_order_reference ||
              documentToEdit.receptionOrder ||
              documentToEdit.reception_order ||
              documentToEdit.orderReference ||
              documentToEdit.order_reference ||
              '';
            loadedFormData.receptionDeliveryNoteRef =
              documentToEdit.deliveryNoteReference ||
              documentToEdit.delivery_note_reference ||
              documentToEdit.receptionDeliveryNote ||
              documentToEdit.reception_delivery_note ||
              '';
            loadedFormData.receiverAddress =
              documentToEdit.receiverAddress ||
              documentToEdit.receiver_address ||
              documentToEdit.receptionAddress ||
              documentToEdit.reception_address ||
              loadedFormData.receiverAddress ||
              '';
            loadedFormData.expectedReceptionDate =
              documentToEdit.expectedReceptionDate ||
              documentToEdit.expected_reception_date ||
              documentToEdit.expectedDeliveryDate ||
              documentToEdit.expected_delivery_date ||
              loadedFormData.expectedReceptionDate ||
              '';
            if (
              !loadedFormData.etablieParName &&
              (loadedFormData.issuedBy || documentToEdit.issuedBy || documentToEdit.issued_by)
            ) {
              loadedFormData.etablieParName =
                loadedFormData.issuedBy ||
                documentToEdit.issuedBy ||
                documentToEdit.issued_by ||
                '';
            }
            const legacyReceivedBy =
              documentToEdit.recueParName ||
              documentToEdit.recue_par_name ||
              documentToEdit.receivedByName ||
              documentToEdit.received_by_name ||
              documentToEdit.receivedBy ||
              documentToEdit.received_by ||
              '';
            if (legacyReceivedBy && !loadedFormData.recueParName) {
              loadedFormData.recueParName = legacyReceivedBy;
              loadedFormData.showRecuePar = true;
            }
            loadedFormData.receptionControlledBy =
              documentToEdit.receptionControlledBy ||
              documentToEdit.controlledByName ||
              documentToEdit.controlled_by_name ||
              '';
            loadedFormData.receptionCarrier =
              documentToEdit.receptionCarrier || documentToEdit.carrier || '';
            loadedFormData.receptionWarehouse =
              documentToEdit.receptionWarehouse ||
              documentToEdit.warehouseName ||
              documentToEdit.warehouse_name ||
              '';
            loadedFormData.receptionOverallCondition =
              documentToEdit.receptionOverallCondition ||
              documentToEdit.generalCondition ||
              documentToEdit.general_condition ||
              '';
            loadedFormData.receptionQuantityCompliance =
              documentToEdit.receptionQuantityCompliance ||
              documentToEdit.quantityConformity ||
              documentToEdit.quantity_conformity ||
              '';
            loadedFormData.receptionQualityCompliance =
              documentToEdit.receptionQualityCompliance ||
              documentToEdit.qualityConformity ||
              documentToEdit.quality_conformity ||
              '';
            loadedFormData.receptionStockDestination =
              documentToEdit.receptionStockDestination ||
              documentToEdit.stockDestination ||
              documentToEdit.stock_destination ||
              '';
            loadedFormData.receptionStatus =
              documentToEdit.receptionStatus || documentToEdit.reception_status || '';
            loadedFormData.receptionTime =
              documentToEdit.receptionTime || documentToEdit.reception_time || '';
            loadedFormData.receptionDate =
              documentToEdit.receptionDate || documentToEdit.reception_date || loadedFormData.date || '';
          } else if (docTypeUnderscore === 'stock_entry' || documentToEdit.type === 'stock-entry') {
            const entryDt =
              documentToEdit.entryDate ||
              documentToEdit.entry_date ||
              documentToEdit.expectedEntryDate ||
              documentToEdit.expected_entry_date ||
              '';
            loadedFormData.stockEntryEntryDate = entryDt || loadedFormData.date || '';
            loadedFormData.expectedEntryDate =
              documentToEdit.expectedEntryDate ||
              documentToEdit.expected_entry_date ||
              loadedFormData.stockEntryEntryDate ||
              '';
            loadedFormData.stockEntryEntryTime =
              documentToEdit.entryTime || documentToEdit.entry_time || '';
            loadedFormData.stockEntryWarehouse =
              documentToEdit.stockEntryWarehouse ||
              documentToEdit.warehouseName ||
              documentToEdit.warehouse_name ||
              '';
            loadedFormData.stockEntrySource =
              documentToEdit.stockEntrySource ||
              documentToEdit.entrySource ||
              documentToEdit.entry_source ||
              '';
            loadedFormData.stockEntryDocumentReference =
              documentToEdit.stockEntryDocumentReference ||
              documentToEdit.entryDocumentReference ||
              documentToEdit.entry_document_reference ||
              '';
            loadedFormData.stockEntrySupplier =
              documentToEdit.stockEntrySupplier ||
              documentToEdit.supplierName ||
              documentToEdit.supplier_name ||
              documentToEdit.client ||
              '';
            loadedFormData.stockEntryReceivedBy =
              documentToEdit.stockEntryReceivedBy ||
              documentToEdit.receivedByName ||
              documentToEdit.received_by_name ||
              '';
            loadedFormData.stockEntryControlledBy =
              documentToEdit.stockEntryControlledBy ||
              documentToEdit.controlledByName ||
              documentToEdit.controlled_by_name ||
              '';
            loadedFormData.stockEntryCondition =
              documentToEdit.stockEntryCondition ||
              documentToEdit.goodsCondition ||
              documentToEdit.goods_condition ||
              '';
            loadedFormData.stockEntryPurchaseOrderRef =
              documentToEdit.stockEntryPurchaseOrderRef ||
              documentToEdit.purchaseOrderReference ||
              documentToEdit.purchase_order_reference ||
              '';
            loadedFormData.stockEntryReceptionNoteRef =
              documentToEdit.stockEntryReceptionNoteRef ||
              documentToEdit.deliveryNoteReference ||
              documentToEdit.delivery_note_reference ||
              '';
            loadedFormData.stockEntryTransferNoteRef =
              documentToEdit.stockEntryTransferNoteRef ||
              documentToEdit.originalDocumentReference ||
              documentToEdit.original_document_reference ||
              '';
          } else if (
            docTypeUnderscore === 'exit_slip' ||
            docTypeUnderscore === 'exit_note' ||
            documentToEdit.type === 'exit-note' ||
            documentToEdit.type === 'exit-slip'
          ) {
            loadedFormData.exitNoteClientOrSupplier =
              documentToEdit.exitNoteClientOrSupplier || documentToEdit.client || '';
            loadedFormData.exitNoteExpectedDate =
              documentToEdit.exitNoteExpectedDate ||
              documentToEdit.expectedDeliveryDate ||
              documentToEdit.expected_delivery_date ||
              loadedFormData.date ||
              '';
            loadedFormData.exitNoteOriginWarehouse =
              documentToEdit.exitNoteOriginWarehouse ||
              documentToEdit.originWarehouseName ||
              documentToEdit.origin_warehouse_name ||
              '';
            loadedFormData.exitNoteDestinationWarehouse =
              documentToEdit.exitNoteDestinationWarehouse ||
              documentToEdit.destinationWarehouseName ||
              documentToEdit.destination_warehouse_name ||
              '';
            loadedFormData.exitNoteHandler =
              documentToEdit.exitNoteHandler ||
              documentToEdit.transferResponsibleName ||
              documentToEdit.transfer_responsible_name ||
              '';
            loadedFormData.exitNoteAuthorizedBy =
              documentToEdit.exitNoteAuthorizedBy ||
              documentToEdit.approvedByName ||
              documentToEdit.approved_by_name ||
              '';
            loadedFormData.exitNoteStatus =
              documentToEdit.exitNoteStatus || documentToEdit.status || '';
            loadedFormData.exitNoteClientOrderRef =
              documentToEdit.exitNoteClientOrderRef ||
              documentToEdit.purchaseOrderReference ||
              documentToEdit.purchase_order_reference ||
              '';
            loadedFormData.exitNoteInvoiceRef =
              documentToEdit.exitNoteInvoiceRef ||
              documentToEdit.invoiceReference ||
              documentToEdit.invoice_reference ||
              '';
            loadedFormData.exitNoteDeliveryNoteRef =
              documentToEdit.exitNoteDeliveryNoteRef ||
              documentToEdit.deliveryNoteReference ||
              documentToEdit.delivery_note_reference ||
              '';
            loadedFormData.exitNoteTransferNoteRef =
              documentToEdit.exitNoteTransferNoteRef ||
              documentToEdit.originalDocumentReference ||
              documentToEdit.original_document_reference ||
              '';
            loadedFormData.exitNoteDestination =
              documentToEdit.exitNoteDestination || documentToEdit.deliveryAddress || '';
            loadedFormData.exitNoteTransportMode =
              documentToEdit.exitNoteTransportMode ||
              documentToEdit.deliveryMode ||
              documentToEdit.delivery_mode ||
              '';
            loadedFormData.exitNoteExitTime =
              documentToEdit.exitNoteExitTime ||
              documentToEdit.departureTime ||
              documentToEdit.departure_time ||
              '';
            loadedFormData.exitNoteDocumentReference =
              documentToEdit.exitNoteDocumentReference ||
              documentToEdit.entryDocumentReference ||
              documentToEdit.entry_document_reference ||
              '';
            loadedFormData.exitNoteRecipient =
              documentToEdit.exitNoteRecipient ||
              documentToEdit.receivedByName ||
              documentToEdit.received_by_name ||
              '';
          } else if (
            docTypeUnderscore === 'transfer_slip' ||
            docTypeUnderscore === 'transfer_note' ||
            documentToEdit.type === 'transfer-note' ||
            documentToEdit.type === 'transfer-slip'
          ) {
            loadedFormData.transferNoteExpectedDate =
              documentToEdit.transferNoteExpectedDate ||
              documentToEdit.expectedDeliveryDate ||
              documentToEdit.expected_delivery_date ||
              loadedFormData.date ||
              '';
            loadedFormData.transferNoteOriginWarehouse =
              documentToEdit.transferNoteOriginWarehouse ||
              documentToEdit.originWarehouseName ||
              documentToEdit.origin_warehouse_name ||
              '';
            loadedFormData.transferNoteDestinationWarehouse =
              documentToEdit.transferNoteDestinationWarehouse ||
              documentToEdit.destinationWarehouseName ||
              documentToEdit.destination_warehouse_name ||
              '';
            loadedFormData.transferNoteDocumentReference =
              documentToEdit.transferNoteDocumentReference ||
              documentToEdit.entryDocumentReference ||
              documentToEdit.entry_document_reference ||
              '';
            loadedFormData.transferNoteTransferRequestRef =
              documentToEdit.transferNoteTransferRequestRef ||
              documentToEdit.originalDocumentReference ||
              documentToEdit.original_document_reference ||
              '';
            loadedFormData.transferNoteRequestedBy =
              documentToEdit.transferNoteRequestedBy ||
              documentToEdit.transferRequestedByName ||
              documentToEdit.transfer_requested_by_name ||
              '';
            loadedFormData.transferNoteHandler =
              documentToEdit.transferNoteHandler ||
              documentToEdit.transferResponsibleName ||
              documentToEdit.transfer_responsible_name ||
              '';
            loadedFormData.transferNoteApprovedBy =
              documentToEdit.transferNoteApprovedBy ||
              documentToEdit.approvedByName ||
              documentToEdit.approved_by_name ||
              '';
            loadedFormData.transferNoteStatus =
              documentToEdit.transferNoteStatus || documentToEdit.transferStatus || '';
            loadedFormData.transferNoteTransportMode =
              documentToEdit.transferNoteTransportMode ||
              documentToEdit.shipmentMode ||
              documentToEdit.shipment_mode ||
              documentToEdit.deliveryMode ||
              '';
            loadedFormData.transferNoteDepartureTime =
              documentToEdit.transferNoteDepartureTime ||
              documentToEdit.departureTime ||
              documentToEdit.departure_time ||
              '';
            loadedFormData.transferNoteArrivalTime =
              documentToEdit.transferNoteArrivalTime ||
              documentToEdit.arrivalTime ||
              documentToEdit.arrival_time ||
              '';
            loadedFormData.transferNoteTrackingNumber =
              documentToEdit.transferNoteTrackingNumber ||
              documentToEdit.trackingNumber ||
              documentToEdit.tracking_number ||
              '';
          } else if (
            docTypeUnderscore === 'shipping' ||
            docTypeUnderscore === 'shipping_slip' ||
            documentToEdit.type === 'shipping-slip'
          ) {
            loadedFormData.shipmentDate =
              documentToEdit.shipmentDate ||
              documentToEdit.shipment_date ||
              loadedFormData.date ||
              '';
            loadedFormData.shipmentOriginWarehouse =
              documentToEdit.shipmentOriginWarehouse ||
              documentToEdit.originWarehouseName ||
              documentToEdit.origin_warehouse_name ||
              '';
            loadedFormData.deliveryLocation =
              documentToEdit.deliveryLocation ||
              documentToEdit.delivery_location ||
              documentToEdit.deliveryAddress ||
              documentToEdit.delivery_address ||
              documentToEdit.lieu_livraison ||
              '';
            loadedFormData.shipmentMethod =
              documentToEdit.shipmentMethod ||
              documentToEdit.shipmentMode ||
              documentToEdit.shipment_mode ||
              documentToEdit.deliveryMethod ||
              documentToEdit.delivery_method ||
              documentToEdit.deliveryMode ||
              documentToEdit.delivery_mode ||
              '';
            loadedFormData.shipmentCarrier =
              documentToEdit.shipmentCarrier ||
              documentToEdit.carrier ||
              '';
            const shipUiDec = decodeDocumentUiOverrides(
              documentToEdit.validityNotes ?? documentToEdit.validity_notes,
            );
            loadedFormData.shipmentCarrierTrackingNumber =
              documentToEdit.shipmentCarrierTrackingNumber ||
              documentToEdit.carrierTrackingNumber ||
              documentToEdit.carrier_tracking_number ||
              shipUiDec?.shipmentCarrierTrackingNumber ||
              shipUiDec?.carrierTrackingNumber ||
              '';
            loadedFormData.shipmentTrackingNumber =
              documentToEdit.shipmentTrackingNumber ||
              documentToEdit.trackingNumber ||
              documentToEdit.tracking_number ||
              '';
            loadedFormData.shipmentPriority =
              documentToEdit.shipmentPriority ||
              documentToEdit.shipment_priority ||
              documentToEdit.priority ||
              '';
            loadedFormData.shipmentPackageType =
              documentToEdit.shipmentPackageType ||
              documentToEdit.packageType ||
              documentToEdit.package_type ||
              '';
            loadedFormData.shipmentProductType =
              documentToEdit.shipmentProductType ||
              documentToEdit.productType ||
              documentToEdit.product_type ||
              '';
            const pkg =
              documentToEdit.shipmentPackageCount ??
              documentToEdit.packageCount ??
              documentToEdit.package_count;
            if (pkg != null && pkg !== '') loadedFormData.shipmentPackageCount = pkg;
            const wt =
              documentToEdit.shipmentTotalWeight ?? documentToEdit.weight ?? documentToEdit.totalWeight;
            if (wt != null && wt !== '') loadedFormData.shipmentTotalWeight = wt;
            const vol = documentToEdit.shipmentVolume ?? documentToEdit.volume;
            if (vol != null && vol !== '') loadedFormData.shipmentVolume = vol;
            const dv = documentToEdit.shipmentDeclaredValue ?? documentToEdit.declaredValue;
            if (dv != null && dv !== '') loadedFormData.shipmentDeclaredValue = dv;
            loadedFormData.shipmentClientOrderRef =
              documentToEdit.shipmentClientOrderRef ||
              documentToEdit.purchaseOrderReference ||
              documentToEdit.purchase_order_reference ||
              '';
            loadedFormData.shipmentInvoiceRef =
              documentToEdit.shipmentInvoiceRef ||
              documentToEdit.invoiceReference ||
              documentToEdit.invoice_reference ||
              '';
            loadedFormData.shipmentDeliveryNoteRef =
              documentToEdit.shipmentDeliveryNoteRef ||
              documentToEdit.deliveryNoteReference ||
              documentToEdit.delivery_note_reference ||
              '';
            loadedFormData.shipmentExitNoteRef =
              documentToEdit.shipmentExitNoteRef ||
              documentToEdit.originalDocumentReference ||
              documentToEdit.original_document_reference ||
              '';
            loadedFormData.instructions =
              documentToEdit.instructions ||
              documentToEdit.observations ||
              loadedFormData.instructions ||
              loadedFormData.notes ||
              '';
          } else if (
            docTypeUnderscore === 'return_slip' ||
            documentToEdit.type === 'return-slip'
          ) {
            loadedFormData.processingDeadline =
              documentToEdit.processingDeadline ||
              documentToEdit.processing_deadline ||
              '';
            loadedFormData.returnWarehouse =
              documentToEdit.returnWarehouse || documentToEdit.return_warehouse || '';
            loadedFormData.returnCarrier =
              documentToEdit.returnCarrier || documentToEdit.return_carrier || '';
            loadedFormData.returnTrackingNumber =
              documentToEdit.returnTrackingNumber || documentToEdit.return_tracking_number || '';
            loadedFormData.returnOriginalPurchaseOrderRef =
              documentToEdit.returnOriginalPurchaseOrder ||
              documentToEdit.return_original_purchase_order ||
              documentToEdit.purchaseOrderReference ||
              documentToEdit.purchase_order_reference ||
              '';
            loadedFormData.returnOriginalDeliveryRef =
              documentToEdit.returnOriginalDelivery ||
              documentToEdit.return_original_delivery ||
              '';
            loadedFormData.returnOriginalInvoiceRef =
              documentToEdit.returnOriginalInvoice ||
              documentToEdit.return_original_invoice ||
              '';
            loadedFormData.returnOriginalInvoiceDate =
              documentToEdit.returnOriginalInvoiceDate ||
              documentToEdit.return_original_invoice_date ||
              '';
            loadedFormData.returnRMANumber =
              documentToEdit.returnRMANumber ||
              documentToEdit.returnRmaNumber ||
              documentToEdit.return_rma_number ||
              '';
            loadedFormData.returnInspector =
              documentToEdit.returnInspector ||
              documentToEdit.return_inspector ||
              documentToEdit.controlledByName ||
              documentToEdit.controlled_by_name ||
              '';
            loadedFormData.returnCondition = normalizeReturnGoodsConditionValue(
              loadedFormData.returnCondition ||
                documentToEdit.returnCondition ||
                documentToEdit.goodsCondition ||
                documentToEdit.goods_condition ||
                '',
            );
            loadedFormData.returnType =
              documentToEdit.returnType || documentToEdit.return_type || '';
            loadedFormData.returnReason =
              documentToEdit.returnReason || documentToEdit.return_reason || '';
            loadedFormData.returnDate =
              documentToEdit.returnDate ||
              documentToEdit.return_date ||
              loadedFormData.date ||
              '';
            loadedFormData.issuedBy =
              loadedFormData.issuedBy ||
              documentToEdit.issuedBy ||
              documentToEdit.issued_by ||
              '';
            loadedFormData.notes =
              loadedFormData.notes || documentToEdit.notes || '';
          } else if (
            docTypeUnderscore === 'client_return_slip' ||
            documentToEdit.type === 'client-return-slip'
          ) {
            loadedFormData.clientReturnOriginalPurchaseOrderRef =
              documentToEdit.clientReturnOriginalPurchaseOrderRef ||
              documentToEdit.returnOriginalPurchaseOrder ||
              documentToEdit.return_original_purchase_order ||
              '';
            loadedFormData.clientReturnOriginalDeliveryRef =
              documentToEdit.clientReturnOriginalDeliveryRef ||
              documentToEdit.returnOriginalDelivery ||
              documentToEdit.return_original_delivery ||
              '';
            loadedFormData.clientReturnOriginalInvoiceRef =
              documentToEdit.clientReturnOriginalInvoiceRef ||
              documentToEdit.returnOriginalInvoice ||
              documentToEdit.return_original_invoice ||
              '';
            loadedFormData.clientReturnWarehouse =
              documentToEdit.clientReturnWarehouse ||
              documentToEdit.returnWarehouse ||
              documentToEdit.return_warehouse ||
              '';
            loadedFormData.clientReturnCarrier =
              documentToEdit.clientReturnCarrier ||
              documentToEdit.returnCarrier ||
              documentToEdit.return_carrier ||
              '';
            loadedFormData.clientReturnTrackingNumber =
              documentToEdit.clientReturnTrackingNumber ||
              documentToEdit.returnTrackingNumber ||
              documentToEdit.return_tracking_number ||
              documentToEdit.trackingNumber ||
              documentToEdit.tracking_number ||
              '';
            loadedFormData.clientReturnCondition = normalizeReturnGoodsConditionValue(
              loadedFormData.clientReturnCondition ||
                documentToEdit.returnCondition ||
                documentToEdit.goodsCondition ||
                documentToEdit.goods_condition ||
                '',
            );
            loadedFormData.clientReturnType =
              documentToEdit.returnType || documentToEdit.return_type || '';
            const retReason =
              documentToEdit.returnReason || documentToEdit.return_reason || '';
            loadedFormData.clientReturnMotif =
              documentToEdit.clientReturnMotif || documentToEdit.motif || retReason || '';
            loadedFormData.clientReturnReason = loadedFormData.clientReturnMotif || retReason || '';
            loadedFormData.clientReturnControlledBy =
              documentToEdit.controlledByName ||
              documentToEdit.controlled_by_name ||
              documentToEdit.returnInspector ||
              documentToEdit.return_inspector ||
              '';
            loadedFormData.clientReturnReturnDate =
              documentToEdit.returnDate || documentToEdit.return_date || loadedFormData.date || '';
            loadedFormData.notes =
              loadedFormData.notes || documentToEdit.notes || '';
          }
          /* Archives persiste parfois le texte « complémentaire » uniquement dans `notes` selon le type. */
          if (docTypeUnderscore === 'quote_request' || docTypeUnderscore === 'proforma_request') {
            loadedFormData.responseDate =
              loadedFormData.responseDate ||
              documentToEdit.response_date ||
              documentToEdit.responseDeadline ||
              documentToEdit.response_deadline ||
              '';
            const storedBudget = Number(
              documentToEdit.maxBudget ??
                documentToEdit.max_budget ??
                documentToEdit.declaredValue ??
                documentToEdit.declared_value,
            );
            const resolvedBudget =
              Number.isFinite(storedBudget) && storedBudget > 0 ? storedBudget : '';
            loadedFormData.approximateBudget = resolvedBudget;
            loadedFormData.maxBudget = resolvedBudget;

            loadedFormData.requirements =
              loadedFormData.requirements ||
              loadedFormData.notes ||
              documentToEdit.requirements ||
              documentToEdit.notes ||
              '';
            const specs = Array.isArray(documentToEdit.specifications)
              ? documentToEdit.specifications
              : [];
            const rawItems = Array.isArray(documentToEdit.items) ? documentToEdit.items : [];
            const looksLikeSpecifications = (rows) =>
              Boolean(
                rows.length &&
                  rows[0] &&
                  typeof rows[0] === 'object' &&
                  ('requirements' in rows[0] || 'budget' in rows[0]),
              );
            loadedFormData.specifications = specs.length
              ? specs
              : looksLikeSpecifications(rawItems)
                ? rawItems
                : [];
            if (
              specs.length === 0 &&
              looksLikeSpecifications(rawItems)
            ) {
              loadedFormData.items = [];
            }
            if (!loadedFormData.specifications?.length) {
              loadedFormData.specifications = [
                { description: '', quantity: 1, requirements: '', budget: 0 },
              ];
            }
          } else if (docTypeUnderscore === 'delivery' || docTypeUnderscore === 'material_exit') {
            loadedFormData.deliveryNotes =
              loadedFormData.deliveryNotes ||
              loadedFormData.notes ||
              documentToEdit.delivery_notes ||
              documentToEdit.notes ||
              '';
            loadedFormData.deliveryConditions =
              loadedFormData.deliveryConditions ||
              loadedFormData.delivery_conditions ||
              '';
            /* Bon de livraison (`delivery` / legacy `material_exit`) : refs, lieu, transport. */
            if (docTypeUnderscore === 'delivery' || docTypeUnderscore === 'material_exit') {
              loadedFormData.customerOrderReference =
                documentToEdit.customerOrderReference ||
                documentToEdit.customer_order_reference ||
                documentToEdit.documentReference ||
                documentToEdit.document_reference ||
                '';
              loadedFormData.invoiceReference =
                documentToEdit.invoiceReference ||
                documentToEdit.invoice_reference ||
                '';
              if (!loadedFormData.customerOrderReference) {
                const legacyOrig =
                  documentToEdit.originalDocumentReference ||
                  documentToEdit.original_document_reference ||
                  '';
                if (
                  legacyOrig &&
                  loadedFormData.invoiceReference &&
                  !documentToEdit.quoteReference &&
                  !documentToEdit.quote_reference
                ) {
                  loadedFormData.customerOrderReference = legacyOrig;
                }
              }
              loadedFormData.quoteReference =
                documentToEdit.quoteReference ||
                documentToEdit.quote_reference ||
                '';
              if (!loadedFormData.quoteReference) {
                const orig =
                  documentToEdit.originalDocumentReference ||
                  documentToEdit.original_document_reference ||
                  '';
                const cor = loadedFormData.customerOrderReference || '';
                if (orig && String(orig).trim() !== String(cor).trim()) {
                  loadedFormData.quoteReference = orig;
                }
              }
              const bc =
                documentToEdit.orderReference ||
                documentToEdit.order_reference ||
                documentToEdit.purchaseOrderReference ||
                documentToEdit.purchase_order_reference ||
                '';
              loadedFormData.orderReference = bc;
              loadedFormData.deliveryOrderReference =
                documentToEdit.deliveryOrderReference ||
                documentToEdit.delivery_order_reference ||
                bc;

              loadedFormData.departureWarehouse =
                documentToEdit.departureWarehouse ||
                documentToEdit.departure_warehouse ||
                documentToEdit.originWarehouseName ||
                documentToEdit.origin_warehouse_name ||
                '';

              loadedFormData.deliveryAddress =
                documentToEdit.deliveryAddress ||
                documentToEdit.delivery_address ||
                loadedFormData.deliveryAddress ||
                '';

              loadedFormData.deliveryMethod =
                documentToEdit.deliveryMethod ||
                documentToEdit.delivery_mode ||
                loadedFormData.deliveryMode ||
                '';
              loadedFormData.deliveryMode = loadedFormData.deliveryMethod;

              loadedFormData.transporterName =
                documentToEdit.transporterName ||
                documentToEdit.transporter_name ||
                documentToEdit.carrier ||
                '';

              loadedFormData.trackingNumber =
                documentToEdit.trackingNumber ||
                documentToEdit.tracking_number ||
                '';

              loadedFormData.carrierTrackingNumber =
                documentToEdit.carrierTrackingNumber ||
                documentToEdit.carrier_tracking_number ||
                documentToEdit.shipmentCarrierTrackingNumber ||
                '';

              const pkg =
                documentToEdit.shipmentPackageCount ??
                documentToEdit.packageCount ??
                documentToEdit.package_count;
              const pkgN =
                pkg != null && pkg !== ''
                  ? Number(pkg)
                  : NaN;
              if (Number.isFinite(pkgN) && pkgN >= 0)
                loadedFormData.shipmentPackageCount = pkgN;

              loadedFormData.deliveryCarrier =
                documentToEdit.deliveryCarrier ||
                documentToEdit.carrier ||
                loadedFormData.deliveryCarrier ||
                '';

              const dd =
                documentToEdit.deliveryDate || documentToEdit.delivery_date;
              loadedFormData.deliveryDate =
                dd != null && String(dd).trim() !== '' ? String(dd).trim() : '';

              loadedFormData.deliveryTime =
                documentToEdit.deliveryTime ||
                documentToEdit.delivery_time ||
                loadedFormData.deliveryTime ||
                '';

              loadedFormData.deliveryConditions =
                documentToEdit.deliveryConditions ||
                documentToEdit.delivery_conditions ||
                loadedFormData.deliveryConditions ||
                '';
              loadedFormData.receivedBy =
                documentToEdit.receivedBy ||
                documentToEdit.received_by ||
                loadedFormData.receivedBy ||
                '';
            }
          } else if (docTypeUnderscore === 'shipping') {
            loadedFormData.instructions =
              loadedFormData.instructions ||
              documentToEdit.observations ||
              loadedFormData.notes ||
              documentToEdit.instructions ||
              documentToEdit.notes ||
              '';
          } else if (docTypeUnderscore === 'invoice' || docTypeUnderscore === 'proforma') {
            if (docTypeUnderscore === 'proforma') {
              loadedFormData.conditions =
                loadedFormData.conditions ||
                documentToEdit.conditions ||
                '';
            }
            if (docTypeUnderscore === 'invoice') {
              loadedFormData.paymentConditions =
                resolveInvoicePaymentConditionsFromArchive(documentToEdit) ||
                loadedFormData.paymentConditions ||
                '';
              loadedFormData.invoiceType =
                documentToEdit.invoiceType || documentToEdit.invoice_type || '';
              loadedFormData.orderReference =
                documentToEdit.orderReference ||
                documentToEdit.order_reference ||
                documentToEdit.purchaseOrderReference ||
                documentToEdit.purchase_order_reference ||
                '';
              loadedFormData.deliveryReference =
                documentToEdit.deliveryReference ||
                documentToEdit.delivery_reference ||
                documentToEdit.deliveryNoteReference ||
                documentToEdit.delivery_note_reference ||
                '';
              loadedFormData.projectNumber =
                documentToEdit.projectNumber ||
                documentToEdit.project_number ||
                documentToEdit.projectReference ||
                documentToEdit.project_reference ||
                '';
              const cor =
                documentToEdit.customerOrderReference ??
                documentToEdit.customer_order_reference ??
                documentToEdit.originalDocumentReference ??
                documentToEdit.original_document_reference ??
                '';
              loadedFormData.customerOrderReference =
                cor != null ? String(cor) : '';
              loadedFormData.paymentMethod =
                documentToEdit.paymentMethod || documentToEdit.payment_method || '';
              loadedFormData.dueDate =
                documentToEdit.dueDate || documentToEdit.due_date || loadedFormData.dueDate || '';
              loadedFormData.deliveryLocation =
                documentToEdit.deliveryLocation ||
                documentToEdit.delivery_location ||
                documentToEdit.deliveryPlace ||
                documentToEdit.delivery_place ||
                documentToEdit.deliveryAddress ||
                documentToEdit.delivery_address ||
                loadedFormData.deliveryLocation ||
                '';
              loadedFormData.deliveryPlace =
                documentToEdit.deliveryPlace ||
                documentToEdit.delivery_place ||
                documentToEdit.deliveryLocation ||
                documentToEdit.delivery_location ||
                documentToEdit.deliveryAddress ||
                documentToEdit.delivery_address ||
                loadedFormData.deliveryPlace ||
                loadedFormData.deliveryLocation ||
                '';
              const dd =
                documentToEdit.deliveryDate || documentToEdit.delivery_date;
              loadedFormData.deliveryDate =
                dd != null && String(dd).trim() !== ''
                  ? String(dd).trim()
                  : '';
            }
            if (docTypeUnderscore === 'proforma') {
              loadedFormData.plannedPaymentMethod =
                documentToEdit.plannedPaymentMethod ||
                documentToEdit.paymentMethod ||
                documentToEdit.payment_method ||
                '';
              loadedFormData.paymentDueDate =
                documentToEdit.paymentDueDate ||
                documentToEdit.dueDate ||
                documentToEdit.due_date ||
                '';
              loadedFormData.acceptanceDeadline =
                documentToEdit.acceptanceDeadline ||
                documentToEdit.acceptance_deadline ||
                '';
              const st =
                documentToEdit.proformaStatus ||
                documentToEdit.status ||
                loadedFormData.status ||
                'pending';
              loadedFormData.proformaStatus = st;
              loadedFormData.status = st;
              loadedFormData.deliveryTerms =
                documentToEdit.deliveryTerms || documentToEdit.delivery_terms || '';
              const dpp = Number(
                documentToEdit.downPayment ?? documentToEdit.down_payment,
              );
              if (Number.isFinite(dpp) && dpp >= 0) {
                loadedFormData.downPaymentPercent = dpp;
              }
              if (
                documentToEdit.guarantees != null &&
                String(documentToEdit.guarantees).trim() !== ''
              ) {
                loadedFormData.warranty = documentToEdit.guarantees;
              }
              loadedFormData.salesPerson =
                documentToEdit.salesPerson ||
                documentToEdit.commercial ||
                loadedFormData.salesPerson ||
                '';
              loadedFormData.commercial = loadedFormData.salesPerson;
              loadedFormData.deliveryTime =
                documentToEdit.deliveryTime ||
                documentToEdit.estimatedDeliveryTime ||
                documentToEdit.estimated_delivery_time ||
                documentToEdit.estimatedDelivery ||
                documentToEdit.estimated_delivery ||
                loadedFormData.deliveryTime ||
                '';
              loadedFormData.estimatedDelivery = loadedFormData.deliveryTime;
              loadedFormData.deliveryDelay =
                documentToEdit.deliveryDelay ||
                documentToEdit.desiredDeliveryTime ||
                documentToEdit.desired_delivery_time ||
                loadedFormData.deliveryDelay ||
                '';
              loadedFormData.paymentTerms =
                documentToEdit.paymentTerms ||
                documentToEdit.desiredPaymentTerms ||
                documentToEdit.desired_payment_terms ||
                documentToEdit.payment_terms ||
                loadedFormData.paymentTerms ||
                '';
              loadedFormData.paymentMethod =
                documentToEdit.paymentMethod ||
                documentToEdit.plannedPaymentMethod ||
                documentToEdit.payment_method ||
                loadedFormData.paymentMethod ||
                '';
              // échéancier : tableau déjà fusionné depuis documentToEdit.installments ci-dessus
            }
          } else if (docTypeUnderscore === 'quote') {
            loadedFormData.salesPerson =
              documentToEdit.salesPerson ||
              documentToEdit.commercial ||
              loadedFormData.salesPerson ||
              '';
            loadedFormData.commercial = loadedFormData.salesPerson;
            loadedFormData.deliveryTime =
              documentToEdit.deliveryTime ||
              documentToEdit.estimatedDeliveryTime ||
              documentToEdit.estimated_delivery_time ||
              documentToEdit.estimatedDelivery ||
              documentToEdit.estimated_delivery ||
              loadedFormData.deliveryTime ||
              '';
            loadedFormData.estimatedDelivery = loadedFormData.deliveryTime;
            loadedFormData.deliveryDelay =
              documentToEdit.deliveryDelay ||
              documentToEdit.desiredDeliveryTime ||
              documentToEdit.desired_delivery_time ||
              loadedFormData.deliveryDelay ||
              '';
            loadedFormData.paymentTerms =
              documentToEdit.paymentTerms ||
              documentToEdit.desiredPaymentTerms ||
              documentToEdit.desired_payment_terms ||
              documentToEdit.payment_terms ||
              loadedFormData.paymentTerms ||
              '';
            loadedFormData.paymentMethod =
              documentToEdit.paymentMethod || documentToEdit.payment_method || '';
            loadedFormData.dueDate =
              documentToEdit.dueDate || documentToEdit.due_date || loadedFormData.dueDate || '';
          } else if (
            docTypeUnderscore === 'credit_note' ||
            documentToEdit.type === 'credit-note'
          ) {
            const oin =
              documentToEdit.originalInvoiceNumber ||
              documentToEdit.original_invoice_number ||
              '';
            const oid =
              documentToEdit.originalInvoiceDate ||
              documentToEdit.original_invoice_date ||
              '';
            const oamtRaw =
              documentToEdit.originalInvoiceAmount ??
              documentToEdit.original_invoice_amount;
            let oamt = Number(oamtRaw);
            if (!Number.isFinite(oamt) && oamtRaw != null && String(oamtRaw).trim() !== '') {
              const p = parseFloat(String(oamtRaw).replace(',', '.'));
              oamt = Number.isFinite(p) ? p : 0;
            } else if (!Number.isFinite(oamt)) {
              oamt = 0;
            }

            loadedFormData.originalInvoice = {
              number: oin,
              date: oid != null ? String(oid).trim() : '',
              amount: oamt,
            };
            loadedFormData.originalInvoiceNumber = oin;
            loadedFormData.originalInvoiceDate = loadedFormData.originalInvoice.date;
            loadedFormData.originalInvoiceAmount = oamt;

            const cnApp =
              documentToEdit.creditNoteApplicationDate ||
              documentToEdit.credit_note_application_date ||
              '';
            const cnType =
              documentToEdit.creditNoteType ||
              documentToEdit.credit_type ||
              documentToEdit.creditType ||
              '';
            const refund =
              documentToEdit.refundMethod ||
              documentToEdit.refund_method ||
              documentToEdit.creditNoteRefundMethod ||
              '';

            loadedFormData.creditNoteApplicationDate = cnApp;
            loadedFormData.creditNoteType = cnType;
            loadedFormData.creditNoteRefundMethod = refund;

            loadedFormData.creditNote = {
              applicationDate: cnApp || loadedFormData.date || '',
              type: cnType,
              refundMethod: refund,
            };

            const cna = Number(
              documentToEdit.creditNoteAmount ?? documentToEdit.credit_note_amount,
            );
            loadedFormData.creditNoteAmount = Number.isFinite(cna) ? cna : 0;
            const cvr = Number(
              documentToEdit.creditNoteVatRate ??
                documentToEdit.credit_note_vat_rate,
            );
            loadedFormData.creditNoteVatRate = Number.isFinite(cvr) ? cvr : 19;
            loadedFormData.creditNoteVatAmount =
              Number(documentToEdit.creditNoteVatAmount ?? documentToEdit.credit_note_vat_amount) ||
              0;
            loadedFormData.creditNoteTotalAmount =
              Number(
                documentToEdit.creditNoteTotalAmount ??
                  documentToEdit.credit_note_total_amount,
              ) || 0;
            loadedFormData.creditNoteRefundDelay =
              documentToEdit.creditNoteRefundDelay ||
              documentToEdit.credit_note_refund_delay ||
              '15_jours';
            loadedFormData.creditNoteStatus =
              documentToEdit.creditNoteStatus ||
              documentToEdit.credit_note_status ||
              '';
            loadedFormData.creditNoteReason =
              documentToEdit.creditNoteReason ||
              documentToEdit.credit_note_reason ||
              documentToEdit.motif ||
              '';
            loadedFormData.creditNoteRefundConditions =
              documentToEdit.creditNoteRefundConditions ||
              documentToEdit.credit_note_refund_conditions ||
              '';

            loadedFormData.issuerName =
              documentToEdit.creditIssuerName ||
              documentToEdit.credit_issuer_name ||
              documentToEdit.issuerName ||
              '';
            loadedFormData.issuerNif =
              documentToEdit.creditIssuerNif ||
              documentToEdit.credit_issuer_nif ||
              documentToEdit.issuerNif ||
              '';
            loadedFormData.issuerAddress =
              documentToEdit.creditIssuerAddress ||
              documentToEdit.credit_issuer_address ||
              documentToEdit.issuerAddress ||
              '';

            loadedFormData.creditNoteParty =
              documentToEdit.creditNoteParty ||
              documentToEdit.credit_note_party ||
              (documentToEdit.isSupplier || documentToEdit.is_supplier ? 'supplier' : 'client');

            if (loadedFormData.creditNoteParty === 'supplier') {
              loadedFormData.supplierName =
                documentToEdit.supplierName ||
                documentToEdit.supplier_name ||
                documentToEdit.client ||
                '';
            } else {
              loadedFormData.clientName =
                documentToEdit.clientName ||
                documentToEdit.client_name ||
                documentToEdit.client ||
                '';
            }
          } else if (
            docTypeUnderscore === 'payment_voucher' ||
            docTypeUnderscore === 'payment_slip' ||
            docTypeUnderscore === 'payment_receipt'
          ) {
            const rows =
              Array.isArray(documentToEdit.paymentLines) && documentToEdit.paymentLines.length
                ? documentToEdit.paymentLines
                : Array.isArray(documentToEdit.payments)
                  ? documentToEdit.payments
                  : null;

            if (
              docTypeUnderscore === 'payment_voucher' ||
              docTypeUnderscore === 'payment_slip'
            ) {
              loadedFormData.expectedCashingDate =
                documentToEdit.expectedCashingDate ||
                documentToEdit.dueDate ||
                documentToEdit.due_date ||
                '';
              loadedFormData.paymentReason = resolvePaymentVoucherReasonValue(
                documentToEdit.paymentReason || documentToEdit.motif,
              );
              loadedFormData.transactionNumber =
                documentToEdit.transactionNumber ||
                documentToEdit.transaction_number ||
                '';
              loadedFormData.paymentMethod =
                documentToEdit.paymentMethod || documentToEdit.payment_method || '';
              loadedFormData.issuerBank =
                documentToEdit.issuerBank ||
                documentToEdit.bankName ||
                documentToEdit.bank_name ||
                '';
              loadedFormData.receiverBank =
                documentToEdit.receiverBank ||
                documentToEdit.beneficiaryName ||
                documentToEdit.beneficiary_name ||
                '';
              const accDec = decodePaymentVoucherAccountsPayerContact(
                documentToEdit.payerContact ?? documentToEdit.payer_contact,
              );
              loadedFormData.issuerAccount =
                documentToEdit.issuerAccount ||
                documentToEdit.issuer_account ||
                accDec.issuerAccount ||
                '';
              loadedFormData.receiverAccount =
                documentToEdit.receiverAccount ||
                documentToEdit.receiver_account ||
                accDec.receiverAccount ||
                '';
              loadedFormData.relatedInvoices =
                documentToEdit.relatedInvoices ||
                documentToEdit.invoicesPaid ||
                documentToEdit.invoices_paid ||
                '';
              loadedFormData.purchaseOrderRef =
                documentToEdit.purchaseOrderRef ||
                documentToEdit.purchaseOrderReference ||
                documentToEdit.purchase_order_reference ||
                documentToEdit.orderReference ||
                documentToEdit.order_reference ||
                '';
              loadedFormData.supplierCreditNoteRef =
                documentToEdit.supplierCreditNoteRef ||
                documentToEdit.originalDocumentReference ||
                documentToEdit.original_document_reference ||
                '';
              loadedFormData.paymentLines = normalizePaymentVoucherLines(rows, {
                purchaseOrderRef: loadedFormData.purchaseOrderRef,
                supplierCreditNoteRef: loadedFormData.supplierCreditNoteRef,
                relatedInvoices: loadedFormData.relatedInvoices,
              });
              const payAmt = Number(
                documentToEdit.paymentAmount ?? documentToEdit.payment_amount,
              );
              if (Number.isFinite(payAmt) && payAmt >= 0) {
                loadedFormData.total = payAmt;
              }
            } else if (docTypeUnderscore === 'payment_receipt') {
              loadedFormData.paymentMethod =
                documentToEdit.paymentMethod || documentToEdit.payment_method || '';
              loadedFormData.paymentReference =
                documentToEdit.paymentReference ||
                documentToEdit.transactionNumber ||
                documentToEdit.transaction_number ||
                '';
              loadedFormData.paymentBank =
                documentToEdit.paymentBank ||
                documentToEdit.bankName ||
                documentToEdit.bank_name ||
                '';
              loadedFormData.paymentSettlementType =
                documentToEdit.paymentSettlementType ||
                documentToEdit.paymentType ||
                documentToEdit.payment_type ||
                '';
              loadedFormData.paymentInvoiceNumbers =
                documentToEdit.paymentInvoiceNumbers ||
                documentToEdit.invoicesPaid ||
                documentToEdit.invoices_paid ||
                '';
              loadedFormData.clientCreditNoteRef =
                documentToEdit.clientCreditNoteRef ||
                documentToEdit.originalDocumentReference ||
                documentToEdit.original_document_reference ||
                '';
              loadedFormData.receiptReceivedBy =
                documentToEdit.receiptReceivedBy ||
                documentToEdit.receivedBy ||
                documentToEdit.received_by ||
                '';
              loadedFormData.paymentDate =
                documentToEdit.paymentDate ||
                documentToEdit.payment_date ||
                loadedFormData.date ||
                '';
              loadedFormData.paymentLines = normalizePaymentReceiptLines(rows, {
                paymentInvoiceNumbers: loadedFormData.paymentInvoiceNumbers,
                clientCreditNoteRef: loadedFormData.clientCreditNoteRef,
              });
              const payAmtRec = Number(
                documentToEdit.paymentAmount ??
                  documentToEdit.payment_amount ??
                  documentToEdit.amount,
              );
              if (Number.isFinite(payAmtRec) && payAmtRec >= 0) {
                loadedFormData.total = payAmtRec;
              }
            } else {
              loadedFormData.paymentLines = rows?.length
                ? rows
                : [{ description: '', amount: 0 }];
            }
          }
          
          // Add document-specific fields
          if (documentToEdit.specifications && !loadedFormData.specifications?.length) {
            loadedFormData.specifications = documentToEdit.specifications;
          }
          
          if (documentToEdit.return) {
            loadedFormData.return = documentToEdit.return;
          }
          
          if (documentToEdit.reception) {
            loadedFormData.reception = documentToEdit.reception;
          }
          
          if (documentToEdit.shipment) {
            loadedFormData.shipment = documentToEdit.shipment;
          }

          const dec = decodeDocumentUiOverrides(
            documentToEdit.validityNotes ?? documentToEdit.validity_notes,
          );
          if (dec) {
            if (Array.isArray(dec.sectionOrder) && dec.sectionOrder.length > 0) {
              setSectionOrder(dec.sectionOrder);
            }
            if (Array.isArray(dec.taxOrder) && dec.taxOrder.length > 0) {
              setTaxOrder(sanitizeTaxOrderForBrand(dec.taxOrder));
            }
            applyDecodedUiOverridesToFormData(loadedFormData, dec);
          }
          applyFallbackUiOverridesOnEditLoad(
            loadedFormData,
            documentToEdit,
            docTypeUnderscore,
          );
          if (loadedFormData.showSupplementarySections === undefined) {
            loadedFormData.showSupplementarySections = true;
          }

          applyTaxFieldsFromArchiveToFormData(
            loadedFormData,
            documentToEdit,
            docTypeUnderscore,
          );

          hydratePartnerFieldsOnEditLoad(
            loadedFormData,
            documentToEdit,
            allClients,
            allSuppliers,
          );

          if (
            docTypeUnderscore === 'credit_note' ||
            documentToEdit.type === 'credit-note'
          ) {
            const party = loadedFormData.creditNoteParty || 'client';
            setSelectedType((prev) =>
              prev
                ? {
                    ...prev,
                    title:
                      party === 'supplier'
                        ? CREDIT_NOTE_LABEL_SUPPLIER
                        : CREDIT_NOTE_LABEL_CLIENT,
                    color: party === 'supplier' ? '#60a5fa' : '#10b981',
                  }
                : prev
            );
          }

          const editStatusTypeId =
            typeConfig?.id || creationTypeId || documentToEdit.type || '';
          const resolvedStatus = normalizeDocumentStatus(
            editStatusTypeId,
            documentToEdit.status || loadedFormData.status,
            [
              loadedFormData.creditNoteStatus,
              documentToEdit.creditNoteStatus,
              documentToEdit.credit_note_status,
              loadedFormData.clientReturnStatus,
              documentToEdit.clientReturnStatus,
              documentToEdit.returnStatus,
              documentToEdit.return_status,
              loadedFormData.receptionStatus,
              documentToEdit.receptionStatus,
              documentToEdit.reception_status,
              loadedFormData.exitNoteStatus,
              documentToEdit.exitNoteStatus,
              loadedFormData.transferNoteStatus,
              documentToEdit.transferNoteStatus,
              documentToEdit.transferStatus,
              documentToEdit.transfer_status,
            ],
          );
          loadedFormData.status = resolvedStatus;
          if (
            docTypeUnderscore === 'credit_note' ||
            documentToEdit.type === 'credit-note'
          ) {
            loadedFormData.creditNoteStatus = resolvedStatus;
          }
          if (
            docTypeUnderscore === 'client_return_slip' ||
            documentToEdit.type === 'client-return-slip'
          ) {
            loadedFormData.clientReturnStatus = resolvedStatus;
          }
          if (
            docTypeUnderscore === 'receipt_note' ||
            documentToEdit.type === 'reception-note'
          ) {
            loadedFormData.receptionStatus = resolvedStatus;
          }
          if (docTypeUnderscore === 'exit_slip' || documentToEdit.type === 'exit-note') {
            loadedFormData.exitNoteStatus = resolvedStatus;
          }
          if (
            docTypeUnderscore === 'transfer_slip' ||
            documentToEdit.type === 'transfer-note'
          ) {
            loadedFormData.transferNoteStatus = resolvedStatus;
          }
          
          setFormData(loadedFormData);
          setIsAutoGenerated(false);
          
          console.log('? Document loaded successfully for editing:', {
            id: documentToEdit.id,
            type: documentToEdit.type,
            number: documentToEdit.number,
            formData: loadedFormData
          });
        } else {
          console.error('? Document type configuration not found:', documentToEdit.type, editType);
          alert(t('document_creation.alerts.unsupported_edit_type'));
        }
      } else if (documents.length > 0) {
        console.error('? Document not found for editing:', editId);
        alert(t('document_creation.alerts.document_not_found'));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [editId, editType, documents, allClients, allSuppliers]);

  /** Édition : synchroniser le libellé partenaire avec la liste déroulante (id connu, nom vide ou obsolète). */
  useEffect(() => {
    if (!editId) return;
    setFormData((prev) => {
      let changed = false;
      const next = { ...prev };
      if (prev.supplier && String(prev.supplier).trim()) {
        const resolved = resolvePartnerDropdownLabel(
          prev.supplier,
          prev.supplierName,
          availableSuppliersForDropdown,
          allSuppliers,
        );
        if (resolved && resolved !== String(prev.supplierName || '').trim()) {
          next.supplierName = resolved;
          changed = true;
        }
      }
      if (prev.client && String(prev.client).trim()) {
        const resolved = resolvePartnerDropdownLabel(
          prev.client,
          prev.clientName,
          availableClientsForDropdown,
          allClients,
        );
        if (resolved && resolved !== String(prev.clientName || '').trim()) {
          next.clientName = resolved;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [
    editId,
    allSuppliers,
    allClients,
    availableSuppliersForDropdown,
    availableClientsForDropdown,
  ]);

  // Handle conversion from Achats/Ventes
  useEffect(() => {
    const convertParam = searchParams.get('convert');
    const typeParam = searchParams.get('type');
    
    console.log('?? Conversion check - convertParam:', convertParam, 'typeParam:', typeParam, 'editId:', editId);
    
    if (convertParam && typeParam && !editId) {
      console.log('? Processing conversion...');
      
      // Get conversion data from sessionStorage
      const conversionDataStr = sessionStorage.getItem('documentConversionData');
      console.log('?? ConversionData from sessionStorage:', conversionDataStr);
      
      if (conversionDataStr) {
        try {
          const conversionData = JSON.parse(conversionDataStr);
          console.log('?? Converting transaction to document:', conversionData);
          
          // Find the document type configuration
          const typeConfig = documentTypes.find(type => type.id === typeParam);
          console.log('?? Found typeConfig:', typeConfig);
          
          if (typeConfig) {
            console.log('?? Setting selectedType and currentStep...');
            setSelectedType(typeConfig);
            setCurrentStep('create');
            
            // Initialize form with conversion data
            const initialFormData = initializeFormData(typeParam);
            console.log('?? Initial form data:', initialFormData);
            
            // Pre-fill with conversion data
            console.log('?? Processing conversion data items:', conversionData.items);
            
            let documentItems = [];
            
            // Check if we have detailed product items from new achats/ventes
            if (conversionData.items && Array.isArray(conversionData.items) && conversionData.items.length > 0) {
              console.log('? Found detailed product items, converting to document format...');
              
              documentItems = conversionData.items.map((item, index) => {
                console.log(`?? Converting item ${index + 1}:`, item);
                return {
                  id: item.id || Date.now() + index,
                  product: item.productName || item.name || `Produit ${index + 1}`,
                  productId: item.productId || null,
                  quantity: item.quantity || 1,
                  price: item.unitPrice || item.price || 0,
                  total: item.subtotal || (item.quantity * item.unitPrice) || 0,
                  description: item.description || '',
                  unit: item.unit || ''
                };
              });
              
              console.log('? Converted product items:', documentItems);
            } else {
              console.log('?? No detailed items found, creating fallback item from total amount...');
              // Fallback: create single item from total amount (old behavior)
              documentItems = [{ 
                id: Date.now(), 
                product: conversionData.clientName || conversionData.supplierName || '', 
                quantity: 1, 
                price: conversionData.amount || 0, 
                total: conversionData.amount || 0,
                unit: facturationConfig.defaultUnit || 'قطعة'
              }];
            }
            
            // Calculate totals from items
            const calculatedSubtotal = documentItems.reduce((sum, item) => sum + (item.total || 0), 0);
            console.log('?? Calculated subtotal from items:', calculatedSubtotal);
            
            const preFilledData = {
              ...initialFormData,
              client: conversionData.client || conversionData.supplier || '',
              clientName: conversionData.clientName || conversionData.supplierName || '',
              date: conversionData.date || new Date().toISOString().split('T')[0],
              notes: conversionData.notes || '',
              items: documentItems,
              number:
                conversionData.source === 'agent'
                  ? initialFormData.number || ''
                  : conversionData.saleNumber || conversionData.purchaseNumber || '',
            };

            if (conversionData.supplier || conversionData.supplierName) {
              preFilledData.supplier = conversionData.supplier || '';
              preFilledData.supplierName = conversionData.supplierName || '';
              if (typeParam === 'credit-note' || typeParam === 'credit_note') {
                preFilledData.creditNoteParty = 'supplier';
              }
            }

            if (conversionData.dueDate) {
              preFilledData.dueDate = conversionData.dueDate;
            }
            if (conversionData.title) {
              preFilledData.title = conversionData.title;
            }
            
            // Add payment method if available
            if (conversionData.paymentMethod) {
              preFilledData.paymentMethod = conversionData.paymentMethod;
            }
            
            // Use calculated totals from items or fallback to original amount
            preFilledData.subtotal = calculatedSubtotal || conversionData.amount || 0;
            preFilledData.total = calculatedSubtotal || conversionData.amount || 0;
            
            console.log('?? Final totals - Subtotal:', preFilledData.subtotal, 'Total:', preFilledData.total);
            
            console.log('?? Pre-filled data:', preFilledData);
            console.log('?? Setting isFromConversion flag to prevent form resets...');
            setIsFromConversion(true); // Set flag to prevent other useEffects from overriding
            setFormData(preFilledData);
            
            // Debug: Check what was actually set
            setTimeout(() => {
              console.log('?? Form data after conversion (delayed check):', formData);
            }, 100);
            
            console.log('?? Cleaning up sessionStorage...');
            // Clean up sessionStorage
            sessionStorage.removeItem('documentConversionData');
          } else {
            console.error('? No typeConfig found for:', typeParam);
          }
        } catch (error) {
          console.error('? Error parsing conversion data:', error);
          sessionStorage.removeItem('documentConversionData');
        }
      } else {
        console.warn('?? No conversion data found in sessionStorage');
        // Even without data, we can still redirect to create step
        const typeConfig = documentTypes.find(type => type.id === typeParam);
        if (typeConfig) {
          console.log('?? Setting up basic conversion without data...');
          setSelectedType(typeConfig);
          setCurrentStep('create');
          setFormData(initializeFormData(typeParam));
        }
      }
    } else {
      console.log('? Conversion conditions not met');
    }
  }, [searchParams, editId]);

  // ?? Handle direct document type selection (from Inventaire / Logistique module)
  useEffect(() => {
    const typeParam = searchParams.get('type');
    const convertParam = searchParams.get('convert');
    
    // Only handle if we have JUST the type parameter (no convert, no editId)
    if (typeParam && !convertParam && !editId && currentStep === 'select') {
      console.log('?? Direct document type selection from module:', typeParam);
      
      // Find the document type configuration
      const typeConfig = documentTypes.find(type => type.id === typeParam);
      
      if (typeConfig) {
        console.log('? Found document type config, switching to create step:', typeConfig.title);
        setSelectedType(typeConfig);
        setCurrentStep('create');
        const init = initializeFormData(typeParam);
        const partyParam = searchParams.get('party');
        const typeNorm = String(typeParam).replace(/_/g, '-');
        if (typeNorm === 'credit-note' && partyParam === 'supplier') {
          init.creditNoteParty = 'supplier';
        } else if (typeNorm === 'credit-note' && partyParam === 'client') {
          init.creditNoteParty = 'client';
        } else if (typeNorm === 'simplified-invoice') {
          init.zatcaInvoiceSubtype = 'simplified';
          init.invoiceType = 'simplified';
          init.zatcaProfile = 'b2c';
        } else if (typeNorm === 'debit-note') {
          init.debitNote = true;
          init.creditNoteParty = 'client';
        }
        setFormData(init);
      } else {
        console.warn('?? Document type not found:', typeParam, 'Available types:', documentTypes.map(t => t.id));
      }
    }
  }, [searchParams, editId, currentStep]);

  // ?? Ensure deliveryDate is set for Bon de livraison when form loads
  useEffect(() => {
    if (selectedType && (selectedType.id === 'delivery-note' || selectedType.id === 'delivery') && currentStep === 'create' && !formData.deliveryDate) {
      console.log('?? Auto-setting deliveryDate with configured delay for delivery note');
      setFormData(prev => ({
        ...prev,
        deliveryDate: calculateDateWithDelay('delivery', 0)
      }));
    }
  }, [selectedType, currentStep, formData.deliveryDate]);

  useEffect(() => {
    if (!isAgentEmbed || currentStep !== 'create' || !selectedType) return;
    if (typeof window === 'undefined' || window.parent === window) return;
    const timer = window.setTimeout(() => {
      window.parent.postMessage({ type: 'agent-document-embed-ready' }, window.location.origin);
    }, 50);
    return () => window.clearTimeout(timer);
  }, [
    isAgentEmbed,
    currentStep,
    selectedType?.id,
    formData.client,
    formData.clientName,
    formData.supplierName,
  ]);

  // Fonction pour générer les numéros de documents avec les préfixes configurés en temps réel
  const generateDocumentNumber = (docType) => {
    // Mapping des types de documents vers les préfixes configurés
    const docPrefix = (key, fallback) => getFacturationDocumentPrefix(settings, key, fallback);
    const prefixMap = {
      'invoice': docPrefix('invoice', 'FAC'),
      'simplified-invoice': docPrefix('simplifiedInvoice', 'FSM'),
      'debit-note': docPrefix('debitNote', 'ND'),
      'quote-request': docPrefix('quoteRequest', 'DD'),
      'proforma-request': docPrefix('proformaRequest', 'DPR'),
      'quote': docPrefix('quote', 'DEV'),
      'purchase-order': docPrefix('order', 'BC'),
      'delivery-note': docPrefix('delivery', 'BL'),
      'shipping-slip': docPrefix('sendSlip', 'BE'),
      'reception-note': docPrefix('reception', 'BRL'),
      'return-slip': docPrefix('supplierReturnSlip', 'BRF'),
      'client-return-slip': docPrefix('customerReturnSlip', 'BRC'),
      'stock-sheet': docPrefix('stockSheet', 'FS'),
      'material-exit': docPrefix('delivery', 'BL'),
      'credit-note': docPrefix('creditNote', 'NC'),
      'account-statement': docPrefix('accountStatement', 'RLC'),
      'advance-invoice': docPrefix('advanceInvoice', 'ACO'),
      'payment-receipt': docPrefix('paymentReceipt', 'RP'),
      'payment-voucher': docPrefix('paymentSlip', 'BV'),
      'payment-schedule': docPrefix('paymentSchedule', 'ECH'),
      'commercial-contract': docPrefix('commercialContract', 'CC'),
      'service-agreement': docPrefix('serviceAgreement', 'CS'),
      'compliance-certificate': docPrefix('complianceCertificate', 'CERT'),
      'formal-notice': docPrefix('formalNotice', 'MD'),
      'reception-report': docPrefix('receptionReport', 'PVR'),
      'intervention-report': docPrefix('interventionReport', 'RI'),
      'non-compliance-sheet': docPrefix('nonComplianceSheet', 'FNC'),
      'tax-certificate': docPrefix('taxCertificate', 'AF'),
      'work-certificate': docPrefix('workCertificate', 'CTE'),
      'legal-notice': docPrefix('legalNotice', 'NL'),
      'payslip': docPrefix('payslip', 'FP'),
      'work-contract': docPrefix('workContract', 'CT'),
      'work-attestation': docPrefix('workAttestation', 'AT'),
      'leave-request': docPrefix('leaveRequest', 'DC'),
      'production-sheet': docPrefix('productionSheet', 'FPR'),
      'quality-control': docPrefix('qualityControl', 'CQ'),
      'manufacturing-order': docPrefix('manufacturingOrder', 'BF'),
      'failure-report': docPrefix('failureReport', 'RD'),
      'route-sheet': docPrefix('routeSheet', 'FR'),
      'delivery-report': docPrefix('deliveryReport', 'RL'),
      'transport-order': docPrefix('transportOrder', 'BT'),
      'rental-contract': docPrefix('rentalContract', 'CL'),
      'property-inspection': docPrefix('propertyInspection', 'EDL'),
      'rent-receipt': docPrefix('rentReceipt', 'QL'),
      'accident-report': docPrefix('accidentReport', 'DA'),
      'security-report': docPrefix('securityReport', 'RS'),
      'safety-compliance': docPrefix('safetyCompliance', 'FCS'),
      'maintenance-report': docPrefix('maintenanceReport', 'RM'),
      'maintenance-plan': docPrefix('maintenancePlan', 'PM'),
      'client-intervention': docPrefix('clientIntervention', 'FIC'),
      'product-warranty': docPrefix('productWarranty', 'GAR'),
      'it-audit': docPrefix('itAudit', 'RAI'),
      'software-install': docPrefix('softwareInstall', 'FIL'),
      'it-maintenance': docPrefix('itMaintenance', 'CMIT'),
      'backup': docPrefix('backup', 'BAK'),
      'purchase-request': docPrefix('purchaseRequest', 'DA'),
      'tender-call': docPrefix('tenderCall', 'AO'),
      'supplier-comparison': docPrefix('supplierComparison', 'CF'),
      'goods-reception': docPrefix('goodsReception', 'RM'),
      'training-plan': docPrefix('trainingPlan', 'PF'),
      'training-evaluation': docPrefix('trainingEvaluation', 'EF'),
      'training-certificate': docPrefix('trainingCertificate', 'CF'),
      'prospect-sheet': docPrefix('prospectSheet', 'FP'),
      'client-visit-report': docPrefix('clientVisitReport', 'RVC'),
      'price-catalog': docPrefix('priceCatalog', 'CAT'),
      'equipment-sheet': docPrefix('equipmentSheet', 'FE'),
      'calibration': docPrefix('calibration', 'CAL'),
      'material-loan': docPrefix('materialLoan', 'PM'),
      'transfer-note': docPrefix('transferNote', 'BT'),
      'exit-note': docPrefix('exitNote', 'BS'),
      'stock-entry': docPrefix('stockEntry', 'BE'),
      'dashboard': docPrefix('dashboard', 'TDB'),
      'financial-report': docPrefix('financialReport', 'RF'),
      'market-study': docPrefix('marketStudy', 'EM'),
      'benchmark': docPrefix('benchmark', 'BEN')
    };

    const prefix = prefixMap[docType] || 'DOC';
    
    // Obtenir le compteur actuel pour ce type de document
    const counterMap = {
      'invoice': 'invoice',
      'simplified-invoice': 'simplifiedInvoice',
      'debit-note': 'debitNote',
      'quote-request': 'quoteRequest',
      'proforma-request': 'proformaRequest',
      'quote': 'quote',
      'purchase-order': 'purchaseOrder',
      'delivery-note': 'deliveryNote',
      'shipping-slip': 'shippingSlip',
      'reception-note': 'receptionNote',
      'return-slip': 'returnSlip',
      'client-return-slip': 'clientReturnSlip',
      'stock-sheet': 'stockSheet',
      'material-exit': 'materialExit',
      'credit-note': 'creditNote',
      'account-statement': 'accountStatement',
      'advance-invoice': 'advanceInvoice',
      'payment-receipt': 'paymentReceipt',
      'payment-schedule': 'paymentSchedule',
      'commercial-contract': 'commercialContract',
      'service-agreement': 'serviceAgreement',
      'compliance-certificate': 'complianceCertificate',
      'formal-notice': 'formalNotice',
      'reception-report': 'receptionReport',
      'intervention-report': 'interventionReport',
      'non-compliance-sheet': 'nonComplianceSheet',
      'tax-certificate': 'taxCertificate',
      'work-certificate': 'workCertificate',
      'legal-notice': 'legalNotice',
      'payslip': 'payslip',
      'work-contract': 'workContract',
      'work-attestation': 'workAttestation',
      'leave-request': 'leaveRequest',
      'production-sheet': 'productionSheet',
      'quality-control': 'qualityControl',
      'manufacturing-order': 'manufacturingOrder',
      'failure-report': 'failureReport',
      'route-sheet': 'routeSheet',
      'delivery-report': 'deliveryReport',
      'transport-order': 'transportOrder',
      'rental-contract': 'rentalContract',
      'property-inspection': 'propertyInspection',
      'rent-receipt': 'rentReceipt',
      'accident-report': 'accidentReport',
      'security-report': 'securityReport',
      'safety-compliance': 'safetyCompliance',
      'maintenance-report': 'maintenanceReport',
      'maintenance-plan': 'maintenancePlan',
      'client-intervention': 'clientIntervention',
      'product-warranty': 'productWarranty',
      'it-audit': 'itAudit',
      'software-install': 'softwareInstall',
      'it-maintenance': 'itMaintenance',
      'backup': 'backup',
      'purchase-request': 'purchaseRequest',
      'tender-call': 'tenderCall',
      'supplier-comparison': 'supplierComparison',
      'goods-reception': 'goodsReception',
      'training-plan': 'trainingPlan',
      'training-evaluation': 'trainingEvaluation',
      'training-certificate': 'trainingCertificate',
      'prospect-sheet': 'prospectSheet',
      'client-visit-report': 'clientVisitReport',
      'price-catalog': 'priceCatalog',
      'equipment-sheet': 'equipmentSheet',
      'calibration': 'calibration',
      'material-loan': 'materialLoan',
      'payment-voucher': 'paymentVoucher',
      'payment-receipt': 'paymentReceipt',
      'transfer-note': 'transferNote',
      'exit-note': 'exitNote',
      'stock-entry': 'stockEntry',
      'credit-note': 'creditNote',
      'dashboard': 'dashboard',
      'financial-report': 'financialReport',
      'market-study': 'marketStudy',
      'benchmark': 'benchmark'
    };

    // Trouver le prochain numéro de séquence basé sur les documents existants
    const existingDocuments = documents.filter(doc => {
      // Normaliser les types de documents pour la comparaison
      const normalizedDocType = docType.replace('-', '_');
      const normalizedExistingType = doc.type?.replace('-', '_');
      return normalizedExistingType === normalizedDocType;
    });

    // Extraire les numéros de séquence existants pour ce type de document
    const existingNumbers = existingDocuments
      .map(doc => {
        if (doc.number && doc.number.startsWith(prefix + '-')) {
          const numberPart = doc.number.split('-').pop();
          return parseInt(numberPart, 10);
        }
        return 0;
      })
      .filter(num => !isNaN(num) && num > 0);

    // Trouver le prochain numéro disponible
    const nextSequence = existingNumbers.length > 0 
      ? Math.max(...existingNumbers) + 1 
      : 1;
    
    // Format du numéro avec 01, 02, etc.
    const sequence = nextSequence.toString().padStart(2, '0');
    
    return `${prefix}-${sequence}`;
  };
  const documentCategories = useMemo(
    () => [
      {
        id: 'all',
        title: t('sidebar.create_document', { defaultValue: 'إنشاء مستند' }),
        color: '#6e9b6e',
        documents: buildPickerDocuments(CREATE_DOCUMENT_PICKER_META, {
          icons: CREATE_DOCUMENT_CARD_ICONS,
          color: '#6e9b6e',
          pickerLabel,
        }),
      },
    ],
    [t, i18n.language]
  );

  // ?? Helper function to calculate date with delay from settings
  const calculateDateWithDelay = (delayKey, defaultDays = 0) => {
    const delay = getFacturationDocumentDelay(settings, delayKey, defaultDays);
    return new Date(Date.now() + delay * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  };

  const initializeFormData = (docType) => {
    const ld = legalDefault;
    const defaultPaymentTerms = resolveFacturationDefaultPaymentTerms(facturationConfig);
    const defaultOrderPaymentTerms = resolveFacturationDefaultPaymentTerms(facturationConfig, { format: 'extended' });
    const baseData = {
      client: '',
      date: new Date().toISOString().split('T')[0],
      items: [{ product: '', quantity: 1, price: 0, total: 0 }],
      notes: '',
      status: 'pending',
      // Smart default values for section display options
      showNotes: true,           // — Always checked (most important)
      showConditions: false,     // — Only when needed
      showObjectif: false,       // — Only for commercial docs
      showObservations: true,    // — Usually checked (common)
      showGaranties: false,      // — Only when needed
      showValidite: false,       // — Only when needed
      showMotif: true,           // — Usually checked (common)
      showInstructions: false,   // — Only for delivery/shipping docs
      // Tax/Financial elements - all default to false, will be set per document type
      showTva: false,
      showTap: false,
      showTimbre: false,
      showRemise: false,
      showRabais: false,
      showEscompte: false,
      showTransport: false,
      showRetour: false,
      showAcompte: false,
      showAvance: false,
      showGarantie: false,
      showIrg: false,
      showIbs: false,
      // Tax/Financial values
      tvaRate: resolveLiveVatRate(settings, facturationConfig),
      vatMode: getFacturationVatMode(settings),
      tapRate: Number.isFinite(settings?.taxes?.tap) ? settings.taxes.tap : 0,
      timbre: settings?.taxes?.stamp || 0,
      remise: settings?.taxes?.remise || 0,
      rabais: settings?.taxes?.rabais || 0,
      escompte: settings?.taxes?.escompte || 0,
      transport: settings?.taxes?.transportCost || 0,
      retour: settings?.taxes?.returnCost || 0,
      acompte: 0,
      avance: settings?.taxes?.avance || 0,
      garantie: settings?.taxes?.guarantee || 0,
      irg: settings?.taxes?.irg || 0,
      ibs: settings?.taxes?.ibs || 0,
      // Old fields for backward compatibility
      guarantee: 0,
      stamp: 0,
      // ?? SIGNATURES - 3 separate signature sections
      // 1. BON POUR (Approval)
      showBonPour: true,
      bonPourName: '',
      bonPourRole: '',
      bonPourDate: new Date().toISOString().split('T')[0],
      bonPourPlace: '',
      // 2. ÉTABLI PAR (Prepared by)
      showEtabliePar: false,
      etablieParName: '',
      etablieParRole: '',
      etablieParDate: new Date().toISOString().split('T')[0],
      etablieParPlace: '',
      // 3. REÇUE PAR (Received by)
      showRecuePar: false,
      recueParName: '',
      recueParRole: '',
      recueParDate: new Date().toISOString().split('T')[0],
      recueParPlace: '',
      // ?? SUPPLEMENTARY SECTIONS (Notes, Conditions, etc.)
      showSupplementarySections: true
    };

    switch (docType) {
      case 'invoice':
        return {
          ...baseData,  // — Spread base defaults first
          number: generateDocumentNumber('invoice'),
          date: new Date().toISOString().split('T')[0],
          client: '',
          clientAddress: '',
          dueDate: calculateDateWithDelay('invoice', 30),
          paymentTerms: defaultPaymentTerms,
          paymentMethod: '',
          paymentConditions: '',
          issuedBy: '',
          salesPerson: '',
          priority: '',
          invoiceType: '',
          orderReference: '',
          deliveryReference: '',
          projectNumber: '',
          customerOrderReference: '',
          deliveryLocation: '',
          deliveryDate: '',
          // ?? LOGICAL CONFIG: Facture (9) - Only common sections pre-checked
          showNotes: true,
          showConditions: true,
          showObjectif: false,
          showObservations: false,
          showGaranties: false,
          showValidite: false,
          showMotif: false,
          ...getTaxDefaults('invoice'),
          showEcheancier: true,
          // Old fields for backward compatibility
          showGuarantee: false,
          showStamp: false,
          tapRate: 0,
          issuerRC: '',
          clientNIF: '',
          clientNIS: '',
          clientAI: '',
          items: [{ product: '', quantity: 1, price: 0, total: 0 }],
          subtotalHT: 0,
          tva19: 0,
          tap2: 0,
          totalTTC: 0,
          amountInWords: '',
          guarantee: 0,
          stamp: 0,
          bankDetails: 'RIB: 123 456 789 012345678901 23',
          legalMentions: 'RC: 23/00-0123456 B23 - NIF: 123456789012345 - AI: 23123456789'
        };
      case 'simplified-invoice':
        return {
          ...baseData,
          number: generateDocumentNumber('simplified-invoice'),
          date: new Date().toISOString().split('T')[0],
          client: '',
          clientAddress: '',
          dueDate: calculateDateWithDelay('invoice', 0),
          paymentTerms: defaultPaymentTerms,
          paymentMethod: '',
          paymentConditions: '',
          issuedBy: '',
          salesPerson: '',
          priority: '',
          invoiceType: 'simplified',
          zatcaInvoiceSubtype: 'simplified',
          zatcaProfile: 'b2c',
          orderReference: '',
          deliveryReference: '',
          projectNumber: '',
          customerOrderReference: '',
          deliveryLocation: '',
          deliveryDate: '',
          showNotes: true,
          showConditions: true,
          showObjectif: false,
          showObservations: false,
          showGaranties: false,
          showValidite: false,
          showMotif: false,
          ...getTaxDefaults('invoice'),
          showEcheancier: false,
          showGuarantee: false,
          showStamp: false,
          tapRate: 0,
          items: [{ product: '', quantity: 1, price: 0, total: 0 }],
          subtotalHT: 0,
          tva19: 0,
          totalTTC: 0,
          amountInWords: '',
        };
      case 'quote-request':
        return {
          ...baseData,  // — Spread base defaults first
          number: generateDocumentNumber('quote-request'),
          date: new Date().toISOString().split('T')[0],
          supplier: '',
          supplierAddress: '',
          supplierContact: '',
          requestedBy: '',
          department: '',
          priority: '',
          responseDate: calculateDateWithDelay('quoteRequest', 15),
          requestType: '',
          requestPriority: '',
          approximateBudget: 0,
          deliveryLocation: '',
          desiredStartDate: new Date().toISOString().split('T')[0],
          desiredDeliveryTime: settings?.configuration?.deliveryTimeframes?.quoteRequest || '',
          preferredPaymentTerms: '',
          selectionCriteria: 'prix_qualite',
          supplierCount: 3,
          requiresOnSiteVisit: false,
          requiresCertifications: false,
          requiresWarranty: false,
          specifications: [{ description: '', quantity: 1, requirements: '', budget: 0 }],
          technicalRequirements: '',
          expectedDelivery: '',
          budgetRange: '',
          certifications: '',
          evaluationCriteria: formLabel('form_defaults.evaluation_criteria'),
          ...getTaxDefaults('quote-request'),
          // ?? LOGICAL CONFIG: Demande de devis (1) - Only common sections pre-checked
          showNotes: true,
          showConditions: true,
          showObjectif: true,
          showObservations: false,
          showGaranties: false,
          showValidite: false,
          showMotif: false,
        };
      case 'quote':
        return {
          ...baseData,  // — Spread base defaults first
          number: generateDocumentNumber('quote'),
          date: new Date().toISOString().split('T')[0],
          // Champs alignés sur le formulaire (évitent l'écart UI vs formData : valeurs affichées sans onChange)
          validUntil: calculateDateWithDelay('quote', 15),
          issuedBy: '',
          salesPerson: '',
            priority: '',
          projectType: '',
          projectReference: '',
          deliveryTime: settings?.configuration?.deliveryTimeframes?.quoteRealizationDeadline || '',
          estimatedDelivery: settings?.configuration?.deliveryTimeframes?.quoteRealizationDeadline || '',
          deliveryDelay: settings?.configuration?.deliveryTimeframes?.quoteDeliveryDelay || '',
          deliveryLocation: '',
            paymentMethod: '',
          paymentTerms: defaultPaymentTerms,
          // Flattened quote fields
          quoteType: 'produits',
          quoteSector: 'industrie',
          quoteValidUntil: '',
          quoteDeliveryTime: '',
          quoteSalesperson: '',
          quoteSource: 'appel_entrant',
          quotePriority: '',
          quoteProbability: '50%',
          quotePaymentTerms: '',
          quotePaymentMethod: '',
          quoteDiscount: '',
          quoteWarranty: formLabel('form_defaults.quote_warranty'),
          quoteStatus: '',
          quoteTransportIncluded: false,
          quoteInstallationIncluded: false,
          quoteFormationIncluded: false,
          quoteDescription: '',
          quoteAdvantages: '',
          items: [{ product: '', quantity: 1, price: 0, total: 0 }],
          showGuarantee: false,
          showStamp: false,
          // ?? LOGICAL CONFIG: Devis (7) - Only common sections pre-checked
          showNotes: true,
          showConditions: true,
          showObjectif: true,
          showObservations: false,
          showGaranties: false,
          showValidite: true,
          showMotif: false,
          ...getTaxDefaults('quote'),
        };
      case 'purchase-order':
        return {
          ...baseData,  // — Spread base defaults first
          number: generateDocumentNumber('purchase-order'),
          date: new Date().toISOString().split('T')[0],
          deliveryDate: calculateDateWithDelay('purchaseOrder', 7),  // ?? Auto-select configured delay for "Date de livraison souhaitée"
          deliveryTimeframe: settings?.configuration?.deliveryTimeframes?.purchaseOrder || '',
          // Flattened order fields
          orderType: 'produits',
          orderDeliveryDate: calculateDateWithDelay('purchaseOrder', 7),
          orderDeliveryLocation: '',
          orderSupplier: '',
          orderSupplierContact: '',
          orderSupplierRef: '',
          orderRequester: '',
          orderDepartment: '',
          orderProject: '',
          orderCostCenter: '',
          orderBudget: 0,
          orderPaymentTerms: defaultOrderPaymentTerms,
          orderStatus: '',
          orderApprovedBy: '',
          orderTransportIncluded: false,
          orderInstallationRequired: false,
          orderWarrantyCovered: false,
          orderJustification: '',
          orderSpecifications: '',
          items: [{ product: '', quantity: 1, price: 0, total: 0 }],
          showGuarantee: false,
          showStamp: false,
          terms: '',
          approvedBy: '',
          deliveryAddress: '',
          // ?? LOGICAL CONFIG: Bon de commande (3) - Only common sections pre-checked
          showNotes: true,
          showConditions: true,
          showObjectif: false,
          showObservations: true,
          showGaranties: false,
          showValidite: false,
          showMotif: false,
          showEcheancier: true,
          ...getTaxDefaults('purchase-order'),
        };
      case 'delivery-note':
      case 'delivery':
        return {
          ...baseData,  // — Spread base defaults first
          number: generateDocumentNumber('delivery-note'),
          date: new Date().toISOString().split('T')[0],
          priority: '',
          issuedBy: '',
          // References
          quoteReference: '',
            orderReference: '',
          invoiceReference: '',
          customerOrderReference: '',
          // Flattened sender fields
          senderName: '',
          senderAddress: '',
          // Flattened recipient fields
          recipientName: '',
          recipientAddress: '',
          // Origin & Destination
          departureWarehouse: '',
          deliveryAddress: '',
          // Flattened delivery fields
          deliveryOrderReference: '',
          deliveryDate: calculateDateWithDelay('delivery', 0), // ?? Auto-select configured delay for "Date de livraison prévue"
          shipment: {  // ?? Auto-select today for "Date de livraison prévue" in material-exit
            expectedDeliveryDate: new Date().toISOString().split('T')[0]
          },
          deliveryTime: '',
          deliveryMethod: '',
          transporterName: '',
          trackingNumber: '',
          carrierTrackingNumber: '',
          shipmentPackageCount: 0,
          deliveryCarrier: '',
          deliveryDriver: '',
          deliveryVehicleRegistration: '',
          deliveryVehicleType: 'camion',
          deliveryConditions: 'franco',
          deliveryStatus: '',
          deliveryReceivedBy: '',
          deliverySignatureDate: new Date().toISOString().split('T')[0],
          deliveryFragile: false,
          deliveryRefrigerated: false,
          deliveryHazardous: false,
          deliveryObservations: '',
          items: [{ reference: '', designation: '', quantityOrdered: 0, quantityDelivered: 0, unit: '', condition: 'bon' }],
          ...getTaxDefaults('delivery-note'),
          deliveryNotes: '',
          showNotes: true,
          showConditions: false,
          showObjectif: false,
          showObservations: true,
          showGaranties: false,
          showValidite: false,
          showMotif: false,
          showInstructions: true
        };
      case 'shipping-slip':
      case 'shipping':
        return {
          ...baseData,  // — Spread base defaults first
          number: generateDocumentNumber('shipping-slip'),
          date: new Date().toISOString().split('T')[0],
          priority: '',
          issuedBy: '',
          // Flattened sender fields
          senderName: '',
          senderAddress: '',
          // Flattened recipient fields
          recipientName: '',
          recipientAddress: '',
          // Flattened shipment fields
          shipmentDate: new Date().toISOString().split('T')[0],
          shipmentOriginWarehouse: '',
          deliveryLocation: '',
          shipmentMethod: '',
          shipmentCarrier: '',
          shipmentCarrierTrackingNumber: '',
          shipmentTrackingNumber: '',
          shipmentExpectedDeliveryDate: new Date().toISOString().split('T')[0],
          shipmentPackageType: '',
          shipmentProductType: '',
          shipmentPackageCount: '',
          shipmentTotalWeight: '',
          shipmentVolume: '',
          shipmentDeclaredValue: '',
          shipmentClientOrderRef: '',
          shipmentInvoiceRef: '',
          shipmentDeliveryNoteRef: '',
          shipmentExitNoteRef: '',
          shipmentPriority: '',
          shipmentInsurance: '',
          shipmentSignatureRequired: false,
          shipmentFragile: false,
          shipmentReturnable: false,
          shipmentInstructions: '',
          // ?? Nested object for form fields
          shipment: {
            expectedDeliveryDate: new Date().toISOString().split('T')[0]
          },
          showGuarantee: false,
          showStamp: false,
          items: [{ reference: '', designation: '', quantity: '', unit: '', weight: 0, value: 0 }],
          // ?? LOGICAL CONFIG: Bordereau d'envoi (11) - Only common sections pre-checked
          showNotes: true,
          showConditions: false,
          showObjectif: false,
          showObservations: true,
          showGaranties: false,
          showValidite: false,
          showMotif: false,
          showInstructions: true,
          ...getTaxDefaults('shipping-slip'),
        };
      case 'reception-note':
        return {
          ...baseData,  // — Spread base defaults first
          number: generateDocumentNumber('reception-note'),
          date: new Date().toISOString().split('T')[0],
          priority: '',
          issuedBy: '',
          expectedReceptionDate: new Date().toISOString().split('T')[0],
          // Flattened receiver fields
          receiverName: '',
          receiverAddress: '',
          // Flattened supplier fields
          supplierName: '',
          // Flattened reception fields
          receptionDeliveryNoteRef: '',
          receptionOrderRef: '',
          receptionDate: new Date().toISOString().split('T')[0],
          receptionTime: '',
          receptionControlledBy: '',
          receptionCarrier: '',
          receptionWarehouse: '',
          receptionOverallCondition: '',
          receptionQuantityCompliance: '',
          receptionQualityCompliance: '',
          receptionStockDestination: '',
          receptionStatus: '',
          receptionDocumentsComplete: false,
          receptionPackagingIntact: false,
          receptionUrgentProcessing: false,
          receptionObservations: '',
          items: [{ reference: '', designation: '', quantity: 1, price: 0, quantityOrdered: 0, quantityReceived: 0, unit: '', condition: 'conforme', defects: '', location: '', total: 0 }],
          // ?? LOGICAL CONFIG: Bon de réception (4) - Notes, Observations ONLY
          showNotes: true,
          showConditions: false,
          showObjectif: false,
          showObservations: true,
          showGaranties: false,
          showValidite: false,
          showMotif: false,
          showRecuePar: true,
          ...getTaxDefaults('reception-note'),
        };
      case 'credit-note':
      case 'credit_note':
        return {
          ...baseData,  // — Spread base defaults first
          number: generateDocumentNumber('credit-note'),
          date: new Date().toISOString().split('T')[0],
          priority: '',
          issuedBy: '',
          // Flattened issuer fields
          issuerName: '',
          issuerNif: '',
          issuerAddress: '',
          // Flattened client fields
          clientName: '',
          clientAddress: '',
          clientNif: '',
          supplier: '',
          supplierName: '',
          creditNoteParty: 'client',
          // Flattened originalInvoice fields
          originalInvoiceNumber: '',
          originalInvoiceDate: '',
          originalInvoiceAmount: 0,
          originalInvoice: {
            number: '',
            date: '',
            amount: 0
          },
          // Flattened creditNote fields
          creditNoteApplicationDate: '',
          creditNoteType: '',
          creditNoteAmount: 0,
          creditNoteVatRate: 19,
          creditNoteVatAmount: 0,
          creditNoteTotalAmount: 0,
          creditNoteRefundMethod: '',
          creditNoteRefundDelay: '15_jours',
          creditNoteStatus: '',
          creditNoteAffectsStock: false,
          creditNoteTaxDeductible: false,
          creditNoteClientNotified: false,
          creditNoteReason: '',
          creditNoteRefundConditions: '',
          creditNoteApprovedBy: '',
          // ?? Nested object for form fields
          creditNote: {
            applicationDate: new Date().toISOString().split('T')[0],
            type: ''
          },
          // ?? LOGICAL CONFIG: Note de crédit (14) - Only common sections pre-checked
          showNotes: true,
          showConditions: false,
          showObjectif: false,
          showObservations: false,
          showGaranties: false,
          showValidite: false,
          showMotif: true,
          ...getTaxDefaults('credit-note'),
        };
      case 'debit-note':
      case 'debit_note':
        return {
          ...baseData,
          number: generateDocumentNumber('debit-note'),
          date: new Date().toISOString().split('T')[0],
          priority: '',
          issuedBy: '',
          issuerName: '',
          issuerAddress: '',
          clientName: '',
          clientAddress: '',
          supplier: '',
          supplierName: '',
          creditNoteParty: 'client',
          debitNote: true,
          zatcaDocumentCode: '383',
          originalInvoiceNumber: '',
          originalInvoiceDate: '',
          originalInvoiceAmount: 0,
          originalInvoice: { number: '', date: '', amount: 0 },
          creditNoteApplicationDate: '',
          creditNoteType: '',
          creditNoteAmount: 0,
          creditNoteVatRate: resolveLiveVatRate(settings, facturationConfig),
          creditNoteVatAmount: 0,
          creditNoteTotalAmount: 0,
          creditNoteReason: '',
          creditNote: {
            applicationDate: new Date().toISOString().split('T')[0],
            type: '',
          },
          showNotes: true,
          showConditions: false,
          showObjectif: false,
          showObservations: false,
          showGaranties: false,
          showValidite: false,
          showMotif: true,
          items: [{ product: '', quantity: 1, price: 0, total: 0 }],
          ...getTaxDefaults('credit-note'),
        };
      case 'account-statement':
        return {
          ...baseData,  // — Spread base defaults first
          number: generateDocumentNumber('account-statement'),
          date: new Date().toISOString().split('T')[0],
          // Flattened company fields
          companyName: '',
          companyNif: '',
          companyAddress: '',
          // Flattened client fields
          clientName: '',
          clientCode: '',
          clientNif: '',
          clientCreditLimit: 0,
          clientPaymentTerms: '30_jours',
          clientAccountStatus: 'actif',
          clientCreditExceeded: false,
          clientPaymentDelay: false,
          clientLegalAction: false,
          clientObservations: '',
          clientRecommendedActions: '',
          // Flattened period fields
          periodStartDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
          periodEndDate: new Date().toISOString().split('T')[0],
          balances: {
            opening: 0,
            totalInvoiced: 0,
            totalPaid: 0,
            totalCredits: 0,
            closing: 0
          },
          aging: {
            overdue: 0,
            days30: 0,
            days60: 0,
            days90Plus: 0
          },
          // ?? Nested objects for form fields
          debt: {
            invoiceDate: new Date().toISOString().split('T')[0],
            dueDate: new Date().toISOString().split('T')[0]
          },
          period: {
            startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
            endDate: new Date().toISOString().split('T')[0]
          }
        };
      case 'advance-invoice':
        return {
          ...baseData,  // — Spread base defaults first
          number: generateDocumentNumber('advance-invoice'),
          date: new Date().toISOString().split('T')[0],
          // Flattened seller fields
          sellerName: '',
          sellerNif: '',
          sellerAddress: '',
          // Flattened buyer fields
          buyerName: '',
          buyerAddress: '',
          buyerNif: '',
          // Flattened advance fields
          advanceContractReference: '',
          advanceContractDate: new Date().toISOString().split('T')[0],
          advanceTotalContractAmount: 0,
          advancePercentage: 30,
          advanceAmount: 0,
          advanceVatRate: 19,
          advanceVatAmount: 0,
          advanceTotalAmount: 0,
          advanceType: 'signature',
          advancePaymentTerm: '',
          advanceDueDate: new Date().toISOString().split('T')[0],
          advancePaymentMethod: 'virement',
          advanceStatus: '',
          advanceExpectedDeliveryDate: new Date().toISOString().split('T')[0],
          advanceDeductibleFromFinal: true,
          advanceRefundableIfCancelled: false,
          advanceGuaranteeRequired: false,
          advancePurpose: '',
          advanceRefundConditions: '',
          // ?? Nested object for form fields
          advance: {
            expectedDeliveryDate: new Date().toISOString().split('T')[0]
          },
        };
      case 'stock-sheet':
        return {
          ...baseData,  // — Spread base defaults first
          number: `FS-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          // Flattened warehouse fields
          warehouseName: '',
          warehouseLocation: '',
          warehouseManager: '',
          // Flattened product fields
          productReference: '',
          productName: '',
          productCategory: 'marchandise',
          productBatchNumber: '',
          productExpirationDate: '',
          // Flattened movement fields
          movementType: '',
          movementQuantity: 0,
          movementUnit: '',
          movementUnitPrice: 0,
          movementTotalValue: 0,
          movementDocumentRef: '',
          movementOperator: '',
          movementApprovedBy: '',
          movementObservations: '',
          // Flattened stock fields
          stockPrevious: 0,
          stockNew: 0,
        };
      case 'payment-voucher':
      case 'payment_slip':
        return {
          ...baseData,  // — Spread base defaults first
          number: generateDocumentNumber('payment-voucher'),
          date: new Date().toISOString().split('T')[0],
          expectedCashingDate: calculateDateWithDelay('paymentVoucher', 0),  // ?? Auto-select configured delay for "Date d'encaissement prévue"
          client: '',
          amount: 0,
          paymentMethod: '',
          checkNumber: '',
          purchaseOrderRef: '',
          supplierCreditNoteRef: '',
          relatedInvoices: '',
          paymentLines: [emptyPaymentVoucherLine('purchase_order')],
          // ?? LOGICAL CONFIG: Bon de versement (6) - Only common sections pre-checked (2/3 available: Notes, Observations, Motif)
          showNotes: true,
          showConditions: false,
          showObjectif: false,
          showObservations: false,
          showGaranties: false,
          showValidite: false,
          showMotif: true
        };
      case 'transfer-note':
      case 'transfer_slip':
        return {
          ...baseData,  // — Spread base defaults first
          number: generateDocumentNumber('transfer-note'),
          date: new Date().toISOString().split('T')[0],
          priority: '',
          issuedBy: '',
          // Flattened transferNote fields
          transferNoteExpectedDate: new Date().toISOString().split('T')[0],
          transferNoteOriginWarehouse: '',
          transferNoteDestinationWarehouse: '',
          transferNoteDocumentReference: '',
          transferNoteTransferRequestRef: '',
          transferNoteRequestedBy: '',
          transferNoteHandler: '',
          transferNoteApprovedBy: '',
          transferNoteStatus: '',
          transferNoteTransportMode: '',
          transferNoteDepartureTime: '',
          transferNoteArrivalTime: '',
          transferNoteTrackingNumber: '',
          items: [{ product: '', quantity: 1, price: 0, total: 0 }],
          // ?? LOGICAL CONFIG: Bon de transfert (16) - Only common sections pre-checked (2/3 available: Notes, Observations, Motif)
          showNotes: true,
          showConditions: false,
          showObjectif: false,
          showObservations: false,
          showGaranties: false,
          showValidite: false,
          showMotif: true
        };
      case 'exit-note':
      case 'exit_slip':
        return {
          ...baseData,  // — Spread base defaults first
          number: generateDocumentNumber('exit-note'),
          date: new Date().toISOString().split('T')[0],
          priority: '',
          issuedBy: '',
          // Flattened exitNote fields
          exitNotePartnerType: 'client',
          exitNoteClientOrSupplier: '',
          exitNoteExpectedDate: new Date().toISOString().split('T')[0],
          exitNoteOriginWarehouse: '',
          exitNoteHandler: '',
          exitNoteAuthorizedBy: '',
          exitNoteStatus: '',
          exitNoteClientOrderRef: '',
          exitNoteInvoiceRef: '',
          exitNoteDeliveryNoteRef: '',
          exitNoteTransferNoteRef: '',
          exitNoteDestination: '',
          exitNoteDestinationWarehouse: '',
          exitNoteTransportMode: '',
          exitNoteExitTime: '',
          exitNoteDocumentReference: '',
          exitNoteRecipient: '',
          exitNoteMotif: '',
          exitNoteWarehouse: '',
          exitNoteWarehouseCode: '',
          exitNoteWarehouseManager: '',
          exitNoteWarehouseAddress: '',
          exitNoteOtherResponsible: '',
          exitNoteOtherRef: '',
          exitNoteOtherBeneficiary: '',
          exitNoteOtherDescription: '',
          exitNoteOtherDateConstat: '',
          exitNoteOtherLocation: '',
          items: [{ product: '', quantity: 1, price: 0, total: 0 }],
          // ?? LOGICAL CONFIG: Bon de sortie (15) - Only common sections pre-checked (2/3 available: Notes, Observations, Motif)
          showNotes: true,
          showConditions: false,
          showObjectif: false,
          showObservations: false,
          showGaranties: false,
          showValidite: false,
          showMotif: true
        };
      case 'stock-entry':
        return {
          ...baseData,  // — Spread base defaults first
          number: generateDocumentNumber('stock-entry'),
          date: new Date().toISOString().split('T')[0],
          priority: '',
          issuedBy: '',
          client: '',
          expectedEntryDate: new Date().toISOString().split('T')[0],
          // Flattened stockEntry fields
          stockEntryEntryDate: new Date().toISOString().split('T')[0],
          stockEntryEntryTime: new Date().toLocaleTimeString(getBrandLocale(i18n.language), { hour: '2-digit', minute: '2-digit' }),
          stockEntryWarehouse: '',
          stockEntrySource: '', // supplier, production, transfer
          stockEntryDocumentReference: '',
          stockEntrySupplier: '',
          stockEntryReceivedBy: '',
          stockEntryControlledBy: '',
          stockEntryCondition: '', // good, damaged, partial
          stockEntryObservations: '',
          stockEntryPurchaseOrderRef: '',
          stockEntryReceptionNoteRef: '',
          stockEntryTransferNoteRef: '',
          items: [{ product: '', quantity: 1, price: 0, total: 0 }],
          // ?? LOGICAL CONFIG: Bon d'entrée stock (17) - Only common sections pre-checked
          showNotes: true,
          showConditions: false,
          showObjectif: false,
          showObservations: true,
          showGaranties: false,
          showValidite: false,
          showMotif: false
        };
      case 'reception-report':
        return {
          ...baseData,  // — Spread base defaults first
          number: `PVR-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          // Flattened projectDetails fields
          projectDetailsName: '',
          projectDetailsReference: '',
          projectDetailsContractNumber: '',
          projectDetailsContractDate: '',
          projectDetailsContractAmount: 0,
          projectDetailsClient: '',
          projectDetailsContractor: '',
          projectDetailsArchitect: '',
          projectDetailsControlOffice: '',
          // Flattened workLocation fields
          workLocationAddress: '',
          workLocationWilaya: '',
          workLocationCommune: '',
          workLocationCoordinates: '',
          workLocationAccessConditions: '',
          // Flattened workDescription fields
          workDescriptionCategory: 'construction',
          workDescriptionNature: '',
          workDescriptionSpecifications: '',
          workDescriptionTechnicalStandards: ld('technical_standards_value'),
          workDescriptionSpecialRequirements: '',
          // Flattened timeSchedule fields
          timeSchedulePlannedStartDate: '',
          timeScheduleActualStartDate: '',
          timeSchedulePlannedCompletionDate: '',
          timeScheduleActualCompletionDate: '',
          timeSchedulePlannedDuration: '',
          timeScheduleActualDuration: '',
          timeScheduleDelays: '',
          timeScheduleDelayJustification: '',
          workItems: [{ description: '', unit: '', plannedQuantity: 0, executedQuantity: 0, conformity: 'conforme', observations: '' }],
          // Flattened qualityControl fields
          qualityControlMaterialTests: [{ material: '', standard: '', result: 'conforme', testDate: '', laboratory: '' }],
          qualityControlDimensionalChecks: [{ element: '', tolerance: '', measured: '', conformity: 'conforme' }],
          qualityControlFinishQuality: 'satisfaisante',
          qualityControlOverallAssessment: 'conforme',
          // Flattened receptionCommittee fields
          receptionCommitteeClientRepresentative: '',
          receptionCommitteeContractorRepresentative: '',
          receptionCommitteeArchitectRepresentative: '',
          receptionCommitteeControlOfficeRepresentative: '',
          receptionCommitteeOtherParticipants: '',
          // Flattened findings fields
          findingsWorkAccepted: true,
          findingsReservations: [{ item: '', description: '', severity: 'mineure', deadline: '' }],
          findingsDefectsFound: [{ location: '', defect: '', cause: '', remedy: '', responsible: '' }],
          findingsCorrectiveActions: [{ action: '', responsible: '', deadline: '', cost: 0 }],
          findingsQualityGrade: 'satisfaisant',
          // Flattened warranties fields
          warrantiesWarrantyPeriod: '12 mois',
          warrantiesWarrantyStart: '',
          warrantiesCoverageDetails: '',
          warrantiesMaintenanceTerms: '',
          warrantiesExclusions: '',
          // Flattened financialAspects fields
          financialAspectsFinalPaymentDue: 0,
          financialAspectsRetentionAmount: 0,
          financialAspectsRetentionPercentage: 5,
          financialAspectsRetentionReleaseDate: '',
          financialAspectsPenaltiesApplied: 0,
          financialAspectsBonusesAwarded: 0,
          receptionDate: new Date().toISOString().split('T')[0],
          provisionalReception: false,
          definitivereception: false,
          nextInspectionDate: '',
          attachments: formLabel('form_defaults.attachments_reception')
        };
      case 'intervention-report':
        return {
          ...baseData,  // — Spread base defaults first
          number: `RI-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          // Flattened interventionDetails fields
          interventionDetailsType: 'maintenance',
          interventionDetailsCategory: 'corrective',
          interventionDetailsPriority: '',
          interventionDetailsUrgency: '',
          interventionDetailsOrigin: 'demande_client',
          interventionDetailsRelatedTicket: '',
          interventionDetailsContractReference: '',
          // Flattened client fields
          clientName: '',
          clientSite: '',
          clientAddress: '',
          clientContact: '',
          clientPhone: '',
          clientEmail: '',
          // Flattened requestInfo fields
          requestInfoRequestedBy: '',
          requestInfoRequestDate: '',
          requestInfoRequestTime: '',
          requestInfoProblemDescription: '',
          requestInfoInitialDiagnosis: '',
          requestInfoExpectedDuration: '',
          // Flattened team fields
          teamLeadTechnician: '',
          teamAssistantTechnicians: '',
          teamSupervisor: '',
          teamExternalExperts: '',
          teamTotalTeamSize: 1,
          // Flattened schedule fields
          schedulePlannedStartDate: '',
          schedulePlannedStartTime: '',
          scheduleActualStartDate: '',
          scheduleActualStartTime: '',
          scheduleActualEndDate: '',
          scheduleActualEndTime: '',
          scheduleTotalDuration: '',
          scheduleTravelTime: '',
          scheduleWorkTime: '',
          // Flattened equipment fields
          equipmentEquipmentId: '',
          equipmentEquipmentType: '',
          equipmentManufacturer: '',
          equipmentModel: '',
          equipmentSerialNumber: '',
          equipmentLocation: '',
          equipmentOperatingHours: 0,
          equipmentLastMaintenanceDate: '',
          // Flattened diagnostic fields
          diagnosticProblemAnalysis: '',
          diagnosticRootCause: '',
          diagnosticFailureMode: '',
          diagnosticAffectedComponents: '',
          diagnosticSeverity: 'moyenne',
          diagnosticImpact: 'local',
          workPerformed: [{ task: '', duration: '', technician: '', toolsUsed: '', result: '' }],
          partsAndMaterials: [{ reference: '', designation: '', quantity: 0, unitPrice: 0, supplier: '', warranty: '' }],
          // Flattened testAndValidation fields
          testAndValidationFunctionalTests: [{ test: '', procedure: '', result: 'conforme', observations: '' }],
          testAndValidationPerformanceMetrics: [{ metric: '', expected: '', measured: '', conformity: 'conforme' }],
          testAndValidationSafetyChecks: [{ check: '', result: 'ok', certifiedBy: '' }],
          testAndValidationOverallValidation: 'satisfaisant',
          // Flattened followUp fields
          followUpFollowUpRequired: false,
          followUpNextMaintenanceDate: '',
          followUpRecommendedActions: [{ action: '', priority: '', deadline: '', responsible: '' }],
          followUpSparePartsToOrder: '',
          followUpTrainingNeeds: '',
          // Flattened clientFeedback fields
          clientFeedbackSatisfaction: 'satisfait',
          clientFeedbackComments: '',
          clientFeedbackSuggestions: '',
          clientFeedbackAdditionalNeeds: '',
          // Flattened documentation fields
          documentationPhotosBeforeAfter: false,
          documentationTechnicalDrawings: false,
          documentationTestReports: false,
          documentationWarrantyDocuments: false,
          documentationUserManualUpdated: false,
          // Flattened signatures fields
          signaturesClientSignature: '',
          signaturesClientDate: '',
          signaturesTechnicianSignature: '',
          signaturesTechnicianDate: '',
          signaturesSupervisorApproval: '',
          signaturesSupervisorDate: '',
          // Flattened financials fields
          financialsLaborCost: 0,
          financialsMaterialsCost: 0,
          financialsTravelCost: 0,
          financialsTotalCost: 0,
          financialsBillingStatus: '',
        };
      case 'non-compliance':
        return {
          ...baseData,  // — Spread base defaults first
          number: `FNC-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          // Flattened identification fields
          identificationDetectedBy: '',
          identificationDetectorRole: '',
          identificationDetectionDate: new Date().toISOString().split('T')[0],
          identificationDetectionMethod: 'inspection',
          identificationDetectionContext: '',
          identificationWitnessNames: '',
          // Flattened projectInfo fields
          projectInfoClient: '',
          projectInfoProjectReference: '',
          projectInfoContractNumber: '',
          projectInfoPhase: '',
          projectInfoLocation: '',
          projectInfoResponsibleEngineer: '',
          // Flattened nonComplianceDetails fields
          nonComplianceDetailsType: '',
          nonComplianceDetailsCategory: '',
          nonComplianceDetailsSeverity: 'moyenne',
          nonComplianceDetailsTitle: '',
          nonComplianceDetailsDescription: '',
          nonComplianceDetailsSpecificLocation: '',
          nonComplianceDetailsAffectedQuantity: '',
          nonComplianceDetailsPhotosAvailable: false,
          // Flattened standards fields
          standardsStandardViolated: '',
          standardsRequirementClause: '',
          standardsSpecificationReference: '',
          standardsToleranceExceeded: '',
          standardsDeviationMeasurement: '',
          // Flattened impact fields
          impactSafetyRisk: 'faible',
          impactQualityImpact: 'mineur',
          impactScheduleImpact: 'aucun',
          impactCostImpact: 0,
          impactClientImpact: '',
          impactPotentialConsequences: '',
          // Flattened analysis fields
          analysisRootCause: '',
          analysisContributingFactors: [{ factor: '', influence: 'faible', description: '' }],
          analysisSystematicIssue: false,
          analysisSimilarOccurrences: '',
          analysisInvestigationReport: '',
          // Flattened actions fields
          actionsImmediateActions: [{ action: '', responsible: '', completedDate: '', effectiveness: '' }],
          actionsCorrectiveActions: [{ action: '', responsible: '', targetDate: '', cost: 0, status: '' }],
          actionsPreventiveActions: [{ action: '', scope: '', responsible: '', implementation: '' }],
          actionsVerificationMethod: '',
          // Flattened responsibilities fields
          responsibilitiesActionOwner: '',
          responsibilitiesQualityManager: '',
          responsibilitiesProjectManager: '',
          responsibilitiesExternalExperts: '',
          responsibilitiesApprovalRequired: false,
          // Flattened timeline fields
          timelineTargetCompletionDate: '',
          timelineActualCompletionDate: '',
          timelineVerificationDate: '',
          timelineFollowUpDates: [{ date: '', milestone: '', responsible: '' }],
          timelineEscalationDate: '',
          // Flattened monitoring fields
          monitoringStatus: 'ouverte',
          monitoringProgressComments: '',
          monitoringLastUpdateDate: '',
          monitoringUpdatedBy: '',
          monitoringNextReviewDate: '',
          // Flattened closure fields
          closureClosureDate: '',
          closureVerifiedBy: '',
          closureVerificationEvidence: '',
          closureEffectivenessConfirmed: false,
          closureLessonsLearned: '',
          closureSystemUpdates: '',
          // Flattened documentation fields
          documentationAttachments: '',
          documentationReferencedDocuments: '',
          documentationDistributionList: '',
          documentationRetentionPeriod: '5 ans',
        };
      case 'tax-certificate':
        return {
          ...baseData,  // — Spread base defaults first
          number: `AF-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          // Flattened applicant fields
          applicantName: '',
          applicantLegalForm: '',
          applicantNif: '',
          applicantNis: '',
          applicantAi: '',
          applicantCommercialRegister: '',
          applicantRegistrationDate: '',
          applicantCapital: 0,
          applicantAddress: '',
          applicantActivity: '',
          applicantActivityCode: '',
          // Flattened certificateDetails fields
          certificateDetailsType: 'fiscal',
          certificateDetailsPurpose: '',
          certificateDetailsRequestReason: '',
          certificateDetailsUrgencyLevel: '',
          certificateDetailsDeliveryMethod: 'retrait',
          // Flattened taxPeriod fields
          taxPeriodTaxYear: new Date().getFullYear(),
          taxPeriodStartDate: '',
          taxPeriodEndDate: '',
          taxPeriodDeclarationPeriod: 'annuelle',
          taxPeriodLastDeclarationDate: '',
          // Flattened taxCompliance fields
          taxComplianceDeclarationStatus: '',
          taxCompliancePaymentStatus: '',
          taxComplianceLastDeclarationNumber: '',
          taxComplianceDeclarationDate: '',
          taxComplianceTotalTaxesDue: 0,
          taxComplianceTotalTaxesPaid: 0,
          taxComplianceOutstandingAmount: 0,
          taxCompliancePenaltiesOwed: 0,
          // Flattened businessData fields
          businessDataTurnover: 0,
          businessDataTaxableIncome: 0,
          businessDataEmployeeCount: 0,
          businessDataActivitySector: '',
          businessDataExportPercentage: 0,
          businessDataInvestmentZone: '',
          // Flattened taxDetails fields
          taxDetailsIrg: { due: 0, paid: 0, balance: 0 },
          taxDetailsIbs: { due: 0, paid: 0, balance: 0 },
          taxDetailsTva: { due: 0, paid: 0, balance: 0 },
          taxDetailsTap: { due: 0, paid: 0, balance: 0 },
          taxDetailsVf: { due: 0, paid: 0, balance: 0 },
          taxDetailsTof: { due: 0, paid: 0, balance: 0 },
          // Flattened verification fields
          verificationLastInspectionDate: '',
          verificationInspectionResult: 'conforme',
          verificationAdjustments: 0,
          verificationLitigationPending: false,
          verificationAppealInProgress: false,
          // Flattened issuance fields
          issuanceIssuingAuthority: formLabel('form_defaults.issuance_tax_authority'),
          issuanceIssuingOffice: '',
          issuanceAuthorizedBy: '',
          issuancePosition: '',
          issuanceIssueDate: new Date().toISOString().split('T')[0],
          issuanceValidityPeriod: '12 mois',
          issuanceExpiryDate: '',
          issuanceSerialNumber: '',
          // Flattened legalFramework fields
          legalFrameworkLegalBasis: '',
          legalFrameworkApplicableTexts: [{ text: 'Article 150', reference: 'CID' }],
          legalFrameworkConditions: '',
          legalFrameworkObligations: '',
          // Flattened restrictions fields
          restrictionsGeographicalScope: 'territoire national',
          restrictionsActivityLimitations: '',
          restrictionsAmountLimitations: '',
          restrictionsSpecialConditions: '',
          // Flattened authentication fields
          authenticationOfficialSeal: true,
          authenticationDigitalSignature: false,
          authenticationQrCode: '',
          authenticationVerificationCode: '',
          authenticationSecurityFeatures: 'Filigrane + cachet officiel',
        };
      case 'work-certificate':
        return {
          ...baseData,  // — Spread base defaults first
          number: `CTE-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          // Flattened contractor fields
          contractorName: '',
          contractorLegalForm: '',
          contractorNif: '',
          contractorNis: '',
          contractorAddress: '',
          contractorPhone: '',
          contractorEmail: '',
          contractorLegalRepresentative: '',
          contractorTechnicalDirector: '',
          // Flattened client fields
          clientName: '',
          clientAddress: '',
          clientContactPerson: '',
          clientPhone: '',
          clientEmail: '',
          clientProjectManager: '',
          // Flattened contractDetails fields
          contractDetailsReference: '',
          contractDetailsSignatureDate: '',
          contractDetailsStartDate: '',
          contractDetailsPlannedCompletionDate: '',
          contractDetailsActualCompletionDate: '',
          contractDetailsContractAmount: 0,
          contractDetailsFinalAmount: 0,
          contractDetailsAmendments: [{ number: '', date: '', description: '', impact: 0 }],
          // Flattened workDescription fields
          workDescriptionCategory: 'construction',
          workDescriptionType: '',
          workDescriptionNature: '',
          workDescriptionSpecifications: '',
          workDescriptionLocation: '',
          workDescriptionTotalSurface: 0,
          workDescriptionVolumeBuilt: 0,
          workDescriptionNumberOfLevels: 0,
          // Flattened compliance fields
          complianceBuildingPermit: '',
          complianceUrbanismCertificate: '',
          complianceConformityStandards: ld('technical_standards_value'),
          complianceEnvironmentalCompliance: true,
          complianceSafetyStandards: '',
          complianceAccessibilityCompliance: true,
          // Flattened qualityAssessment fields
          qualityAssessmentOverallLevel: 'conforme',
          qualityAssessmentMaterialQuality: 'satisfaisante',
          qualityAssessmentWorkmanshipQuality: 'bonne',
          qualityAssessmentFinishingQuality: 'satisfaisante',
          qualityAssessmentTechnicalPerformance: 'conforme',
          qualityAssessmentDurabilityExpected: '',
          inspections: [{ date: '', inspector: '', scope: '', result: 'conforme', observations: '' }],
          tests: [{ test: '', standard: '', result: 'conforme', date: '', laboratory: '' }],
          // Flattened warranties fields
          warrantiesStructuralWarranty: '10 ans',
          warrantiesEquipmentWarranty: '2 ans',
          warrantiesFinishingWarranty: '1 an',
          warrantiesWarrantyStart: '',
          warrantiesMaintenanceObligations: '',
          // Flattened performance fields
          performanceTimelyCompletion: true,
          performanceBudgetRespected: true,
          performanceQualityTargetsMet: true,
          performanceClientSatisfaction: 'satisfait',
          performanceAdditionalBenefits: '',
          // Flattened references fields
          referencesSimilarProjects: [{ name: '', year: '', client: '', amount: 0, description: '' }],
          referencesTotalExperience: '',
          referencesSpecializations: '',
          referencesCertifications: '',
          // Flattened issuance fields
          issuanceIssuingAuthority: '',
          issuanceAuthorizedBy: '',
          issuancePosition: '',
          issuanceRegistrationNumber: '',
          issuanceIssueDate: new Date().toISOString().split('T')[0],
          issuanceValidityPeriod: 'permanent',
          issuanceUsageScope: '',
        };
      case 'legal-notice':
        return {
          ...baseData,  // — Spread base defaults first
          number: `NL-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          // parties: nested sender/recipient (form still uses formData.parties?.sender|recipient)
          parties: {
            sender: {
              name: '',
              legalForm: '',
              nif: '',
              legalRepresentative: '',
              lawyer: ''
            },
            recipient: {
              name: '',
              address: '',
              legalForm: '',
              identification: '',
              lastKnownAddress: ''
            }
          },
          // Flattened noticeDetails fields
          noticeDetailsType: 'contractuelle',
          noticeDetailsCategory: 'mise_en_demeure',
          noticeDetailsUrgency: '',
          noticeDetailsSubject: '',
          noticeDetailsReference: '',
          noticeDetailsRelatedContract: '',
          // Flattened legalFramework fields
          legalFrameworkLegalBasis: '',
          legalFrameworkApplicableArticles: [{ article: '', law: '', content: '' }],
          legalFrameworkJurisdiction: ld('courts'),
          legalFrameworkApplicableLaw: ld('applicable_law_full'),
          legalFrameworkPrecedents: '',
          // Flattened factualBasis fields
          factualBasisChronology: [{ date: '', event: '', evidence: '' }],
          factualBasisObligations: [{ obligation: '', source: '', status: '' }],
          factualBasisBreaches: [{ breach: '', severity: '', consequences: '' }],
          factualBasisDamages: { type: '', amount: 0, calculation: '' },
          // Flattened demands fields
          demandsPrimaryDemands: [{ demand: '', legal_basis: '', deadline: '' }],
          demandsAlternativeDemands: [{ demand: '', conditions: '' }],
          demandsPenalties: [{ penalty: '', amount: 0, calculation_method: '' }],
          demandsInterests: { rate: 0, calculation_start: '', legal_rate: true },
          // Flattened procedure fields
          procedureFormalNotice: true,
          procedureDeadline: '',
          procedureConsequences: '',
          procedureGracePeriod: 0,
          procedureEscalationSteps: [{ step: '', timeline: '', action: '' }],
          // Flattened attachments fields
          attachmentsContracts: [],
          attachmentsCorrespondence: [],
          attachmentsEvidence: [],
          attachmentsExpertReports: [],
          attachmentsLegalTexts: [],
          // Flattened delivery fields
          deliveryMethod: '',
          deliveryRecipientSignature: false,
          deliveryWitnessRequired: false,
          deliveryBailiffService: false,
          deliveryElectronicDelivery: false,
          deliveryDeliveryDate: '',
          deliveryDeliveryProof: '',
          // Flattened followUp fields
          followUpResponseRequired: true,
          followUpResponseDeadline: '',
          followUpNextActions: '',
          followUpLegalProceedingsDate: '',
          followUpAlternativeResolution: '',
          // Flattened authentication fields
          authenticationNotaryRequired: false,
          authenticationLegalRepresentation: '',
          authenticationOfficialStamp: true,
          authenticationRegistrationRequired: false,
          authenticationArchivingPeriod: '10 ans',
        };
      case 'old-certificate':
        return {
          ...baseData,  // — Spread base defaults first
          number: `CTE-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          supervisionBy: '',
          receivedBy: '',
          warrantyProvided: true,
          warrantyPeriod: '12 mois',
          certificationBody: '',
          technicalApproval: '',
          qualityControl: '',
          safetyCompliance: true,
          environmentalCompliance: true,
          finalInspection: true,
          clientSatisfaction: 'satisfait'
        };
      case 'payslip':
        return {
          ...baseData,  // — Spread base defaults first
          number: `FP-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          // Flattened employer fields
          employerName: '',
          employerAddress: '',
          employerNif: '',
          employerNis: '',
          employerCnas: '',
          employerActivity: '',
          employerConventionCollective: '',
          // Flattened employee fields
          employeeName: '',
          employeeFirstName: '',
          employeeEmployeeId: '',
          employeeNas: '',
          employeePosition: '',
          employeeDepartment: '',
          employeeClassification: '',
          employeeHireDate: '',
          employeeSeniority: '',
          // Flattened payPeriod fields
          payPeriodType: '',
          payPeriodStartDate: '',
          payPeriodEndDate: '',
          payPeriodPaymentDate: '',
          payPeriodWorkingDays: 22,
          payPeriodActualDaysWorked: 22,
          payPeriodAbsenceDays: 0,
          payPeriodSickLeaveDays: 0,
          payPeriodVacationDays: 0,
          // Flattened earnings fields
          earningsBaseSalary: 0,
          earningsSeniorityAllowance: 0,
          earningsTransportAllowance: 0,
          earningsFoodAllowance: 0,
          earningsFamilyAllowance: 0,
          earningsOvertimeHours: 0,
          earningsOvertimeRate: 0,
          earningsOvertimePay: 0,
          earningsNightWorkHours: 0,
          earningsNightWorkPay: 0,
          earningsBonus: 0,
          earningsCommission: 0,
          earningsTotalEarnings: 0,
          // Flattened deductions fields
          deductionsSocialSecurity: { rate: 9, amount: 0 },
          deductionsUnemployment: { rate: 1.5, amount: 0 },
          deductionsRetirement: { rate: 7, amount: 0 },
          deductionsIncomeTax: { rate: 0, amount: 0, exemption: 0 },
          deductionsHealthInsurance: { rate: 1.5, amount: 0 },
          deductionsAdvances: 0,
          deductionsLoans: 0,
          deductionsOther: 0,
          deductionsTotalDeductions: 0,
          // Flattened employerContributions fields
          employerContributionsSocialSecurity: { rate: 26, amount: 0 },
          employerContributionsAccidents: { rate: 1.25, amount: 0 },
          employerContributionsUnemployment: { rate: 0.5, amount: 0 },
          employerContributionsFormation: { rate: 1.5, amount: 0 },
          employerContributionsTotalEmployerContributions: 0,
          // Flattened netCalculation fields
          netCalculationGrossSalary: 0,
          netCalculationTaxableIncome: 0,
          netCalculationNetBeforeTax: 0,
          netCalculationNetSalary: 0,
          netCalculationTakeHome: 0,
          // Flattened paymentDetails fields
          paymentDetailsBankName: '',
          paymentDetailsAccountNumber: '',
          paymentDetailsPaymentMethod: 'virement',
          paymentDetailsPaymentDate: '',
          paymentDetailsCheckNumber: '',
          // Flattened legalMentions fields
          legalMentionsCnas: formLabel('form_defaults.legal_mentions_cnas'),
          legalMentionsRetirement: formLabel('form_defaults.legal_mentions_retirement'),
          legalMentionsTaxes: formLabel('form_defaults.legal_mentions_taxes'),
          legalMentionsSocialLaws: ld('labor_compliance'),
        };
      case 'work-contract':
        return {
          ...baseData,  // — Spread base defaults first
          number: `CT-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          // Flattened employer fields
          employerName: '',
          employerLegalForm: '',
          employerAddress: '',
          employerNif: '',
          employerNis: '',
          employerActivity: '',
          employerLegalRepresentative: '',
          employerPosition: '',
          // Flattened employee fields
          employeeName: '',
          employeeFirstName: '',
          employeeBirthDate: '',
          employeeBirthPlace: '',
          employeeNationality: ld('nationality'),
          employeeAddress: '',
          employeePhone: '',
          employeeEmail: '',
          employeeMaritalStatus: '',
          employeeChildren: 0,
          employeeEducation: '',
          employeeExperience: '',
          // Flattened contractDetails fields
          contractDetailsType: 'CDI',
          contractDetailsCategory: 'cadre',
          contractDetailsDuration: '',
          contractDetailsRenewalConditions: '',
          contractDetailsTransformation: false,
          contractDetailsPreviousContract: '',
          contractDetailsCollectiveAgreement: '',
          // Flattened position fields
          positionTitle: '',
          positionDepartment: '',
          positionHierarchicalSuperior: '',
          positionTeamSize: 0,
          positionClassification: '',
          positionCoefficient: 0,
          positionWorkLocation: '',
          // Flattened duration fields
          durationStartDate: '',
          durationEndDate: '',
          durationAutomaticRenewal: false,
          durationNoticePeriod: '1 mois',
          durationTrialPeriod: '3 mois',
          durationRenewalTrialPeriod: '1 mois',
          // Flattened remuneration fields
          remunerationBaseSalary: 0,
          remunerationThirteenthMonth: true,
          remunerationPerformanceBonus: false,
          remunerationTransportAllowance: 0,
          remunerationMealAllowance: 0,
          remunerationHousing: false,
          remunerationCar: false,
          remunerationPhone: false,
          remunerationOtherBenefits: '',
          // Flattened workingTime fields
          workingTimeWeeklyHours: 40,
          workingTimeSchedule: '08h00-17h00',
          workingTimeOvertime: 'compensated',
          workingTimeNightWork: false,
          workingTimeShiftWork: false,
          workingTimeFlexibleSchedule: false,
          workingTimeTeleworking: false,
          // Flattened leavePolitics fields
          leavePoliticsAnnualLeave: 30,
          leavePoliticsSickLeave: 'statutory',
          leavePoliticsMaternityLeave: '14 weeks',
          leavePoliticsPaternityLeave: '3 days',
          leavePoliticsTraining: '5 days/year',
          leavePoliticsExceptional: 'case by case',
          // Flattened responsibilities fields
          responsibilitiesMainDuties: [{ duty: '', description: '', priority: '' }],
          responsibilitiesObjectives: [{ objective: '', deadline: '', measurement: '' }],
          responsibilitiesAutonomyLevel: '',
          responsibilitiesDecisionMaking: '',
          responsibilitiesConfidentiality: 'required',
          // Flattened obligations fields
          obligationsLoyalty: true,
          obligationsConfidentiality: true,
          obligationsNonCompete: false,
          obligationsNonCompeteDuration: '',
          obligationsNonSolicitation: false,
          obligationsIntellectualProperty: true,
          obligationsExclusivity: true,
          // Flattened termination fields
          terminationTerminationCauses: [{ cause: '', notice: '1 mois' }],
          terminationSeverancePay: 'statutory',
          terminationNonCompeteCompensation: 0,
          terminationReturnOfProperty: 'required',
          terminationFinalSettlement: '30 days',
          // Flattened socialBenefits fields
          socialBenefitsHealthInsurance: true,
          socialBenefitsRetirement: 'CNAS',
          socialBenefitsWorkAccidents: 'covered',
          socialBenefitsTraining: 'annual plan',
          socialBenefitsTransportation: 'allowance',
          socialBenefitsMeals: 'restaurant tickets',
          // Flattened legalCompliance fields
          legalComplianceLaborCode: 'Law 90-11',
          legalComplianceCollectiveAgreement: '',
          legalComplianceInternalRegulations: 'applicable',
          legalComplianceMedicalExam: 'required',
          legalComplianceDeclaration: 'CNAS + CNR',
          // Flattened signatures fields
          signaturesEmployerDate: '',
          signaturesEmployeeDate: '',
          signaturesWitnessName: '',
          signaturesWitnessDate: '',
          signaturesRegistrationDate: '',
        };
      case 'employment-certificate':
        return {
          ...baseData,  // — Spread base defaults first
          number: `AT-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          // Flattened employer fields
          employerName: '',
          employerLegalForm: '',
          employerAddress: '',
          employerNif: '',
          employerNis: '',
          employerActivity: '',
          employerLegalRepresentative: '',
          employerHrManager: '',
          // Flattened employee fields
          employeeName: '',
          employeeFirstName: '',
          employeeEmployeeId: '',
          employeeNas: '',
          employeeBirthDate: '',
          employeeNationality: ld('nationality'),
          employeePosition: '',
          employeeDepartment: '',
          employeeClassification: '',
          // Flattened employment fields
          employmentContractType: 'CDI',
          employmentStartDate: '',
          employmentEndDate: '',
          employmentCurrentStatus: 'en_service',
          employmentTerminationReason: '',
          employmentNotice: '',
          employmentWorkLocation: '',
          // Flattened remuneration fields
          remunerationLastSalary: 0,
          remunerationAverageSalary: 0,
          remunerationCurrency: getBrandDefaultCurrency(),
          remunerationSalaryProgression: [{ period: '', amount: 0 }],
          remunerationBenefits: '',
          remunerationThirteenthMonth: true,
          // Flattened performance fields
          performanceOverallRating: 'satisfaisante',
          performancePunctuality: 'excellente',
          performanceReliability: 'bonne',
          performanceTeamwork: 'satisfaisante',
          performanceInitiative: 'bonne',
          performanceProfessionalDevelopment: '',
          performancePromotions: [{ date: '', from: '', to: '', reason: '' }],
          // Flattened responsibilities fields
          responsibilitiesMainDuties: [{ duty: '', period: '', level: '' }],
          responsibilitiesAchievements: [{ achievement: '', impact: '', year: '' }],
          responsibilitiesProjects: [{ project: '', role: '', duration: '', result: '' }],
          responsibilitiesSupervision: 0,
          responsibilitiesAutonomyLevel: '',
          // Flattened skills fields
          skillsTechnical: [{ skill: '', level: '', certified: false }],
          skillsLanguages: [{ language: '', level: 'courant', certification: '' }],
          skillsSoftware: [{ software: '', level: '' }],
          skillsTraining: [{ training: '', date: '', organization: '', duration: '' }],
          // Flattened conduct fields
          conductDisciplinaryActions: 0,
          conductWarnings: 0,
          conductAbsences: '',
          conductPunctuality: 'exemplaire',
          conductIntegrity: '',
          conductConfidentiality: '',
          // Flattened certification fields
          certificationPurpose: 'administrative',
          certificationRequestedBy: 'employee',
          certificationSpecificUse: '',
          certificationConfidentialityLevel: 'public',
          certificationValidityPeriod: '6 mois',
          certificationReplacementPolicy: 'unique',
          // Flattened legalCompliance fields
          legalComplianceDataProtection: 'conforme RGPD',
          legalComplianceEmploymentLaw: ld('labor_code'),
          legalComplianceReferences: '',
          legalComplianceFutureEmployment: '',
          // Flattened issuance fields
          issuanceIssuedBy: '',
          issuancePosition: '',
          issuanceIssueDate: new Date().toISOString().split('T')[0],
          issuanceSignature: true,
          issuanceOfficialStamp: true,
          issuanceRegistrationNumber: '',
        };
      case 'leave-request':
        return {
          ...baseData,  // — Spread base defaults first
          number: `DCO-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          // Flattened employee fields
          employeeName: '',
          employeeFirstName: '',
          employeeEmployeeId: '',
          employeePosition: '',
          employeeDepartment: '',
          employeeSupervisor: '',
          employeeHireDate: '',
          employeeSeniority: '',
          // Flattened leaveDetails fields
          leaveDetailsType: '',
          leaveDetailsCategory: 'repos',
          leaveDetailsStartDate: '',
          leaveDetailsEndDate: '',
          leaveDetailsTotalDays: 0,
          leaveDetailsWorkingDaysAffected: 0,
          leaveDetailsPartialDays: false,
          leaveDetailsHalfDays: 0,
          // Flattened entitlement fields
          entitlementAnnualEntitlement: 30,
          entitlementUsedDays: 0,
          entitlementRemainingDays: 30,
          entitlementCarriedForward: 0,
          entitlementAnticipatedDays: 0,
          entitlementCompensationDays: 0,
          // Flattened justification fields
          justificationReason: '',
          justificationUrgency: '',
          justificationMedicalCertificate: false,
          justificationSupportingDocuments: [],
          justificationFamilyCircumstances: '',
          justificationPersonalReasons: '',
          // Flattened workCoverage fields
          workCoverageTemporary: '',
          workCoverageHandoverCompleted: false,
          workCoverageHandoverDate: '',
          workCoverageCriticalTasks: [{ task: '', delegate: '', instructions: '' }],
          workCoverageClientNotifications: false,
          workCoverageProjectImpact: 'aucun',
          // Flattened emergencyContacts fields
          emergencyContactsPrimaryContact: '',
          emergencyContactsPrimaryPhone: '',
          emergencyContactsAlternativeContact: '',
          emergencyContactsAlternativePhone: '',
          emergencyContactsAvailability: '',
          // Flattened approval fields
          approvalRequestDate: new Date().toISOString().split('T')[0],
          approvalSupervisorApproval: false,
          approvalSupervisorDate: '',
          approvalSupervisorComments: '',
          approvalHrApproval: false,
          approvalHrDate: '',
          approvalHrComments: '',
          approvalFinalApproval: false,
          // Flattened administration fields
          administrationPayrollImpact: 'normal',
          administrationBenefitsImpact: 'maintained',
          administrationReturnDate: '',
          administrationMedicalClearance: false,
          administrationAttendanceUpdate: false,
          administrationCalendarBlocked: false,
          // Flattened conditions fields
          conditionsModificationAllowed: true,
          conditionsCancellationPossible: true,
          conditionsPartialReturn: false,
          conditionsExtensionConditions: '',
          conditionsEarlyReturnProcedure: '',
        };
      case 'production-sheet':
        return {
          ...baseData,  // — Spread base defaults first
          number: `FPR-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          // Flattened productInfo fields
          productInfoName: '',
          productInfoCode: '',
          productInfoRevision: '',
          productInfoCategory: 'produit_fini',
          productInfoSpecification: '',
          productInfoCustomerCode: '',
          productInfoInternalReference: '',
          // Flattened productionOrder fields
          productionOrderNumber: '',
          productionOrderIssueDate: '',
          productionOrderRequestedBy: '',
          productionOrderPriority: '',
          productionOrderDueDate: '',
          productionOrderTargetQuantity: 0,
          productionOrderActualQuantity: 0,
          productionOrderRemainingQuantity: 0,
          // Flattened batchInfo fields
          batchInfoBatchNumber: '',
          batchInfoLotNumber: '',
          batchInfoBatchSize: 0,
          batchInfoExpiryDate: '',
          batchInfoStorageConditions: '',
          batchInfoTraceabilityCode: '',
          // Flattened schedule fields
          schedulePlannedStartDate: '',
          schedulePlannedStartTime: '',
          scheduleActualStartDate: '',
          scheduleActualStartTime: '',
          schedulePlannedEndDate: '',
          schedulePlannedEndTime: '',
          scheduleActualEndDate: '',
          scheduleActualEndTime: '',
          scheduleTotalDuration: '',
          scheduleBreakTime: '',
          scheduleEffectiveTime: '',
          // Flattened team fields
          teamOperator: '',
          teamSupervisor: '',
          teamQualityController: '',
          teamTeamSize: 1,
          teamShiftType: 'jour',
          teamExperience: '',
          // Flattened equipment fields
          equipmentPrimaryMachine: '',
          equipmentMachineNumber: '',
          equipmentAuxiliaryEquipment: [{ equipment: '', number: '', condition: 'ok' }],
          equipmentToolsUsed: [{ tool: '', number: '', calibrationDate: '' }],
          equipmentMaintenanceStatus: 'ok',
          // Flattened materials fields
          materialsRawMaterials: [{ material: '', reference: '', batchNumber: '', quantityUsed: 0, supplier: '' }],
          materialsConsumables: [{ item: '', quantity: 0, unitCost: 0 }],
          materialsPackaging: [{ type: '', quantity: 0, specifications: '' }],
          materialsTotalMaterialCost: 0,
          processSteps: [{ step: '', startTime: '', endTime: '', operator: '', parameters: '', result: 'ok' }],
          // Flattened qualityControls fields
          qualityControlsInProcessChecks: [{ checkpoint: '', standard: '', result: '', time: '', inspector: '' }],
          qualityControlsFinalInspection: { conducted: true, result: 'conforme', inspector: '', signoff: '' },
          qualityControlsTests: [{ test: '', method: '', result: '', conformity: 'ok' }],
          qualityControlsDefects: [{ defect: '', quantity: 0, severity: 'mineure', action: '' }],
          // Flattened performance fields
          performanceEfficiency: 100,
          performanceProductivity: 0,
          performanceScrapRate: 0,
          performanceReworkRate: 0,
          performanceConformityRate: 100,
          performanceOeeRate: 100,
          // Flattened environmental fields
          environmentalEnergyConsumption: 0,
          environmentalWasteGenerated: 0,
          environmentalWaterUsage: 0,
          environmentalEmissions: 0,
          environmentalRecycling: 0,
          // Flattened costs fields
          costsLaborCost: 0,
          costsMaterialCost: 0,
          costsOverheadCost: 0,
          costsTotalCost: 0,
          costsUnitCost: 0,
          // Flattened signatures fields
          signaturesOperatorSignature: '',
          signaturesSupervisorSignature: '',
          signaturesQualitySignature: '',
          signaturesCompletionDate: '',
        };
      case 'quality-control':
        return {
          ...baseData,  // — Spread base defaults first
          number: `CQ-${Date.now()}`,
          date: '',
          // Flattened productDetails fields
          productDetailsName: '',
          productDetailsCode: '',
          productDetailsBatchNumber: '',
          productDetailsLotNumber: '',
          productDetailsProductionDate: '',
          productDetailsExpiryDate: '',
          productDetailsCustomerOrder: '',
          productDetailsSpecification: '',
          // Flattened inspectionInfo fields
          inspectionInfoType: '',
          inspectionInfoStage: 'final',
          inspectionInfoSampling: 'statistique',
          inspectionInfoSampleSize: 0,
          inspectionInfoLotSize: 0,
          inspectionInfoInspector: '',
          inspectionInfoInspectionDate: '',
          inspectionInfoShift: 'jour',
          // Flattened standards fields
          standardsPrimaryStandard: 'ISO 9001',
          standardsTechnicalStandard: '',
          standardsCustomerSpecs: '',
          standardsInternalSpecs: '',
          standardsRegulatoryRequirements: '',
          standardsRevisionLevel: '',
          testProgram: [{ 
            test: '', 
            method: '', 
            equipment: '', 
            standard: '', 
            acceptanceCriteria: '', 
            frequency: '',
            responsible: ''
          }],
          // Flattened measurements fields
          measurementsDimensionalChecks: [{ dimension: '', specification: '', tolerance: '', measured: '', conformity: 'ok' }],
          measurementsPhysicalTests: [{ property: '', method: '', result: '', unit: '', limit: '', conformity: 'ok' }],
          measurementsChemicalTests: [{ parameter: '', method: '', result: '', specification: '', conformity: 'ok' }],
          measurementsFunctionalTests: [{ function: '', procedure: '', result: '', requirement: '', conformity: 'ok' }],
          // Flattened visualInspection fields
          visualInspectionSurfaceFinish: 'acceptable',
          visualInspectionColorConformity: 'conforme',
          visualInspectionPackagingIntegrity: 'satisfaisante',
          visualInspectionLabeling: 'conforme',
          visualInspectionOverallAppearance: 'acceptable',
          visualInspectionDefectsObserved: [{ defect: '', location: '', severity: 'mineure', quantity: 0 }],
          // Flattened results fields
          resultsOverallResult: 'conforme',
          resultsConformingUnits: 0,
          resultsNonConformingUnits: 0,
          resultsConformityRate: 100,
          resultsCriticalDefects: 0,
          resultsMajorDefects: 0,
          resultsMinorDefects: 0,
          nonConformities: [{ 
            description: '', 
            classification: 'majeure', 
            rootCause: '', 
            immediateAction: '',
            preventiveAction: '',
            responsible: '',
            deadline: ''
          }],
          // Flattened decisions fields
          decisionsLotAccepted: true,
          decisionsConditionalAcceptance: false,
          decisionsRejected: false,
          decisionsReworkRequired: false,
          decisionsSegregationRequired: false,
          decisionsCustomerNotification: false,
          // Flattened followUp fields
          followUpCorrectiveActions: [{ action: '', responsible: '', deadline: '', status: '' }],
          followUpPreventiveActions: [{ action: '', scope: '', responsible: '', implementation: '' }],
          followUpNextInspection: '',
          followUpMonitoringRequired: false,
          followUpReviewDate: '',
          // Flattened equipment fields
          equipmentMeasuringDevices: [{ device: '', calibrationDate: '', nextCalibration: '', accuracy: '' }],
          equipmentEnvironmentalConditions: { temperature: '', humidity: '', cleanliness: 'conforme' },
          equipmentTestLocation: '',
          equipmentLaboratoryAccreditation: '',
          // Flattened documentation fields
          documentationPhotographs: false,
          documentationTestReports: false,
          documentationCertificates: false,
          documentationCustomerReports: false,
          documentationArchiving: '5 ans',
          // Flattened signatures fields
          signaturesInspectorSignature: '',
          signaturesQualityManagerSignature: '',
          signaturesApprovalDate: '',
          signaturesNextReviewDate: '',
        };
      case 'manufacturing-order':
        return {
          ...baseData,  // — Spread base defaults first
          number: `BF-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          // Flattened orderInfo fields
          orderInfoIssueDate: new Date().toISOString().split('T')[0],
          orderInfoIssuedBy: '',
          orderInfoDepartment: 'production',
          orderInfoPriority: '',
          orderInfoUrgency: 'standard',
          orderInfoCustomerOrder: '',
          orderInfoInternalOrder: '',
          // Flattened productSpecification fields
          productSpecificationName: '',
          productSpecificationCode: '',
          productSpecificationRevision: '',
          productSpecificationDrawing: '',
          productSpecificationSpecification: '',
          productSpecificationQuality: 'standard',
          productSpecificationPackaging: 'standard',
          productSpecificationLabeling: 'conforme',
          // Flattened quantity fields
          quantityOrdered: 0,
          quantityPlannedProduction: 0,
          quantitySafetyStock: 0,
          quantityMinimumLot: 0,
          quantityEconomicLot: 0,
          quantityYield: 95,
          // Flattened scheduling fields
          schedulingRequestedDate: '',
          schedulingPlannedStartDate: '',
          schedulingPlannedEndDate: '',
          schedulingEstimatedDuration: '',
          schedulingShift: 'jour',
          schedulingWorkCenter: '',
          schedulingSequence: 1,
          // Flattened resources fields
          resourcesPrimaryMachine: '',
          resourcesBackupMachine: '',
          resourcesTooling: [{ tool: '', quantity: 1, location: '', condition: 'ok' }],
          resourcesFixtures: [{ fixture: '', setup: '', calibration: '' }],
          resourcesManpower: { skilled: 1, semiskilled: 0, unskilled: 0 },
          // Flattened materials fields
          materialsRawMaterials: [{ material: '', specification: '', quantity: 0, unit: '', supplier: '', stock: 0 }],
          materialsSubAssemblies: [{ component: '', quantity: 0, source: 'internal', leadTime: '' }],
          materialsConsumables: [{ item: '', consumption: 0, unit: '', stock: 0 }],
          materialsPackaging: [{ type: '', quantity: 0, specifications: '' }],
          processRouting: [{ 
            operation: '', 
            workCenter: '', 
            machine: '', 
            setupTime: 0, 
            cycleTime: 0, 
            operator: '', 
            tools: '', 
            instructions: '', 
            qualityChecks: ''
          }],
          // Flattened qualityRequirements fields
          qualityRequirementsInspectionPlan: '',
          qualityRequirementsCheckpoints: [{ checkpoint: '', standard: '', frequency: '', method: '' }],
          qualityRequirementsTestRequirements: [{ test: '', specification: '', equipment: '' }],
          qualityRequirementsDocumentation: [{ document: '', required: true, location: '' }],
          qualityRequirementsCertifications: '',
          // Flattened safety fields
          safetyHazards: [{ hazard: '', risk: 'faible', mitigation: '' }],
          safetyPpe: [{ equipment: '', mandatory: true, standard: '' }],
          safetyProcedures: [{ procedure: '', critical: true, training: '' }],
          safetyEmergencyContacts: '',
          // Flattened costing fields
          costingMaterialCost: 0,
          costingLaborCost: 0,
          costingOverheadCost: 0,
          costingSetupCost: 0,
          costingTotalCost: 0,
          costingUnitCost: 0,
          costingMarginTarget: 25,
          // Flattened approval fields
          approvalProductionManager: false,
          approvalQualityManager: false,
          approvalMaterialPlanner: false,
          approvalFinalApproval: false,
          approvalApprovalDate: '',
          approvalConditions: '',
          // Flattened tracking fields
          trackingStatus: '',
          trackingProgress: 0,
          trackingStartedDate: '',
          trackingCompletedDate: '',
          trackingActualQuantity: 0,
          trackingScrap: 0,
          trackingRework: 0,
        };
      case 'failure-report':
        return {
          ...baseData,  // — Spread base defaults first
          number: `RD-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          // Flattened equipment fields
          equipmentId: '',
          equipmentName: '',
          equipmentType: '',
          equipmentManufacturer: '',
          equipmentModel: '',
          equipmentSerialNumber: '',
          equipmentLocation: '',
          equipmentInstallationDate: '',
          equipmentOperatingHours: 0,
          equipmentLastMaintenance: '',
          // Flattened failure fields
          failureType: 'panne',
          failureCategory: '',
          failureSeverity: 'moyenne',
          failureImpact: 'local',
          failureDetectionDate: new Date().toISOString().split('T')[0],
          failureDetectionTime: '',
          failureDetectedBy: '',
          failureReportedBy: '',
          failureReportDate: new Date().toISOString().split('T')[0],
          // Flattened conditions fields
          conditionsOperatingMode: 'automatique',
          conditionsWorkload: '',
          conditionsEnvironmentalConditions: 'standard',
          conditionsOperatorPresent: true,
          conditionsMaintenanceRecent: false,
          conditionsModifications: false,
          // Flattened symptoms fields
          symptomsInitialSymptoms: '',
          symptomsProgressiveSymptoms: '',
          symptomsAlarms: [{ alarm: '', time: '', severity: 'info' }],
          symptomsObservations: [{ observer: '', time: '', description: '' }],
          symptomsMeasurements: [{ parameter: '', value: '', unit: '', normal: '' }],
          // Flattened investigation fields
          investigationImmediateCause: '',
          investigationRootCause: '',
          investigationContributingFactors: [{ factor: '', influence: 'faible', description: '' }],
          investigationInvestigationMethod: '5 pourquoi',
          investigationEvidenceCollected: [{ evidence: '', type: 'photo', location: '' }],
          investigationWitnessStatements: [{ witness: '', statement: '' }],
          // Flattened analysis fields
          analysisFailureMode: '',
          analysisMechanismOfFailure: '',
          analysisSystemsAffected: [{ system: '', impact: 'partiel', description: '' }],
          analysisRiskAssessment: { probability: 'faible', severity: 'moyenne', risk: 'acceptable' },
          analysisPrecedentsFound: false,
          analysisSimilarFailures: '',
          // Flattened impact fields
          impactProductionLoss: 0,
          impactDowntimeHours: 0,
          impactQualityImpact: 'aucun',
          impactSafetyImpact: 'aucun',
          impactEnvironmentalImpact: 'aucun',
          impactCustomerImpact: 'aucun',
          impactFinancialImpact: 0,
          // Flattened actions fields
          actionsImmediateActions: [{ action: '', responsible: '', time: '', effectiveness: '' }],
          actionsTemporarySolution: { implemented: false, description: '', limitations: '' },
          actionsCorrectiveActions: [{ action: '', responsible: '', deadline: '', cost: 0, status: '' }],
          actionsPreventiveActions: [{ action: '', scope: '', implementation: '', monitoring: '' }],
          // Flattened resources fields
          resourcesPersonnelInvolved: [{ person: '', role: '', hours: 0, cost: 0 }],
          resourcesMaterialsUsed: [{ material: '', quantity: 0, cost: 0, supplier: '' }],
          resourcesExternalServices: [{ service: '', provider: '', cost: 0, duration: '' }],
          resourcesTotalCost: 0,
          // Flattened resolution fields
          resolutionResolved: false,
          resolutionResolutionDate: '',
          resolutionResolutionTime: '',
          resolutionSolutionDescription: '',
          resolutionEffectiveness: '',
          resolutionFollowUpRequired: true,
          resolutionNextInspection: '',
          // Flattened lessons fields
          lessonsLessonsLearned: '',
          lessonsProcessImprovements: [{ improvement: '', priority: 'moyenne', implementation: '' }],
          lessonsTrainingNeeds: [{ training: '', audience: '', urgency: '' }],
          lessonsSystemUpdates: [{ update: '', system: '', priority: '' }],
          // Flattened documentation fields
          documentationPhotos: false,
          documentationVideos: false,
          documentationTechnicalDrawings: false,
          documentationTestReports: false,
          documentationDistributionList: '',
          documentationRetentionPeriod: '10 ans',
          // Flattened signatures fields
          signaturesReporterSignature: '',
          signaturesSupervisorSignature: '',
          signaturesMaintenanceManagerSignature: '',
          signaturesApprovalDate: '',
          signaturesNextReviewDate: '',
        };
      case 'route-sheet':
        return {
          ...baseData,  // — Spread base defaults first
          number: `FR-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          // Flattened driver fields
          driverName: '',
          driverLicenseNumber: '',
          driverLicenseExpiry: '',
          driverPhone: '',
          driverExperience: '',
          driverCertifications: [{ certification: '', expiry: '', authority: '' }],
          driverEmergencyContact: '',
          // Flattened vehicle fields
          vehicleRegistration: '',
          vehicleType: 'camion',
          vehicleBrand: '',
          vehicleModel: '',
          vehicleCapacity: 0,
          vehicleFuelType: 'diesel',
          vehicleInsuranceExpiry: '',
          vehicleLastMaintenance: '',
          vehicleTechnicalControl: '',
          // Flattened route fields
          routeName: '',
          routeCode: '',
          routeTerritory: '',
          routeTotalDistance: 0,
          routeEstimatedDuration: '',
          routeDifficultyLevel: '',
          routeAlternativeRoute: '',
          routeRestrictions: '',
          // Flattened schedule fields
          scheduleDepartureDate: new Date().toISOString().split('T')[0],
          scheduleDepartureTime: '',
          schedulePlannedReturnTime: '',
          scheduleEstimatedReturnTime: '',
          scheduleBreakTimes: [{ start: '', end: '', location: '', type: 'repos' }],
          scheduleDrivingTime: '',
          scheduleRestTime: '',
          deliveries: [{ 
            order: '', 
            client: '', 
            address: '', 
            contactPerson: '', 
            phone: '',
            deliveryWindow: '',
            estimatedTime: '',
            priority: '',
            specialInstructions: '',
            packages: 0,
            weight: 0,
            value: 0,
            signature: false
          }],
          // Flattened cargo fields
          cargoTotalWeight: 0,
          cargoTotalVolume: 0,
          cargoTotalValue: 0,
          cargoFragileItems: 0,
          cargoHazardousItems: 0,
          cargoTemperatureControlled: false,
          cargoSpecialHandling: '',
          // Flattened preTrip fields
          preTripVehicleInspection: { completed: false, issues: '', inspector: '' },
          preTripFuelLevel: 100,
          preTripMileageStart: 0,
          preTripDocumentsCheck: { completed: false, missing: '' },
          preTripEquipmentCheck: { gps: true, phone: true, uniform: true, tools: true },
          preTripWeatherConditions: 'favorable',
          // Flattened safetyPlan fields
          safetyPlanHazards: [{ hazard: '', mitigation: '', equipment: '' }],
          safetyPlanEmergencyContacts: [{ contact: '', phone: '', role: 'dispatch' }],
          safetyPlanFirstAid: { available: true, location: '', trained: true },
          safetyPlanCommunication: { frequency: '2h', method: 'telephone', backup: 'radio' },
          // Flattened compliance fields
          complianceDrivingHours: 0,
          complianceRestPeriods: 0,
          complianceTachographCheck: false,
          complianceWeighBridgeRequired: false,
          complianceCustomsDocuments: false,
          complianceSpecialPermits: '',
          // Flattened tracking fields
          trackingGpsEnabled: true,
          trackingTrackingSystem: '',
          trackingCheckpoints: [{ location: '', estimatedTime: '', actualTime: '', status: '' }],
          trackingUpdates: [{ time: '', location: '', status: '', comment: '' }],
          // Flattened costs fields
          costsFuelBudget: 0,
          costsTollsCost: 0,
          costsOtherExpenses: 0,
          costsTotalBudget: 0,
          costsAdvanceGiven: 0,
        };
      case 'delivery-report':
        return {
          ...baseData,  // — Spread base defaults first
          number: generateDocumentNumber('delivery-report'),
          date: new Date().toISOString().split('T')[0],
          // Flattened routeInfo fields
          routeInfoRouteNumber: '',
          routeInfoRouteName: '',
          routeInfoPlannedDate: '',
          routeInfoActualDate: '',
          routeInfoDriver: '',
          routeInfoVehicle: '',
          routeInfoTerritory: '',
          // Flattened timing fields
          timingDepartureTime: '',
          timingReturnTime: '',
          timingTotalTime: '',
          timingDrivingTime: '',
          timingBreakTime: '',
          timingDeliveryTime: '',
          timingDelays: '',
          // Flattened performance fields
          performanceDeliveriesPlanned: 0,
          performanceDeliveriesCompleted: 0,
          performanceDeliveriesFailed: 0,
          performanceDeliverySuccessRate: 100,
          performanceOnTimeDeliveries: 0,
          performanceCustomerSignatures: 0,
          deliveryDetails: [{ 
            order: '', 
            client: '', 
            address: '',
            plannedTime: '',
            actualTime: '',
            status: '',
            packages: 0,
            signature: '',
            notes: '',
            issues: ''
          }],
          failedDeliveries: [{ 
            order: '', 
            client: '', 
            reason: '', 
            action: '', 
            rescheduled: '',
            compensation: 0
          }],
          // Flattened vehicle fields
          vehicleMileageStart: 0,
          vehicleMileageEnd: 0,
          vehicleTotalKm: 0,
          vehicleFuelStart: 100,
          vehicleFuelEnd: 0,
          vehicleFuelConsumed: 0,
          vehicleFuelCost: 0,
          vehicleCondition: 'bon',
          incidents: [{ 
            time: '', 
            location: '', 
            type: 'retard', 
            description: '', 
            action: '',
            impact: 'mineur',
            cost: 0
          }],
          // Flattened expenses fields
          expensesFuel: 0,
          expensesTolls: 0,
          expensesParking: 0,
          expensesMeals: 0,
          expensesOther: 0,
          expensesTotal: 0,
          expensesReceipts: false,
          customerFeedback: [{ 
            client: '', 
            rating: 5, 
            comment: '', 
            complaint: false,
            compliment: false
          }],
          // Flattened safety fields
          safetyAccidentsReported: 0,
          safetyTrafficViolations: 0,
          safetySafetyIncidents: 0,
          safetyPpeDamage: false,
          safetyVehicleDamage: false,
          // Flattened cargo fields
          cargoDamageReported: false,
          cargoDamages: [{ item: '', damage: '', value: 0, cause: '', responsible: '' }],
          cargoTemperatureDeviations: 0,
          cargoSecurityBreaches: 0,
          cargoInventoryDiscrepancies: 0,
          // Flattened documentation fields
          documentationDeliveryNotes: 'complete',
          documentationProofOfDelivery: 'obtained',
          documentationPhotos: false,
          documentationCustomerForms: 'signed',
          documentationReturnItems: [{ item: '', reason: '', value: 0 }],
          // Flattened nextActions fields
          nextActionsFollowUpRequired: false,
          nextActionsCustomerCalls: [{ client: '', reason: '', priority: '' }],
          nextActionsVehicleMaintenance: false,
          nextActionsRouteOptimization: '',
          nextActionsDriverTraining: '',
          // Flattened signatures fields
          signaturesDriverSignature: '',
          signaturesSupervisorSignature: '',
          signaturesCompletionTime: '',
          signaturesApprovalDate: '',
        };
      case 'transport-voucher':
        return {
          ...baseData,  // — Spread base defaults first
          number: `BT-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          // Flattened shipper fields
          shipperName: '',
          shipperAddress: '',
          shipperPhone: '',
          shipperEmail: '',
          shipperContactPerson: '',
          shipperLicenseNumber: '',
          shipperInsurancePolicy: '',
          // Flattened carrier fields
          carrierCompany: '',
          carrierAddress: '',
          carrierPhone: '',
          carrierEmail: '',
          carrierContactPerson: '',
          carrierLicenseNumber: '',
          carrierInsurancePolicy: '',
          carrierVehicleRegistration: '',
          // Flattened consignee fields
          consigneeName: '',
          consigneeAddress: '',
          consigneePhone: '',
          consigneeEmail: '',
          consigneeContactPerson: '',
          consigneeDeliveryInstructions: '',
          consigneeSpecialRequirements: '',
          // Flattened transport fields
          transportMode: 'routier',
          transportType: 'livraison',
          transportUrgency: '',
          transportServiceLevel: 'standard',
          transportPickupDate: '',
          transportPickupTime: '',
          transportDeliveryDate: '',
          transportDeliveryTime: '',
          transportEstimatedTransitTime: '',
          // Flattened route fields
          routeOrigin: '',
          routeDistance: 0,
          routeIntermediateStops: [{ location: '', purpose: '', estimatedTime: '' }],
          routeRouteRestrictions: '',
          routePreferredRoute: '',
          // Flattened cargo fields
          cargoDescription: '',
          cargoCommodity: '',
          cargoPackagingType: 'carton',
          cargoPackages: 0,
          cargoWeight: 0,
          cargoVolume: 0,
          cargoDimensions: { length: 0, width: 0, height: 0 },
          cargoValue: 0,
          cargoCurrency: getBrandDefaultCurrency(),
          // Flattened specialHandling fields
          specialHandlingFragile: false,
          specialHandlingHazardous: false,
          specialHandlingPerishable: false,
          specialHandlingTemperatureControlled: false,
          specialHandlingTemperatureRange: '',
          specialHandlingSpecialInstructions: '',
          specialHandlingHandlingEquipment: '',
          // Flattened documentation fields
          documentationInvoiceRequired: true,
          documentationPackingList: true,
          documentationCertificate: false,
          documentationCustomsDeclaration: false,
          documentationAdditionalDocs: [{ document: '', required: true, provided: false }],
          // Flattened pricing fields
          pricingTransportCost: 0,
          pricingFuelSurcharge: 0,
          pricingHandlingFee: 0,
          pricingInsuranceFee: 0,
          pricingAdditionalCharges: 0,
          pricingTotalCost: 0,
          pricingPaymentTerms: '',
          pricingPaymentMethod: '',
          // Flattened insurance fields
          insuranceRequired: false,
          insuranceCoverage: 0,
          insuranceDeductible: 0,
          insurancePolicyNumber: '',
          insuranceInsurer: '',
          insuranceClaims: 'carrier',
          // Flattened tracking fields
          trackingTrackingNumber: '',
          trackingExpectedDeliveryDate: '',
          trackingTrackingSystem: '',
          trackingMilestones: [{ milestone: '', estimatedTime: '', actualTime: '', status: '' }],
          trackingNotifications: { sms: false, email: false, app: false },
          // Flattened conditions fields
          conditionsDeliveryConfirmation: 'signature',
          conditionsUndeliverableAction: 'return',
          conditionsStorageCharges: 0,
          conditionsDemurrageCharges: 0,
          conditionsLiabilityLimit: 0,
          conditionsClaimsDeadline: '7 jours',
          // Flattened compliance fields
          compliancePermits: [{ permit: '', number: '', expiry: '', authority: '' }],
          complianceRegulations: [{ regulation: '', compliance: 'ok', notes: '' }],
          complianceSafetyRequirements: '',
          complianceEnvironmentalCompliance: 'standard',
          // Flattened signatures fields
          signaturesShipperSignature: '',
          signaturesCarrierSignature: '',
          signaturesContractDate: '',
          signaturesPickupSignature: '',
          signaturesDeliverySignature: '',
        };
      case 'rental-contract':
        return {
          ...baseData,  // — Spread base defaults first
          number: `CL-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          // Flattened landlord fields
          landlordName: '',
          landlordLegalForm: 'physique',
          landlordAddress: '',
          landlordPhone: '',
          landlordEmail: '',
          landlordNif: '',
          landlordBankAccount: '',
          landlordLegalRepresentative: '',
          // Flattened tenant fields
          tenantName: '',
          tenantLegalForm: 'entreprise',
          tenantAddress: '',
          tenantPhone: '',
          tenantEmail: '',
          tenantNif: '',
          tenantNis: '',
          tenantActivity: '',
          tenantLegalRepresentative: '',
          tenantGuarantor: '',
          // Flattened property fields
          propertyType: 'commercial',
          propertyDescription: '',
          propertyAddress: '',
          propertyWilaya: '',
          propertyCommune: '',
          propertyCadastralReference: '',
          propertyTitleDeed: '',
          propertySurface: 0,
          propertyFloors: 1,
          propertyParking: 0,
          propertyAnnexes: '',
          // Flattened lease fields
          leaseType: 'commercial',
          leasePurpose: 'bureau',
          leaseStartDate: '',
          leaseDuration: '12 mois',
          leaseRenewalTerms: 'tacite',
          leaseNoticePeriod: '3 mois',
          leaseEndDate: '',
          leaseHandoverDate: '',
          // Flattened financial fields
          financialMonthlyRent: 0,
          financialRentIncludes: [{ service: 'eau', included: true }],
          financialCharges: 0,
          financialChargesDetails: '',
          financialDeposit: 0,
          financialAdvancePayment: 0,
          financialIndexation: 'fixed',
          financialIndexationRate: 0,
          financialPaymentDay: 1,
          financialPaymentMethod: '',
          // Flattened obligations fields
          obligationsLandlordObligations: [{ obligation: 'maintenance structure', description: '' }],
          obligationsTenantObligations: [{ obligation: 'entretien courant', description: '' }],
          obligationsUsageRestrictions: '',
          obligationsAlterationsAllowed: false,
          obligationsSublettingAllowed: false,
          obligationsTransferAllowed: false,
          // Flattened charges fields
          chargesUtilities: { water: 'tenant', electricity: 'tenant', gas: 'tenant', internet: 'tenant' },
          chargesMaintenance: { interior: 'tenant', exterior: 'landlord', hvac: 'shared' },
          chargesInsurance: { building: 'landlord', contents: 'tenant', liability: 'tenant' },
          chargesTaxes: { property: 'landlord', business: 'tenant' },
          // Flattened conditions fields
          conditionsStateOfPremises: 'good',
          conditionsInventoryRequired: true,
          conditionsKeyDeposit: 0,
          conditionsAccessHours: '24/7',
          conditionsParkingSpaces: 0,
          conditionsStorageSpace: false,
          conditionsSecuritySystem: false,
          // Flattened insurance fields
          insuranceLandlordInsurance: { required: true, coverage: 'building', amount: 0 },
          insuranceTenantInsurance: { required: true, coverage: 'liability', amount: 0 },
          insuranceProofRequired: true,
          insuranceBeneficiaryClause: true,
          // Flattened termination fields
          terminationEarlyTermination: [{ cause: 'breach', notice: '30 days', penalty: 0 }],
          terminationRenewalProcess: 'automatic',
          terminationDepositsReturn: '30 days',
          terminationStateAssessment: 'required',
          terminationRestorationRequired: 'original state',
          // Flattened legal fields
          legalJurisdiction: ld('courts'),
          legalApplicableLaw: ld('civil_code'),
          legalDisputeResolution: 'mediation first',
          legalRegistrationRequired: true,
          legalNotarizationRequired: false,
          legalWitnesses: [{ name: '', address: '', signature: false }],
          // Flattened signatures fields
          signaturesLandlordSignature: '',
          signaturesTenantSignature: '',
          signaturesWitnessSignatures: '',
          signaturesContractDate: '',
          signaturesRegistrationDate: '',
          signaturesRegistrationNumber: '',
        };
      case 'property-inspection':
        return {
          ...baseData,  // — Spread base defaults first
          number: `EL-${Date.now()}`,
          date: '',
          // Flattened inspection fields
          inspectionType: '',
          inspectionPurpose: 'contractuel',
          inspectionDate: new Date().toISOString().split('T')[0],
          inspectionTime: '',
          inspectionDuration: '',
          inspectionWeather: 'normal',
          inspectionLighting: 'naturelle',
          // Flattened property fields
          propertyAddress: '',
          propertyType: 'commercial',
          propertySurface: 0,
          propertyFloors: 1,
          propertyRooms: 0,
          propertyBuildingYear: '',
          propertyLastRenovation: '',
          propertyHeatingType: '',
          // Flattened parties fields
          partiesLandlord: { name: '', present: true, representative: '' },
          partiesTenant: { name: '', present: true, representative: '' },
          partiesInspector: { name: '', qualification: '', certification: '' },
          partiesWitnesses: [{ name: '', role: '', signature: false }],
          // inventory: nested structure (rooms — equipment — too deep for flat init)
          inventory: {
            rooms: [{ 
              name: '', 
              surface: 0, 
              flooring: '', 
              walls: '', 
              ceiling: '', 
              windows: '', 
              doors: '', 
              fixtures: '', 
              condition: 'bon',
            equipment: [{ 
              item: '', 
              brand: '', 
              model: '', 
              serialNumber: '', 
              condition: 'bon', 
              working: true,
                accessories: '',
            utilities: {
              electricity: { functional: true, meters: '', safety: 'ok' },
              plumbing: { functional: true, leaks: false, pressure: 'normal' },
              heating: { functional: true, maintenance: '', efficiency: 'good' },
              internet: { available: true, speed: '', provider: '' }
            }
              }]
            }]
          },
          // Flattened condition fields
          conditionOverall: 'bon',
          conditionStructural: 'satisfaisant',
          conditionCleanliness: 'acceptable',
          conditionMaintenance: 'recent',
          conditionSafety: 'conforme',
          conditionAccessibility: 'standard',
          observations: [{ 
            location: '', 
            item: '', 
            condition: '', 
            defect: '', 
            severity: 'mineure', 
            cause: '',
            photos: false,
            action: ''
          }],
          damages: [{ 
            location: '', 
            description: '', 
            extent: '', 
            cause: 'usure', 
            responsibility: 'tenant',
            estimatedCost: 0,
            urgent: false
          }],
          improvements: [{ 
            location: '', 
            improvement: '', 
            cost: 0, 
            priority: '', 
            timeline: '',
            approval: 'required'
          }],
          // Flattened safety fields
          safetyFireExtinguishers: { present: true, expiry: '', tested: true },
          safetySmokeDetectors: { present: true, functional: true, batteries: 'ok' },
          safetyEmergencyExits: { accessible: true, signage: 'visible', lighting: 'functional' },
          safetyFirstAid: { available: false, location: '', updated: false },
          // Flattened meters fields
          metersElectricity: { reading: 0, date: '', photos: false },
          metersWater: { reading: 0, date: '', photos: false },
          metersGas: { reading: 0, date: '', photos: false },
          // Flattened keys fields
          keysDelivered: [{ type: 'entrance', quantity: 2, copies: 0 }],
          keysRetained: [{ type: 'master', quantity: 1, location: 'management' }],
          keysBadges: [{ type: 'access', quantity: 1, code: '' }],
          // Flattened documentation fields
          documentationPhotos: { taken: false, count: 0, storage: 'digital' },
          documentationVideos: { taken: false, duration: 0, purpose: '' },
          documentationPlans: { available: true, updated: true, scale: '' },
          documentationCertificates: [{ type: 'conformity', valid: true, expiry: '' }],
          // Flattened agreements fields
          agreementsDisputesNoted: false,
          agreementsDiscrepancies: '',
          agreementsReservations: [{ party: '', reservation: '', accepted: false }],
          agreementsAmendments: '',
          agreementsFutureActions: [{ action: '', responsible: '', deadline: '' }],
          // Flattened signatures fields
          signaturesLandlordSignature: '',
          signaturesTenantSignature: '',
          signaturesInspectorSignature: '',
          signaturesWitnessSignatures: '',
          signaturesDate: '',
          signaturesCopies: 2,
        };
      case 'maintenance-planning':
        return {
          ...baseData,  // — Spread base defaults first
          number: `PMT-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          // Flattened equipment fields
          equipmentId: '',
          equipmentName: '',
          equipmentType: 'machinery',
          equipmentManufacturer: '',
          equipmentModel: '',
          equipmentLocation: '',
          equipmentCriticality: 'high',
          equipmentOperatingHours: 0,
          equipmentInstallationDate: '',
          // Flattened strategy fields
          strategyMaintenanceType: '',
          strategyFrequency: '',
          strategyBasis: 'time based',
          strategyRiskAssessment: 'medium',
          strategyBusinessImpact: 'high',
          strategyRegulatory: false,
          strategyManufacturer: true,
          // Flattened schedule fields
          schedulePlanningPeriod: '12 months',
          scheduleBaseDate: new Date().toISOString().split('T')[0],
          scheduleLastMaintenance: '',
          scheduleNextMaintenance: '',
          scheduleFrequency: 30,
          scheduleFlexibilityWindow: 7,
          scheduleSeasonalAdjustment: false,
          // Flattened tasks fields
          tasksRoutine: [{ task: '', frequency: 'monthly', duration: 0, skill: 'technician', tools: '' }],
          tasksPeriodic: [{ task: '', frequency: 'quarterly', duration: 0, skill: 'specialist', cost: 0 }],
          tasksSeasonal: [{ task: '', season: 'spring', duration: 0, special: true, external: false }],
          tasksConditional: [{ task: '', condition: '', trigger: '', urgency: 'normal' }],
          // Flattened resources fields
          resourcesPrimaryTechnician: '',
          resourcesBackupTechnician: '',
          resourcesSkillsRequired: [{ skill: '', level: 'intermediate', certified: true }],
          resourcesContractors: [{ contractor: '', specialty: '', availability: 'on call' }],
          resourcesTeamSize: 1,
          resourcesSupervision: 'self supervised',
          // Flattened materials fields
          materialsSpareParts: [{ part: '', partNumber: '', quantity: 1, cost: 0, leadTime: 30, critical: false }],
          materialsConsumables: [{ item: '', quantity: 0, unit: '', cost: 0, supplier: '' }],
          materialsSpecialTools: [{ tool: '', required: false, available: true, calibration: '' }],
          materialsTotalBudget: 0,
          // Flattened procedures fields
          proceduresWorkInstructions: [{ procedure: '', reference: '', version: '', safety: true }],
          proceduresStandards: [{ standard: '', applicability: 'mandatory', compliance: true }],
          proceduresChecklists: [{ checklist: '', items: 0, completion: 'required' }],
          proceduresDocumentation: [{ document: '', type: 'manual', current: true }],
          // Flattened compliance fields
          complianceRegulatory: [{ regulation: '', applicable: true, frequency: '', inspector: '' }],
          complianceWarranty: [{ warranty: '', requirement: '', impact: 'maintain', compliance: true }],
          complianceInsurance: [{ requirement: '', frequency: '', certification: '', cost: 0 }],
          complianceSafety: [{ requirement: '', standard: '', training: 'required' }],
          // Flattened optimization fields
          optimizationCostReduction: [{ opportunity: '', saving: 0, implementation: '', effort: 'low' }],
          optimizationEfficiency: [{ improvement: '', benefit: '', metric: '', target: 0 }],
          optimizationReliability: [{ enhancement: '', impact: 'positive', investment: 0 }],
          optimizationSustainability: [{ initiative: '', environmental: true, saving: '' }],
          // Flattened performance fields
          performanceKpis: [{ metric: 'MTBF', target: 1000, current: 0, trend: 'improving' }],
          performanceAvailability: { target: 95, current: 0, downtime: 0 },
          performanceCosts: { budgeted: 0, actual: 0, variance: 0, trend: 'stable' },
          performanceQuality: { defectRate: 0, reworkRate: 0, customerSatisfaction: 95 },
          // Flattened risks fields
          risksOperational: [{ risk: '', probability: 'low', impact: 'medium', mitigation: '' }],
          risksSafety: [{ hazard: '', severity: 'low', controls: '', monitoring: '' }],
          risksFinancial: [{ risk: '', cost: 0, probability: 'unlikely', contingency: '' }],
          risksEnvironmental: [{ impact: '', severity: 'minor', prevention: '', monitoring: '' }],
          // Flattened approval fields
          approvalPlanApproved: false,
          approvalApprovedBy: '',
          approvalApprovalDate: '',
          approvalBudgetApproved: false,
          approvalReviewDate: '',
          approvalNextReview: '',
          // Flattened tracking fields
          trackingCompletionRate: 0,
          trackingDelayedTasks: 0,
          trackingBudgetUtilization: 0,
          trackingEffectivenessScore: 0,
          trackingLastUpdate: '',
          trackingNextUpdate: '',
        };
      case 'client-intervention':
        return {
          ...baseData,  // — Spread base defaults first
          number: `FIC-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          // Flattened request fields
          requestRequestDate: new Date().toISOString().split('T')[0],
          requestRequestTime: '',
          requestRequestedBy: '',
          requestRequestMethod: 'phone',
          requestPriority: '',
          requestUrgency: 'standard',
          requestSla: '24h',
          requestContractType: 'maintenance',
          // Flattened client fields
          clientName: '',
          clientAddress: '',
          clientContact: '',
          clientPhone: '',
          clientEmail: '',
          clientContractNumber: '',
          clientServiceLevel: 'standard',
          clientSpecialInstructions: '',
          // Flattened intervention fields
          interventionType: '',
          interventionCategory: 'corrective',
          interventionEquipmentType: '',
          interventionLocation: '',
          interventionAccessRequirements: '',
          interventionSafetyRequirements: '',
          interventionPermitRequired: false,
          // Flattened scheduling fields
          schedulingScheduledDate: '',
          schedulingScheduledTime: '',
          schedulingEstimatedDuration: '',
          schedulingTechnician: '',
          schedulingBackupTechnician: '',
          schedulingTravelTime: '',
          schedulingArrivalWindow: '2h',
          // Flattened problem fields
          problemDescription: '',
          problemSymptoms: '',
          problemReportedBy: '',
          problemOccurrence: 'first time',
          problemImpact: 'minor',
          problemBusinessCriticality: 'low',
          problemEnvironmentalConditions: '',
          // Flattened diagnosis fields
          diagnosisInitialAssessment: '',
          diagnosisRootCause: '',
          diagnosisInvestigation: [{ step: '', finding: '', conclusion: '' }],
          diagnosisTesting: [{ test: '', result: '', equipment: '' }],
          diagnosisConfirmation: '',
          diagnosisAdditionalIssues: [{ issue: '', severity: 'minor', action: '' }],
          // Flattened solution fields
          solutionActionTaken: '',
          solutionMethod: 'repair',
          solutionDifficulty: 'standard',
          solutionStandards: [{ standard: '', followed: true, deviation: '' }],
          solutionQualityCheck: true,
          solutionTesting: [{ test: '', result: 'pass', notes: '' }],
          // Flattened materials fields
          materialsPartsUsed: [{ part: '', partNumber: '', quantity: 1, cost: 0, warranty: '', source: 'stock' }],
          materialsConsumables: [{ item: '', quantity: 0, cost: 0, purpose: '' }],
          materialsSpecialTools: [{ tool: '', purpose: '', owned: true, cost: 0 }],
          materialsTotalCost: 0,
          // Flattened time fields
          timeArrivalTime: '',
          timeStartTime: '',
          timeEndTime: '',
          timeWorkDuration: '',
          timeTravelTime: '',
          timeWaitTime: '',
          timeTotalTime: '',
          timeOvertime: false,
          // Flattened results fields
          resultsProblemResolved: true,
          resultsPartialSolution: false,
          resultsTemporaryFix: false,
          resultsFollowUpRequired: false,
          resultsEquipmentStatus: 'operational',
          resultsPerformanceRestored: true,
          resultsClientNotified: true,
          // Flattened quality fields
          qualityWorkQuality: 'excellent',
          qualityClientSatisfaction: '',
          qualityIssuesReported: false,
          qualityReworkNeeded: false,
          qualityQualityScore: 10,
          qualityFeedback: '',
          // Flattened followUp fields
            followUpRequired: false,
          followUpType: 'monitoring',
          followUpScheduledDate: '',
          followUpResponsible: '',
          followUpRecommendations: [{ recommendation: '', priority: 'normal', cost: 0 }],
          followUpPreventiveMeasures: [{ measure: '', implementation: '', timeline: '' }],
          // Flattened documentation fields
          documentationPhotos: false,
          documentationReports: false,
          documentationManuals: false,
          documentationWarranties: false,
          documentationCertificates: false,
          documentationClientCopy: true,
          // Flattened billing fields
          billingBillable: true,
          billingContractCovered: false,
          billingLaborCost: 0,
          billingMaterialCost: 0,
          billingTravelCost: 0,
          billingTotalAmount: 0,
          billingInvoiceGenerated: false,
          billingPaymentTerms: '30 days',
          // Flattened approval fields
          approvalWorkCompleted: true,
          approvalClientApproval: true,
          approvalClientSignature: '',
          approvalTechnicianSignature: '',
          approvalApprovalDate: '',
          approvalRemarks: '',
        };
      case 'product-warranty':
        return {
          ...baseData,  // — Spread base defaults first
          number: `GP-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          // Flattened product fields
          productName: '',
          productModel: '',
          productBrand: '',
          productCategory: '',
          productSerialNumber: '',
          productLotNumber: '',
          productManufactureDate: '',
          productSpecifications: '',
          // Flattened purchase fields
          purchaseClient: '',
          purchasePurchaseDate: '',
          purchaseInvoiceNumber: '',
          purchaseDealer: '',
          purchasePurchasePrice: 0,
          purchaseCurrency: getBrandDefaultCurrency(),
          purchaseProofOfPurchase: true,
          // Flattened warranty fields
          warrantyType: 'manufacturer',
          warrantyPeriod: '12 mois',
          warrantyStartDate: '',
          warrantyExpiryDate: '',
          warrantyTransferable: true,
          warrantyInternational: false,
          warrantyExtendable: false,
          // Flattened coverage fields
          coverageType: '',
          coverageDefects: true,
          coverageManufacturing: true,
          coverageMaterials: true,
          coverageWorkmanship: true,
          coverageFunctional: true,
          coverageParts: true,
          coverageLabor: true,
          // Flattened conditions fields
          conditionsNormalUse: true,
          conditionsProperInstallation: true,
          conditionsAuthorizedService: true,
          conditionsOriginalParts: true,
          conditionsMaintenance: 'required',
          conditionsDocumentation: 'complete',
          // Flattened exclusions fields
          exclusionsMisuse: true,
          exclusionsAccident: true,
          exclusionsNegligence: true,
          exclusionsUnauthorizedRepair: true,
          exclusionsModification: true,
          exclusionsWearItems: [{ item: '', period: '' }],
          // Flattened service fields
          serviceServiceCenter: '',
          serviceContactInfo: '',
          serviceSupportHours: '8h-17h',
          serviceEmergencyContact: '',
          serviceOnSiteService: false,
          servicePickupService: false,
          // Flattened process fields
          processClaimProcedure: '',
          processDocumentationRequired: [{ document: '', mandatory: true }],
          processInspectionRequired: false,
          processApprovalTime: '5-10 days',
          processRepairTime: '7-15 days',
          // Flattened terms fields
          termsLimitations: '',
          termsLiability: 'product value',
          termsConsequentialDamages: false,
          termsJurisdiction: ld('jurisdiction_country'),
          termsGoverningLaw: ld('consumer_protection_law'),
          // Flattened registration fields
          registrationRegistrationRequired: false,
          registrationRegistrationDeadline: '',
          registrationBenefits: [{ benefit: '', description: '' }],
          registrationContact: '',
        };
      case 'it-audit':
        return {
          ...baseData,  // — Spread base defaults first
          number: `RAI-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          // Flattened audit fields
          auditType: 'security',
          auditScope: 'infrastructure',
          auditObjective: 'compliance verification',
          auditStandard: 'ISO 27001',
          auditFrequency: 'annual',
          auditDuration: '5 days',
          auditMethodology: 'risk-based',
          // Flattened auditor fields
          auditorName: '',
          auditorOrganization: 'internal',
          auditorCertification: 'CISSP',
          auditorExperience: '',
          auditorTeam: [{ member: '', role: '', expertise: '' }],
          auditorIndependence: 'confirmed',
          // Flattened systems fields
          systemsNetwork: { audited: true, scope: 'full', criticality: 'high', findings: 0 },
          systemsServers: { audited: true, scope: 'critical', criticality: 'high', findings: 0 },
          systemsDatabases: { audited: true, scope: 'sensitive', criticality: 'high', findings: 0 },
          systemsApplications: { audited: true, scope: 'business-critical', criticality: 'medium', findings: 0 },
          systemsEndpoints: { audited: true, scope: 'sample', criticality: 'medium', findings: 0 },
          systemsCloud: { audited: false, scope: '', criticality: 'low', findings: 0 },
          // Flattened security fields
          securityAccessControl: { status: 'compliant', findings: 0, risk: 'low' },
          securityAuthentication: { status: 'compliant', findings: 0, risk: 'low' },
          securityEncryption: { status: 'non-compliant', findings: 2, risk: 'medium' },
          securityBackup: { status: 'compliant', findings: 0, risk: 'low' },
          securityMonitoring: { status: 'partially compliant', findings: 1, risk: 'medium' },
          securityIncidentResponse: { status: 'compliant', findings: 0, risk: 'low' },
          vulnerabilities: [{ 
            vulnerability: '', 
            severity: 'medium', 
            cvss: 6.5, 
            system: '', 
            impact: 'data exposure',
            exploitability: 'medium',
            remediation: '',
            timeline: '30 days'
          }],
          // Flattened compliance fields
          complianceFramework: 'ISO 27001',
          complianceRequirements: [{ requirement: '', status: 'compliant', evidence: '', notes: '' }],
          complianceGaps: [{ gap: '', severity: 'minor', action: '', deadline: '' }],
          complianceOverallStatus: 'substantially compliant',
          complianceScore: 85,
          // Flattened risks fields
          risksIdentified: [{ risk: '', probability: 'medium', impact: 'high', level: 'medium', treatment: 'mitigate' }],
          risksRegister: 'updated',
          risksAppetite: 'low',
          risksTolerance: 'medium',
          risksResidual: 'acceptable',
          // Flattened controls fields
          controlsExisting: [{ control: '', effectiveness: 'adequate', testing: 'annual', status: 'operating' }],
          controlsDeficient: [{ control: '', deficiency: '', impact: 'medium', remediation: '' }],
          controlsRecommended: [{ control: '', justification: '', cost: 0, benefit: 'high' }],
          // Flattened findings fields
          findingsCritical: 0,
          findingsHigh: 1,
          findingsMedium: 3,
          findingsLow: 5,
          findingsTotal: 9,
          findingsCategoryBreakdown: [{ category: 'access control', count: 2 }],
          recommendations: [{ 
            recommendation: '', 
            priority: 'high', 
            category: 'technical',
            effort: 'medium',
            cost: 0,
            timeline: '60 days',
            owner: '',
            benefit: 'risk reduction'
          }],
          // Flattened actionPlan fields
          actionPlanImmediate: [{ action: '', responsible: '', deadline: '', status: 'planned' }],
          actionPlanShortTerm: [{ action: '', responsible: '', deadline: '', budget: 0 }],
          actionPlanLongTerm: [{ action: '', planning: '', investment: 0 }],
          actionPlanMonitoring: [{ kpi: '', target: '', frequency: 'monthly' }],
          // Flattened timeline fields
          timelinePlanningPhase: '1 week',
          timelineFieldwork: '3 weeks',
          timelineReporting: '1 week',
          timelineFollowUp: '2 weeks',
          timelineNextAudit: '',
          // Flattened documentation fields
          documentationProcedures: true,
          documentationEvidences: true,
          documentationInterviews: true,
          documentationConfigurations: true,
          documentationLogs: true,
          documentationRetention: '7 years',
          // Flattened reporting fields
          reportingExecutiveSummary: true,
          reportingDetailedFindings: true,
          reportingTechnicalAppendix: true,
          reportingActionPlan: true,
          reportingDistribution: [{ recipient: '', level: 'confidential' }],
        };

      case 'payment-schedule':
        return {
          number: `ECH-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          status: 'pending',
          // Flattened creditor fields
          creditorName: '',
          creditorNif: '',
          // Flattened debtor fields
          debtorName: '',
          debtorContact: '',
          // Flattened schedule fields
          scheduleTotalDebt: 0,
          scheduleInvoiceNumbers: '',
          scheduleOriginalDueDate: new Date().toISOString().split('T')[0],
          scheduleSignatureDate: new Date().toISOString().split('T')[0],
          scheduleInstallmentCount: 3,
          scheduleFrequency: '',
          scheduleFirstPaymentDate: new Date().toISOString().split('T')[0],
          scheduleInstallmentAmount: 0,
          scheduleInitialPayment: 0,
          schedulePaymentMethod: '',
          schedulePenaltyRate: 0,
          scheduleGracePeriod: 5,
          scheduleStatus: '',
          scheduleGuarantees: 'aucune',
          scheduleAutomaticDefault: false,
          scheduleInterestSuspension: false,
          scheduleNotarizedAgreement: false,
          scheduleSpecialConditions: '',
          scheduleDefaultConsequences: '',
          notes: '',
          // ?? Nested object for form fields
          schedule: {
            originalDueDate: new Date().toISOString().split('T')[0],
            signatureDate: new Date().toISOString().split('T')[0],
            firstPaymentDate: new Date().toISOString().split('T')[0]
          }
        };

      // DOCUMENTS CONTRACTUELS Documents
      case 'commercial-contract':
        return {
          number: `CC-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          status: 'pending',
          // Flattened party1 fields
          party1Name: '',
          party1LegalForm: '',
          party1Nif: '',
          party1Representative: '',
          party1Title: '',
          party1Address: '',
          // Flattened party2 fields
          party2Name: '',
          party2LegalForm: '',
          party2Nif: '',
          party2Representative: '',
          party2Address: '',
          // Flattened contract fields
          contractType: 'vente',
          contractObject: '',
          contractTotalAmount: 0,
          contractCurrency: getBrandDefaultCurrency(),
          contractSignatureDate: new Date().toISOString().split('T')[0],
          contractEffectiveDate: new Date().toISOString().split('T')[0],
          contractDuration: '12_mois',
          contractExpirationDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          contractPaymentTerms: '',
          contractPenaltyRate: 0,
          contractCompetentCourt: ld('competent_court'),
          contractApplicableLaw: ld('applicable_law_value'),
          contractRenewalClause: false,
          contractConfidentialityClause: false,
          contractExclusivityClause: false,
          contractParty1Obligations: '',
          contractParty2Obligations: '',
          contractSpecialConditions: '',
          notes: '',
          // ?? Nested object for form fields
          contract: {
            signatureDate: new Date().toISOString().split('T')[0],
            effectiveDate: new Date().toISOString().split('T')[0],
            expirationDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
          }
        };
      case 'service-agreement':
        return {
          number: `CS-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          status: 'pending',
          // Flattened provider fields
          providerName: '',
          providerNif: '',
          providerAddress: '',
          // Flattened client fields
          clientName: '',
          clientSite: '',
          clientContact: '',
          // Flattened service fields
          serviceType: 'maintenance',
          serviceSlaLevel: 'standard',
          serviceResponseTime: 4,
          serviceResolutionTime: 24,
          serviceAvailability: 99,
          serviceFrequency: '',
          serviceMonthlyCost: 0,
          serviceInterventionCost: 0,
          serviceStartDate: new Date().toISOString().split('T')[0],
          serviceEndDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          serviceNoticePeriod: 30,
          serviceSlaPenalty: 0,
          servicePartsIncluded: false,
          serviceRemoteSupport: false,
          serviceEmergencyService: false,
          serviceScope: '',
          serviceExclusions: '',
          notes: '',
          // ?? Nested object for form fields
          service: {
            startDate: new Date().toISOString().split('T')[0],
            endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
          }
        };
      case 'compliance-certificate':
        return {
          number: `CRT-${Date.now()}`,
          date: new Date().toISOString().split('T')[0],
          status: 'pending',
          // Flattened certifier fields
          certifierName: '',
          certifierAccreditation: '',
          certifierAddress: '',
          // Flattened manufacturer fields
          manufacturerName: '',
          manufacturerAddress: '',
          manufacturerNif: '',
          // Flattened product fields
          productName: '',
          productModel: '',
          productSerialNumber: '',
          productManufacturingDate: new Date().toISOString().split('T')[0],
          // Flattened compliance fields
          complianceStandard: 'ISO_9001',
          complianceStandardReference: '',
          complianceCertificationType: 'produit',
          complianceCertificationDate: new Date().toISOString().split('T')[0],
          complianceExpirationDate: new Date(Date.now() + 3 * 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          complianceValidity: '3',
          complianceTestLaboratory: '',
          complianceTestReportNumber: '',
          complianceTestDate: new Date().toISOString().split('T')[0],
          complianceAuthorizedSignatory: '',
          complianceSignatoryPosition: '',
          complianceConformityDeclaration: false,
          complianceSurveillanceRequired: false,
          complianceInternationalRecognition: false,
          complianceCertifiedCharacteristics: '',
          complianceUsageConditions: '',
          notes: '',
          // ?? Nested objects for form fields
          product: {
            manufacturingDate: new Date().toISOString().split('T')[0]
          },
          compliance: {
            certificationDate: new Date().toISOString().split('T')[0],
            expirationDate: new Date(Date.now() + 3 * 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            testDate: new Date().toISOString().split('T')[0]
          }
        };

      default:
        return baseData;
    };
  };

  const handleDocumentSelect = (document, categoryId) => {
    setIsEditMode(false);
    setEditingDocument(null);
    setIsAutoGenerated(true);
    setSelectedType(document);
    const init = initializeFormData(document.id);
    if (document.id === 'credit-note' || document.id === 'credit_note') {
      init.creditNoteParty = categoryId === 'purchases' ? 'supplier' : 'client';
    }
    if (document.id === 'simplified-invoice') {
      init.zatcaInvoiceSubtype = 'simplified';
      init.invoiceType = 'simplified';
      init.zatcaProfile = 'b2c';
    }
    if (document.id === 'debit-note' || document.id === 'debit_note') {
      init.debitNote = true;
      init.creditNoteParty = 'client';
    }
    setFormData(init);
    setCurrentStep('create');
  };

  const handleInputChange = (field, value) => {
    // ?? If user manually edits notes/requirements field, mark as NOT auto-generated
    const noteFields = ['notes', 'requirements', 'conditions', 'terms', 'deliveryNotes', 'instructions'];
    if (noteFields.includes(field)) {
      setIsAutoGenerated(false);
    }
    if (SUPPLEMENTARY_SECTION_TOGGLE_FIELDS.includes(field)) {
      setIsAutoGenerated(true);
    }

    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };

      if (field === 'originalInvoice' && value && typeof value === 'object') {
        newData.originalInvoiceNumber = value.number != null ? String(value.number) : '';
        newData.originalInvoiceDate = value.date != null ? String(value.date).trim() : '';
        const amt = Number(value.amount);
        newData.originalInvoiceAmount = Number.isFinite(amt) ? amt : 0;
      }

      if (field === 'creditNote' && value && typeof value === 'object') {
        if (value.type != null && String(value.type).trim() !== '') {
          newData.creditNoteType = String(value.type).trim();
        }
        if (value.refundMethod != null && String(value.refundMethod).trim() !== '') {
          newData.creditNoteRefundMethod = String(value.refundMethod).trim();
        }
        if (value.applicationDate != null && String(value.applicationDate).trim() !== '') {
          newData.creditNoteApplicationDate = String(value.applicationDate).trim();
        }
      }

      // If client changed, recalculate all item prices to apply preferential rate based on client type
      if (field === 'client' && prev.items) {
        // We need to use the new client ID (value) not the previous one
        const selectedClient = clients.find(c => c.id.toString() === String(value));
        let preferentialRate = 0;
        
        // Get client type discount from settings
        if (selectedClient && selectedClient.type) {
          preferentialRate = getClientTypePreferentialRate(selectedClient.type, settings);
          console.log("Applied preferential rate:", preferentialRate);
        }
        
        // Recalculate all item totals with the preferential rate
        const newItems = prev.items.map(item => {
          const basePrice = item.price || 0;
          const discountedPrice = basePrice * (1 - (preferentialRate / 100));
          return {
            ...item,
            originalPrice: basePrice,
            discountedPrice: discountedPrice,
            total: (item.quantity || 1) * discountedPrice
          };
        });
        
        newData.items = newItems;
      }

      return newData;
    });
  };

  // ?? Drag & Drop Handlers for Section Reordering
  const handleDragStart = (e, section) => {
    setDraggedSection(section);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetSection) => {
    e.preventDefault();
    if (!draggedSection || draggedSection === targetSection) return;
    
    const newOrder = [...sectionOrder];
    const draggedIndex = newOrder.indexOf(draggedSection);
    const targetIndex = newOrder.indexOf(targetSection);
    
    // Remove dragged item and insert at target position
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedSection);
    
    setSectionOrder(newOrder);
    setIsAutoGenerated(true);
    setDraggedSection(null);
  };

  const handleDragEnd = () => {
    setDraggedSection(null);
  };

  // ?? Drag & Drop Handlers for Tax Reordering
  const handleTaxDragStart = (e, taxElement) => {
    setDraggedTax(taxElement);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleTaxDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleTaxDrop = (e, targetTax) => {
    e.preventDefault();
    if (!draggedTax || draggedTax === targetTax) return;
    
    const newOrder = [...taxOrder];
    const draggedIndex = newOrder.indexOf(draggedTax);
    const targetIndex = newOrder.indexOf(targetTax);
    
    // Remove dragged item and insert at target position
    newOrder.splice(draggedIndex, 1);
    newOrder.splice(targetIndex, 0, draggedTax);
    
    setTaxOrder(newOrder);
    setDraggedTax(null);
  };

  const handleTaxDragEnd = () => {
    setDraggedTax(null);
  };

  // ?? Helper function to get signature name options based on document type and signature section
  const getSignatureNameOptions = (signatureType) => {
    if (!selectedType) return [];
    
    const docType = selectedType.id;
    const creditNoteIsSupplier =
      (docType === 'credit-note' || docType === 'credit_note') &&
      formData.creditNoteParty === 'supplier';
    
    // For "Établie par" - always facturiers (who create documents)
    if (signatureType === 'etabliePar') {
      return availableFacturiersForDropdown;
    }
    
    // For "Bon pour" - ALWAYS EMPLOYEES (internal approval by managers/directors)
    if (signatureType === 'bonPour') {
      return availableEmployeesForDropdown;
    }
    
    // For "Reçue par" ONLY - depends on document type
    const clientDocuments = ['quote', 'proforma', 'invoice', 'delivery-note', 'delivery', 'payment-receipt', 'credit-note', 'credit_note'];
    const supplierDocuments = ['quote-request', 'proforma-request', 'proforma_request', 'purchase-order', 'purchase_order', 'return-slip', 'supplier-return', 'payment-voucher'];
    const internalDocuments = ['receipt-note', 'reception-note', 'stock-entry', 'client-return-slip'];
    const variableDocuments = ['shipping-slip', 'shipping', 'exit-note', 'exit_slip', 'transfer-note', 'transfer_slip'];
    
    if (supplierDocuments.includes(docType) || creditNoteIsSupplier) {
      return availableSuppliersForDropdown
        .filter((s) => s.destinataire_name || s.contact)
        .map((s) => ({
          value: s.destinataire_name || s.contact,
          label: s.destinataire_name || s.contact,
          destinataire_fonction: s.destinataire_fonction || s.contact_fonction,
        }));
    } else if (clientDocuments.includes(docType)) {
      return availableClientsForDropdown
        .filter((c) => c.destinataire_name || c.contact)
        .map((c) => ({
          value: c.destinataire_name || c.contact,
          label: c.destinataire_name || c.contact,
          destinataire_fonction: c.destinataire_fonction || c.contact_fonction,
        }));
    } else if (internalDocuments.includes(docType)) {
      return availableEmployeesForDropdown;
    } else if (variableDocuments.includes(docType)) {
      // For variable documents, combine all options
      return [
        ...availableClientsForDropdown.filter(c => c.destinataire_name).map(c => ({ 
          value: c.destinataire_name, 
          label: `${c.destinataire_name} ${formLabel('partner_suffix_client')}`,
          destinataire_fonction: c.destinataire_fonction
        })),
        ...availableSuppliersForDropdown.filter(s => s.destinataire_name).map(s => ({ 
          value: s.destinataire_name, 
          label: `${s.destinataire_name} ${formLabel('partner_suffix_supplier')}`,
          destinataire_fonction: s.destinataire_fonction
        })),
        ...availableEmployeesForDropdown.map(e => ({ 
          value: e.value, 
          label: `${e.label} ${formLabel('partner_suffix_employee')}`,
          position: e.position
        }))
      ];
    } else {
      // Default: employees
      return availableEmployeesForDropdown;
    }
  };

  // ?? Helper function to get auto-populated name for "Reçue par" based on document type
  const findPartnerForRecuePar = useCallback(() => {
    if (!selectedType) return null;
    const isSupplier = resolveIsSupplierDocument(selectedType.id, formData);
    if (isSupplier && formData.supplier) {
      return (
        availableSuppliersForDropdown.find((s) => String(s.id) === String(formData.supplier)) ||
        (allSuppliers || []).find((s) => String(s.id) === String(formData.supplier)) ||
        null
      );
    }
    if (formData.client) {
      return (
        availableClientsForDropdown.find((c) => String(c.id) === String(formData.client)) ||
        (allClients || []).find((c) => String(c.id) === String(formData.client)) ||
        null
      );
    }
    return null;
  }, [
    selectedType,
    formData.supplier,
    formData.client,
    formData.creditNoteParty,
    availableSuppliersForDropdown,
    availableClientsForDropdown,
    allSuppliers,
    allClients,
  ]);

  const getAutoPopulatedRecipientName = useCallback(() => {
    return resolvePartnerDestinataire(findPartnerForRecuePar()).name;
  }, [findPartnerForRecuePar]);

  // ?? Helper function to auto-populate recipient role (fonction) from client/supplier
  const getAutoPopulatedRecipientRole = useCallback(() => {
    return resolvePartnerDestinataire(findPartnerForRecuePar()).role;
  }, [findPartnerForRecuePar]);

  /** Reçue par ← destinataire du client/fournisseur sélectionné (temps réel). */
  useEffect(() => {
    if (!formData.showRecuePar) return;
    const partner = findPartnerForRecuePar();
    if (!partner) return;
    const { name, role } = resolvePartnerDestinataire(partner);
    setFormData((prev) => {
      if (prev.recueParName === name && prev.recueParRole === role) return prev;
      return { ...prev, recueParName: name, recueParRole: role };
    });
  }, [
    formData.showRecuePar,
    formData.supplier,
    formData.client,
    formData.creditNoteParty,
    findPartnerForRecuePar,
    availableSuppliersForDropdown,
    availableClientsForDropdown,
    allSuppliers,
    allClients,
  ]);

  /** Établi par ← facturier « Établi par » du haut (temps réel). */
  useEffect(() => {
    if (!formData.showEtabliePar) return;
    const name = String(formData.issuedBy || '').trim();
    if (!name) return;
    setFormData((prev) => {
      if (prev.etablieParName === name) return prev;
      return { ...prev, etablieParName: name };
    });
  }, [formData.showEtabliePar, formData.issuedBy]);

  // ?? Helper function to check if tax element is available for current document type
  const isTaxAvailable = (taxElement) => {
    if (!selectedType) return false;
    return getTaxAvailability(selectedType.id)(taxElement);
  };

  const handleItemChange = (index, field, value) => {
    setFormData(prev => {
      // Handle different item types
      if (prev.items) {
        const newItems = [...prev.items];
        
        // Validate numeric fields to prevent NaN
        let validatedValue = value;
        if (field === 'quantity' || field === 'price') {
          const numValue = parseFloat(value);
          validatedValue = isNaN(numValue) || numValue < 0 ? 0 : numValue;
        }
        
        newItems[index] = {
          ...newItems[index],
          [field]: validatedValue
        };

        // Find the selected client to check for preferential rate based on client type
        const selectedClient = clients.find(c => c.id.toString() === String(prev.client));
        let preferentialRate = 0;
        
        // Get client type discount from settings
        if (selectedClient && selectedClient.type) {
          preferentialRate = getClientTypePreferentialRate(selectedClient.type, settings);
        }

        // Recalculate item total with preferential rate discount if applicable
        if (field === 'quantity' || field === 'price') {
          const basePrice = parseFloat(newItems[index].price) || 0;
          const quantity = parseFloat(newItems[index].quantity) || 0;
          // Apply preferential rate discount if it exists
          const discountedPrice = basePrice * (1 - (preferentialRate / 100));
          // Store both the original price and the discounted price
          newItems[index].originalPrice = basePrice;
          newItems[index].discountedPrice = discountedPrice;
          // Apply the discount directly to the total
          newItems[index].total = quantity * discountedPrice;
        }

        return {
          ...prev,
          items: newItems
        };
      } else if (prev.specifications) {
        const newSpecs = [...prev.specifications];
        newSpecs[index] = {
          ...newSpecs[index],
          [field]: value
        };
        return { ...prev, specifications: newSpecs };
      } else if (prev.packages) {
        const newPackages = [...prev.packages];
        newPackages[index] = {
          ...newPackages[index],
          [field]: value
        };
        return { ...prev, packages: newPackages };
      }
      
      return prev;
    });
  };
  const addItem = () => {
    setFormData(prev => {
      // Check which type of items array this document uses
      if (prev.items) {
        // Check for preferential rate based on client type
        const selectedClient = clients.find(c => c.id.toString() === String(prev.client));
        let preferentialRate = 0;
        
        // Get client type discount from settings (legacy bridge via syncFacturationConfigToSettings)
        if (selectedClient && selectedClient.type) {
          preferentialRate = getClientTypePreferentialRate(selectedClient.type, settings);
        }

        // New item with zero total (will be calculated if price changes)
        const emptyQtyShippingSlip =
          selectedType?.id === 'shipping-slip' || selectedType?.id === 'shipping';
        const newItem = { 
          product: '', 
          quantity: emptyQtyShippingSlip ? '' : 1, 
          price: 0, 
          unit: facturationConfig.defaultUnit || '', 
          total: 0,
          // Add these properties for consistency with existing items
          originalPrice: 0,
          discountedPrice: preferentialRate > 0 ? 0 : undefined
        };

        // ?? Store the item type (product or service) for the new item
        const newIndex = prev.items.length;
        setItemTypes(prevTypes => ({
          ...prevTypes,
          [newIndex]: itemType // Store current itemType (product/service)
        }));

        return {
          ...prev,
          items: [...prev.items, newItem]
        };
      } else if (prev.specifications) {
        return {
          ...prev,
          specifications: [...prev.specifications, { description: '', quantity: 1, requirements: '', budget: 0 }]
        };
      } else if (prev.packages) {
        return {
          ...prev,
          packages: [...prev.packages, { type: 'carton', quantity: 1, weight: 0, dimensions: '', contents: '', value: 0 }]
        };
      } else {
        // Default for documents that use items
        return {
          ...prev,
          items: [{ product: '', quantity: 1, price: 0, unit: '', total: 0 }]
        };
      }
    });
  };

  const removeItem = (index) => {
    setFormData(prev => {
      if (prev.items && prev.items.length > 1) {
        // ?? Remove item type from itemTypes and reindex remaining items
        setItemTypes(prevTypes => {
          const newTypes = {};
          Object.keys(prevTypes).forEach(key => {
            const keyIndex = parseInt(key);
            if (keyIndex < index) {
              newTypes[keyIndex] = prevTypes[keyIndex];
            } else if (keyIndex > index) {
              newTypes[keyIndex - 1] = prevTypes[keyIndex]; // Shift down
            }
          });
          return newTypes;
        });
        
        return {
          ...prev,
          items: prev.items.filter((_, i) => i !== index)
        };
      } else if (prev.specifications && prev.specifications.length > 1) {
        return {
          ...prev,
          specifications: prev.specifications.filter((_, i) => i !== index)
        };
      } else if (prev.packages && prev.packages.length > 1) {
        return {
          ...prev,
          packages: prev.packages.filter((_, i) => i !== index)
        };
      }
      return prev; // Don't remove if only one item
    });
  };

  const handleProductSelect = (index, productId) => {
    // Trouver le produit en comparant les identifiants comme chaînes pour éviter les problèmes de type
    const product = activeProducts.find(p => p.id.toString() === productId.toString());
    
    if (product && formData.items) {
      const newItems = [...formData.items];
      const prevQty = newItems[index].quantity;
      const parsedQty = parseFloat(prevQty);
      // Bon de commande, bordereau retour, etc. : si pas de quantité saisie, défaut 1 au choix catalogue
      const quantity =
        prevQty === '' || prevQty == null || Number.isNaN(parsedQty) || parsedQty <= 0
          ? 1
          : parsedQty;
      newItems[index] = {
        ...newItems[index],
        product: productId, // Store as string to match select value
        name: product.name || '', // Add product name explicitly for Archives display
        designation: product.name || '', // Add product name as designation
        category: product.category || '', // Add product category
        reference: product.sku || '', // Add product SKU/reference explicitly 
        price: product.price,
        quantity,
        unit: canonicalizeMeasureUnit(product.unit || '') || product.unit || '', // Unitaire unifiée (code canonique)
        // Copy fabrication-specific fields for proper table display
        longueur: product.longueur || '', // Copy length from product
        remarque: product.remarque || '', // Copy remarks from product
        total: quantity * (parseFloat(product.price) || 0),
        // Clear any manual product data when selecting from dropdown
        manualProductName: '',
        manualPrice: '',
        manualUnit: ''
      };

      // Update form data
      setFormData({
        ...formData,
        items: newItems
      });
    }
  };

  const handleManualProductChange = (index, field, value) => {
    if (formData.items) {
      const newItems = [...formData.items];
      
      // Log the input to help debug
      console.log(`Manual product ${field} change: ${value}`);
      
      // Update the specific field in the manual product data
      const key = field === 'name' ? 'manualProductName' : `manual${field.charAt(0).toUpperCase() + field.slice(1)}`;
      newItems[index] = {
        ...newItems[index],
        [key]: value
      };

      // If manual name is filled, use it as the actual product data
      if (value.trim() !== '') {
        newItems[index] = {
          ...newItems[index],
          product: '', // Clear any selected product
          name: value,
          designation: value,
          // Use the price and quantity from the form fields
          total: newItems[index].quantity * (parseFloat(newItems[index].price) || 0)
        };
        
        console.log('Updated item with manual name:', newItems[index]);
      }

      // Update form data
      setFormData({
        ...formData,
        items: newItems
      });
    }
  };
  const handleSave = async () => {
    // Validation des données minimales
    if (!formData.number) {
      alert(t('document_creation.alerts.enter_document_number'));
      return;
    }

    if (!formData.date) {
      alert(t('document_creation.alerts.enter_date'));
      return;
    }

    const isSupplierDocument = resolveIsSupplierDocument(selectedType.id, formData);

    if (facturationConfig.requireClient !== false) {
      if (isSupplierDocument) {
        if (!formData.supplier) {
          alert(t('document_creation.alerts.select_supplier'));
          return;
        }
      } else if (!formData.client) {
        alert(t('document_creation.alerts.select_client'));
        return;
      }
    }

    const lineItemDocTypes = ['invoice', 'proforma', 'quote', 'purchase-order', 'delivery-note', 'shipping-slip', 'credit-note'];
    if (facturationConfig.requireLineItems !== false && lineItemDocTypes.includes(selectedType.id)) {
      const hasLineItems = Array.isArray(formData.items) && formData.items.some(
        (item) => item && (item.product || item.name || item.description),
      );
      if (!hasLineItems) {
        alert(t('document_creation.alerts.add_products'));
        return;
      }
    }

    if (facturationConfig.requireApproval === true) {
      const finalizedStatuses = ['approved', 'sent', 'paid', 'validated', 'completed', 'finalized'];
      const currentStatus = String(formData.status || 'pending').toLowerCase();
      if (finalizedStatuses.includes(currentStatus)) {
        alert(t('document_creation.alerts.require_approval', { defaultValue: 'يجب الموافقة على المستند قبل تفعيل هذه الحالة' }));
        return;
      }
    }

    // ?? RESTRICTIONS REMOVED: Allow saving without products/items
    // Pour les documents de facturation, vérification des articles
    // if (['invoice', 'proforma', 'quote', 'purchase-order', 'delivery-note', 'shipping-slip'].includes(selectedType.id)) {
    //   if (!formData.items || formData.items.length === 0 || (!formData.items[0].product && !formData.items[0].name)) {
    //     alert(t('document_creation.alerts.add_products'));
    //     return;
    //   }
    // }

    // Récupération des données de la partie contractante (client ou fournisseur) — liste complète du contexte
    const selectedPartner = resolveDocumentPartner(formData, isSupplierDocument, allClients, allSuppliers);

    try {
      // Only increment counter for new documents (not when editing)
      if (!isEditMode) {
        incrementDocumentCounter(selectedType.id);
      }

      // Préparer les données pour l'archivage
      // Convert hyphenated types to underscore format for consistency
      const normalizeType = (type) => {
        // Map from hyphenated to underscore format
        const typeMap = {
          'quote-request': 'quote_request',
          'proforma-request': 'proforma_request',
          'purchase-order': 'purchase_order',
          'delivery-note': 'delivery',
          'shipping-slip': 'shipping',
          'material-exit': 'delivery',
          'reception-note': 'reception_note',
          'return-slip': 'return_slip',
          'payment-voucher': 'payment_voucher',
          'payment_slip': 'payment_voucher',
          'payment_voucher': 'payment_voucher',
          'credit-note': 'credit_note',
          'exit-note': 'exit_note',
          'exit_slip': 'exit_note',
          'exit-slip': 'exit_note',
          'transfer-note': 'transfer_note',
          'transfer_slip': 'transfer_note',
          'transfer-slip': 'transfer_note',
          'stock-entry': 'stock_entry',
          'shipping': 'shipping',
          'payment-receipt': 'payment_receipt',
          'payment_receipt': 'payment_receipt',
        };

        return typeMap[type] || type;
      };

      // Format supplier/client number for storage
      const formatPartnerNumber = (partner, isSupplier) => {
        if (!partner) return '';
        const stored = resolvePartnerReferenceCode(partner, { isSupplier });
        if (stored) return stored;

        if (isSupplier) {
          const supplierIndex = suppliers.findIndex((s) => s.id === partner.id);
          const number = String(supplierIndex >= 0 ? supplierIndex + 1 : 1).padStart(2, '0');
          return `FRN-${number}`;
        }

        const clientIndex = allClients.findIndex((c) => c.id === partner.id);
        const number = String(clientIndex >= 0 ? clientIndex + 1 : 1).padStart(2, '0');
        return `CLI-${number}`;
      };
      
      const archiveDocType = (() => {
        const sid = selectedType.id;
        if (sid === 'payment-receipt' || sid === 'payment_receipt') return 'payment_receipt';
        if (sid === 'payment-voucher' || sid === 'payment_slip' || sid === 'payment_voucher') return 'payment_voucher';
        return normalizeType(sid);
      })();

      const paymentReceiptHeaderFromLines =
        archiveDocType === 'payment_receipt'
          ? syncPaymentReceiptHeaderFromLines(formData.paymentLines || [])
          : null;

      const partnerDisplayName =
        (isSupplierDocument
          ? formData.supplierName ||
            selectedPartner.company_name ||
            selectedPartner.name
          : formData.clientName ||
            selectedPartner.company_name ||
            selectedPartner.name) || '';

      const documentData = {
        type: archiveDocType,
        number: formData.number,
        title: generateDocumentTitle(formData, selectedType, i18n.language),
        client: partnerDisplayName,
        clientId: selectedPartner.id,
        ...(isSupplierDocument
          ? {
              supplierId: selectedPartner.id,
              supplierName: partnerDisplayName,
            }
          : {
              clientName: partnerDisplayName,
            }),
        // Store the correctly formatted partner number
        partnerNumber: formatPartnerNumber(selectedPartner, isSupplierDocument),
        // For client-side documents, also store clientCode (used by older templates)
        ...(isSupplierDocument ? {} : { clientCode: formatPartnerNumber(selectedPartner, false) }),
        // Also store supplier/client type flags for clarity
        isSupplier: isSupplierDocument,
        date: formData.date,
        dueDate: formData.dueDate || formData.validUntil,
        amount: formData.total || 0,
        // Canonical archive status — do not let proformaStatus override other document types (see proforma fields block below).
        status:
          archiveDocType === 'proforma'
            ? normalizeDocumentStatus(
                'proforma',
                formData.proformaStatus || formData.status || 'pending',
              )
            : normalizeDocumentStatus(
                selectedType.id,
                formData.status || 'pending',
              ),
        category: selectedType.category || 'FACTURATION',
        items: formData.items,
        // Include specifications for quote-request documents
        specifications: formData.specifications || [],
        
        // Add document detail fields for consistent display
        paymentTerms: formData.paymentTerms || formData.payment_terms || '',
        deliveryDate: formData.deliveryDate || '',
        // Store delivery location in multiple formats for maximum compatibility
        deliveryLocation:
          formData.deliveryLocation ||
          formData.deliveryPlace ||
          formData.deliveryAddress ||
          formData.lieu ||
          '',
        delivery_location:
          formData.deliveryLocation ||
          formData.deliveryPlace ||
          formData.deliveryAddress ||
          formData.lieu ||
          '',
        deliveryPlace:
          formData.deliveryPlace ||
          formData.deliveryLocation ||
          formData.deliveryAddress ||
          formData.lieu ||
          '',
        delivery_place:
          formData.deliveryPlace ||
          formData.deliveryLocation ||
          formData.deliveryAddress ||
          formData.lieu ||
          '',
        deliveryAddress:
          formData.deliveryLocation ||
          formData.deliveryPlace ||
          formData.deliveryAddress ||
          formData.lieu ||
          '',
        lieu_livraison:
          formData.deliveryLocation ||
          formData.deliveryPlace ||
          formData.deliveryAddress ||
          formData.lieu ||
          '',
        createdBy: formData.issuedBy || formData.createdBy || '',
        buyer: formData.buyer || formData.acheteur || '',
        
        // Quote request specific fields
        requestType: formData.requestType || '',
        responseDate: formData.responseDate || '',
        desiredStartDate: formData.desiredStartDate || '',
        approximateBudget: formData.approximateBudget || 0,
        maxBudget: formData.maxBudget || '',
        desiredPaymentTerms:
          selectedType.id === 'quote-request' || selectedType.id === 'proforma-request'
            ? (formData.preferredPaymentTerms || formData.desiredPaymentTerms || '')
            : selectedType.id === 'purchase-order'
              ? (formData.paymentTerms ||
                  formData.paymentConditions ||
                  formData.preferredPaymentTerms ||
                  formData.desiredPaymentTerms ||
                  '')
              : (formData.paymentConditions ||
                  formData.preferredPaymentTerms ||
                  formData.desiredPaymentTerms ||
                  ''),
        estimatedDeliveryTime: formData.estimatedDeliveryTime || '',
        requiredDocuments: formData.requiredDocuments || '',
        desiredDeliveryTime: formData.desiredDeliveryTime || '',
        projectType: formData.projectType || '',
        projectReference: formData.projectReference || '',
        commercial: formData.salesPerson || formData.commercial || '',
        priority: formData.priority ?? '',
        
        // Return slip specific fields
        // Return fields are now flat (no nested object)
        // Also store return fields at the top level for archives display
        returnDate: formData.returnDate || '',
        returnReason: serializeReturnReasonValue(formData.returnReason),
        returnType: formData.returnType || '',
        returnCarrier: formData.returnCarrier || '',
        returnTrackingNumber: formData.returnTrackingNumber || '',
        returnCondition: formData.returnCondition || '',
        returnInspector: formData.returnInspector || '',
        returnWarehouse: formData.returnWarehouse || '',
        returnOriginalPurchaseOrder: formData.returnOriginalPurchaseOrderRef || '',
        returnOriginalDelivery: formData.returnOriginalDeliveryRef || '',
        returnOriginalInvoice: formData.returnOriginalInvoiceRef || '',
        returnOriginalInvoiceDate: formData.returnOriginalInvoiceDate || '',
        returnRMANumber: formData.returnRMANumber || '',
        
        // Reception note specific fields
        // Reception fields are now flat (no nested object)
        // Also store reception fields at the top level for archives display
        receptionAddress: formData.receiverAddress || '',
        receiverAddress: formData.receiverAddress || '',
        reception_address: formData.receiverAddress || '',
        deliveryNoteReference: formData.receptionDeliveryNoteRef || '',  // Backend mapping
        purchaseOrderReference: formData.receptionOrderRef || '',  // Backend mapping
        receptionDate: formData.receptionDate || '',
        expectedDeliveryDate: formData.expectedReceptionDate || formData.expectedDeliveryDate || '',
        receptionTime: formData.receptionTime || '',
        receivedByName: formData.recueParName || formData.receptionReceivedBy || '',
        controlledByName: formData.receptionControlledBy || '',  // Backend mapping
        generalCondition: formData.receptionOverallCondition || '',  // Backend mapping
        quantityConformity: formData.receptionQuantityCompliance || '',  // Backend mapping
        qualityConformity: formData.receptionQualityCompliance || '',  // Backend mapping
        stockDestination: formData.receptionStockDestination || '',
        receptionStatus: formData.receptionStatus || '',
        
        // Invoice specific fields
        paymentMethod: formData.paymentMethod || '',
        paymentConditions: formData.paymentConditions || '',
        dueDate: formData.dueDate || '',
        issuedBy: formData.issuedBy || '',
        salesPerson: formData.salesPerson || '',
        bankDetails: formData.bankDetails || '',
        amountInWords: formData.amountInWords || '',
        invoiceType: formData.invoiceType ?? '',
        priority: formData.priority ?? '',
        orderReference: formData.orderReference || '',
        purchaseOrderReference: formData.orderReference || '',  // Backend mapping
        deliveryReference: formData.deliveryReference || '',
        deliveryNoteReference: formData.deliveryReference || '',  // Backend mapping
        projectNumber: formData.projectNumber || '',
        customerOrderReference: formData.customerOrderReference || '',
        originalDocumentReference: formData.customerOrderReference || '',  // Backend mapping (temporary)
        
        // Proforma specific fields
        validUntil: formData.validUntil || '',
        estimatedDelivery: formData.estimatedDelivery || '',
        paymentMethod: formData.plannedPaymentMethod || formData.paymentMethod || '',
        dueDate: formData.paymentDueDate || formData.dueDate || '',
        downPayment: formData.downPaymentPercent || formData.downPayment || 0,
        deliveryTerms: formData.deliveryTerms || '',
        guarantees: formData.warranty || formData.guarantees || '',
        
        // Quote specific fields
        deliveryTime: formData.deliveryTime || '',
        projectType: formData.projectType || '',
        projectReference: formData.projectReference || '',
        paymentConditions: formData.paymentConditions || '',
        conditions: formData.conditions || '',
        commercial: formData.salesPerson || formData.commercial || '',
        priority: formData.priority ?? '',
        
        // Shipping slip specific fields
        // Shipment fields are now flat (no nested object)
        // Also store shipment fields at the top level for archives display
        shipmentDate: formData.shipmentDate || '',
        shipmentMethod: formData.shipmentMethod || '',
        trackingNumber: formData.shipmentTrackingNumber || '',
        totalWeight: formData.shipmentTotalWeight || '',
        packageCount: formData.shipmentPackageCount || '',
        packageType: formData.shipmentPackageType || '',
        instructions: formData.instructions || '',
        
        // Delivery note specific fields
        // Delivery fields are now flat (no nested object)
        // Also store delivery fields at the top level for archives display
        priority: formData.priority ?? '',
        quoteReference: formData.quoteReference || '',
        originalDocumentReference: formData.quoteReference || '',  // Backend mapping for quote reference
        orderReference: formData.deliveryOrderReference || formData.orderReference || '',
        purchaseOrderReference: formData.orderReference || '',  // Backend mapping
        invoiceReference: formData.invoiceReference || '',
        departureWarehouse: formData.departureWarehouse || '',
        originWarehouseName: formData.departureWarehouse || '',  // Backend mapping
        deliveryDate: formData.deliveryDate || '',
        deliveryTime: formData.deliveryTime || '',
        carrier: formData.deliveryCarrier || formData.carrier || formData.transporterName || '',
        transporterName: formData.transporterName || '',
        driver: formData.deliveryDriver || formData.driver || '',
        vehicleType: formData.deliveryVehicleType || formData.vehicleType || '',
        vehicleRegistration: formData.deliveryVehicleRegistration || formData.vehicleRegistration || '',
        deliveryConditions: formData.deliveryConditions || '',
        deliveryAddress: formData.deliveryAddress || formData.recipientAddress || '',
        deliveryMethod: formData.deliveryMethod || '',
        trackingNumber: formData.trackingNumber || '',
        carrierTrackingNumber: formData.carrierTrackingNumber || '',
        shipmentPackageCount: formData.shipmentPackageCount || 0,
        packageCount: formData.shipmentPackageCount || formData.shipment?.packageCount || 0,
        
        // Sender and recipient information for delivery note
        // Sender fields are now flat (no nested object)
        senderName: formData.senderName || '',
        senderAddress: formData.senderAddress || '',
        // Recipient fields are now flat (no nested object)
        recipientName: formData.recipientName || '',
        recipientAddress: formData.recipientAddress || '',
        
        // Payment receipt specific fields
        // Payment fields are now flat (no nested object)
        priority: formData.priority ?? '',
        paymentDate: formData.paymentDate || formData.date || '',
        paymentMethod: formData.paymentMethod || '',
        paymentReference: formData.paymentReference || '',
        transactionNumber: formData.paymentReference || '',  // Backend mapping
        paymentBank: formData.paymentBank || '',
        bankName: formData.paymentBank || '',  // Backend mapping
        paymentSettlementType: formData.paymentSettlementType || '',
        paymentType: formData.paymentSettlementType || '',  // Backend mapping
        paymentInvoiceNumbers:
          paymentReceiptHeaderFromLines?.paymentInvoiceNumbers ||
          formData.paymentInvoiceNumbers ||
          '',
        invoicesPaid:
          paymentReceiptHeaderFromLines?.paymentInvoiceNumbers ||
          formData.paymentInvoiceNumbers ||
          '',  // Backend mapping
        receiptReceivedBy: formData.receiptReceivedBy || '',
        receivedBy: formData.receiptReceivedBy || '',  // Backend mapping
        
        // Payment voucher and payment receipt specific fields
        // Payment lines table (used by both payment-voucher and payment-receipt)
        paymentLines: formData.paymentLines || [],
        checkNumber: formData.checkNumber || '',
        voucherReceivedBy: formData.receivedBy || '',
        
        // Client return slip specific fields
        // ClientReturn fields are now flat (no nested object)
        priority: formData.priority ?? '',
        clientReturnWarehouse: formData.clientReturnWarehouse || '',
        clientReturnOriginalPurchaseOrderRef: formData.clientReturnOriginalPurchaseOrderRef || '',
        clientReturnOriginalDeliveryRef: formData.clientReturnOriginalDeliveryRef || '',
        clientReturnOriginalInvoiceRef: formData.clientReturnOriginalInvoiceRef || '',
        clientReturnReturnDate: formData.clientReturnReturnDate || '',
        clientReturnReason: formData.clientReturnMotif || formData.clientReturnReason || '',
        clientReturnType: formData.clientReturnType || '',
        clientReturnCarrier: formData.clientReturnCarrier || '',
        clientReturnTrackingNumber: formData.clientReturnTrackingNumber || '',
        clientReturnCondition: formData.clientReturnCondition || '',
        clientReturnControlledBy: formData.clientReturnControlledBy || '',
        clientReturnStatus: formData.clientReturnStatus || '',
        clientReturnMotif: formData.clientReturnMotif || '',
        
        // Credit note specific fields — flat (+ approvedByName SQLite, voir buildCreditNoteArchivePayload)
        ...buildCreditNoteArchivePayload(formData),
        
        // Stock entry specific fields
        // Stock entry fields are now flat (no nested object)
        entryDate: formData.stockEntryEntryDate || '',
        entryTime: formData.stockEntryEntryTime || '',
        warehouseName: formData.stockEntryWarehouse || '',  // Backend mapping
        entrySource: formData.stockEntrySource || '',
        entryDocumentReference: formData.stockEntryDocumentReference || '',
        supplierName: formData.stockEntrySupplier || '',  // Backend mapping
        receivedByName: formData.stockEntryReceivedBy || '',  // Backend mapping
        controlledByName: formData.stockEntryControlledBy || '',  // Backend mapping
        goodsCondition: formData.stockEntryCondition || '',  // Backend mapping
        
        // Exit note specific fields
        // Exit note fields are now flat (no nested object)
        exitNoteClientOrSupplier: formData.exitNoteClientOrSupplier || '',
        exitNoteExpectedDate: formData.exitNoteExpectedDate || '',
        originWarehouseName: formData.exitNoteOriginWarehouse || '',  // Backend mapping
        exitNoteHandler: formData.exitNoteHandler || '',
        approvedByName: formData.exitNoteAuthorizedBy || '',  // Backend mapping
        exitNoteStatus: formData.exitNoteStatus || '',
        purchaseOrderReference: formData.exitNoteClientOrderRef || '',  // Backend mapping
        invoiceReference: formData.exitNoteInvoiceRef || '',  // Backend mapping
        deliveryNoteReference: formData.exitNoteDeliveryNoteRef || '',  // Backend mapping
        exitNoteTransferNoteRef: formData.exitNoteTransferNoteRef || '',
        exitNoteDestination: formData.exitNoteDestination || '',
        destinationWarehouseName: formData.exitNoteDestinationWarehouse || '',  // Backend mapping
        exitNoteTransportMode: formData.exitNoteTransportMode || '',
        exitNoteExitTime: formData.exitNoteExitTime || '',
        exitNoteDocumentReference: formData.exitNoteDocumentReference || '',
        exitNoteRecipient: formData.exitNoteRecipient || '',
        
        // Transfer note specific fields
        // Transfer note fields are now flat (no nested object)
        transferNoteExpectedDate: formData.transferNoteExpectedDate || '',
        originWarehouseName: formData.transferNoteOriginWarehouse || '',  // Backend mapping
        destinationWarehouseName: formData.transferNoteDestinationWarehouse || '',  // Backend mapping
        transferNoteDocumentReference: formData.transferNoteDocumentReference || '',
        transferNoteTransferRequestRef: formData.transferNoteTransferRequestRef || '',
        transferNoteRequestedBy: formData.transferNoteRequestedBy || '',
        transferNoteHandler: formData.transferNoteHandler || '',
        approvedByName: formData.transferNoteApprovedBy || '',  // Backend mapping
        transferNoteStatus: formData.transferNoteStatus || '',
        transferNoteTransportMode: formData.transferNoteTransportMode || '',
        departureTime: formData.transferNoteDepartureTime || '',  // Backend mapping
        arrivalTime: formData.transferNoteArrivalTime || '',  // Backend mapping
        trackingNumber: formData.transferNoteTrackingNumber || '',  // Backend mapping
        
        // Shipping slip specific fields
        // Shipping fields are already flat (no nested object)
        shipmentDate: formData.shipmentDate || '',
        originWarehouseName: formData.shipmentOriginWarehouse || '',  // Backend mapping
        shipmentMode: formData.shipmentMethod || '',  // Backend mapping
        carrier: formData.shipmentCarrier || '',  // Backend mapping
        carrierTrackingNumber: formData.shipmentCarrierTrackingNumber || '',
        carrier_tracking_number: formData.shipmentCarrierTrackingNumber || '',
        trackingNumber: formData.shipmentTrackingNumber || '',  // Backend mapping
        shipmentPriority: formData.shipmentPriority || '',
        packageType: formData.shipmentPackageType || '',  // Backend mapping
        productType: formData.shipmentProductType || '',  // Backend mapping
        packageCount: formData.shipmentPackageCount ?? null,
        weight: formData.shipmentTotalWeight ?? null,
        volume: formData.shipmentVolume ?? null,
        declaredValue: formData.shipmentDeclaredValue ?? null,
        shipmentClientOrderRef: formData.shipmentClientOrderRef || formData.shipment?.clientOrderRef || '',
        shipmentInvoiceRef: formData.shipmentInvoiceRef || formData.shipment?.invoiceRef || '',
        shipmentDeliveryNoteRef: formData.shipmentDeliveryNoteRef || formData.shipment?.deliveryNoteRef || '',
        shipmentExitNoteRef: formData.shipmentExitNoteRef || formData.shipment?.exitNoteRef || '',
        shipmentCarrierTrackingNumber: formData.shipmentCarrierTrackingNumber || '',
        
        // Document options and conditions
        documentOptions: JSON.stringify({
          showNotes: formData.showNotes !== false,
          showConditions: formData.showConditions !== false,
          showObjectif: formData.showObjectif !== false
        }),
        
        // Additional fields to ensure they're preserved
        notes: formData.notes || '',
        conditions: formData.conditions || '',
        terms: formData.terms || '',
        
        // Taxe sur la Valeur Ajoutée et autres taxes
        tvaRate: formData.tvaRate ?? 19,
        showTva: formData.showTva !== false, // default true
        tapRate: formData.showTap ? (parseFloat(formData.tapRate) || 0) : 0,
        showTap: formData.showTap ?? false,
        guarantee: formData.guarantee ?? 0,
        showGuarantee: formData.showGuarantee ?? false,
        stamp: formData.stamp ?? 0,
        showStamp: formData.showStamp ?? false,
        
        // échéances de paiement
        installments: formData.installments || [],
        hasInstallments: formData.installments && formData.installments.length > 0,

        // Bon pour (persisté SQLite : show_bon_pour / bon_pour_*)
        showBonPour: formData.showBonPour !== false,
        bonPourName: formData.bonPourName || '',
        bonPourRole: formData.bonPourRole || '',
        bonPourDate: formData.bonPourDate || '',
        bonPourPlace: formData.bonPourPlace || '',
      };
      
      // Log the document type for debugging
      console.log("Creating document with type:", documentData.type);

      // === CALCUL AUTOMATIQUE DU MONTANT FINAL ===
      let finalInvoiceAmount = 0;
      
      // Calculer le montant selon le type de document
      const items = formData.items || [];
      
      if (items.length > 0) {
        // Calcul du sous-total
        let subtotal = items.reduce((sum, item) => {
          const quantity = parseFloat(item.quantity) || 1;
          const price = parseFloat(item.price || item.unitPrice) || 0;
          return sum + (quantity * price);
        }, 0);
        
        console.log('?? Calcul final:', {
          items: items.length,
          subtotal,
          // Flattened formData fields
          formDataShowTva: formData.showTva,
          formDataTvaRate: formData.tvaRate,
        });
        
        // Appliquer les taxes si c'est une facture
        if (selectedType.id === 'invoice') {
          const showTva = formData.showTva !== false;
          const tvaRate = parseFloat(formData.tvaRate) || 19;
          
          if (showTva) {
            finalInvoiceAmount = subtotal * (1 + tvaRate / 100);
          } else {
            finalInvoiceAmount = subtotal;
          }
          
          console.log('?? Final invoice amount:', finalInvoiceAmount);
        } else {
          finalInvoiceAmount = subtotal;
        }

        const skipTaxRecapPersist = [
          'reception-note',
          'payment-voucher',
          'shipping-slip',
          'shipping',
          'payment-receipt',
          'exit-note',
          'transfer-note',
          'stock-entry',
          'quote-request',
          'proforma-request',
          'quote_request',
          'proforma_request',
        ].includes(selectedType.id);
        if (!skipTaxRecapPersist) {
          persistArchiveTaxTotals(documentData, formData, subtotal, getFacturationVatMode(settings));
        }
      }

      const isQuoteOrProformaRequest =
        selectedType.id === 'quote-request' || selectedType.id === 'proforma-request';

      if (!isQuoteOrProformaRequest) {
        documentData.amount = Math.round(finalInvoiceAmount * 100) / 100;
        console.log('?? Saved amount:', documentData.amount);
      }

      /** Bon de versement : carte dédiée (évite transactionNumber depuis paymentReference, motif, banques, refs, lignes, payment_amount). */
      if (archiveDocType === 'payment_voucher') {
        const lines = Array.isArray(formData.paymentLines) ? formData.paymentLines : [];
        const paymentSum =
          Math.round(lines.reduce((s, l) => s + (parseFloat(l?.amount) || 0), 0) * 100) /
          100;
        documentData.paymentAmount = paymentSum;
        documentData.amount = paymentSum;

        const headerFromLines = syncPaymentVoucherHeaderFromLines(lines);

        const encCash = formData.expectedCashingDate || formData.dueDate || '';
        if (encCash != null && String(encCash).trim() !== '')
          documentData.dueDate = String(encCash).trim();

        const pr = formData.paymentReason;
        documentData.motif =
          pr != null && String(pr).trim() !== '' ? String(pr).trim() : '';
        documentData.paymentReason = documentData.motif;

        documentData.transactionNumber = formData.transactionNumber || '';
        documentData.bankName = formData.issuerBank || '';
        documentData.beneficiaryName = formData.receiverBank || '';
        documentData.invoicesPaid = formData.relatedInvoices || headerFromLines.relatedInvoices || '';
        documentData.purchaseOrderReference =
          headerFromLines.purchaseOrderRef || formData.purchaseOrderRef || '';
        documentData.originalDocumentReference =
          headerFromLines.supplierCreditNoteRef || formData.supplierCreditNoteRef || '';
        documentData.original_document_reference = documentData.originalDocumentReference;

        const pc = encodePaymentVoucherAccountsPayerContact(
          formData.issuerAccount,
          formData.receiverAccount,
        );
        documentData.payerContact = pc || null;

        documentData.paymentDate =
          formData.date || documentData.paymentDate || '';

        documentData.validityNotes = encodeDocumentUiOverrides(formData, sectionOrder, taxOrder);
        documentData.paymentLines = lines;
        documentData.receivedBy = '';
        documentData.received_by = '';
        documentData.voucherReceivedBy = '';
      }

      /** Reçu de paiement : montant des lignes ; mode/référence/banque/factures — les blocs transfert/expédition polluent carrier, tracking et refs. */
      if (archiveDocType === 'payment_receipt') {
        const lines = Array.isArray(formData.paymentLines) ? formData.paymentLines : [];
        const headerPatch = syncPaymentReceiptHeaderFromLines(lines);
        documentData.paymentLines = lines;
        const paymentSum =
          Math.round(lines.reduce((s, l) => s + (parseFloat(l?.amount) || 0), 0) * 100) /
          100;
        documentData.paymentAmount = paymentSum;
        documentData.amount = paymentSum;

        documentData.validityNotes = encodeDocumentUiOverrides(formData, sectionOrder, taxOrder);

        const trimRp = (v) =>
          v != null && String(v).trim() !== '' ? String(v).trim() : '';

        documentData.paymentMethod = trimRp(formData.paymentMethod);
        documentData.paymentReference = trimRp(formData.paymentReference);
        documentData.transactionNumber = documentData.paymentReference;

        documentData.paymentBank = trimRp(formData.paymentBank);
        documentData.bankName = documentData.paymentBank;

        documentData.paymentSettlementType = trimRp(formData.paymentSettlementType);
        documentData.paymentType = documentData.paymentSettlementType;

        documentData.paymentInvoiceNumbers = trimRp(headerPatch.paymentInvoiceNumbers);
        documentData.invoicesPaid = documentData.paymentInvoiceNumbers;

        documentData.clientCreditNoteRef = trimRp(headerPatch.clientCreditNoteRef);
        documentData.originalDocumentReference = documentData.clientCreditNoteRef;
        documentData.original_document_reference = documentData.clientCreditNoteRef;

        documentData.paymentDate =
          trimRp(formData.paymentDate) ||
          trimRp(formData.date) ||
          trimRp(documentData.paymentDate);

        documentData.receiptReceivedBy = trimRp(formData.receiptReceivedBy);
        documentData.receivedBy = documentData.receiptReceivedBy;

        documentData.issuedBy = trimRp(formData.issuedBy);
        documentData.createdBy = documentData.issuedBy || trimRp(formData.createdBy) || '';

        documentData.carrier = '';
        documentData.trackingNumber = '';
        documentData.originWarehouseName = '';
        documentData.destinationWarehouseName = '';
        documentData.purchaseOrderReference = '';
        documentData.invoiceReference = '';
        documentData.deliveryNoteReference = '';
      }

      /**
       * Bordereau retour client : entrepôt, refs BC/BL/FAC et transport après blocs génériques ;
       * `controlled_by` / marchandises écrasés par stock ; carrier/tracking par expédition.
       */
      if (archiveDocType === 'client-return-slip') {
        const tr = (v) =>
          v != null && String(v).trim() !== '' ? String(v).trim() : '';

        documentData.validityNotes = encodeDocumentUiOverrides(formData, sectionOrder, taxOrder);

        const ware = tr(formData.clientReturnWarehouse);
        documentData.clientReturnWarehouse = ware;
        documentData.returnWarehouse = ware;

        const por = tr(formData.clientReturnOriginalPurchaseOrderRef);
        documentData.clientReturnOriginalPurchaseOrderRef = por;
        documentData.returnOriginalPurchaseOrder = por;

        const dor = tr(formData.clientReturnOriginalDeliveryRef);
        documentData.clientReturnOriginalDeliveryRef = dor;
        documentData.returnOriginalDelivery = dor;

        const ivr = tr(formData.clientReturnOriginalInvoiceRef);
        documentData.clientReturnOriginalInvoiceRef = ivr;
        documentData.returnOriginalInvoice = ivr;

        const car = tr(formData.clientReturnCarrier);
        documentData.clientReturnCarrier = car;
        documentData.returnCarrier = car;

        const track = tr(formData.clientReturnTrackingNumber);
        documentData.clientReturnTrackingNumber = track;
        documentData.returnTrackingNumber = track;
        documentData.trackingNumber = track;

        documentData.clientReturnType = tr(formData.clientReturnType);
        documentData.returnType = documentData.clientReturnType;

        const cond = tr(formData.clientReturnCondition);
        documentData.clientReturnCondition = cond;
        documentData.returnCondition = cond;
        documentData.goodsCondition = cond;

        const motif = tr(formData.clientReturnMotif) || tr(formData.clientReturnReason);
        documentData.clientReturnMotif = motif;
        documentData.clientReturnReason = motif || documentData.clientReturnReason || '';
        documentData.returnReason = motif;

        const normalizedReturnStatus = normalizeDocumentStatus(
          'client-return-slip',
          tr(formData.clientReturnStatus) || tr(formData.status),
        );
        documentData.clientReturnStatus = normalizedReturnStatus;
        documentData.returnStatus = normalizedReturnStatus;
        documentData.status = normalizedReturnStatus;

        const rd =
          tr(formData.clientReturnReturnDate) ||
          tr(formData.date);
        documentData.clientReturnReturnDate = rd;
        documentData.returnDate = rd;

        documentData.clientReturnControlledBy = tr(formData.clientReturnControlledBy);
        documentData.controlledByName = documentData.clientReturnControlledBy;

        documentData.issuedBy = tr(formData.issuedBy);
        documentData.createdBy =
          documentData.issuedBy || tr(formData.createdBy) || documentData.createdBy || '';

        documentData.priority =
          formData.priority !== undefined && formData.priority !== null
            ? String(formData.priority)
            : '';

        documentData.carrier = car || '';
        documentData.trackingNumber = track || documentData.trackingNumber || '';
        documentData.purchaseOrderReference = '';
        documentData.invoiceReference = '';
        documentData.deliveryNoteReference = '';
        documentData.originWarehouseName = '';
        documentData.destinationWarehouseName = '';
      }

      /**
       * Note de crédit : champs métier écrasés par stock, sortie, transfert, expédition (approvedByName, refs, etc.).
       */
      if (archiveDocType === 'credit_note') {
        Object.assign(documentData, buildCreditNoteArchivePayload(formData));
        const cnStatus = normalizeDocumentStatus(
          'credit-note',
          formData.status,
          [formData.creditNoteStatus],
        );
        documentData.status = cnStatus;
        documentData.creditNoteStatus = cnStatus;
        const cnApp =
          documentData.creditNoteApplicationDate ||
          formData.creditNote?.applicationDate ||
          formData.date ||
          '';
        if (cnApp && String(cnApp).trim()) {
          documentData.orderDate = String(cnApp).trim().slice(0, 10);
          documentData.order_date = documentData.orderDate;
        }
        documentData.validityNotes = encodeDocumentUiOverrides(formData, sectionOrder, taxOrder);
        const ib =
          formData.issuedBy != null && String(formData.issuedBy).trim() !== ''
            ? String(formData.issuedBy).trim()
            : '';
        documentData.issuedBy = ib;
        documentData.createdBy =
          ib ||
          (formData.createdBy != null && String(formData.createdBy).trim() !== ''
            ? String(formData.createdBy).trim()
            : '') ||
          documentData.createdBy ||
          '';

        documentData.carrier = '';
        documentData.trackingNumber = '';
        documentData.purchaseOrderReference = '';
        documentData.invoiceReference = '';
        documentData.deliveryNoteReference = '';
        documentData.originWarehouseName = '';
        documentData.destinationWarehouseName = '';
        documentData.controlledByName = '';
        documentData.goodsCondition = '';
      }

      /** Devis / facture proforma : validity_notes = coches + ordre sections (échéancier inclus). */
      if (archiveDocType === 'quote' || archiveDocType === 'proforma') {
        documentData.validityNotes = encodeDocumentUiOverrides(formData, sectionOrder, taxOrder);
        const vu =
          formData.validUntil != null && String(formData.validUntil).trim() !== ''
            ? String(formData.validUntil).trim()
            : '';
        if (vu) documentData.validUntil = vu;
        if (archiveDocType === 'quote') {
          const qd =
            formData.dueDate != null && String(formData.dueDate).trim() !== ''
              ? String(formData.dueDate).trim()
              : '';
          documentData.dueDate = qd || vu || documentData.dueDate || '';

          const sp = formData.salesPerson || formData.commercial || '';
          if (sp && String(sp).trim()) {
            documentData.commercial = String(sp).trim();
            documentData.salesPerson = documentData.commercial;
          }
          const realisation =
            formData.deliveryTime || formData.estimatedDelivery || '';
          if (realisation && String(realisation).trim()) {
            documentData.estimatedDeliveryTime = String(realisation).trim();
            documentData.estimated_delivery_time = documentData.estimatedDeliveryTime;
            documentData.deliveryTime = documentData.estimatedDeliveryTime;
            documentData.estimatedDelivery = documentData.estimatedDeliveryTime;
          }
          const deliveryDelay = formData.deliveryDelay || '';
          if (deliveryDelay && String(deliveryDelay).trim()) {
            documentData.desiredDeliveryTime = String(deliveryDelay).trim();
            documentData.desired_delivery_time = documentData.desiredDeliveryTime;
            documentData.deliveryDelay = documentData.desiredDeliveryTime;
          }
          const pt = formData.paymentTerms || '';
          if (pt && String(pt).trim()) {
            documentData.desiredPaymentTerms = String(pt).trim();
            documentData.desired_payment_terms = documentData.desiredPaymentTerms;
            documentData.paymentTerms = documentData.desiredPaymentTerms;
          }
          const pm = formData.paymentMethod || '';
          if (pm && String(pm).trim()) {
            documentData.paymentMethod = String(pm).trim();
            documentData.payment_method = documentData.paymentMethod;
          }
        }
      }

      /** Facture proforma : n'aligne pas l'échéance de paiement sur — Valable jusqu'au — ; date limite d'acceptation. */
      if (archiveDocType === 'proforma') {
        documentData.paymentMethod =
          formData.plannedPaymentMethod || formData.paymentMethod || '';
        const payDue =
          formData.paymentDueDate != null && String(formData.paymentDueDate).trim() !== ''
            ? String(formData.paymentDueDate).trim()
            : '';
        const genericDue =
          formData.dueDate != null && String(formData.dueDate).trim() !== ''
            ? String(formData.dueDate).trim()
            : '';
        documentData.dueDate =
          payDue || genericDue || documentData.dueDate || '';
        documentData.acceptanceDeadline =
          formData.acceptanceDeadline != null &&
          String(formData.acceptanceDeadline).trim() !== ''
            ? String(formData.acceptanceDeadline).trim()
            : null;
        documentData.estimatedDelivery =
          formData.estimatedDelivery ||
          formData.deliveryTime ||
          documentData.estimatedDelivery ||
          '';
        const sp = formData.salesPerson || formData.commercial || '';
        if (sp && String(sp).trim()) {
          documentData.commercial = String(sp).trim();
          documentData.salesPerson = documentData.commercial;
        }
        const realisation =
          formData.deliveryTime || formData.estimatedDelivery || '';
        if (realisation && String(realisation).trim()) {
          documentData.estimatedDeliveryTime = String(realisation).trim();
          documentData.estimated_delivery_time = documentData.estimatedDeliveryTime;
          documentData.deliveryTime = documentData.estimatedDeliveryTime;
          documentData.estimatedDelivery = documentData.estimatedDeliveryTime;
        }
        const deliveryDelay = formData.deliveryDelay || '';
        if (deliveryDelay && String(deliveryDelay).trim()) {
          documentData.desiredDeliveryTime = String(deliveryDelay).trim();
          documentData.desired_delivery_time = documentData.desiredDeliveryTime;
          documentData.deliveryDelay = documentData.desiredDeliveryTime;
        }
        const pt = formData.paymentTerms || '';
        if (pt && String(pt).trim()) {
          documentData.desiredPaymentTerms = String(pt).trim();
          documentData.desired_payment_terms = documentData.desiredPaymentTerms;
          documentData.paymentTerms = documentData.desiredPaymentTerms;
        }
      }

      /**
       * Facture : littéral `documentData` écrase paiement/refs (blocs proforma, bon de livraison, bon de sortie, etc.).
       * Réapplique les champs formulaire après ces fusions génériques.
       */
      if (archiveDocType === 'invoice') {
        documentData.validityNotes = encodeDocumentUiOverrides(formData, sectionOrder, taxOrder);
        documentData.paymentMethod = formData.paymentMethod || '';
        const invDue =
          formData.dueDate != null && String(formData.dueDate).trim() !== ''
            ? String(formData.dueDate).trim()
            : '';
        documentData.dueDate = invDue || documentData.dueDate || '';
        documentData.paymentConditions = formData.paymentConditions || '';
        documentData.conditions = documentData.paymentConditions;
        documentData.payment_conditions = documentData.paymentConditions;

        const bc =
          formData.orderReference != null && String(formData.orderReference).trim() !== ''
            ? String(formData.orderReference).trim()
            : '';
        const bl =
          formData.deliveryReference != null && String(formData.deliveryReference).trim() !== ''
            ? String(formData.deliveryReference).trim()
            : '';
        documentData.orderReference = bc;
        documentData.deliveryReference = bl;
        documentData.purchaseOrderReference = bc;
        documentData.deliveryNoteReference = bl;

        const pj =
          formData.projectNumber != null && String(formData.projectNumber).trim() !== ''
            ? String(formData.projectNumber).trim()
            : '';
        documentData.projectNumber = pj;
        documentData.project_number = pj;
        documentData.projectReference = pj;
        documentData.project_reference = pj;

        const cor =
          formData.customerOrderReference != null &&
          String(formData.customerOrderReference).trim() !== ''
            ? String(formData.customerOrderReference).trim()
            : '';
        documentData.originalDocumentReference = cor;
        documentData.customerOrderReference = cor;

        documentData.invoiceType =
          formData.invoiceType != null && formData.invoiceType !== '' ? formData.invoiceType : '';

        const dl =
          formData.deliveryLocation ||
          formData.deliveryPlace ||
          formData.deliveryAddress ||
          formData.lieu ||
          '';
        documentData.deliveryLocation = dl;
        documentData.delivery_location = dl;
        documentData.deliveryAddress = dl;
        documentData.lieu_livraison = dl;

        documentData.deliveryDate =
          formData.deliveryDate != null && String(formData.deliveryDate).trim() !== ''
            ? String(formData.deliveryDate).trim()
            : '';
      }

      /**
       * Bon de livraison (`delivery`) ou sortie matériel (`material_exit`) : refs, entrepôt, transport
       * et colis sont écrasés par les blocs bon de sortie / transfert / expédition / encaissement du littéral.
       */
      if (archiveDocType === 'delivery' || archiveDocType === 'material_exit') {
        const nest = formData.delivery || {};
        const trimv = (v) =>
          v != null && String(v).trim() !== '' ? String(v).trim() : '';

        documentData.validityNotes = encodeDocumentUiOverrides(formData, sectionOrder, taxOrder);

        const quoteRef =
          trimv(formData.quoteReference) ||
          trimv(nest.quoteReference || nest.quote_reference);
        documentData.quoteReference = quoteRef;
        documentData.originalDocumentReference = quoteRef;

        const bcRef =
          trimv(formData.orderReference) ||
          trimv(formData.deliveryOrderReference) ||
          trimv(nest.orderReference);
        documentData.orderReference = bcRef;
        documentData.purchaseOrderReference = bcRef;

        const cor = trimv(formData.customerOrderReference);
        documentData.customerOrderReference = cor;
        documentData.documentReference = cor;
        documentData.document_reference = cor;

        documentData.invoiceReference =
          trimv(formData.invoiceReference) || trimv(nest.invoiceReference);

        const ware =
          trimv(formData.departureWarehouse) || trimv(formData.departure_warehouse);
        documentData.departureWarehouse = ware;
        documentData.originWarehouseName = ware;

        const delAddr =
          trimv(formData.deliveryLocation) ||
          trimv(formData.deliveryAddress) ||
          trimv(formData.recipientAddress);
        documentData.deliveryAddress = delAddr;
        documentData.deliveryLocation = delAddr;
        documentData.delivery_location = delAddr;
        documentData.deliveryPlace = delAddr;
        documentData.lieu_livraison = delAddr;
        documentData.recipientContact = '';
        documentData.deliveryContactName = '';

        documentData.deliveryMethod = trimv(formData.deliveryMethod);
        documentData.deliveryMode =
          trimv(formData.deliveryMethod) ||
          trimv(formData.deliveryMode) ||
          documentData.deliveryMode ||
          '';

        const transp =
          trimv(formData.deliveryCarrier) ||
          trimv(formData.transporterName) ||
          trimv(formData.carrier) ||
          trimv(nest.carrier);
        documentData.carrier = transp;
        documentData.transporterName = transp;

        documentData.trackingNumber =
          trimv(formData.trackingNumber) || trimv(nest.trackingNumber);

        const carrierTrack =
          trimv(formData.carrierTrackingNumber) ||
          trimv(formData.shipmentCarrierTrackingNumber) ||
          trimv(nest.carrierTrackingNumber);
        documentData.carrierTrackingNumber = carrierTrack;
        documentData.carrier_tracking_number = carrierTrack;
        documentData.shipmentCarrierTrackingNumber = carrierTrack;

        const pkgRaw = formData.shipmentPackageCount ?? nest.packageCount;
        let pkgNum = Number(pkgRaw);
        if (!Number.isFinite(pkgNum) || pkgNum < 0) pkgNum = 0;
        documentData.shipmentPackageCount = pkgNum;
        documentData.packageCount = pkgNum;

        const planDel =
          trimv(formData.deliveryDate) || trimv(nest.date) || trimv(nest.deliveryDate);
        documentData.deliveryDate = planDel;
        documentData.expectedDeliveryDate = planDel;

        documentData.deliveryTime = trimv(formData.deliveryTime) || trimv(nest.time);

        documentData.driver = trimv(formData.deliveryDriver) || trimv(formData.driver || nest.driver);
        documentData.vehicleType =
          trimv(formData.deliveryVehicleType) || trimv(formData.vehicleType || nest.vehicleType);
        documentData.vehicleRegistration =
          trimv(formData.deliveryVehicleRegistration) ||
          trimv(formData.vehicleRegistration || nest.vehicleRegistration);

        documentData.deliveryConditions = trimv(formData.deliveryConditions || nest.deliveryConditions);

        const dn = trimv(formData.deliveryNotes);
        documentData.deliveryNotes = dn;
        documentData.delivery_notes = dn;
        documentData.notes = dn;

        documentData.createdBy = trimv(formData.issuedBy) || trimv(formData.createdBy) || '';
        documentData.issuedBy = trimv(formData.issuedBy) || '';

        documentData.receivedBy = trimv(formData.receivedBy);
      }

      /** Demande de devis / proforma : budget PDF/archives = saisie manuelle uniquement (pas total lignes). */
      if (selectedType.id === 'quote-request' || selectedType.id === 'proforma-request') {
        const budgetRaw = formData.maxBudget ?? formData.approximateBudget;
        const budget = parseFloat(String(budgetRaw ?? '').replace(/\s/g, '').replace(',', '.'));
        if (Number.isFinite(budget) && budget > 0) {
          const rounded = Math.round(budget * 100) / 100;
          documentData.amount = rounded;
          documentData.approximateBudget = rounded;
          documentData.maxBudget = rounded;
          documentData.declaredValue = rounded;
          documentData.declared_value = rounded;
        } else {
          documentData.amount = 0;
          documentData.approximateBudget = null;
          documentData.maxBudget = null;
          documentData.declaredValue = null;
          documentData.declared_value = null;
        }
        if (formData.responseDate) {
          documentData.responseDate = formData.responseDate;
          documentData.responseDeadline = formData.responseDate;
          documentData.dueDate = formData.responseDate;
        }
        documentData.requirements = formData.requirements || '';
        documentData.validityNotes = encodeDocumentUiOverrides(formData, sectionOrder, taxOrder);
        const ib =
          formData.issuedBy != null && String(formData.issuedBy).trim() !== ''
            ? String(formData.issuedBy).trim()
            : '';
        documentData.issuedBy = ib;
        documentData.createdBy = ib || documentData.createdBy || '';
        const deliveryPlace =
          formData.deliveryPlace != null && String(formData.deliveryPlace).trim() !== ''
            ? String(formData.deliveryPlace).trim()
            : formData.deliveryLocation != null && String(formData.deliveryLocation).trim() !== ''
              ? String(formData.deliveryLocation).trim()
              : '';
        if (deliveryPlace) {
          documentData.deliveryPlace = deliveryPlace;
          documentData.delivery_place = deliveryPlace;
          documentData.deliveryLocation = deliveryPlace;
          documentData.delivery_location = deliveryPlace;
          documentData.lieu_livraison = deliveryPlace;
        }
      }

      /** Bon de commande : références DD/DPR, acheteur, délai — pas de budget estimé (réservé DD/DPR). */
      if (selectedType.id === 'purchase-order') {
        const ddRef = formData.quoteRequestRef;
        if (ddRef != null && String(ddRef).trim() !== '')
          documentData.documentReference = String(ddRef).trim();
        const dprRef = formData.proformaRequestRef;
        if (dprRef != null && String(dprRef).trim() !== '')
          documentData.originalDocumentReference = String(dprRef).trim();
        const bc = formData.buyerContact;
        if (bc != null && String(bc).trim() !== '') documentData.buyerContact = String(bc).trim();
        const timeframe = formData.deliveryTimeframe;
        if (timeframe != null && String(timeframe).trim() !== '') {
          documentData.desiredDeliveryTime = String(timeframe).trim();
          documentData.desired_delivery_time = documentData.desiredDeliveryTime;
        }
        const dm = formData.deliveryMode;
        if (dm != null && String(dm).trim() !== '') {
          documentData.deliveryMode = String(dm).trim();
          documentData.delivery_mode = documentData.deliveryMode;
        }
        const pt = formData.paymentTerms;
        if (pt != null && String(pt).trim() !== '') {
          documentData.desiredPaymentTerms = String(pt).trim();
          documentData.desired_payment_terms = documentData.desiredPaymentTerms;
          documentData.paymentTerms = documentData.desiredPaymentTerms;
        }
        documentData.validityNotes = encodeDocumentUiOverrides(formData, sectionOrder, taxOrder);
      }

      /**
       * Bordereau retour fournisseur : refs BC/BL/FAC, RMA, logistique — écrasés par stock/transfert/expédition.
       */
      if (archiveDocType === 'return_slip' || selectedType.id === 'return-slip') {
        Object.assign(documentData, buildReturnSlipArchivePayload(formData));
        const rsStatus = normalizeDocumentStatus('return-slip', formData.status, [
          formData.returnStatus,
        ]);
        documentData.status = rsStatus;
        documentData.returnStatus = rsStatus;
        documentData.validityNotes = encodeDocumentUiOverrides(formData, sectionOrder, taxOrder);
        const ib = documentData.issuedBy || '';
        documentData.createdBy =
          ib ||
          (formData.createdBy != null && String(formData.createdBy).trim() !== ''
            ? String(formData.createdBy).trim()
            : '') ||
          documentData.createdBy ||
          '';
      }

      const logisticsTypeKey = String(archiveDocType || '').replace(/-/g, '_');
      const tr = (v) =>
        v != null && String(v).trim() !== '' ? String(v).trim() : '';

      /** Bon de réception : réapplique champs métier après littéral générique (pollution BL/expédition). */
      if (logisticsTypeKey === 'reception_note' || logisticsTypeKey === 'receipt_note') {
        documentData.validityNotes = encodeDocumentUiOverrides(formData, sectionOrder, taxOrder);
        documentData.purchaseOrderReference = tr(formData.receptionOrderRef);
        documentData.deliveryNoteReference = tr(formData.receptionDeliveryNoteRef);
        documentData.receptionOrder = documentData.purchaseOrderReference;
        documentData.reception_order = documentData.purchaseOrderReference;
        documentData.receiverAddress = tr(formData.receiverAddress);
        documentData.receptionAddress = documentData.receiverAddress;
        documentData.reception_address = documentData.receiverAddress;
        const expRec =
          tr(formData.expectedReceptionDate) ||
          tr(formData.expectedDeliveryDate) ||
          tr(formData.date);
        documentData.expectedReceptionDate = expRec;
        documentData.expected_reception_date = expRec;
        documentData.expectedDeliveryDate = expRec;
        documentData.expected_delivery_date = expRec;
        documentData.receptionDate = tr(formData.receptionDate) || tr(formData.date);
        documentData.receptionTime = tr(formData.receptionTime);
        documentData.receivedByName = tr(formData.recueParName || formData.receptionReceivedBy);
        documentData.received_by_name = documentData.receivedByName;
        documentData.controlledByName = tr(formData.receptionControlledBy);
        documentData.controlled_by_name = documentData.controlledByName;
        documentData.generalCondition = tr(formData.receptionOverallCondition);
        documentData.general_condition = documentData.generalCondition;
        documentData.quantityConformity = tr(formData.receptionQuantityCompliance);
        documentData.quantity_conformity = documentData.quantityConformity;
        documentData.qualityConformity = tr(formData.receptionQualityCompliance);
        documentData.quality_conformity = documentData.qualityConformity;
        documentData.stockDestination = tr(formData.receptionStockDestination);
        documentData.stock_destination = documentData.stockDestination;
        documentData.receptionStatus = normalizeDocumentStatus(
          'reception-note',
          tr(formData.receptionStatus) || tr(formData.status),
        );
        documentData.reception_status = documentData.receptionStatus;
        documentData.status = documentData.receptionStatus;
        const rw = tr(formData.receptionWarehouse);
        documentData.receptionWarehouse = rw;
        documentData.warehouseName = rw;
        documentData.warehouse_name = rw;
        documentData.receptionCarrier = tr(formData.receptionCarrier);
        documentData.carrier = documentData.receptionCarrier;
        documentData.trackingNumber = '';
        documentData.invoiceReference = '';
        documentData.paymentMethod = '';
        documentData.paymentConditions = '';
        documentData.conditions = '';
      }

      /** Bon d'entrée stock : entrepôt, source, refs, contrôle. */
      if (logisticsTypeKey === 'stock_entry') {
        documentData.validityNotes = encodeDocumentUiOverrides(formData, sectionOrder, taxOrder);
        const entryDt =
          tr(formData.stockEntryEntryDate) ||
          tr(formData.expectedEntryDate) ||
          tr(formData.date);
        documentData.entryDate = entryDt;
        documentData.entry_date = entryDt;
        documentData.expectedEntryDate = tr(formData.expectedEntryDate) || entryDt;
        documentData.expected_entry_date = documentData.expectedEntryDate;
        documentData.entryTime = tr(formData.stockEntryEntryTime);
        documentData.entry_time = documentData.entryTime;
        const wh = tr(formData.stockEntryWarehouse);
        documentData.warehouseName = wh;
        documentData.warehouse_name = wh;
        documentData.stockEntryWarehouse = wh;
        documentData.entrySource = tr(formData.stockEntrySource);
        documentData.entry_source = documentData.entrySource;
        documentData.entryDocumentReference = tr(formData.stockEntryDocumentReference);
        documentData.entry_document_reference = documentData.entryDocumentReference;
        documentData.supplierName = tr(formData.stockEntrySupplier);
        documentData.receivedByName = tr(formData.stockEntryReceivedBy);
        documentData.controlledByName = tr(formData.stockEntryControlledBy);
        documentData.goodsCondition = tr(formData.stockEntryCondition);
        documentData.purchaseOrderReference = tr(formData.stockEntryPurchaseOrderRef);
        documentData.deliveryNoteReference = tr(formData.stockEntryReceptionNoteRef);
        documentData.originalDocumentReference = tr(formData.stockEntryTransferNoteRef);
        documentData.originWarehouseName = '';
        documentData.destinationWarehouseName = '';
        documentData.carrier = '';
        documentData.trackingNumber = '';
        documentData.invoiceReference = '';
      }

      /** Bon de sortie : entrepôts, refs, transport — sans pollution transfert/expédition. */
      if (logisticsTypeKey === 'exit_slip' || logisticsTypeKey === 'exit_note') {
        documentData.validityNotes = encodeDocumentUiOverrides(formData, sectionOrder, taxOrder);
        documentData.exitNoteClientOrSupplier = tr(formData.exitNoteClientOrSupplier);
        documentData.exitNoteExpectedDate =
          tr(formData.exitNoteExpectedDate) || tr(formData.date);
        const orig = tr(formData.exitNoteOriginWarehouse);
        documentData.originWarehouseName = orig;
        documentData.origin_warehouse_name = orig;
        documentData.exitNoteOriginWarehouse = orig;
        const destWh = tr(formData.exitNoteDestinationWarehouse);
        documentData.destinationWarehouseName = destWh;
        documentData.destination_warehouse_name = destWh;
        documentData.exitNoteDestinationWarehouse = destWh;
        documentData.exitNoteHandler = tr(formData.exitNoteHandler);
        documentData.transferResponsibleName = documentData.exitNoteHandler;
        documentData.approvedByName = tr(formData.exitNoteAuthorizedBy);
        documentData.approved_by_name = documentData.approvedByName;
        documentData.exitNoteStatus = normalizeDocumentStatus(
          'exit-note',
          tr(formData.exitNoteStatus) || tr(formData.status),
        );
        documentData.status = documentData.exitNoteStatus;
        documentData.purchaseOrderReference = tr(formData.exitNoteClientOrderRef);
        documentData.invoiceReference = tr(formData.exitNoteInvoiceRef);
        documentData.deliveryNoteReference = tr(formData.exitNoteDeliveryNoteRef);
        documentData.originalDocumentReference = tr(formData.exitNoteTransferNoteRef);
        documentData.exitNoteDestination = tr(formData.exitNoteDestination);
        documentData.deliveryAddress = documentData.exitNoteDestination;
        documentData.exitNoteTransportMode = tr(formData.exitNoteTransportMode);
        documentData.deliveryMode = documentData.exitNoteTransportMode;
        documentData.exitNoteExitTime = tr(formData.exitNoteExitTime);
        documentData.departureTime = documentData.exitNoteExitTime;
        documentData.exitNoteDocumentReference = tr(formData.exitNoteDocumentReference);
        documentData.entryDocumentReference = documentData.exitNoteDocumentReference;
        documentData.exitNoteRecipient = tr(formData.exitNoteRecipient);
        documentData.receivedByName = documentData.exitNoteRecipient;
        documentData.carrier = '';
        documentData.trackingNumber = '';
        documentData.packageCount = null;
        documentData.shipmentMode = '';
      }

      /** Bon de transfert : entrepôts, suivi, horaires. */
      if (logisticsTypeKey === 'transfer_slip' || logisticsTypeKey === 'transfer_note') {
        documentData.validityNotes = encodeDocumentUiOverrides(formData, sectionOrder, taxOrder);
        documentData.transferNoteExpectedDate =
          tr(formData.transferNoteExpectedDate) || tr(formData.date);
        documentData.expectedDeliveryDate = documentData.transferNoteExpectedDate;
        const origT = tr(formData.transferNoteOriginWarehouse);
        documentData.originWarehouseName = origT;
        documentData.origin_warehouse_name = origT;
        documentData.transferNoteOriginWarehouse = origT;
        const destT = tr(formData.transferNoteDestinationWarehouse);
        documentData.destinationWarehouseName = destT;
        documentData.destination_warehouse_name = destT;
        documentData.transferNoteDestinationWarehouse = destT;
        documentData.transferNoteDocumentReference = tr(formData.transferNoteDocumentReference);
        documentData.entryDocumentReference = documentData.transferNoteDocumentReference;
        documentData.transferNoteTransferRequestRef = tr(formData.transferNoteTransferRequestRef);
        documentData.originalDocumentReference = documentData.transferNoteTransferRequestRef;
        documentData.transferNoteRequestedBy = tr(formData.transferNoteRequestedBy);
        documentData.transferRequestedByName = documentData.transferNoteRequestedBy;
        documentData.transferNoteHandler = tr(formData.transferNoteHandler);
        documentData.transferResponsibleName = documentData.transferNoteHandler;
        documentData.transferNoteApprovedBy = tr(formData.transferNoteApprovedBy);
        documentData.approvedByName = documentData.transferNoteApprovedBy;
        documentData.transferNoteStatus = normalizeDocumentStatus(
          'transfer-note',
          tr(formData.transferNoteStatus) || tr(formData.status),
        );
        documentData.status = documentData.transferNoteStatus;
        documentData.transferNoteTransportMode = tr(formData.transferNoteTransportMode);
        documentData.shipmentMode = documentData.transferNoteTransportMode;
        documentData.transferNoteDepartureTime = tr(formData.transferNoteDepartureTime);
        documentData.departureTime = documentData.transferNoteDepartureTime;
        documentData.transferNoteArrivalTime = tr(formData.transferNoteArrivalTime);
        documentData.arrivalTime = documentData.transferNoteArrivalTime;
        documentData.transferNoteTrackingNumber = tr(formData.transferNoteTrackingNumber);
        documentData.trackingNumber = documentData.transferNoteTrackingNumber;
        documentData.carrier = '';
        documentData.invoiceReference = '';
      }

      /** Bordereau d'envoi : expédition, colis, refs livraison. */
      if (logisticsTypeKey === 'shipping' || logisticsTypeKey === 'shipping_slip') {
        documentData.validityNotes = encodeDocumentUiOverrides(formData, sectionOrder, taxOrder);
        documentData.shipmentDate = tr(formData.shipmentDate) || tr(formData.date);
        const shipWh = tr(formData.shipmentOriginWarehouse);
        documentData.originWarehouseName = shipWh;
        documentData.origin_warehouse_name = shipWh;
        documentData.shipmentOriginWarehouse = shipWh;
        const shipDelLoc =
          tr(formData.deliveryLocation) ||
          tr(formData.deliveryAddress) ||
          tr(formData.lieu_livraison);
        documentData.deliveryLocation = shipDelLoc;
        documentData.deliveryAddress = shipDelLoc;
        documentData.delivery_location = shipDelLoc;
        documentData.lieu_livraison = shipDelLoc;
        documentData.shipmentMethod = tr(formData.shipmentMethod);
        documentData.shipmentMode = documentData.shipmentMethod;
        documentData.deliveryMethod = documentData.shipmentMethod;
        documentData.delivery_method = documentData.shipmentMethod;
        documentData.deliveryMode = documentData.shipmentMethod;
        documentData.delivery_mode = documentData.shipmentMethod;
        documentData.shipmentCarrier = tr(formData.shipmentCarrier);
        documentData.carrier = documentData.shipmentCarrier;
        documentData.shipmentCarrierTrackingNumber = tr(formData.shipmentCarrierTrackingNumber);
        documentData.carrierTrackingNumber = documentData.shipmentCarrierTrackingNumber;
        documentData.carrier_tracking_number = documentData.shipmentCarrierTrackingNumber;
        documentData.shipmentTrackingNumber = tr(formData.shipmentTrackingNumber);
        documentData.trackingNumber = documentData.shipmentTrackingNumber;
        documentData.shipmentPriority = tr(formData.shipmentPriority);
        documentData.shipmentPackageType = tr(formData.shipmentPackageType);
        documentData.packageType = documentData.shipmentPackageType;
        documentData.shipmentProductType = tr(formData.shipmentProductType);
        documentData.productType = documentData.shipmentProductType;
        const pkgN = Number(formData.shipmentPackageCount);
        documentData.packageCount = Number.isFinite(pkgN) && pkgN >= 0 ? pkgN : null;
        documentData.shipmentPackageCount = documentData.packageCount;
        const wt = Number(formData.shipmentTotalWeight);
        documentData.weight = Number.isFinite(wt) && wt >= 0 ? wt : null;
        documentData.shipmentTotalWeight = documentData.weight;
        const vol = Number(formData.shipmentVolume);
        documentData.volume = Number.isFinite(vol) && vol >= 0 ? vol : null;
        documentData.shipmentVolume = documentData.volume;
        const dv = Number(formData.shipmentDeclaredValue);
        documentData.declaredValue = Number.isFinite(dv) && dv >= 0 ? dv : null;
        documentData.shipmentDeclaredValue = documentData.declaredValue;
        documentData.amount =
          documentData.declaredValue != null && documentData.declaredValue > 0
            ? documentData.declaredValue
            : 0;
        documentData.shipmentClientOrderRef = tr(formData.shipmentClientOrderRef);
        documentData.purchaseOrderReference = documentData.shipmentClientOrderRef;
        documentData.shipmentInvoiceRef = tr(formData.shipmentInvoiceRef);
        documentData.invoiceReference = documentData.shipmentInvoiceRef;
        documentData.shipmentDeliveryNoteRef = tr(formData.shipmentDeliveryNoteRef);
        documentData.deliveryNoteReference = documentData.shipmentDeliveryNoteRef;
        documentData.shipmentExitNoteRef = '';
        documentData.instructions = tr(formData.instructions);
        documentData.destinationWarehouseName = '';
        documentData.paymentMethod = '';
      }

      if (selectedType.id === 'client-return-slip') {
        documentData.returnOriginalPurchaseOrder =
          formData.clientReturnOriginalPurchaseOrderRef || documentData.returnOriginalPurchaseOrder || '';
        documentData.returnOriginalDelivery =
          formData.clientReturnOriginalDeliveryRef || documentData.returnOriginalDelivery || '';
        documentData.returnOriginalInvoice =
          formData.clientReturnOriginalInvoiceRef || documentData.returnOriginalInvoice || '';
        documentData.returnWarehouse =
          formData.clientReturnWarehouse || documentData.returnWarehouse || '';
        documentData.returnCarrier = formData.clientReturnCarrier || documentData.returnCarrier || '';
        const crMotif = formData.clientReturnMotif || formData.clientReturnReason || '';
        if (crMotif) documentData.returnReason = crMotif;
        if (formData.clientReturnType) documentData.returnType = formData.clientReturnType;
        if (
          formData.clientReturnCondition != null &&
          String(formData.clientReturnCondition).trim() !== ''
        ) {
          const cc = normalizeReturnGoodsConditionValue(formData.clientReturnCondition);
          documentData.returnCondition = cc;
          documentData.goodsCondition = cc;
        }
        if (formData.clientReturnReturnDate)
          documentData.returnDate = formData.clientReturnReturnDate;
        if (formData.clientReturnControlledBy) {
          documentData.controlledByName = formData.clientReturnControlledBy;
        }
      }
      
      // IMPORTANT: Sauvegarder les échéances dans le document
      if (
        (selectedType.id === 'invoice' ||
          selectedType.id === 'purchase-order' ||
          selectedType.id === 'proforma') &&
        formData.installments &&
        formData.installments.length > 0
      ) {
        documentData.installments = formData.installments;
        console.log(
          '✅ échéances sauvegardées:',
          selectedType.id,
          formData.installments.length,
        );
      }

      const fromModule = searchParams.get('from');
      const archiveModuleForSave =
        fromModule === 'inventaire'
          ? ARCHIVE_MODULE_INVENTAIRE
          : fromModule === 'logistique'
            ? ARCHIVE_MODULE_LOGISTIQUE
            : selectedType?.id
              ? getArchiveModuleForDocType(selectedType.id)
              : editingDocument?.sourceType ||
                editingDocument?.source_type ||
                undefined;
      applyArchiveModuleToDocumentPayload(documentData, archiveModuleForSave);
      const sectionReturnRoute = resolveSectionRouteFromFromParam(fromModule);
      const archivesRouteAfterSave =
        sectionReturnRoute || getArchivesRouteForModule(documentData.sourceType);
      
      // Save or update the document in the DocumentsContext
      let savedDocument;
      if (isEditMode && editingDocument) {
        // Update existing document
        const updatedData = {
          ...documentData,
          id: editingDocument.id, // Keep the original ID
          // Keep original metadata if needed
          createdAt: editingDocument.createdAt,
          updatedAt: new Date().toISOString()
        };
        savedDocument = await updateDocument(editingDocument.id, updatedData);
        console.log(`?? Document updated successfully:`, {
          type: documentData.type,
          number: documentData.number,
          client: documentData.client,
          amount: documentData.amount,
          documentId: editingDocument.id,
          updatedInArchives: !!savedDocument
        });
      } else {
        // Create new document
        savedDocument = await addDocument(documentData);
        console.log(`?? Document saved successfully to Archives:`, {
          type: documentData.type,
          number: documentData.number,
          client: documentData.client,
          amount: documentData.amount,
          documentId: savedDocument?.id,
          savedToArchives: !!savedDocument
        });
      }

      // ?? AUTOMATIC INTEGRATION: Create corresponding Achat/Vente entry
      if (savedDocument) {
        try {
          const integrationResult = await createTransactionFromDocument({
            id: savedDocument.id,
            type: selectedType.id, // Use original ID (not normalized)
            number: formData.number,
            clientId: selectedPartner.id,
            clientName: selectedPartner.name,
            supplierId: isSupplierDocument ? selectedPartner.id : null,
            supplierName: isSupplierDocument ? selectedPartner.name : null,
            amount: formData.total || 0,
            total: formData.total || 0,
            date: formData.date,
            status: 'in_progress',
            items: formData.items || [],
            paymentMethod: formData.paymentMethod || ''
          });

          if (integrationResult.success) {
            console.log(`✅ ${isSupplierDocument ? 'Purchase' : 'Sale'} automatically created from document:`, savedDocument.id);
          } else {
            console.warn('?? Error during automatic Purchase/Sale creation:', integrationResult.error);
          }
        } catch (error) {
          console.error('— Purchase/Sale integration error:', error);
        }
      }
      
      // Si c'est une facture avec des échéances, créer les enregistrements de paiement
      if (selectedType.id === 'invoice' && formData.installments && formData.installments.length > 0) {
        try {
          await createPayment(savedDocument.id, {
            dueDate: formData.dueDate,
            installments: formData.installments.map(inst => ({
              amount: parseFloat(inst.amount) || 0,
              dueDate: inst.dueDate,
              notes: inst.notes || '',
              status: 'pending'
            })),
            notes: t('document_creation.form.installment_payment_notes', { count: formData.installments.length })
          });
          console.log('Payment installments created successfully');
        } catch (error) {
          console.error('Error creating payment installments:', error);
        }
      }
      
      // If this is a return slip, also create a return entry
      // NOTE: Only do this if we're converting FROM another document type to return-slip
      // If we're creating a return-slip directly, the document IS the return, no need to duplicate
      if (selectedType.id === 'return-slip' && formData.convertedFrom) {
        try {
          await createReturnFromDocument({
            ...documentData,
            id: savedDocument.id,
            supplier: isSupplierDocument ? formData.supplier : null,
            client: !isSupplierDocument ? formData.client : null,
            supplierName: isSupplierDocument ? selectedPartner.name : null,
            clientName: !isSupplierDocument ? selectedPartner.name : null,
            return: formData.return || {}
          });
        } catch (error) {
          console.error('Error creating return entry:', error);
          // Don't show alert, the document was saved successfully
        }
      }
      
      // Navigate to archives with success toast
      if (isAgentEmbed) {
        window.parent.postMessage(
          {
            type: 'agent-document-saved',
            documentId: savedDocument?.id,
            documentNumber: formData.number,
            documentType: selectedType.id,
          },
          window.location.origin,
        );
        return;
      }

      if (isEditMode) {
        navigate(archivesRouteAfterSave, {
          state: {
            toast: {
              visible: true,
              message: t('document_creation.form.toast_document_updated', { title: selectedType.title }),
              status: 'success',
              documentId: savedDocument?.id,
              documentNumber: formData.number,
              documentType: selectedType.id
            }
          }
        });
        // Reset edit mode
        setIsEditMode(false);
        setEditingDocument(null);
      } else {
        navigate(archivesRouteAfterSave, {
          state: {
            toast: {
              visible: true,
              message: t('document_creation.form.toast_document_saved', { title: selectedType.title }),
              status: 'success',
              documentId: savedDocument?.id,
              documentNumber: formData.number,
              documentType: selectedType.id
            }
          }
        });
      }
    } catch (error) {
      console.error('Error during save:', error);
      alert(t('document_creation.alerts.save_error'));
    }
  };

  // Handle document status changes and sync with Achats/Ventes
  const handleDocumentStatusChange = async (documentId, newStatus, documentType) => {
    try {
      // Determine if this is a sales or purchase document - ONLY actual completed transactions
      const salesDocuments = ['invoice', 'delivery-note']; // Only actual sales, not quotes/proforma
      const purchaseDocuments = ['purchase-order', 'reception-note', 'return-slip']; // Only actual purchases, not quote requests
      
      if (salesDocuments.includes(documentType)) {
        // Update linked sale status
        await updateTransactionStatus(`sale-doc-${documentId}`, 'SALE', newStatus);
      } else if (purchaseDocuments.includes(documentType)) {
        // Update linked purchase status  
        await updateTransactionStatus(`purchase-doc-${documentId}`, 'PURCHASE', newStatus);
      }
      
      console.log(`? Document status '${newStatus}' synced to Purchases/Sales for document ${documentId}`);
      return { success: true };
    } catch (error) {
      console.error('Error syncing document status change:', error);
      return { success: false, error: error.message };
    }
  };

  const handleBackFromPreview = () => {
    setCurrentStep('create');
  };

  // Fonction pour valider les informations fiscales du client
  const validateClientFiscalInfo = (clientData) => {
    const missingFields = [];
    
    if (!clientData?.fiscalId && !clientData?.nif) missingFields.push('NIF');
    if (!clientData?.article) missingFields.push('Article');
    if (!clientData?.rc) missingFields.push('RC N°');
    if (!clientData?.contactPhone && !clientData?.phone) missingFields.push('Contact Tel');
    
    return {
      isValid: missingFields.length === 0,
      missingFields
    };
  };
  const handlePreview = () => {
    if (!formData.number) {
      alert(t('document_creation.alerts.enter_document_number'));
      return;
    }

    if (!formData.date) {
      alert(t('document_creation.alerts.enter_date'));
      return;
    }

    // — REMOVED: Auto-selection of priority = 'normale' (users must select explicitly)
    // if (!formData.priority && (selectedType.id === 'purchase-order' || ...)) {
    //   formData.priority = 'normale';
    // }

    // — REMOVED: Auto-fill default values for Devis/Quote (users must select explicitly)
    // if (selectedType.id === 'quote') {
    //   if (!String(formData.deliveryTime || '').trim()) {
    //     formData.deliveryTime = formData.quoteDeliveryTime || '15 jours ouvrables';
    //   }
    //   if (!String(formData.estimatedDelivery || '').trim()) {
    //     formData.estimatedDelivery = formData.deliveryTime;
    //   }
    //   if (!String(formData.deliveryDelay || '').trim()) {
    //     formData.deliveryDelay = '15 jours ouvrables';
    //   }
    //   if (!String(formData.paymentMethod || '').trim()) {
    //     formData.paymentMethod = formData.quotePaymentMethod || 'virement';
    //   }
    //   if (!String(formData.paymentTerms || '').trim()) {
    //     formData.paymentTerms = 'comptant';
    //   }
    // }

    // Aperçu autorisé sans fournisseur/client : préparation optionnelle des codes d'affichage si un partenaire est choisi
    if (resolveIsSupplierDocument(selectedType.id, formData)) {
      // For stock-entry, supplier might be just text in stockEntrySupplier
      if (formData.supplier) {
        const selectedSupplier = allSuppliers.find((s) => String(s.id) === String(formData.supplier));
      if (selectedSupplier) {
          const index = allSuppliers.findIndex((s) => String(s.id) === String(formData.supplier));
        const formattedNumber = `FRN-${String(index >= 0 ? index + 1 : 1).padStart(2, '0')}`;
        selectedSupplier.formattedNumber = formattedNumber;
        formData.supplierCode = formattedNumber;
        console.log(`?? DocumentCreation handlePreview: Generated ${formattedNumber} for supplier ${selectedSupplier.name} (index: ${index})`);
        }
      }
    } else {
      const selectedClient = allClients.find((c) => String(c.id) === String(formData.client));
      if (selectedClient) {
        const clientIndex = allClients.findIndex((c) => String(c.id) === String(formData.client));
        const clientFormattedNumber = `CLI-${String(clientIndex >= 0 ? clientIndex + 1 : 1).padStart(2, '0')}`;
        formData.clientCode = clientFormattedNumber;
        console.log(`?? DocumentCreation handlePreview: Generated ${clientFormattedNumber} for client ${selectedClient.name} (index: ${clientIndex})`);
      }
    }

    setCurrentStep('preview');
  };

  const handleGeneratePDF = () => {
    // Validation des données minimales
    if (!formData.number) {
      alert(t('document_creation.alerts.enter_document_number'));
      return;
    }

    if (!formData.date) {
      alert(t('document_creation.alerts.enter_date'));
      return;
    }

    // ?? RESTRICTIONS REMOVED: Allow PDF generation without products/items
    // Pour les documents de facturation, vérification des articles
    // if (['invoice', 'proforma', 'quote', 'purchase-order', 'delivery-note', 'shipping-slip'].includes(selectedType.id)) {
    //   if (!formData.items || formData.items.length === 0 || !formData.items[0].product) {
    //     alert(t('document_creation.alerts.add_products'));
    //     return;
    //   }
    // }

    // Récupération des données de la partie contractante — liste complète du contexte
    let isSupplierDocument = resolveIsSupplierDocument(selectedType.id, formData);

    const selectedPartner = resolveDocumentPartner(formData, isSupplierDocument, allClients, allSuppliers);
    
    // Télécharger directement le PDF sans passer par l'aperçu
    try {
      const pdfFormData =
        selectedType.id === 'credit-note' || selectedType.id === 'credit_note'
          ? coerceCreditNoteFormData(formData)
          : formData;
      downloadDocumentPDF(
        selectedType.id,
        pdfFormData,
        normalizeClientDataForInvoicePreview(selectedPartner),
        settings
      )
        .then(() => {
          console.log('PDF downloaded successfully');
        })
        .catch(error => {
          console.error('Error downloading PDF:', error);
          alert(t('document_creation.alerts.pdf_error'));
        });
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert(t('document_creation.alerts.pdf_error'));
    }
  };

  const handleBack = () => {
    if (isAgentEmbed) {
      window.parent.postMessage({ type: 'agent-document-embed-close' }, window.location.origin);
      return;
    }

    const fromModule = searchParams.get('from');
    const editIdParam = searchParams.get('editId');
    const sectionReturnRoute = resolveSectionRouteFromFromParam(fromModule);
    const inventaireDocIds = ['reception-note', 'stock-entry', 'exit-note', 'transfer-note'];
    const logistiqueDocIds = ['shipping-slip'];
    const isInventaireDoc = selectedType && inventaireDocIds.includes(selectedType.id);
    const isLogistiqueDoc = selectedType && logistiqueDocIds.includes(selectedType.id);

    if (!editIdParam && !isEditMode && sectionReturnRoute) {
      navigate(sectionReturnRoute, { replace: true });
      return;
    }

    if (!editIdParam && !isEditMode && (fromModule === 'logistique' || isLogistiqueDoc)) {
      navigate('/logistique/document-creation', { replace: true });
      return;
    }

    if (!editIdParam && !isEditMode && (fromModule === 'inventaire' || isInventaireDoc)) {
      navigate('/inventaire/document-creation', { replace: true });
      return;
    }

    if (sectionReturnRoute) {
      navigate(sectionReturnRoute, { replace: true });
      return;
    }

    navigate('/document-creation', { replace: true });
  };

  // Preview mode
  if (currentStep === 'preview' && selectedType) {
    // Partenaire issu de la base ou libellé saisi / placeholder pour aperçu sans sélection
    const isSupplierDocument = resolveIsSupplierDocument(selectedType.id, formData);
    
    const selectedPartner = resolveDocumentPartner(formData, isSupplierDocument, allClients, allSuppliers);
    
    // ?? USE TEMPLATE WRAPPER - It will handle template selection, data processing, and rendering
    console.log(`?? Rendering document via TemplateWrapper for type: ${selectedType.id}`);
    
      return (
      <>
        <style>{DOCUMENT_CREATION_INJECTED_STYLES}</style>
      <div className={`flex flex-col${documentCreationLightClass ? ` ${documentCreationLightClass}` : ''}`}>
        {/* Sticky header — same neutral glass as step "create" (no blue tint on the bar) */}
        <div 
          className="document-creation-sticky-bar sticky top-0 z-50 backdrop-blur-lg rounded-xl p-5 border border-white/20 overflow-visible shadow-lg mb-4"
          style={documentCreationStickyBarStyle}
        >
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex min-w-0 flex-wrap items-center gap-3 lg:gap-4">
              <button
                onClick={handleBackFromPreview}
                className={`flex items-center gap-2 ${isDashboardLightMode ? 'text-gray-800 hover:text-gray-600' : 'text-white hover:text-white/80'} transition-colors`}
              >
                <ArrowLeft className={`h-5 w-5 ${isRTL ? 'rotate-180' : ''}`} /> {t('common.back')}
              </button>
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-lg" style={{ backgroundColor: `${selectedType.color}20` }}>
                  {React.createElement(selectedType.icon, { size: 24, style: { color: selectedType.color } })}
                </div>
                <h1 className={`text-2xl font-bold ${isDashboardLightMode ? 'text-gray-900' : 'text-white'}`}>
                  {docDisplayTitle(selectedType)}
                </h1>
              </div>
              
              {/* Paper Format Toggle in Preview */}
              <div className="flex gap-1 bg-white/10 p-1 rounded-lg border border-white/20">
                {['A4', 'A5'].map((format) => (
                  <button
                    key={format}
                    onClick={() => setSelectedPaperFormat(format)}
                    className={`px-3 py-1.5 rounded-lg font-medium text-sm transition-all duration-200 ${
                      selectedPaperFormat === format
                        ? 'bg-blue-500 text-white shadow-lg'
                        : 'text-blue-400 hover:text-blue-300 hover:bg-white/20'
                    }`}
                  >
                    {format}
                  </button>
                ))}
              </div>
            </div>

            {/* Action Buttons: Print, Download & Save */}
            <div className="document-creation-sticky-bar__actions flex shrink-0 flex-row flex-nowrap items-center gap-2 md:gap-3">
              <button
                onClick={() => {
                  const pdfFd =
                    selectedType.id === 'credit-note' || selectedType.id === 'credit_note'
                      ? coerceCreditNoteFormData(formData)
                      : formData;
                  downloadDocumentPDF(
                    selectedType.id,
                    pdfFd,
                    normalizeClientDataForInvoicePreview(selectedPartner),
                    settings
                  );
                }}
                className="document-creation-action-btn group relative bg-purple-500/40 hover:bg-purple-500/50 text-white px-5 py-2.5 rounded-xl font-medium flex items-center gap-2 border border-purple-500/50 hover:border-purple-400/70 shadow-lg shadow-purple-500/20"
              >
                <Printer className="document-creation-action-btn__icon h-5 w-5" />
                <span>{t('document_creation.print')}</span>
              </button>
              <button
                onClick={() => {
                  const pdfFd =
                    selectedType.id === 'credit-note' || selectedType.id === 'credit_note'
                      ? coerceCreditNoteFormData(formData)
                      : formData;
                  downloadDocumentPDFSilent(
                    selectedType.id,
                    pdfFd,
                    normalizeClientDataForInvoicePreview(selectedPartner),
                    settings
                  );
                }}
                className="document-creation-action-btn group relative bg-green-500/40 hover:bg-green-500/50 text-white px-5 py-2.5 rounded-xl font-medium flex items-center gap-2 border border-green-500/50 hover:border-green-400/70 shadow-lg shadow-green-500/20"
              >
                <Download className="document-creation-action-btn__icon h-5 w-5" />
                <span>{t('document_creation.download')}</span>
              </button>
              <button
                onClick={handleSave}
                className="document-creation-action-btn group relative bg-blue-500/40 hover:bg-blue-500/50 text-white px-5 py-2.5 rounded-xl font-medium flex items-center gap-2 border border-blue-500/50 hover:border-blue-400/70 shadow-lg shadow-blue-500/20"
              >
                <Save className="document-creation-action-btn__icon h-5 w-5" />
                <span>{t('document_creation.form.save_document')}</span>
              </button>
            </div>
          </div>
        </div>

        {/* ?? RENDER TEMPLATE VIA WRAPPER (Wrapper handles template selection + drag/drop) */}
        <TemplateWrapper 
          documentType={selectedType.id}
          formData={formData}
          clientData={selectedPartner}
          partnerType={isSupplierDocument ? 'supplier' : 'client'}
          paperSize={selectedPaperFormat}
          onBack={handleBackFromPreview}
          onSave={handleSave}
        />
      </div>
      </>
      );
  }
  if (currentStep === 'create' && selectedType) {
    // Cette variable n'est plus nécessaire ici car nous avons les onglets
    // mais on la garde pour la rétrocompatibilité
    
    return (
      <>
        <style>{DOCUMENT_CREATION_INJECTED_STYLES}{isAgentEmbed ? AGENT_EMBED_STYLES : ''}</style>
      <div 
        style={{ 
          padding: isAgentEmbed ? '0.75rem' : 0, 
          maxWidth: '100%', 
          position: 'relative', 
          width: '100%', 
          minWidth: 0,
          display: 'flex', 
          flexDirection: 'column',
          boxSizing: 'border-box',
        }} 
        className={`animate-fade-in${documentCreationLightClass ? ` ${documentCreationLightClass}` : ''}${isAgentEmbed ? ' agent-document-embed-root' : ''}`}
      >
        {/* Content Container - NO internal scrollbar */}
        <div style={{ display: 'flex', gap: '0.5rem', width: '100%' }}>
          <div className="flex min-w-0 w-full flex-1 flex-col gap-4">
            {/* Sticky Header - Inside scrolling container */}
            <div 
              className="document-creation-sticky-bar sticky top-0 z-50 rounded-xl border border-white/20 p-3 shadow-lg backdrop-blur-lg overflow-visible sm:p-5"
              style={documentCreationStickyBarStyle}
            >
              {/* Mobile : ligne outils + ligne titre lisible. Desktop : une seule ligne. */}
              <div className="md:hidden">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex min-w-0 flex-wrap items-center gap-1.5 sm:gap-2">
                    <button
                      type="button"
                      onClick={handleBack}
                      className={`flex shrink-0 items-center gap-1 text-sm font-medium ${isDashboardLightMode ? 'text-black hover:text-black/80' : 'text-white hover:text-white/80'} transition-colors`}
                    >
                      <ArrowLeft className="h-5 w-5 shrink-0" />
                      <span>{t('common.back')}</span>
                    </button>
                    {selectedType && (() => {
                      const availableFormats = getAvailableFormats(selectedType.id);
                      return (
                        <div className="paper-format-mobile-toggle inline-flex shrink-0 items-center gap-px rounded-lg border border-white/20 bg-white/10 p-px shadow-md">
                          {availableFormats.map((format) => (
                            <button
                              key={format}
                              type="button"
                              onClick={() => setSelectedPaperFormat(format)}
                              className={`box-border flex !size-8 !min-h-8 !min-w-8 !max-h-8 !max-w-8 flex-none items-center justify-center !p-0 text-[11px] font-bold uppercase leading-none tracking-tight transition-all duration-150 aspect-square rounded-md ${
                                selectedPaperFormat === format
                                  ? 'bg-blue-500 text-white shadow-sm'
                                  : 'text-blue-400 hover:bg-white/10 hover:text-blue-300'
                              }`}
                            >
                              {format}
                            </button>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                  <div className="document-creation-sticky-bar__actions flex shrink-0 flex-row flex-nowrap items-center gap-1.5 sm:gap-2">
                    <button
                      type="button"
                      onClick={handlePreview}
                      className={`document-creation-action-btn group relative inline-flex shrink-0 items-center gap-1 rounded-lg border px-2 py-1 text-xs font-medium ${
                        isDashboardLightMode
                          ? 'border-gray-300/50 bg-white/70 text-gray-800 hover:bg-white/90 hover:border-gray-400/60 shadow-sm'
                          : 'border-white/30 bg-white/10 text-white hover:bg-white/20 hover:border-white/45 shadow-md shadow-black/5'
                      }`}
                      title={t('document_creation.preview')}
                      aria-label={t('document_creation.preview')}
                    >
                      <FileSearch className="document-creation-action-btn__icon h-4 w-4 shrink-0" strokeWidth={2} />
                      <span>{t('document_creation.preview')}</span>
                    </button>
                    <button
                      type="button"
                      onClick={handleSave}
                      className="document-creation-action-btn group relative inline-flex shrink-0 items-center gap-1 rounded-lg border border-blue-500/50 bg-blue-500/40 px-2 py-1 text-xs font-medium text-white hover:border-blue-400/70 hover:bg-blue-500/50 shadow-md shadow-blue-500/20"
                      title={t('document_creation.form.save_document')}
                      aria-label={t('document_creation.form.save_document')}
                    >
                      <Save className="document-creation-action-btn__icon h-4 w-4 shrink-0" strokeWidth={2} />
                      <span>{t('document_creation.form.save_document')}</span>
                    </button>
                  </div>
                </div>
                <div className="flex items-start gap-2 border-t border-white/10 pt-2">
                  <div
                    className="mt-0.5 shrink-0 rounded-lg p-1.5"
                    style={{ backgroundColor: `${selectedType.color}20` }}
                  >
                    {React.createElement(selectedType.icon, {
                      size: 20,
                      style: { color: selectedType.color },
                    })}
                  </div>
                  <h1
                    className={`min-w-0 flex-1 text-sm font-bold leading-snug break-words ${isDashboardLightMode ? 'text-black' : 'text-white'}`}
                  >
                    {isEditMode ? `${t('document_creation.form.edit_prefix')} ${docDisplayTitle(selectedType)}` : docDisplayTitle(selectedType)}
                  </h1>
                </div>
              </div>

              <div className="document-creation-sticky-bar__row hidden w-full min-w-0 flex-nowrap items-center gap-3 md:flex">
                <button
                  type="button"
                  onClick={handleBack}
                  className={`flex shrink-0 items-center gap-1.5 text-sm font-medium ${isDashboardLightMode ? 'text-black hover:text-black/80' : 'text-white hover:text-white/80'} transition-colors`}
                >
                  <ArrowLeft className="h-5 w-5 shrink-0" />
                  <span className="whitespace-nowrap">{t('common.back')}</span>
                </button>

                {selectedType && (() => {
                  const availableFormats = getAvailableFormats(selectedType.id);
                  return (
                    <div className="flex shrink-0 gap-0.5 rounded-lg border border-white/20 bg-white/10 p-0.5 shadow-lg">
                      {availableFormats.map((format) => (
                        <button
                          key={format}
                          type="button"
                          onClick={() => setSelectedPaperFormat(format)}
                          className={`rounded-md px-3 py-1.5 text-sm font-medium transition-all duration-200 ${
                            selectedPaperFormat === format
                              ? 'bg-blue-500 text-white shadow-lg ring-2 ring-blue-300/40'
                              : 'text-blue-400 hover:bg-white/10 hover:text-blue-300'
                          }`}
                        >
                          {format}
                        </button>
                      ))}
                    </div>
                  );
                })()}

                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <div
                    className="shrink-0 rounded-lg p-2"
                    style={{ backgroundColor: `${selectedType.color}20` }}
                  >
                    {React.createElement(selectedType.icon, {
                      size: 22,
                      style: { color: selectedType.color },
                    })}
                  </div>
                  <h1
                    className={`min-w-0 truncate text-xl font-bold md:text-2xl ${isDashboardLightMode ? 'text-black' : 'text-white'}`}
                    title={isEditMode ? `${t('document_creation.form.edit_prefix')} ${docDisplayTitle(selectedType)}` : docDisplayTitle(selectedType)}
                  >
                    {isEditMode ? `${t('document_creation.form.edit_prefix')} ${docDisplayTitle(selectedType)}` : docDisplayTitle(selectedType)}
                  </h1>
                </div>

                <div className="document-creation-sticky-bar__actions flex shrink-0 flex-row flex-nowrap items-center gap-2">
                  <button
                    type="button"
                    onClick={handlePreview}
                    className={`document-creation-action-btn group relative flex shrink-0 items-center gap-2 rounded-xl border px-5 py-2.5 font-medium ${
                      isDashboardLightMode
                        ? 'border-gray-300/50 bg-white/70 text-gray-800 hover:bg-white/90 hover:border-gray-400/60 shadow-sm'
                        : 'border-white/30 bg-white/10 text-white hover:bg-white/20 hover:border-white/45 shadow-lg shadow-black/5'
                    }`}
                    title={t('document_creation.preview')}
                    aria-label={t('document_creation.preview')}
                  >
                    <FileSearch className="document-creation-action-btn__icon h-5 w-5 shrink-0" />
                    <span className="whitespace-nowrap">{t('document_creation.preview')}</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    className="document-creation-action-btn group relative bg-blue-500/40 hover:bg-blue-500/50 text-white px-5 py-2.5 rounded-xl font-medium flex shrink-0 items-center gap-2 border border-blue-500/50 hover:border-blue-400/70 shadow-lg shadow-blue-500/20"
                  >
                    <Save className="document-creation-action-btn__icon h-5 w-5" />
                    <span className="whitespace-nowrap">{t('document_creation.form.save_document')}</span>
                  </button>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-4 w-full pb-4">

          {/* ===== PARTNER CARD (Client / Fournisseur) ===== */}
          {(() => {
            const isCreditNote = isCreditNoteDocType(selectedType?.id);
            const creditNoteIsSupplier = isCreditNote && formData.creditNoteParty === 'supplier';
            const clientDocTypes = ['invoice', 'proforma', 'delivery-note', 'delivery', 'material-exit', 'payment-receipt', 'client-return-slip', 'quote'];
            const supplierDocTypes = ['purchase-order', 'quote-request', 'proforma-request', 'return-slip', 'reception-note', 'payment-voucher', 'stock-entry'];
            const bothDocTypes = ['exit-note'];
            const isClientDoc = isCreditNote ? !creditNoteIsSupplier : clientDocTypes.includes(selectedType?.id);
            const isSupplierDoc = isCreditNote ? creditNoteIsSupplier : supplierDocTypes.includes(selectedType?.id);
            const isBothDoc = bothDocTypes.includes(selectedType?.id);
            if (!isClientDoc && !isSupplierDoc && !isBothDoc) return null;

            // For docs that accept both, use the toggle state
            const resolvedIsClient = isClientDoc || (isBothDoc && partnerCardType === 'client');
            const partnerLabel = isBothDoc ? (partnerCardType === 'client' ? t('document_creation.document_client') : t('document_creation.supplier_info')) : (isClientDoc ? t('document_creation.document_client') : t('document_creation.supplier_info'));
            const partnerName = resolvedIsClient ? (formData.clientName || '') : (formData.supplierName || '');
            const partnerList = resolvedIsClient ? availableClientsForDropdown : availableSuppliersForDropdown;
            const allPartners = resolvedIsClient ? (allClients || []) : (allSuppliers || []);
            const partnerId = resolvedIsClient ? formData.client : formData.supplier;
            const partnerDetails = allPartners.find(p => String(p.id) === String(partnerId)) ||
                                   partnerList.find(p => p.name === partnerName || p.value === partnerName);

            const displayName = partnerDetails?.company_name || partnerDetails?.name || partnerName;
            const partnerSelectValue = resolvePartnerDropdownLabel(
              partnerId,
              partnerName,
              partnerList,
              allPartners,
            );

            const handlePartnerChange = (value) => {
              if (resolvedIsClient) {
                const sel = availableClientsForDropdown.find(c => c.name === value || c.value === value);
                setFormData((prev) => {
                  const next = {
                    ...prev,
                    client: sel?.id || '',
                    clientName: value,
                  };
                  if (sel?.code) next.clientCode = sel.code;
                  if (sel?.email) next.clientEmail = sel.email;
                  if (sel?.phone) next.clientPhone = sel.phone;
                  if (prev.showRecuePar && sel) {
                    const { name, role } = resolvePartnerDestinataire(sel);
                    next.recueParName = name;
                    next.recueParRole = role;
                  }
                  return next;
                });
              } else {
                const sel = availableSuppliersForDropdown.find(s => s.name === value || s.value === value);
                setFormData((prev) => {
                  const next = {
                    ...prev,
                    supplier: sel?.id || '',
                    supplierName: value,
                  };
                  if (sel?.code) next.supplierCode = sel.code;
                  if (sel?.email) next.supplierEmail = sel.email;
                  if (sel?.phone) next.supplierPhone = sel.phone;
                  if (prev.showRecuePar && sel) {
                    const { name, role } = resolvePartnerDestinataire(sel);
                    next.recueParName = name;
                    next.recueParRole = role;
                  }
                  return next;
                });
              }
            };

            const displayPhone = partnerDetails?.phone || partnerDetails?.phone1 || '';
            const displayEmail = partnerDetails?.email || '';
            const displayNif =
              partnerDetails?.fiscal_id ||
              partnerDetails?.nif ||
              partnerDetails?.fiscalId ||
              '';
            const displayRc =
              partnerDetails?.rc ||
              partnerDetails?.rc_number ||
              partnerDetails?.registrationNumber ||
              '';
            const displayArt = partnerDetails?.article || '';
            const displayNis = partnerDetails?.nis || partnerDetails?.nis_number || '';
            const displayAddress =
              [partnerDetails?.address, partnerDetails?.city].filter(Boolean).join(', ') ||
              partnerDetails?.address ||
              '';
            const hasDetails = !!(partnerDetails && partnerId && displayName && displayName !== '---');

            return (
              <div className="bg-white/5 rounded-xl p-5 border border-white/20 overflow-hidden">
                <div className="flex items-center gap-3 mb-4">
                  <User className="h-5 w-5 text-blue-400" />
                  <h3 className={`text-lg font-semibold ${isDashboardLightMode ? 'text-black' : 'text-white'}`}>
                    {partnerLabel}
                  </h3>
                  {isBothDoc && (
                    <div className="flex gap-1 bg-white/5 p-1 rounded-xl border border-white/20 ml-2">
                      {['client', 'supplier', 'warehouse', 'other'].map((type) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => {
                            setPartnerCardType(type);
                            handleInputChange('exitNotePartnerType', type);
                            handleInputChange('clientName', ''); handleInputChange('client', '');
                            handleInputChange('supplierName', ''); handleInputChange('supplier', '');
                            handleInputChange('exitNoteWarehouse', ''); handleInputChange('exitNoteWarehouseCode', '');
                            handleInputChange('exitNoteWarehouseAddress', ''); handleInputChange('exitNoteWarehouseManager', '');
                            handleInputChange('exitNoteMotif', '');
                            handleInputChange('exitNoteOtherResponsible', ''); handleInputChange('exitNoteOtherRef', '');
                            handleInputChange('exitNoteOtherBeneficiary', ''); handleInputChange('exitNoteOtherDescription', '');
                            handleInputChange('exitNoteOtherDateConstat', ''); handleInputChange('exitNoteOtherLocation', '');
                          }}
                          className={`px-3 py-1 rounded-lg text-sm font-medium transition-all ${partnerCardType === type ? 'bg-blue-500 text-white' : 'text-white/60 hover:text-white hover:bg-white/10'}`}
                        >
                          {type === 'client' ? t('document_creation.document_client') : type === 'supplier' ? t('document_creation.supplier_info') : type === 'warehouse' ? t('document_creation.form.warehouse') : t('document_creation.form.other')}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Content by type */}
                {(!isBothDoc || partnerCardType === 'client' || partnerCardType === 'supplier') && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Row 1: Selector + Téléphone + Email */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="form-group">
                        <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{ color: isDashboardLightMode ? '#000000' : '#ffffff' }}>
                          <RequiredLabelText
                            text={resolvedIsClient ? t('document_creation.form.select_a_client') : t('document_creation.form.select_a_supplier')}
                            required={requirePartner}
                          />
                        </label>
                        <CustomSelect
                          value={partnerSelectValue}
                          onChange={handlePartnerChange}
                          options={partnerList}
                          placeholder={resolvedIsClient ? t('document_creation.form.search_or_enter_client') : t('document_creation.form.search_or_enter_supplier')}
                          searchable
                        />
                      </div>
                      <div className="form-group">
                        <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{ color: isDashboardLightMode ? '#000000' : '#ffffff' }}>{t('document_creation.form.phone')}</label>
                        <input type="text" value={displayPhone} readOnly className="readonly-input" placeholder={t('document_creation.form.phone')} />
                      </div>
                      <div className="form-group">
                        <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{ color: isDashboardLightMode ? '#000000' : '#ffffff' }}>{t('common.email')}</label>
                        <input type="text" value={displayEmail} readOnly className="readonly-input" placeholder={t('common.email')} />
                      </div>
                    </div>
                    {/* Row 2: NIF + RC + Art + NIS */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="form-group">
                        <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{ color: isDashboardLightMode ? '#000000' : '#ffffff' }}>{t('document_creation.form.nif')}</label>
                        <input type="text" value={displayNif} readOnly className="readonly-input" placeholder={t('document_creation.form.nif')} />
                      </div>
                      <div className="form-group">
                        <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{ color: isDashboardLightMode ? '#000000' : '#ffffff' }}>{t('document_creation.form.rc')}</label>
                        <input type="text" value={displayRc} readOnly className="readonly-input" placeholder={t('document_creation.form.rc')} />
                      </div>
                      <div className="form-group">
                        <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{ color: isDashboardLightMode ? '#000000' : '#ffffff' }}>{t('document_creation.form.art')}</label>
                        <input type="text" value={displayArt} readOnly className="readonly-input" placeholder={t('document_creation.form.art')} />
                      </div>
                      <div className="form-group">
                        <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{ color: isDashboardLightMode ? '#000000' : '#ffffff' }}>{t('document_creation.form.nis')}</label>
                        <input type="text" value={displayNis} readOnly className="readonly-input" placeholder={t('document_creation.form.nis')} />
                      </div>
                    </div>
                    {/* Row 3: Adresse */}
                    <div className="form-group">
                      <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{ color: isDashboardLightMode ? '#000000' : '#ffffff' }}>{t('document_creation.form.address')}</label>
                      <input type="text" value={displayAddress} readOnly className="readonly-input" placeholder={t('document_creation.form.full_address')} />
                    </div>
                  </div>
                )}

                {/* Entrepôt fields */}
                {isBothDoc && partnerCardType === 'warehouse' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="form-group">
                        <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{ color: isDashboardLightMode ? '#000000' : '#ffffff' }}>{formLabel('warehouse_name')}</label>
                        <CustomSelect
                          value={formData.exitNoteWarehouse || ''}
                          onChange={(value) => {
                            handleInputChange('exitNoteWarehouse', value);
                            const wh = availableWarehousesForDropdown.find(w => w.value === value || w.name === value);
                            if (wh) {
                              if (wh.reference) handleInputChange('exitNoteWarehouseCode', wh.reference);
                              if (wh.address) handleInputChange('exitNoteWarehouseAddress', wh.address);
                              if (wh.manager) handleInputChange('exitNoteWarehouseManager', wh.manager);
                            }
                          }}
                          options={availableWarehousesForDropdown}
                          placeholder={t('document_creation.form.search_warehouse')}
                          searchable
                        />
                      </div>
                      <div className="form-group">
                        <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{ color: isDashboardLightMode ? '#000000' : '#ffffff' }}>{t('document_creation.form.reference_label')}</label>
                        <input type="text" value={formData.exitNoteWarehouseCode || ''} readOnly className="readonly-input" placeholder={formLabel('ph_warehouse_code')} />
                      </div>
                      <div className="form-group">
                        <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{ color: isDashboardLightMode ? '#000000' : '#ffffff' }}>{t('document_creation.form.manager_name')}</label>
                        <input type="text" value={formData.exitNoteWarehouseManager || ''} readOnly className="readonly-input" placeholder={t('document_creation.form.manager_name')} />
                      </div>
                      <div className="form-group">
                        <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{ color: isDashboardLightMode ? '#000000' : '#ffffff' }}>{t('document_creation.form.address')}</label>
                        <input type="text" value={formData.exitNoteWarehouseAddress || ''} readOnly className="readonly-input" placeholder={t('document_creation.form.warehouse_address')} />
                      </div>
                    </div>
                  </div>
                )}

                {/* Autre — motif + details */}
                {isBothDoc && partnerCardType === 'other' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Row 1 (3 cols): Destinataire | Motif de sortie | Date de constat */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="form-group">
                        <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{ color: isDashboardLightMode ? '#000000' : '#ffffff' }}>{formLabel('recipient')}</label>
                        <input
                          type="text"
                          value={formData.exitNoteOtherBeneficiary || ''}
                          onChange={(e) => handleInputChange('exitNoteOtherBeneficiary', e.target.value)}
                          className="form-input"
                          placeholder={t('document_creation.form.recipient_name')}
                        />
                      </div>
                      <div className="form-group">
                        <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{ color: isDashboardLightMode ? '#000000' : '#ffffff' }}>{t('document_creation.form.exit_reason')}</label>
                        <CustomSelect
                          value={formData.exitNoteMotif || ''}
                          onChange={(value) => handleInputChange('exitNoteMotif', value)}
                          options={[
                            { value: 'Perte / Manquant', label: formLabel('exit_reasons.loss') },
                            { value: 'Casse / Dommage', label: formLabel('exit_reasons.damage') },
                            { value: 'Produit périmé', label: formLabel('exit_reasons.expired') },
                            { value: 'Don / Donation', label: formLabel('exit_reasons.donation') },
                            { value: 'Échantillon', label: formLabel('exit_reasons.sample') },
                            { value: 'Rebut / Déchet', label: formLabel('exit_reasons.scrap') },
                            { value: 'Destruction', label: formLabel('exit_reasons.destruction') },
                            { value: 'Autre motif', label: formLabel('exit_reasons.other') }
                          ]}
                          placeholder={t('document_creation.form.select_reason')}
                          searchable
                        />
                      </div>
                      <div className="form-group">
                        <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{ color: isDashboardLightMode ? '#000000' : '#ffffff' }}>{t('document_creation.form.finding_date')}</label>
                        <CustomDatePicker
                          value={formData.exitNoteOtherDateConstat || ''}
                          onChange={(dateString) => handleInputChange('exitNoteOtherDateConstat', dateString)}
                        />
                      </div>
                    </div>
                    {/* Row 2 (3 cols): Lieu / Emplacement | Référence interne | Description */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="form-group">
                        <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{ color: isDashboardLightMode ? '#000000' : '#ffffff' }}>{formLabel('location_place')}</label>
                        <input
                          type="text"
                          value={formData.exitNoteOtherLocation || ''}
                          onChange={(e) => handleInputChange('exitNoteOtherLocation', e.target.value)}
                          className="form-input"
                          placeholder={formLabel('ph_location_zone')}
                        />
                      </div>
                      <div className="form-group">
                        <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{ color: isDashboardLightMode ? '#000000' : '#ffffff' }}>{t('document_creation.form.internal_reference')}</label>
                        <input
                          type="text"
                          value={formData.exitNoteOtherRef || ''}
                          onChange={(e) => handleInputChange('exitNoteOtherRef', e.target.value)}
                          className="form-input"
                          placeholder={formLabel('ph_internal_ref')}
                        />
                      </div>
                      <div className="form-group">
                        <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{ color: isDashboardLightMode ? '#000000' : '#ffffff' }}>{t('document_creation.form.description')}</label>
                        <input
                          type="text"
                          value={formData.exitNoteOtherDescription || ''}
                          onChange={(e) => handleInputChange('exitNoteOtherDescription', e.target.value)}
                          className="form-input"
                          placeholder={t('document_creation.form.exit_details')}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Document Header - Hidden for quote-request and proforma-request (have combined cards) */}
          {selectedType.id !== 'quote-request' && selectedType.id !== 'proforma-request' && selectedType.id !== 'purchase-order' && selectedType.id !== 'return-slip' && !isPaymentVoucherDoc(selectedType) && selectedType.id !== 'quote' && selectedType.id !== 'proforma' && selectedType.id !== 'invoice' && selectedType.id !== 'delivery-note' && selectedType.id !== 'delivery' && selectedType.id !== 'material-exit' && !isPaymentReceiptDoc(selectedType) && selectedType.id !== 'client-return-slip' && !isCreditNoteDocType(selectedType.id) && selectedType.id !== 'reception-note' && selectedType.id !== 'stock-entry' && selectedType.id !== 'exit-note' && selectedType.id !== 'transfer-note' && selectedType.id !== 'shipping-slip' && (
          <div className="bg-white/5 rounded-xl p-5 border border-white/20 overflow-hidden">
            <div className="flex items-center gap-2 mb-4">
              <Hash className="h-5 w-5 text-blue-400" />
              <h3 className="text-lg font-semibold text-white">{t('document_creation.header_info')}</h3>
            </div>
            
            <div className={`grid gap-4 ${
              (selectedType.id === 'quote-request' || selectedType.id === 'proforma-request' || selectedType.id === 'purchase-order' || selectedType.id === 'reception-note' || selectedType.id === 'return-slip' || selectedType.id === 'payment-voucher' || selectedType.id === 'quote' || selectedType.id === 'proforma' || selectedType.id === 'invoice' || selectedType.id === 'material-exit' || selectedType.id === 'shipping-slip' || selectedType.id === 'payment-receipt' || selectedType.id === 'credit-note' || selectedType.id === 'exit-note' || selectedType.id === 'transfer-note' || selectedType.id === 'stock-entry' || selectedType.id === 'client-return-slip')
                ? 'grid-cols-1 md:grid-cols-3'
                : 'grid-cols-1 md:grid-cols-3'
            }`}>
              <div className="form-group">
                <label className="text-white/80">{t('document_creation.document_number')}</label>
                <input
                  type="text"
                  value={formData.number || ''}
                  readOnly
                  className="readonly-input"
                />
              </div>
              
              <div className="form-group">
                <label className="text-white/80">{t('document_creation.document_date')}</label>
                <CustomDatePicker
                  value={formData.date}
                  onChange={(dateString) => {
                    handleInputChange('date', dateString);
                  }}
                />
              </div>

              {/* 3rd Element: Date limite de réponse OR Date de livraison souhaitée OR Valable jusqu'au (Conditional) */}
              {(selectedType.id === 'quote-request' || selectedType.id === 'proforma-request') && (
                <div className="form-group">
                  <label className="text-white/80">{t('document_creation.form.response_deadline')}</label>
                  <CustomDatePicker
                    value={formData.responseDate}
                    onChange={(dateString) => handleInputChange('responseDate', dateString)}
                  />
                </div>
              )}
              
              {selectedType.id === 'purchase-order' && (
                <div className="form-group">
                  <label className="text-white/80">{t('document_creation.form.desired_delivery_date')}</label>
                  <CustomDatePicker
                    value={formData.deliveryDate}
                    onChange={(dateString) => handleInputChange('deliveryDate', dateString)}
                  />
                </div>
              )}
              
              {selectedType.id === 'reception-note' && (
                <div className="form-group">
                  <label className="text-white/80">{t('document_creation.form.reception_warehouse')}</label>
                  <CustomSelect
                    value={formData.reception?.warehouse || ''}
                    onChange={(value) => handleInputChange('reception', {...formData.reception, warehouse: value})}
                    options={availableWarehousesForDropdown}
                    placeholder={t('document_creation.form.search_warehouse')}
                    searchable
                  />
                </div>
              )}
              
              {selectedType.id === 'return-slip' && (
                <div className="form-group">
                  <label className="text-white/80">{t('document_creation.form.processing_deadline')}</label>
                  <CustomDatePicker
                    value={formData.processingDeadline}
                    onChange={(dateString) => handleInputChange('processingDeadline', dateString)}
                  />
                </div>
              )}
              
              {(selectedType.id === 'quote' || selectedType.id === 'proforma') && (
                <div className="form-group">
                  <label className="text-white/80">{t('document_creation.form.valid_until')}</label>
                  <CustomDatePicker
                    value={formData.validUntil}
                    onChange={(dateString) => handleInputChange('validUntil', dateString)}
                  />
                </div>
              )}
              
              {selectedType.id === 'invoice' && (
                <div className="form-group">
                  <label className="text-white/80">{t('document_creation.form.due_date')}</label>
                  <CustomDatePicker
                    value={formData.dueDate}
                    onChange={(dateString) => handleInputChange('dueDate', dateString)}
                  />
                </div>
              )}
              
              {selectedType.id === 'material-exit' && (
                <div className="form-group">
                  <label className="text-white/80">{t('document_creation.form.expected_delivery_date')}</label>
                  <CustomDatePicker
                    value={formData.shipment?.expectedDeliveryDate}
                    onChange={(dateString) => handleInputChange('shipment', {...formData.shipment, expectedDeliveryDate: dateString})}
                  />
                </div>
              )}
              
              {selectedType.id === 'shipping-slip' && (
                <div className="form-group">
                  <label className="text-white/80">{t('document_creation.form.expected_shipment_date')}</label>
                  <CustomDatePicker
                    value={formData.shipmentDate || formData.date}
                    onChange={(dateString) => handleInputChange('shipmentDate', dateString)}
                  />
                </div>
              )}

              {selectedType.id === 'exit-note' && (
                <div className="form-group">
                  <label className="text-white/80">{t('document_creation.form.expected_exit_date')}</label>
                  <CustomDatePicker
                    value={formData.exitNoteExpectedDate}
                    onChange={(dateString) => handleInputChange('exitNoteExpectedDate', dateString)}
                  />
                </div>
              )}

              {selectedType.id === 'transfer-note' && (
                <div className="form-group">
                  <label className="text-white/80">{t('document_creation.form.expected_transfer_date')}</label>
                  <CustomDatePicker
                    value={formData.transferNoteExpectedDate}
                    onChange={(dateString) => handleInputChange('transferNoteExpectedDate', dateString)}
                  />
                </div>
              )}

              {selectedType.id === 'stock-entry' && (
                <div className="form-group">
                  <label className="text-white/80">{t('document_creation.form.destination_warehouse')}</label>
                  <CustomSelect
                    value={formData.stockEntryWarehouse || ''}
                    onChange={(value) => handleInputChange('stockEntryWarehouse', value)}
                    options={availableWarehousesForDropdown}
                    placeholder={t('document_creation.form.search_warehouse')}
                    searchable
                  />
                </div>
              )}

              {selectedType.id === 'client-return-slip' && (
                <div className="form-group">
                  <label className="text-white/80">{t('document_creation.form.return_date')}</label>
                  <CustomDatePicker
                    value={formData.clientReturnReturnDate}
                    onChange={(dateString) => handleInputChange('clientReturnReturnDate', dateString)}
                  />
                </div>
              )}

              {/* 4th Element: Établi par - FOR ALL DOCUMENTS */}
              <div className="form-group">
                <label className="text-white/80">{t('document_creation.form.prepared_by')}</label>
                <CustomSelect
                  value={formData.issuedBy || ''}
                  onChange={(value) => {
                    handleInputChange('issuedBy', value);
                    // Sync with "Établie par" signature if enabled
                    if (formData.showEtabliePar) {
                      handleInputChange('etablieParName', value);
                    }
                  }}
                  options={availableFacturiersForDropdown}
                  placeholder={t('document_creation.form.search_or_enter_biller')}
                  searchable
                  creatable
                />
              </div>

              {/* 5th Element: Supplier/Client/Destinataire Selection (Conditional) */}
              {(selectedType.id === 'purchase-order' || selectedType.id === 'quote-request' || selectedType.id === 'proforma-request' || selectedType.id === 'reception-note' || selectedType.id === 'return-slip' || selectedType.id === 'payment-voucher') ? (
                <div className="form-group">
                  <label className="text-white/80"><RequiredLabelText text={t('document_creation.select_supplier')} required={requirePartner} /></label>
                  <CustomSelect
                    value={resolvePartnerDropdownLabel(
                      formData.supplier,
                      formData.supplierName,
                      availableSuppliersForDropdown,
                      allSuppliers,
                    )}
                    onChange={(value) => {
                      const selectedSupplier = availableSuppliersForDropdown.find(s => s.name === value || s.value === value);
                      handleInputChange('supplier', selectedSupplier?.id || '');
                      handleInputChange('supplierName', value);
                      if (selectedSupplier?.code) {
                        handleInputChange('supplierCode', selectedSupplier.code);
                      }
                      if (selectedSupplier?.email) {
                        handleInputChange('supplierEmail', selectedSupplier.email);
                      }
                      if (selectedSupplier?.phone) {
                        handleInputChange('supplierPhone', selectedSupplier.phone);
                      }
                    }}
                    options={availableSuppliersForDropdown}
                    placeholder={t('document_creation.form.search_or_enter_supplier')}
                    searchable
                  />
                </div>
              ) : selectedType.id === 'exit-note' ? (
                <div className="form-group">
                  <label className="text-white/80">{t('document_creation.form.recipient')}</label>
                  <CustomSelect
                    value={formData.exitNoteRecipient || ''}
                    onChange={(value) => handleInputChange('exitNoteRecipient', value)}
                    options={availableClientsForDropdown}
                    placeholder={t('document_creation.form.search_recipient')}
                    searchable
                  />
                </div>
              ) : (selectedType.id === 'transfer-note' || selectedType.id === 'stock-entry' || selectedType.id === 'shipping-slip') ? null : (
                <div className="form-group">
                  <label className="text-white/80"><RequiredLabelText text={t('document_creation.form.select_a_client')} required={requirePartner} /></label>
                  <CustomSelect
                    value={resolvePartnerDropdownLabel(
                      formData.client,
                      formData.clientName,
                      availableClientsForDropdown,
                      allClients,
                    )}
                    onChange={(value) => {
                      const selectedClient = availableClientsForDropdown.find(c => c.name === value || c.value === value);
                      handleInputChange('client', selectedClient?.id || '');
                      handleInputChange('clientName', value);
                      // Sync with "Reçue par" signature if enabled (for CLIENT documents)
                      if (formData.showRecuePar && selectedClient) {
                        const fullName = `${selectedClient.first_name || ''} ${selectedClient.last_name || ''}`.trim() || selectedClient.company_name || value;
                        handleInputChange('recueParName', fullName);
                      }
                      if (selectedClient?.code) {
                        handleInputChange('clientCode', selectedClient.code);
                      }
                      if (selectedClient?.email) {
                        handleInputChange('clientEmail', selectedClient.email);
                      }
                      if (selectedClient?.phone) {
                        handleInputChange('clientPhone', selectedClient.phone);
                      }
                    }}
                    options={availableClientsForDropdown}
                    placeholder={t('document_creation.form.search_or_enter_client')}
                    searchable
                  />
                </div>
              )}

              {/* 5th Element: Statut Initial - FOR ALL DOCUMENTS (MOVED TO END FOR QUOTE-REQUEST) */}
              {!(selectedType.id === 'quote-request' || selectedType.id === 'proforma-request' || selectedType.id === 'proforma') && (
                <div className="form-group">
                  <label className="text-white/80">{t('document_creation.form.initial_status')}</label>
                  <CustomSelect
                    value={documentStatusValue()}
                    onChange={handleFormStatusChange}
                    creatable={false}
                    searchable={false}
                    options={getStatusOptions(selectedType.id)}
                    placeholder={t('document_creation.form.select_placeholder')}
                  />
                </div>
              )}

              {/* Statut Initial as 6th field for quote-request/proforma-request */}
              {(selectedType.id === 'quote-request' || selectedType.id === 'proforma-request') && (
                <div className="form-group">
                  <label className="text-white/80">{t('document_creation.form.initial_status')}</label>
                  <CustomSelect
                    value={documentStatusValue()}
                    onChange={handleFormStatusChange}
                    creatable={false}
                    searchable={false}
                    options={getStatusOptions(selectedType.id)}
                    placeholder={t('document_creation.form.select_placeholder')}
                  />
                </div>
              )}

              {/* Date limite d'acceptation as 6th field for proforma */}
              {selectedType.id === 'proforma' && (
                <div className="form-group">
                  <label className="text-white/80">{t('document_creation.form.acceptance_deadline')}</label>
                  <CustomDatePicker
                    value={formData.acceptanceDeadline}
                    onChange={(dateString) => handleInputChange('acceptanceDeadline', dateString)}
                  />
                </div>
              )}

              {/* Type d'avoir as 6th field for credit-note */}
              {isCreditNoteDocType(selectedType.id) && (
                <div className="form-group">
                  <label className="text-white/80">{t('document_creation.form.credit_note_type')}</label>
                  <CustomSelect
                    value={formData.creditNote?.type || ''}
                    onChange={(value) => handleInputChange('creditNote', {...formData.creditNote, type: value})}
                    options={creditNoteTypeOptions}
                    placeholder={t('document_creation.form.select_or_enter')}
                    searchable
                  />
                </div>
              )}

              {/* Priorité as 6th field for transfer-note */}
              {selectedType.id === 'transfer-note' && (
                <div className="form-group">
                  <label className="text-white/80">{t('document_creation.form.priority')}</label>
                  <CustomSelect
                    value={formData.priority ?? ''}
                    onChange={(value) => handleInputChange('priority', value)}
                    autoSelect={false}
                    createLabel={t('document_creation.form.create_priority')}
                    options={prioritySelectOptions}
                    placeholder={t('document_creation.form.select_placeholder')}
                    searchable
                  />
                </div>
              )}

              {/* Source de l'entrée as 6th field for stock-entry */}
              {selectedType.id === 'stock-entry' && (
                <div className="form-group">
                  <label className="text-white/80">{t('document_creation.form.entry_source')}</label>
                  <CustomSelect
                    value={formData.stockEntrySource || ''}
                    onChange={(value) => handleInputChange('stockEntrySource', value)}
                    options={entrySourceOptions}
                    placeholder={t('document_creation.form.select_placeholder')}
                    searchable
                  />
                </div>
              )}

            </div>
          </div>
          )}

          {/* Détails du document - Combined card for proforma-request ONLY */}
          {selectedType.id === 'proforma-request' && (
            <div className="bg-white/5 rounded-xl p-5 border border-white/20 overflow-hidden">
              <div className="flex items-center gap-2 mb-4">
                <FileSearch className="h-5 w-5 text-blue-400" />
                <h3 className={`text-lg font-semibold ${isDashboardLightMode ? 'text-black' : 'text-white'}`}>{t('document_creation.form.document_details')}</h3>
                      </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Identifiants du document */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(96, 165, 250, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.document_identifiers')}</h4>
                    </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.document_number')}</label>
                    <input
                      type="text"
                      value={formData.number || ''}
                      readOnly
                      className="readonly-input"
                    />
              </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.date_label')}</label>
                    <CustomDatePicker
                      value={formData.date}
                      onChange={(dateString) => handleInputChange('date', dateString)}
                    />
                      </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.response_deadline')}</label>
                    <CustomDatePicker
                      value={formData.responseDate}
                      onChange={(dateString) => handleInputChange('responseDate', dateString)}
                    />
                    </div>
              </div>
                
                {/* Informations de suivi */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(96, 165, 250, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.tracking_info')}</h4>
          </div>

                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.prepared_by')}</label>
                  <CustomSelect
                    value={formData.issuedBy || ''}
                    onChange={(value) => {
                    handleInputChange('issuedBy', value);
                    // Sync with "Établie par" signature if enabled
                    if (formData.showEtabliePar) {
                      handleInputChange('etablieParName', value);
                    }
                  }}
                    options={availableFacturiersForDropdown}
                    placeholder={t('document_creation.form.search_or_enter_biller')}
                    searchable
                  />
                </div>
              
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.initial_status')}</label>
                  <CustomSelect
                    value={documentStatusValue()}
                    onChange={handleFormStatusChange}
                    creatable={false}
                    searchable={false}
                    options={getStatusOptions(selectedType.id)}
                    placeholder={t('document_creation.form.select_placeholder')}
                  />
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.request_priority')}</label>
                  <CustomSelect
                    value={formData.priority ?? ''}
                    onChange={(value) => handleInputChange('priority', value)}
                    autoSelect={false}
                    createLabel={t('document_creation.form.create_priority')}
                    options={[
                      { value: 'urgent', label: t('document_creation.form.priority_levels.urgent') },
                      { value: 'high', label: t('document_creation.form.priority_levels.high') },
                      { value: 'normale', label: t('document_creation.form.priority_levels.normal') },
                      { value: 'low', label: t('document_creation.form.priority_levels.low') }
                    ]}
                    placeholder={t('document_creation.form.select_placeholder')}
                  />
                </div>
                
                {/* Projet & Budget */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(96, 165, 250, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{formLabel('project_type_section')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.project_ref')}</label>
                    <CustomSelect
                      value={formData.projectReference || ''}
                      onChange={(value) => handleInputChange('projectReference', value)}
                      options={[]}
                      placeholder={t('document_creation.form.search_reference')}
                      searchable
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('max_estimated_budget')} (${getBrandCurrencySuffix()})</label>
                    <CustomNumberInput
                      value={formData.maxBudget || ''}
                      onChange={(value) => handleInputChange('maxBudget', value)}
                      placeholder={formLabel('ph_max_budget')}
                      suffixInText={` ${getBrandCurrencySuffix()}`}
                      step={1000}
                      min={0}
                    />
                  </div>
                </div>
                
                {/* Livraison & Documents */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(96, 165, 250, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.delivery_payment_section')}</h4>
              </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.delivery_location')}</label>
                  <input
                    type="text"
                    value={formData.deliveryPlace || formData.deliveryLocation || formData.deliveryAddress || ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      handleInputChange('deliveryPlace', v);
                      handleInputChange('deliveryLocation', v);
                      handleInputChange('deliveryAddress', v);
                    }}
                    placeholder={t('document_creation.form.search_location')}
                  />
              </div>
              
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.desired_delivery_delay')}</label>
                  <CustomSelect
                    value={formData.desiredDeliveryTime || ''}
                    onChange={(value) => handleInputChange('desiredDeliveryTime', value)}
                    options={delaySelectOptions}
                    placeholder={t('document_creation.form.select_or_enter')}
                    searchable
                  />
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.required_supplier_documents')}</label>
                  <input
                    type="text"
                    value={formData.requiredDocuments || ''}
                    onChange={(e) => handleInputChange('requiredDocuments', e.target.value)}
                    placeholder={formLabel('ph_required_supplier_docs')}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Facture - Section combinée: Détails du document */}
          {selectedType.id === 'invoice' && (
            <div className="bg-white/5 rounded-xl p-5 border border-white/20 overflow-hidden">
              <div className="flex items-center gap-2 mb-4">
                <Receipt className="h-5 w-5 text-green-400" />
                <h3 className={`text-lg font-semibold ${isDashboardLightMode ? 'text-black' : 'text-white'}`}>{t('document_creation.form.document_details')}</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Identifiants du document */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(16, 185, 129, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.document_identifiers')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.document_number')}</label>
                    <input
                      type="text"
                      value={formData.number || ''}
                      readOnly
                      className="readonly-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.invoice_type')}</label>
                  <CustomSelect
                      value={formData.invoiceType ?? ''}
                      onChange={(value) => handleInputChange('invoiceType', value)}
                    options={[
                        { value: 'vente', label: formLabel('invoice_types.sale') },
                        { value: 'acompte', label: formLabel('invoice_types.down_payment') },
                        { value: 'solde', label: formLabel('invoice_types.balance') },
                        { value: 'rectificative', label: formLabel('invoice_types.corrective') },
                        { value: 'annulation', label: formLabel('invoice_types.cancellation') }
                      ]}
                      placeholder={t('document_creation.form.select_placeholder')}
                      searchable
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.date_label')}</label>
                    <CustomDatePicker
                      value={formData.date}
                      onChange={(dateString) => {
                        handleInputChange('date', dateString);
                      }}
                    />
              </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('due_date')}</label>
                    <CustomDatePicker
                      value={formData.dueDate}
                      onChange={(dateString) => handleInputChange('dueDate', dateString)}
                    />
            </div>
                </div>
                
                {/* Informations de suivi */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(16, 185, 129, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.tracking_info')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.prepared_by')}</label>
                  <CustomSelect
                    value={formData.issuedBy || ''}
                    onChange={(value) => {
                    handleInputChange('issuedBy', value);
                    // Sync with "Établie par" signature if enabled
                    if (formData.showEtabliePar) {
                      handleInputChange('etablieParName', value);
                    }
                  }}
                    options={availableFacturiersForDropdown}
                    placeholder={t('document_creation.form.search_or_enter_biller')}
                    searchable
                  />
                </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.sales_rep')}</label>
                    <CustomSelect
                      value={formData.salesPerson || ''}
                      onChange={(value) => handleInputChange('salesPerson', value)}
                      options={availableEmployeesForDropdown.map(emp => ({
                        value: `${emp.firstName} ${emp.lastName}`,
                        label: `${emp.firstName} ${emp.lastName}${emp.position ? ` - ${emp.position}` : ''}`
                      }))}
                      placeholder={t('document_creation.form.search_sales_rep')}
                      searchable
                    />
              </div>
              
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.initial_status')}</label>
                    <CustomSelect
                      value={documentStatusValue()}
                      onChange={handleFormStatusChange}
                      creatable={false}
                      searchable={false}
                      options={getStatusOptions(selectedType.id)}
                      placeholder={t('document_creation.form.select_placeholder')}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.priority')}</label>
                    <CustomSelect
                      value={formData.priority ?? ''}
                      onChange={(value) => handleInputChange('priority', value)}
                      autoSelect={false}
                      createLabel={t('document_creation.form.create_priority')}
                      options={[
                        { value: 'urgent', label: t('document_creation.form.priority_levels.urgent') },
                        { value: 'high', label: t('document_creation.form.priority_levels.high') },
                        { value: 'normale', label: t('document_creation.form.priority_levels.normal') },
                        { value: 'low', label: t('document_creation.form.priority_levels.low') }
                      ]}
                      placeholder={t('document_creation.form.select_placeholder')}
                    />
                  </div>
                </div>
                
                {/* Références */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(16, 185, 129, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.references')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.client_order_reference')}</label>
                    <input
                      type="text"
                      value={formData.customerOrderReference || ''}
                      onChange={(e) => handleInputChange('customerOrderReference', e.target.value)}
                      placeholder={formLabel('ph_client_order_ref')}
                    />
                  </div>

                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.po_reference')}</label>
                    <CustomSelect
                      value={formData.orderReference || ''}
                      onChange={(value) => handleInputChange('orderReference', value)}
                      options={purchaseOrdersForSalesClient.map((doc) => ({
                        value: doc.value,
                        label: doc.label,
                      }))}
                      placeholder={formLabel('ph_po_reference')}
                      searchable
                      creatable
                    />
                  </div>

                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.delivery_note_reference')}</label>
                    <CustomSelect
                      value={formData.deliveryReference || ''}
                      onChange={(value) => handleInputChange('deliveryReference', value)}
                      options={[]}
                      placeholder={formLabel('ph_delivery_note_ref')}
                      searchable
                      creatable
                    />
                  </div>

                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.project_ref')}</label>
                    <CustomSelect
                      value={formData.projectNumber || ''}
                      onChange={(value) => handleInputChange('projectNumber', value)}
                      options={[]}
                      placeholder={formLabel('ph_project_number')}
                      searchable
                      creatable
                    />
                  </div>
                </div>
                
                {/* Livraison & Paiement */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(16, 185, 129, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.delivery_payment_section')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.delivery_location')}</label>
                    <input
                      type="text"
                      value={formData.deliveryLocation || formData.deliveryAddress || ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        handleInputChange('deliveryLocation', v);
                        handleInputChange('deliveryAddress', v);
                      }}
                      placeholder={t('document_creation.form.search_location')}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.delivery_date')}</label>
                    <CustomDatePicker
                      value={formData.deliveryDate}
                      onChange={(dateString) => handleInputChange('deliveryDate', dateString)}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.payment_method')}</label>
                    <CustomSelect
                      value={formData.paymentMethod || ''}
                      onChange={(value) => handleInputChange('paymentMethod', value)}
                      options={paymentMethodWithInstallmentOptions}
                      placeholder={t('document_creation.form.select_placeholder')}
                      searchable
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.payment_terms')}</label>
                    <CustomSelect
                      value={formData.paymentConditions || ''}
                      onChange={(value) => handleInputChange('paymentConditions', value)}
                      options={paymentTermsExtendedOptions}
                      placeholder={t('document_creation.form.select_or_enter')}
                      searchable
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Note de crédit - Section combinée: Détails du document */}
          {isCreditNoteDocType(selectedType.id) && (() => {
            const cnIsSupplier = formData.creditNoteParty === 'supplier';
            const cnSectionAccent = cnIsSupplier ? 'rgba(96, 165, 250, 0.3)' : 'rgba(16, 185, 129, 0.3)';
            const cnInvoiceRefLabel = cnIsSupplier
              ? t('document_creation.form.ref_supplier_invoice')
              : t('document_creation.form.invoice_ref');
            const cnInvoiceRefPlaceholder = cnIsSupplier
              ? formLabel('ph_supplier_invoice_ref')
              : formLabel('ph_invoice_ref');
            return (
            <div className="bg-white/5 rounded-xl p-5 border border-white/20 overflow-hidden">
              <div className="flex items-center gap-2 mb-4">
                <FileX
                  className={`h-5 w-5 ${
                    formData.creditNoteParty === 'supplier' ? 'text-blue-400' : 'text-green-400'
                  }`}
                />
                <h3 className={`text-lg font-semibold ${isDashboardLightMode ? 'text-black' : 'text-white'}`}>{t('document_creation.form.document_details')}</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Identifiants du document */}
                <div className="col-span-full">
                  <h4
                    className="section-subtitle"
                    style={{
                      display: 'inline-block',
                      borderBottom: `2px solid ${cnSectionAccent}`,
                      paddingBottom: '0.25rem',
                      marginBottom: '0.75rem',
                    }}
                  >
                    {t('document_creation.form.document_identifiers')}
                  </h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.document_number')}</label>
                    <input
                      type="text"
                      value={formData.number || ''}
                      readOnly
                      className="readonly-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.date_label')}</label>
                    <CustomDatePicker
                      value={formData.date}
                      onChange={(dateString) => handleInputChange('date', dateString)}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('application_date')}</label>
                    <CustomDatePicker
                      value={formData.creditNote?.applicationDate || formData.creditNoteApplicationDate || ''}
                      onChange={(dateString) =>
                        handleInputChange('creditNote', {...formData.creditNote, applicationDate: dateString})
                      }
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('credit_note_type')}</label>
                    <CustomSelect
                      value={formData.creditNote?.type || ''}
                      onChange={(value) => handleInputChange('creditNote', {...formData.creditNote, type: value})}
                      options={creditNoteTypeOptions}
                      placeholder={t('document_creation.form.select_placeholder')}
                      searchable
                    />
                  </div>
                </div>
                
                {/* Informations de suivi */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: `2px solid ${cnSectionAccent}`, paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.tracking_info')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.prepared_by')}</label>
                  <CustomSelect
                    value={formData.issuedBy || ''}
                    onChange={(value) => {
                    handleInputChange('issuedBy', value);
                    // Sync with "Établie par" signature if enabled
                    if (formData.showEtabliePar) {
                      handleInputChange('etablieParName', value);
                    }
                  }}
                    options={availableFacturiersForDropdown}
                    placeholder={t('document_creation.form.search_or_enter_biller')}
                    searchable
                  />
                </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.initial_status')}</label>
                    <CustomSelect
                      value={documentStatusValue()}
                      onChange={handleFormStatusChange}
                      creatable={false}
                      searchable={false}
                      options={getStatusOptions(selectedType.id)}
                      placeholder={t('document_creation.form.select_placeholder')}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.priority')}</label>
                    <CustomSelect
                      value={formData.priority ?? ''}
                      onChange={(value) => handleInputChange('priority', value)}
                      autoSelect={false}
                      createLabel={t('document_creation.form.create_priority')}
                      options={[
                        { value: 'urgent', label: t('document_creation.form.priority_levels.urgent') },
                        { value: 'high', label: t('document_creation.form.priority_levels.high') },
                        { value: 'normale', label: t('document_creation.form.priority_levels.normal') },
                        { value: 'low', label: t('document_creation.form.priority_levels.low') }
                      ]}
                      placeholder={t('document_creation.form.select_placeholder')}
                    />
                  </div>
                </div>
                
                {/* Facture fournisseur / Référence facture & Remboursement */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: `2px solid ${cnSectionAccent}`, paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{cnInvoiceRefLabel}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{cnInvoiceRefLabel}</label>
                  {cnIsSupplier ? (
                    <input
                      type="text"
                      value={formData.originalInvoice?.number || ''}
                      onChange={(e) =>
                        handleInputChange('originalInvoice', {
                          ...formData.originalInvoice,
                          number: e.target.value,
                        })
                      }
                      placeholder={cnInvoiceRefPlaceholder}
                    />
                  ) : (
                    <CustomSelect
                      value={
                        formData.originalInvoiceNumber ||
                        formData.originalInvoice?.number ||
                        ''
                      }
                      onChange={handleClientCreditNoteInvoiceRefChange}
                      options={invoiceDocumentsForSalesClient.map((doc) => ({
                        value: doc.value,
                        label: doc.label,
                      }))}
                      placeholder={cnInvoiceRefPlaceholder}
                      searchable
                      creatable
                    />
                  )}
                </div>
                  
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('original_doc_date', { ref: cnInvoiceRefLabel.toLowerCase() })}</label>
                  <CustomDatePicker
                    value={formData.originalInvoice?.date}
                    onChange={(dateString) => handleInputChange('originalInvoice', {...formData.originalInvoice, date: dateString})}
                  />
                </div>
                  
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('original_doc_amount', { ref: cnInvoiceRefLabel.toLowerCase(), currency: getBrandCurrencySuffix() })}</label>
                  <CustomNumberInput
                    value={formData.originalInvoice?.amount || ''}
                    onChange={(val) => handleInputChange('originalInvoice', {...formData.originalInvoice, amount: parseFloat(val) || 0})}
                    placeholder="0.00"
                                        suffixInText={` ${getBrandCurrencySuffix()}`}
                    min={0}
                    step={0.01}
                    decimals={2}
                  />
                </div>
                  
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.refund_method')}</label>
                  <CustomSelect
                    value={formData.creditNote?.refundMethod || ''}
                    onChange={(value) => handleInputChange('creditNote', {...formData.creditNote, refundMethod: value})}
                    options={[
                      { value: 'account-credit', label: cnIsSupplier ? formLabel('account_credit_supplier') : formLabel('account_credit_client') },
                      { value: 'bank-transfer', label: formLabel('payment_methods.bank_transfer') },
                      { value: 'check', label: formLabel('payment_methods.cheque') },
                      { value: 'cash', label: formLabel('payment_methods.cash') },
                      { value: 'card', label: formLabel('payment_methods.card') },
                      { value: 'compensation', label: formLabel('compensation') }
                    ]}
                      placeholder={t('document_creation.form.select_placeholder')}
                    searchable
                  />
                </div>
                </div>
              </div>
            </div>
            );
          })()}

          {/* Bon de livraison / Sortie matériel - Section combinée: Détails du document */}
          {(selectedType.id === 'material-exit' || selectedType.id === 'delivery-note' || selectedType.id === 'delivery') && (
            <div className="bg-white/5 rounded-xl p-5 border border-white/20 overflow-hidden">
              <div className="flex items-center gap-2 mb-4">
                <Truck className="h-5 w-5 text-green-400" />
                <h3 className={`text-lg font-semibold ${isDashboardLightMode ? 'text-black' : 'text-white'}`}>{t('document_creation.form.document_details')}</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Identifiants du document */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(16, 185, 129, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.document_identifiers')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.document_number')}</label>
                    <input
                      type="text"
                      value={formData.number || ''}
                      readOnly
                      className="readonly-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.date_label')}</label>
                    <CustomDatePicker
                      value={formData.date}
                      onChange={(dateString) => handleInputChange('date', dateString)}
                    />
              </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.expected_delivery_date')}</label>
                    <CustomDatePicker
                      value={formData.deliveryDate || new Date().toISOString().split('T')[0]}
                      onChange={(dateString) => handleInputChange('deliveryDate', dateString)}
                    />
            </div>
                </div>
                
                {/* Informations de suivi */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(16, 185, 129, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.tracking_info')}</h4>
              </div>
              
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.prepared_by')}</label>
                  <CustomSelect
                    value={formData.issuedBy || ''}
                    onChange={(value) => {
                    handleInputChange('issuedBy', value);
                    // Sync with "Établie par" signature if enabled
                    if (formData.showEtabliePar) {
                      handleInputChange('etablieParName', value);
                    }
                  }}
                    options={availableFacturiersForDropdown}
                    placeholder={t('document_creation.form.search_or_enter_biller')}
                    searchable
                  />
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.initial_status')}</label>
                  <CustomSelect
                    value={documentStatusValue()}
                    onChange={handleFormStatusChange}
                    creatable={false}
                    searchable={false}
                    options={getStatusOptions(selectedType.id)}
                    placeholder={t('document_creation.form.select_placeholder')}
                  />
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.priority')}</label>
                  <CustomSelect
                    value={formData.priority ?? ''}
                    onChange={(value) => handleInputChange('priority', value)}
                    autoSelect={false}
                    createLabel={t('document_creation.form.create_priority')}
                    options={[
                      { value: 'urgent', label: t('document_creation.form.priority_levels.urgent') },
                      { value: 'high', label: t('document_creation.form.priority_levels.high') },
                      { value: 'normale', label: t('document_creation.form.priority_levels.normal') },
                      { value: 'low', label: t('document_creation.form.priority_levels.low') }
                    ]}
                    placeholder={t('document_creation.form.select_placeholder')}
                  />
                </div>
                
                {/* Références */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(16, 185, 129, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.references')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.client_order_reference')}</label>
                  <input
                    type="text"
                    value={formData.customerOrderReference || ''}
                    onChange={(e) => handleInputChange('customerOrderReference', e.target.value)}
                    placeholder={formLabel('ph_client_order_ref')}
                  />
                </div>

                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.po_reference')}</label>
                  <CustomSelect
                    value={formData.orderReference || ''}
                    onChange={(value) => handleInputChange('orderReference', value)}
                    options={purchaseOrdersForSalesClient.map((doc) => ({
                      value: doc.value,
                      label: doc.label,
                    }))}
                    placeholder={formLabel('ph_po_reference')}
                    searchable
                    creatable
                  />
                </div>

                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('invoice_ref')}</label>
                  <CustomSelect
                    value={formData.invoiceReference || ''}
                    onChange={(value) => handleInputChange('invoiceReference', value)}
                    options={invoiceDocumentsForDelivery.map((doc) => ({
                      value: doc.value,
                      label: doc.label,
                    }))}
                    placeholder={formLabel('ph_invoice_ref')}
                    searchable
                    creatable
                  />
                </div>

                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('ref_quote_request')}</label>
                  <CustomSelect
                    value={formData.quoteReference || ''}
                    onChange={(value) => handleInputChange('quoteReference', value)}
                    options={quoteDocumentsForDelivery.map((doc) => ({
                      value: doc.value,
                      label: doc.label,
                    }))}
                    placeholder={formLabel('ph_quote_ref')}
                    searchable
                    creatable
                  />
                </div>
                </div>
                
                {/* Expédition & livraison — 2 lignes × 3 champs */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(16, 185, 129, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{formLabel('shipping_delivery_section')}</h4>
                </div>

                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('source_warehouse')}</label>
                    <CustomSelect
                      value={formData.departureWarehouse || ''}
                      onChange={(value) => handleInputChange('departureWarehouse', value)}
                      options={availableWarehousesForDropdown}
                      placeholder={formLabel('ph_source_warehouse')}
                      searchable
                    />
                  </div>

                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.delivery_location')}</label>
                    <input
                      type="text"
                      value={formData.deliveryLocation || formData.deliveryAddress || ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        handleInputChange('deliveryLocation', v);
                        handleInputChange('deliveryAddress', v);
                      }}
                      placeholder={t('document_creation.form.search_location')}
                    />
                  </div>

                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('delivery_mode')}</label>
                    <CustomSelect
                      value={formData.deliveryMethod || ''}
                      onChange={(value) => handleInputChange('deliveryMethod', value)}
                      options={deliveryModeSimpleOptions}
                      placeholder={formLabel('ph_delivery_mode')}
                      searchable
                    />
                  </div>
                </div>

                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('carrier')}</label>
                    <CustomSelect
                      value={formData.transporterName || ''}
                      onChange={(value) => handleInputChange('transporterName', value)}
                      options={carrierSelectOptions.slice(0, 6)}
                      placeholder={formLabel('ph_carrier_search')}
                      searchable
                    />
                  </div>

                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('carrier_tracking_number')}</label>
                    <input
                      type="text"
                      value={formData.carrierTrackingNumber || ''}
                      onChange={(e) => handleInputChange('carrierTrackingNumber', e.target.value)}
                      placeholder={formLabel('ph_carrier_tracking')}
                    />
                  </div>
                </div>

                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('tracking_number')}</label>
                    <input
                      type="text"
                      value={formData.trackingNumber || ''}
                      onChange={(e) => handleInputChange('trackingNumber', e.target.value)}
                      placeholder={formLabel('ph_transport_tracking')}
                    />
                  </div>

                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('package_count')}</label>
                    <CustomNumberInput
                      value={formData.shipmentPackageCount || ''}
                      onChange={(val) => handleInputChange('shipmentPackageCount', parseInt(val) || 0)}
                      placeholder={formLabel('ph_package_count')}
                      min={0}
                      step={1}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Facture proforma - Section combinée: Détails du document */}
          {selectedType.id === 'proforma' && (
            <div className="bg-white/5 rounded-xl p-5 border border-white/20 overflow-hidden">
              <div className="flex items-center gap-2 mb-4">
                <FileCheck className="h-5 w-5 text-green-400" />
                <h3 className={`text-lg font-semibold ${isDashboardLightMode ? 'text-black' : 'text-white'}`}>{t('document_creation.form.document_details')}</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Identifiants du document */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(16, 185, 129, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.document_identifiers')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.document_number')}</label>
                    <input
                      type="text"
                      value={formData.number || ''}
                      readOnly
                      className="readonly-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.date_label')}</label>
                    <CustomDatePicker
                      value={formData.date}
                      onChange={(dateString) => handleInputChange('date', dateString)}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.valid_until')}</label>
                    <CustomDatePicker
                      value={formData.validUntil}
                      onChange={(dateString) => handleInputChange('validUntil', dateString)}
                    />
                  </div>
                </div>
                
                {/* Informations de suivi */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(16, 185, 129, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.tracking_info')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.prepared_by')}</label>
                  <CustomSelect
                    value={formData.issuedBy || ''}
                    onChange={(value) => {
                    handleInputChange('issuedBy', value);
                    // Sync with "Établie par" signature if enabled
                    if (formData.showEtabliePar) {
                      handleInputChange('etablieParName', value);
                    }
                  }}
                    options={availableFacturiersForDropdown}
                    placeholder={t('document_creation.form.search_or_enter_biller')}
                    searchable
                  />
                </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.sales_rep')}</label>
                    <CustomSelect
                      value={formData.salesPerson || ''}
                      onChange={(value) => handleInputChange('salesPerson', value)}
                      options={availableEmployeesForDropdown.map(emp => ({
                        value: `${emp.firstName} ${emp.lastName}`,
                        label: `${emp.firstName} ${emp.lastName}${emp.position ? ` - ${emp.position}` : ''}`
                      }))}
                      placeholder={t('document_creation.form.search_sales_rep')}
                      searchable
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.initial_status')}</label>
                    <CustomSelect
                      value={documentStatusValue()}
                      onChange={handleFormStatusChange}
                      creatable={false}
                      searchable={false}
                      options={getStatusOptions(selectedType.id)}
                      placeholder={t('document_creation.form.select_placeholder')}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.priority')}</label>
                    <CustomSelect
                      value={formData.priority ?? ''}
                      onChange={(value) => handleInputChange('priority', value)}
                      autoSelect={false}
                      createLabel={t('document_creation.form.create_priority')}
                      options={[
                        { value: 'urgent', label: t('document_creation.form.priority_levels.urgent') },
                        { value: 'high', label: t('document_creation.form.priority_levels.high') },
                        { value: 'normale', label: t('document_creation.form.priority_levels.normal') },
                        { value: 'low', label: t('document_creation.form.priority_levels.low') }
                      ]}
                      placeholder={t('document_creation.form.select_placeholder')}
                    />
                  </div>
                </div>
                
                {/* Projet & Type */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(16, 185, 129, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.project_type_section')}</h4>
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.project_type')}</label>
                  <CustomSelect
                    value={formData.projectType || ''}
                    onChange={(value) => handleInputChange('projectType', value)}
                    options={projectTypeOptions}
                    placeholder={t('document_creation.form.search_generic')}
                    searchable
                  />
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.project_ref')}</label>
                  <CustomSelect
                    value={formData.projectReference || ''}
                    onChange={(value) => handleInputChange('projectReference', value)}
                    options={[]}
                    placeholder={t('document_creation.form.search_reference')}
                    searchable
                  />
              </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.delivery_time')}</label>
                  <CustomSelect
                    value={formData.deliveryTime || formData.estimatedDelivery || ''}
                    onChange={(value) => {
                      handleInputChange('deliveryTime', value);
                      handleInputChange('estimatedDelivery', value);
                    }}
                    options={delaySelectOptions}
                    placeholder={t('document_creation.form.select_or_enter')}
                    searchable
                  />
            </div>
                
                {/* Livraison & Paiement */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(16, 185, 129, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.delivery_payment_section')}</h4>
              </div>
              
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.delivery_location')}</label>
                    <input
                      type="text"
                      value={formData.deliveryLocation || formData.deliveryAddress || ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        handleInputChange('deliveryLocation', v);
                        handleInputChange('deliveryAddress', v);
                      }}
                      placeholder={t('document_creation.form.search_location')}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.delivery_delay')}</label>
                    <CustomSelect
                      value={formData.deliveryDelay || ''}
                      onChange={(value) => handleInputChange('deliveryDelay', value)}
                      options={delaySelectOptions}
                      placeholder={t('document_creation.form.select_or_enter')}
                      searchable
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.payment_method')}</label>
                  <CustomSelect
                    value={formData.paymentMethod || ''}
                    onChange={(value) => handleInputChange('paymentMethod', value)}
                    options={[
                      { value: 'virement', label: formLabel('payment_methods.bank_transfer') },
                      { value: 'cheque', label: formLabel('payment_methods.cheque') },
                      { value: 'especes', label: formLabel('payment_methods.cash') },
                      { value: 'carte', label: formLabel('payment_methods.card') },
                      { value: 'effet', label: formLabel('payment_methods.trade_bill') },
                      { value: 'traite', label: formLabel('payment_methods.promissory') }
                    ]}
                    placeholder={t('document_creation.form.select_placeholder')}
                    searchable
                  />
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.payment_terms')}</label>
                  <CustomSelect
                    value={formData.paymentTerms || ''}
                    onChange={(value) => handleInputChange('paymentTerms', value)}
                    options={paymentTermsOptions}
                    placeholder={t('document_creation.form.select_or_enter')}
                    searchable
                  />
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('due_date')}</label>
                  <CustomDatePicker
                    value={formData.dueDate}
                    onChange={(dateString) => handleInputChange('dueDate', dateString)}
                  />
                </div>
              </div>
            </div>
          )}

          {selectedType.id === 'quote-request' && (
              <div className="bg-white/5 rounded-xl p-5 border border-white/20 overflow-hidden">
                <div className="flex items-center gap-2 mb-4">
                <ClipboardList className="h-5 w-5 text-blue-400" />
                <h3 className={`text-lg font-semibold ${isDashboardLightMode ? 'text-black' : 'text-white'}`}>{t('document_creation.form.document_details')}</h3>
                </div>
                
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Identifiants du document */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(96, 165, 250, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.document_identifiers')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.document_number')}</label>
                    <input
                      type="text"
                      value={formData.number || ''}
                      readOnly
                      className="readonly-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.date_label')}</label>
                    <CustomDatePicker
                      value={formData.date}
                      onChange={(dateString) => handleInputChange('date', dateString)}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.response_deadline')}</label>
                    <CustomDatePicker
                      value={formData.responseDate}
                      onChange={(dateString) => handleInputChange('responseDate', dateString)}
                    />
                  </div>
                </div>
                
                {/* Informations de suivi */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(96, 165, 250, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.tracking_info')}</h4>
                </div>
                
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.prepared_by')}</label>
                  <CustomSelect
                    value={formData.issuedBy || ''}
                    onChange={(value) => {
                    handleInputChange('issuedBy', value);
                    // Sync with "Établie par" signature if enabled
                    if (formData.showEtabliePar) {
                      handleInputChange('etablieParName', value);
                    }
                  }}
                    options={availableFacturiersForDropdown}
                    placeholder={t('document_creation.form.search_or_enter_biller')}
                    searchable
                  />
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.initial_status')}</label>
                  <CustomSelect
                    value={documentStatusValue()}
                    onChange={handleFormStatusChange}
                    creatable={false}
                    searchable={false}
                    options={getStatusOptions(selectedType.id)}
                    placeholder={t('document_creation.form.select_placeholder')}
                  />
                  </div>
                
                  <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.request_priority')}</label>
                  <CustomSelect
                    value={formData.priority ?? ''}
                    onChange={(value) => handleInputChange('priority', value)}
                    autoSelect={false}
                    createLabel={t('document_creation.form.create_priority')}
                    options={[
                      { value: 'urgent', label: t('document_creation.form.priority_levels.urgent') },
                      { value: 'high', label: t('document_creation.form.priority_levels.high') },
                      { value: 'normale', label: t('document_creation.form.priority_levels.normal') },
                      { value: 'low', label: t('document_creation.form.priority_levels.low') }
                    ]}
                    placeholder={t('document_creation.form.select_placeholder')}
                    />
                  </div>
                
                {/* Projet & Budget */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(96, 165, 250, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{formLabel('project_type_section')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.project_ref')}</label>
                    <CustomSelect
                      value={formData.projectReference || ''}
                      onChange={(value) => handleInputChange('projectReference', value)}
                      options={[]}
                      placeholder={t('document_creation.form.search_reference')}
                      searchable
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('max_estimated_budget')} (${getBrandCurrencySuffix()})</label>
                    <CustomNumberInput
                      value={formData.maxBudget || ''}
                      onChange={(value) => handleInputChange('maxBudget', value)}
                      placeholder={formLabel('ph_max_budget')}
                      suffixInText={` ${getBrandCurrencySuffix()}`}
                      step={1000}
                      min={0}
                    />
                  </div>
                </div>
                
                {/* Livraison & Documents */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(96, 165, 250, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.delivery_payment_section')}</h4>
              </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.delivery_location')}</label>
                  <input
                    type="text"
                    value={formData.deliveryPlace || formData.deliveryLocation || formData.deliveryAddress || ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      handleInputChange('deliveryPlace', v);
                      handleInputChange('deliveryLocation', v);
                      handleInputChange('deliveryAddress', v);
                    }}
                    placeholder={t('document_creation.form.search_location')}
                  />
              </div>
              
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.desired_delivery_delay')}</label>
                  <CustomSelect
                    value={formData.desiredDeliveryTime || ''}
                    onChange={(value) => handleInputChange('desiredDeliveryTime', value)}
                    options={delaySelectOptions}
                    placeholder={t('document_creation.form.select_or_enter')}
                    searchable
                  />
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.required_supplier_documents')}</label>
                  <input
                    type="text"
                    value={formData.requiredDocuments || ''}
                    onChange={(e) => handleInputChange('requiredDocuments', e.target.value)}
                    placeholder={formLabel('ph_required_supplier_docs_list')}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Demande de Proforma - Request Details Section */}
          {/* OFFRES COMMERCIALES Documents - Specialized Forms */}
          
          {/* Devis - Section combinée: Détails du document */}
          {selectedType.id === 'quote' && (
            <div className="bg-white/5 rounded-xl p-5 border border-white/20 overflow-hidden">
              <div className="flex items-center gap-2 mb-4">
                <DollarSign className="h-5 w-5 text-green-400" />
                <h3 className={`text-lg font-semibold ${isDashboardLightMode ? 'text-black' : 'text-white'}`}>{t('document_creation.form.document_details')}</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Identifiants du document */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(16, 185, 129, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.document_identifiers')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4 w-full">
                  <div className="form-group min-w-0">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.document_number')}</label>
                    <input
                      type="text"
                      value={formData.number || ''}
                      readOnly
                      className="readonly-input"
                    />
                  </div>
                  
                  <div className="form-group min-w-0">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.date_label')}</label>
                    <CustomDatePicker
                      value={formData.date}
                      onChange={(dateString) => handleInputChange('date', dateString)}
                    />
                  </div>
                  
                  <div className="form-group min-w-0">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.valid_until')}</label>
                    <CustomDatePicker
                      value={formData.validUntil}
                      onChange={(dateString) => handleInputChange('validUntil', dateString)}
                    />
                  </div>
                </div>
                
                {/* Informations de suivi */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(16, 185, 129, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.tracking_info')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.prepared_by')}</label>
                  <CustomSelect
                    value={formData.issuedBy || ''}
                    onChange={(value) => {
                    handleInputChange('issuedBy', value);
                    // Sync with "Établie par" signature if enabled
                    if (formData.showEtabliePar) {
                      handleInputChange('etablieParName', value);
                    }
                  }}
                    options={availableFacturiersForDropdown}
                    placeholder={t('document_creation.form.search_or_enter_biller')}
                    searchable
                  />
                </div>
                  
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.sales_rep')}</label>
                  <CustomSelect
                    value={formData.salesPerson || ''}
                    onChange={(value) => handleInputChange('salesPerson', value)}
                    options={availableEmployeesForDropdown.map(emp => ({
                      value: `${emp.firstName} ${emp.lastName}`,
                      label: `${emp.firstName} ${emp.lastName}${emp.position ? ` - ${emp.position}` : ''}`
                    }))}
                    placeholder={t('document_creation.form.search_sales_rep')}
                    searchable
                  />
                </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.initial_status')}</label>
                    <CustomSelect
                      value={documentStatusValue()}
                      onChange={handleFormStatusChange}
                      creatable={false}
                      searchable={false}
                      options={getStatusOptions(selectedType.id)}
                      placeholder={t('document_creation.form.select_placeholder')}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.priority')}</label>
                    <CustomSelect
                      value={formData.priority ?? ''}
                      onChange={(value) => handleInputChange('priority', value)}
                      autoSelect={false}
                      createLabel={t('document_creation.form.create_priority')}
                      options={[
                        { value: 'urgent', label: t('document_creation.form.priority_levels.urgent') },
                        { value: 'high', label: t('document_creation.form.priority_levels.high') },
                        { value: 'normale', label: t('document_creation.form.priority_levels.normal') },
                        { value: 'low', label: t('document_creation.form.priority_levels.low') }
                      ]}
                      placeholder={t('document_creation.form.select_placeholder')}
                    />
                  </div>
                </div>
                
                {/* Projet & Type */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(16, 185, 129, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.project_type_section')}</h4>
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.project_type')}</label>
                  <CustomSelect
                    value={formData.projectType || ''}
                    onChange={(value) => handleInputChange('projectType', value)}
                    options={projectTypeOptions}
                    placeholder={t('document_creation.form.search_generic')}
                    searchable
                  />
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.project_ref')}</label>
                  <CustomSelect
                    value={formData.projectReference || ''}
                    onChange={(value) => handleInputChange('projectReference', value)}
                    options={[]}
                    placeholder={t('document_creation.form.search_reference')}
                    searchable
                  />
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.delivery_time')}</label>
                  <CustomSelect
                    value={formData.deliveryTime || formData.estimatedDelivery || formData.quoteDeliveryTime || ''}
                    onChange={(value) => {
                      handleInputChange('deliveryTime', value);
                      handleInputChange('estimatedDelivery', value);
                    }}
                    options={delaySelectOptions}
                    placeholder={t('document_creation.form.select_or_enter')}
                    searchable
                  />
              </div>
                
                {/* Livraison & Paiement */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(16, 185, 129, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.delivery_payment_section')}</h4>
            </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.delivery_location')}</label>
                    <input
                      type="text"
                      value={formData.deliveryLocation || formData.deliveryAddress || ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        handleInputChange('deliveryLocation', v);
                        handleInputChange('deliveryAddress', v);
                      }}
                      placeholder={t('document_creation.form.search_location')}
                    />
              </div>
              
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.delivery_delay')}</label>
                  <CustomSelect
                      value={formData.deliveryDelay || ''}
                      onChange={(value) => handleInputChange('deliveryDelay', value)}
                    options={delaySelectOptions}
                    placeholder={t('document_creation.form.select_or_enter')}
                    searchable
                  />
                </div>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.payment_method')}</label>
                    <CustomSelect
                      value={formData.paymentMethod || ''}
                      onChange={(value) => handleInputChange('paymentMethod', value)}
                      options={paymentMethodOptions}
                      placeholder={t('document_creation.form.select_placeholder')}
                      searchable
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.payment_terms')}</label>
                  <CustomSelect
                    value={formData.paymentTerms || ''}
                    onChange={(value) => handleInputChange('paymentTerms', value)}
                    options={paymentTermsOptions}
                    placeholder={t('document_creation.form.select_or_enter')}
                    searchable
                  />
                </div>
                </div>
              </div>
            </div>
          )}

          {selectedType.id === 'purchase-order' && (
            <div className="bg-white/5 rounded-xl p-5 border border-white/20 overflow-hidden">
              <div className="flex items-center gap-2 mb-4">
                <ShoppingCart className="h-5 w-5 text-blue-400" />
                <h3 className={`text-lg font-semibold ${isDashboardLightMode ? 'text-black' : 'text-white'}`}>{t('document_creation.form.document_details')}</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Identifiants du document */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(96, 165, 250, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.document_identifiers')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.document_number')}</label>
                    <input
                      type="text"
                      value={formData.number || ''}
                      readOnly
                      className="readonly-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('buyer')}</label>
                    <CustomSelect
                      value={formData.buyerContact || ''}
                      onChange={(value) => handleInputChange('buyerContact', value)}
                      options={availableEmployeesForDropdown}
                      placeholder={formLabel('search_employee')}
                      searchable
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.date_label')}</label>
                    <CustomDatePicker
                      value={formData.date}
                      onChange={(dateString) => handleInputChange('date', dateString)}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('desired_delivery_date')}</label>
                    <CustomDatePicker
                      value={formData.deliveryDate}
                      onChange={(dateString) => handleInputChange('deliveryDate', dateString)}
                    />
                  </div>
                </div>
                
                {/* Informations de suivi */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(96, 165, 250, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.tracking_info')}</h4>
                </div>
                
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.prepared_by')}</label>
                  <CustomSelect
                    value={formData.issuedBy || ''}
                    onChange={(value) => {
                    handleInputChange('issuedBy', value);
                    // Sync with "Établie par" signature if enabled
                    if (formData.showEtabliePar) {
                      handleInputChange('etablieParName', value);
                    }
                  }}
                    options={availableFacturiersForDropdown}
                    placeholder={t('document_creation.form.search_or_enter_biller')}
                    searchable
                  />
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.initial_status')}</label>
                  <CustomSelect
                    value={documentStatusValue()}
                    onChange={handleFormStatusChange}
                    creatable={false}
                    searchable={false}
                    options={getStatusOptions(selectedType.id)}
                    placeholder={t('document_creation.form.select_placeholder')}
                  />
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.priority')}</label>
                  <CustomSelect
                    value={formData.priority ?? ''}
                    onChange={(value) => handleInputChange('priority', value)}
                    autoSelect={false}
                    createLabel={t('document_creation.form.create_priority')}
                    options={[
                      { value: 'urgent', label: t('document_creation.form.priority_levels.urgent') },
                      { value: 'high', label: t('document_creation.form.priority_levels.high') },
                      { value: 'normale', label: t('document_creation.form.priority_levels.normal') },
                      { value: 'low', label: t('document_creation.form.priority_levels.low') }
                    ]}
                    placeholder={t('document_creation.form.select_placeholder')}
                  />
                </div>
                
                {/* Références */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(96, 165, 250, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.references')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.ref_quote_request')}</label>
                    <CustomSelect
                      value={formData.quoteRequestRef || ''}
                      onChange={(value) => handleInputChange('quoteRequestRef', value)}
                      options={quoteRequestDocuments.map((doc) => ({
                        value: doc.reference,
                        label: doc.label,
                      }))}
                      placeholder={formLabel('ph_quote_request_ref')}
                      searchable
                      creatable
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.ref_proforma_request')}</label>
                    <CustomSelect
                      value={formData.proformaRequestRef || ''}
                      onChange={(value) => handleInputChange('proformaRequestRef', value)}
                      options={proformaRequestDocuments.map((doc) => ({
                        value: doc.reference,
                        label: doc.label,
                      }))}
                      placeholder={formLabel('ph_proforma_request_ref')}
                      searchable
                      creatable
                    />
                  </div>

                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.project_ref')}</label>
                    <CustomSelect
                      value={formData.projectReference || ''}
                      onChange={(value) => handleInputChange('projectReference', value)}
                      options={[]}
                      placeholder={t('document_creation.form.search_reference')}
                      searchable
                    />
                  </div>
                </div>
                
                {/* Livraison & Paiement */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(96, 165, 250, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.delivery_payment_section')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.delivery_location')}</label>
                  <input
                    type="text"
                    value={formData.deliveryAddress || formData.deliveryLocation || ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      handleInputChange('deliveryAddress', v);
                      handleInputChange('deliveryLocation', v);
                    }}
                    placeholder={t('document_creation.form.search_location')}
                  />
                </div>
                  
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.delivery_delay')}</label>
                  <CustomSelect
                      value={formData.deliveryTimeframe || ''}
                      onChange={(value) => handleInputChange('deliveryTimeframe', value)}
                    options={delaySelectOptions}
                    placeholder={t('document_creation.form.select_or_enter')}
                    searchable
                  />
                </div>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('delivery_mode')}</label>
                  <CustomSelect
                    value={formData.deliveryMode || ''}
                    onChange={(value) => handleInputChange('deliveryMode', value)}
                    options={purchaseOrderDeliveryModeOptions}
                    placeholder={t('document_creation.form.select_or_enter')}
                    searchable
                  />
                </div>
                  
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.payment_terms')}</label>
                  <CustomSelect
                      value={formData.paymentTerms || ''}
                      onChange={(value) => handleInputChange('paymentTerms', value)}
                    options={paymentTermsDaysValueOptions}
                    placeholder={t('document_creation.form.select_or_enter')}
                    searchable
                  />
                </div>
                </div>
              </div>
            </div>
          )}
          {selectedType.id === 'attachment' && (
            <div className="form-section">
              <h3 className="section-title" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                <Paperclip size={20} color={getIconColor()} />
                {formLabel('technical_attachment')}
              </h3>
              
              <div className="section-subtitle">{t('document_creation.header_info')}</div>
              <div className="form-grid">
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('subject')}</label>
                  <input
                    type="text"
                    value={formData.subject || ''}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    placeholder={formLabel('ph_attachment_subject')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('attachment_type')}</label>
                  <CustomSelect
                    value={formData.attachmentType || 'specification'}
                    onChange={(e) => handleInputChange('attachmentType', e.target.value)}
                  >
                    <option value="specification">{formLabel('attachment_types.specification')}</option>
                    <option value="requirement">{formLabel('attachment_types.requirement')}</option>
                    <option value="procedure">{formLabel('attachment_types.procedure')}</option>
                    <option value="manual">{formLabel('attachment_types.manual')}</option>
                    <option value="contract">{formLabel('attachment_types.contract')}</option>
                    <option value="certification">{formLabel('attachment_types.certification')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.priority')}</label>
                  <CustomSelect
                    value={formData.priority ?? ''}
                    onChange={(e) => handleInputChange('priority', e.target.value)}
                  >
                    <option value="">{formLabel('select_placeholder')}</option>
                    <option value="normale">{formLabel('attachment_priority_levels.normal')}</option>
                    <option value="importante">{formLabel('attachment_priority_levels.important')}</option>
                    <option value="urgente">{formLabel('attachment_priority_levels.urgent')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('version')}</label>
                  <input
                    type="text"
                    value={formData.version || 'v1.0'}
                    onChange={(e) => handleInputChange('version', e.target.value)}
                    placeholder={formLabel('ph_version')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('manager_name')}</label>
                  <CustomSelect
                    value={formData.issuedBy || ''}
                    onChange={(e) => handleInputChange('issuedBy', e.target.value)}
                  >
                    <option value="">{t('document_creation.select_type')}...</option>
                    {activeEmployees.filter(emp => emp.status === 'active').map(employee => (
                      <option key={employee.id} value={`${employee.firstName} ${employee.lastName}`}>
                        {employee.firstName} {employee.lastName} - {employee.position}
                      </option>
                    ))}
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('status_label')}</label>
                  <CustomSelect
                    value={formData.status ?? ''}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                  >
                    <option value="draft">{statusLbl('draft')}</option>
                    <option value="review">{statusLbl('review')}</option>
                    <option value="approved">{statusLbl('approved')}</option>
                    <option value="final">{statusLbl('final')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group full-width">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('need_summary')}</label>
                  <textarea
                    value={formData.summary || ''}
                    onChange={(e) => handleInputChange('summary', e.target.value)}
                    placeholder={formLabel('ph_need_summary')}
                    rows="3"
                  />
                </div>
              </div>
              
              <div className="section-subtitle">{t('document_creation.document_info')}</div>
              <div className="form-group full-width">
                <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('products.product_description')}</label>
                <textarea
                  value={formData.technicalDetails || ''}
                  onChange={(e) => handleInputChange('technicalDetails', e.target.value)}
                  placeholder={t('products.product_description')}
                  rows="4"
                />
              </div>
              
              <div className="section-subtitle">{t('document_creation.document_totals')}</div>
              <div className="form-grid">
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('facturation.total_amount')}</label>
                  <CustomNumberInput
                    value={formData.estimatedBudget || ''}
                    onChange={(val) => handleInputChange('estimatedBudget', val)}
                    placeholder="0.00"
                    min={0}
                    step={0.01}
                    decimals={2}
                  />
                </div>
              </div>
              
              <div className="section-subtitle">{formLabel('attachments_section')}</div>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('external_doc_references')}</label>
                  <textarea
                    value={formData.externalDocuments || ''}
                    onChange={(e) => handleInputChange('externalDocuments', e.target.value)}
                    placeholder={formLabel('ph_external_attachment_refs')}
                    rows="2"
                  />
                </div>
              </div>
              
              <div className="section-subtitle">{formLabel('observations_comments')}</div>
              <div className="form-group full-width">
                <textarea
                  value={formData.comments || ''}
                  onChange={(e) => handleInputChange('comments', e.target.value)}
                  placeholder={formLabel('ph_extra_comments')}
                  rows="3"
                />
              </div>
            </div>
          )}
          {/* The supplier section is already defined above, so we removed the duplicate here */}
          
          {/* Bon de réception - Section combinée: Détails du document */}
          {selectedType.id === 'reception-note' && (
            <div className="bg-white/5 rounded-xl p-5 border border-white/20 overflow-hidden">
              <div className="flex items-center gap-2 mb-4">
                <PackageCheck className="h-5 w-5 text-orange-400" />
                <h3 className={`text-lg font-semibold ${isDashboardLightMode ? 'text-black' : 'text-white'}`}>{t('document_creation.form.document_details')}</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Identifiants du document */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(249, 115, 22, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.document_identifiers')}</h4>
                </div>

                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.document_number')}</label>
                  <input
                    type="text"
                    value={formData.number || ''}
                    readOnly
                    className="readonly-input"
                  />
                </div>

                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.date_label')}</label>
                  <CustomDatePicker
                    value={formData.date}
                    onChange={(dateString) => handleInputChange('date', dateString)}
                  />
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('expected_reception_date')}</label>
                  <CustomDatePicker
                    value={formData.expectedReceptionDate}
                    onChange={(dateString) => handleInputChange('expectedReceptionDate', dateString)}
                  />
                </div>
              </div>
                
                {/* Informations de suivi */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(249, 115, 22, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.tracking_info')}</h4>
            </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.prepared_by')}</label>
                  <CustomSelect
                    value={formData.issuedBy || ''}
                    onChange={(value) => {
                    handleInputChange('issuedBy', value);
                    // Sync with "Établie par" signature if enabled
                    if (formData.showEtabliePar) {
                      handleInputChange('etablieParName', value);
                    }
                  }}
                    options={availableFacturiersForDropdown}
                    placeholder={t('document_creation.form.search_or_enter_biller')}
                    searchable
                  />
                </div>
                  
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('checked_by')}</label>
                  <CustomSelect
                      value={formData.receptionControlledBy || ''}
                    onChange={(value) => handleInputChange('receptionControlledBy', value)}
                    options={availableEmployeesForDropdown.map(emp => ({
                      value: `${emp.firstName} ${emp.lastName}`,
                      label: `${emp.firstName} ${emp.lastName}${emp.position ? ` - ${emp.position}` : ''}`
                    }))}
                    placeholder={t('document_creation.form.search_employee')}
                    searchable
                  />
                </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.initial_status')}</label>
                    <CustomSelect
                      value={documentStatusValue()}
                      onChange={handleFormStatusChange}
                      creatable={false}
                      searchable={false}
                      options={getStatusOptions(selectedType.id)}
                      placeholder={t('document_creation.form.select_placeholder')}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.priority')}</label>
                    <CustomSelect
                      value={formData.priority ?? ''}
                      onChange={(value) => handleInputChange('priority', value)}
                      autoSelect={false}
                      createLabel={t('document_creation.form.create_priority')}
                      options={[
                        { value: 'urgent', label: t('document_creation.form.priority_levels.urgent') },
                        { value: 'high', label: t('document_creation.form.priority_levels.high') },
                        { value: 'normale', label: t('document_creation.form.priority_levels.normal') },
                        { value: 'low', label: t('document_creation.form.priority_levels.low') }
                      ]}
                      placeholder={t('document_creation.form.select_placeholder')}
                    />
                  </div>
                </div>
                
                {/* Références */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(249, 115, 22, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.references')}</h4>
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.ref_purchase_order')}</label>
                  <CustomSelect
                    value={formData.receptionOrderRef || ''}
                    onChange={(value) => handleInputChange('receptionOrderRef', value)}
                    options={purchaseOrders.map((doc) => ({
                      value: doc.reference,
                      label: doc.label,
                    }))}
                    placeholder={formLabel('ph_po_ref_short')}
                    searchable
                    creatable
                  />
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.ref_delivery_note')}</label>
                  <CustomSelect
                    value={formData.receptionDeliveryNoteRef || ''}
                    onChange={(value) => handleInputChange('receptionDeliveryNoteRef', value)}
                    options={deliveryNotes.map((doc) => ({
                      value: doc.reference,
                      label: doc.label,
                    }))}
                    placeholder={formLabel('ph_delivery_note_ref_short')}
                    searchable
                    creatable
                  />
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('carrier')}</label>
                  <CustomSelect
                    value={formData.receptionCarrier || ''}
                    onChange={(value) => handleInputChange('receptionCarrier', value)}
                    options={[
                      { value: 'SMSA', label: t('document_creation.form.carriers.smsa') },
                      { value: 'Aramex', label: t('document_creation.form.carriers.aramex') },
                      { value: 'DHL', label: t('document_creation.form.carriers.dhl') },
                      { value: 'Saudi Post', label: t('document_creation.form.carriers.saudi_post') },
                      { value: 'FedEx', label: t('document_creation.form.carriers.fedex') },
                      { value: 'UPS', label: t('document_creation.form.carriers.ups') },
                      { value: 'autre', label: formLabel('carriers.other') }
                    ]}
                    placeholder={t('document_creation.form.search_carrier')}
                    searchable
                  />
                </div>
                
                {/* Réception & Stockage */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(249, 115, 22, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{formLabel('reception_storage')}</h4>
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('reception_warehouse')}</label>
                  <CustomSelect
                    value={formData.receptionWarehouse || ''}
                    onChange={(value) => handleInputChange('receptionWarehouse', value)}
                    options={availableWarehousesForDropdown}
                    placeholder={t('document_creation.form.search_warehouse')}
                    searchable
                  />
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('reception_address')}</label>
                  <input
                    type="text"
                    value={formData.receiverAddress || ''}
                    onChange={(e) => handleInputChange('receiverAddress', e.target.value)}
                    placeholder={formLabel('ph_reception_address')}
                  />
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('stock_destination')}</label>
                  <CustomSelect
                    value={formData.receptionStockDestination || ''}
                    onChange={(value) => handleInputChange('receptionStockDestination', value)}
                    options={[
                      { value: 'entrepôt_principal', label: formLabel('stock_destinations.main') },
                      { value: 'entrepôt_secondaire', label: formLabel('stock_destinations.secondary') },
                      { value: 'quarantaine', label: formLabel('stock_destinations.quarantine') },
                      { value: 'retour_fournisseur', label: formLabel('return_types.supplier_return') },
                      { value: 'mise_au_rebut', label: formLabel('stock_destinations.scrap') }
                    ]}
                    placeholder={t('document_creation.form.select_or_enter')}
                    searchable
                  />
                </div>
                
                {/* Contrôle & Qualité */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(249, 115, 22, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{formLabel('control_quality')}</h4>
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('general_condition')}</label>
                  <CustomSelect
                    value={formData.receptionOverallCondition || ''}
                    onChange={(value) => handleInputChange('receptionOverallCondition', value)}
                    options={[
                      { value: 'excellent', label: formLabel('general_conditions.excellent') },
                      { value: 'bon', label: formLabel('general_conditions.good') },
                      { value: 'satisfaisant', label: formLabel('general_conditions.satisfactory') },
                      { value: 'défectueux', label: formLabel('general_conditions.defective') },
                      { value: 'endommagé', label: formLabel('general_conditions.damaged') }
                    ]}
                    placeholder={t('document_creation.form.select_or_enter')}
                    searchable
                  />
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('quantity_compliance')}</label>
                  <CustomSelect
                    value={formData.receptionQuantityCompliance || ''}
                    onChange={(value) => handleInputChange('receptionQuantityCompliance', value)}
                    options={[
                      { value: 'conforme', label: formLabel('quantity_compliance_options.compliant') },
                      { value: 'surplus', label: formLabel('quantity_compliance_options.surplus') },
                      { value: 'manquant', label: formLabel('quantity_compliance_options.missing') },
                      { value: 'partiel', label: formLabel('delivery_modes.partial') }
                    ]}
                    placeholder={t('document_creation.form.select_or_enter')}
                    searchable
                  />
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('quality_compliance')}</label>
                  <CustomSelect
                    value={formData.receptionQualityCompliance || ''}
                    onChange={(value) => handleInputChange('receptionQualityCompliance', value)}
                    options={[
                      { value: 'conforme', label: formLabel('quality_compliance_options.compliant') },
                      { value: 'non_conforme', label: formLabel('quality_compliance_options.non_compliant') },
                      { value: 'à_vérifier', label: formLabel('quality_compliance_options.to_verify') },
                      { value: 'avec_réserves', label: formLabel('quality_compliance_options.with_reserves') }
                    ]}
                    placeholder={t('document_creation.form.select_or_enter')}
                    searchable
                  />
                </div>
              </div>
            </div>
          )}
          
          {/* Bordereau de retour fournisseur - Section combinée: Détails du document */}
          {selectedType.id === 'return-slip' && (
            <div className="bg-white/5 rounded-xl p-5 border border-white/20 overflow-hidden">
              <div className="flex items-center gap-2 mb-4">
                <ArrowLeft className="h-5 w-5 text-blue-400" />
                <h3 className={`text-lg font-semibold ${isDashboardLightMode ? 'text-black' : 'text-white'}`}>{t('document_creation.form.document_details')}</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Identifiants du document */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(96, 165, 250, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.document_identifiers')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.document_number')}</label>
                    <input
                      type="text"
                      value={formData.number || ''}
                      readOnly
                      className="readonly-input"
                    />
                  </div>
                  
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.date_label')}</label>
                    <CustomDatePicker
                      value={formData.date}
                      onChange={(dateString) => handleInputChange('date', dateString)}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('processing_deadline')}</label>
                    <CustomDatePicker
                      value={formData.processingDeadline}
                      onChange={(dateString) => handleInputChange('processingDeadline', dateString)}
                    />
                  </div>
                </div>
                
                {/* Informations de suivi */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(96, 165, 250, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.tracking_info')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.prepared_by')}</label>
                  <CustomSelect
                    value={formData.issuedBy || ''}
                    onChange={(value) => {
                    handleInputChange('issuedBy', value);
                    // Sync with "Établie par" signature if enabled
                    if (formData.showEtabliePar) {
                      handleInputChange('etablieParName', value);
                    }
                  }}
                    options={availableFacturiersForDropdown}
                    placeholder={t('document_creation.form.search_or_enter_biller')}
                    searchable
                  />
                </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('checked_by')}</label>
                    <CustomSelect
                      value={formData.returnInspector || ''}
                      onChange={(value) => handleInputChange('returnInspector', value)}
                      options={availableEmployeesForDropdown}
                      placeholder={formLabel('search_employee')}
                      searchable
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.initial_status')}</label>
                    <CustomSelect
                      value={documentStatusValue()}
                      onChange={handleFormStatusChange}
                      creatable={false}
                      searchable={false}
                      options={getStatusOptions(selectedType.id)}
                      placeholder={t('document_creation.form.select_placeholder')}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.priority')}</label>
                    <CustomSelect
                      value={formData.priority ?? ''}
                      onChange={(value) => handleInputChange('priority', value)}
                      autoSelect={false}
                      createLabel={t('document_creation.form.create_priority')}
                      options={[
                        { value: 'urgent', label: t('document_creation.form.priority_levels.urgent') },
                        { value: 'high', label: t('document_creation.form.priority_levels.high') },
                        { value: 'normale', label: t('document_creation.form.priority_levels.normal') },
                        { value: 'low', label: t('document_creation.form.priority_levels.low') }
                      ]}
                      placeholder={t('document_creation.form.select_placeholder')}
                    />
              </div>
            </div>
                
                {/* Références */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(96, 165, 250, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.references')}</h4>
              </div>
              
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.ref_purchase_order')}</label>
                  <CustomSelect
                      value={formData.returnOriginalPurchaseOrderRef || ''}
                      onChange={(value) => handleInputChange('returnOriginalPurchaseOrderRef', value)}
                      options={purchaseOrders.map((doc) => ({
                        value: doc.reference,
                        label: doc.label,
                      }))}
                      placeholder={t('document_creation.form.search_generic')}
                    searchable
                  />
                </div>

                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.ref_supplier_delivery_note')}</label>
                  <input
                    type="text"
                    value={formData.returnOriginalDeliveryRef || ''}
                    onChange={(e) => handleInputChange('returnOriginalDeliveryRef', e.target.value)}
                    placeholder={formLabel('ph_supplier_delivery_note_ref')}
                  />
                </div>
                  
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.ref_supplier_invoice')}</label>
                  <input
                    type="text"
                    value={formData.returnOriginalInvoiceRef || ''}
                    onChange={(e) => handleInputChange('returnOriginalInvoiceRef', e.target.value)}
                    placeholder={formLabel('ph_supplier_invoice_ref')}
                  />
                </div>

                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.supplier_invoice_date')}</label>
                  <CustomDatePicker
                    value={formData.returnOriginalInvoiceDate || ''}
                    onChange={(dateString) => handleInputChange('returnOriginalInvoiceDate', dateString)}
                  />
                </div>
                  
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.rma_number')}</label>
                    <input
                      type="text"
                      value={formData.returnRMANumber || ''}
                      onChange={(e) => handleInputChange('returnRMANumber', e.target.value)}
                      placeholder={formLabel('ph_rma_number')}
                  />
                </div>
                </div>
                
                {/* Logistique */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(96, 165, 250, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{formLabel('logistics_section')}</h4>
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('source_warehouse')}</label>
                  <CustomSelect
                    value={formData.returnWarehouse || ''}
                    onChange={(value) => handleInputChange('returnWarehouse', value)}
                    options={availableWarehousesForDropdown}
                    placeholder={t('document_creation.form.search_warehouse')}
                    searchable
                  />
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('return_carrier')}</label>
                  <CustomSelect
                    value={formData.returnCarrier || ''}
                    onChange={(value) => handleInputChange('returnCarrier', value)}
                    options={[
                      { value: 'SMSA', label: t('document_creation.form.carriers.smsa') },
                      { value: 'Aramex', label: t('document_creation.form.carriers.aramex') },
                      { value: 'DHL', label: t('document_creation.form.carriers.dhl') },
                      { value: 'Saudi Post', label: t('document_creation.form.carriers.saudi_post') },
                      { value: 'FedEx', label: t('document_creation.form.carriers.fedex') },
                      { value: 'UPS', label: t('document_creation.form.carriers.ups') },
                      { value: 'Transport interne', label: formLabel('carriers.internal_transport') },
                      { value: 'Collecte fournisseur', label: formLabel('carriers.supplier_pickup') }
                    ]}
                    placeholder={t('document_creation.form.search_carrier')}
                    searchable
                  />
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('carrier_tracking_number')}</label>
                  <input
                    type="text"
                    value={formData.returnTrackingNumber || ''}
                    onChange={(e) => handleInputChange('returnTrackingNumber', e.target.value)}
                    placeholder={formLabel('ph_tracking_number')}
                  />
              </div>
                
                {/* Contrôle & Traitement */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(96, 165, 250, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{formLabel('control_processing')}</h4>
              </div>
              
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('goods_condition')}</label>
                  <CustomSelect
                    value={normalizeReturnGoodsConditionValue(formData.returnCondition || '')}
                    onChange={(value) =>
                      handleInputChange('returnCondition', normalizeReturnGoodsConditionValue(value))
                    }
                    options={getReturnGoodsConditionOptions(t)}
                    placeholder={t('document_creation.form.select_or_enter')}
                    searchable
                  />
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('return_type')}</label>
                  <CustomSelect
                    value={formData.returnType || ''}
                    onChange={(value) => handleInputChange('returnType', value)}
                    options={[
                      { value: 'Échange', label: formLabel('return_type_options.exchange') },
                      { value: 'remboursement', label: formLabel('return_type_options.refund') },
                      { value: 'réparation', label: formLabel('return_type_options.repair') },
                      { value: 'avoir', label: formLabel('credit_note_doc') },
                      { value: 'destruction', label: formLabel('return_type_options.destruction') },
                      { value: 'recyclage', label: formLabel('return_type_options.recycling') }
                    ]}
                    placeholder={t('document_creation.form.select_or_enter')}
                    searchable
                  />
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('return_reason')}</label>
                  <CustomSelect
                    multiple
                    multipleCheckColor="#10b981"
                    searchable
                    creatable
                    createLabel={formLabel('create_return_reason')}
                    value={formData.returnReason || ''}
                    onChange={(value) =>
                      handleInputChange(
                        'returnReason',
                        Array.isArray(value) ? value.filter(Boolean).join('|') : value,
                      )
                    }
                    options={[
                      { value: 'defectueux', label: formLabel('return_reasons.defective') },
                      { value: 'non_conforme', label: formLabel('return_reasons.non_compliant') },
                      { value: 'erreur_livraison', label: formLabel('return_reasons.delivery_error') },
                      { value: 'endommage', label: formLabel('return_reasons.transport_damage') },
                      { value: 'fin_contrat', label: formLabel('return_reasons.contract_end') },
                      { value: 'changement_besoin', label: formLabel('return_reasons.need_change') },
                      { value: 'garantie', label: formLabel('return_reasons.under_warranty') },
                      { value: 'surstockage', label: formLabel('return_reasons.overstock') },
                      { value: 'perime', label: formLabel('return_reasons.expired') },
                    ]}
                    placeholder={t('document_creation.form.select_or_enter')}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Bordereau de retour client - Section combinée: Détails du document */}
          {selectedType.id === 'client-return-slip' && (
            <div className="bg-white/5 rounded-xl p-5 border border-white/20 overflow-hidden">
              <div className="flex items-center gap-2 mb-4">
                <Undo2 className="h-5 w-5 text-green-400" />
                <h3 className={`text-lg font-semibold ${isDashboardLightMode ? 'text-black' : 'text-white'}`}>{t('document_creation.form.document_details')}</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Identifiants du document */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(16, 185, 129, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.document_identifiers')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.document_number')}</label>
                    <input
                      type="text"
                      value={formData.number || ''}
                      readOnly
                      className="readonly-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.date_label')}</label>
                    <CustomDatePicker
                      value={formData.date}
                      onChange={(dateString) => handleInputChange('date', dateString)}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('return_date')}</label>
                    <CustomDatePicker
                      value={formData.clientReturnReturnDate}
                      onChange={(dateString) => handleInputChange('clientReturnReturnDate', dateString)}
                    />
                  </div>
                </div>
                
                {/* Informations de suivi */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(16, 185, 129, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.tracking_info')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.prepared_by')}</label>
                  <CustomSelect
                    value={formData.issuedBy || ''}
                    onChange={(value) => {
                    handleInputChange('issuedBy', value);
                    // Sync with "Établie par" signature if enabled
                    if (formData.showEtabliePar) {
                      handleInputChange('etablieParName', value);
                    }
                  }}
                    options={availableFacturiersForDropdown}
                    placeholder={t('document_creation.form.search_or_enter_biller')}
                    searchable
                  />
                </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('checked_by')}</label>
                    <CustomSelect
                      value={formData.clientReturnControlledBy || ''}
                      onChange={(value) => handleInputChange('clientReturnControlledBy', value)}
                      options={availableEmployeesForDropdown.map(emp => ({
                        value: `${emp.firstName} ${emp.lastName}`,
                        label: `${emp.firstName} ${emp.lastName}${emp.position ? ` - ${emp.position}` : ''}`
                      }))}
                      placeholder={t('document_creation.form.search_employee')}
                      searchable
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.initial_status')}</label>
                    <CustomSelect
                      value={documentStatusValue()}
                      onChange={handleFormStatusChange}
                      creatable={false}
                      searchable={false}
                      options={getStatusOptions(selectedType.id)}
                      placeholder={t('document_creation.form.select_placeholder')}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.priority')}</label>
                    <CustomSelect
                      value={formData.priority ?? ''}
                      onChange={(value) => handleInputChange('priority', value)}
                      autoSelect={false}
                      createLabel={t('document_creation.form.create_priority')}
                      options={[
                        { value: 'urgent', label: t('document_creation.form.priority_levels.urgent') },
                        { value: 'high', label: t('document_creation.form.priority_levels.high') },
                        { value: 'normale', label: t('document_creation.form.priority_levels.normal') },
                        { value: 'low', label: t('document_creation.form.priority_levels.low') }
                      ]}
                      placeholder={t('document_creation.form.select_placeholder')}
                    />
                  </div>
                </div>
                
                {/* Références */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(16, 185, 129, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.references')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.ref_purchase_order')}</label>
                    <input
                      type="text"
                      value={formData.clientReturnOriginalPurchaseOrderRef || ''}
                      onChange={(e) => handleInputChange('clientReturnOriginalPurchaseOrderRef', e.target.value)}
                      placeholder={formLabel('ph_po_reference')}
                    />
                  </div>
                  
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.invoice_ref')}</label>
                  <CustomSelect
                    value={formData.clientReturnOriginalInvoiceRef || ''}
                    onChange={(value) => handleInputChange('clientReturnOriginalInvoiceRef', value)}
                    options={invoiceDocumentsForSalesClient.map((doc) => ({
                      value: doc.value,
                      label: doc.label,
                    }))}
                    placeholder={formLabel('ph_invoice_ref')}
                    searchable
                    creatable
                  />
                </div>
                  
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.ref_delivery_note')}</label>
                  <CustomSelect
                    value={formData.clientReturnOriginalDeliveryRef || ''}
                    onChange={(value) => handleInputChange('clientReturnOriginalDeliveryRef', value)}
                    options={deliveryDocumentsForSalesClient.map((doc) => ({
                      value: doc.value,
                      label: doc.label,
                    }))}
                    placeholder={formLabel('ph_delivery_note_ref')}
                    searchable
                    creatable
                  />
                </div>
                </div>
                
                {/* Contrôle & Traitement */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(16, 185, 129, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{formLabel('control_processing')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('goods_condition')}</label>
                    <CustomSelect
                      value={normalizeReturnGoodsConditionValue(formData.clientReturnCondition || '')}
                      onChange={(value) =>
                        handleInputChange(
                          'clientReturnCondition',
                          normalizeReturnGoodsConditionValue(value),
                        )
                      }
                      options={getReturnGoodsConditionOptions(t).filter(
                        (o) => o.value !== 'a_recycler',
                      )}
                      placeholder={t('document_creation.form.select_placeholder')}
                      searchable
                    />
                  </div>
                  
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('return_type')}</label>
                  <CustomSelect
                    value={formData.clientReturnType || ''}
                    onChange={(value) => handleInputChange('clientReturnType', value)}
                    options={[
                      { value: 'echange', label: formLabel('return_type_options.exchange') },
                      { value: 'remboursement', label: formLabel('return_type_options.refund') },
                      { value: 'reparation', label: formLabel('return_type_options.repair') },
                      { value: 'avoir', label: formLabel('credit_note_doc') }
                    ]}
                      placeholder={t('document_creation.form.select_placeholder')}
                    searchable
                  />
                </div>
                  
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('return_reason')}</label>
                  <CustomSelect
                      value={formData.clientReturnMotif || ''}
                      onChange={(value) => handleInputChange('clientReturnMotif', value)}
                      options={[
                        { value: 'defectueux', label: formLabel('return_reasons.defective') },
                        { value: 'non_conforme', label: formLabel('return_reasons.non_compliant') },
                        { value: 'erreur_livraison', label: formLabel('return_reasons.delivery_error') },
                        { value: 'endommage', label: formLabel('return_reasons.transport_damage') },
                        { value: 'changement_avis', label: formLabel('return_reasons.change_of_mind') },
                        { value: 'garantie', label: formLabel('return_reasons.under_warranty') },
                        { value: 'autre', label: formLabel('other') }
                      ]}
                      placeholder={t('document_creation.form.select_placeholder')}
                      searchable
                    />
                </div>
                </div>

                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('destination_warehouse')}</label>
                    <CustomSelect
                      value={formData.clientReturnWarehouse || ''}
                      onChange={(value) => handleInputChange('clientReturnWarehouse', value)}
                      options={availableWarehousesForDropdown}
                      placeholder={t('document_creation.form.search_warehouse')}
                      searchable
                    />
                  </div>

                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('return_carrier')}</label>
                  <CustomSelect
                    value={formData.clientReturnCarrier || ''}
                    onChange={(value) => handleInputChange('clientReturnCarrier', value)}
                    options={[
                      { value: 'SMSA', label: t('document_creation.form.carriers.smsa') },
                      { value: 'Aramex', label: t('document_creation.form.carriers.aramex') },
                      { value: 'DHL', label: t('document_creation.form.carriers.dhl') },
                      { value: 'Saudi Post', label: t('document_creation.form.carriers.saudi_post') },
                      { value: 'FedEx', label: t('document_creation.form.carriers.fedex') },
                      { value: 'UPS', label: t('document_creation.form.carriers.ups') },
                    ]}
                    placeholder={t('document_creation.form.search_carrier')}
                    searchable
                  />
                </div>

                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('carrier_tracking_number')}</label>
                  <input
                    type="text"
                    value={formData.clientReturnTrackingNumber || ''}
                    onChange={(e) => handleInputChange('clientReturnTrackingNumber', e.target.value)}
                    placeholder={formLabel('ph_tracking_number')}
                  />
                </div>
                </div>
              </div>
            </div>
          )}

          {/* Bon de sortie - Section combinée: Détails du document */}
          {selectedType.id === 'exit-note' && (
            <div className="bg-white/5 rounded-xl p-5 border border-white/20 overflow-hidden">
              <div className="flex items-center gap-2 mb-4">
                <FileOutput className="h-5 w-5 text-orange-400" />
                <h3 className={`text-lg font-semibold ${isDashboardLightMode ? 'text-black' : 'text-white'}`}>{t('document_creation.form.document_details')}</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Identifiants du document */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(249, 115, 22, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.document_identifiers')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.document_number')}</label>
                    <input
                      type="text"
                      value={formData.number || ''}
                      readOnly
                      className="readonly-input"
                    />
                  </div>
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.date_label')}</label>
                    <CustomDatePicker
                      value={formData.date}
                      onChange={(dateString) => handleInputChange('date', dateString)}
                    />
                  </div>
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('expected_exit_date')}</label>
                    <CustomDatePicker
                      value={formData.exitNoteExpectedDate || formData.date}
                      onChange={(dateString) => handleInputChange('exitNoteExpectedDate', dateString)}
                    />
                  </div>
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('source_warehouse')}</label>
                    <CustomSelect
                      value={formData.exitNoteOriginWarehouse || ''}
                      onChange={(value) => handleInputChange('exitNoteOriginWarehouse', value)}
                      options={availableWarehousesForDropdown}
                      placeholder={t('document_creation.form.search_warehouse')}
                      searchable
                    />
                  </div>
                </div>
                
                {/* Informations de suivi */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(249, 115, 22, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.tracking_info')}</h4>
            </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.prepared_by')}</label>
                  <CustomSelect
                    value={formData.issuedBy || ''}
                    onChange={(value) => {
                    handleInputChange('issuedBy', value);
                    // Sync with "Établie par" signature if enabled
                    if (formData.showEtabliePar) {
                      handleInputChange('etablieParName', value);
                    }
                  }}
                    options={availableFacturiersForDropdown}
                    placeholder={t('document_creation.form.search_or_enter_biller')}
                    searchable
                  />
                </div>
              
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('exit_manager')}</label>
                  <CustomSelect
                      value={formData.exitNoteHandler || ''}
                      onChange={(value) => handleInputChange('exitNoteHandler', value)}
                    options={availableEmployeesForDropdown.map(emp => ({
                      value: `${emp.firstName} ${emp.lastName}`,
                      label: `${emp.firstName} ${emp.lastName}${emp.position ? ` - ${emp.position}` : ''}`
                    }))}
                    placeholder={t('document_creation.form.search_employee')}
                    searchable
                  />
                </div>
                  
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('approved_by')}</label>
                  <CustomSelect
                    value={formData.exitNoteAuthorizedBy || ''}
                    onChange={(value) => handleInputChange('exitNoteAuthorizedBy', value)}
                    options={availableEmployeesForDropdown.map(emp => ({
                      value: `${emp.firstName} ${emp.lastName}`,
                      label: `${emp.firstName} ${emp.lastName}${emp.position ? ` - ${emp.position}` : ''}`
                    }))}
                    placeholder={t('document_creation.form.search_employee')}
                    searchable
                  />
                </div>
                  
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.initial_status')}</label>
                    <CustomSelect
                      value={documentStatusValue()}
                      onChange={handleFormStatusChange}
                      creatable={false}
                      searchable={false}
                      options={getStatusOptions(selectedType.id)}
                      placeholder={t('document_creation.form.select_placeholder')}
                    />
                </div>
                  
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.priority')}</label>
                    <CustomSelect
                      value={formData.priority ?? ''}
                      onChange={(value) => handleInputChange('priority', value)}
                      autoSelect={false}
                      createLabel={t('document_creation.form.create_priority')}
                      options={[
                        { value: 'urgent', label: t('document_creation.form.priority_levels.urgent') },
                        { value: 'high', label: t('document_creation.form.priority_levels.high') },
                        { value: 'normale', label: t('document_creation.form.priority_levels.normal') },
                        { value: 'low', label: t('document_creation.form.priority_levels.low') }
                      ]}
                      placeholder={t('document_creation.form.select_placeholder')}
                  />
              </div>
            </div>
                
                {/* Références */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(249, 115, 22, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.references')}</h4>
              </div>
              
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('client_po_ref')}</label>
                    <CustomSelect
                      value={formData.exitNoteClientOrderRef || ''}
                      onChange={(value) => handleInputChange('exitNoteClientOrderRef', value)}
                      options={[]}
                      placeholder={formLabel('ph_client_order_ref')}
                      searchable
                      creatable
                    />
                  </div>
              
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('invoice_doc')}</label>
                    <CustomSelect
                      value={formData.exitNoteInvoiceRef || ''}
                      onChange={(value) => handleInputChange('exitNoteInvoiceRef', value)}
                      options={[]}
                      placeholder={formLabel('ph_invoice_ref')}
                      searchable
                      creatable
                    />
                  </div>
                  
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('delivery_note_ref')}</label>
                    <CustomSelect
                      value={formData.exitNoteDeliveryNoteRef || ''}
                      onChange={(value) => handleInputChange('exitNoteDeliveryNoteRef', value)}
                      options={[]}
                      placeholder={formLabel('ph_delivery_note_ref')}
                      searchable
                      creatable
                    />
                  </div>
                  
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('transfer_note_ref')}</label>
                    <CustomSelect
                      value={formData.exitNoteTransferNoteRef || ''}
                      onChange={(value) => handleInputChange('exitNoteTransferNoteRef', value)}
                      options={[]}
                      placeholder={formLabel('ph_transfer_note_ref')}
                      searchable
                      creatable
                    />
                  </div>
                </div>
                
                {/* Destination & Transport */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(249, 115, 22, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{formLabel('destination_transport_section')}</h4>
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('destination')}</label>
                  <CustomSelect
                    value={formData.exitNoteDestination || ''}
                    onChange={(value) => handleInputChange('exitNoteDestination', value)}
                    options={[
                      { value: 'client', label: formLabel('destinations.client') },
                      { value: 'fournisseur', label: formLabel('supplier') },
                      { value: 'depot', label: formLabel('destinations.other_depot') },
                      { value: 'production', label: formLabel('entry_sources.production') },
                      { value: 'externe', label: formLabel('destinations.external') }
                    ]}
                    placeholder={t('document_creation.form.select_placeholder')}
                    searchable
                  />
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('destination_warehouse')}</label>
                  <CustomSelect
                    value={formData.exitNoteDestinationWarehouse || ''}
                    onChange={(value) => handleInputChange('exitNoteDestinationWarehouse', value)}
                    options={availableWarehousesForDropdown}
                    placeholder={t('document_creation.form.search_warehouse')}
                    searchable
                  />
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('transport_mode')}</label>
                  <CustomSelect
                    value={formData.exitNoteTransportMode || ''}
                    onChange={(value) => handleInputChange('exitNoteTransportMode', value)}
                    options={[
                      { value: 'camion', label: formLabel('transport_modes.truck') },
                      { value: 'fourgonnette', label: formLabel('transport_modes.van') },
                      { value: 'coursier', label: formLabel('delivery_modes.courier') },
                      { value: 'palette', label: formLabel('transport_modes.palette') },
                      { value: 'transport_express', label: formLabel('delivery_modes.express') },
                      { value: 'livraison_standard', label: formLabel('delivery_modes.standard') }
                    ]}
                    placeholder={t('document_creation.form.search_generic')}
                    searchable
                  />
                </div>
              </div>
            </div>
          )}

          {/* Bon de transfert - Section combinée: Détails du document */}
          {selectedType.id === 'transfer-note' && (
            <div className="bg-white/5 rounded-xl p-5 border border-white/20 overflow-hidden">
              <div className="flex items-center gap-2 mb-4">
                <ArrowRightLeft className="h-5 w-5 text-orange-400" />
                <h3 className={`text-lg font-semibold ${isDashboardLightMode ? 'text-black' : 'text-white'}`}>{t('document_creation.form.document_details')}</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Identifiants du document */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(249, 115, 22, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.document_identifiers')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.document_number')}</label>
                    <input
                      type="text"
                      value={formData.number || ''}
                      readOnly
                      className="readonly-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('tracking_number')}</label>
                    <input
                      type="text"
                      value={formData.transferNoteTrackingNumber || ''}
                      onChange={(e) => handleInputChange('transferNoteTrackingNumber', e.target.value)}
                      placeholder={formLabel('ph_transport_tracking')}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.date_label')}</label>
                    <CustomDatePicker
                      value={formData.date}
                      onChange={(dateString) => handleInputChange('date', dateString)}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('expected_transfer_date')}</label>
                    <CustomDatePicker
                      value={formData.transferNoteExpectedDate || formData.date}
                      onChange={(dateString) => handleInputChange('transferNoteExpectedDate', dateString)}
                    />
                  </div>
                </div>
                
                {/* Informations de suivi */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(249, 115, 22, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.tracking_info')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.prepared_by')}</label>
                    <CustomSelect
                      value={formData.issuedBy || ''}
                      onChange={(value) => {
                    handleInputChange('issuedBy', value);
                    if (formData.showEtabliePar) {
                      handleInputChange('etablieParName', value);
                    }
                  }}
                      options={availableFacturiersForDropdown}
                      placeholder={t('document_creation.form.search_or_enter_biller')}
                      searchable
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('requested_by')}</label>
                    <CustomSelect
                      value={formData.transferNoteRequestedBy || ''}
                      onChange={(value) => handleInputChange('transferNoteRequestedBy', value)}
                      options={availableEmployeesForDropdown.map(emp => ({
                        value: `${emp.firstName} ${emp.lastName}`,
                        label: `${emp.firstName} ${emp.lastName}${emp.position ? ` - ${emp.position}` : ''}`
                      }))}
                      placeholder={t('document_creation.form.search_employee')}
                      searchable
                    />
                  </div>

                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('transfer_manager')}</label>
                    <CustomSelect
                      value={formData.transferNoteHandler || ''}
                      onChange={(value) => handleInputChange('transferNoteHandler', value)}
                      options={availableEmployeesForDropdown.map(emp => ({
                        value: `${emp.firstName} ${emp.lastName}`,
                        label: `${emp.firstName} ${emp.lastName}${emp.position ? ` - ${emp.position}` : ''}`
                      }))}
                      placeholder={t('document_creation.form.search_employee')}
                      searchable
                    />
                  </div>

                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.priority')}</label>
                    <CustomSelect
                      value={formData.priority ?? ''}
                      onChange={(value) => handleInputChange('priority', value)}
                      autoSelect={false}
                      createLabel={t('document_creation.form.create_priority')}
                      options={[
                        { value: 'urgent', label: t('document_creation.form.priority_levels.urgent') },
                        { value: 'high', label: t('document_creation.form.priority_levels.high') },
                        { value: 'normale', label: t('document_creation.form.priority_levels.normal') },
                        { value: 'low', label: t('document_creation.form.priority_levels.low') }
                      ]}
                      placeholder={t('document_creation.form.select_placeholder')}
                    />
                  </div>

                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.initial_status')}</label>
                    <CustomSelect
                      value={documentStatusValue()}
                      onChange={handleFormStatusChange}
                      creatable={false}
                      searchable={false}
                      options={getStatusOptions(selectedType.id)}
                      placeholder={t('document_creation.form.select_placeholder')}
                    />
                  </div>

                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('transport_mode')}</label>
                    <CustomSelect
                      value={formData.transferNoteTransportMode || ''}
                      onChange={(value) => handleInputChange('transferNoteTransportMode', value)}
                      options={[
                        { value: 'camion', label: formLabel('transport_modes.truck') },
                        { value: 'fourgonnette', label: formLabel('transport_modes.van') },
                        { value: 'coursier', label: formLabel('delivery_modes.courier') },
                        { value: 'palette', label: formLabel('transport_modes.palette') },
                        { value: 'transport_interne', label: formLabel('carriers.internal_transport') },
                        { value: 'chariot', label: formLabel('transport_modes.forklift') }
                      ]}
                      placeholder={t('document_creation.form.search_generic')}
                      searchable
                    />
                  </div>
                </div>
                
                {/* Références */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(249, 115, 22, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.references')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('source_doc_reference')}</label>
                    <CustomSelect
                      value={formData.transferNoteDocumentReference || ''}
                      onChange={(value) => handleInputChange('transferNoteDocumentReference', value)}
                      options={[]}
                      placeholder={formLabel('ph_source_doc_ref')}
                      searchable
                      creatable
                    />
                  </div>
                  
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('transfer_request')}</label>
                    <CustomSelect
                      value={formData.transferNoteTransferRequestRef || ''}
                      onChange={(value) => handleInputChange('transferNoteTransferRequestRef', value)}
                      options={[]}
                      placeholder={formLabel('ph_transfer_request_ref')}
                      searchable
                      creatable
                    />
                  </div>
                </div>
                
                {/* ENTREPÔT D'ORIGINE */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(249, 115, 22, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{formLabel('origin_warehouse_section')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('warehouse_name')}</label>
                    <CustomSelect
                      value={formData.transferNoteOriginWarehouse || ''}
                      onChange={(value) => {
                        handleInputChange('transferNoteOriginWarehouse', value);
                        const wh = availableWarehousesForDropdown.find(w => w.value === value || w.name === value);
                        if (wh) {
                          if (wh.reference) handleInputChange('transferNoteOriginWarehouseCode', wh.reference);
                          if (wh.address) handleInputChange('transferNoteOriginWarehouseAddress', wh.address);
                          if (wh.manager) handleInputChange('transferNoteOriginWarehouseManager', wh.manager);
                        }
                      }}
                      options={availableWarehousesForDropdown}
                      placeholder={t('document_creation.form.search_warehouse')}
                      searchable
                    />
                  </div>

                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('warehouse_reference')}</label>
                    <input
                      type="text"
                      value={formData.transferNoteOriginWarehouseCode || ''}
                      onChange={(e) => handleInputChange('transferNoteOriginWarehouseCode', e.target.value)}
                      placeholder={formLabel('ph_warehouse_code')}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('warehouse_address')}</label>
                    <input
                      type="text"
                      value={formData.transferNoteOriginWarehouseAddress || ''}
                      onChange={(e) => handleInputChange('transferNoteOriginWarehouseAddress', e.target.value)}
                      placeholder={t('document_creation.form.full_address')}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('manager_name')}</label>
                    <input
                      type="text"
                      value={formData.transferNoteOriginWarehouseManager || ''}
                      onChange={(e) => handleInputChange('transferNoteOriginWarehouseManager', e.target.value)}
                      placeholder={formLabel('ph_manager_full_name')}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('planned_departure_time')}</label>
                    <input
                      type="time"
                      value={formData.transferNoteDepartureTime || ''}
                      onChange={(e) => handleInputChange('transferNoteDepartureTime', e.target.value)}
                    />
                  </div>
                </div>
                
                {/* ENTREPÔT DE DESTINATION */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(249, 115, 22, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{formLabel('destination_warehouse_section')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('warehouse_name')}</label>
                    <CustomSelect
                      value={formData.transferNoteDestinationWarehouse || ''}
                      onChange={(value) => {
                        handleInputChange('transferNoteDestinationWarehouse', value);
                        const wh = availableWarehousesForDropdown.find(w => w.value === value || w.name === value);
                        if (wh) {
                          if (wh.reference) handleInputChange('transferNoteDestinationWarehouseCode', wh.reference);
                          if (wh.address) handleInputChange('transferNoteDestinationWarehouseAddress', wh.address);
                          if (wh.manager) handleInputChange('transferNoteDestinationWarehouseManager', wh.manager);
                        }
                      }}
                      options={availableWarehousesForDropdown}
                      placeholder={t('document_creation.form.search_warehouse')}
                      searchable
                    />
                  </div>

                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('warehouse_reference')}</label>
                    <input
                      type="text"
                      value={formData.transferNoteDestinationWarehouseCode || ''}
                      onChange={(e) => handleInputChange('transferNoteDestinationWarehouseCode', e.target.value)}
                      placeholder={formLabel('ph_warehouse_code_dest')}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('warehouse_address')}</label>
                    <input
                      type="text"
                      value={formData.transferNoteDestinationWarehouseAddress || ''}
                      onChange={(e) => handleInputChange('transferNoteDestinationWarehouseAddress', e.target.value)}
                      placeholder={t('document_creation.form.full_address')}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('manager_name')}</label>
                    <input
                      type="text"
                      value={formData.transferNoteDestinationWarehouseManager || ''}
                      onChange={(e) => handleInputChange('transferNoteDestinationWarehouseManager', e.target.value)}
                      placeholder={formLabel('ph_manager_full_name')}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('planned_arrival_time')}</label>
                    <input
                      type="time"
                      value={formData.transferNoteArrivalTime || ''}
                      onChange={(e) => handleInputChange('transferNoteArrivalTime', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Bon d'entrée en stock - Section combinée: Détails du document */}
          {selectedType.id === 'stock-entry' && (
            <div className="bg-white/5 rounded-xl p-5 border border-white/20 overflow-hidden">
              <div className="flex items-center gap-2 mb-4">
                <PackagePlus className="h-5 w-5 text-orange-400" />
                <h3 className={`text-lg font-semibold ${isDashboardLightMode ? 'text-black' : 'text-white'}`}>{t('document_creation.form.document_details')}</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Identifiants du document */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(249, 115, 22, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.document_identifiers')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.document_number')}</label>
                  <input
                    type="text"
                      value={formData.number || ''}
                      readOnly
                      className="readonly-input"
                  />
                </div>
                  
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('supplier')}</label>
                  <CustomSelect
                    value={formData.stockEntrySupplier || ''}
                    onChange={(value) => {
                      handleInputChange('stockEntrySupplier', value);
                      // Also set supplier ID if found in list for full details in PDF
                      const selectedSupplier = availableSuppliersForDropdown.find(s => s.name === value || s.value === value);
                      if (selectedSupplier?.id) {
                        handleInputChange('supplier', selectedSupplier.id);
                      }
                    }}
                    options={availableSuppliersForDropdown}
                    placeholder={t('document_creation.form.search_or_enter_supplier')}
                    searchable
                  />
                </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.date_label')}</label>
                    <CustomDatePicker
                      value={formData.date}
                      onChange={(dateString) => handleInputChange('date', dateString)}
                    />
              </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('expected_entry_date')}</label>
                    <CustomDatePicker
                      value={formData.expectedEntryDate}
                      onChange={(dateString) => handleInputChange('expectedEntryDate', dateString)}
                    />
            </div>
              </div>
              
                {/* Informations de suivi */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(249, 115, 22, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.tracking_info')}</h4>
              </div>
              
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.prepared_by')}</label>
                  <CustomSelect
                    value={formData.issuedBy || ''}
                    onChange={(value) => {
                    handleInputChange('issuedBy', value);
                    // Sync with "Établie par" signature if enabled
                    if (formData.showEtabliePar) {
                      handleInputChange('etablieParName', value);
                    }
                  }}
                    options={availableFacturiersForDropdown}
                    placeholder={t('document_creation.form.search_or_enter_biller')}
                    searchable
                  />
                </div>
                  
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('received_by')}</label>
                  <CustomSelect
                    value={formData.stockEntryReceivedBy || ''}
                    onChange={(value) => handleInputChange('stockEntryReceivedBy', value)}
                    options={availableEmployeesForDropdown.map(emp => ({
                      value: `${emp.firstName} ${emp.lastName}`,
                      label: `${emp.firstName} ${emp.lastName}${emp.position ? ` - ${emp.position}` : ''}`
                    }))}
                    placeholder={t('document_creation.form.search_employee')}
                    searchable
                  />
                </div>
                  
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('checked_by')}</label>
                  <CustomSelect
                    value={formData.stockEntryControlledBy || ''}
                    onChange={(value) => handleInputChange('stockEntryControlledBy', value)}
                    options={availableEmployeesForDropdown.map(emp => ({
                      value: `${emp.firstName} ${emp.lastName}`,
                      label: `${emp.firstName} ${emp.lastName}${emp.position ? ` - ${emp.position}` : ''}`
                    }))}
                    placeholder={t('document_creation.form.search_employee')}
                    searchable
                  />
                </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.initial_status')}</label>
                    <CustomSelect
                      value={documentStatusValue()}
                      onChange={handleFormStatusChange}
                      creatable={false}
                      searchable={false}
                      options={getStatusOptions(selectedType.id)}
                      placeholder={t('document_creation.form.select_placeholder')}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.priority')}</label>
                    <CustomSelect
                      value={formData.priority ?? ''}
                      onChange={(value) => handleInputChange('priority', value)}
                      autoSelect={false}
                      createLabel={t('document_creation.form.create_priority')}
                      options={[
                        { value: 'urgent', label: t('document_creation.form.priority_levels.urgent') },
                        { value: 'high', label: t('document_creation.form.priority_levels.high') },
                        { value: 'normale', label: t('document_creation.form.priority_levels.normal') },
                        { value: 'low', label: t('document_creation.form.priority_levels.low') }
                      ]}
                      placeholder={t('document_creation.form.select_placeholder')}
                    />
                  </div>
                </div>
                
                {/* Références */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(249, 115, 22, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.references')}</h4>
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('ref_purchase_order')}</label>
                  <CustomSelect
                    value={formData.stockEntryPurchaseOrderRef || ''}
                    onChange={(value) => handleInputChange('stockEntryPurchaseOrderRef', value)}
                    options={[]}
                    placeholder={formLabel('ph_example_po_supplier')}
                    searchable
                    creatable
                  />
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('reception_note_ref')}</label>
                  <CustomSelect
                    value={formData.stockEntryReceptionNoteRef || ''}
                    onChange={(value) => handleInputChange('stockEntryReceptionNoteRef', value)}
                    options={[]}
                    placeholder={formLabel('ph_example_reception_short')}
                    searchable
                    creatable
                  />
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('transfer_note_ref')}</label>
                  <CustomSelect
                    value={formData.stockEntryTransferNoteRef || ''}
                    onChange={(value) => handleInputChange('stockEntryTransferNoteRef', value)}
                    options={[]}
                    placeholder={formLabel('ph_transfer_note_ref')}
                    searchable
                    creatable
                  />
                </div>
                
                {/* Source & Contrôle */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(249, 115, 22, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{formLabel('source_control_section')}</h4>
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('destination_warehouse')}</label>
                  <CustomSelect
                    value={formData.stockEntryWarehouse || ''}
                    onChange={(value) => handleInputChange('stockEntryWarehouse', value)}
                    options={availableWarehousesForDropdown}
                    placeholder={t('document_creation.form.search_warehouse')}
                    searchable
                  />
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('entry_source')}</label>
                  <CustomSelect
                    value={formData.stockEntrySource || ''}
                    onChange={(value) => handleInputChange('stockEntrySource', value)}
                    options={[
                      { value: 'supplier', label: formLabel('entry_sources.supplier') },
                      { value: 'production', label: formLabel('entry_sources.production') },
                      { value: 'transfer', label: formLabel('entry_sources.transfer') },
                      { value: 'return', label: formLabel('return_types.client_return') },
                      { value: 'adjustment', label: formLabel('entry_sources.adjustment') },
                      { value: 'other', label: formLabel('other') }
                    ]}
                    placeholder={t('document_creation.form.select_placeholder')}
                    searchable
                  />
                </div>
                
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('goods_condition')}</label>
                  <CustomSelect
                    value={formData.stockEntryCondition || ''}
                    onChange={(value) => handleInputChange('stockEntryCondition', value)}
                    options={[
                      { value: 'good', label: formLabel('stock_entry_conditions.good') },
                      { value: 'excellent', label: formLabel('stock_entry_conditions.excellent') },
                      { value: 'damaged', label: formLabel('stock_entry_conditions.damaged') },
                      { value: 'partial', label: statusLbl('partial_short') },
                      { value: 'quarantine', label: formLabel('stock_entry_conditions.quarantine') },
                      { value: 'defective', label: formLabel('stock_entry_conditions.defective') }
                    ]}
                    placeholder={t('document_creation.form.select_placeholder')}
                    searchable
                  />
                </div>
              </div>
            </div>
          )}

          {/* Bordereau d'envoi - Section 1: Client */}
          {selectedType.id === 'shipping-slip' && (() => {
            const partnerList = availableClientsForDropdown;
            const allPartners = allClients || [];
            const partnerId = formData.client;
            const partnerName = formData.clientName || '';
            const partnerDetails = allPartners.find(p => String(p.id) === String(partnerId)) ||
                                   partnerList.find(p => p.name === partnerName || p.value === partnerName);
            const displayPhone = partnerDetails?.phone || partnerDetails?.phone1 || '';
            const displayEmail = partnerDetails?.email || '';
            const displayNif =
              partnerDetails?.fiscal_id ||
              partnerDetails?.nif ||
              partnerDetails?.fiscalId ||
              '';
            const displayRc =
              partnerDetails?.rc ||
              partnerDetails?.rc_number ||
              partnerDetails?.registrationNumber ||
              '';
            const displayArt = partnerDetails?.article || '';
            const displayNis = partnerDetails?.nis || partnerDetails?.nis_number || '';
            const displayAddress =
              [partnerDetails?.address, partnerDetails?.city].filter(Boolean).join(', ') ||
              partnerDetails?.address ||
              '';
            const partnerSelectValue = resolvePartnerDropdownLabel(
              partnerId,
              partnerName,
              partnerList,
              allPartners,
            );

            return (
              <div className="bg-white/5 rounded-xl p-5 border border-white/20 overflow-hidden">
                <div className="flex items-center gap-3 mb-4">
                  <User className="h-5 w-5 text-blue-400" />
                  <h3 className={`text-lg font-semibold ${isDashboardLightMode ? 'text-black' : 'text-white'}`}>
                    {t('document_creation.document_client')}
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="form-group">
                      <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{ color: isDashboardLightMode ? '#000000' : '#ffffff' }}>
                        {t('document_creation.form.select_a_client')}
                      </label>
                      <CustomSelect
                        value={partnerSelectValue}
                        onChange={(value) => {
                          const sel = availableClientsForDropdown.find(c => c.name === value || c.value === value);
                          setFormData((prev) => {
                            const next = {
                              ...prev,
                              client: sel?.id || '',
                              clientName: value,
                            };
                            if (sel?.code) next.clientCode = sel.code;
                            if (sel?.email) next.clientEmail = sel.email;
                            if (sel?.phone) next.clientPhone = sel.phone;
                            return next;
                          });
                        }}
                        options={partnerList}
                        placeholder={t('document_creation.form.search_or_enter_client')}
                        searchable
                      />
                    </div>
                    <div className="form-group">
                      <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{ color: isDashboardLightMode ? '#000000' : '#ffffff' }}>{t('document_creation.form.phone')}</label>
                      <input type="text" value={displayPhone} readOnly className="readonly-input" placeholder={t('document_creation.form.phone')} />
                    </div>
                    <div className="form-group">
                      <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{ color: isDashboardLightMode ? '#000000' : '#ffffff' }}>{t('common.email')}</label>
                      <input type="text" value={displayEmail} readOnly className="readonly-input" placeholder={t('common.email')} />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="form-group">
                      <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{ color: isDashboardLightMode ? '#000000' : '#ffffff' }}>{t('document_creation.form.nif')}</label>
                      <input type="text" value={displayNif} readOnly className="readonly-input" placeholder={t('document_creation.form.nif')} />
                    </div>
                    <div className="form-group">
                      <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{ color: isDashboardLightMode ? '#000000' : '#ffffff' }}>{t('document_creation.form.rc')}</label>
                      <input type="text" value={displayRc} readOnly className="readonly-input" placeholder={t('document_creation.form.rc')} />
                    </div>
                    <div className="form-group">
                      <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{ color: isDashboardLightMode ? '#000000' : '#ffffff' }}>{t('document_creation.form.art')}</label>
                      <input type="text" value={displayArt} readOnly className="readonly-input" placeholder={t('document_creation.form.art')} />
                    </div>
                    <div className="form-group">
                      <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{ color: isDashboardLightMode ? '#000000' : '#ffffff' }}>{t('document_creation.form.nis')}</label>
                      <input type="text" value={displayNis} readOnly className="readonly-input" placeholder={t('document_creation.form.nis')} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{ color: isDashboardLightMode ? '#000000' : '#ffffff' }}>{t('document_creation.form.address')}</label>
                    <input type="text" value={displayAddress} readOnly className="readonly-input" placeholder={t('document_creation.form.full_address')} />
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Bordereau d'envoi - Section 2: Détails du document */}
          {selectedType.id === 'shipping-slip' && (
            <div className="bg-white/5 rounded-xl p-5 border border-white/20 overflow-hidden">
              <div className="flex items-center gap-2 mb-4">
                <Send className="h-5 w-5 text-orange-400" />
                <h3 className={`text-lg font-semibold ${isDashboardLightMode ? 'text-black' : 'text-white'}`}>{t('document_creation.form.document_details')}</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Identifiants du document */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(249, 115, 22, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.document_identifiers')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.document_number')}</label>
                    <input
                      type="text"
                      value={formData.number || ''}
                      readOnly
                      className="readonly-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('tracking_number')}</label>
                    <input
                      type="text"
                      value={formData.shipmentTrackingNumber || ''}
                      onChange={(e) => handleInputChange('shipmentTrackingNumber', e.target.value)}
                      placeholder={formLabel('ph_transport_tracking')}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.date_label')}</label>
                    <CustomDatePicker
                      value={formData.date}
                      onChange={(dateString) => handleInputChange('date', dateString)}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('expected_shipment_date')}</label>
                    <CustomDatePicker
                      value={formData.shipmentDate || formData.date}
                      onChange={(dateString) => handleInputChange('shipmentDate', dateString)}
                    />
                  </div>
                </div>
                
                {/* Informations de suivi */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(249, 115, 22, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.tracking_info')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.prepared_by')}</label>
                  <CustomSelect
                    value={formData.issuedBy || ''}
                    onChange={(value) => {
                    handleInputChange('issuedBy', value);
                    // Sync with "Établie par" signature if enabled
                    if (formData.showEtabliePar) {
                      handleInputChange('etablieParName', value);
                    }
                  }}
                    options={availableFacturiersForDropdown}
                    placeholder={t('document_creation.form.search_or_enter_biller')}
                    searchable
                  />
                </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.initial_status')}</label>
                    <CustomSelect
                      value={documentStatusValue()}
                      onChange={handleFormStatusChange}
                      creatable={false}
                      searchable={false}
                      options={getStatusOptions(selectedType.id)}
                      placeholder={t('document_creation.form.select_placeholder')}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.priority')}</label>
                    <CustomSelect
                      value={formData.priority ?? ''}
                      onChange={(value) => handleInputChange('priority', value)}
                      autoSelect={false}
                      createLabel={t('document_creation.form.create_priority')}
                      options={[
                        { value: 'urgent', label: t('document_creation.form.priority_levels.urgent') },
                        { value: 'high', label: t('document_creation.form.priority_levels.high') },
                        { value: 'normale', label: t('document_creation.form.priority_levels.normal') },
                        { value: 'low', label: t('document_creation.form.priority_levels.low') }
                      ]}
                      placeholder={t('document_creation.form.select_placeholder')}
                    />
                  </div>
                </div>
                
                {/* Références */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(249, 115, 22, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.references')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('ref_delivery_note')}</label>
                    <CustomSelect
                      value={formData.shipmentDeliveryNoteRef || formData.shipment?.deliveryNoteRef || ''}
                      onChange={(value) => handleInputChange('shipmentDeliveryNoteRef', value)}
                      options={deliveryDocumentsForSalesClient.map((doc) => ({
                        value: doc.value,
                        label: doc.label,
                      }))}
                      placeholder={formLabel('ph_delivery_note_ref')}
                      searchable
                      creatable
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('invoice_ref')}</label>
                    <CustomSelect
                      value={formData.shipmentInvoiceRef || formData.shipment?.invoiceRef || ''}
                      onChange={(value) => handleInputChange('shipmentInvoiceRef', value)}
                      options={invoiceDocumentsForSalesClient.map((doc) => ({
                        value: doc.value,
                        label: doc.label,
                      }))}
                      placeholder={formLabel('ph_invoice_ref')}
                      searchable
                      creatable
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('client_order_reference')}</label>
                    <input
                      type="text"
                      value={formData.shipmentClientOrderRef || formData.shipment?.clientOrderRef || ''}
                      onChange={(e) => handleInputChange('shipmentClientOrderRef', e.target.value)}
                      placeholder={formLabel('ph_client_order_ref')}
                    />
                  </div>
                </div>
                
                {/* Expédition */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(249, 115, 22, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{formLabel('shipment_section')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('source_warehouse')}</label>
                    <CustomSelect
                      value={formData.shipmentOriginWarehouse || formData.shipment?.originWarehouse || ''}
                      onChange={(value) => handleInputChange('shipmentOriginWarehouse', value)}
                      options={availableWarehousesForDropdown}
                      placeholder={t('document_creation.form.search_warehouse')}
                      searchable
                    />
                  </div>

                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.delivery_location')}</label>
                    <input
                      type="text"
                      value={formData.deliveryLocation || formData.deliveryAddress || ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        handleInputChange('deliveryLocation', v);
                        handleInputChange('deliveryAddress', v);
                      }}
                      placeholder={t('document_creation.form.search_location')}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('delivery_mode')}</label>
                    <CustomSelect
                      value={formData.shipmentMethod || ''}
                      onChange={(value) => handleInputChange('shipmentMethod', value)}
                      options={deliveryModeSimpleOptions}
                      placeholder={formLabel('ph_delivery_mode')}
                      searchable
                    />
                  </div>
                </div>

                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('carrier')}</label>
                    <CustomSelect
                      value={formData.shipmentCarrier || formData.shipment?.carrier || ''}
                      onChange={(value) => handleInputChange('shipmentCarrier', value)}
                      options={carrierSelectOptions}
                      placeholder={t('document_creation.form.search_carrier')}
                      searchable
                    />
                  </div>

                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('carrier_tracking_number')}</label>
                    <input
                      type="text"
                      value={formData.shipmentCarrierTrackingNumber || ''}
                      onChange={(e) => handleInputChange('shipmentCarrierTrackingNumber', e.target.value)}
                      placeholder={formLabel('ph_carrier_tracking')}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Bordereau d'envoi - Section 3: Détails du colis */}
          {selectedType.id === 'shipping-slip' && (
            <div className="bg-white/5 rounded-xl p-5 border border-white/20 overflow-hidden">
              <div className="flex items-center gap-2 mb-4">
                <Package className="h-5 w-5 text-orange-400" />
                <h3 className={`text-lg font-semibold ${isDashboardLightMode ? 'text-black' : 'text-white'}`}>{formLabel('package_details_title')}</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('package_type')}</label>
                  <CustomSelect
                    value={formData.shipmentPackageType || formData.shipment?.packageType || ''}
                    onChange={(value) => handleInputChange('shipmentPackageType', value)}
                    options={[
                      { value: 'carton', label: formLabel('package_types.carton') },
                      { value: 'palette', label: formLabel('package_types.palette') },
                      { value: 'caisse', label: formLabel('package_types.crate') },
                      { value: 'sac', label: formLabel('package_types.bag') },
                      { value: 'conteneur', label: formLabel('package_types.container') },
                      { value: 'vrac', label: formLabel('package_types.bulk') },
                      { value: 'enveloppe', label: formLabel('package_types.envelope') }
                    ]}
                    placeholder={t('document_creation.form.select_or_enter')}
                    searchable
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('product_type')}</label>
                  <CustomSelect
                    value={formData.shipmentProductType || formData.shipment?.productType || ''}
                    onChange={(value) => handleInputChange('shipmentProductType', value)}
                    options={[
                      { value: 'fragile', label: formLabel('product_types.fragile') },
                      { value: 'normal', label: formLabel('product_types.normal') },
                      { value: 'perissable', label: formLabel('product_types.perishable') },
                      { value: 'dangereux', label: formLabel('product_types.dangerous') },
                      { value: 'precieux', label: formLabel('product_types.precious') },
                      { value: 'lourd', label: formLabel('product_types.heavy') }
                    ]}
                    placeholder={t('document_creation.form.select_or_enter')}
                    searchable
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('package_count')}</label>
                  <CustomNumberInput
                    value={formData.shipmentPackageCount || ''}
                    onChange={(val) => handleInputChange('shipmentPackageCount', val === '' ? null : parseInt(val, 10) || 0)}
                    placeholder={formLabel('ph_package_count')}
                    min={0}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('total_weight_kg')}</label>
                  <CustomNumberInput
                    value={formData.shipmentTotalWeight || ''}
                    onChange={(val) => handleInputChange('shipmentTotalWeight', val === '' ? null : parseFloat(val) || 0)}
                    placeholder={formLabel('ph_package_count')}
                    min={0}
                    step={0.1}
                    decimals={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('volume_m3')}</label>
                  <CustomNumberInput
                    value={formData.shipmentVolume || ''}
                    onChange={(val) => handleInputChange('shipmentVolume', val === '' ? null : parseFloat(val) || 0)}
                    placeholder={formLabel('ph_package_count')}
                    min={0}
                    step={0.01}
                    decimals={2}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('declared_value')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.shipmentDeclaredValue || ''}
                    onChange={(value) => handleInputChange('shipmentDeclaredValue', value)}
                    placeholder={formLabel('ph_max_budget')}
                                        suffixInText={` ${getBrandCurrencySuffix()}`}
                    min={0}
                    step={1000}
                  />
                </div>
              </div>
            </div>
          )}
          
          {/* Dynamic Items/Products Section - Show for documents with items or special document types */}
          {/* Exclude payment-receipt and payment-voucher (they don't have products) */}
          {(formData.items || ['quote-request', 'proforma-request', 'purchase-order', 'reception-note', 'return-slip', 
                                'quote', 'proforma', 'invoice', 'delivery-note', 'delivery', 'material-exit', 'shipping-slip', 'client-return-slip', 'credit-note',
                                'exit-note', 'transfer-note', 'stock-entry'].includes(selectedType?.id) ||
           (facturationTableType === 'fabrication' && ['quote', 'delivery-note', 'delivery', 'material-exit'].includes(selectedType?.id))) && 
           !isPaymentReceiptDoc(selectedType) && !isPaymentVoucherDoc(selectedType) && (
            <div className={`form-section ${selectedType.id === 'proforma-request' ? 'proforma-products-section' : ''}`}>
              <h3 className="section-title" style={{ margin: 0, borderBottom: 'none', paddingBottom: 0, marginBottom: '1rem' }}>
                <Building size={20} color={getIconColor()} />
                {/* ?? ACHATS (5 documents) */}
                {productsSectionTitle(selectedType.id)}
              </h3>

              <div className="items-container" style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
                {/* Ensure at least one item exists for product selection */}
                {(() => {
                  let items = formData.items || [];
                  // For proforma-request, fabrication, stock/logistics documents, and return-slip, ensure at least one item with quantity 1
                  if (items.length === 0 && (selectedType?.id === 'proforma-request' || 
                      selectedType?.id === 'exit-note' || 
                      selectedType?.id === 'transfer-note' || selectedType?.id === 'transfer_slip' ||
                      selectedType?.id === 'stock-entry' ||
                      selectedType?.id === 'return-slip' ||
                      (facturationTableType === 'fabrication' && ['proforma-request', 'quote', 'purchase-order', 'delivery-note', 'quote-request'].includes(selectedType?.id)))) {
                    items = [{ product: '', quantity: 1, price: 0, total: 0 }];
                  }
                  return items;
                })().map((item, index) => (
                  <div key={index} style={{ display: 'flex', alignItems: 'end', gap: '1rem', padding: '0.1rem', background: 'transparent', borderRadius: '0.75rem', position: 'relative' }}>
                    <div style={{ 
                      flex: 1, 
                      display: 'grid', 
                      gridTemplateColumns: (() => {
                        /* ?? Type 1: REQUEST - 3 columns (Description + Quantité + Unité + Delete) */
                        if (['quote-request', 'proforma-request', 'proforma_request'].includes(selectedType.id)) {
                          return '3fr 1fr 1.5fr auto';
                        }
                        /* ?? Type 2: PURCHASE ORDER & SUPPLIER RETURN ? Product + Quantité + Unité + Prix + Delete (unit for produits et services) */
                        if (['purchase-order', 'return-slip'].includes(selectedType.id)) {
                          return '3fr 1fr 1.5fr 1.5fr auto';
                        }
                        /* Type 3: FINANCIAL (ventes / crédit / stock avec prix) — Product + Quantité + Unité + Prix + Delete */
                        if (['quote', 'proforma', 'invoice', 'delivery-note', 'delivery', 'material-exit', 'credit-note', 'credit_note', 'client-return-slip', 'stock-entry', 'exit-note', 'exit_slip', 'transfer-note', 'transfer_slip'].includes(selectedType.id)) {
                          return '3fr 1fr 1.5fr 1.5fr auto';
                        }
                        /* Bon de réception — Produit + Quantité + Prix unitaire + Delete */
                        if (selectedType.id === 'reception-note') {
                          return '3fr 1fr 1.5fr auto';
                        }
                        /* ?? Type 4: LOGISTICS - 2 columns (Product + Quantité + Delete) */
                        return '3fr 1fr auto';
                      })(), 
                      columnGap: '1rem',
                      rowGap: '1rem',
                      alignItems: 'end' 
                    }}>
                      <div className="form-group">
                        {index === 0 && (
                          <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                            {/* ?? Simplified labels - avoid repetition with section title */}
                            {selectedType.id === 'quote-request' || selectedType.id === 'proforma-request' || selectedType.id === 'proforma_request' ? formLabel('need_description') :
                             /* Products & Services documents - simple label */
                             selectedType.id === 'purchase-order' || selectedType.id === 'quote' || selectedType.id === 'proforma' || 
                             selectedType.id === 'invoice' || selectedType.id === 'credit-note' || selectedType.id === 'credit_note' ? t('document_creation.form.products_and_services') :
                             /* Products-only documents - simple label */
                             'منتج'}
                          </label>
                        )}
                        
                        {/* ?? CustomSelect for Product/Service */}
                        <CustomSelect
                          value={item.name || item.designation || item.manualProductName || ''}
                          onChange={(value) => {
                            // Find selected product/service from the appropriate list
                            const currentItemType = itemTypes[index] || itemType || 'product';
                            const sourceList = currentItemType === 'service' ? availableServicesForDropdown : availableProductsForDropdown;
                            const selectedItem = sourceList.find(p => p.value === value || p.name === value);
                            
                            if (selectedItem) {
                              // Use existing product from database
                              handleProductSelect(index, selectedItem.id);
                            } else {
                              // Manual entry - new product/service
                              handleManualProductChange(index, 'name', value);
                            }
                          }}
                          options={(() => {
                            const currentItemType = itemTypes[index] || itemType || 'product';
                            return currentItemType === 'service' ? availableServicesForDropdown : availableProductsForDropdown;
                          })()}
                          placeholder={(() => {
                            const currentItemType = itemTypes[index] || itemType || 'product';
                            return currentItemType === 'service' ? t('document_creation.form.search_service') : t('document_creation.form.search_product');
                          })()}
                          searchable
                          creatable
                        />
                        
                        {/* Hidden: Old dropdown system */}
                        {false && showProductDropdown[index] && (
                              <div style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                marginTop: '0.25rem',
                                background: '#1e293b',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                borderRadius: '0.75rem',
                                maxHeight: '300px',
                                overflowY: 'auto',
                                zIndex: 1000,
                                boxShadow: '0 10px 25px rgba(0,0,0,0.3)'
                              }}>
                                {(() => {
                                  const itemType = itemTypes[index] || 'product';
                                  const searchTerm = (productSearch[index] || '').toLowerCase();
                                  const filtered = activeProducts.filter(product => {
                                    // Filter by type
                                    if (itemType === 'service' && product.type !== 'service') return false;
                                    if (itemType === 'product' && product.type === 'service') return false;
                                    
                                    // Filter by search term
                                    if (searchTerm) {
                                      const nameMatch = product.name?.toLowerCase().includes(searchTerm);
                                      const categoryMatch = product.category?.toLowerCase().includes(searchTerm);
                                      return nameMatch || categoryMatch;
                                    }
                                    return true;
                                  });
                                  
                                  if (filtered.length === 0) {
                                    return (
                                      <div style={{ padding: '1rem', color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>
                                        {searchTerm ? t('common.no_results') : t('document_creation.form.no_products_available')}
                                      </div>
                                    );
                                  }
                                  
                                  return filtered.slice(0, 50).map(product => (
                                    <div
                                      key={product.id}
                                      onClick={() => {
                                        handleProductSelect(index, product.id);
                                        setProductSearch(prev => ({ ...prev, [index]: product.name }));
                                        setShowProductDropdown(prev => ({ ...prev, [index]: false }));
                                      }}
                                      style={{
                                        padding: '0.75rem 1rem',
                                        cursor: 'pointer',
                                        borderBottom: '1px solid rgba(255,255,255,0.1)',
                                        transition: 'background 0.2s',
                                        color: '#ffffff'
                                      }}
                                      onMouseEnter={(e) => e.currentTarget.style.background = getDocCreationHoverBgRgba()}
                                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                    >
                                      <div style={{ fontWeight: '500' }}>{product.name}</div>
                                      <div style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.6)', marginTop: '0.25rem' }}>
                                        {product.category} ? {formatBrandMoney(product.price)}/{formatMeasureUnitForDisplay(product.unit)}
                                      </div>
                                    </div>
                                ));
                              })()}
                            </div>
                          )}
                      </div>

                      <div className="form-group">
                        {index === 0 && (
                          <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                            {selectedType.id === 'quote-request' ? formLabel('quantity_short') : t('document_creation.item_quantity')}
                          </label>
                        )}
                        <CustomNumberInput
                          value={
                            item.quantity === undefined ||
                            item.quantity === null ||
                            item.quantity === '' ||
                            ((selectedType.id === 'shipping-slip' || selectedType.id === 'shipping') &&
                              Number(item.quantity) === 0)
                              ? ''
                              : item.quantity
                          }
                          onChange={(val) => {
                            const emptyQtyShippingSlip =
                              selectedType.id === 'shipping-slip' || selectedType.id === 'shipping';
                            if (val === '' || val === null || (typeof val === 'string' && val.trim() === '')) {
                              handleItemChange(index, 'quantity', emptyQtyShippingSlip ? '' : 0);
                              return;
                            }
                            const n = parseFloat(val);
                            handleItemChange(index, 'quantity', Number.isNaN(n) ? (emptyQtyShippingSlip ? '' : 0) : n);
                          }}
                          placeholder={
                            selectedType.id === 'shipping-slip' || selectedType.id === 'shipping'
                              ? formLabel('ph_quantity_example')
                              : formLabel('ph_quantity')
                          }
                          min={['quote-request', 'proforma-request', 'proforma_request'].includes(selectedType.id) ? 1 : 0.1}
                          step={['quote-request', 'proforma-request', 'proforma_request'].includes(selectedType.id) ? 1 : 0.1}
                          decimals={['quote-request', 'proforma-request', 'proforma_request'].includes(selectedType.id) ? undefined : 1}
                        />
                      </div>

                      {/* ?? Unité de mesure - For REQUEST documents (in same row) */}
                      {(selectedType.id === 'quote-request' || selectedType.id === 'proforma-request' || selectedType.id === 'proforma_request') && (
                        <>
                        <div className="form-group">
                          {index === 0 && (
                            <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.unit_of_measure')}</label>
                          )}
                          <CustomSelect
                            value={measureUnitSelectValue(item.unit)}
                            onChange={(val) => handleItemChange(index, 'unit', val)}
                            options={MEASURE_UNIT_OPTIONS}
                            placeholder={t('document_creation.form.select_unit')}
                            searchable={true}
                            creatable={true}
                          />
                        </div>

                        {/* Delete Button - Right next to Unité de mesure */}
                        {(() => {
                          let items = formData.items || [];
                          return items.length > 1 && (
                            <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', marginLeft: 'calc(1px - 1rem)' }}>
                              {index === 0 && <div style={{ height: '22px', marginBottom: '0.5rem' }} />}
                              <button
                                type="button"
                                onClick={() => removeItem(index)}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  color: 'rgba(239, 68, 68, 0.8)',
                                  cursor: 'pointer',
                                  padding: '0.75rem 0.35rem',
                                  borderRadius: '0.5rem',
                                  transition: 'all 0.2s',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  height: '48px'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                                  e.currentTarget.style.color = 'rgba(239, 68, 68, 1)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = 'transparent';
                                  e.currentTarget.style.color = 'rgba(239, 68, 68, 0.8)';
                                }}
                                title={t('document_creation.form.delete')}
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          );
                        })()}
                        </>
                      )}

                      {/* ?? Unité de mesure ? achats retour fournisseur / bon de commande (produit ou service) */}
                      {['purchase-order', 'return-slip'].includes(selectedType.id) && (
                          <div className="form-group">
                            {index === 0 && (
                              <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.unit_of_measure')}</label>
                            )}
                            <CustomSelect
                              value={item.unit || ''}
                              onChange={(val) => handleItemChange(index, 'unit', val)}
                              options={MEASURE_UNIT_OPTIONS}
                              placeholder={t('document_creation.form.select_unit')}
                              searchable={true}
                              creatable={true}
                            />
                          </div>
                      )}

                      {/* ?? Unité de mesure ? devis, proforma, facture, BL, note de crédit, retour client, etc. (produit ou service) */}
                      {['quote', 'proforma', 'invoice', 'delivery-note', 'delivery', 'material-exit', 'credit-note', 'credit_note', 'client-return-slip', 'stock-entry', 'exit-note', 'exit_slip', 'transfer-note', 'transfer_slip'].includes(selectedType.id) && (
                        <div className="form-group">
                          {index === 0 && (
                            <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.unit_of_measure')}</label>
                          )}
                          <CustomSelect
                            value={measureUnitSelectValue(item.unit)}
                            onChange={(val) => handleItemChange(index, 'unit', val)}
                            options={MEASURE_UNIT_OPTIONS}
                            placeholder={t('document_creation.form.select_unit')}
                            searchable={true}
                            creatable={true}
                          />
                        </div>
                      )}

                      {/* ?? Prix column - ONLY for Type 2: FINANCIAL documents */}
                      {['purchase-order', 'quote', 'proforma', 'invoice', 'delivery-note', 'delivery', 'material-exit', 'credit-note', 'credit_note', 'return-slip', 'client-return-slip', 'stock-entry', 'exit-note', 'exit_slip', 'transfer-note', 'transfer_slip', 'reception-note'].includes(selectedType.id) && (
                        <>
                        <div className="form-group">
                          {index === 0 && (
                            <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                              {facturationTableType === 'fabrication' ? t('document_creation.form.unit_price') : t('document_creation.form.unit_price')}
                            </label>
                          )}
                          {facturationTableType === 'fabrication' ? (
                            <CustomNumberInput
                              value={item.longueur || ''}
                              onChange={(val) => handleItemChange(index, 'longueur', val)}
                              placeholder="0.00"
                              min={0}
                              step={0.01}
                              decimals={2}
                            />
                          ) : (
                            <div className="price-input-container">
                              <CustomNumberInput
                                value={item.price === 0 || item.price === '0' ? '' : item.price}
                                onChange={(val) => handleItemChange(index, 'price', parseFloat(val))}
                                placeholder={t('document_creation.form.enter_price')}
                                min={0}
                                step={0.01}
                                decimals={2}
                              />
                              {(() => {
                                const basePrice = parseFloat(item.price || item.unitPrice) || 0;
                                // Determine preferential rate based on currently selected client on the fly
                                let preferentialRate = 0;
                                const selectedClient = clients.find(c => c.id.toString() === String(formData.client));
                                if (selectedClient && selectedClient.type) {
                                  preferentialRate = getClientTypePreferentialRate(selectedClient.type, settings);
                                }
                                const discountAmount = basePrice * (preferentialRate / 100);
                                if (discountAmount <= 0) return null;
                                 return (
                                   <div className="discounted-price">
                                     <span>{formLabel('discounted_price')}: {discountAmount.toFixed(2)}</span>
                                   </div>
                                 );
                              })()} 
                            </div>
                          )}
                        </div>

                        {/* Delete Button - Right next to Prix unitaire */}
                        {(() => {
                          let items = formData.items || [];
                          return items.length > 1 && (
                            <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', marginLeft: 'calc(1px - 1rem)' }}>
                              {index === 0 && <div style={{ height: '22px', marginBottom: '0.5rem' }} />}
                              <button
                                type="button"
                                onClick={() => removeItem(index)}
                                style={{
                                  background: 'transparent',
                                  border: 'none',
                                  color: 'rgba(239, 68, 68, 0.8)',
                                  cursor: 'pointer',
                                  padding: '0.75rem 0.35rem',
                                  borderRadius: '0.5rem',
                                  transition: 'all 0.2s',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  height: '48px'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                                  e.currentTarget.style.color = 'rgba(239, 68, 68, 1)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = 'transparent';
                                  e.currentTarget.style.color = 'rgba(239, 68, 68, 0.8)';
                                }}
                                title={t('document_creation.form.delete')}
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          );
                        })()}
                        </>
                      )}

                      {/* Delete Button - For LOGISTICS documents (not REQUEST or FINANCIAL) */}
                      {!['quote-request', 'proforma-request', 'proforma_request', 'purchase-order', 'quote', 'proforma', 'invoice', 'delivery-note', 'delivery', 'material-exit', 'credit-note', 'credit_note', 'return-slip', 'client-return-slip', 'stock-entry', 'exit-note', 'exit_slip', 'transfer-note', 'transfer_slip', 'reception-note'].includes(selectedType.id) && (() => {
                        let items = formData.items || [];
                        return items.length > 1 && (
                          <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', marginLeft: 'calc(1px - 1rem)' }}>
                            {index === 0 && <div style={{ height: '22px', marginBottom: '0.5rem' }} />}
                            <button
                              type="button"
                              onClick={() => removeItem(index)}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                color: 'rgba(239, 68, 68, 0.8)',
                                cursor: 'pointer',
                                padding: '0.75rem 0.35rem',
                                borderRadius: '0.5rem',
                                transition: 'all 0.2s',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '48px'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                                e.currentTarget.style.color = 'rgba(239, 68, 68, 1)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'transparent';
                                e.currentTarget.style.color = 'rgba(239, 68, 68, 0.8)';
                              }}
                              title={t('document_creation.form.delete')}
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        );
                      })()}

                      {selectedType.id === 'attachment' && (
                        <div className="form-group full-width">
                          <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('technical_comments')}</label>
                          <textarea
                            value={item.technicalNotes || ''}
                            onChange={(e) => handleItemChange(index, 'technicalNotes', e.target.value)}
                            placeholder={formLabel('ph_technical_specs')}
                            rows="2"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Product/Service Toggle - Conditional: Single Button OR Two Buttons */}
              {/* ?? Products-only documents: Show single large "Ajouter un produit" button */}
              {['return-slip', 'delivery-note', 'delivery', 'material-exit', 'client-return-slip', 
                'reception-note', 'exit-note', 'exit_slip', 'transfer-note', 'transfer_slip', 
                'stock-entry', 'shipping-slip', 'shipping'].includes(selectedType.id) ? (
                <button
                  type="button"
                  onClick={() => addItem()}
                  style={{
                    width: '100%',
                    padding: '14px 24px',
                    borderRadius: '12px',
                    background: getDocCreationAccentGradient(),
                    color: '#ffffff',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    marginTop: '0.75rem',
                    boxShadow: getDocCreationAccentShadow(0.3),
                    transform: 'translateY(0)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = getDocCreationAccentShadow(0.4);
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = getDocCreationAccentShadow(0.3);
                  }}
                >
                  <Plus size={20} />
                  {t('document_creation.form.add_product_btn')}
                </button>
              ) : (
                /* ?? Products AND Services documents: Show two-button toggle */
                <div style={{ display: 'flex', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '9999px', padding: '4px', border: `1px solid ${getDocCreationAccentBorderRgba()}`, marginTop: '0.75rem' }}>
                  <button
                    type="button"
                    onClick={() => {
                      if (itemType === 'product') {
                        // Second click on same type = add item
                        addItem();
                      } else {
                        // First click = just switch mode
                        setItemType('product');
                      }
                    }}
                    style={{
                      flex: 1,
                      padding: '12px 24px',
                      borderRadius: '9999px',
                      background: itemType === 'product' ? getDocCreationAccentSolid() : 'transparent',
                      color: '#ffffff',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <Plus size={18} />
                    {t('document_creation.form.add_product')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (itemType === 'service') {
                        // Second click on same type = add item
                        addItem();
                      } else {
                        // First click = just switch mode
                        setItemType('service');
                      }
                    }}
                    style={{
                      flex: 1,
                      padding: '12px 24px',
                      borderRadius: '9999px',
                      background: itemType === 'service' ? getDocCreationAccentSolid() : 'transparent',
                      color: '#ffffff',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <Plus size={18} />
                    {t('document_creation.form.add_service')}
                  </button>
                </div>
              )}
              
              {/* Auto-calculated Totals Section */}
              {formData.items && formData.items.length > 0 && !isCreditNoteDocType(selectedType.id) && !['quote-request', 'proforma-request', 'purchase-order', 'reception-note', 'return-slip',
                                                                  'quote', 'proforma', 'invoice', 'delivery-note', 'delivery', 'material-exit', 'shipping-slip', 'client-return-slip',
                                                                  'exit-note', 'transfer-note', 'stock-entry'].includes(selectedType.id) && (
                <div className="bg-white/5 rounded-xl p-4 space-y-2 border border-white/20 mt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-white/70 text-sm">{t('document_creation.form.subtotal')}:</span>
                    <span className="text-white font-semibold">{formatBrandMoney(formData.items.reduce((sum, item) => sum + ((parseFloat(item.price) || 0) * (parseFloat(item.quantity) || 1)), 0))}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-white/70 text-sm">{formLabel('vat_label')} ({formData.tvaRate || 19}%):</span>
                    <span className="text-white font-semibold">{formatBrandMoney(formData.items.reduce((sum, item) => sum + ((parseFloat(item.price) || 0) * (parseFloat(item.quantity) || 1)), 0) * ((formData.tvaRate || 19) / 100))}</span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-white/20">
                    <span className="text-white font-bold">{formLabel('total_label')}</span>
                    <span className="text-white font-bold text-xl">{formatBrandMoney(formData.items.reduce((sum, item) => sum + ((parseFloat(item.price) || 0) * (parseFloat(item.quantity) || 1)), 0) * (1 + (formData.tvaRate || 19) / 100))}</span>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Packages Section for shipping-slip */}
          {formData.packages && (
            <div className="form-section">
              <h3 className="section-title" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                <Building size={20} color={getIconColor()} />
                {formLabel('packages_detail_title')}
              </h3>

              <div className="items-container">
                {formData.packages?.map((pkg, index) => (
                  <div key={index} className="item-row">
                    <div className="item-fields">
                      <div className="form-group">
                        <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('type_label')}</label>
                        <CustomSelect
                          value={pkg.type}
                          onChange={(e) => handleItemChange(index, 'type', e.target.value)}
                        >
                          <option value="">{formLabel('select_placeholder')}</option>
                          <option value="carton">{formLabel('package_types.carton')}</option>
                          <option value="palette">{formLabel('package_types.palette')}</option>
                          <option value="sac">{formLabel('package_types.bag')}</option>
                          <option value="caisse">{formLabel('package_types.crate')}</option>
                          <option value="enveloppe">{formLabel('package_types.envelope')}</option>
                        </CustomSelect>
                      </div>

                      <div className="form-group">
                        <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.item_quantity')}</label>
                        <CustomNumberInput
                          value={pkg.quantity}
                          onChange={(val) => handleItemChange(index, 'quantity', parseFloat(val))}
                          min={1}
                          step={1}
                        />
                      </div>

                      <div className="form-group">
                        <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('weight_kg')}</label>
                        <CustomNumberInput
                          value={pkg.weight}
                          onChange={(val) => handleItemChange(index, 'weight', parseFloat(val))}
                          min={0}
                          step={0.1}
                          decimals={1}
                        />
                      </div>

                      <div className="form-group">
                        <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('dimensions')}</label>
                        <input
                          type="text"
                          value={pkg.dimensions}
                          onChange={(e) => handleItemChange(index, 'dimensions', e.target.value)}
                          placeholder={formLabel('ph_dimensions')}
                        />
                      </div>

                      <div className="form-group">
                        <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('contents')}</label>
                        <input
                          type="text"
                          value={pkg.contents}
                          onChange={(e) => handleItemChange(index, 'contents', e.target.value)}
                          placeholder={formLabel('ph_package_contents')}
                        />
                      </div>

                      <div className="form-group">
                        <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('declared_value')} (${getBrandCurrencySuffix()})</label>
                        <CustomNumberInput
                          value={pkg.value}
                          onChange={(val) => handleItemChange(index, 'value', parseFloat(val))}
                          min={0}
                          step={0.01}
                          decimals={2}
                        />
                      </div>
                    </div>

                    <button
                      className="btn-remove"
                      onClick={() => removeItem(index)}
                      disabled={formData.packages && formData.packages.length === 1}
                      title={formLabel('delete_line')}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}

                <button className="btn btn-secondary add-item" onClick={addItem}>
                  <PlusCircle size={16} />
                  {formLabel('add_package')}
                </button>
              </div>
            </div>
          )}

          {/* PAIEMENTS & RECOUVREMENT Documents */}
          {selectedType.id === 'payment-demand' && (
            <div className="form-section">
              <h3 className="section-title" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                <FileText size={20} color={getIconColor()} />
                {formLabel('payment_demand_title')}
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('creditor_name')}</label>
                  <input
                    type="text"
                    value={formData.creditor?.name || ''}
                    onChange={(e) => handleInputChange('creditor', {...formData.creditor, name: e.target.value})}
                    placeholder={formLabel('ph_creditor_company')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('legal_form')}</label>
                  <CustomSelect
                    value={formData.creditor?.legalForm || ''}
                    onChange={(e) => handleInputChange('creditor', {...formData.creditor, legalForm: e.target.value})}
                  >
                    <option value="SARL">SARL</option>
                    <option value="SPA">SPA</option>
                    <option value="EURL">EURL</option>
                    <option value="SNC">SNC</option>
                    <option value="entreprise_individuelle">{formLabel('legal_forms.individual_enterprise')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('creditor_nif')}</label>
                  <input
                    type="text"
                    value={formData.creditor?.nif || ''}
                    onChange={(e) => handleInputChange('creditor', {...formData.creditor, nif: e.target.value})}
                    placeholder={formLabel('ph_fiscal_id')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('debtor_client')}</label>
                  <input
                    type="text"
                    value={formData.debtor?.name || ''}
                    onChange={(e) => handleInputChange('debtor', {...formData.debtor, name: e.target.value})}
                    placeholder={formLabel('ph_debtor_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('debtor_address')}</label>
                  <input
                    type="text"
                    value={formData.debtor?.address || ''}
                    onChange={(e) => handleInputChange('debtor', {...formData.debtor, address: e.target.value})}
                    placeholder={formLabel('ph_debtor_full_address')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('debtor_contact')}</label>
                  <input
                    type="text"
                    value={formData.debtor?.contact || ''}
                    onChange={(e) => handleInputChange('debtor', {...formData.debtor, contact: e.target.value})}
                    placeholder={formLabel('ph_phone_email')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('unpaid_invoices')}</label>
                  <input
                    type="text"
                    value={formData.debt?.invoiceNumbers || ''}
                    onChange={(e) => handleInputChange('debt', {...formData.debt, invoiceNumbers: e.target.value})}
                    placeholder={formLabel('ph_invoice_numbers_comma')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('invoice_billing_date')}</label>
                  <CustomDatePicker
                      value={formData.debt?.invoiceDate || ''}
                      onChange={(val) => handleInputChange('debt', {...formData.debt, invoiceDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('due_date')}</label>
                  <CustomDatePicker
                      value={formData.debt?.dueDate || ''}
                      onChange={(val) => handleInputChange('debt', {...formData.debt, dueDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('principal_amount')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.debt?.principalAmount || 0}
                    onChange={(val) => handleInputChange('debt', {...formData.debt, principalAmount: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('late_penalties')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.debt?.penalties || 0}
                    onChange={(val) => handleInputChange('debt', {...formData.debt, penalties: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('late_interest_rate')}</label>
                  <CustomNumberInput
                    value={formData.debt?.interestRate || 0}
                    onChange={(val) => handleInputChange('debt', {...formData.debt, interestRate: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={0.1}
                    decimals={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('total_amount_due')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.debt?.totalAmount || 0}
                    onChange={(val) => handleInputChange('debt', {...formData.debt, totalAmount: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('reminder_count')}</label>
                  <CustomNumberInput
                    value={formData.demand.reminderCount || 1}
                    onChange={(val) => handleInputChange('demand', {...formData.demand, reminderCount: parseInt(val) || 1})}
                    placeholder="1"
                    min={1}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('demand_type')}</label>
                  <CustomSelect
                    value={formData.demand.type || 'amiable'}
                    onChange={(e) => handleInputChange('demand', {...formData.demand, type: e.target.value})}
                  >
                    <option value="amiable">{formLabel('demand_types.amiable')}</option>
                    <option value="formelle">{formLabel('demand_types.formal')}</option>
                    <option value="dernière">{formLabel('demand_types.last_reminder')}</option>
                    <option value="judiciaire">{formLabel('demand_types.before_legal')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('payment_deadline_granted')}</label>
                  <CustomSelect
                    value={formData.demand.paymentDeadline || '15_jours'}
                    onChange={(e) => handleInputChange('demand', {...formData.demand, paymentDeadline: e.target.value})}
                  >
                    <option value="8_jours">{formLabel('payment_deadlines.8_days')}</option>
                    <option value="15_jours">{formLabel('payment_deadlines.15_days')}</option>
                    <option value="30_jours">{formLabel('payment_deadlines.30_days')}</option>
                    <option value="immédiat">{formLabel('payment_deadlines.immediate')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('delivery_method_mode')}</label>
                  <CustomSelect
                    value={formData.demand.deliveryMethod || ''}
                    onChange={(e) => handleInputChange('demand', {...formData.demand, deliveryMethod: e.target.value})}
                  >
                    <option value="recommandé">{formLabel('delivery_methods.registered')}</option>
                    <option value="huissier">{formLabel('delivery_methods.bailiff')}</option>
                    <option value="main_propre">{formLabel('delivery_methods.hand_delivery')}</option>
                    <option value="email">{formLabel('delivery_methods.email_ack')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('recovery_fees')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.demand.recoveryFees || 0}
                    onChange={(val) => handleInputChange('demand', {...formData.demand, recoveryFees: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('legal_reference')}</label>
                  <CustomSelect
                    value={formData.demand.legalReference || 'code_civil'}
                    onChange={(e) => handleInputChange('demand', {...formData.demand, legalReference: e.target.value})}
                  >
                    <option value="code_civil">{formLabel('legal_references.code_civil')}</option>
                    <option value="code_commerce">{formLabel('legal_references.code_commerce')}</option>
                    <option value="conditions_générales">{formLabel('legal_references.general_terms')}</option>
                    <option value="contrat">{formLabel('legal_references.contract_provisions')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.demand.formalNotice || false}
                      onChange={(e) => handleInputChange('demand', {...formData.demand, formalNotice: e.target.checked})}
                    />
                    {formLabel('formal_payment_demand')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.demand.legalAction || false}
                      onChange={(e) => handleInputChange('demand', {...formData.demand, legalAction: e.target.checked})}
                    />
                    {formLabel('legal_action_threat')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.demand.paymentPlan || false}
                      onChange={(e) => handleInputChange('demand', {...formData.demand, paymentPlan: e.target.checked})}
                    />
                    {formLabel('payment_plan_option')}
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('delay_reason')}</label>
                <textarea
                  value={formData.demand.delayReason || ''}
                  onChange={(e) => handleInputChange('demand', {...formData.demand, delayReason: e.target.value})}
                  placeholder={formLabel('ph_delay_reason')}
                  rows="2"
                />
              </div>
              
              <div className="form-group">
                <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('settlement_conditions')}</label>
                <textarea
                  value={formData.demand.settlementConditions || ''}
                  onChange={(e) => handleInputChange('demand', {...formData.demand, settlementConditions: e.target.value})}
                  placeholder={formLabel('ph_settlement_conditions')}
                  rows="2"
                />
              </div>
            </div>
          )}
          {/* Reçu de paiement - Section combinée: Détails du document */}
          {isPaymentReceiptDoc(selectedType) && (
            <div className="bg-white/5 rounded-xl p-5 border border-white/20 overflow-hidden">
              <div className="flex items-center gap-2 mb-4">
                <CreditCard className="h-5 w-5 text-green-400" />
                <h3 className={`text-lg font-semibold ${isDashboardLightMode ? 'text-black' : 'text-white'}`}>{t('document_creation.form.document_details')}</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Identifiants du document */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(16, 185, 129, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.document_identifiers')}</h4>
                  </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.document_number')}</label>
                    <input
                      type="text"
                      value={formData.number || ''}
                      readOnly
                      className="readonly-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.date_label')}</label>
                    <CustomDatePicker
                      value={formData.date}
                      onChange={(dateString) => handleInputChange('date', dateString)}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('payment_date')}</label>
                    <CustomDatePicker
                      value={formData.paymentDate}
                      onChange={(dateString) => handleInputChange('paymentDate', dateString)}
                    />
                  </div>
                </div>
                
                {/* Informations de suivi */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(16, 185, 129, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.tracking_info')}</h4>
              </div>

                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.prepared_by')}</label>
                  <CustomSelect
                    value={formData.issuedBy || ''}
                    onChange={(value) => {
                    handleInputChange('issuedBy', value);
                    // Sync with "Établie par" signature if enabled
                    if (formData.showEtabliePar) {
                      handleInputChange('etablieParName', value);
                    }
                  }}
                    options={availableFacturiersForDropdown}
                    placeholder={t('document_creation.form.search_or_enter_biller')}
                    searchable
                  />
                </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.initial_status')}</label>
                    <CustomSelect
                      value={documentStatusValue()}
                      onChange={handleFormStatusChange}
                      creatable={false}
                      searchable={false}
                      options={getStatusOptions(selectedType.id)}
                      placeholder={t('document_creation.form.select_placeholder')}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.priority')}</label>
                    <CustomSelect
                      value={formData.priority ?? ''}
                      onChange={(value) => handleInputChange('priority', value)}
                      autoSelect={false}
                      createLabel={t('document_creation.form.create_priority')}
                      options={[
                        { value: 'urgent', label: t('document_creation.form.priority_levels.urgent') },
                        { value: 'high', label: t('document_creation.form.priority_levels.high') },
                        { value: 'normale', label: t('document_creation.form.priority_levels.normal') },
                        { value: 'low', label: t('document_creation.form.priority_levels.low') }
                      ]}
                      placeholder={t('document_creation.form.select_placeholder')}
                    />
                  </div>
                </div>
                
                {/* Détails du paiement */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(16, 185, 129, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{formLabel('payment_details_section')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.payment_method')}</label>
                    <CustomSelect
                      value={formData.paymentMethod || ''}
                      onChange={(value) => handleInputChange('paymentMethod', value)}
                      options={[
                        { value: 'virement', label: formLabel('payment_methods.bank_transfer') },
                        { value: 'chèque', label: formLabel('payment_methods.cheque') },
                        { value: 'espèces', label: formLabel('payment_methods.cash') },
                        { value: 'carte', label: formLabel('payment_methods.card') },
                        { value: 'traite', label: formLabel('payment_methods.promissory') },
                        { value: 'compensation', label: formLabel('compensation') }
                      ]}
                      placeholder={t('document_creation.form.select_or_enter')}
                      searchable
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('payment_reference')}</label>
                    <CustomSelect
                      value={formData.paymentReference || ''}
                      onChange={(value) => handleInputChange('paymentReference', value)}
                      options={[]}
                      placeholder={formLabel('ph_payment_reference')}
                      searchable
                      creatable
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('settlement_type')}</label>
                    <CustomSelect
                      value={formData.paymentSettlementType || ''}
                      onChange={(value) => handleInputChange('paymentSettlementType', value)}
                      options={[
                        { value: 'total', label: formLabel('settlement_types.total') },
                        { value: 'partiel', label: formLabel('settlement_types.partial') },
                        { value: 'acompte', label: formLabel('settlement_types.advance') },
                        { value: 'solde', label: formLabel('settlement_types.final_balance') }
                      ]}
                      placeholder={t('document_creation.form.select_or_enter')}
                      searchable
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('issuing_bank')}</label>
                    <CustomSelect
                      value={formData.paymentBank || ''}
                      onChange={(value) => handleInputChange('paymentBank', value)}
                      options={[
                        { value: 'SNB', label: formLabel('saudi_banks.snb') },
                        { value: 'RJHI', label: formLabel('saudi_banks.rjhi') },
                        { value: 'RIBL', label: formLabel('saudi_banks.ribl') },
                        { value: 'INMA', label: formLabel('saudi_banks.inma') },
                        { value: 'SAIB', label: formLabel('saudi_banks.saib') },
                        { value: 'BSFR', label: formLabel('saudi_banks.bsfr') },
                        { value: 'BLAD', label: formLabel('saudi_banks.blad') },
                        { value: 'SABB', label: formLabel('saudi_banks.sabb') }
                      ]}
                      placeholder={formLabel('search_bank')}
                      searchable
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Payment Lines Section for payment-receipt (typed: facture / avoir sur vente) */}
          {isPaymentReceiptDoc(selectedType) && (
            <div className="form-section">
              <h3 className="section-title" style={{ margin: 0, borderBottom: 'none', paddingBottom: 0, marginBottom: '1rem' }}>
                <DollarSign size={20} color={getIconColor()} />
                {formLabel('payment_lines_title')}
              </h3>

              <div className="items-container" style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
                {(() => {
                  let paymentLines = formData.paymentLines || [];
                  if (paymentLines.length === 0) {
                    paymentLines = [emptyPaymentReceiptLine('invoice')];
                  }
                  return paymentLines;
                })().map((line, index, allLines) => {
                  const lineType = line.lineType || 'invoice';
                  const isCreditNoteLine = lineType === 'credit_note';
                  const usedRefs = getUsedPaymentReceiptLineRefs(allLines, index, lineType);
                  const refOptions = (isCreditNoteLine ? clientCreditNotes : clientInvoicesForReceipt)
                    .map((doc) => ({
                      value: doc.reference,
                      label: doc.label,
                    }))
                    .filter((opt) => !usedRefs.has(String(opt.value || '').trim()));
                  const showPayLineDelete = (formData.paymentLines?.length > 1);
                  return (
                  <div key={index} style={{ padding: '0.1rem', background: 'transparent', borderRadius: '0.75rem', position: 'relative' }}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: showPayLineDelete ? 'minmax(0, 3fr) minmax(0, 1fr) auto' : 'minmax(0, 3fr) minmax(0, 1fr)',
                      columnGap: '1rem',
                      alignItems: 'end'
                    }}>
                      <div className="form-group">
                        <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                          {isCreditNoteLine ? formLabel('ref_credit_note_sale') : formLabel('ref_invoice')}
                        </label>
                        <CustomSelect
                          value={line.reference || ''}
                          onChange={(value) => handlePaymentReceiptLineRefChange(index, value)}
                          options={refOptions}
                          placeholder={t('document_creation.form.search_generic')}
                          searchable
                        />
                      </div>

                      <div className="form-group">
                        {index === 0 && (
                          <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('amount')}</label>
                        )}
                        <CustomNumberInput
                          value={line.amount || ''}
                          onChange={(val) => handlePaymentReceiptLineAmountChange(index, val)}
                          placeholder={formLabel('ph_amount_example')}
                          min={0}
                          step={100}
                        />
                      </div>

                      {showPayLineDelete && (
                        <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', marginLeft: 'calc(1px - 1rem)' }}>
                          {index === 0 && <div style={{ height: '22px', marginBottom: '0.5rem' }} />}
                          <button
                            type="button"
                            className="payment-line-delete-btn"
                            onClick={() => removePaymentReceiptLine(index)}
                            title={t('document_creation.form.delete')}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>

              <div style={{ display: 'flex', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '9999px', padding: '4px', border: `1px solid ${getDocCreationAccentBorderRgba()}`, marginTop: '0.75rem' }}>
                <button
                  type="button"
                  onClick={() => handlePaymentReceiptLineAddTypeClick('invoice')}
                  style={{
                    flex: 1,
                    padding: '12px 24px',
                    borderRadius: '9999px',
                    background: paymentReceiptLineAddType === 'invoice' ? getDocCreationAccentSolid() : 'transparent',
                    color: '#ffffff',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Plus size={18} />
                  {formLabel('invoice_doc')}
                </button>
                <button
                  type="button"
                  onClick={() => handlePaymentReceiptLineAddTypeClick('credit_note')}
                  style={{
                    flex: 1,
                    padding: '12px 24px',
                    borderRadius: '9999px',
                    background: paymentReceiptLineAddType === 'credit_note' ? getDocCreationAccentSolid() : 'transparent',
                    color: '#ffffff',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Plus size={18} />
                  {formLabel('credit_note_sale')}
                </button>
              </div>
            </div>
          )}
          {selectedType.id === 'payment-schedule' && (
            <div className="form-section">
              <h3 className="section-title" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                <Calendar size={20} color={getIconColor()} />
                {formLabel('payment_schedule')}
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('creditor_name')}</label>
                  <input
                    type="text"
                    value={formData.creditor?.name || ''}
                    onChange={(e) => handleInputChange('creditor', {...formData.creditor, name: e.target.value})}
                    placeholder={formLabel('ph_creditor_company')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('creditor_nif')}</label>
                  <input
                    type="text"
                    value={formData.creditor?.nif || ''}
                    onChange={(e) => handleInputChange('creditor', {...formData.creditor, nif: e.target.value})}
                    placeholder={formLabel('ph_fiscal_id')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('debtor_client')}</label>
                  <input
                    type="text"
                    value={formData.debtor?.name || ''}
                    onChange={(e) => handleInputChange('debtor', {...formData.debtor, name: e.target.value})}
                    placeholder={formLabel('ph_debtor_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('debtor_contact')}</label>
                  <input
                    type="text"
                    value={formData.debtor?.contact || ''}
                    onChange={(e) => handleInputChange('debtor', {...formData.debtor, contact: e.target.value})}
                    placeholder={formLabel('ph_debtor_phone_email')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('total_amount_due')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.schedule?.totalDebt || 0}
                    onChange={(val) => handleInputChange('schedule', {...formData.schedule, totalDebt: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('concerned_invoices')}</label>
                  <input
                    type="text"
                    value={formData.schedule?.invoiceNumbers || ''}
                    onChange={(e) => handleInputChange('schedule', {...formData.schedule, invoiceNumbers: e.target.value})}
                    placeholder={formLabel('ph_invoice_numbers')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('initial_due_date')}</label>
                  <CustomDatePicker
                      value={formData.schedule?.originalDueDate || ''}
                      onChange={(val) => handleInputChange('schedule', {...formData.schedule, originalDueDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('schedule_signature_date')}</label>
                  <CustomDatePicker
                      value={formData.schedule?.signatureDate || ''}
                      onChange={(val) => handleInputChange('schedule', {...formData.schedule, signatureDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('installment_count')}</label>
                  <CustomNumberInput
                    value={formData.schedule?.installmentCount || 3}
                    onChange={(val) => handleInputChange('schedule', {...formData.schedule, installmentCount: parseInt(val) || 3})}
                    placeholder="3"
                    min={1}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('payment_frequency')}</label>
                  <CustomSelect
                    value={formData.schedule?.frequency || ''}
                    onChange={(e) => handleInputChange('schedule', {...formData.schedule, frequency: e.target.value})}
                  >
                    <option value="hebdomadaire">{formLabel('payment_frequencies.weekly')}</option>
                    <option value="bi_mensuel">{formLabel('payment_frequencies.bi_monthly')}</option>
                    <option value="mensuel">{formLabel('payment_frequencies.monthly')}</option>
                    <option value="bimestriel">{formLabel('payment_frequencies.bimonthly')}</option>
                    <option value="trimestriel">{formLabel('payment_frequencies.quarterly')}</option>
                    <option value="personnalisé">{formLabel('payment_frequencies.custom')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('first_payment_date')}</label>
                  <CustomDatePicker
                      value={formData.schedule?.firstPaymentDate || ''}
                      onChange={(val) => handleInputChange('schedule', {...formData.schedule, firstPaymentDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('installment_amount')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.schedule?.installmentAmount || 0}
                    onChange={(val) => handleInputChange('schedule', {...formData.schedule, installmentAmount: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('initial_down_payment')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.schedule?.initialPayment || 0}
                    onChange={(val) => handleInputChange('schedule', {...formData.schedule, initialPayment: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('accepted_payment_method')}</label>
                  <CustomSelect
                    value={formData.schedule?.paymentMethod || ''}
                    onChange={(e) => handleInputChange('schedule', {...formData.schedule, paymentMethod: e.target.value})}
                  >
                    <option value="">{formLabel('select_placeholder')}</option>
                    <option value="virement">{formLabel('payment_methods.bank_transfer')}</option>
                    <option value="chèque">{formLabel('payment_methods.cheque')}</option>
                    <option value="prélèvement">{formLabel('payment_methods.trade_bill')}</option>
                    <option value="espèces">{formLabel('payment_methods.cash')}</option>
                    <option value="mixte">{formLabel('other')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('penalty_on_delay')}</label>
                  <CustomNumberInput
                    value={formData.schedule?.penaltyRate || 0}
                    onChange={(val) => handleInputChange('schedule', {...formData.schedule, penaltyRate: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={0.1}
                    decimals={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('grace_period_days')}</label>
                  <CustomNumberInput
                    value={formData.schedule?.gracePeriod || 5}
                    onChange={(val) => handleInputChange('schedule', {...formData.schedule, gracePeriod: parseInt(val) || 5})}
                    placeholder="5"
                    min={0}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('schedule_status')}</label>
                  <CustomSelect
                    value={formData.schedule?.status || 'en_cours'}
                    onChange={(e) => handleInputChange('schedule', {...formData.schedule, status: e.target.value})}
                  >
                    <option value="proposé">{formLabel('schedule_statuses.proposed')}</option>
                    <option value="accepté">{formLabel('schedule_statuses.accepted')}</option>
                    <option value="en_cours">{formLabel('schedule_statuses.in_progress')}</option>
                    <option value="respecté">{formLabel('schedule_statuses.complied')}</option>
                    <option value="défaillant">{formLabel('schedule_statuses.defaulting')}</option>
                    <option value="soldé">{formLabel('schedule_statuses.settled')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('requested_guarantees')}</label>
                  <CustomSelect
                    value={formData.schedule?.guarantees || 'aucune'}
                    onChange={(e) => handleInputChange('schedule', {...formData.schedule, guarantees: e.target.value})}
                  >
                    <option value="aucune">{t('document_creation.form.option_none')}</option>
                    <option value="aval">{formLabel('guarantee_types.endorsement')}</option>
                    <option value="caution">{formLabel('guarantee_types.personal_surety')}</option>
                    <option value="hypothèque">{formLabel('guarantee_types.mortgage')}</option>
                    <option value="nantissement">{formLabel('guarantee_types.pledge')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.schedule?.automaticDefault || false}
                      onChange={(e) => handleInputChange('schedule', {...formData.schedule, automaticDefault: e.target.checked})}
                    />
                    {formLabel('automatic_acceleration')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.schedule?.interestSuspension || false}
                      onChange={(e) => handleInputChange('schedule', {...formData.schedule, interestSuspension: e.target.checked})}
                    />
                    {formLabel('interest_suspension')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.schedule?.notarizedAgreement || false}
                      onChange={(e) => handleInputChange('schedule', {...formData.schedule, notarizedAgreement: e.target.checked})}
                    />
                    {formLabel('notarized_agreement')}
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('special_conditions')}</label>
                <textarea
                  value={formData.schedule?.specialConditions || ''}
                  onChange={(e) => handleInputChange('schedule', {...formData.schedule, specialConditions: e.target.value})}
                  placeholder={formLabel('ph_special_conditions_long')}
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('default_consequences')}</label>
                <textarea
                  value={formData.schedule?.defaultConsequences || ''}
                  onChange={(e) => handleInputChange('schedule', {...formData.schedule, defaultConsequences: e.target.value})}
                  placeholder={formLabel('ph_default_consequences')}
                  rows="2"
                />
              </div>
            </div>
          )}

          {/* DOCUMENTS CONTRACTUELS Documents */}
          {selectedType.id === 'commercial-contract' && (
            <div className="form-section">
              <h3 className="section-title" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                <FileCheck size={20} color={getIconColor()} />
                {formLabel('commercial_contract_title')}
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('contracting_party1_name')}</label>
                  <input
                    type="text"
                    value={formData.party1?.name || ''}
                    onChange={(e) => handleInputChange('party1', {...formData.party1, name: e.target.value})}
                    placeholder={formLabel('ph_contracting_company')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('party1_legal_form')}</label>
                  <CustomSelect
                    value={formData.party1?.legalForm || ''}
                    onChange={(e) => handleInputChange('party1', {...formData.party1, legalForm: e.target.value})}
                  >
                    <option value="SARL">SARL</option>
                    <option value="SPA">SPA</option>
                    <option value="EURL">EURL</option>
                    <option value="SNC">SNC</option>
                    <option value="entreprise_individuelle">{formLabel('legal_forms.individual_enterprise')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('party1_nif')}</label>
                  <input
                    type="text"
                    value={formData.party1?.nif || ''}
                    onChange={(e) => handleInputChange('party1', {...formData.party1, nif: e.target.value})}
                    placeholder={formLabel('ph_fiscal_id')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('party1_representative')}</label>
                  <input
                    type="text"
                    value={formData.party1?.representative || ''}
                    onChange={(e) => handleInputChange('party1', {...formData.party1, representative: e.target.value})}
                    placeholder={formLabel('ph_representative_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('party1_representative_title')}</label>
                  <input
                    type="text"
                    value={formData.party1?.title || ''}
                    onChange={(e) => handleInputChange('party1', {...formData.party1, title: e.target.value})}
                    placeholder={formLabel('ph_representative_title')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('party1_address')}</label>
                  <input
                    type="text"
                    value={formData.party1?.address || ''}
                    onChange={(e) => handleInputChange('party1', {...formData.party1, address: e.target.value})}
                    placeholder={t('document_creation.form.full_address')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('contracting_party2_client')}</label>
                  <input
                    type="text"
                    value={formData.party2?.name || ''}
                    onChange={(e) => handleInputChange('party2', {...formData.party2, name: e.target.value})}
                    placeholder={formLabel('ph_client_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('party2_legal_form')}</label>
                  <CustomSelect
                    value={formData.party2?.legalForm || ''}
                    onChange={(e) => handleInputChange('party2', {...formData.party2, legalForm: e.target.value})}
                  >
                    <option value="SARL">SARL</option>
                    <option value="SPA">SPA</option>
                    <option value="EURL">EURL</option>
                    <option value="SNC">SNC</option>
                    <option value="entreprise_individuelle">{formLabel('legal_forms.individual_enterprise')}</option>
                    <option value="particulier">{formLabel('legal_forms.individual')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('party2_nif')}</label>
                  <input
                    type="text"
                    value={formData.party2?.nif || ''}
                    onChange={(e) => handleInputChange('party2', {...formData.party2, nif: e.target.value})}
                    placeholder={formLabel('ph_fiscal_id')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('party2_representative')}</label>
                  <input
                    type="text"
                    value={formData.party2?.representative || ''}
                    onChange={(e) => handleInputChange('party2', {...formData.party2, representative: e.target.value})}
                    placeholder={formLabel('ph_representative_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('party2_address')}</label>
                  <input
                    type="text"
                    value={formData.party2?.address || ''}
                    onChange={(e) => handleInputChange('party2', {...formData.party2, address: e.target.value})}
                    placeholder={formLabel('ph_client_full_address')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('contract_type')}</label>
                  <CustomSelect
                    value={formData.contract?.type || 'vente'}
                    onChange={(e) => handleInputChange('contract', {...formData.contract, type: e.target.value})}
                  >
                    <option value="vente">{formLabel('contract_types.sale')}</option>
                    <option value="prestation">{formLabel('contract_types.service')}</option>
                    <option value="distribution">{formLabel('contract_types.distribution')}</option>
                    <option value="partenariat">{formLabel('contract_types.partnership')}</option>
                    <option value="fourniture">{formLabel('contract_types.supply')}</option>
                    <option value="maintenance">{formLabel('contract_types.maintenance')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('contract_object')}</label>
                  <input
                    type="text"
                    value={formData.contract?.object || ''}
                    onChange={(e) => handleInputChange('contract', {...formData.contract, object: e.target.value})}
                    placeholder={formLabel('ph_contract_object')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('total_amount')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.contract?.totalAmount || 0}
                    onChange={(val) => handleInputChange('contract', {...formData.contract, totalAmount: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('currency')}</label>
                  <CustomSelect
                    value={formData.contract?.currency || getBrandDefaultCurrency()}
                    onChange={(e) => handleInputChange('contract', {...formData.contract, currency: e.target.value})}
                  >
                    {getBrandCurrencyOptions().map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('signature_date')}</label>
                  <CustomDatePicker
                      value={formData.contract?.signatureDate || ''}
                      onChange={(val) => handleInputChange('contract', {...formData.contract, signatureDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('effective_date')}</label>
                  <CustomDatePicker
                      value={formData.contract?.effectiveDate || ''}
                      onChange={(val) => handleInputChange('contract', {...formData.contract, effectiveDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('contract_duration')}</label>
                  <CustomSelect
                    value={formData.contract?.duration || '12_mois'}
                    onChange={(e) => handleInputChange('contract', {...formData.contract, duration: e.target.value})}
                  >
                    <option value="3_mois">{formLabel('contract_durations.3m')}</option>
                    <option value="6_mois">{formLabel('contract_durations.6m')}</option>
                    <option value="12_mois">{formLabel('contract_durations.12m')}</option>
                    <option value="24_mois">{formLabel('contract_durations.24m')}</option>
                    <option value="36_mois">{formLabel('contract_durations.36m')}</option>
                    <option value="indéterminée">{formLabel('contract_durations.indefinite')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('expiration_date')}</label>
                  <CustomDatePicker
                      value={formData.contract?.expirationDate || ''}
                      onChange={(val) => handleInputChange('contract', {...formData.contract, expirationDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('payment_modalities')}</label>
                  <CustomSelect
                    value={formData.contract?.paymentTerms || ''}
                    onChange={(e) => handleInputChange('contract', {...formData.contract, paymentTerms: e.target.value})}
                  >
                    <option value="">{formLabel('select_placeholder')}</option>
                    <option value="comptant">{formLabel('payment_terms_options.cash')}</option>
                    <option value="15_jours">{formLabel('payment_terms_options.15')}</option>
                    <option value="30_jours">{formLabel('payment_terms_options.30')}</option>
                    <option value="45_jours">{formLabel('payment_terms_options.45')}</option>
                    <option value="60_jours">{formLabel('payment_terms_options.60')}</option>
                    <option value="échelonné">{formLabel('payment_terms_options.installment')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('penalty_rate')}</label>
                  <CustomNumberInput
                    value={formData.contract?.penaltyRate || 0}
                    onChange={(val) => handleInputChange('contract', {...formData.contract, penaltyRate: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={0.1}
                    decimals={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('competent_court')}</label>
                  <input
                    type="text"
                    value={formData.contract?.competentCourt || ''}
                    onChange={(e) => handleInputChange('contract', {...formData.contract, competentCourt: e.target.value})}
                    placeholder={formLabel('ph_competent_court')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('applicable_law')}</label>
                  <CustomSelect
                    value={formData.contract?.applicableLaw || legalDefault('applicable_law_value')}
                    onChange={(e) => handleInputChange('contract', {...formData.contract, applicableLaw: e.target.value})}
                  >
                    <option value={legalDefault('applicable_law_value')}>{formLabel('applicable_laws.saudi')}</option>
                    <option value="français">{formLabel('applicable_laws.french')}</option>
                    <option value="international">{formLabel('applicable_laws.international')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.contract?.renewalClause || false}
                      onChange={(e) => handleInputChange('contract', {...formData.contract, renewalClause: e.target.checked})}
                    />
                    {formLabel('renewal_clause_auto')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.contract?.confidentialityClause || false}
                      onChange={(e) => handleInputChange('contract', {...formData.contract, confidentialityClause: e.target.checked})}
                    />
                    {formLabel('confidentiality_clause')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.contract?.exclusivityClause || false}
                      onChange={(e) => handleInputChange('contract', {...formData.contract, exclusivityClause: e.target.checked})}
                    />
                    {formLabel('exclusivity_clause')}
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('party1_obligations')}</label>
                <textarea
                  value={formData.contract?.party1Obligations || ''}
                  onChange={(e) => handleInputChange('contract', {...formData.contract, party1Obligations: e.target.value})}
                  placeholder={formLabel('ph_party1_obligations')}
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('party2_obligations')}</label>
                <textarea
                  value={formData.contract?.party2Obligations || ''}
                  onChange={(e) => handleInputChange('contract', {...formData.contract, party2Obligations: e.target.value})}
                  placeholder={formLabel('ph_party2_obligations')}
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('special_conditions')}</label>
                <textarea
                  value={formData.contract?.specialConditions || ''}
                  onChange={(e) => handleInputChange('contract', {...formData.contract, specialConditions: e.target.value})}
                  placeholder={formLabel('ph_special_conditions_long')}
                  rows="3"
                />
              </div>
            </div>
          )}
          {selectedType.id === 'service-agreement' && (
            <div className="form-section">
              <h3 className="section-title" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                <Settings size={20} color={getIconColor()} />
                {formLabel('service_agreement_title')}
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('provider_name')}</label>
                  <input
                    type="text"
                    value={formData.provider?.name || ''}
                    onChange={(e) => handleInputChange('provider', {...formData.provider, name: e.target.value})}
                    placeholder={formLabel('ph_provider_company')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('provider_nif')}</label>
                  <input
                    type="text"
                    value={formData.provider?.nif || ''}
                    onChange={(e) => handleInputChange('provider', {...formData.provider, nif: e.target.value})}
                    placeholder={formLabel('ph_fiscal_id')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('provider_address')}</label>
                  <input
                    type="text"
                    value={formData.provider?.address || ''}
                    onChange={(e) => handleInputChange('provider', {...formData.provider, address: e.target.value})}
                    placeholder={formLabel('ph_provider_full_address')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('beneficiary_client')}</label>
                  <input
                    type="text"
                    value={formData.client?.name || ''}
                    onChange={(e) => handleInputChange('client', {...formData.client, name: e.target.value})}
                    placeholder={formLabel('ph_beneficiary_client_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('client_site')}</label>
                  <input
                    type="text"
                    value={formData.client?.site || ''}
                    onChange={(e) => handleInputChange('client', {...formData.client, site: e.target.value})}
                    placeholder={formLabel('ph_client_site_location')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('client_contact')}</label>
                  <input
                    type="text"
                    value={formData.client?.contact || ''}
                    onChange={(e) => handleInputChange('client', {...formData.client, contact: e.target.value})}
                    placeholder={formLabel('ph_client_contact_person')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('service_type')}</label>
                  <CustomSelect
                    value={formData.service?.type || 'maintenance'}
                    onChange={(e) => handleInputChange('service', {...formData.service, type: e.target.value})}
                  >
                    <option value="maintenance">{formLabel('service_types.preventive_maintenance')}</option>
                    <option value="support">{formLabel('service_types.technical_support')}</option>
                    <option value="dépannage">{formLabel('service_types.troubleshooting')}</option>
                    <option value="formation">{formLabel('project_types.training')}</option>
                    <option value="consulting">{formLabel('service_types.consulting_expertise')}</option>
                    <option value="installation">{formLabel('project_types.installation')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('sla_level')}</label>
                  <CustomSelect
                    value={formData.service?.slaLevel || 'standard'}
                    onChange={(e) => handleInputChange('service', {...formData.service, slaLevel: e.target.value})}
                  >
                    <option value="basique">{formLabel('sla_levels.basic')}</option>
                    <option value="standard">{formLabel('sla_levels.standard')}</option>
                    <option value="Étendu">{formLabel('sla_levels.extended')}</option>
                    <option value="premium">{formLabel('sla_levels.premium')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('response_time_hours')}</label>
                  <CustomNumberInput
                    value={formData.service?.responseTime || 4}
                    onChange={(val) => handleInputChange('service', {...formData.service, responseTime: parseInt(val) || 4})}
                    placeholder="4"
                    min={1}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('resolution_time_hours')}</label>
                  <CustomNumberInput
                    value={formData.service?.resolutionTime || 24}
                    onChange={(val) => handleInputChange('service', {...formData.service, resolutionTime: parseInt(val) || 24})}
                    placeholder="24"
                    min={1}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('guaranteed_availability_pct')}</label>
                  <CustomNumberInput
                    value={formData.service?.availability || 99}
                    onChange={(val) => handleInputChange('service', {...formData.service, availability: parseFloat(val) || 99})}
                    placeholder="99"
                    min={0}
                    max={100}
                    step={0.1}
                    decimals={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('intervention_frequency')}</label>
                  <CustomSelect
                    value={formData.service?.frequency || ''}
                    onChange={(e) => handleInputChange('service', {...formData.service, frequency: e.target.value})}
                  >
                    <option value="hebdomadaire">{formLabel('payment_frequencies.weekly')}</option>
                    <option value="bi_mensuelle">{formLabel('payment_frequencies.bi_monthly')}</option>
                    <option value="mensuelle">{formLabel('payment_frequencies.monthly')}</option>
                    <option value="trimestrielle">{formLabel('payment_frequencies.quarterly')}</option>
                    <option value="semestrielle">{formLabel('intervention_frequencies.semiannual')}</option>
                    <option value="annuelle">{formLabel('intervention_frequencies.annual')}</option>
                    <option value="à_la_demande">{formLabel('intervention_frequencies.on_demand')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('monthly_cost')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.service?.monthlyCost || 0}
                    onChange={(val) => handleInputChange('service', {...formData.service, monthlyCost: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('intervention_cost')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.service?.interventionCost || 0}
                    onChange={(val) => handleInputChange('service', {...formData.service, interventionCost: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('start_date')}</label>
                  <CustomDatePicker
                      value={formData.service?.startDate || ''}
                      onChange={(val) => handleInputChange('service', {...formData.service, startDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('end_date')}</label>
                  <CustomDatePicker
                      value={formData.service?.endDate || ''}
                      onChange={(val) => handleInputChange('service', {...formData.service, endDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('notice_period_days')}</label>
                  <CustomNumberInput
                    value={formData.service?.noticePeriod || 30}
                    onChange={(val) => handleInputChange('service', {...formData.service, noticePeriod: parseInt(val) || 30})}
                    placeholder="30"
                    min={0}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('sla_penalties_pct')}</label>
                  <CustomNumberInput
                    value={formData.service?.slaPenalty || 0}
                    onChange={(val) => handleInputChange('service', {...formData.service, slaPenalty: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={0.1}
                    decimals={1}
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.service?.partsIncluded || false}
                      onChange={(e) => handleInputChange('service', {...formData.service, partsIncluded: e.target.checked})}
                    />
                    {formLabel('spare_parts_included')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.service?.remoteSupport || false}
                      onChange={(e) => handleInputChange('service', {...formData.service, remoteSupport: e.target.checked})}
                    />
                    {formLabel('remote_support_included')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.service?.emergencyService || false}
                      onChange={(e) => handleInputChange('service', {...formData.service, emergencyService: e.target.checked})}
                    />
                    Service d'urgence 24h/24
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('intervention_scope')}</label>
                <textarea
                  value={formData.service?.scope || ''}
                  onChange={(e) => handleInputChange('service', {...formData.service, scope: e.target.value})}
                  placeholder={formLabel('ph_intervention_scope')}
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('exclusions')}</label>
                <textarea
                  value={formData.service?.exclusions || ''}
                  onChange={(e) => handleInputChange('service', {...formData.service, exclusions: e.target.value})}
                  placeholder={formLabel('ph_exclusions')}
                  rows="2"
                />
              </div>
            </div>
          )}
          {selectedType.id === 'compliance-certificate' && (
            <div className="form-section">
              <h3 className="section-title" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                <CheckCircle size={20} color={getIconColor()} />
                {formLabel('compliance_certificate_title')}
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('certifying_body')}</label>
                  <input
                    type="text"
                    value={formData.certifier?.name || ''}
                    onChange={(e) => handleInputChange('certifier', {...formData.certifier, name: e.target.value})}
                    placeholder={formLabel('ph_certifying_body_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('body_accreditation')}</label>
                  <input
                    type="text"
                    value={formData.certifier?.accreditation || ''}
                    onChange={(e) => handleInputChange('certifier', {...formData.certifier, accreditation: e.target.value})}
                    placeholder={formLabel('ph_algerac_accreditation')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('body_address')}</label>
                  <input
                    type="text"
                    value={formData.certifier?.address || ''}
                    onChange={(e) => handleInputChange('certifier', {...formData.certifier, address: e.target.value})}
                    placeholder={formLabel('ph_body_address')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('manufacturer_supplier')}</label>
                  <input
                    type="text"
                    value={formData.manufacturer?.name || ''}
                    onChange={(e) => handleInputChange('manufacturer', {...formData.manufacturer, name: e.target.value})}
                    placeholder={formLabel('ph_manufacturer_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('manufacturer_address')}</label>
                  <input
                    type="text"
                    value={formData.manufacturer?.address || ''}
                    onChange={(e) => handleInputChange('manufacturer', {...formData.manufacturer, address: e.target.value})}
                    placeholder={formLabel('ph_manufacturer_address')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('manufacturer_nif')}</label>
                  <input
                    type="text"
                    value={formData.manufacturer?.nif || ''}
                    onChange={(e) => handleInputChange('manufacturer', {...formData.manufacturer, nif: e.target.value})}
                    placeholder={formLabel('ph_fiscal_id')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('certified_product_service')}</label>
                  <input
                    type="text"
                    value={formData.product?.name || ''}
                    onChange={(e) => handleInputChange('product', {...formData.product, name: e.target.value})}
                    placeholder={formLabel('ph_product_designation')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('model_reference')}</label>
                  <CustomSelect
                    value={formData.product?.model || ''}
                    onChange={(value) => handleInputChange('product', {...formData.product, model: value})}
                    options={[]}
                    placeholder={formLabel('ph_commercial_model_ref')}
                    searchable
                    creatable
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('serial_lot_number')}</label>
                  <input
                    type="text"
                    value={formData.product?.serialNumber || ''}
                    onChange={(e) => handleInputChange('product', {...formData.product, serialNumber: e.target.value})}
                    placeholder={formLabel('ph_serial_lot_number')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('manufacturing_date')}</label>
                  <CustomDatePicker
                      value={formData.product?.manufacturingDate || ''}
                      onChange={(val) => handleInputChange('product', {...formData.product, manufacturingDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('reference_standard')}</label>
                  <CustomSelect
                    value={formData.compliance?.standard || 'ISO_9001'}
                    onChange={(e) => handleInputChange('compliance', {...formData.compliance, standard: e.target.value})}
                  >
                    <option value="ISO_9001">{formLabel('standards.iso9001')}</option>
                    <option value="ISO_14001">{formLabel('standards.iso14001')}</option>
                    <option value="ISO_45001">{formLabel('standards.iso45001')}</option>
                    <option value="CE">{formLabel('standards.ce_marking')}</option>
                    <option value="NF">{formLabel('standards.nf')}</option>
                    <option value="AFNOR">{formLabel('standards.afnor')}</option>
                    <option value="IANOR">{formLabel('standards.ianor')}</option>
                    <option value="autre">{formLabel('standards.other')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('standard_reference')}</label>
                  <CustomSelect
                    value={formData.compliance?.standardReference || ''}
                    onChange={(value) => handleInputChange('compliance', {...formData.compliance, standardReference: value})}
                    options={[]}
                    placeholder={formLabel('ph_standard_ref_example')}
                    searchable
                    creatable
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('certification_type')}</label>
                  <CustomSelect
                    value={formData.compliance?.certificationType || 'produit'}
                    onChange={(e) => handleInputChange('compliance', {...formData.compliance, certificationType: e.target.value})}
                  >
                    <option value="produit">{formLabel('certification_types.product')}</option>
                    <option value="système">{formLabel('certification_types.system')}</option>
                    <option value="service">{formLabel('certification_types.service')}</option>
                    <option value="personnel">{formLabel('certification_types.personnel')}</option>
                    <option value="processus">{formLabel('certification_types.process')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('certification_date')}</label>
                  <CustomDatePicker
                      value={formData.compliance?.certificationDate || ''}
                      onChange={(val) => handleInputChange('compliance', {...formData.compliance, certificationDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('expiration_date')}</label>
                  <CustomDatePicker
                      value={formData.compliance?.expirationDate || ''}
                      onChange={(val) => handleInputChange('compliance', {...formData.compliance, expirationDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('validity_years')}</label>
                  <CustomSelect
                    value={formData.compliance?.validity || '3'}
                    onChange={(e) => handleInputChange('compliance', {...formData.compliance, validity: e.target.value})}
                  >
                    <option value="1">{formLabel('validity_periods.1y')}</option>
                    <option value="2">{formLabel('validity_periods.2y')}</option>
                    <option value="3">{formLabel('validity_periods.3y')}</option>
                    <option value="5">{formLabel('validity_periods.5y')}</option>
                    <option value="permanente">{formLabel('validity_periods.permanent')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('test_laboratory')}</label>
                  <input
                    type="text"
                    value={formData.compliance?.testLaboratory || ''}
                    onChange={(e) => handleInputChange('compliance', {...formData.compliance, testLaboratory: e.target.value})}
                    placeholder={formLabel('ph_test_laboratory_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('test_report_number')}</label>
                  <input
                    type="text"
                    value={formData.compliance?.testReportNumber || ''}
                    onChange={(e) => handleInputChange('compliance', {...formData.compliance, testReportNumber: e.target.value})}
                    placeholder={formLabel('ph_test_report_ref')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('test_date')}</label>
                  <CustomDatePicker
                      value={formData.compliance?.testDate || ''}
                      onChange={(val) => handleInputChange('compliance', {...formData.compliance, testDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('authorized_signatory')}</label>
                  <input
                    type="text"
                    value={formData.compliance?.authorizedSignatory || ''}
                    onChange={(e) => handleInputChange('compliance', {...formData.compliance, authorizedSignatory: e.target.value})}
                    placeholder={formLabel('ph_authorized_signatory_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('signatory_role')}</label>
                  <input
                    type="text"
                    value={formData.compliance?.signatoryPosition || ''}
                    onChange={(e) => handleInputChange('compliance', {...formData.compliance, signatoryPosition: e.target.value})}
                    placeholder={formLabel('ph_signatory_role_example')}
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.compliance?.conformityDeclaration || false}
                      onChange={(e) => handleInputChange('compliance', {...formData.compliance, conformityDeclaration: e.target.checked})}
                    />
                    {formLabel('conformity_declaration_included')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.compliance?.surveillanceRequired || false}
                      onChange={(e) => handleInputChange('compliance', {...formData.compliance, surveillanceRequired: e.target.checked})}
                    />
                    {formLabel('periodic_surveillance_required')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.compliance?.internationalRecognition || false}
                      onChange={(e) => handleInputChange('compliance', {...formData.compliance, internationalRecognition: e.target.checked})}
                    />
                    {formLabel('international_recognition')}
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('certified_characteristics')}</label>
                <textarea
                  value={formData.compliance?.certifiedCharacteristics || ''}
                  onChange={(e) => handleInputChange('compliance', {...formData.compliance, certifiedCharacteristics: e.target.value})}
                  placeholder={formLabel('ph_certified_characteristics')}
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('usage_conditions')}</label>
                <textarea
                  value={formData.compliance?.usageConditions || ''}
                  onChange={(e) => handleInputChange('compliance', {...formData.compliance, usageConditions: e.target.value})}
                  placeholder={formLabel('ph_usage_conditions')}
                  rows="2"
                />
              </div>
            </div>
          )}

          {selectedType.id === 'account-statement' && (
            <div className="form-section">
              <h3 className="section-title" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                <FileCheck size={20} color={getIconColor()} />
                {formLabel('account_statement_title')}
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('company_name_label')}</label>
                  <input
                    type="text"
                    value={formData.company?.name || ''}
                    onChange={(e) => handleInputChange('company', {...formData.company, name: e.target.value})}
                    placeholder={formLabel('ph_your_company_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('company_nif')}</label>
                  <input
                    type="text"
                    value={formData.company?.nif || ''}
                    onChange={(e) => handleInputChange('company', {...formData.company, nif: e.target.value})}
                    placeholder={formLabel('ph_fiscal_id')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('company_address')}</label>
                  <input
                    type="text"
                    value={formData.company?.address || ''}
                    onChange={(e) => handleInputChange('company', {...formData.company, address: e.target.value})}
                    placeholder={formLabel('ph_company_full_address')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('client')}</label>
                  <input
                    type="text"
                    value={formData.client?.name || ''}
                    onChange={(e) => handleInputChange('client', {...formData.client, name: e.target.value})}
                    placeholder={formLabel('ph_client_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('client_code')}</label>
                  <input
                    type="text"
                    value={formData.client?.code || ''}
                    onChange={(e) => handleInputChange('client', {...formData.client, code: e.target.value})}
                    placeholder={formLabel('ph_client_id_code')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('client_nif')}</label>
                  <input
                    type="text"
                    value={formData.client?.nif || ''}
                    onChange={(e) => handleInputChange('client', {...formData.client, nif: e.target.value})}
                    placeholder={formLabel('ph_client_nif')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('period_from')}</label>
                  <CustomDatePicker
                      value={formData.period.startDate || ''}
                      onChange={(val) => handleInputChange('period', {...formData.period, startDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('period_to')}</label>
                  <CustomDatePicker
                      value={formData.period.endDate || ''}
                      onChange={(val) => handleInputChange('period', {...formData.period, endDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('opening_balance')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.balances?.opening || 0}
                    onChange={(val) => handleInputChange('balances', {...formData.balances, opening: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('total_invoiced')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.balances?.totalInvoiced || 0}
                    onChange={(val) => handleInputChange('balances', {...formData.balances, totalInvoiced: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('total_paid')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.balances?.totalPaid || 0}
                    onChange={(val) => handleInputChange('balances', {...formData.balances, totalPaid: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('total_credits')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.balances?.totalCredits || 0}
                    onChange={(val) => handleInputChange('balances', {...formData.balances, totalCredits: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('closing_balance')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.balances?.closing || 0}
                    onChange={(val) => handleInputChange('balances', {...formData.balances, closing: parseFloat(val) || 0})}
                    placeholder="0"
                    disabled
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('overdue_receivables')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.aging?.overdue || 0}
                    onChange={(val) => handleInputChange('aging', {...formData.aging, overdue: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('receivables_30_days')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.aging?.days30 || 0}
                    onChange={(val) => handleInputChange('aging', {...formData.aging, days30: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('receivables_60_days')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.aging?.days60 || 0}
                    onChange={(val) => handleInputChange('aging', {...formData.aging, days60: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('receivables_90_plus_days')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.aging?.days90Plus || 0}
                    onChange={(val) => handleInputChange('aging', {...formData.aging, days90Plus: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('credit_limit')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.client?.creditLimit || 0}
                    onChange={(val) => handleInputChange('client', {...formData.client, creditLimit: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={1000}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.payment_terms')}</label>
                  <CustomSelect
                    value={formData.client?.paymentTerms || ''}
                    onChange={(e) => handleInputChange('client', {...formData.client, paymentTerms: e.target.value})}
                  >
                    <option value="">{formLabel('select_placeholder')}</option>
                    <option value="comptant">{formLabel('payment_terms_options.cash')}</option>
                    <option value="15_jours">{formLabel('payment_terms_options.15')}</option>
                    <option value="30_jours">{formLabel('payment_terms_options.30')}</option>
                    <option value="45_jours">{formLabel('payment_terms_options.45')}</option>
                    <option value="60_jours">{formLabel('payment_terms_options.60')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('account_status')}</label>
                  <CustomSelect
                    value={formData.client?.accountStatus || 'actif'}
                    onChange={(e) => handleInputChange('client', {...formData.client, accountStatus: e.target.value})}
                  >
                    <option value="actif">{formLabel('account_statuses.active')}</option>
                    <option value="suspendu">{formLabel('account_statuses.suspended')}</option>
                    <option value="bloqué">{formLabel('account_statuses.blocked')}</option>
                    <option value="contentieux">{formLabel('account_statuses.dispute')}</option>
                    <option value="fermé">{formLabel('account_statuses.closed')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.client?.creditExceeded || false}
                      onChange={(e) => handleInputChange('client', {...formData.client, creditExceeded: e.target.checked})}
                    />
                    {formLabel('credit_limit_exceeded')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.client?.paymentDelay || false}
                      onChange={(e) => handleInputChange('client', {...formData.client, paymentDelay: e.target.checked})}
                    />
                    {formLabel('recurring_payment_delays')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.client?.legalAction || false}
                      onChange={(e) => handleInputChange('client', {...formData.client, legalAction: e.target.checked})}
                    />
                    {formLabel('legal_action_in_progress')}
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('account_observations')}</label>
                <textarea
                  value={formData.client?.observations || ''}
                  onChange={(e) => handleInputChange('client', {...formData.client, observations: e.target.value})}
                  placeholder={formLabel('ph_account_observations')}
                  rows="2"
                />
              </div>
              
              <div className="form-group">
                <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('recommended_actions')}</label>
                <textarea
                  value={formData.client?.recommendedActions || ''}
                  onChange={(e) => handleInputChange('client', {...formData.client, recommendedActions: e.target.value})}
                  placeholder={formLabel('ph_recommended_actions')}
                  rows="2"
                />
              </div>
            </div>
          )}
          {selectedType.id === 'advance-invoice' && (
            <div className="form-section">
              <h3 className="section-title" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                <DollarSign size={20} color={getIconColor()} />
                {formLabel('advance_invoice_title')}
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('seller_name')}</label>
                  <input
                    type="text"
                    value={formData.seller?.name || ''}
                    onChange={(e) => handleInputChange('seller', {...formData.seller, name: e.target.value})}
                    placeholder={formLabel('ph_seller_company')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('seller_nif')}</label>
                  <input
                    type="text"
                    value={formData.seller?.nif || ''}
                    onChange={(e) => handleInputChange('seller', {...formData.seller, nif: e.target.value})}
                    placeholder={formLabel('ph_fiscal_id')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('seller_address')}</label>
                  <input
                    type="text"
                    value={formData.seller?.address || ''}
                    onChange={(e) => handleInputChange('seller', {...formData.seller, address: e.target.value})}
                    placeholder={formLabel('ph_seller_full_address')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('buyer_client')}</label>
                  <input
                    type="text"
                    value={formData.buyer?.name || ''}
                    onChange={(e) => handleInputChange('buyer', {...formData.buyer, name: e.target.value})}
                    placeholder={formLabel('ph_buyer_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('buyer_address')}</label>
                  <input
                    type="text"
                    value={formData.buyer?.address || ''}
                    onChange={(e) => handleInputChange('buyer', {...formData.buyer, address: e.target.value})}
                    placeholder={formLabel('ph_buyer_address')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('buyer_nif')}</label>
                  <input
                    type="text"
                    value={formData.buyer?.nif || ''}
                    onChange={(e) => handleInputChange('buyer', {...formData.buyer, nif: e.target.value})}
                    placeholder={formLabel('ph_buyer_nif')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('contract_reference')}</label>
                  <input
                    type="text"
                    value={formData.advance?.contractReference || ''}
                    onChange={(e) => handleInputChange('advance', {...formData.advance, contractReference: e.target.value})}
                    placeholder={formLabel('ph_contract_or_po_number')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('contract_date')}</label>
                  <CustomDatePicker
                      value={formData.advance?.contractDate || ''}
                      onChange={(val) => handleInputChange('advance', {...formData.advance, contractDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('total_contract_amount')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.advance?.totalContractAmount || 0}
                    onChange={(val) => handleInputChange('advance', {...formData.advance, totalContractAmount: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('advance_percentage')}</label>
                  <CustomNumberInput
                    value={formData.advance?.percentage || 30}
                    onChange={(val) => handleInputChange('advance', {...formData.advance, percentage: parseFloat(val) || 30})}
                    placeholder="30"
                    min={0}
                    max={100}
                    step={0.1}
                    decimals={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('advance_amount')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.advance?.amount || 0}
                    onChange={(val) => handleInputChange('advance', {...formData.advance, amount: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('advance_vat_rate')}</label>
                  <CustomNumberInput
                    value={formData.advance?.vatRate || 19}
                    onChange={(val) => handleInputChange('advance', {...formData.advance, vatRate: parseFloat(val) || 19})}
                    placeholder="19"
                    min={0}
                    max={100}
                    step={0.1}
                    decimals={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('vat_amount')}</label>
                  <CustomNumberInput
                    value={formData.advance?.vatAmount || 0}
                    onChange={(val) => handleInputChange('advance', {...formData.advance, vatAmount: parseFloat(val) || 0})}
                    placeholder="0"
                    disabled
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('total_amount_inc_vat')}</label>
                  <CustomNumberInput
                    value={formData.advance?.totalAmount || 0}
                    onChange={(val) => handleInputChange('advance', {...formData.advance, totalAmount: parseFloat(val) || 0})}
                    placeholder="0"
                    disabled
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('advance_type')}</label>
                  <CustomSelect
                    value={formData.advance?.type || 'signature'}
                    onChange={(e) => handleInputChange('advance', {...formData.advance, type: e.target.value})}
                  >
                    <option value="signature">{formLabel('advance_types.at_signature')}</option>
                    <option value="commande">{formLabel('advance_types.on_order')}</option>
                    <option value="livraison">{formLabel('advance_types.before_delivery')}</option>
                    <option value="Étape">{formLabel('advance_types.by_milestone')}</option>
                    <option value="mensuel">{formLabel('advance_types.monthly')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('payment_due_term')}</label>
                  <CustomSelect
                    value={formData.advance?.paymentTerm || ''}
                    onChange={(e) => handleInputChange('advance', {...formData.advance, paymentTerm: e.target.value})}
                  >
                    <option value="immédiat">{formLabel('payment_deadlines.immediate')}</option>
                    <option value="8_jours">{formLabel('payment_deadlines.8_days')}</option>
                    <option value="15_jours">{formLabel('payment_terms_options.15')}</option>
                    <option value="30_jours">{formLabel('payment_terms_options.30')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('due_date')}</label>
                  <CustomDatePicker
                      value={formData.advance?.dueDate || ''}
                      onChange={(val) => handleInputChange('advance', {...formData.advance, dueDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('accepted_payment_method')}</label>
                  <CustomSelect
                    value={formData.advance?.paymentMethod || ''}
                    onChange={(e) => handleInputChange('advance', {...formData.advance, paymentMethod: e.target.value})}
                  >
                    <option value="virement">{formLabel('payment_methods.bank_transfer')}</option>
                    <option value="chèque">{formLabel('payment_methods.cheque')}</option>
                    <option value="espèces">{formLabel('payment_methods.cash')}</option>
                    <option value="traite">{formLabel('payment_methods.promissory')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('advance_status')}</label>
                  <CustomSelect
                    value={formData.advance?.status || ''}
                    onChange={(e) => handleInputChange('advance', {...formData.advance, status: e.target.value})}
                  >
                    <option value="facturé">{formLabel('advance_statuses.invoiced')}</option>
                    <option value="payé">{formLabel('advance_statuses.paid')}</option>
                    <option value="en_attente">{formLabel('statuses.pending')}</option>
                    <option value="en_retard">{formLabel('statuses.overdue')}</option>
                    <option value="annulé">{formLabel('statuses.cancelled')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.expected_delivery_date')}</label>
                  <CustomDatePicker
                      value={formData.advance?.expectedDeliveryDate || ''}
                      onChange={(val) => handleInputChange('advance', {...formData.advance, expectedDeliveryDate: val})}
                    />
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.advance?.deductibleFromFinal || true}
                      onChange={(e) => handleInputChange('advance', {...formData.advance, deductibleFromFinal: e.target.checked})}
                    />
                    {formLabel('deductible_from_final_invoice')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.advance?.refundableIfCancelled || false}
                      onChange={(e) => handleInputChange('advance', {...formData.advance, refundableIfCancelled: e.target.checked})}
                    />
                    {formLabel('refundable_if_cancelled')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.advance?.guaranteeRequired || false}
                      onChange={(e) => handleInputChange('advance', {...formData.advance, guaranteeRequired: e.target.checked})}
                    />
                    {formLabel('bank_guarantee_required')}
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('advance_purpose')}</label>
                <textarea
                  value={formData.advance?.purpose || ''}
                  onChange={(e) => handleInputChange('advance', {...formData.advance, purpose: e.target.value})}
                  placeholder={formLabel('ph_advance_purpose')}
                  rows="2"
                />
              </div>
              
              <div className="form-group">
                <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('refund_conditions')}</label>
                <textarea
                  value={formData.advance?.refundConditions || ''}
                  onChange={(e) => handleInputChange('advance', {...formData.advance, refundConditions: e.target.value})}
                  placeholder={formLabel('ph_refund_conditions')}
                  rows="2"
                />
              </div>
            </div>
          )}

          {/* OFFRES COMMERCIALES Documents - Specialized Forms */}




          {selectedType.id === 'attachment' && (
          <div className="form-section">
            <h3 className="section-title" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                <Paperclip size={20} color={getIconColor()} />
                {formLabel('specifications_title')}
            </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('specifications_type')}</label>
                  <CustomSelect
                    value={formData.attachment?.type || 'technique'}
                    onChange={(e) => handleInputChange('attachment', {...formData.attachment, type: e.target.value})}
                  >
                    <option value="technique">{formLabel('specification_types.technical')}</option>
                    <option value="fonctionnel">{formLabel('specification_types.functional')}</option>
                    <option value="commercial">{formLabel('specification_types.commercial')}</option>
                    <option value="juridique">{formLabel('specification_types.legal')}</option>
                    <option value="qualité">{formLabel('specification_types.quality')}</option>
                    <option value="sécurité">{formLabel('specification_types.security')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('application_domain')}</label>
                  <CustomSelect
                    value={formData.attachment?.domain || ''}
                    onChange={(e) => handleInputChange('attachment', {...formData.attachment, domain: e.target.value})}
                  >
                    <option value="informatique">{formLabel('application_domains.it')}</option>
                    <option value="btp">{formLabel('application_domains.construction')}</option>
                    <option value="industrie">{formLabel('application_domains.industry')}</option>
                    <option value="Énergie">{formLabel('application_domains.energy')}</option>
                    <option value="transport">{formLabel('application_domains.transport')}</option>
                    <option value="santé">{formLabel('application_domains.health')}</option>
                    <option value="Éducation">{formLabel('application_domains.education')}</option>
                    <option value="agriculture">{formLabel('application_domains.agriculture')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('project_priority')}</label>
                  <CustomSelect
                    value={formData.attachment?.priority || ''}
                    onChange={(e) => handleInputChange('attachment', {...formData.attachment, priority: e.target.value})}
                  >
                    <option value="">{formLabel('select_placeholder')}</option>
                    <option value="normale">{formLabel('priority_levels.normal')}</option>
                    <option value="importante">{formLabel('project_priorities.important')}</option>
                    <option value="urgente">{formLabel('priority_levels.urgent')}</option>
                    <option value="critique">{formLabel('project_priorities.critical')}</option>
                    <option value="stratégique">{formLabel('project_priorities.strategic')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('project_manager')}</label>
                  <input
                    type="text"
                    value={formData.attachment?.projectManager || ''}
                    onChange={(e) => handleInputChange('attachment', {...formData.attachment, projectManager: e.target.value})}
                    placeholder={formLabel('ph_project_manager_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('client_order_giver')}</label>
                  <input
                    type="text"
                    value={formData.attachment?.client || ''}
                    onChange={(e) => handleInputChange('attachment', {...formData.attachment, client: e.target.value})}
                    placeholder={formLabel('ph_client_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('estimated_budget')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.attachment?.budget || 0}
                    onChange={(val) => handleInputChange('attachment', {...formData.attachment, budget: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('planned_start_date')}</label>
                  <CustomDatePicker
                      value={formData.attachment?.startDate || ''}
                      onChange={(val) => handleInputChange('attachment', {...formData.attachment, startDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('expected_end_date')}</label>
                  <CustomDatePicker
                      value={formData.attachment?.endDate || ''}
                      onChange={(val) => handleInputChange('attachment', {...formData.attachment, endDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('estimated_duration')}</label>
                  <input
                    type="text"
                    value={formData.attachment?.duration || ''}
                    onChange={(e) => handleInputChange('attachment', {...formData.attachment, duration: e.target.value})}
                    placeholder={formLabel('ph_duration_example')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('team_size')}</label>
                  <CustomNumberInput
                    value={formData.attachment?.teamSize || 1}
                    onChange={(val) => handleInputChange('attachment', {...formData.attachment, teamSize: parseInt(val) || 1})}
                    placeholder="1"
                    min={1}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('applicable_standards')}</label>
                  <input
                    type="text"
                    value={formData.attachment?.standards || ''}
                    onChange={(e) => handleInputChange('attachment', {...formData.attachment, standards: e.target.value})}
                    placeholder={formLabel('ph_applicable_standards')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('confidentiality_level')}</label>
                  <CustomSelect
                    value={formData.attachment?.confidentiality || 'normal'}
                    onChange={(e) => handleInputChange('attachment', {...formData.attachment, confidentiality: e.target.value})}
                  >
                    <option value="public">{formLabel('confidentiality_levels.public')}</option>
                    <option value="normal">{formLabel('confidentiality_levels.normal')}</option>
                    <option value="confidentiel">{formLabel('confidentiality_levels.confidential')}</option>
                    <option value="secret">{formLabel('confidentiality_levels.secret')}</option>
                    <option value="très_secret">{formLabel('confidentiality_levels.top_secret')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('document_status')}</label>
                  <CustomSelect
                    value={formData.attachment?.status || ''}
                    onChange={(e) => handleInputChange('attachment', {...formData.attachment, status: e.target.value})}
                  >
                    <option value="brouillon">{statusLbl('draft')}</option>
                    <option value="en_cours">{formLabel('document_statuses.in_progress')}</option>
                    <option value="en_révision">{formLabel('document_statuses.in_review')}</option>
                    <option value="validé">{statusLbl('validated')}</option>
                    <option value="approuvé">{formLabel('statuses.approved')}</option>
                    <option value="archivé">{formLabel('document_statuses.archived')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('document_version')}</label>
                  <input
                    type="text"
                    value={formData.attachment?.version || '1.0'}
                    onChange={(e) => handleInputChange('attachment', {...formData.attachment, version: e.target.value})}
                    placeholder="1.0"
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.attachment?.requiresApproval || false}
                      onChange={(e) => handleInputChange('attachment', {...formData.attachment, requiresApproval: e.target.checked})}
                    />
                    {formLabel('approval_required')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.attachment?.technicalValidation || false}
                      onChange={(e) => handleInputChange('attachment', {...formData.attachment, technicalValidation: e.target.checked})}
                    />
                    {formLabel('technical_validation')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.attachment?.legalReview || false}
                      onChange={(e) => handleInputChange('attachment', {...formData.attachment, legalReview: e.target.checked})}
                    />
                    {formLabel('legal_review')}
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('project_objectives')}</label>
                <textarea
                  value={formData.attachment?.objectives || ''}
                  onChange={(e) => handleInputChange('attachment', {...formData.attachment, objectives: e.target.value})}
                  placeholder={formLabel('ph_project_objectives')}
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('technical_constraints')}</label>
                <textarea
                  value={formData.attachment?.technicalConstraints || ''}
                  onChange={(e) => handleInputChange('attachment', {...formData.attachment, technicalConstraints: e.target.value})}
                  placeholder={formLabel('ph_project_constraints')}
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('expected_deliverables')}</label>
                <textarea
                  value={formData.attachment?.deliverables || ''}
                  onChange={(e) => handleInputChange('attachment', {...formData.attachment, deliverables: e.target.value})}
                  placeholder={formLabel('ph_expected_deliverables')}
                  rows="2"
                />
              </div>
              
              <div className="form-group">
                <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('acceptance_criteria')}</label>
                <textarea
                  value={formData.attachment?.acceptanceCriteria || ''}
                  onChange={(e) => handleInputChange('attachment', {...formData.attachment, acceptanceCriteria: e.target.value})}
                  placeholder={formLabel('ph_acceptance_criteria')}
                  rows="2"
                />
              </div>
            </div>
          )}
          {selectedType.id === 'payment-voucher' && (
            <div className="bg-white/5 rounded-xl p-5 border border-white/20 overflow-hidden">
              <div className="flex items-center gap-2 mb-4">
                <Banknote className="h-5 w-5 text-blue-400" />
                <h3 className={`text-lg font-semibold ${isDashboardLightMode ? 'text-black' : 'text-white'}`}>{t('document_creation.form.document_details')}</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Identifiants du document */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(96, 165, 250, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.document_identifiers')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.document_number')}</label>
                    <input
                      type="text"
                      value={formData.number || ''}
                      readOnly
                      className="readonly-input"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.date_label')}</label>
                    <CustomDatePicker
                      value={formData.date}
                      onChange={(dateString) => handleInputChange('date', dateString)}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('expected_cashing_date')}</label>
                    <CustomDatePicker
                      value={formData.expectedCashingDate}
                      onChange={(dateString) => handleInputChange('expectedCashingDate', dateString)}
                    />
                  </div>
                </div>
                
                {/* Informations de suivi */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(96, 165, 250, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.tracking_info')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.prepared_by')}</label>
                  <CustomSelect
                    value={formData.issuedBy || ''}
                    onChange={(value) => {
                    handleInputChange('issuedBy', value);
                    // Sync with "Établie par" signature if enabled
                    if (formData.showEtabliePar) {
                      handleInputChange('etablieParName', value);
                    }
                  }}
                    options={availableFacturiersForDropdown}
                    placeholder={t('document_creation.form.search_or_enter_biller')}
                    searchable
                  />
                </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.initial_status')}</label>
                    <CustomSelect
                      value={documentStatusValue()}
                      onChange={handleFormStatusChange}
                      creatable={false}
                      searchable={false}
                      options={getStatusOptions(selectedType.id)}
                      placeholder={t('document_creation.form.select_placeholder')}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.priority')}</label>
                    <CustomSelect
                      value={formData.priority ?? ''}
                      onChange={(value) => handleInputChange('priority', value)}
                      autoSelect={false}
                      createLabel={t('document_creation.form.create_priority')}
                      options={[
                        { value: 'urgent', label: t('document_creation.form.priority_levels.urgent') },
                        { value: 'high', label: t('document_creation.form.priority_levels.high') },
                        { value: 'normale', label: t('document_creation.form.priority_levels.normal') },
                        { value: 'low', label: t('document_creation.form.priority_levels.low') }
                      ]}
                      placeholder={t('document_creation.form.select_placeholder')}
                    />
                  </div>
                </div>
                
                {/* Transaction & références */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(96, 165, 250, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.transaction_and_references')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.payment_method')}</label>
                    <CustomSelect
                      value={formData.paymentMethod || ''}
                      onChange={(value) => handleInputChange('paymentMethod', value)}
                      options={paymentMethodOptions}
                      placeholder={t('document_creation.form.select_placeholder')}
                      searchable
                    />
                  </div>

                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('payment_reason')}</label>
                    <CustomSelect
                      value={formData.paymentReason || ''}
                      onChange={(value) => handleInputChange('paymentReason', value)}
                      options={getPaymentVoucherReasonOptions()}
                      placeholder={t('document_creation.form.select_or_enter')}
                      searchable
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('transaction_number')}</label>
                    <input
                      type="text"
                      value={formData.transactionNumber || ''}
                      onChange={(e) => handleInputChange('transactionNumber', e.target.value)}
                      placeholder={formLabel('ph_transaction_example')}
                    />
                  </div>

                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.ref_supplier_invoice')}</label>
                    <input
                      type="text"
                      value={formData.relatedInvoices || ''}
                      onChange={(e) => handleInputChange('relatedInvoices', e.target.value)}
                      placeholder={formLabel('ph_supplier_invoice_number_example')}
                    />
                  </div>
                </div>
                
                {/* Coordonnées bancaires */}
                <div className="col-span-full">
                  <h4 className="section-subtitle" style={{ display: 'inline-block', borderBottom: '2px solid rgba(96, 165, 250, 0.3)', paddingBottom: '0.25rem', marginBottom: '0.75rem' }}>{t('document_creation.form.bank_details')}</h4>
                </div>
                
                <div className="col-span-full grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('issuing_bank')}</label>
                    <CustomSelect
                      value={formData.issuerBank || ''}
                      onChange={(value) => handleInputChange('issuerBank', value)}
                      options={[
                        { value: 'SNB', label: formLabel('saudi_banks.snb') },
                        { value: 'RJHI', label: formLabel('saudi_banks.rjhi') },
                        { value: 'RIBL', label: formLabel('saudi_banks.ribl') },
                        { value: 'INMA', label: formLabel('saudi_banks.inma') },
                        { value: 'SAIB', label: formLabel('saudi_banks.saib') },
                        { value: 'BSFR', label: formLabel('saudi_banks.bsfr') },
                        { value: 'BLAD', label: formLabel('saudi_banks.blad') },
                        { value: 'SABB', label: formLabel('saudi_banks.sabb') }
                      ]}
                      placeholder={t('document_creation.form.search_generic')}
                      searchable
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('issuer_account')}</label>
                    <input
                      type="text"
                      value={formData.issuerAccount || ''}
                      onChange={(e) => handleInputChange('issuerAccount', e.target.value)}
                      placeholder={formLabel('ph_bank_account_example')}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('receiver_bank')}</label>
                    <CustomSelect
                      value={formData.receiverBank || ''}
                      onChange={(value) => handleInputChange('receiverBank', value)}
                      options={[
                        { value: 'SNB', label: formLabel('saudi_banks.snb') },
                        { value: 'RJHI', label: formLabel('saudi_banks.rjhi') },
                        { value: 'RIBL', label: formLabel('saudi_banks.ribl') },
                        { value: 'INMA', label: formLabel('saudi_banks.inma') },
                        { value: 'SAIB', label: formLabel('saudi_banks.saib') },
                        { value: 'BSFR', label: formLabel('saudi_banks.bsfr') },
                        { value: 'BLAD', label: formLabel('saudi_banks.blad') },
                        { value: 'SABB', label: formLabel('saudi_banks.sabb') }
                      ]}
                      placeholder={t('document_creation.form.search_generic')}
                      searchable
                    />
                  </div>
                  
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('receiver_account')}</label>
                    <input
                      type="text"
                      value={formData.receiverAccount || ''}
                      onChange={(e) => handleInputChange('receiverAccount', e.target.value)}
                      placeholder={formLabel('ph_bank_account_example')}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ?? Custom Payment Lines Section for payment-voucher ONLY */}
          {selectedType?.id === 'payment-voucher' && (
            <div className="form-section">
              <h3 className="section-title" style={{ margin: 0, borderBottom: 'none', paddingBottom: 0, marginBottom: '1rem' }}>
                <DollarSign size={20} color={getIconColor()} />
                {formLabel('payment_details')}
              </h3>

              <div className="items-container" style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
                {(() => {
                  let paymentLines = formData.paymentLines || [];
                  if (paymentLines.length === 0) {
                    paymentLines = [emptyPaymentVoucherLine('purchase_order')];
                  }
                  return paymentLines;
                })().map((line, index, allLines) => {
                  const lineType = line.lineType || 'purchase_order';
                  const isCreditNoteLine = lineType === 'credit_note';
                  const usedRefs = getUsedPaymentVoucherLineRefs(allLines, index, lineType);
                  const refOptions = (isCreditNoteLine ? supplierCreditNotes : purchaseOrders)
                    .map((doc) => ({
                      value: doc.reference,
                      label: doc.label,
                    }))
                    .filter((opt) => !usedRefs.has(String(opt.value || '').trim()));
                  const showPayLineDelete = (formData.paymentLines?.length > 1);
                  return (
                  <div key={index} style={{ padding: '0.1rem', background: 'transparent', borderRadius: '0.75rem', position: 'relative' }}>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: showPayLineDelete ? 'minmax(0, 3fr) minmax(0, 1fr) auto' : 'minmax(0, 3fr) minmax(0, 1fr)',
                      columnGap: '1rem',
                      alignItems: 'end'
                    }}>
                      <div className="form-group">
                        <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                          {isCreditNoteLine
                            ? t('document_creation.form.ref_supplier_credit_note')
                            : t('document_creation.form.ref_purchase_order')}
                        </label>
                        <CustomSelect
                          value={line.reference || ''}
                          onChange={(value) => handlePaymentVoucherLineRefChange(index, value)}
                          options={refOptions}
                          placeholder={t('document_creation.form.search_generic')}
                          searchable
                        />
                      </div>

                      <div className="form-group">
                        {index === 0 && (
                          <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('amount')}</label>
                        )}
                        <CustomNumberInput
                          value={line.amount || ''}
                          onChange={(val) => handlePaymentVoucherLineAmountChange(index, val)}
                          placeholder={formLabel('ph_amount_example')}
                          min={0}
                          step={100}
                        />
                      </div>

                      {showPayLineDelete && (
                        <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', marginLeft: 'calc(1px - 1rem)' }}>
                          {index === 0 && <div style={{ height: '22px', marginBottom: '0.5rem' }} />}
                          <button
                            type="button"
                            className="payment-line-delete-btn"
                            onClick={() => removePaymentVoucherLine(index)}
                            title={t('document_creation.form.delete')}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>

              <div style={{ display: 'flex', background: 'rgba(255, 255, 255, 0.1)', borderRadius: '9999px', padding: '4px', border: `1px solid ${getDocCreationAccentBorderRgba()}`, marginTop: '0.75rem' }}>
                <button
                  type="button"
                  onClick={() => handlePaymentLineAddTypeClick('purchase_order')}
                  style={{
                    flex: 1,
                    padding: '12px 24px',
                    borderRadius: '9999px',
                    background: paymentLineAddType === 'purchase_order' ? getDocCreationAccentSolid() : 'transparent',
                    color: '#ffffff',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <Plus size={18} />
                  {formLabel('add_purchase_order')}
                </button>
                <button
                  type="button"
                  onClick={() => handlePaymentLineAddTypeClick('credit_note')}
                  style={{
                    flex: 1,
                    padding: '12px 24px',
                    borderRadius: '9999px',
                    background: paymentLineAddType === 'credit_note' ? getDocCreationAccentSolid() : 'transparent',
                    color: '#ffffff',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    whiteSpace: 'nowrap'
                  }}
                >
                  <Plus size={18} />
                  {formLabel('credit_note_purchase')}
                </button>
              </div>
            </div>
          )}

          {/* ? REMOVED: échéancier de Paiement should NOT appear on payment-voucher 
              Payment voucher is a single payment being executed NOW, not a schedule for future payments */}
          
          {false && (
            <div className="form-section" style={{display:'none'}}>
              <h3 className="section-title" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                <Package size={20} color={getIconColor()} />
                Produits ? livrer
              </h3>
              
              <div className="items-container">
                {formData.items?.map((item, index) => (
                  <div key={index} className="item-row">
                    <div className="item-fields">
                      <div className="form-group">
                        <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('reference_label')}</label>
                        <input
                          type="text"
                          value={item.reference}
                          onChange={(e) => handleItemChange(index, 'reference', e.target.value)}
                          placeholder={formLabel('ph_code_reference')}
                        />
                      </div>
                      <div className="form-group">
                        <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('designation')}</label>
                        <input
                          type="text"
                          value={item.designation}
                          onChange={(e) => handleItemChange(index, 'designation', e.target.value)}
                          placeholder={formLabel('ph_product_description')}
                        />
                      </div>
                      <div className="form-group">
                        <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.item_quantity')}</label>
                        <CustomNumberInput
                          value={item.quantity}
                          onChange={(val) => {
                            const numVal = parseFloat(val);
                            handleItemChange(index, 'quantity', isNaN(numVal) ? 0 : numVal);
                          }}
                          min={0}
                          step={0.1}
                          decimals={1}
                        />
                      </div>
                      <div className="form-group">
                        <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('unit_of_measure')}</label>
                        <input
                          type="text"
                          value={item.unit}
                          onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                          placeholder={formLabel('ph_unit')}
                        />
                      </div>
                    </div>
                    <button
                      className="btn-remove"
                      onClick={() => removeItem(index)}
                      disabled={formData.items && formData.items.length === 1}
                      title={formLabel('delete_line')}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
                <button className="btn btn-secondary add-item" onClick={addItem}>
                  <PlusCircle size={16} />
                  {t('document_creation.form.add_product_btn')}
                </button>
              </div>
            </div>
          )}
          {/* PROJETS & TRAVAUX Documents */}
          {selectedType.id === 'reception-report' && (
            <div className="form-section">
              <h3 className="section-title" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                <CheckCircle size={20} color={getIconColor()} />
                {formLabel('reception_report_title')}
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('project_owner')}</label>
                  <input
                    type="text"
                    value={formData.client?.name || ''}
                    onChange={(e) => handleInputChange('client', {...formData.client, name: e.target.value})}
                    placeholder={formLabel('ph_project_owner_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('client_representative')}</label>
                  <input
                    type="text"
                    value={formData.client?.representative || ''}
                    onChange={(e) => handleInputChange('client', {...formData.client, representative: e.target.value})}
                    placeholder={formLabel('ph_representative_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('executing_company')}</label>
                  <input
                    type="text"
                    value={formData.contractor?.name || ''}
                    onChange={(e) => handleInputChange('contractor', {...formData.contractor, name: e.target.value})}
                    placeholder={formLabel('ph_company_legal_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('work_supervisor')}</label>
                  <input
                    type="text"
                    value={formData.contractor?.supervisor || ''}
                    onChange={(e) => handleInputChange('contractor', {...formData.contractor, supervisor: e.target.value})}
                    placeholder={formLabel('ph_work_supervisor')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('control_office')}</label>
                  <input
                    type="text"
                    value={formData.supervision?.controlOffice || ''}
                    onChange={(e) => handleInputChange('supervision', {...formData.supervision, controlOffice: e.target.value})}
                    placeholder={formLabel('ph_control_office_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('technical_controller')}</label>
                  <input
                    type="text"
                    value={formData.supervision?.technicalController || ''}
                    onChange={(e) => handleInputChange('supervision', {...formData.supervision, technicalController: e.target.value})}
                    placeholder={formLabel('ph_controller_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('market_reference')}</label>
                  <input
                    type="text"
                    value={formData.project?.contractReference || ''}
                    onChange={(e) => handleInputChange('project', {...formData.project, contractReference: e.target.value})}
                    placeholder={formLabel('ph_contract_or_po_number')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('work_nature')}</label>
                  <CustomSelect
                    value={formData.project?.workType || 'construction'}
                    onChange={(e) => handleInputChange('project', {...formData.project, workType: e.target.value})}
                  >
                    <option value="construction">{formLabel('work_types.construction')}</option>
                    <option value="rénovation">{formLabel('work_types.renovation')}</option>
                    <option value="aménagement">{formLabel('work_types.layout')}</option>
                    <option value="infrastructure">{formLabel('work_types.infrastructure')}</option>
                    <option value="Équipement">{formLabel('work_types.equipment')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('location_label')}</label>
                  <input
                    type="text"
                    value={formData.project?.location || ''}
                    onChange={(e) => handleInputChange('project', {...formData.project, location: e.target.value})}
                    placeholder={formLabel('ph_site_address')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('start_date')}</label>
                  <CustomDatePicker
                      value={formData.project?.startDate || ''}
                      onChange={(val) => handleInputChange('project', {...formData.project, startDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('expected_end_date')}</label>
                  <CustomDatePicker
                      value={formData.project?.plannedEndDate || ''}
                      onChange={(val) => handleInputChange('project', {...formData.project, plannedEndDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('actual_end_date')}</label>
                  <CustomDatePicker
                      value={formData.project?.actualEndDate || ''}
                      onChange={(val) => handleInputChange('project', {...formData.project, actualEndDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('work_amount')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.project?.contractAmount || 0}
                    onChange={(val) => handleInputChange('project', {...formData.project, contractAmount: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('reception_type')}</label>
                  <CustomSelect
                    value={formData.reception?.type || 'provisoire'}
                    onChange={(e) => handleInputChange('reception', {...formData.reception, type: e.target.value})}
                  >
                    <option value="provisoire">{formLabel('reception_types.provisional')}</option>
                    <option value="définitive">{formLabel('reception_types.definitive')}</option>
                    <option value="partielle">{formLabel('reception_types.partial')}</option>
                    <option value="avec_réserves">{formLabel('reception_types.with_reserves')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('reception_date')}</label>
                  <CustomDatePicker
                      value={formData.receptionDate || ''}
                      onChange={(val) => handleInputChange('reception', {...formData.reception, date: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('overall_compliance')}</label>
                  <CustomSelect
                    value={formData.reception?.overallCompliance || 'conforme'}
                    onChange={(e) => handleInputChange('reception', {...formData.reception, overallCompliance: e.target.value})}
                  >
                    <option value="">{formLabel('select_placeholder')}</option>
                    <option value="">{formLabel('select_placeholder')}</option>
                    <option value="conforme">{formLabel('compliance_levels.compliant')}</option>
                    <option value="conforme_avec_réserves">{formLabel('compliance_levels.compliant_with_reserves')}</option>
                    <option value="non_conforme">{formLabel('compliance_levels.non_compliant')}</option>
                    <option value="en_attente">{formLabel('compliance_levels.pending_correction')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('execution_quality')}</label>
                  <CustomSelect
                    value={formData.reception?.executionQuality || 'satisfaisante'}
                    onChange={(e) => handleInputChange('reception', {...formData.reception, executionQuality: e.target.value})}
                  >
                    <option value="excellente">{formLabel('rating_levels.excellent')}</option>
                    <option value="très_bonne">{formLabel('rating_levels.very_good')}</option>
                    <option value="bonne">{formLabel('rating_levels.good')}</option>
                    <option value="satisfaisante">{formLabel('rating_levels.satisfactory')}</option>
                    <option value="insuffisante">{formLabel('rating_levels.insufficient')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('timeline_compliance')}</label>
                  <CustomSelect
                    value={formData.receptionTimelineCompliance || ''}
                    onChange={(e) => handleInputChange('reception', {...formData.reception, timelineCompliance: e.target.value})}
                  >
                    <option value="respecté">{formLabel('timeline_statuses.respected')}</option>
                    <option value="retard_mineur">{formLabel('timeline_statuses.minor_delay')}</option>
                    <option value="retard_important">{formLabel('timeline_statuses.major_delay')}</option>
                    <option value="avance">{formLabel('timeline_statuses.ahead')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('reservation_count')}</label>
                  <CustomNumberInput
                    value={formData.reception?.reservationCount || 0}
                    onChange={(val) => handleInputChange('reception', {...formData.reception, reservationCount: parseInt(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('reservation_deadline')}</label>
                  <CustomNumberInput
                    value={formData.reception?.reservationDeadline || 30}
                    onChange={(val) => handleInputChange('reception', {...formData.reception, reservationDeadline: parseInt(val) || 30})}
                    placeholder={formLabel('ph_days_with_unit')}
                    min={0}
                    step={1}
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.reception?.documentsProvided || false}
                      onChange={(e) => handleInputChange('reception', {...formData.reception, documentsProvided: e.target.checked})}
                    />
                    {formLabel('technical_file_provided')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.reception?.warrantyActivated || false}
                      onChange={(e) => handleInputChange('reception', {...formData.reception, warrantyActivated: e.target.checked})}
                    />
                    {formLabel('warranty_activated')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.reception?.maintenanceHandover || false}
                      onChange={(e) => handleInputChange('reception', {...formData.reception, maintenanceHandover: e.target.checked})}
                    />
                    {formLabel('maintenance_handover_done')}
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('general_observations')}</label>
                <textarea
                  value={formData.reception?.observations || ''}
                  onChange={(e) => handleInputChange('reception', {...formData.reception, observations: e.target.value})}
                  placeholder={formLabel('ph_work_reception_observations')}
                  rows="3"
                />
              </div>
            </div>
          )}

          {selectedType.id === 'intervention-report' && (
            <div className="form-section">
              <h3 className="section-title" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                <FileText size={20} color={getIconColor()} />
                {formLabel('intervention_report_title')}
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('technician_intervening')}</label>
                  <input
                    type="text"
                    value={formData.technician?.name || ''}
                    onChange={(e) => handleInputChange('technician', {...formData.technician, name: e.target.value})}
                    placeholder={formLabel('ph_technician_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('qualification_label')}</label>
                  <CustomSelect
                    value={formData.technician?.qualification || 'technicien'}
                    onChange={(e) => handleInputChange('technician', {...formData.technician, qualification: e.target.value})}
                  >
                    <option value="technicien">{formLabel('technician_roles.technician')}</option>
                    <option value="technicien_supérieur">{formLabel('technician_roles.senior_technician')}</option>
                    <option value="ingénieur">{formLabel('technician_qualifications.engineer')}</option>
                    <option value="expert">{formLabel('technician_qualifications.expert')}</option>
                    <option value="spécialiste">{formLabel('technician_qualifications.specialist')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('company_label')}</label>
                  <input
                    type="text"
                    value={formData.technician?.company || ''}
                    onChange={(e) => handleInputChange('technician', {...formData.technician, company: e.target.value})}
                    placeholder={formLabel('ph_company_legal_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('client_site_label')}</label>
                  <input
                    type="text"
                    value={formData.client?.name || ''}
                    onChange={(e) => handleInputChange('client', {...formData.client, name: e.target.value})}
                    placeholder={formLabel('ph_client_site_location')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('client_contact')}</label>
                  <input
                    type="text"
                    value={formData.client?.contact || ''}
                    onChange={(e) => handleInputChange('client', {...formData.client, contact: e.target.value})}
                    placeholder={formLabel('ph_client_contact_person')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('intervention_location')}</label>
                  <input
                    type="text"
                    value={formData.intervention?.location || ''}
                    onChange={(e) => handleInputChange('intervention', {...formData.intervention, location: e.target.value})}
                    placeholder={formLabel('ph_precise_address')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('intervention_date')}</label>
                  <CustomDatePicker
                      value={formData.intervention?.date || ''}
                      onChange={(val) => handleInputChange('intervention', {...formData.intervention, date: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('start_time')}</label>
                  <input
                    type="time"
                    value={formData.intervention?.startTime || ''}
                    onChange={(e) => handleInputChange('intervention', {...formData.intervention, startTime: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('end_time')}</label>
                  <input
                    type="time"
                    value={formData.intervention?.endTime || ''}
                    onChange={(e) => handleInputChange('intervention', {...formData.intervention, endTime: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('intervention_type')}</label>
                  <CustomSelect
                    value={formData.intervention?.type || 'maintenance'}
                    onChange={(e) => handleInputChange('intervention', {...formData.intervention, type: e.target.value})}
                  >
                    <option value="maintenance">{formLabel('service_types.preventive_maintenance')}</option>
                    <option value="dépannage">{formLabel('intervention_types_options.troubleshooting')}</option>
                    <option value="installation">{formLabel('project_types.installation')}</option>
                    <option value="diagnostic">{formLabel('intervention_types_options.diagnostic')}</option>
                    <option value="mise_en_service">{formLabel('intervention_types_options.commissioning')}</option>
                    <option value="contrôle">{formLabel('intervention_types_options.technical_inspection')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('urgency')}</label>
                  <CustomSelect
                    value={formData.intervention?.urgency || ''}
                    onChange={(e) => handleInputChange('intervention', {...formData.intervention, urgency: e.target.value})}
                  >
                    <option value="">{formLabel('select_placeholder')}</option>
                    <option value="normale">{formLabel('priority_levels.normal')}</option>
                    <option value="urgente">{formLabel('priority_levels.urgent')}</option>
                    <option value="critique">{formLabel('project_priorities.critical')}</option>
                    <option value="planifiée">{formLabel('intervention_priorities.planned')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('equipment_concerned')}</label>
                  <input
                    type="text"
                    value={formData.equipment?.name || ''}
                    onChange={(e) => handleInputChange('equipment', {...formData.equipment, name: e.target.value})}
                    placeholder={formLabel('ph_equipment_name_type')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('reference_series')}</label>
                  <input
                    type="text"
                    value={formData.equipment?.reference || ''}
                    onChange={(e) => handleInputChange('equipment', {...formData.equipment, reference: e.target.value})}
                    placeholder={formLabel('ph_serial_lot_number')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('brand_model')}</label>
                  <input
                    type="text"
                    value={formData.equipment?.brand || ''}
                    onChange={(e) => handleInputChange('equipment', {...formData.equipment, brand: e.target.value})}
                    placeholder={formLabel('ph_commercial_model_ref')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('initial_state')}</label>
                  <CustomSelect
                    value={formData.equipment?.initialState || 'fonctionnel'}
                    onChange={(e) => handleInputChange('equipment', {...formData.equipment, initialState: e.target.value})}
                  >
                    <option value="fonctionnel">{formLabel('specification_types.functional')}</option>
                    <option value="défaillant">{formLabel('equipment_states.faulty')}</option>
                    <option value="arrêté">{formLabel('equipment_states.stopped')}</option>
                    <option value="dégradé">{formLabel('equipment_states.degraded')}</option>
                    <option value="neuf">{formLabel('equipment_states.new_installation')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('final_state')}</label>
                  <CustomSelect
                    value={formData.equipment?.finalState || 'fonctionnel'}
                    onChange={(e) => handleInputChange('equipment', {...formData.equipment, finalState: e.target.value})}
                  >
                    <option value="fonctionnel">{formLabel('specification_types.functional')}</option>
                    <option value="réparé">{formLabel('equipment_states.repaired')}</option>
                    <option value="installé">{formLabel('equipment_states.installed')}</option>
                    <option value="en_attente">{formLabel('equipment_states.waiting_parts')}</option>
                    <option value="non_réparable">{formLabel('equipment_states.irreparable')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('intervention_duration_hours')}</label>
                  <CustomNumberInput
                    value={formData.intervention?.duration || 0}
                    onChange={(val) => handleInputChange('intervention', {...formData.intervention, duration: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={0.5}
                    decimals={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('intervention_cost')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.intervention?.cost || 0}
                    onChange={(val) => handleInputChange('intervention', {...formData.intervention, cost: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.intervention?.partsUsed || false}
                      onChange={(e) => handleInputChange('intervention', {...formData.intervention, partsUsed: e.target.checked})}
                    />
                    {formLabel('parts_used')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.intervention?.clientSatisfied || false}
                      onChange={(e) => handleInputChange('intervention', {...formData.intervention, clientSatisfied: e.target.checked})}
                    />
                    {formLabel('client_satisfied')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.intervention?.followUpRequired || false}
                      onChange={(e) => handleInputChange('intervention', {...formData.intervention, followUpRequired: e.target.checked})}
                    />
                    {formLabel('follow_up_required')}
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('problem_description')}</label>
                <textarea
                  value={formData.intervention?.problemDescription || ''}
                  onChange={(e) => handleInputChange('intervention', {...formData.intervention, problemDescription: e.target.value})}
                  placeholder={formLabel('ph_problem_description')}
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('work_performed')}</label>
                <textarea
                  value={formData.intervention?.workPerformed || ''}
                  onChange={(e) => handleInputChange('intervention', {...formData.intervention, workPerformed: e.target.value})}
                  placeholder={formLabel('ph_work_performed_description')}
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('recommendations')}</label>
                <textarea
                  value={formData.intervention?.recommendations || ''}
                  onChange={(e) => handleInputChange('intervention', {...formData.intervention, recommendations: e.target.value})}
                  placeholder={formLabel('ph_maintenance_recommendations')}
                  rows="2"
                />
              </div>
            </div>
          )}
          {selectedType.id === 'non-compliance' && (
            <div className="form-section">
              <h3 className="section-title" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                <FileCheck size={20} color={getIconColor()} />
                {formLabel('non_compliance_sheet_title')}
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('detector_label')}</label>
                  <input
                    type="text"
                    value={formData.detector?.name || ''}
                    onChange={(e) => handleInputChange('detector', {...formData.detector, name: e.target.value})}
                    placeholder={formLabel('ph_detector_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('detector_role')}</label>
                  <CustomSelect
                    value={formData.detector?.role || ''}
                    onChange={(e) => handleInputChange('detector', {...formData.detector, role: e.target.value})}
                  >
                    <option value="contrôleur">{formLabel('detector_roles.quality_controller')}</option>
                    <option value="superviseur">{formLabel('detector_roles.supervisor')}</option>
                    <option value="technicien">{formLabel('technician_roles.technician')}</option>
                    <option value="client">{formLabel('detector_roles.client')}</option>
                    <option value="auditeur">{formLabel('detector_roles.auditor')}</option>
                    <option value="autre">{formLabel('other')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('concerned_service')}</label>
                  <input
                    type="text"
                    value={formData.detector?.department || ''}
                    onChange={(e) => handleInputChange('detector', {...formData.detector, department: e.target.value})}
                    placeholder={formLabel('ph_concerned_service')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('detection_date')}</label>
                  <CustomDatePicker
                      value={formData.nonCompliance?.detectionDate || ''}
                      onChange={(val) => handleInputChange('nonCompliance', {...formData.nonCompliance, detectionDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('detection_location')}</label>
                  <input
                    type="text"
                    value={formData.nonCompliance?.location || ''}
                    onChange={(e) => handleInputChange('nonCompliance', {...formData.nonCompliance, location: e.target.value})}
                    placeholder={formLabel('ph_zone_workshop')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('non_compliance_type')}</label>
                  <CustomSelect
                    value={formData.nonCompliance?.type || ''}
                    onChange={(e) => handleInputChange('nonCompliance', {...formData.nonCompliance, type: e.target.value})}
                  >
                    <option value="qualité">{formLabel('non_compliance_types.product_quality')}</option>
                    <option value="processus">{formLabel('non_compliance_types.process')}</option>
                    <option value="documentation">{formLabel('non_compliance_types.documentation')}</option>
                    <option value="sécurité">{formLabel('specification_types.security')}</option>
                    <option value="environnement">{formLabel('non_compliance_types.environment')}</option>
                    <option value="livraison">{formLabel('non_compliance_types.delivery')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('category')}</label>
                  <CustomSelect
                    value={formData.nonCompliance?.category || 'mineure'}
                    onChange={(e) => handleInputChange('nonCompliance', {...formData.nonCompliance, category: e.target.value})}
                  >
                    <option value="critique">{formLabel('project_priorities.critical')}</option>
                    <option value="majeure">{formLabel('non_compliance_severities.major')}</option>
                    <option value="mineure">{formLabel('non_compliance_severities.minor')}</option>
                    <option value="observation">{formLabel('non_compliance_severities.observation')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('criticality_label')}</label>
                  <CustomSelect
                    value={formData.nonCompliance?.severity || 'moyenne'}
                    onChange={(e) => handleInputChange('nonCompliance', {...formData.nonCompliance, severity: e.target.value})}
                  >
                    <option value="très_élevée">{formLabel('criticality_levels.very_high')}</option>
                    <option value="élevée">{formLabel('criticality_levels.high')}</option>
                    <option value="moyenne">{formLabel('criticality_levels.medium')}</option>
                    <option value="faible">{formLabel('criticality_levels.low')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('concerned_product')}</label>
                  <input
                    type="text"
                    value={formData.nonCompliance?.product || ''}
                    onChange={(e) => handleInputChange('nonCompliance', {...formData.nonCompliance, product: e.target.value})}
                    placeholder={formLabel('ph_product_designation')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('reference_lot')}</label>
                  <input
                    type="text"
                    value={formData.nonCompliance?.reference || ''}
                    onChange={(e) => handleInputChange('nonCompliance', {...formData.nonCompliance, reference: e.target.value})}
                    placeholder={formLabel('ph_batch_ref')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('affected_quantity')}</label>
                  <CustomNumberInput
                    value={formData.nonCompliance?.affectedQuantity || 0}
                    onChange={(val) => handleInputChange('nonCompliance', {...formData.nonCompliance, affectedQuantity: parseInt(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('standard_specification')}</label>
                  <input
                    type="text"
                    value={formData.nonCompliance?.standard || ''}
                    onChange={(e) => handleInputChange('nonCompliance', {...formData.nonCompliance, standard: e.target.value})}
                    placeholder={formLabel('ph_noncompliance_norm_ref')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('processing_manager')}</label>
                  <input
                    type="text"
                    value={formData.treatment?.responsible || ''}
                    onChange={(e) => handleInputChange('treatment', {...formData.treatment, responsible: e.target.value})}
                    placeholder={formLabel('ph_manager_full_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('processing_deadline')}</label>
                  <CustomDatePicker
                      value={formData.treatment?.deadline || ''}
                      onChange={(val) => handleInputChange('treatment', {...formData.treatment, deadline: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('status_label')}</label>
                  <CustomSelect
                    value={formData.treatment?.status || 'ouverte'}
                    onChange={(e) => handleInputChange('treatment', {...formData.treatment, status: e.target.value})}
                  >
                    <option value="ouverte">{formLabel('nc_statuses.open')}</option>
                    <option value="en_cours">{formLabel('nc_statuses.in_progress')}</option>
                    <option value="fermée">{formLabel('nc_statuses.closed')}</option>
                    <option value="vérifiée">{formLabel('nc_statuses.verified')}</option>
                    <option value="annulée">{formLabel('nc_statuses.cancelled')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('estimated_cost')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.treatment?.estimatedCost || 0}
                    onChange={(val) => handleInputChange('treatment', {...formData.treatment, estimatedCost: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.nonCompliance?.clientNotified || false}
                      onChange={(e) => handleInputChange('nonCompliance', {...formData.nonCompliance, clientNotified: e.target.checked})}
                    />
                    {formLabel('client_informed')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.nonCompliance?.productBlocked || false}
                      onChange={(e) => handleInputChange('nonCompliance', {...formData.nonCompliance, productBlocked: e.target.checked})}
                    />
                    {formLabel('product_blocked')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.treatment?.immediateAction || false}
                      onChange={(e) => handleInputChange('treatment', {...formData.treatment, immediateAction: e.target.checked})}
                    />
                    {formLabel('immediate_action_required')}
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('non_compliance_description')}</label>
                <textarea
                  value={formData.nonCompliance?.description || ''}
                  onChange={(e) => handleInputChange('nonCompliance', {...formData.nonCompliance, description: e.target.value})}
                  placeholder={formLabel('ph_non_compliance_description')}
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('probable_cause')}</label>
                <textarea
                  value={formData.nonCompliance?.probableCause || ''}
                  onChange={(e) => handleInputChange('nonCompliance', {...formData.nonCompliance, probableCause: e.target.value})}
                  placeholder={formLabel('ph_probable_cause_analysis')}
                  rows="2"
                />
              </div>
              
              <div className="form-group">
                <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('corrective_actions')}</label>
                <textarea
                  value={formData.treatment?.correctiveActions || ''}
                  onChange={(e) => handleInputChange('treatment', {...formData.treatment, correctiveActions: e.target.value})}
                  placeholder={formLabel('ph_corrective_actions')}
                  rows="3"
                />
              </div>
            </div>
          )}
          {/* DOCUMENTS LÉGAUX */}
          {selectedType.id === 'tax-certificate' && (
          <div className="form-section">
            <h3 className="section-title" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                <FileText size={20} color={getIconColor()} />
                {formLabel('tax_certificate_title')}
            </h3>

              <div className="form-grid">
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('company_name')}</label>
                  <input
                    type="text"
                    value={formData.applicant?.name || ''}
                    onChange={(e) => handleInputChange('applicant', {...formData.applicant, name: e.target.value})}
                    placeholder={t('document_creation.form.company_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('legal_form')}</label>
                  <CustomSelect
                    value={formData.applicant?.legalForm || ''}
                    onChange={(e) => handleInputChange('applicant', {...formData.applicant, legalForm: e.target.value})}
                  >
                    <option value="SARL">SARL</option>
                    <option value="SPA">SPA</option>
                    <option value="EURL">EURL</option>
                    <option value="SNC">SNC</option>
                    <option value="entreprise_individuelle">{formLabel('legal_forms.individual_enterprise')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('nif')}</label>
                  <input
                    type="text"
                    value={formData.applicant?.nif || ''}
                    onChange={(e) => handleInputChange('applicant', {...formData.applicant, nif: e.target.value})}
                    placeholder={formLabel('ph_fiscal_id')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('nis')}</label>
                  <input
                    type="text"
                    value={formData.applicant?.nis || ''}
                    onChange={(e) => handleInputChange('applicant', {...formData.applicant, nis: e.target.value})}
                    placeholder={formLabel('ph_nis_number')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('tax_article_ai')}</label>
                  <input
                    type="text"
                    value={formData.applicant?.ai || ''}
                    onChange={(e) => handleInputChange('applicant', {...formData.applicant, ai: e.target.value})}
                    placeholder={formLabel('ph_tax_article_number')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('rc')}</label>
                  <input
                    type="text"
                    value={formData.applicant?.commercialRegister || ''}
                    onChange={(e) => handleInputChange('applicant', {...formData.applicant, commercialRegister: e.target.value})}
                    placeholder={formLabel('ph_rc_number')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('share_capital')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.applicant?.capital || 0}
                    onChange={(val) => handleInputChange('applicant', {...formData.applicant, capital: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={1000}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('activity_sector')}</label>
                  <input
                    type="text"
                    value={formData.applicant?.activity || ''}
                    onChange={(e) => handleInputChange('applicant', {...formData.applicant, activity: e.target.value})}
                    placeholder={formLabel('ph_activity_description')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('certificate_type_label')}</label>
                  <CustomSelect
                    value={formData.certificateDetails?.type || 'fiscal'}
                    onChange={(e) => handleInputChange('certificateDetails', {...formData.certificateDetails, type: e.target.value})}
                  >
                    <option value="fiscal">{formLabel('tax_certificate_title')}</option>
                    <option value="non_redevable">{formLabel('tax_certificate_types.vat_exempt')}</option>
                    <option value="exonération">{formLabel('tax_certificate_types.tax_exemption')}</option>
                    <option value="régularité">{formLabel('tax_certificate_types.tax_compliance')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('request_purpose')}</label>
                  <CustomSelect
                    value={formData.certificateDetails?.purpose || ''}
                    onChange={(e) => handleInputChange('certificateDetails', {...formData.certificateDetails, purpose: e.target.value})}
                  >
                    <option value="marchés_publics">{formLabel('tax_request_purposes.public_procurement')}</option>
                    <option value="appel_offres">{formLabel('tax_request_purposes.tender')}</option>
                    <option value="banque">{formLabel('tax_request_purposes.banking')}</option>
                    <option value="partenariat">{formLabel('tax_request_purposes.partnership')}</option>
                    <option value="subvention">{formLabel('tax_request_purposes.subsidy')}</option>
                    <option value="autre">{formLabel('other')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('fiscal_year')}</label>
                  <CustomNumberInput
                    value={formData.taxPeriod.taxYear || new Date().getFullYear()}
                    onChange={(val) => handleInputChange('taxPeriod', {...formData.taxPeriod, taxYear: parseInt(val) || new Date().getFullYear()})}
                    placeholder={new Date().getFullYear().toString()}
                    min={2000}
                    max={2100}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('declaration_status')}</label>
                  <CustomSelect
                    value={formData.taxCompliance?.declarationStatus || ''}
                    onChange={(e) => handleInputChange('taxCompliance', {...formData.taxCompliance, declarationStatus: e.target.value})}
                  >
                    <option value="à_jour">{formLabel('declaration_statuses.up_to_date')}</option>
                    <option value="en_retard">{formLabel('statuses.overdue')}</option>
                    <option value="non_déposées">{formLabel('declaration_statuses.not_filed')}</option>
                    <option value="partielles">{formLabel('declaration_statuses.partial')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('payment_status')}</label>
                  <CustomSelect
                    value={formData.taxCompliance?.paymentStatus || ''}
                    onChange={(e) => handleInputChange('taxCompliance', {...formData.taxCompliance, paymentStatus: e.target.value})}
                  >
                    <option value="à_jour">{formLabel('declaration_statuses.up_to_date')}</option>
                    <option value="arriérés">{formLabel('payment_compliance_statuses.arrears')}</option>
                    <option value="échéancier">{formLabel('payment_compliance_statuses.payment_plan')}</option>
                    <option value="contentieux">{formLabel('payment_compliance_statuses.dispute')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('turnover')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.businessData?.turnover || 0}
                    onChange={(val) => handleInputChange('businessData', {...formData.businessData, turnover: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={1000}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('employee_count')}</label>
                  <CustomNumberInput
                    value={formData.businessData?.employeeCount || 0}
                    onChange={(val) => handleInputChange('businessData', {...formData.businessData, employeeCount: parseInt(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('irg_due')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.taxDetails?.irg?.due || 0}
                    onChange={(val) => handleInputChange('taxDetails', {...formData.taxDetails, irg: {...formData.taxDetails.irg, due: parseFloat(val) || 0}})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('ibs_due')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.taxDetails?.ibs?.due || 0}
                    onChange={(val) => handleInputChange('taxDetails', {...formData.taxDetails, ibs: {...formData.taxDetails.ibs, due: parseFloat(val) || 0}})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('tva_due')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.taxDetails?.tva?.due || 0}
                    onChange={(val) => handleInputChange('taxDetails', {...formData.taxDetails, tva: {...formData.taxDetails.tva, due: parseFloat(val) || 0}})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('issuing_authority')}</label>
                  <input
                    type="text"
                    value={formData.issuance?.issuingAuthority || formLabel('tax_authority_dgi')}
                    onChange={(e) => handleInputChange('issuance', {...formData.issuance, issuingAuthority: e.target.value})}
                    placeholder={formLabel('ph_tax_authority_dgi')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('issuing_office')}</label>
                  <input
                    type="text"
                    value={formData.issuance?.issuingOffice || ''}
                    onChange={(e) => handleInputChange('issuance', {...formData.issuance, issuingOffice: e.target.value})}
                    placeholder={formLabel('ph_office_center_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('validity_period')}</label>
                  <CustomSelect
                    value={formData.issuance?.validityPeriod || '12 mois'}
                    onChange={(e) => handleInputChange('issuance', {...formData.issuance, validityPeriod: e.target.value})}
                  >
                    <option value="3 mois">{formLabel('validity_period_options.3m')}</option>
                    <option value="6 mois">{formLabel('validity_period_options.6m')}</option>
                    <option value="12 mois">{formLabel('validity_period_options.12m')}</option>
                    <option value="permanent">{formLabel('validity_periods.permanent')}</option>
                  </CustomSelect>
                </div>
              </div>
            </div>
          )}

          {selectedType.id === 'work-certificate' && (
            <div className="form-section">
              <h3 className="section-title" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                <CheckCircle size={20} color={getIconColor()} />
                {formLabel('work_completion_certificate_title')}
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('company_name')}</label>
                  <input
                    type="text"
                    value={formData.contractor?.name || ''}
                    onChange={(e) => handleInputChange('contractor', {...formData.contractor, name: e.target.value})}
                    placeholder={formLabel('ph_contractor_legal_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('legal_form')}</label>
                  <CustomSelect
                    value={formData.contractor?.legalForm || ''}
                    onChange={(e) => handleInputChange('contractor', {...formData.contractor, legalForm: e.target.value})}
                  >
                    <option value="SARL">SARL</option>
                    <option value="SPA">SPA</option>
                    <option value="EURL">EURL</option>
                    <option value="entreprise_individuelle">{formLabel('legal_forms.individual_enterprise')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('nif_entrepreneur')}</label>
                  <input
                    type="text"
                    value={formData.contractor?.nif || ''}
                    onChange={(e) => handleInputChange('contractor', {...formData.contractor, nif: e.target.value})}
                    placeholder={formLabel('ph_fiscal_id')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('legal_representative')}</label>
                  <input
                    type="text"
                    value={formData.contractor?.legalRepresentative || ''}
                    onChange={(e) => handleInputChange('contractor', {...formData.contractor, legalRepresentative: e.target.value})}
                    placeholder={formLabel('ph_manager_director_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('technical_director')}</label>
                  <input
                    type="text"
                    value={formData.contractor?.technicalDirector || ''}
                    onChange={(e) => handleInputChange('contractor', {...formData.contractor, technicalDirector: e.target.value})}
                    placeholder={formLabel('ph_technical_director_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('client_name_label')}</label>
                  <input
                    type="text"
                    value={formData.client?.name || ''}
                    onChange={(e) => handleInputChange('client', {...formData.client, name: e.target.value})}
                    placeholder={formLabel('ph_project_owner')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('client_project_manager')}</label>
                  <input
                    type="text"
                    value={formData.client?.projectManager || ''}
                    onChange={(e) => handleInputChange('client', {...formData.client, projectManager: e.target.value})}
                    placeholder={formLabel('ph_client_project_manager')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('contract_ref')}</label>
                  <input
                    type="text"
                    value={formData.contractDetails?.reference || ''}
                    onChange={(e) => handleInputChange('contractDetails', {...formData.contractDetails, reference: e.target.value})}
                    placeholder={formLabel('ph_market_reference')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('signature_date')}</label>
                  <CustomDatePicker
                      value={formData.contractDetails?.signatureDate || ''}
                      onChange={(val) => handleInputChange('contractDetails', {...formData.contractDetails, signatureDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('start_date')}</label>
                  <CustomDatePicker
                      value={formData.contractDetails?.startDate || ''}
                      onChange={(val) => handleInputChange('contractDetails', {...formData.contractDetails, startDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('completion_date')}</label>
                  <CustomDatePicker
                      value={formData.contractDetails?.actualCompletionDate || ''}
                      onChange={(val) => handleInputChange('contractDetails', {...formData.contractDetails, actualCompletionDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('total_contract_amount')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.contractDetails?.contractAmount || 0}
                    onChange={(val) => handleInputChange('contractDetails', {...formData.contractDetails, contractAmount: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('work_category')}</label>
                  <CustomSelect
                    value={formData.workDescription?.category || 'construction'}
                    onChange={(e) => handleInputChange('workDescription', {...formData.workDescription, category: e.target.value})}
                  >
                    <option value="construction">{formLabel('work_types.construction')}</option>
                    <option value="rénovation">{formLabel('work_types.renovation')}</option>
                    <option value="infrastructure">{formLabel('work_types.infrastructure')}</option>
                    <option value="génie_civil">{formLabel('building_types.infrastructure')}</option>
                    <option value="Équipement">{formLabel('work_types.equipment')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('building_type')}</label>
                  <CustomSelect
                    value={formData.workDescription?.type || ''}
                    onChange={(e) => handleInputChange('workDescription', {...formData.workDescription, type: e.target.value})}
                  >
                    <option value="bâtiment">{formLabel('building_types.building')}</option>
                    <option value="">{formLabel('select_placeholder')}</option>
                    <option value="route">{formLabel('building_types.route')}</option>
                    <option value="pont">{formLabel('building_types.bridge')}</option>
                    <option value="usine">{formLabel('building_types.factory')}</option>
                    <option value="logement">{formLabel('building_types.housing')}</option>
                    <option value="bureau">{formLabel('building_types.office')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('total_surface_area')}</label>
                  <CustomNumberInput
                    value={formData.workDescription?.totalSurface || 0}
                    onChange={(val) => handleInputChange('workDescription', {...formData.workDescription, totalSurface: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('level_count')}</label>
                  <CustomNumberInput
                    value={formData.workDescription?.numberOfLevels || 0}
                    onChange={(val) => handleInputChange('workDescription', {...formData.workDescription, numberOfLevels: parseInt(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('compliance_standards')}</label>
                  <CustomSelect
                    value={formData.compliance?.conformityStandards || legalDefault('technical_standards_value')}
                    onChange={(e) => handleInputChange('compliance', {...formData.compliance, conformityStandards: e.target.value})}
                  >
                    <option value={legalDefault('technical_standards_value')}>{formLabel('compliance_standard_options.saudi_saso')}</option>
                    <option value="DTR + normes internationales">{formLabel('compliance_standard_options.dtr_international')}</option>
                    <option value="normes européennes">{formLabel('compliance_standard_options.european')}</option>
                    <option value="normes françaises">{formLabel('compliance_standard_options.french')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('overall_quality_level')}</label>
                  <CustomSelect
                    value={formData.qualityAssessment?.overallLevel || 'conforme'}
                    onChange={(e) => handleInputChange('qualityAssessment', {...formData.qualityAssessment, overallLevel: e.target.value})}
                  >
                    <option value="">{formLabel('select_placeholder')}</option>
                    <option value="excellent">{formLabel('quality_levels.excellent')}</option>
                    <option value="très_bon">{formLabel('quality_levels.very_good')}</option>
                    <option value="bon">{formLabel('quality_levels.good')}</option>
                    <option value="">{formLabel('select_placeholder')}</option>
                    <option value="">{formLabel('select_placeholder')}</option>
                    <option value="conforme">{formLabel('compliance_levels.compliant')}</option>
                    <option value="acceptable">{formLabel('rating_levels.acceptable')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('materials_quality')}</label>
                  <CustomSelect
                    value={formData.qualityAssessment?.materialQuality || 'satisfaisante'}
                    onChange={(e) => handleInputChange('qualityAssessment', {...formData.qualityAssessment, materialQuality: e.target.value})}
                  >
                    <option value="excellente">{formLabel('rating_levels.excellent')}</option>
                    <option value="très_bonne">{formLabel('rating_levels.very_good')}</option>
                    <option value="bonne">{formLabel('rating_levels.good')}</option>
                    <option value="satisfaisante">{formLabel('rating_levels.satisfactory')}</option>
                    <option value="insuffisante">{formLabel('rating_levels.insufficient')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('structural_warranty')}</label>
                  <CustomSelect
                    value={formData.warranties?.structuralWarranty || '10 ans'}
                    onChange={(e) => handleInputChange('warranties', {...formData.warranties, structuralWarranty: e.target.value})}
                  >
                    <option value="5 ans">{formLabel('warranty_periods.5y')}</option>
                    <option value="10 ans">{formLabel('warranty_periods.10y')}</option>
                    <option value="15 ans">{formLabel('warranty_periods.15y')}</option>
                    <option value="20 ans">{formLabel('warranty_periods.20y')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('client_satisfaction')}</label>
                  <CustomSelect
                    value={formData.performance?.clientSatisfaction || 'satisfait'}
                    onChange={(e) => handleInputChange('performance', {...formData.performance, clientSatisfaction: e.target.value})}
                  >
                    <option value="très_satisfait">{formLabel('rating_levels.very_satisfied')}</option>
                    <option value="satisfait">{formLabel('rating_levels.satisfied')}</option>
                    <option value="acceptable">{formLabel('rating_levels.acceptable')}</option>
                    <option value="insatisfait">{formLabel('rating_levels.dissatisfied')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.performance?.timelyCompletion || false}
                      onChange={(e) => handleInputChange('performance', {...formData.performance, timelyCompletion: e.target.checked})}
                    />
                    {formLabel('timely_completion')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.performance?.budgetRespected || false}
                      onChange={(e) => handleInputChange('performance', {...formData.performance, budgetRespected: e.target.checked})}
                    />
                    {formLabel('budget_respected')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.compliance?.environmentalCompliance || false}
                      onChange={(e) => handleInputChange('compliance', {...formData.compliance, environmentalCompliance: e.target.checked})}
                    />
                    {formLabel('environmental_compliance')}
                  </label>
                </div>
              </div>
            </div>
          )}
          {selectedType.id === 'legal-notice' && (
            <div className="form-section">
              <h3 className="section-title" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                <FileCheck size={20} color={getIconColor()} />
                {formLabel('legal_notice_title')}
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('sender_name')}</label>
                  <input
                    type="text"
                    value={formData.parties?.sender?.name || ''}
                    onChange={(e) => handleInputChange('parties', {...formData.parties, sender: {...formData.parties.sender, name: e.target.value}})}
                    placeholder={formLabel('ph_sender_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('sender_legal_form')}</label>
                  <CustomSelect
                    value={formData.parties?.sender?.legalForm || ''}
                    onChange={(e) => handleInputChange('parties', {...formData.parties, sender: {...formData.parties.sender, legalForm: e.target.value}})}
                  >
                    <option value="SARL">SARL</option>
                    <option value="SPA">SPA</option>
                    <option value="EURL">EURL</option>
                    <option value="personne_physique">{formLabel('legal_entity_forms.natural_person')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('sender_nif')}</label>
                  <input
                    type="text"
                    value={formData.parties?.sender?.nif || ''}
                    onChange={(e) => handleInputChange('parties', {...formData.parties, sender: {...formData.parties.sender, nif: e.target.value}})}
                    placeholder={formLabel('ph_fiscal_id')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('legal_representative')}</label>
                  <input
                    type="text"
                    value={formData.parties?.sender?.legalRepresentative || ''}
                    onChange={(e) => handleInputChange('parties', {...formData.parties, sender: {...formData.parties.sender, legalRepresentative: e.target.value}})}
                    placeholder={formLabel('ph_representative_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('lawyer_optional')}</label>
                  <input
                    type="text"
                    value={formData.parties?.sender?.lawyer || ''}
                    onChange={(e) => handleInputChange('parties', {...formData.parties, sender: {...formData.parties.sender, lawyer: e.target.value}})}
                    placeholder={formLabel('ph_lawyer_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('recipient_name')}</label>
                  <input
                    type="text"
                    value={formData.parties?.recipient?.name || ''}
                    onChange={(e) => handleInputChange('parties', {...formData.parties, recipient: {...formData.parties.recipient, name: e.target.value}})}
                    placeholder={formLabel('ph_recipient_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('recipient_address')}</label>
                  <input
                    type="text"
                    value={formData.parties?.recipient?.address || ''}
                    onChange={(e) => handleInputChange('parties', {...formData.parties, recipient: {...formData.parties.recipient, address: e.target.value}})}
                    placeholder={t('document_creation.form.full_address')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('notification_type')}</label>
                  <CustomSelect
                    value={formData.noticeDetails?.type || 'contractuelle'}
                    onChange={(e) => handleInputChange('noticeDetails', {...formData.noticeDetails, type: e.target.value})}
                  >
                    <option value="contractuelle">{formLabel('notification_types.contractual')}</option>
                    <option value="commerciale">{formLabel('notification_types.commercial')}</option>
                    <option value="administrative">{formLabel('notification_types.administrative')}</option>
                    <option value="judiciaire">{formLabel('notification_types.judicial')}</option>
                    <option value="règlementaire">{formLabel('notification_types.regulatory')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('category')}</label>
                  <CustomSelect
                    value={formData.noticeDetails?.category || 'mise_en_demeure'}
                    onChange={(e) => handleInputChange('noticeDetails', {...formData.noticeDetails, category: e.target.value})}
                  >
                    <option value="mise_en_demeure">{formLabel('payment_demand_title')}</option>
                    <option value="résiliation">{formLabel('notice_categories.termination')}</option>
                    <option value="convocation">{formLabel('notice_categories.summons')}</option>
                    <option value="sommation">{formLabel('notice_categories.injunction')}</option>
                    <option value="notification">{formLabel('notice_categories.simple_notification')}</option>
                    <option value="assignation">{formLabel('notice_categories.assignment')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('urgency_level')}</label>
                  <CustomSelect
                    value={formData.noticeDetails?.urgency || ''}
                    onChange={(e) => handleInputChange('noticeDetails', {...formData.noticeDetails, urgency: e.target.value})}
                  >
                    <option value="">{formLabel('select_placeholder')}</option>
                    <option value="normale">{formLabel('priority_levels.normal')}</option>
                    <option value="urgente">{formLabel('priority_levels.urgent')}</option>
                    <option value="très_urgente">{formLabel('rating_levels.very_urgent')}</option>
                    <option value="exceptionnelle">{formLabel('rating_levels.exceptional')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('main_subject')}</label>
                  <input
                    type="text"
                    value={formData.noticeDetails?.subject || ''}
                    onChange={(e) => handleInputChange('noticeDetails', {...formData.noticeDetails, subject: e.target.value})}
                    placeholder={formLabel('ph_notification_subject')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('contract_ref')}</label>
                  <input
                    type="text"
                    value={formData.noticeDetails?.relatedContract || ''}
                    onChange={(e) => handleInputChange('noticeDetails', {...formData.noticeDetails, relatedContract: e.target.value})}
                    placeholder={formLabel('ph_contract_or_po_number')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('legal_basis')}</label>
                  <input
                    type="text"
                    value={formData.legalFramework?.legalBasis || ''}
                    onChange={(e) => handleInputChange('legalFramework', {...formData.legalFramework, legalBasis: e.target.value})}
                    placeholder={formLabel('ph_legal_basis_article')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('competent_jurisdiction')}</label>
                  <CustomSelect
                    value={formData.legalFramework?.jurisdiction || legalDefault('courts')}
                    onChange={(e) => handleInputChange('legalFramework', {...formData.legalFramework, jurisdiction: e.target.value})}
                  >
                    <option value={legalDefault('courts')}>{formLabel('jurisdictions.saudi_courts')}</option>
                    <option value="Tribunal de commerce">{formLabel('jurisdictions.commercial_court')}</option>
                    <option value="Tribunal civil">{formLabel('jurisdictions.civil_court')}</option>
                    <option value="Conseil d'État">{formLabel('jurisdictions.council_of_state')}</option>
                    <option value="Arbitrage">{formLabel('jurisdictions.arbitration')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('applicable_law')}</label>
                  <CustomSelect
                    value={formData.legalFramework?.applicableLaw || legalDefault('applicable_law_full')}
                    onChange={(e) => handleInputChange('legalFramework', {...formData.legalFramework, applicableLaw: e.target.value})}
                  >
                    <option value={legalDefault('applicable_law_full')}>{formLabel('applicable_law_options.saudi_law')}</option>
                    <option value="Code civil">{formLabel('applicable_law_options.civil_code')}</option>
                    <option value="Code de commerce">{formLabel('applicable_law_options.commerce_code')}</option>
                    <option value="Code du travail">{formLabel('applicable_law_options.labor_code')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('delivery_method_mode_label')}</label>
                  <CustomSelect
                    value={formData.delivery?.method || ''}
                    onChange={(e) => handleInputChange('delivery', {...formData.delivery, method: e.target.value})}
                  >
                    <option value="">{formLabel('select_placeholder')}</option>
                    <option value="recommandé">{formLabel('notice_delivery_methods.registered_ack')}</option>
                    <option value="huissier">{formLabel('notice_delivery_methods.bailiff')}</option>
                    <option value="main_propre">{formLabel('notice_delivery_methods.hand_delivery')}</option>
                    <option value="Électronique">{formLabel('notice_delivery_methods.electronic')}</option>
                    <option value="affichage">{formLabel('notice_delivery_methods.legal_posting')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('response_deadline_days')}</label>
                  <CustomNumberInput
                    value={formData.followUp?.responseDeadline || ''}
                    onChange={(val) => handleInputChange('followUp', {...formData.followUp, responseDeadline: val})}
                    placeholder={formLabel('ph_days_count')}
                    min={0}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('alternative_resolution')}</label>
                  <CustomSelect
                    value={formData.followUp?.alternativeResolution || ''}
                    onChange={(e) => handleInputChange('followUp', {...formData.followUp, alternativeResolution: e.target.value})}
                  >
                    <option value="médiation">{formLabel('resolution_methods.mediation')}</option>
                    <option value="conciliation">{formLabel('resolution_methods.conciliation')}</option>
                    <option value="arbitrage">{formLabel('resolution_methods.arbitration')}</option>
                    <option value="négociation">{formLabel('resolution_methods.direct_negotiation')}</option>
                    <option value="aucune">{t('document_creation.form.option_none')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.delivery?.recipientSignature || false}
                      onChange={(e) => handleInputChange('delivery', {...formData.delivery, recipientSignature: e.target.checked})}
                    />
                    {formLabel('recipient_signature_required')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.delivery?.witnessRequired || false}
                      onChange={(e) => handleInputChange('delivery', {...formData.delivery, witnessRequired: e.target.checked})}
                    />
                    {formLabel('witness_required')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.followUp?.responseRequired || false}
                      onChange={(e) => handleInputChange('followUp', {...formData.followUp, responseRequired: e.target.checked})}
                    />
                    {formLabel('response_required')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.authentication?.officialStamp || false}
                      onChange={(e) => handleInputChange('authentication', {...formData.authentication, officialStamp: e.target.checked})}
                    />
                    {formLabel('official_stamp_required')}
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('request_reason')}</label>
                <textarea
                  value={formData.certificateDetails?.requestReason || ''}
                  onChange={(e) => handleInputChange('certificateDetails', {...formData.certificateDetails, requestReason: e.target.value})}
                  placeholder={formLabel('ph_legal_notice_reason')}
                  rows="4"
                />
              </div>
            </div>
          )}
          {selectedType.id === 'employment-certificate' && (
            <div className="form-section">
              <h3 className="section-title" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                <CheckCircle size={20} color={getIconColor()} />
                {formLabel('employment_certificate_title')}
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('employer_name')}</label>
                  <input
                    type="text"
                    value={formData.employer?.name || ''}
                    onChange={(e) => handleInputChange('employer', {...formData.employer, name: e.target.value})}
                    placeholder={formLabel('ph_company_legal_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('legal_form')}</label>
                  <CustomSelect
                    value={formData.employer?.legalForm || ''}
                    onChange={(e) => handleInputChange('employer', {...formData.employer, legalForm: e.target.value})}
                  >
                    <option value="SARL">SARL</option>
                    <option value="SPA">SPA</option>
                    <option value="EURL">EURL</option>
                    <option value="SNC">SNC</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('employer_nif')}</label>
                  <input
                    type="text"
                    value={formData.employer?.nif || ''}
                    onChange={(e) => handleInputChange('employer', {...formData.employer, nif: e.target.value})}
                    placeholder={formLabel('ph_fiscal_id')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('employee_last_name')}</label>
                  <input
                    type="text"
                    value={formData.employee?.name || ''}
                    onChange={(e) => handleInputChange('employee', {...formData.employee, name: e.target.value})}
                    placeholder={formLabel('ph_last_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('employee_first_name')}</label>
                  <input
                    type="text"
                    value={formData.employee?.firstName || ''}
                    onChange={(e) => handleInputChange('employee', {...formData.employee, firstName: e.target.value})}
                    placeholder={formLabel('ph_first_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('employee_id')}</label>
                  <input
                    type="text"
                    value={formData.employee?.employeeId || ''}
                    onChange={(e) => handleInputChange('employee', {...formData.employee, employeeId: e.target.value})}
                    placeholder={formLabel('ph_employee_id')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('position_held')}</label>
                  <input
                    type="text"
                    value={formData.employee?.position || ''}
                    onChange={(e) => handleInputChange('employee', {...formData.employee, position: e.target.value})}
                    placeholder={formLabel('ph_job_title')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('department')}</label>
                  <input
                    type="text"
                    value={formData.employee?.department || ''}
                    onChange={(e) => handleInputChange('employee', {...formData.employee, department: e.target.value})}
                    placeholder={formLabel('ph_department')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('contract_type')}</label>
                  <CustomSelect
                    value={formData.employment?.contractType || 'CDI'}
                    onChange={(e) => handleInputChange('employment', {...formData.employment, contractType: e.target.value})}
                  >
                    <option value="CDI">{formLabel('employment_contract_types.cdi')}</option>
                    <option value="CDD">{formLabel('employment_contract_types.cdd')}</option>
                    <option value="stage">{formLabel('employment_contract_types.internship')}</option>
                    <option value="apprentissage">{formLabel('employment_contract_types.apprenticeship')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('start_date')}</label>
                  <CustomDatePicker
                      value={formData.employment?.startDate || ''}
                      onChange={(val) => handleInputChange('employment', {...formData.employment, startDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('end_date')}</label>
                  <CustomDatePicker
                      value={formData.employment?.endDate || ''}
                      onChange={(val) => handleInputChange('employment', {...formData.employment, endDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('current_status')}</label>
                  <CustomSelect
                    value={formData.employment?.currentStatus || 'en_service'}
                    onChange={(e) => handleInputChange('employment', {...formData.employment, currentStatus: e.target.value})}
                  >
                    <option value="en_service">{formLabel('employment_statuses.active')}</option>
                    <option value="démission">{formLabel('employment_statuses.resignation')}</option>
                    <option value="licenciement">{formLabel('employment_statuses.dismissal')}</option>
                    <option value="fin_contrat">{formLabel('employment_statuses.contract_end')}</option>
                    <option value="retraite">{formLabel('employment_statuses.retirement')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('last_salary')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.remuneration?.lastSalary || 0}
                    onChange={(val) => handleInputChange('remuneration', {...formData.remuneration, lastSalary: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={1000}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('overall_rating')}</label>
                  <CustomSelect
                    value={formData.performance?.overallRating || 'satisfaisante'}
                    onChange={(e) => handleInputChange('performance', {...formData.performance, overallRating: e.target.value})}
                  >
                    <option value="excellente">{formLabel('rating_levels.excellent')}</option>
                    <option value="très_bonne">{formLabel('rating_levels.very_good')}</option>
                    <option value="bonne">{formLabel('rating_levels.good')}</option>
                    <option value="satisfaisante">{formLabel('rating_levels.satisfactory')}</option>
                    <option value="insuffisante">{formLabel('rating_levels.insufficient')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('punctuality')}</label>
                  <CustomSelect
                    value={formData.performance?.punctuality || 'excellente'}
                    onChange={(e) => handleInputChange('performance', {...formData.performance, punctuality: e.target.value})}
                  >
                    <option value="excellente">{formLabel('rating_levels.excellent')}</option>
                    <option value="bonne">{formLabel('rating_levels.good')}</option>
                    <option value="satisfaisante">{formLabel('rating_levels.satisfactory')}</option>
                    <option value="insuffisante">{formLabel('rating_levels.insufficient')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('certificate_purpose')}</label>
                  <CustomSelect
                    value={formData.certification?.purpose || 'administrative'}
                    onChange={(e) => handleInputChange('certification', {...formData.certification, purpose: e.target.value})}
                  >
                    <option value="administrative">{formLabel('certificate_purposes.administrative')}</option>
                    <option value="banque">{formLabel('tax_request_purposes.banking')}</option>
                    <option value="visa">{formLabel('certificate_purposes.visa')}</option>
                    <option value="logement">{formLabel('certificate_purposes.housing')}</option>
                    <option value="autre">{formLabel('other')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('issued_by')}</label>
                  <input
                    type="text"
                    value={formData.issuance?.issuedBy || ''}
                    onChange={(e) => handleInputChange('issuance', {...formData.issuance, issuedBy: e.target.value})}
                    placeholder={formLabel('ph_hr_manager_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('signatory_function')}</label>
                  <input
                    type="text"
                    value={formData.issuance?.position || ''}
                    onChange={(e) => handleInputChange('issuance', {...formData.issuance, position: e.target.value})}
                    placeholder={formLabel('ph_signatory_role_example')}
                  />
                </div>
              </div>
            </div>
          )}

          {selectedType.id === 'leave-request' && (
            <div className="form-section">
              <h3 className="section-title" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                <Calendar size={20} color={getIconColor()} />
                {formLabel('leave_request_title')}
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('employee_last_name')}</label>
                  <input
                    type="text"
                    value={formData.employee?.name || ''}
                    onChange={(e) => handleInputChange('employee', {...formData.employee, name: e.target.value})}
                    placeholder={formLabel('ph_last_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('employee_first_name')}</label>
                  <input
                    type="text"
                    value={formData.employee?.firstName || ''}
                    onChange={(e) => handleInputChange('employee', {...formData.employee, firstName: e.target.value})}
                    placeholder={formLabel('ph_first_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('employee_id')}</label>
                  <input
                    type="text"
                    value={formData.employee?.employeeId || ''}
                    onChange={(e) => handleInputChange('employee', {...formData.employee, employeeId: e.target.value})}
                    placeholder={formLabel('ph_employee_id')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('position_label')}</label>
                  <input
                    type="text"
                    value={formData.employee?.position || ''}
                    onChange={(e) => handleInputChange('employee', {...formData.employee, position: e.target.value})}
                    placeholder={formLabel('ph_job_title')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('department')}</label>
                  <input
                    type="text"
                    value={formData.employee?.department || ''}
                    onChange={(e) => handleInputChange('employee', {...formData.employee, department: e.target.value})}
                    placeholder={formLabel('ph_department')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('direct_supervisor')}</label>
                  <input
                    type="text"
                    value={formData.employee?.supervisor || ''}
                    onChange={(e) => handleInputChange('employee', {...formData.employee, supervisor: e.target.value})}
                    placeholder={formLabel('ph_supervisor_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('leave_type')}</label>
                  <CustomSelect
                    value={formData.leaveDetails?.type || ''}
                    onChange={(e) => handleInputChange('leaveDetails', {...formData.leaveDetails, type: e.target.value})}
                  >
                    <option value="congé_annuel">{formLabel('leave_types.annual')}</option>
                    <option value="congé_maladie">{formLabel('leave_types.sick')}</option>
                    <option value="congé_maternité">{formLabel('leave_types.maternity')}</option>
                    <option value="congé_paternité">{formLabel('leave_types.paternity')}</option>
                    <option value="congé_exceptionnel">{formLabel('leave_types.exceptional')}</option>
                    <option value="congé_sans_solde">{formLabel('leave_types.unpaid')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('category')}</label>
                  <CustomSelect
                    value={formData.leaveDetails?.category || 'repos'}
                    onChange={(e) => handleInputChange('leaveDetails', {...formData.leaveDetails, category: e.target.value})}
                  >
                    <option value="repos">{formLabel('leave_categories.rest')}</option>
                    <option value="familial">{formLabel('leave_categories.family')}</option>
                    <option value="médical">{formLabel('leave_categories.medical')}</option>
                    <option value="formation">{formLabel('project_types.training')}</option>
                    <option value="personnel">{formLabel('leave_categories.personal')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('start_date')}</label>
                  <CustomDatePicker
                      value={formData.leaveDetails?.startDate || ''}
                      onChange={(val) => handleInputChange('leaveDetails', {...formData.leaveDetails, startDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('end_date')}</label>
                  <CustomDatePicker
                      value={formData.leaveDetails?.endDate || ''}
                      onChange={(val) => handleInputChange('leaveDetails', {...formData.leaveDetails, endDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('day_count')}</label>
                  <CustomNumberInput
                    value={formData.leaveDetails?.totalDays || 0}
                    onChange={(val) => handleInputChange('leaveDetails', {...formData.leaveDetails, totalDays: parseInt(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('leave_balance_days')}</label>
                  <CustomNumberInput
                    value={formData.entitlement?.remainingDays || 30}
                    onChange={(val) => handleInputChange('entitlement', {...formData.entitlement, remainingDays: parseInt(val) || 30})}
                    placeholder="30"
                    min={0}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('urgency')}</label>
                  <CustomSelect
                    value={formData.justification?.urgency || ''}
                    onChange={(e) => handleInputChange('justification', {...formData.justification, urgency: e.target.value})}
                  >
                    <option value="">{formLabel('select_placeholder')}</option>
                    <option value="normale">{formLabel('priority_levels.normal')}</option>
                    <option value="urgente">{formLabel('priority_levels.urgent')}</option>
                    <option value="exceptionnelle">{formLabel('rating_levels.exceptional')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('temporary_replacement')}</label>
                  <input
                    type="text"
                    value={formData.workCoverage?.temporary || ''}
                    onChange={(e) => handleInputChange('workCoverage', {...formData.workCoverage, temporary: e.target.value})}
                    placeholder={formLabel('ph_replacement_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('project_impact')}</label>
                  <CustomSelect
                    value={formData.workCoverage?.projectImpact || 'aucun'}
                    onChange={(e) => handleInputChange('workCoverage', {...formData.workCoverage, projectImpact: e.target.value})}
                  >
                    <option value="aucun">{t('document_creation.form.option_none')}</option>
                    <option value="mineur">{formLabel('impact_levels.minor')}</option>
                    <option value="modéré">{formLabel('impact_levels.moderate')}</option>
                    <option value="important">{formLabel('impact_levels.important')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('emergency_contact')}</label>
                  <input
                    type="text"
                    value={formData.emergencyContacts?.primaryContact || ''}
                    onChange={(e) => handleInputChange('emergencyContacts', {...formData.emergencyContacts, primaryContact: e.target.value})}
                    placeholder={formLabel('ph_emergency_contact_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('emergency_phone')}</label>
                  <input
                    type="tel"
                    value={formData.emergencyContacts?.primaryPhone || ''}
                    onChange={(e) => handleInputChange('emergencyContacts', {...formData.emergencyContacts, primaryPhone: e.target.value})}
                    placeholder={formLabel('ph_phone_number')}
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.justification?.medicalCertificate || false}
                      onChange={(e) => handleInputChange('justification', {...formData.justification, medicalCertificate: e.target.checked})}
                    />
                    {formLabel('medical_certificate_provided')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.workCoverage?.handoverCompleted || false}
                      onChange={(e) => handleInputChange('workCoverage', {...formData.workCoverage, handoverCompleted: e.target.checked})}
                    />
                    {formLabel('handover_completed')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.approval?.supervisorApproval || false}
                      onChange={(e) => handleInputChange('approval', {...formData.approval, supervisorApproval: e.target.checked})}
                    />
                    {formLabel('supervisor_approved')}
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('request_reason')}</label>
                <textarea
                  value={formData.justification?.reason || ''}
                  onChange={(e) => handleInputChange('justification', {...formData.justification, reason: e.target.value})}
                  placeholder={formLabel('ph_leave_reason')}
                  rows="3"
                />
              </div>
            </div>
          )}
          {/* PRODUCTION & QUALITÉ Documents */}
          {selectedType.id === 'production-sheet' && (
            <div className="form-section">
              <h3 className="section-title" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                <FileText size={20} color={getIconColor()} />
                {formLabel('production_sheet_title')}
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('product_name')}</label>
                  <input
                    type="text"
                    value={formData.productInfo?.name || ''}
                    onChange={(e) => handleInputChange('productInfo', {...formData.productInfo, name: e.target.value})}
                    placeholder={formLabel('ph_product_to_manufacture')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('product_code')}</label>
                  <input
                    type="text"
                    value={formData.productInfo?.code || ''}
                    onChange={(e) => handleInputChange('productInfo', {...formData.productInfo, code: e.target.value})}
                    placeholder={formLabel('ph_code_reference')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('category')}</label>
                  <CustomSelect
                    value={formData.productInfo?.category || 'produit_fini'}
                    onChange={(e) => handleInputChange('productInfo', {...formData.productInfo, category: e.target.value})}
                  >
                    <option value="produit_fini">{formLabel('product_categories.finished')}</option>
                    <option value="sous_ensemble">{formLabel('product_categories.subassembly')}</option>
                    <option value="composant">{formLabel('product_categories.component')}</option>
                    <option value="matière_première">{formLabel('product_categories.raw_material')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('production_order_number')}</label>
                  <input
                    type="text"
                    value={formData.productionOrder?.number || ''}
                    onChange={(e) => handleInputChange('productionOrder', {...formData.productionOrder, number: e.target.value})}
                    placeholder={formLabel('ph_production_order_number')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('target_quantity')}</label>
                  <CustomNumberInput
                    value={formData.productionOrder?.targetQuantity || 0}
                    onChange={(val) => handleInputChange('productionOrder', {...formData.productionOrder, targetQuantity: parseInt(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('actual_quantity')}</label>
                  <CustomNumberInput
                    value={formData.productionOrder?.actualQuantity || 0}
                    onChange={(val) => handleInputChange('productionOrder', {...formData.productionOrder, actualQuantity: parseInt(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('batch_number')}</label>
                  <input
                    type="text"
                    value={formData.batchInfo?.batchNumber || ''}
                    onChange={(e) => handleInputChange('batchInfo', {...formData.batchInfo, batchNumber: e.target.value})}
                    placeholder={formLabel('ph_production_batch_number')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('batch_size')}</label>
                  <CustomNumberInput
                    value={formData.batchInfo?.batchSize || 0}
                    onChange={(val) => handleInputChange('batchInfo', {...formData.batchInfo, batchSize: parseInt(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('operator')}</label>
                  <input
                    type="text"
                    value={formData.team.operator || ''}
                    onChange={(e) => handleInputChange('team', {...formData.team, operator: e.target.value})}
                    placeholder={formLabel('ph_operator_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('supervisor_label')}</label>
                  <input
                    type="text"
                    value={formData.team.supervisor || ''}
                    onChange={(e) => handleInputChange('team', {...formData.team, supervisor: e.target.value})}
                    placeholder={formLabel('ph_supervisor_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('shift_type')}</label>
                  <CustomSelect
                    value={formData.team.shiftType || 'jour'}
                    onChange={(e) => handleInputChange('team', {...formData.team, shiftType: e.target.value})}
                  >
                    <option value="jour">{formLabel('shift_types.day')}</option>
                    <option value="nuit">{formLabel('shift_types.night')}</option>
                    <option value="weekend">{formLabel('shift_types.weekend')}</option>
                    <option value="continu">{formLabel('shift_types.continuous')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('main_machine')}</label>
                  <input
                    type="text"
                    value={formData.equipment?.primaryMachine || ''}
                    onChange={(e) => handleInputChange('equipment', {...formData.equipment, primaryMachine: e.target.value})}
                    placeholder={formLabel('ph_equipment_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('machine_number')}</label>
                  <input
                    type="text"
                    value={formData.equipment?.machineNumber || ''}
                    onChange={(e) => handleInputChange('equipment', {...formData.equipment, machineNumber: e.target.value})}
                    placeholder={formLabel('ph_equipment_id')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('planned_start_date')}</label>
                  <CustomDatePicker
                      value={formData.schedule?.plannedStartDate || ''}
                      onChange={(val) => handleInputChange('schedule', {...formData.schedule, plannedStartDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('expected_end_date')}</label>
                  <CustomDatePicker
                      value={formData.schedule?.plannedEndDate || ''}
                      onChange={(val) => handleInputChange('schedule', {...formData.schedule, plannedEndDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('efficiency_pct')}</label>
                  <CustomNumberInput
                    value={formData.performance?.efficiency || 100}
                    onChange={(val) => handleInputChange('performance', {...formData.performance, efficiency: parseFloat(val) || 100})}
                    placeholder="100"
                    min={0}
                    max={100}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('conformity_rate')}</label>
                  <CustomNumberInput
                    value={formData.performance?.conformityRate || 100}
                    onChange={(val) => handleInputChange('performance', {...formData.performance, conformityRate: parseFloat(val) || 100})}
                    placeholder="100"
                    min={0}
                    max={100}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('total_cost_label')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.costs?.totalCost || 0}
                    onChange={(val) => handleInputChange('costs', {...formData.costs, totalCost: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
              </div>
            </div>
          )}
          {selectedType.id === 'quality-control' && (
            <div className="form-section">
              <h3 className="section-title" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                <CheckCircle size={20} color={getIconColor()} />
                {formLabel('quality_control_title')}
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('product_name')}</label>
                  <input
                    type="text"
                    value={formData.productDetails?.name || ''}
                    onChange={(e) => handleInputChange('productDetails', {...formData.productDetails, name: e.target.value})}
                    placeholder={formLabel('ph_controlled_product_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('product_code')}</label>
                  <input
                    type="text"
                    value={formData.productDetails?.code || ''}
                    onChange={(e) => handleInputChange('productDetails', {...formData.productDetails, code: e.target.value})}
                    placeholder={formLabel('ph_code_reference')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('batch_number')}</label>
                  <input
                    type="text"
                    value={formData.productDetails?.batchNumber || ''}
                    onChange={(e) => handleInputChange('productDetails', {...formData.productDetails, batchNumber: e.target.value})}
                    placeholder={formLabel('ph_production_batch_number')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('inspection_type')}</label>
                  <CustomSelect
                    value={formData.inspectionInfo?.type || ''}
                    onChange={(e) => handleInputChange('inspectionInfo', {...formData.inspectionInfo, type: e.target.value})}
                  >
                    <option value="réception">{formLabel('inspection_types.reception')}</option>
                    <option value="en_cours">{formLabel('inspection_types.in_process')}</option>
                    <option value="final">{formLabel('inspection_types.final')}</option>
                    <option value="expédition">{formLabel('inspection_types.shipping')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('inspection_stage')}</label>
                  <CustomSelect
                    value={formData.inspectionInfo?.stage || 'final'}
                    onChange={(e) => handleInputChange('inspectionInfo', {...formData.inspectionInfo, stage: e.target.value})}
                  >
                    <option value="matières_premières">{formLabel('inspection_stages.raw_materials')}</option>
                    <option value="composants">{formLabel('inspection_stages.components')}</option>
                    <option value="sous_ensembles">{formLabel('inspection_stages.subassemblies')}</option>
                    <option value="final">{formLabel('inspection_stages.final_product')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('sampling_method')}</label>
                  <CustomSelect
                    value={formData.inspectionInfo?.sampling || 'statistique'}
                    onChange={(e) => handleInputChange('inspectionInfo', {...formData.inspectionInfo, sampling: e.target.value})}
                  >
                    <option value="statistique">{formLabel('sampling_methods.statistical')}</option>
                    <option value="aléatoire">{formLabel('sampling_methods.random')}</option>
                    <option value="systématique">{formLabel('sampling_methods.systematic')}</option>
                    <option value="complet">{formLabel('sampling_methods.full')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('sample_size')}</label>
                  <CustomNumberInput
                    value={formData.inspectionInfo?.sampleSize || 0}
                    onChange={(val) => handleInputChange('inspectionInfo', {...formData.inspectionInfo, sampleSize: parseInt(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('batch_size')}</label>
                  <CustomNumberInput
                    value={formData.inspectionInfo?.lotSize || 0}
                    onChange={(val) => handleInputChange('inspectionInfo', {...formData.inspectionInfo, lotSize: parseInt(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('inspector_label')}</label>
                  <input
                    type="text"
                    value={formData.inspectionInfo?.inspector || ''}
                    onChange={(e) => handleInputChange('inspectionInfo', {...formData.inspectionInfo, inspector: e.target.value})}
                    placeholder={formLabel('ph_inspector_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('primary_standard')}</label>
                  <CustomSelect
                    value={formData.standards?.primaryStandard || 'ISO 9001'}
                    onChange={(e) => handleInputChange('standards', {...formData.standards, primaryStandard: e.target.value})}
                  >
                    <option value="ISO 9001">ISO 9001</option>
                    <option value="ISO 14001">ISO 14001</option>
                    <option value="IATF 16949">IATF 16949</option>
                    <option value="AS9100">AS9100</option>
                    <option value="ISO 13485">ISO 13485</option>
                    <option value="FDA">FDA</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('surface_finish')}</label>
                  <CustomSelect
                    value={formData.visualInspection?.surfaceFinish || 'acceptable'}
                    onChange={(e) => handleInputChange('visualInspection', {...formData.visualInspection, surfaceFinish: e.target.value})}
                  >
                    <option value="">{formLabel('select_placeholder')}</option>
                    <option value="excellent">{formLabel('surface_finish_levels.excellent')}</option>
                    <option value="acceptable">{formLabel('rating_levels.acceptable')}</option>
                    <option value="limite">{formLabel('surface_finish_levels.limit')}</option>
                    <option value="inacceptable">{formLabel('surface_finish_levels.unacceptable')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('color_conformity')}</label>
                  <CustomSelect
                    value={formData.visualInspection?.colorConformity || 'conforme'}
                    onChange={(e) => handleInputChange('visualInspection', {...formData.visualInspection, colorConformity: e.target.value})}
                  >
                    <option value="">{formLabel('select_placeholder')}</option>
                    <option value="">{formLabel('select_placeholder')}</option>
                    <option value="conforme">{formLabel('compliance_levels.compliant')}</option>
                    <option value="acceptable">{formLabel('rating_levels.acceptable')}</option>
                    <option value="non_conforme">{formLabel('compliance_levels.non_compliant')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('overall_result')}</label>
                  <CustomSelect
                    value={formData.results?.overallResult || 'conforme'}
                    onChange={(e) => handleInputChange('results', {...formData.results, overallResult: e.target.value})}
                  >
                    <option value="">{formLabel('select_placeholder')}</option>
                    <option value="">{formLabel('select_placeholder')}</option>
                    <option value="conforme">{formLabel('compliance_levels.compliant')}</option>
                    <option value="acceptable">{formLabel('rating_levels.acceptable')}</option>
                    <option value="non_conforme">{formLabel('compliance_levels.non_compliant')}</option>
                    <option value="rejeté">{formLabel('inspection_results.rejected')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('conforming_units')}</label>
                  <CustomNumberInput
                    value={formData.results?.conformingUnits || 0}
                    onChange={(val) => handleInputChange('results', {...formData.results, conformingUnits: parseInt(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('non_conforming_units')}</label>
                  <CustomNumberInput
                    value={formData.results?.nonConformingUnits || 0}
                    onChange={(val) => handleInputChange('results', {...formData.results, nonConformingUnits: parseInt(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('conformity_rate')}</label>
                  <CustomNumberInput
                    value={formData.results?.conformityRate || 100}
                    onChange={(val) => handleInputChange('results', {...formData.results, conformityRate: parseFloat(val) || 100})}
                    placeholder="100"
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.decisions?.lotAccepted || false}
                      onChange={(e) => handleInputChange('decisions', {...formData.decisions, lotAccepted: e.target.checked})}
                    />
                    {formLabel('lot_accepted')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.decisions?.reworkRequired || false}
                      onChange={(e) => handleInputChange('decisions', {...formData.decisions, reworkRequired: e.target.checked})}
                    />
                    {formLabel('rework_required')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.followUp?.monitoringRequired || false}
                      onChange={(e) => handleInputChange('followUp', {...formData.followUp, monitoringRequired: e.target.checked})}
                    />
                    {formLabel('monitoring_required')}
                  </label>
                </div>
              </div>
            </div>
          )}

          {selectedType.id === 'manufacturing-order' && (
            <div className="form-section">
              <h3 className="section-title" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                <FileCheck size={20} color={getIconColor()} />
                {formLabel('manufacturing_order_title')}
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('issued_by')}</label>
                  <input
                    type="text"
                    value={formData.orderInfo?.issuedBy || ''}
                    onChange={(e) => handleInputChange('orderInfo', {...formData.orderInfo, issuedBy: e.target.value})}
                    placeholder={formLabel('ph_manager_full_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('department')}</label>
                  <CustomSelect
                    value={formData.orderInfo?.department || 'production'}
                    onChange={(e) => handleInputChange('orderInfo', {...formData.orderInfo, department: e.target.value})}
                  >
                    <option value="production">{formLabel('departments.production')}</option>
                    <option value="assemblage">{formLabel('manufacturing_departments.assembly')}</option>
                    <option value="usinage">{formLabel('manufacturing_departments.machining')}</option>
                    <option value="peinture">{formLabel('manufacturing_departments.painting')}</option>
                    <option value="contrôle">{formLabel('manufacturing_departments.quality_control')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('product_name')}</label>
                  <input
                    type="text"
                    value={formData.productSpecification?.name || ''}
                    onChange={(e) => handleInputChange('productSpecification', {...formData.productSpecification, name: e.target.value})}
                    placeholder={formLabel('ph_product_to_manufacture')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('product_code')}</label>
                  <input
                    type="text"
                    value={formData.productSpecification?.code || ''}
                    onChange={(e) => handleInputChange('productSpecification', {...formData.productSpecification, code: e.target.value})}
                    placeholder={formLabel('ph_code_reference')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('revision_label')}</label>
                  <input
                    type="text"
                    value={formData.productSpecification?.revision || ''}
                    onChange={(e) => handleInputChange('productSpecification', {...formData.productSpecification, revision: e.target.value})}
                    placeholder={formLabel('ph_version_revision')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('quality_label')}</label>
                  <CustomSelect
                    value={formData.productSpecification?.quality || 'standard'}
                    onChange={(e) => handleInputChange('productSpecification', {...formData.productSpecification, quality: e.target.value})}
                  >
                    <option value="">{formLabel('select_placeholder')}</option>
                    <option value="standard">{formLabel('quality_spec_levels.standard')}</option>
                    <option value="premium">{formLabel('quality_spec_levels.premium')}</option>
                    <option value="prototype">{formLabel('quality_spec_levels.prototype')}</option>
                    <option value="série">{formLabel('quality_spec_levels.series')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('ordered_quantity')}</label>
                  <CustomNumberInput
                    value={formData.quantity?.ordered || 0}
                    onChange={(val) => handleInputChange('quantity', {...formData.quantity, ordered: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={0.1}
                    decimals={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('planned_production')}</label>
                  <CustomNumberInput
                    value={formData.quantity?.plannedProduction || 0}
                    onChange={(val) => handleInputChange('quantity', {...formData.quantity, plannedProduction: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={0.1}
                    decimals={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('yield_target_pct')}</label>
                  <CustomNumberInput
                    value={formData.quantity?.yield || 95}
                    onChange={(val) => handleInputChange('quantity', {...formData.quantity, yield: parseFloat(val) || 95})}
                    placeholder="95"
                    min={0}
                    max={100}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('requested_date')}</label>
                  <CustomDatePicker
                      value={formData.scheduling?.requestedDate || ''}
                      onChange={(val) => handleInputChange('scheduling', {...formData.scheduling, requestedDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('planned_start')}</label>
                  <CustomDatePicker
                      value={formData.scheduling?.plannedStartDate || ''}
                      onChange={(val) => handleInputChange('scheduling', {...formData.scheduling, plannedStartDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('planned_end')}</label>
                  <CustomDatePicker
                      value={formData.scheduling?.plannedEndDate || ''}
                      onChange={(val) => handleInputChange('scheduling', {...formData.scheduling, plannedEndDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('team_label')}</label>
                  <CustomSelect
                    value={formData.scheduling?.shift || 'jour'}
                    onChange={(e) => handleInputChange('scheduling', {...formData.scheduling, shift: e.target.value})}
                  >
                    <option value="jour">{formLabel('shift_types.day')}</option>
                    <option value="nuit">{formLabel('shift_types.night')}</option>
                    <option value="weekend">{formLabel('shift_types.weekend')}</option>
                    <option value="continu">{formLabel('shift_types.continuous')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('main_machine')}</label>
                  <input
                    type="text"
                    value={formData.resources?.primaryMachine || ''}
                    onChange={(e) => handleInputChange('resources', {...formData.resources, primaryMachine: e.target.value})}
                    placeholder={formLabel('ph_equipment_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('total_cost_label')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.costing?.totalCost || 0}
                    onChange={(val) => handleInputChange('costing', {...formData.costing, totalCost: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('target_margin_pct')}</label>
                  <CustomNumberInput
                    value={formData.costing?.marginTarget || 25}
                    onChange={(val) => handleInputChange('costing', {...formData.costing, marginTarget: parseFloat(val) || 25})}
                    placeholder="25"
                    min={0}
                    max={100}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('status_label')}</label>
                  <CustomSelect
                    value={formData.tracking?.status || ''}
                    onChange={(e) => handleInputChange('tracking', {...formData.tracking, status: e.target.value})}
                  >
                    <option value="planifié">{formLabel('order_tracking_statuses.planned')}</option>
                    <option value="approuvé">{formLabel('statuses.approved')}</option>
                    <option value="en_cours">{formLabel('order_tracking_statuses.in_progress')}</option>
                    <option value="suspendu">{formLabel('account_statuses.suspended')}</option>
                    <option value="terminé">{formLabel('order_tracking_statuses.completed')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.approval?.productionManager || false}
                      onChange={(e) => handleInputChange('approval', {...formData.approval, productionManager: e.target.checked})}
                    />
                    {formLabel('approved_by_production_manager')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.approval?.qualityManager || false}
                      onChange={(e) => handleInputChange('approval', {...formData.approval, qualityManager: e.target.checked})}
                    />
                    {formLabel('approved_by_quality_manager')}
                  </label>
                </div>
              </div>
            </div>
          )}
          {selectedType.id === 'failure-report' && (
            <div className="form-section">
              <h3 className="section-title" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                <FileText size={20} color={getIconColor()} />
                {formLabel('failure_report_title')}
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('equipment_id')}</label>
                  <input
                    type="text"
                    value={formData.equipment?.id || ''}
                    onChange={(e) => handleInputChange('equipment', {...formData.equipment, id: e.target.value})}
                    placeholder={formLabel('ph_equipment_id')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('equipment_name')}</label>
                  <input
                    type="text"
                    value={formData.equipment?.name || ''}
                    onChange={(e) => handleInputChange('equipment', {...formData.equipment, name: e.target.value})}
                    placeholder={formLabel('ph_equipment_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('equipment_type')}</label>
                  <input
                    type="text"
                    value={formData.equipment?.type || ''}
                    onChange={(e) => handleInputChange('equipment', {...formData.equipment, type: e.target.value})}
                    placeholder={formLabel('ph_equipment_type')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('manufacturer_label')}</label>
                  <input
                    type="text"
                    value={formData.equipment?.manufacturer || ''}
                    onChange={(e) => handleInputChange('equipment', {...formData.equipment, manufacturer: e.target.value})}
                    placeholder={formLabel('ph_manufacturer_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('failure_type')}</label>
                  <CustomSelect
                    value={formData.failure?.type || 'panne'}
                    onChange={(e) => handleInputChange('failure', {...formData.failure, type: e.target.value})}
                  >
                    <option value="panne">{formLabel('failure_types.breakdown')}</option>
                    <option value="dysfonctionnement">{formLabel('failure_types.malfunction')}</option>
                    <option value="dégradation">{formLabel('failure_types.degradation')}</option>
                    <option value="arrêt">{formLabel('failure_types.unexpected_stop')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('category')}</label>
                  <CustomSelect
                    value={formData.failure?.category || ''}
                    onChange={(e) => handleInputChange('failure', {...formData.failure, category: e.target.value})}
                  >
                    <option value="Électrique">{formLabel('failure_categories.electrical')}</option>
                    <option value="mécanique">{formLabel('failure_categories.mechanical')}</option>
                    <option value="hydraulique">{formLabel('failure_categories.hydraulic')}</option>
                    <option value="pneumatique">{formLabel('failure_categories.pneumatic')}</option>
                    <option value="Électronique">{formLabel('failure_categories.electronic')}</option>
                    <option value="logiciel">{formLabel('failure_categories.software')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('severity_label')}</label>
                  <CustomSelect
                    value={formData.failure?.severity || 'moyenne'}
                    onChange={(e) => handleInputChange('failure', {...formData.failure, severity: e.target.value})}
                  >
                    <option value="mineure">{formLabel('non_compliance_severities.minor')}</option>
                    <option value="moyenne">{formLabel('criticality_levels.medium')}</option>
                    <option value="majeure">{formLabel('non_compliance_severities.major')}</option>
                    <option value="critique">{formLabel('project_priorities.critical')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('impact_label')}</label>
                  <CustomSelect
                    value={formData.failure?.impact || 'local'}
                    onChange={(e) => handleInputChange('failure', {...formData.failure, impact: e.target.value})}
                  >
                    <option value="local">{formLabel('failure_impacts.local')}</option>
                    <option value="ligne">{formLabel('failure_impacts.production_line')}</option>
                    <option value="atelier">{formLabel('failure_impacts.workshop')}</option>
                    <option value="usine">{formLabel('failure_impacts.full_plant')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('detection_date')}</label>
                  <CustomDatePicker
                      value={formData.failure?.detectionDate || ''}
                      onChange={(val) => handleInputChange('failure', {...formData.failure, detectionDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('detection_time')}</label>
                  <input
                    type="time"
                    value={formData.failure?.detectionTime || ''}
                    onChange={(e) => handleInputChange('failure', {...formData.failure, detectionTime: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('detected_by')}</label>
                  <input
                    type="text"
                    value={formData.failure?.detectedBy || ''}
                    onChange={(e) => handleInputChange('failure', {...formData.failure, detectedBy: e.target.value})}
                    placeholder={formLabel('ph_person_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('operating_mode')}</label>
                  <CustomSelect
                    value={formData.conditions?.operatingMode || 'automatique'}
                    onChange={(e) => handleInputChange('conditions', {...formData.conditions, operatingMode: e.target.value})}
                  >
                    <option value="automatique">{formLabel('operating_modes.automatic')}</option>
                    <option value="manuel">{formLabel('operating_modes.manual')}</option>
                    <option value="maintenance">{formLabel('operating_modes.maintenance')}</option>
                    <option value="test">{formLabel('operating_modes.test')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('workload_label')}</label>
                  <CustomSelect
                    value={formData.conditions?.workload || ''}
                    onChange={(e) => handleInputChange('conditions', {...formData.conditions, workload: e.target.value})}
                  >
                    <option value="faible">{formLabel('criticality_levels.low')}</option>
                    <option value="">{formLabel('select_placeholder')}</option>
                    <option value="normale">{formLabel('priority_levels.normal')}</option>
                    <option value="élevée">{formLabel('criticality_levels.high')}</option>
                    <option value="surcharge">{formLabel('workload_levels.overload')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('investigation_method')}</label>
                  <CustomSelect
                    value={formData.investigation?.investigationMethod || '5 pourquoi'}
                    onChange={(e) => handleInputChange('investigation', {...formData.investigation, investigationMethod: e.target.value})}
                  >
                    <option value="5 pourquoi">{formLabel('investigation_methods.five_whys')}</option>
                    <option value="ishikawa">{formLabel('investigation_methods.ishikawa')}</option>
                    <option value="amdec">{formLabel('investigation_methods.fmea')}</option>
                    <option value="pareto">{formLabel('investigation_methods.pareto')}</option>
                    <option value="rca">{formLabel('investigation_methods.rca')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('production_loss_hours')}</label>
                  <CustomNumberInput
                    value={formData.impact?.downtimeHours || 0}
                    onChange={(val) => handleInputChange('impact', {...formData.impact, downtimeHours: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={0.1}
                    decimals={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('financial_impact')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.impact?.financialImpact || 0}
                    onChange={(val) => handleInputChange('impact', {...formData.impact, financialImpact: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('total_cost_label')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.resources?.totalCost || 0}
                    onChange={(val) => handleInputChange('resources', {...formData.resources, totalCost: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.resolution?.resolved || false}
                      onChange={(e) => handleInputChange('resolution', {...formData.resolution, resolved: e.target.checked})}
                    />
                    {formLabel('failure_resolved')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.resolution?.followUpRequired || false}
                      onChange={(e) => handleInputChange('resolution', {...formData.resolution, followUpRequired: e.target.checked})}
                    />
                    {formLabel('follow_up_required')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.documentation?.photos || false}
                      onChange={(e) => handleInputChange('documentation', {...formData.documentation, photos: e.target.checked})}
                    />
                    {formLabel('photos_available')}
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('initial_symptoms')}</label>
                <textarea
                  value={formData.symptoms?.initialSymptoms || ''}
                  onChange={(e) => handleInputChange('symptoms', {...formData.symptoms, initialSymptoms: e.target.value})}
                  placeholder={formLabel('ph_initial_symptoms')}
                  rows="2"
                />
              </div>
              
              <div className="form-group">
                <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('immediate_cause')}</label>
                <textarea
                  value={formData.investigation?.immediateCause || ''}
                  onChange={(e) => handleInputChange('investigation', {...formData.investigation, immediateCause: e.target.value})}
                  placeholder={formLabel('ph_immediate_failure_cause')}
                  rows="2"
                />
              </div>
              
              <div className="form-group">
                <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('root_cause_label')}</label>
                <textarea
                  value={formData.investigation?.rootCause || ''}
                  onChange={(e) => handleInputChange('investigation', {...formData.investigation, rootCause: e.target.value})}
                  placeholder={formLabel('ph_root_cause')}
                  rows="2"
                />
              </div>
              
              <div className="form-group">
                <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('lessons_learned')}</label>
                <textarea
                  value={formData.lessons?.lessonsLearned || ''}
                  onChange={(e) => handleInputChange('lessons', {...formData.lessons, lessonsLearned: e.target.value})}
                  placeholder={formLabel('ph_lessons_learned')}
                  rows="2"
                />
              </div>
            </div>
          )}
          {/* TRANSPORT & LIVRAISON Documents */}
          {selectedType.id === 'route-sheet' && (
            <div className="form-section">
              <h3 className="section-title" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                <Truck size={20} color={getIconColor()} />
                {formLabel('route_sheet_title')}
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('driver_name')}</label>
                  <input
                    type="text"
                    value={formData.driver?.name || ''}
                    onChange={(e) => handleInputChange('driver', {...formData.driver, name: e.target.value})}
                    placeholder={formLabel('ph_driver_full_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('license_number')}</label>
                  <input
                    type="text"
                    value={formData.driver?.licenseNumber || ''}
                    onChange={(e) => handleInputChange('driver', {...formData.driver, licenseNumber: e.target.value})}
                    placeholder={formLabel('ph_driving_license')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('driver_experience')}</label>
                  <CustomSelect
                    value={formData.driver?.experience || ''}
                    onChange={(e) => handleInputChange('driver', {...formData.driver, experience: e.target.value})}
                  >
                    <option value="débutant">{formLabel('driver_experience_options.beginner')}</option>
                    <option value="confirmé">{formLabel('driver_experience_options.confirmed')}</option>
                    <option value="expérimenté">{formLabel('driver_experience_options.experienced')}</option>
                    <option value="expert">{formLabel('driver_experience_options.expert')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('vehicle_registration')}</label>
                  <input
                    type="text"
                    value={formData.vehicle?.registration || ''}
                    onChange={(e) => handleInputChange('vehicle', {...formData.vehicle, registration: e.target.value})}
                    placeholder={formLabel('ph_vehicle_registration')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('vehicle_type')}</label>
                  <CustomSelect
                    value={formData.vehicle?.type || 'camion'}
                    onChange={(e) => handleInputChange('vehicle', {...formData.vehicle, type: e.target.value})}
                  >
                    <option value="camion">{formLabel('vehicle_types.truck')}</option>
                    <option value="camionnette">{formLabel('vehicle_types.pickup_van')}</option>
                    <option value="fourgon">{formLabel('vehicle_types.cargo_van')}</option>
                    <option value="semi-remorque">{formLabel('vehicle_types.semi_trailer')}</option>
                    <option value="utilitaire">{formLabel('vehicle_types.utility')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('load_capacity_kg')}</label>
                  <CustomNumberInput
                    value={formData.vehicle?.capacity || 0}
                    onChange={(val) => handleInputChange('vehicle', {...formData.vehicle, capacity: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('route_name')}</label>
                  <input
                    type="text"
                    value={formData.route?.name || ''}
                    onChange={(e) => handleInputChange('route', {...formData.route, name: e.target.value})}
                    placeholder={formLabel('ph_route_example_north')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('delivery_territory')}</label>
                  <input
                    type="text"
                    value={formData.route?.territory || ''}
                    onChange={(e) => handleInputChange('route', {...formData.route, territory: e.target.value})}
                    placeholder={formLabel('ph_geographic_zone')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('total_distance_km')}</label>
                  <CustomNumberInput
                    value={formData.route?.totalDistance || 0}
                    onChange={(val) => handleInputChange('route', {...formData.route, totalDistance: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('departure_date')}</label>
                  <CustomDatePicker
                      value={formData.schedule?.departureDate || ''}
                      onChange={(val) => handleInputChange('schedule', {...formData.schedule, departureDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('departure_time')}</label>
                  <input
                    type="time"
                    value={formData.schedule?.departureTime || ''}
                    onChange={(e) => handleInputChange('schedule', {...formData.schedule, departureTime: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('planned_return_time')}</label>
                  <input
                    type="time"
                    value={formData.schedule?.plannedReturnTime || ''}
                    onChange={(e) => handleInputChange('schedule', {...formData.schedule, plannedReturnTime: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('total_weight_kg')}</label>
                  <CustomNumberInput
                    value={formData.cargo?.totalWeight || 0}
                    onChange={(val) => handleInputChange('cargo', {...formData.cargo, totalWeight: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('route_difficulty')}</label>
                  <CustomSelect
                    value={formData.route?.difficultyLevel || ''}
                    onChange={(e) => handleInputChange('route', {...formData.route, difficultyLevel: e.target.value})}
                  >
                    <option value="facile">{formLabel('difficulty_levels.easy')}</option>
                    <option value="">{formLabel('select_placeholder')}</option>
                    <option value="normale">{formLabel('priority_levels.normal')}</option>
                    <option value="difficile">{formLabel('difficulty_levels.difficult')}</option>
                    <option value="très difficile">{formLabel('difficulty_levels.very_difficult')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('fuel_budget')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.costs?.fuelBudget || 0}
                    onChange={(val) => handleInputChange('costs', {...formData.costs, fuelBudget: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.tracking?.gpsEnabled || false}
                      onChange={(e) => handleInputChange('tracking', {...formData.tracking, gpsEnabled: e.target.checked})}
                    />
                    {formLabel('gps_tracking_enabled')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.cargo?.temperatureControlled || false}
                      onChange={(e) => handleInputChange('cargo', {...formData.cargo, temperatureControlled: e.target.checked})}
                    />
                    {formLabel('refrigerated_goods')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.compliance?.tachographCheck || false}
                      onChange={(e) => handleInputChange('compliance', {...formData.compliance, tachographCheck: e.target.checked})}
                    />
                    {formLabel('tachograph_check')}
                  </label>
                </div>
              </div>
            </div>
          )}

          {selectedType.id === 'delivery-report' && (
            <div className="form-section">
              <h3 className="section-title" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                <FileText size={20} color={getIconColor()} />
                {formLabel('delivery_report_title')}
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('route_number')}</label>
                  <input
                    type="text"
                    value={formData.routeInfo?.routeNumber || ''}
                    onChange={(e) => handleInputChange('routeInfo', {...formData.routeInfo, routeNumber: e.target.value})}
                    placeholder={formLabel('ph_tour_reference')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('route_name')}</label>
                  <input
                    type="text"
                    value={formData.routeInfo?.routeName || ''}
                    onChange={(e) => handleInputChange('routeInfo', {...formData.routeInfo, routeName: e.target.value})}
                    placeholder={formLabel('ph_route_example_west')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('driver')}</label>
                  <input
                    type="text"
                    value={formData.routeInfo?.driver || ''}
                    onChange={(e) => handleInputChange('routeInfo', {...formData.routeInfo, driver: e.target.value})}
                    placeholder={formLabel('ph_driver_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('vehicle_used')}</label>
                  <input
                    type="text"
                    value={formData.routeInfo?.vehicle || ''}
                    onChange={(e) => handleInputChange('routeInfo', {...formData.routeInfo, vehicle: e.target.value})}
                    placeholder={formLabel('ph_vehicle_plate')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('departure_time')}</label>
                  <input
                    type="time"
                    value={formData.timing?.departureTime || ''}
                    onChange={(e) => handleInputChange('timing', {...formData.timing, departureTime: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('return_time')}</label>
                  <input
                    type="time"
                    value={formData.timing?.returnTime || ''}
                    onChange={(e) => handleInputChange('timing', {...formData.timing, returnTime: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('deliveries_planned')}</label>
                  <CustomNumberInput
                    value={formData.performance?.deliveriesPlanned || 0}
                    onChange={(val) => handleInputChange('performance', {...formData.performance, deliveriesPlanned: parseInt(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('deliveries_completed')}</label>
                  <CustomNumberInput
                    value={formData.performance?.deliveriesCompleted || 0}
                    onChange={(val) => handleInputChange('performance', {...formData.performance, deliveriesCompleted: parseInt(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('deliveries_failed')}</label>
                  <CustomNumberInput
                    value={formData.performance?.deliveriesFailed || 0}
                    onChange={(val) => handleInputChange('performance', {...formData.performance, deliveriesFailed: parseInt(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('mileage_start')}</label>
                  <CustomNumberInput
                    value={formData.vehicle?.mileageStart || 0}
                    onChange={(val) => handleInputChange('vehicle', {...formData.vehicle, mileageStart: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('mileage_end')}</label>
                  <CustomNumberInput
                    value={formData.vehicle?.mileageEnd || 0}
                    onChange={(val) => handleInputChange('vehicle', {...formData.vehicle, mileageEnd: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('fuel_consumed_l')}</label>
                  <CustomNumberInput
                    value={formData.vehicle?.fuelConsumed || 0}
                    onChange={(val) => handleInputChange('vehicle', {...formData.vehicle, fuelConsumed: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={0.1}
                    decimals={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('fuel_cost')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.vehicle?.fuelCost || 0}
                    onChange={(val) => handleInputChange('vehicle', {...formData.vehicle, fuelCost: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('vehicle_condition')}</label>
                  <CustomSelect
                    value={formData.vehicle?.condition || 'bon'}
                    onChange={(e) => handleInputChange('vehicle', {...formData.vehicle, condition: e.target.value})}
                  >
                    <option value="">{formLabel('select_placeholder')}</option>
                    <option value="excellent">{formLabel('condition_levels.excellent')}</option>
                    <option value="bon">{formLabel('condition_levels.good')}</option>
                    <option value="satisfaisant">{formLabel('condition_levels.satisfactory')}</option>
                    <option value="médiocre">{formLabel('condition_levels.mediocre')}</option>
                    <option value="mauvais">{formLabel('condition_levels.bad')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('total_expenses')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.expenses?.total || 0}
                    onChange={(val) => handleInputChange('expenses', {...formData.expenses, total: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('success_rate_pct')}</label>
                  <CustomNumberInput
                    value={formData.performance?.deliverySuccessRate || 100}
                    onChange={(val) => handleInputChange('performance', {...formData.performance, deliverySuccessRate: parseFloat(val) || 100})}
                    placeholder="100"
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.cargo?.damageReported || false}
                      onChange={(e) => handleInputChange('cargo', {...formData.cargo, damageReported: e.target.checked})}
                    />
                    {formLabel('damage_reported')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.expenses?.receipts || false}
                      onChange={(e) => handleInputChange('expenses', {...formData.expenses, receipts: e.target.checked})}
                    />
                    {formLabel('receipts_provided')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.safety?.accidentsReported === 0 ? false : true}
                      onChange={(e) => handleInputChange('safety', {...formData.safety, accidentsReported: e.target.checked ? 1 : 0})}
                    />
                    {formLabel('accidents_reported')}
                  </label>
                </div>
              </div>
            </div>
          )}
          {selectedType.id === 'transport-voucher' && (
            <div className="form-section">
              <h3 className="section-title" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                <Send size={20} color={getIconColor()} />
                {formLabel('transport_voucher_title')}
              </h3>
              
              <div className="form-grid">
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('shipper')}</label>
                  <input
                    type="text"
                    value={formData.shipper?.name || ''}
                    onChange={(e) => handleInputChange('shipper', {...formData.shipper, name: e.target.value})}
                    placeholder={formLabel('ph_shipper_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('carrier')}</label>
                  <input
                    type="text"
                    value={formData.carrier?.company || ''}
                    onChange={(e) => handleInputChange('carrier', {...formData.carrier, company: e.target.value})}
                    placeholder={formLabel('ph_transport_company')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('recipient')}</label>
                  <input
                    type="text"
                    value={formData.consignee?.name || ''}
                    onChange={(e) => handleInputChange('consignee', {...formData.consignee, name: e.target.value})}
                    placeholder={formLabel('ph_recipient_name')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('transport_mode')}</label>
                  <CustomSelect
                    value={formData.transport?.mode || 'routier'}
                    onChange={(e) => handleInputChange('transport', {...formData.transport, mode: e.target.value})}
                  >
                    <option value="routier">{formLabel('shipment_modes.road')}</option>
                    <option value="ferroviaire">{formLabel('shipment_modes.rail')}</option>
                    <option value="maritime">{formLabel('shipment_modes.sea')}</option>
                    <option value="aérien">{formLabel('shipment_modes.air')}</option>
                    <option value="multimodal">{formLabel('shipment_modes.multimodal')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('service_type')}</label>
                  <CustomSelect
                    value={formData.transport?.type || 'livraison'}
                    onChange={(e) => handleInputChange('transport', {...formData.transport, type: e.target.value})}
                  >
                    <option value="livraison">{formLabel('transport_service_types.direct_delivery')}</option>
                    <option value="messagerie">{formLabel('transport_service_types.courier')}</option>
                    <option value="groupage">{formLabel('transport_service_types.groupage')}</option>
                    <option value="déménagement">{formLabel('transport_service_types.moving')}</option>
                    <option value="frigorifique">{formLabel('transport_service_types.refrigerated')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('urgency_level')}</label>
                  <CustomSelect
                    value={formData.transport?.urgency || ''}
                    onChange={(e) => handleInputChange('transport', {...formData.transport, urgency: e.target.value})}
                  >
                    <option value="">{formLabel('select_placeholder')}</option>
                    <option value="normale">{formLabel('priority_levels.normal')}</option>
                    <option value="prioritaire">{formLabel('transport_urgency_options.priority')}</option>
                    <option value="urgente">{formLabel('priority_levels.urgent')}</option>
                    <option value="express">{formLabel('transport_urgency_options.express')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('pickup_date')}</label>
                  <CustomDatePicker
                      value={formData.transport?.pickupDate || ''}
                      onChange={(val) => handleInputChange('transport', {...formData.transport, pickupDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.delivery_date')}</label>
                  <CustomDatePicker
                      value={formData.transport?.deliveryDate || ''}
                      onChange={(val) => handleInputChange('transport', {...formData.transport, deliveryDate: val})}
                    />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('packaging_type')}</label>
                  <CustomSelect
                    value={formData.cargo?.packagingType || 'carton'}
                    onChange={(e) => handleInputChange('cargo', {...formData.cargo, packagingType: e.target.value})}
                  >
                    <option value="">{formLabel('select_placeholder')}</option>
                    <option value="carton">{formLabel('packaging_types.carton')}</option>
                    <option value="caisse">{formLabel('packaging_types.wooden_crate')}</option>
                    <option value="palette">{formLabel('packaging_types.pallet')}</option>
                    <option value="fût">{formLabel('packaging_types.drum')}</option>
                    <option value="vrac">{formLabel('packaging_types.bulk')}</option>
                    <option value="conteneur">{formLabel('packaging_types.container')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('package_count')}</label>
                  <CustomNumberInput
                    value={formData.cargo?.packages || 0}
                    onChange={(val) => handleInputChange('cargo', {...formData.cargo, packages: parseInt(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('total_weight_kg')}</label>
                  <CustomNumberInput
                    value={formData.cargo?.weight || 0}
                    onChange={(val) => handleInputChange('cargo', {...formData.cargo, weight: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('declared_value')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.cargo?.value || 0}
                    onChange={(val) => handleInputChange('cargo', {...formData.cargo, value: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('transport_cost')} (${getBrandCurrencySuffix()})</label>
                  <CustomNumberInput
                    value={formData.pricing?.transportCost || 0}
                    onChange={(val) => handleInputChange('pricing', {...formData.pricing, transportCost: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={100}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('distance_km')}</label>
                  <CustomNumberInput
                    value={formData.route?.distance || 0}
                    onChange={(val) => handleInputChange('route', {...formData.route, distance: parseFloat(val) || 0})}
                    placeholder="0"
                    min={0}
                    step={1}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('origin')}</label>
                  <input
                    type="text"
                    value={formData.route?.origin || ''}
                    onChange={(e) => handleInputChange('route', {...formData.route, origin: e.target.value})}
                    placeholder={formLabel('ph_departure_address')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('destination')}</label>
                  <input
                    type="text"
                    value={formData.route?.destination || ''}
                    onChange={(e) => handleInputChange('route', {...formData.route, destination: e.target.value})}
                    placeholder={t('document_creation.form.search_location')}
                  />
                </div>
                <div className="form-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.payment_terms')}</label>
                  <CustomSelect
                    value={formData.pricing?.paymentTerms || ''}
                    onChange={(e) => handleInputChange('pricing', {...formData.pricing, paymentTerms: e.target.value})}
                  >
                    <option value="">{formLabel('select_placeholder')}</option>
                    <option value="comptant">{formLabel('payment_terms_options.cash')}</option>
                    <option value="15 jours">{formLabel('payment_deadlines.15_days')}</option>
                    <option value="30 jours">{formLabel('payment_deadlines.30_days')}</option>
                    <option value="60 jours">{formLabel('payment_deadlines.60_days')}</option>
                  </CustomSelect>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.specialHandling?.fragile || false}
                      onChange={(e) => handleInputChange('specialHandling', {...formData.specialHandling, fragile: e.target.checked})}
                    />
                    {formLabel('fragile_goods')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.specialHandling?.hazardous || false}
                      onChange={(e) => handleInputChange('specialHandling', {...formData.specialHandling, hazardous: e.target.checked})}
                    />
                    {formLabel('hazardous_materials')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.insurance?.required || false}
                      onChange={(e) => handleInputChange('insurance', {...formData.insurance, required: e.target.checked})}
                    />
                    {formLabel('insurance_required')}
                  </label>
                </div>
                <div className="form-group checkbox-group">
                  <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                    <input
                      type="checkbox"
                      checked={formData.specialHandling?.temperatureControlled || false}
                      onChange={(e) => handleInputChange('specialHandling', {...formData.specialHandling, temperatureControlled: e.target.checked})}
                    />
                    {formLabel('refrigerated_transport')}
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('cargo_description')}</label>
                <textarea
                  value={formData.cargo?.description || ''}
                  onChange={(e) => handleInputChange('cargo', {...formData.cargo, description: e.target.value})}
                  placeholder={formLabel('ph_cargo_description')}
                  rows="2"
                />
              </div>
              
              <div className="form-group">
                <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.delivery_location')}</label>
                <textarea
                  value={formData.consignee?.address || ''}
                  onChange={(e) => handleInputChange('consignee', {...formData.consignee, address: e.target.value})}
                  placeholder={t('document_creation.form.search_location')}
                  rows="2"
                />
              </div>
            </div>
          )}
          {/* Taxes et totaux — masqués pour docs logistique/paiement et demandes devis/proforma */}
          {selectedType && hasCalculsRemisesSection(selectedType.id) && (
            <div className="form-section">
              <h3 className="section-title" style={{ borderBottom: 'none', paddingBottom: 0, marginBottom: '1rem' }}>
                <Calculator size={20} color={getIconColor()} />
                {t('document_creation.form.calculations_discounts')}
              </h3>
              
              {/* Tax Element Checkboxes with Drag & Drop */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                {(() => {
                  let isFirst = true;
                  return taxOrder.map((taxElement) => {
                    if (!isTaxAvailable(taxElement)) return null;
                    
                    // ?? FIX: Convert field name to match formData naming (e.g., 'TVA' -> 'showTva')
                    const field = `show${taxElement.charAt(0).toUpperCase()}${taxElement.slice(1).toLowerCase()}`;
                    const showSeparator = !isFirst;
                    isFirst = false;

                    return (
                      <React.Fragment key={taxElement}>
                        {showSeparator && <span className="text-white/40">/</span>}
                        <button 
                          type="button"
                          draggable
                          onDragStart={(e) => handleTaxDragStart(e, taxElement)}
                          onDragOver={handleTaxDragOver}
                          onDrop={(e) => handleTaxDrop(e, taxElement)}
                          onDragEnd={handleTaxDragEnd}
                          onClick={() => handleInputChange(field, !formData[field])}
                          className="flex items-center gap-2 hover:text-blue-300 transition-colors cursor-pointer"
                          style={{ 
                            fontSize: '0.95rem', 
                            fontWeight: 'normal', 
                            color: isDashboardLightMode ? '#374151' : '#ffffff',
                            opacity: draggedTax === taxElement ? 0.5 : 1
                          }}
                        >
                          {formData[field] ? <CheckSquare size={18} style={{ color: isDashboardLightMode ? '#059669' : '#ffffff' }} /> : <Square size={18} style={{ color: isDashboardLightMode ? '#9ca3af' : '#ffffff' }} />}
                          <span style={{ cursor: draggedTax === taxElement ? 'grabbing' : 'grab' }}>
                            {getTaxElementDisplayLabel(t, taxElement)}
                          </span>
                        </button>
                      </React.Fragment>
                    );
                  });
                })()}
              </div>
              
              {/* Separator line under checkboxes */}
              <div style={{ borderTop: '2px solid rgba(255, 255, 255, 0.15)', paddingTop: '0.75rem', marginTop: '0.25rem', marginBottom: '1rem' }}></div>

              {/* Two-column layout: Tax inputs (left) + Totals (right) */}
              <div className="totals-container" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                
                {/* LEFT: Tax Input Fields */}
<div className="taxes-config">
                  {taxOrder.map((taxElement) => {
                    // ?? FIX: Convert field name to match formData naming (e.g., 'TVA' -> 'showTva')
                    const showField = `show${taxElement.charAt(0).toUpperCase()}${taxElement.slice(1).toLowerCase()}`;
                    if (!isTaxAvailable(taxElement) || !formData[showField]) return null;
                    
                    // Timbre is auto-calculated ? show info note, no manual input
                    if (taxElement === 'Timbre') {
                      return (
                        <div key={taxElement} className="form-group">
                          <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                            {formLabel('fiscal_stamp_auto')}
                          </label>
                          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', lineHeight: '1.5', padding: '6px 0' }}>
                            {formLabel('fiscal_stamp_calc_header')}<br/>
                            {formLabel('fiscal_stamp_calc_exempt')}<br/>
                            {formLabel('fiscal_stamp_calc_tier1')}<br/>
                            {formLabel('fiscal_stamp_calc_tier2')}<br/>
                            {formLabel('fiscal_stamp_calc_tier3')}
                          </div>
                        </div>
                      );
                    }

                    const isPercentage = ['TVA', 'TAP', 'Remise', 'Escompte', 'IRG', 'IBS'].includes(taxElement);
                    const fieldName = taxElement === 'TVA' ? 'tvaRate' : 
                                     taxElement === 'TAP' ? 'tapRate' :
                                     taxElement === 'IRG' ? 'irg' :
                                     taxElement === 'IBS' ? 'ibs' :
                                     taxElement.toLowerCase();
                    const defaultValue = taxElement === 'TVA' ? getBrandVatRate() : 0;
                    
                    const label = getTaxElementDisplayLabel(t, taxElement);
                    
                    return (
                      <div key={taxElement} className="form-group">
                        <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
                          {label} {isPercentage ? '(%)' : '(${getBrandCurrencySuffix()})'}
                        </label>
                        <CustomNumberInput
                          value={formData[fieldName] ?? defaultValue}
                          onChange={(val) => handleInputChange(fieldName, val === '' ? 0 : parseFloat(val))}
                          min={0}
                          step={0.01}
                          decimals={2}
                        />
                      </div>
                    );
                  })}
                </div>

                {/* RIGHT: Totals Summary */}
                <div className="totals-summary">
                  <div className="totals-summary-header">{t('document_creation.form.totals_summary')}</div>
                  <div className="totals-summary-body">
                  {(() => {
                    const items = formData.items || [];
                    
                    // Standard line total: same basis as handleItemChange / handleProductSelect (items[].price ? qty, or line total)
                    const sumItemsSubtotal = () => items.reduce((sum, item) => {
                      const lineFromTotal = parseFloat(item.total);
                      if (!Number.isNaN(lineFromTotal) && lineFromTotal > 0) {
                        return sum + lineFromTotal;
                      }
                      let pr = 0;
                      const clientOrSupplierId = formData.client || formData.supplier;
                      const sc = clients.find(c => c.id.toString() === String(clientOrSupplierId));
                      if (sc && sc.type) {
                        pr = getClientTypePreferentialRate(sc.type, settings);
                      }
                      const bp = parseFloat(item.price || item.unitPrice) || 0;
                      const unitPrice = bp * (1 - pr / 100);
                      const quantity = parseFloat(item.quantity) || 1;
                      return sum + (unitPrice * quantity);
                    }, 0);
                    
                    // Calculate subtotal: Demande de devis uses the ligne produits (items), NOT specifications alone ?
                    // specifications kept legacy budget lines; UI fills items. Use specs only if they have non-zero budget.
                    let subtotal = 0;
                    if (selectedType.id === 'quote-request' && formData.specifications && formData.specifications.length > 0) {
                      const specSubtotal = formData.specifications.reduce((sum, spec) => {
                        const budget = parseFloat(spec.budget) || 0;
                        const quantity = parseInt(spec.quantity, 10) || 1;
                        return sum + (budget * quantity);
                      }, 0);
                      const fromItems = sumItemsSubtotal();
                      subtotal = specSubtotal > 0 ? specSubtotal : fromItems;
                    } else {
                      subtotal = sumItemsSubtotal();
                    }
                    
                    // ?? ALGERIAN TAX CALCULATION - Complete flow with all 10 elements
                    
                    // 1. Sous-total HT (from products)
                    let currentTotal = subtotal;
                    
                    // 2. Apply Remise commerciale (%) - Before tax
                    const remiseRate = formData.showRemise ? (parseFloat(formData.remise) || 0) : 0;
                    const remiseAmount = currentTotal * (remiseRate / 100);
                    currentTotal = currentTotal - remiseAmount;
                    const netCommercial = currentTotal;
                    
                    // 3. Add Transport
                    const transport = formData.showTransport ? (parseFloat(formData.transport) || 0) : 0;
                    currentTotal = currentTotal + transport;
                    const vatMode = formData.vatMode || getFacturationVatMode(settings);
                    const tvaRate = formData.showTva ? (parseFloat(formData.tvaRate) || getBrandVatRate()) : 0;
                    const { totalHT, tvaAmount, totalTTC: ttcBeforeTap } = formData.showTva
                      ? computeVatBreakdown(currentTotal, tvaRate, vatMode)
                      : { totalHT: currentTotal, tvaAmount: 0, totalTTC: currentTotal };
                    
                    // 5. Calculate TAP (2%)
                    const tapParsed = parseFloat(formData.tapRate);
                    const tapRate = formData.showTap && Number.isFinite(tapParsed) ? tapParsed : 0;
                    const tapAmount = totalHT * (tapRate / 100);

                    currentTotal = ttcBeforeTap + tapAmount;
                    const totalTTC = currentTotal;
                    
                    // 7. Add Timbre (Droits de timbre) ? auto-calculated per LF 2025
                    const calcTimbre = (base) => {
                      if (base <= 300) return 0;
                      const tranches = Math.ceil(base / 100);
                      let t;
                      if (base <= 30000) t = tranches * 1;
                      else if (base <= 100000) t = tranches * 1.5;
                      else t = tranches * 2;
                      return Math.max(5, t);
                    };
                    const timbre = formData.showTimbre ? calcTimbre(totalTTC) : 0;
                    currentTotal = currentTotal + timbre;
                    
                    // 8. Subtract Rabais (fixed amount)
                    const rabais = formData.showRabais ? (parseFloat(formData.rabais) || 0) : 0;
                    currentTotal = currentTotal - rabais;
                    
                    // 9. Subtract Escompte (%) - Early payment discount
                    const escompteRate = formData.showEscompte ? (parseFloat(formData.escompte) || 0) : 0;
                    const escompteAmount = totalTTC * (escompteRate / 100);
                    currentTotal = currentTotal - escompteAmount;
                    
                    // 11. Subtract Acompte (down payment already paid)
                    const acompte = formData.showAcompte ? (parseFloat(formData.acompte) || 0) : 0;
                    currentTotal = currentTotal - acompte;

                    // 11b. Subtract Avance (advance payment)
                    const avance = formData.showAvance ? (parseFloat(formData.avance) || 0) : 0;
                    currentTotal = currentTotal - avance;

                    // 12. Subtract Retenue de garantie
                    const garantie = formData.showGarantie ? (parseFloat(formData.garantie) || 0) : 0;
                    currentTotal = currentTotal - garantie;
                    
                    // 12b. Subtract IRG withholding tax (%)
                    const irgRate = formData.showIrg ? (parseFloat(formData.irg) || 0) : 0;
                    const irgAmount = totalTTC * (irgRate / 100);
                    currentTotal = currentTotal - irgAmount;
                    
                    // 12c. Subtract IBS withholding tax (%)
                    const ibsRate = formData.showIbs ? (parseFloat(formData.ibs) || 0) : 0;
                    const ibsAmount = totalTTC * (ibsRate / 100);
                    currentTotal = currentTotal - ibsAmount;
                    
                    // 13. Add Retour (return shipping cost)
                    const retour = formData.showRetour ? (parseFloat(formData.retour) || 0) : 0;
                    currentTotal = currentTotal + retour;
                    
                    // Final: Net à Payer
                    const netAPayer = currentTotal;
                    
                    return (
                      <>
                        <div className="total-line">
                          <span>{t('document_creation.form.totals_subtotal_ex_vat')}:</span>
                          <span>{formatBrandMoney(subtotal)}</span>
                        </div>
                        {formData.showRemise && (
                          <div className="total-line">
                            <span>{t('document_creation.form.totals_discount')} ({remiseRate}%):</span>
                            <span style={{ color: '#ef4444', fontWeight: 700 }}>-{formatBrandMoney(remiseAmount)}</span>
                          </div>
                        )}
                        {formData.showRemise && (
                          <div className="total-line">
                            <span>{t('document_creation.form.totals_net_commercial')}:</span>
                            <span>{formatBrandMoney(netCommercial)}</span>
                          </div>
                        )}
                        {formData.showTransport && (
                          <div className="total-line">
                            <span>{t('document_creation.form.totals_transport')}:</span>
                            <span>{formatBrandMoney(transport)}</span>
                          </div>
                        )}
                        <div className="total-line">
                          <span>{t('document_creation.form.totals_total_ex_vat')}:</span>
                          <span>{formatBrandMoney(totalHT)}</span>
                        </div>
                        {formData.showTva && (
                          <div className="total-line">
                            <span>{t('document_creation.form.totals_vat')} ({tvaRate}%):</span>
                            <span>{formatBrandMoney(tvaAmount)}</span>
                          </div>
                        )}
                        {formData.showTap && (
                          <div className="total-line">
                            <span>{formLabel('totals_tap')} ({tapRate}%):</span>
                            <span>{formatBrandMoney(tapAmount)}</span>
                          </div>
                        )}
                        <div className="total-line">
                          <span>{t('document_creation.form.totals_total_inc_vat')}:</span>
                          <span>{formatBrandMoney(totalTTC)}</span>
                        </div>
                        {formData.showTimbre && (
                          <div className="total-line">
                            <span>{formLabel('totals_fiscal_stamp')}:</span>
                            <span>{formatBrandMoney(timbre)}</span>
                          </div>
                        )}
                        {formData.showRabais && (
                          <div className="total-line">
                            <span>{formLabel('totals_rebate')}:</span>
                            <span style={{ color: '#ef4444', fontWeight: 700 }}>-{formatBrandMoney(rabais)}</span>
                          </div>
                        )}
                        {formData.showEscompte && (
                          <div className="total-line">
                            <span>{formLabel('totals_early_discount')} ({escompteRate}%):</span>
                            <span style={{ color: '#ef4444', fontWeight: 700 }}>-{formatBrandMoney(escompteAmount)}</span>
                          </div>
                        )}
                        {formData.showAcompte && (
                          <div className="total-line">
                            <span>{t('document_creation.form.totals_down_payment')}:</span>
                            <span style={{ color: '#ef4444', fontWeight: 700 }}>-{formatBrandMoney(acompte)}</span>
                          </div>
                        )}
                        {formData.showAvance && (
                          <div className="total-line">
                            <span>{t('document_creation.form.totals_advance')}:</span>
                            <span style={{ color: '#ef4444', fontWeight: 700 }}>-{formatBrandMoney(avance)}</span>
                          </div>
                        )}
                        {formData.showGarantie && (
                          <div className="total-line">
                            <span>{t('document_creation.form.totals_retention')}:</span>
                            <span style={{ color: '#ef4444', fontWeight: 700 }}>-{formatBrandMoney(garantie)}</span>
                          </div>
                        )}
                        {formData.showIrg && (
                          <div className="total-line">
                            <span>{formLabel('withholding_irg')} ({irgRate}%):</span>
                            <span style={{ color: '#ef4444', fontWeight: 700 }}>-{formatBrandMoney(irgAmount)}</span>
                          </div>
                        )}
                        {formData.showIbs && (
                          <div className="total-line">
                            <span>{formLabel('withholding_ibs')} ({ibsRate}%):</span>
                            <span style={{ color: '#ef4444', fontWeight: 700 }}>-{formatBrandMoney(ibsAmount)}</span>
                          </div>
                        )}
                        {formData.showRetour && (
                          <div className="total-line">
                            <span>{formLabel('return_cost')}:</span>
                            <span>{formatBrandMoney(retour)}</span>
                          </div>
                        )}
                        <div className="total-line total">
                          <span>{t('document_creation.form.totals_net_payable')} :</span>
                          <span>{formatBrandMoney(netAPayer)}</span>
                        </div>
                      </>
                    );
                  })()}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Attachment Description Section */}
          {selectedType.id === 'attachment' && (
            <div className="form-section">
              <h3 className="section-title" style={{ borderBottom: 'none', paddingBottom: 0 }}>
                <FileText size={20} color={getIconColor()} />
                {formLabel('attachment_description_title')}
              </h3>
              
              <div className="form-group">
                <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('detailed_description')}</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder={formLabel('ph_document_content_objective')}
                  rows="4"
                />
              </div>
            </div>
          )}

          {/* Section échéances - AFTER Calculs & Remises (invoice, proforma, purchase-order) */}
          {(selectedType.id === 'invoice' || selectedType.id === 'proforma' || selectedType.id === 'purchase-order') && (
            <InstallmentsSection 
              formData={formData}
              handleInputChange={handleInputChange}
              selectedType={selectedType}
              isDashboardLightMode={isDashboardLightMode}
              getIconColor={getIconColor}
            />
          )}

          {/* Supplementary information — single free-text field */}
          <div className="form-section">
            {/* Section Title with Checkbox */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
              <h3 className="section-title" style={{ borderBottom: 'none', paddingBottom: 0, marginBottom: 0 }}>
                <FileText size={20} color={getIconColor()} />
                {t('document_creation.form.supplementary_info')}
              </h3>
              <button 
                type="button"
                onClick={() => handleInputChange('showSupplementarySections', formData.showSupplementarySections === false)}
                className="flex items-center gap-2 hover:text-blue-300 transition-colors"
              >
                {formData.showSupplementarySections !== false ? 
                  <CheckSquare size={20} style={{ color: '#10b981' }} /> : 
                  <Square size={20} style={{ color: '#666666' }} />
                }
              </button>
            </div>
            
            {formData.showSupplementarySections !== false && (
              <div className="form-group">
                <textarea
                  value={formData[getSupplementaryFormFieldName(selectedType.id)] || ''}
                  onChange={(e) => handleInputChange(getSupplementaryFormFieldName(selectedType.id), e.target.value)}
                  placeholder={t('document_creation.form.supplementary_placeholder')}
                  rows="4"
                />
              </div>
            )}
          </div>

          {/* ?? SIGNATURES - 3 Sections in One Card */}
          <div className="form-section">
            
            {/* ?? SECTION 1: BON POUR */}
            <div style={{ marginBottom: '30px' }}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <PenTool size={20} color="#10b981" />
                  <h3 className={`text-lg font-semibold ${isDashboardLightMode ? 'text-black' : 'text-white'}`}>
                    {t(`document_creation.form.signatures.bon_pour.${resolveBonPourTitleKey(selectedType.id)}`, {
                      defaultValue: t('document_creation.form.signatures.bon_pour.default'),
                    })}
                  </h3>
                </div>
                <button 
                  type="button"
                  onClick={() => handleInputChange('showBonPour', !formData.showBonPour)}
                  className="flex items-center gap-2 hover:text-blue-300 transition-colors"
                >
                  {formData.showBonPour ? 
                    <CheckSquare size={20} style={{ color: '#10b981' }} /> : 
                    <Square size={20} style={{ color: '#666666' }} />
                  }
                </button>
              </div>
              {formData.showBonPour && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'}><RequiredLabelText text={t('document_creation.form.signatures.full_name')} required /></label>
                    <CustomSelect
                      value={formData.bonPourName || ''}
                      onChange={(value) => {
                        handleInputChange('bonPourName', value);
                        const selectedEmp = availableEmployeesForDropdown.find(e => e.value === value);
                        if (selectedEmp && selectedEmp.position) {
                          handleInputChange('bonPourRole', selectedEmp.position);
                        }
                      }}
                      options={getSignatureNameOptions('bonPour')}
                      placeholder={t('document_creation.form.signatures.select_employee')}
                      searchable={true}
                      creatable={true}
                      createLabel={t('document_creation.form.signatures.create_signatory')}
                    />
                  </div>
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'}>{t('document_creation.form.signatures.role')}</label>
                    <input type="text" value={formData.bonPourRole || ''} onChange={(e) => handleInputChange('bonPourRole', e.target.value)} placeholder={t('document_creation.form.signatures.role_example')} />
                  </div>
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'}>{t('document_creation.form.signatures.signature_date')}</label>
                    <CustomDatePicker value={formData.bonPourDate || ''} onChange={(val) => handleInputChange('bonPourDate', val)} placeholder={t('document_creation.form.signatures.select_date')} />
                  </div>
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'}>{t('document_creation.form.signatures.signature_place')}</label>
                    <input type="text" value={formData.bonPourPlace || ''} onChange={(e) => handleInputChange('bonPourPlace', e.target.value)} placeholder={t('document_creation.form.signatures.place_example')} />
                  </div>
                </div>
              )}
            </div>

            {/* ?? SECTION 2: ÉTABLIE PAR */}
            <div style={{ marginBottom: '30px', paddingTop: '20px', borderTop: isDashboardLightMode ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(255,255,255,0.1)' }}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <User size={20} color={getIconColor()} />
                  <h3 className={`text-lg font-semibold ${isDashboardLightMode ? 'text-black' : 'text-white'}`}>
                    {t('document_creation.form.signatures.prepared_by_title')}
                  </h3>
                </div>
                <button 
                  type="button"
                  onClick={() => {
                    const newValue = !formData.showEtabliePar;
                    handleInputChange('showEtabliePar', newValue);
                    // Auto-fill from "Établi par" field when enabling
                    if (newValue && formData.issuedBy) {
                      handleInputChange('etablieParName', formData.issuedBy);
                      handleInputChange('etablieParDate', new Date().toISOString().split('T')[0]);
                    }
                  }}
                  className="flex items-center gap-2 hover:text-blue-300 transition-colors"
                >
                  {formData.showEtabliePar ? 
                    <CheckSquare size={20} style={{ color: isSaudiBrand() ? '#3a624e' : '#3b82f6' }} /> : 
                    <Square size={20} style={{ color: '#666666' }} />
                  }
                </button>
              </div>
              {formData.showEtabliePar && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'}>{t('document_creation.form.signatures.full_name')}</label>
                    <CustomSelect
                      value={formData.etablieParName || ''}
                      onChange={(value) => {
                        handleInputChange('etablieParName', value);
                        const selectedFact = availableFacturiersForDropdown.find(f => f.value === value);
                        if (selectedFact && selectedFact.position) {
                          handleInputChange('etablieParRole', selectedFact.position);
                        }
                      }}
                      options={getSignatureNameOptions('etabliePar')}
                      placeholder={t('document_creation.form.signatures.select_or_enter_name')}
                      searchable={true}
                      creatable={true}
                      createLabel={t('document_creation.form.signatures.create_preparer')}
                    />
                  </div>
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'}>{t('document_creation.form.signatures.role')}</label>
                    <input type="text" value={formData.etablieParRole || ''} onChange={(e) => handleInputChange('etablieParRole', e.target.value)} placeholder={t('document_creation.form.signatures.role_example_finance')} />
                  </div>
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'}>{t('document_creation.form.signatures.preparation_date')}</label>
                    <CustomDatePicker value={formData.etablieParDate || ''} onChange={(val) => handleInputChange('etablieParDate', val)} placeholder={t('document_creation.form.signatures.select_date')} />
                  </div>
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'}>{t('document_creation.form.signatures.preparation_place')}</label>
                    <input type="text" value={formData.etablieParPlace || ''} onChange={(e) => handleInputChange('etablieParPlace', e.target.value)} placeholder={t('document_creation.form.signatures.place_example')} />
                  </div>
                </div>
              )}
            </div>

            {/* ?? SECTION 3: REÇUE PAR */}
            <div style={{ paddingTop: '20px', borderTop: isDashboardLightMode ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(255,255,255,0.1)' }}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Package size={20} color="#f59e0b" />
                  <h3 className={`text-lg font-semibold ${isDashboardLightMode ? 'text-black' : 'text-white'}`}>
                    {t(`document_creation.form.signatures.${resolveReceivedByTitleKey(selectedType.id, formData.creditNoteParty)}`)}
                  </h3>
                </div>
                <button 
                  type="button"
                  onClick={() => {
                    const newValue = !formData.showRecuePar;
                    handleInputChange('showRecuePar', newValue);
                    // Auto-populate recipient name and fonction when enabling
                    if (newValue) {
                      const autoName = getAutoPopulatedRecipientName();
                      const autoRole = getAutoPopulatedRecipientRole();
                      if (autoName) {
                        handleInputChange('recueParName', autoName);
                      }
                      if (autoRole) {
                        handleInputChange('recueParRole', autoRole);
                      }
                      handleInputChange('recueParDate', new Date().toISOString().split('T')[0]);
                    }
                  }}
                  className="flex items-center gap-2 hover:text-blue-300 transition-colors"
                >
                  {formData.showRecuePar ? 
                    <CheckSquare size={20} style={{ color: '#f59e0b' }} /> : 
                    <Square size={20} style={{ color: '#666666' }} />
                  }
                </button>
              </div>
              {formData.showRecuePar && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'}>{t('document_creation.form.signatures.full_name')}</label>
                    <CustomSelect
                      value={formData.recueParName || ''}
                      onChange={(value) => {
                        handleInputChange('recueParName', value);
                        const options = getSignatureNameOptions('recuePar');
                        const selectedPerson = options.find(opt => opt.value === value);
                        if (selectedPerson && (selectedPerson.destinataire_fonction || selectedPerson.position)) {
                          handleInputChange('recueParRole', selectedPerson.destinataire_fonction || selectedPerson.position);
                        }
                      }}
                      options={getSignatureNameOptions('recuePar')}
                      placeholder={t('document_creation.form.signatures.select_or_enter_name')}
                      searchable={true}
                      creatable={true}
                      createLabel={t('document_creation.form.signatures.create_recipient')}
                    />
                  </div>
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'}>{t('document_creation.form.signatures.role')}</label>
                    <input type="text" value={formData.recueParRole || ''} onChange={(e) => handleInputChange('recueParRole', e.target.value)} placeholder={t('document_creation.form.signatures.role_example_reception')} />
                  </div>
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'}>{t('document_creation.form.signatures.reception_date')}</label>
                    <CustomDatePicker value={formData.recueParDate || ''} onChange={(val) => handleInputChange('recueParDate', val)} placeholder={t('document_creation.form.signatures.select_date')} />
                  </div>
                  <div className="form-group">
                    <label className={isDashboardLightMode ? 'text-black' : 'text-white'}>{t('document_creation.form.signatures.reception_place')}</label>
                    <input type="text" value={formData.recueParPlace || ''} onChange={(e) => handleInputChange('recueParPlace', e.target.value)} placeholder={t('document_creation.form.signatures.place_example')} />
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>
          </div>
        </div>
      </div>
      </>
    );
  }

  return (
    <>
      <style>{DOCUMENT_CREATION_INJECTED_STYLES}</style>
      
      <div 
        className={documentCreationLightClass}
        style={{
          minHeight: '100vh',
          padding: '1.5rem',
          width: '100%',
          maxWidth: '100%',
          margin: '0 auto'
        }}
      >
      {/* <DocumentButtonsOverride /> */} {/* FILE DELETED */}
      
      {/* Header Section */}
      <div className="mb-3">
        <h1 className={`text-3xl font-bold mb-1 flex items-center gap-3 ${isDashboardLightMode ? 'text-gray-900' : 'text-white'}`}>
          <FileText className="h-8 w-8 text-blue-400" />
          {t('sidebar.create_document')}
        </h1>
        <p className={`text-lg ${isDashboardLightMode ? 'text-gray-600' : 'text-white/70'}`}>
          {t('document_creation.page_subtitle')}
        </p>
      </div>

      {/* Document picker — 5 client types, one place */}
      <div className="w-full">
        <motion.div
          className="document-categories"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-4">
            {documentCategories[0].documents.map((doc, docIndex) => (
              <motion.div
                key={doc.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: docIndex * 0.05 }}
                onClick={() => !doc.disabled && handleDocumentSelect(doc, 'all')}
                className={`group relative cursor-pointer ${doc.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                  <div className={`
                    relative h-full p-5 rounded-2xl border transition-all duration-300
                    ${doc.disabled 
                      ? 'bg-white/5 border-white/10' 
                      : 'bg-gradient-to-br from-white/10 to-white/5 border-white/10 hover:border-white/30 hover:shadow-2xl hover:shadow-black/20 hover:-translate-y-2'
                    }
                  `}>
                    {/* Top accent line */}
                    <div 
                      className="absolute top-0 left-5 right-5 h-1 rounded-b-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      style={{ background: `linear-gradient(90deg, transparent, ${doc.color}, transparent)` }}
                    />
                    
                    {/* Icon */}
                    <div className="flex items-start justify-between mb-4">
                      <div 
                        className="p-3 rounded-xl transition-all duration-300 group-hover:scale-110"
                        style={{ 
                          background: `linear-gradient(135deg, ${doc.color}20, ${doc.color}10)`,
                          border: `1px solid ${doc.color}30`
                        }}
                      >
                        {React.createElement(doc.icon, { 
                          size: 22, 
                          style: { color: doc.color },
                          className: 'transition-transform duration-300 group-hover:scale-110'
                        })}
                      </div>
                      <ArrowRight 
                        className="h-5 w-5 text-white/20 group-hover:text-white/60 group-hover:translate-x-1 transition-all duration-300" 
                      />
                    </div>

                    {/* Content */}
                    <div>
                      <h3
                        className={`font-semibold text-base mb-1 transition-colors line-clamp-2 ${
                          isDashboardLightMode ? 'text-gray-900 group-hover:text-gray-950' : 'text-white group-hover:text-white'
                        } ${isRTL ? 'text-right' : ''}`}
                      >
                        {doc.title}
                      </h3>
                      <p
                        className={`text-xs mb-2 uppercase tracking-wider font-medium ${
                          isDashboardLightMode ? 'text-gray-500' : 'text-white/40'
                        } ${isRTL ? 'text-right' : ''}`}
                      >
                        {doc.subtitle}
                      </p>
                      <p
                        className={`text-sm line-clamp-2 leading-relaxed ${
                          isDashboardLightMode ? 'text-gray-600' : 'text-white/60'
                        } ${isRTL ? 'text-right' : ''}`}
                      >
                        {doc.description}
                      </p>
                    </div>

                    {/* Disabled badge */}
                    {doc.disabled && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-2xl">
                        <span className="px-3 py-1.5 bg-amber-500/20 border border-amber-500/30 rounded-full text-amber-400 text-xs font-medium flex items-center gap-1.5">
                          <AlertTriangle className="h-3 w-3" />
                          {t('document_creation.coming_soon')}
                        </span>
                      </div>
                    )}

                    {/* Hover glow effect */}
                    <div 
                      className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                      style={{ 
                        background: `radial-gradient(circle at 50% 0%, ${doc.color}15 0%, transparent 70%)` 
                      }}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
        </motion.div>
      </div>
    </div>
    </>
  );
};
// Composant pour le formulaire de facture avec sections séparées (Général et Paiement)
const InvoiceForm = ({ formData, handleInputChange, activeEmployees, isDashboardLightMode }) => {
  const { t } = useTranslation();
  const formLabel = (key) => t(`document_creation.form.${key}`);
  const orderOriginOptions = useMemo(
    () => [
      { value: 'site-web', label: formLabel('order_origins.website') },
      { value: 'telephone', label: formLabel('order_origins.phone') },
      { value: 'email', label: formLabel('order_origins.email') },
      { value: 'visite', label: formLabel('order_origins.visit') },
      { value: 'marketplace', label: formLabel('order_origins.marketplace') },
      { value: 'recommandation', label: formLabel('order_origins.recommendation') },
      { value: 'autre', label: formLabel('order_origins.other') },
    ],
    [t],
  );
  const invoicePaymentMethodOptions = useMemo(
    () => [
      { value: 'virement', label: formLabel('payment_methods.bank_transfer') },
      { value: 'cheque', label: formLabel('payment_methods.cheque') },
      { value: 'especes', label: formLabel('payment_methods.cash') },
      { value: 'carte', label: formLabel('payment_methods.card') },
      { value: 'effet', label: formLabel('payment_methods.trade_bill') },
      { value: 'echeances', label: formLabel('payment_installment') },
    ],
    [t],
  );
  const paymentTypeOptions = useMemo(
    () => [
      { value: 'total', label: formLabel('payment_types.full') },
      { value: 'partial', label: formLabel('payment_types.partial') },
      { value: 'advance', label: formLabel('payment_types.advance') },
      { value: 'installment', label: formLabel('payment_types.installment') },
    ],
    [t],
  );

  return (
    <>
      {/* Section 1: Références et traçabilité */}
      <div className="bg-white/5 rounded-xl p-5 border border-white/20 overflow-hidden">
        <div className="flex items-center gap-2 mb-4">
          <FileText size={20} color={getIconColor()} />
          <h3 className={`text-lg font-semibold ${isDashboardLightMode ? 'text-black' : 'text-white'}`}>{formLabel('references_and_traceability')}</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="form-group">
            <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('sales_rep')}</label>
            <CustomSelect
              value={formData.salesPerson || ''}
              onChange={(value) => handleInputChange('salesPerson', value)}
              options={[
                ...activeEmployees.filter(emp => emp.status === 'active' && (emp.department === 'Commercial' || emp.position?.toLowerCase().includes('commercial'))).map(emp => ({
                  value: `${emp.firstName} ${emp.lastName}`,
                  label: `${emp.firstName} ${emp.lastName}${emp.position ? ` - ${emp.position}` : ''}`
                })),
                ...activeEmployees.filter(emp => emp.status === 'active' && emp.department !== 'Commercial' && !emp.position?.toLowerCase().includes('commercial')).map(emp => ({
                  value: `${emp.firstName} ${emp.lastName}`,
                  label: `${emp.firstName} ${emp.lastName}${emp.position ? ` - ${emp.position}` : ''}`
                }))
              ]}
              placeholder={t('document_creation.form.search_sales_rep')}
              searchable
              creatable
            />
          </div>
          <div className="form-group">
            <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.form.po_reference')}</label>
            <CustomSelect
              value={formData.orderReference || ''}
              onChange={(value) => handleInputChange('orderReference', value)}
              options={purchaseOrdersForSalesClient.map((doc) => ({
                value: doc.value,
                label: doc.label,
              }))}
              placeholder={formLabel('ph_po_reference')}
              searchable
              creatable
            />
          </div>
          <div className="form-group">
            <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('delivery_note_ref')}</label>
            <input
              type="text"
              value={formData.deliveryReference || ''}
              onChange={(e) => handleInputChange('deliveryReference', e.target.value)}
              placeholder={formLabel('ph_delivery_note_ref')}
            />
          </div>
          <div className="form-group">
            <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('project_number')}</label>
            <input
              type="text"
              value={formData.projectNumber || ''}
              onChange={(e) => handleInputChange('projectNumber', e.target.value)}
              placeholder={formLabel('ph_project_number')}
            />
          </div>
          <div className="form-group">
            <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('order_origin')}</label>
            <CustomSelect
              value={formData.orderOrigin || ''}
              onChange={(value) => handleInputChange('orderOrigin', value)}
              options={orderOriginOptions}
              placeholder={t('document_creation.form.select_or_enter')}
              searchable
              creatable
            />
          </div>
        </div>
      </div>

      {/* Section 2: Modalités financières */}
      <div className="bg-white/5 rounded-xl p-5 border border-white/20 overflow-hidden">
        <div className="flex items-center gap-2 mb-4">
          <CreditCard size={20} color={getIconColor()} />
          <h3 className={`text-lg font-semibold ${isDashboardLightMode ? 'text-black' : 'text-white'}`}>{formLabel('financial_terms')}</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="form-group">
            <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('payment_delay_days')}</label>
            <CustomNumberInput
              value={formData.paymentTerms || ''}
              onChange={(val) => {
                const days = parseInt(val, 10);
                const n = Number.isNaN(days) ? '' : days;
                handleInputChange('paymentTerms', n === '' ? '' : n);
                const dateToUse = formData.date || new Date().toISOString().split('T')[0];
                if (typeof n === 'number' && n > 0) {
                  const invoiceDate = new Date(dateToUse);
                  const dueDate = new Date(invoiceDate);
                  dueDate.setDate(invoiceDate.getDate() + n);
                  handleInputChange('dueDate', dueDate.toISOString().split('T')[0]);
                }
              }}
              placeholder="30"
              min={0}
              max={365}
              step={1}
            />
          </div>
          <div className="form-group">
            <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.payment_method')}</label>
            <CustomSelect
              value={formData.paymentMethod || ''}
              onChange={(value) => handleInputChange('paymentMethod', value)}
              options={invoicePaymentMethodOptions}
              placeholder={t('document_creation.form.select_or_enter')}
              searchable
              creatable
            />
          </div>
          <div className="form-group">
            <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('payment_type')}</label>
            <CustomSelect
              value={formData.paymentType || ''}
              onChange={(value) => handleInputChange('paymentType', value)}
              options={paymentTypeOptions}
              placeholder={t('document_creation.form.select_or_enter')}
              searchable
              creatable
            />
          </div>
          <div className="form-group">
            <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('discount_conditions')}</label>
            <input
              type="text"
              value={formData.discountConditions || ''}
              onChange={(e) => handleInputChange('discountConditions', e.target.value)}
              placeholder={formLabel('ph_discount_conditions')}
            />
          </div>
          <div className="form-group col-span-2">
            <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('retention_clause')}</label>
            <textarea
              value={formData.retentionClause || ''}
              onChange={(e) => handleInputChange('retentionClause', e.target.value)}
              placeholder={formLabel('ph_retention_clause')}
              rows="2"
            />
          </div>
        </div>
      </div>
    </>
  );
};

// Composant pour le formulaire de bon de versement avec le même contenu que la facture
const PaymentVoucherForm = ({ formData, handleInputChange, activeEmployees, isDashboardLightMode }) => {
  const { t } = useTranslation();
  const formLabel = (key) => t(`document_creation.form.${key}`);

  return (
    <>
      {/* Section 1: Détails du paiement */}
      <div className="bg-white/5 rounded-xl p-5 border border-white/20 overflow-hidden">
        <div className="flex items-center gap-2 mb-4">
          <DollarSign size={20} color={getIconColor()} />
          <h3 className={`text-lg font-semibold ${isDashboardLightMode ? 'text-black' : 'text-white'}`}>{formLabel('payment_details')}</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="form-group">
            <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
              {formLabel('payment_amount')} (${getBrandCurrencySuffix()}) <span className="text-red-500">*</span>
            </label>
            <CustomNumberInput
              value={formData.paymentAmount || ''}
              onChange={(val) => handleInputChange('paymentAmount', parseFloat(val) || 0)}
              placeholder="0.00"
              min={0}
              step={0.01}
              decimals={2}
            />
            <small className={isDashboardLightMode ? 'text-gray-600' : 'text-white/60'}>{formLabel('exact_amount_hint')}</small>
          </div>

          <div className="form-group">
            <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>
              {formLabel('document_reference_optional')}
            </label>
            <input
              type="text"
              value={formData.documentReference || ''}
              onChange={(e) => handleInputChange('documentReference', e.target.value)}
              placeholder={formLabel('select_document')}
            />
            <small className={isDashboardLightMode ? 'text-gray-600' : 'text-white/60'}>{formLabel('document_reference_hint')}</small>
          </div>

          <div className="form-group">
            <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{formLabel('transaction_number')}</label>
            <input
              type="text"
              value={formData.transactionNumber || ''}
              onChange={(e) => handleInputChange('transactionNumber', e.target.value)}
              placeholder={formLabel('ph_transaction_example')}
            />
          </div>

          <div className="form-group">
            <label className={isDashboardLightMode ? 'text-black' : 'text-white'} style={{color: isDashboardLightMode ? '#000000' : '#ffffff'}}>{t('document_creation.payment_method')}</label>
            <CustomSelect
              value={formData.paymentMethod || ''}
              onChange={(e) => handleInputChange('paymentMethod', e.target.value)}
            >
              <option value="">{formLabel('select_placeholder')}</option>
              <option value="virement">{formLabel('payment_methods.bank_transfer')}</option>
              <option value="cheque">{formLabel('payment_methods.cheque')}</option>
              <option value="especes">{formLabel('payment_methods.cash')}</option>
              <option value="carte">{formLabel('payment_methods.card')}</option>
              <option value="effet">{formLabel('payment_methods.trade_bill')}</option>
              <option value="echeances">{formLabel('payment_installment')}</option>
            </CustomSelect>
          </div>
        </div>
      </div>
    </>
  );
};

// Composant séparé pour les échéances (après PRODUITS/SERVICES)
const STATUS_OPTIONS_SALE = [
  { value: 'pending', label: 'قيد الانتظار', color: '#fbbf24' },
  { value: 'paid',    label: 'محصّل',   color: '#4ade80' },
];
const STATUS_OPTIONS_PURCHASE = [
  { value: 'pending', label: 'قيد الانتظار', color: '#fbbf24' },
  { value: 'paid',    label: 'مسدّد',      color: '#4ade80' },
];

const StatusSelect = ({ value, onChange, isPurchaseOrder, isDashboardLightMode, placeholder }) => {
  const [open, setOpen] = React.useState(false);
  const [dropdownPos, setDropdownPos] = React.useState({ top: 0, left: 0, width: 0 });
  const btnRef = React.useRef(null);
  const dropRef = React.useRef(null);
  const options = isPurchaseOrder ? STATUS_OPTIONS_PURCHASE : STATUS_OPTIONS_SALE;
  const selected = options.find(o => o.value === value);

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (btnRef.current && !btnRef.current.contains(e.target) &&
          dropRef.current && !dropRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleOpen = () => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + window.scrollY + 8, left: rect.left + window.scrollX, width: rect.width });
    }
    setOpen(o => !o);
  };

  return (
    <div ref={btnRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={handleOpen}
        className={`w-full rounded-xl px-4 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all flex items-center justify-between cursor-pointer hover:bg-blue-500/10 ${isDashboardLightMode ? 'bg-gray-50 border border-gray-200 text-gray-900' : 'bg-white/10 border border-white/20 text-white'}`}
        style={{ height: '46px', minHeight: '46px' }}
      >
        <span style={{ fontSize: '0.875rem', color: selected ? selected.color : (isDashboardLightMode ? '#9ca3af' : 'rgba(255,255,255,0.5)'), fontWeight: selected ? '500' : '400' }}>
          {selected ? selected.label : (placeholder || 'الحالة...')}
        </span>
        <ChevronDown size={20} className={`transition-transform ${isDashboardLightMode ? 'text-gray-500' : 'text-white/60'} ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && createPortal(
        <div
          ref={dropRef}
          className="rounded-xl shadow-2xl overflow-hidden"
          style={{
            position: 'absolute',
            top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width,
            zIndex: 9999999,
            background: 'linear-gradient(to bottom right, rgb(51,65,85), rgb(29,71,196), rgb(51,65,85))',
            border: '1px solid rgba(96,165,250,0.6)',
            backdropFilter: 'blur(12px)',
            boxShadow: '0 8px 32px rgba(59,130,246,0.5), 0 0 0 1px rgba(255,255,255,0.2)',
          }}
        >
          {options.map(opt => (
            <div
              key={opt.value}
              onClick={() => { onChange(opt.value); setOpen(false); }}
              style={{
                padding: '0.65rem 1rem', fontSize: '0.875rem',
                color: opt.color, fontWeight: '500', cursor: 'pointer',
                background: value === opt.value ? 'rgba(255,255,255,0.12)' : 'transparent',
                transition: 'background 0.1s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
              onMouseLeave={e => e.currentTarget.style.background = value === opt.value ? 'rgba(255,255,255,0.12)' : 'transparent'}
            >
              {opt.label}
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

const InstallmentsSection = ({ formData, handleInputChange, selectedType, isDashboardLightMode, getIconColor }) => {
  const { t } = useTranslation();
  const [activePreset, setActivePreset] = React.useState(null);

  
  // Calculer le total EXACT correspondant au "NET À PAYER" - CALCUL COMPLET
  const actualTotal = useMemo(() => {
    console.log('=== INSTALLMENT CALCULATION - FULL CALCULATION LIKE NET TO PAY ===', formData);
    
    const items = formData.items || [];
    
    if (!items.length) {
      console.log('No items found');
      return 0;
    }
    
    // Calcul du sous-total des articles (HT)
    let subtotal = 0;
    items.forEach((item, index) => {
      const quantity = parseFloat(item.quantity) || 1;
      const price = parseFloat(item.discountedPrice || item.price || item.unitPrice) || 0;
      const itemTotal = quantity * price;
      console.log(`Item ${index}:`, { quantity, price, itemTotal });
      subtotal += itemTotal;
    });
    
    console.log('?? Sous-total HT:', subtotal);
    
    let total = subtotal;
    
    // Appliquer la TVA si activée (par défaut 19%)
    const showTva = formData.showTva !== false;
    const tvaRate = parseFloat(formData.tvaRate) || getBrandVatRate();
    if (showTva) {
      const tvaAmount = subtotal * (tvaRate / 100);
      total += tvaAmount;
    }
    
    if (!isSaudiBrand()) {
      const showTap = formData.showTap !== false && selectedType?.id === 'proforma';
      const tapParsed = parseFloat(formData.tapRate);
      const tapRate = showTap && Number.isFinite(tapParsed) ? tapParsed : 0;
      if (showTap) {
        const tapAmount = subtotal * (tapRate / 100);
        total += tapAmount;
      }
    }
    
    if (!isSaudiBrand()) {
      const showStamp = formData.showStamp && selectedType?.id !== 'proforma';
      if (showStamp && formData.stamp) {
        const stampAmount = parseFloat(formData.stamp) || 0;
        total += stampAmount;
      }
    }
    
    const showGuarantee = formData.showGuarantee && selectedType?.id !== 'proforma';
    if (showGuarantee && formData.guarantee) {
      const guaranteeAmount = parseFloat(formData.guarantee) || 0;
      total += guaranteeAmount;
    }
    
    // Ajouter les frais de retour si activés (montant fixe)
    const showReturnCost = formData.showReturnCost;
    if (showReturnCost && formData.returnCost) {
      const returnAmount = parseFloat(formData.returnCost) || 0;
      total += returnAmount;
      console.log('Return fees added:', returnAmount);
    }
    
    // Ajouter les frais de transport si définis (montant fixe, pour factures)
    if (formData.transportCost && selectedType?.id === 'invoice') {
      const transportAmount = parseFloat(formData.transportCost) || 0;
      total += transportAmount;
      console.log('Transport fees added:', transportAmount);
    }
    
    console.log('✅ FINAL INSTALLMENT TOTAL (= Net to Pay):', total);
    
    return Math.round(total * 100) / 100;
  }, [formData, selectedType]);

  // Gérer les échéances
  const addInstallment = () => {
    const installments = formData.installments || [];
    const installmentNumber = installments.length + 1;
    
    // Calcul de date d'échéance par défaut basée sur la date de facture
    const baseDate = formData.date ? new Date(formData.date) : new Date();
    const dueDate = new Date(baseDate);
    dueDate.setDate(baseDate.getDate() + (installmentNumber * 30)); // +30 jours par échéance
    
    // Montant par défaut : si c'est la première échéance et qu'il y a un montant total, le prendre
    let defaultAmount = 0;
    if (installments.length === 0 && actualTotal > 0) {
      defaultAmount = actualTotal;
    }
    
    const newInstallment = {
      id: crypto.randomUUID(),
      number: installmentNumber,
      amount: defaultAmount,
      dueDate: dueDate.toISOString().split('T')[0],
      status: '',
      notes: ''
    };
    
    handleInputChange('installments', [...installments, newInstallment]);
  };

  const removeInstallment = (installmentId) => {
    const installments = formData.installments || [];
    const updated = installments.filter(inst => inst.id !== installmentId);
    handleInputChange('installments', updated);
    setActivePreset(null); // deselect preset ? no longer matches original split
  };

  const updateInstallment = (installmentId, field, value) => {
    const installments = formData.installments || [];
    const updated = installments.map(inst => {
      if (inst.id !== installmentId) return inst;
      if (field === 'percentage') {
        const pct = parseFloat(value) || 0;
        const amt = actualTotal > 0 ? Math.round((pct / 100) * actualTotal * 100) / 100 : 0;
        return { ...inst, percentage: pct, amount: amt };
      }
      if (field === 'amount') {
        const amt = parseFloat(value) || 0;
        const pct = actualTotal > 0 ? Math.round((amt / actualTotal) * 10000) / 100 : 0;
        return { ...inst, amount: amt, percentage: pct };
      }
      return { ...inst, [field]: value };
    });
    handleInputChange('installments', updated);
  };

  const calculateRemainingAmount = () => {
    const installments = formData.installments || [];
    const allocatedAmount = installments.reduce((sum, inst) => sum + (parseFloat(inst.amount) || 0), 0);
    return actualTotal - allocatedAmount;
  };

  // Distribuer automatiquement le montant total sur les échéances
  const distributeAmount = () => {
    const installments = formData.installments || [];
    if (installments.length === 0) return;
    const amountPerInstallment = actualTotal / installments.length;
    const updatedInstallments = installments.map(inst => ({
      ...inst,
      amount: Math.round(amountPerInstallment * 100) / 100,
      percentage: actualTotal > 0 ? Math.round((amountPerInstallment / actualTotal) * 10000) / 100 : 0
    }));
    handleInputChange('installments', updatedInstallments);
  };

  const applyPreset = (percentages, label) => {
    if (activePreset === label) {
      // Deselect ? clear installments
      handleInputChange('installments', []);
      setActivePreset(null);
      return;
    }
    const baseDate = formData.date ? new Date(formData.date) : new Date();
    const newInstallments = percentages.map((pct, index) => {
      const dueDate = new Date(baseDate);
      dueDate.setDate(baseDate.getDate() + ((index + 1) * 30));
      return {
        id: crypto.randomUUID(),
        number: index + 1,
        percentage: pct,
        amount: Math.round((pct / 100) * actualTotal * 100) / 100,
        dueDate: dueDate.toISOString().split('T')[0],
        status: '',
        notes: ''
      };
    });
    handleInputChange('installments', newInstallments);
    setActivePreset(label);
  };

  const isPurchaseOrder = selectedType?.id === 'purchase-order';
  const sectionTitle = isPurchaseOrder
    ? t('document_creation.form.installments_title_purchase')
    : t('document_creation.form.installments_title_sale');

  const labelMuted = isDashboardLightMode ? '#6b7280' : 'rgba(255,255,255,0.6)';
  const labelStrong = isDashboardLightMode ? '#111827' : '#ffffff';
  const cardBg = isDashboardLightMode ? '#f9fafb' : 'rgba(255,255,255,0.05)';
  const cardBorder = isDashboardLightMode ? '#e5e7eb' : 'rgba(255,255,255,0.15)';

  const getInstallmentLabel = (number) => {
    const base = isPurchaseOrder
      ? t('document_creation.form.installments_tranche_supplier')
      : t('document_creation.form.installments_tranche_client');
    return `${base} ${number}`;
  };

  return (
    <div className={`form-section installments-panel${isDashboardLightMode ? ' installments-panel-light' : ''}`}>
      {/* Section Title + Presets + Add button ? all in one row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '1rem', borderBottom: 'none' }}>
        <Calendar size={20} color={getIconColor()} style={{ flexShrink: 0 }} />
        <span style={{ fontWeight: '600', fontSize: '1rem', color: labelStrong, marginRight: '0.25rem', whiteSpace: 'nowrap' }}>{sectionTitle}</span>
        {[
          { label: '2 × 50%', percentages: [50, 50] },
          { label: '30 / 40 / 30', percentages: [30, 40, 30] },
          { label: '3 × 33%', percentages: [33.33, 33.33, 33.34] },
          { label: '4 × 25%', percentages: [25, 25, 25, 25] },
          { label: '50 / 25 / 25', percentages: [50, 25, 25] },
        ].map(preset => {
          const isActive = activePreset === preset.label;
          return (
            <button
              key={preset.label}
              type="button"
              onClick={() => applyPreset(preset.percentages, preset.label)}
              style={{
                background: isActive ? 'rgba(59,130,246,0.8)' : 'rgba(59,130,246,0.15)',
                border: `1px solid ${isActive ? 'rgba(59,130,246,1)' : 'rgba(59,130,246,0.4)'}`,
                color: isActive ? '#ffffff' : 'rgba(147,197,253,0.9)',
                padding: '0.2rem 0.6rem', borderRadius: '6px', cursor: 'pointer',
                fontSize: '0.75rem', fontWeight: isActive ? '600' : '500',
                transition: 'all 0.15s', whiteSpace: 'nowrap',
                boxShadow: isActive ? '0 0 8px rgba(59,130,246,0.5)' : 'none'
              }}
              onMouseEnter={e => { if (!isActive) { e.currentTarget.style.background = 'rgba(59,130,246,0.35)'; e.currentTarget.style.color = '#ffffff'; }}}
              onMouseLeave={e => { if (!isActive) { e.currentTarget.style.background = 'rgba(59,130,246,0.15)'; e.currentTarget.style.color = 'rgba(147,197,253,0.9)'; }}}
            >
              {preset.label}
            </button>
          );
        })}
        <div style={{ flex: 1 }} />
        <button
          type="button"
          onClick={addInstallment}
          style={{ background: 'rgba(59,130,246,0.8)', color: '#ffffff', padding: '0.35rem 0.85rem', borderRadius: '8px', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', whiteSpace: 'nowrap', flexShrink: 0 }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(59,130,246,1)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(59,130,246,0.8)'}
        >
          <PlusCircle size={14} />
          {isPurchaseOrder ? t('document_creation.form.installments_add_purchase') : t('document_creation.form.installments_add_sale')}
        </button>
      </div>

      {/* Financial Summary - 3 cards: Total | Encaissé | Restant */}
      {(() => {
        const installments = formData.installments || [];
        const encaisse = installments
          .filter(i => i.status === 'paid')
          .reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
        const restant = actualTotal - encaisse;
        const hasInstallments = installments.length > 0;
        const isFullyCovered = hasInstallments && restant === 0;
        return (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem', marginBottom: '1rem' }}>
            <div style={{ background: cardBg, borderRadius: '12px', padding: '0.75rem 1rem', border: `1px solid ${cardBorder}` }}>
              <div style={{ color: labelMuted, fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                {isPurchaseOrder ? t('document_creation.form.installments_total_order') : t('document_creation.form.installments_total_invoice')}
              </div>
              <div style={{ color: labelStrong, fontWeight: '600', fontSize: '1rem' }}>{formatBrandMoney(actualTotal)}</div>
            </div>
            <div style={{ background: isDashboardLightMode ? '#ecfdf5' : 'rgba(34,197,94,0.08)', borderRadius: '12px', padding: '0.75rem 1rem', border: isDashboardLightMode ? '1px solid #bbf7d0' : '1px solid rgba(34,197,94,0.25)' }}>
              <div style={{ color: isDashboardLightMode ? '#059669' : 'rgba(134,239,172,0.8)', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                {isPurchaseOrder ? t('document_creation.form.installments_paid_supplier') : t('document_creation.form.installments_collected')}
              </div>
              <div style={{ color: '#4ade80', fontWeight: '600', fontSize: '1rem' }}>{formatBrandMoney(encaisse)}</div>
            </div>
            <div style={{
              background: isFullyCovered ? (isDashboardLightMode ? '#ecfdf5' : 'rgba(34,197,94,0.08)') : hasInstallments ? (isDashboardLightMode ? '#fffbeb' : 'rgba(251,191,36,0.08)') : cardBg,
              borderRadius: '12px', padding: '0.75rem 1rem',
              border: isFullyCovered ? (isDashboardLightMode ? '1px solid #bbf7d0' : '1px solid rgba(34,197,94,0.25)') : hasInstallments ? (isDashboardLightMode ? '1px solid #fde68a' : '1px solid rgba(251,191,36,0.3)') : `1px solid ${cardBorder}`
            }}>
              <div style={{ color: isFullyCovered ? (isDashboardLightMode ? '#059669' : 'rgba(134,239,172,0.8)') : hasInstallments ? (isDashboardLightMode ? '#d97706' : 'rgba(253,230,138,0.8)') : labelMuted, fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                {isPurchaseOrder ? t('document_creation.form.installments_remaining_purchase') : t('document_creation.form.installments_remaining_sale')}
              </div>
              <div style={{ color: isFullyCovered ? '#4ade80' : hasInstallments ? '#fbbf24' : labelStrong, fontWeight: '600', fontSize: '1rem' }}>{formatBrandMoney(restant)}</div>
            </div>
          </div>
        );
      })()}


      {/* Validation: total tranches > total facture */}
      {(() => {
        const totalTranches = (formData.installments || []).reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
        if (totalTranches > actualTotal + 0.01) {
          return (
            <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', padding: '0.5rem 0.75rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <AlertTriangle size={14} style={{ color: 'rgba(252,165,165,0.9)' }} />
              <span style={{ color: isDashboardLightMode ? '#b91c1c' : 'rgba(252,165,165,0.9)', fontSize: '0.75rem' }}>
                {t('document_creation.form.installments_over_limit', {
                  total: formatBrandMoney(totalTranches),
                  docLabel: isPurchaseOrder ? t('document_creation.form.installments_doc_order') : t('document_creation.form.installments_doc_invoice'),
                  diff: formatBrandMoney(totalTranches - actualTotal),
                })}
              </span>
            </div>
          );
        }
        return null;
      })()}

      {/* Liste des échéances - simplified */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {(formData.installments || []).map((installment, index) => (
          <div key={installment.id} style={{ 
            background: cardBg, 
            borderRadius: '8px', 
            padding: '1rem',
            border: `1px solid ${cardBorder}`
          }}>
            {/* Header with title and delete */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '0.75rem',
              paddingBottom: '0.5rem',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <h5 style={{ color: labelStrong, fontWeight: '500', fontSize: '0.9rem', margin: 0 }}>
                {getInstallmentLabel(installment.number)}
              </h5>
              <button
                onClick={() => removeInstallment(installment.id)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'rgba(248, 113, 113, 0.8)',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(248, 113, 113, 1)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(248, 113, 113, 0.8)'}
              >
                <Trash2 size={16} />
              </button>
            </div>
            
            {/* Row 1: Condition + Statut */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 3fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label style={{ color: isDashboardLightMode ? '#111827' : '#ffffff', fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                  {t('document_creation.form.installments_status_placeholder')}
                </label>
                <StatusSelect
                  value={installment.status || ''}
                  onChange={val => updateInstallment(installment.id, 'status', val)}
                  isPurchaseOrder={isPurchaseOrder}
                  isDashboardLightMode={isDashboardLightMode}
                  placeholder={t('document_creation.form.installments_status_placeholder')}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label style={{ color: isDashboardLightMode ? '#111827' : '#ffffff', fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                  {t('document_creation.payment_terms')}
                </label>
                <CustomSelect
                  value={installment.condition || ''}
                  onChange={val => updateInstallment(installment.id, 'condition', val)}
                  placeholder={t('document_creation.form.select_or_enter')}
                  creatable={true}
                  options={isPurchaseOrder ? [
                    { value: 'a_la_commande',  label: 'عند الطلب' },
                    { value: 'a_la_livraison', label: 'عند التسليم' },
                    { value: 'a_la_reception', label: 'عند الاستلام' },
                    { value: 'apres_reception',label: 'بعد الاستلام (30 يوماً)' },
                    { value: 'date_precise',   label: 'في تاريخ محدد' },
                  ] : [
                    { value: 'a_la_commande',  label: 'عند الطلب (دفعة مقدمة)' },
                    { value: 'a_la_livraison', label: 'عند التسليم' },
                    { value: 'a_la_reception', label: 'عند الاستلام النهائي' },
                    { value: 'fin_de_mois',    label: 'نهاية الشهر' },
                    { value: 'date_precise',   label: 'في تاريخ محدد' },
                  ]}
                />
              </div>
            </div>

            {/* Row 2: % | Montant | Date | Notes */}
            <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr 1fr 1fr', gap: '0.75rem' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label style={{ color: isDashboardLightMode ? '#111827' : '#ffffff', fontSize: '0.875rem', marginBottom: '0.25rem' }}>%</label>
                <CustomNumberInput
                  value={installment.percentage === 0 ? '' : installment.percentage}
                  onChange={val => updateInstallment(installment.id, 'percentage', parseFloat(val) || 0)}
                  placeholder="0" min={0} max={100} step={1} decimals={2}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label style={{ color: isDashboardLightMode ? '#111827' : '#ffffff', fontSize: '0.875rem', marginBottom: '0.25rem' }}>{t('document_creation.form.installments_amount')} ({getBrandCurrencySuffix()})</label>
                <CustomNumberInput
                  value={installment.amount === 0 ? '' : installment.amount}
                  onChange={val => updateInstallment(installment.id, 'amount', parseFloat(val) || 0)}
                  placeholder={t('document_creation.form.enter_price')} min={0} step={0.01} decimals={2}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label style={{ color: isDashboardLightMode ? '#111827' : '#ffffff', fontSize: '0.875rem', marginBottom: '0.25rem' }}>{t('document_creation.form.delivery_date')}</label>
                <CustomDatePicker value={installment.dueDate} onChange={val => updateInstallment(installment.id, 'dueDate', val)} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label style={{ color: isDashboardLightMode ? '#111827' : '#ffffff', fontSize: '0.875rem', marginBottom: '0.25rem' }}>{t('document_creation.document_notes')}</label>
                <textarea
                  rows="1"
                  value={installment.notes}
                  onChange={e => updateInstallment(installment.id, 'notes', e.target.value)}
                  placeholder={isPurchaseOrder ? t('document_creation.form.installments_notes_purchase') : t('document_creation.form.installments_notes_sale')}
                  style={{ fontSize: '0.875rem', height: '46px', minHeight: '46px', maxHeight: '46px', resize: 'none', overflow: 'hidden' }}
                />
              </div>
            </div>
          </div>
        ))}

        {(!formData.installments || formData.installments.length === 0) && (
          <div style={{ 
            textAlign: 'center', 
            padding: '1.25rem 1rem', 
            color: isDashboardLightMode ? '#6b7280' : 'rgba(255, 255, 255, 0.4)' 
          }}>
            <Calendar size={32} style={{ margin: '0 auto 0.75rem', opacity: 0.5, color: isDashboardLightMode ? '#9ca3af' : undefined }} />
            <p style={{ margin: 0, fontSize: '0.875rem' }}>
              {isPurchaseOrder ? t('document_creation.form.installments_empty_purchase') : t('document_creation.form.installments_empty_sale')}
            </p>
            <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem' }}>
              {isPurchaseOrder
                ? t('document_creation.form.installments_empty_hint_purchase')
                : t('document_creation.form.installments_empty_hint_sale')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentCreation;
